
const {useState,useMemo,useRef,useEffect,useCallback}=React;

/* ── ANIMATION HOOKS ─────────────────────────────────────── */
function useAnimatedNumber(target,duration=900,delay=0){
  const [val,setVal]=useState(0);
  const num=typeof target==='number'?target:parseFloat(String(target).replace(/[^0-9.]/g,''))||0;
  useEffect(()=>{
    let raf,start=null;
    // Preserve the target's precision: integer targets (SF, counts) round to a
    // whole number; decimal targets (e.g. $12.5M) keep ONE decimal. Rounding a
    // decimal to an integer here would settle 12.5 at 13 → the card would show
    // "$13.0M" while the narrative shows "$12.5M" (a consistency/accuracy bug).
    const factor=Number.isInteger(num)?1:10;
    const run=ts=>{
      if(!start)start=ts+delay;
      const t=Math.max(0,ts-start);
      const p=Math.min(t/duration,1);
      const eased=1-Math.pow(1-p,3); // ease-out cubic
      setVal(Math.round(eased*num*factor)/factor);
      if(p<1)raf=requestAnimationFrame(run);
    };
    raf=requestAnimationFrame(run);
    return()=>cancelAnimationFrame(raf);
  },[num,duration,delay]);
  return val;
}

/* ── TOAST SYSTEM ────────────────────────────────────────── */
const ToastCtx=React.createContext({addToast:()=>{}});
function useToast(){return React.useContext(ToastCtx);}
function ToastProvider({children}){
  const [toasts,setToasts]=useState([]);
  const addToast=useCallback((msg,type='success',dur=3200)=>{
    const id=Date.now()+Math.random();
    setToasts(t=>[...t,{id,msg,type}]);
    setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),dur);
  },[]);
  const icons={success:'✓',error:'✕',info:'ℹ',warn:'⚠'};
  const colors={success:'#3D1F0D',error:'#dc2626',info:'#2563EB',warn:'#D97706'};
  return(
    <ToastCtx.Provider value={{addToast}}>
      {children}
      <div style={{position:'fixed',bottom:28,right:28,zIndex:9999,display:'flex',flexDirection:'column',gap:8,pointerEvents:'none',alignItems:'flex-end'}}>
        {toasts.map(t=>(
          <div key={t.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 16px',borderRadius:10,background:colors[t.type]||colors.success,color:'#fff',fontSize:12,fontWeight:600,boxShadow:'0 8px 28px rgba(0,0,0,.18)',animation:'toastIn .22s cubic-bezier(.22,1,.36,1) both',pointerEvents:'auto',letterSpacing:'.01em',maxWidth:320}}>
            <span style={{fontSize:14,flexShrink:0}}>{icons[t.type]||icons.success}</span>
            <span>{t.msg}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ── LIVE DATA CONTEXT ────────────────────────────────────── */
const DataCtx=React.createContext({});
function useData(){return React.useContext(DataCtx);}

/* API_BASE — Railway backend when hosted on Cloudflare Pages, relative otherwise.
   All /api/* calls are routed through authFetch which prepends this prefix. */
const API_BASE=(()=>{
  const h=window.location.hostname;
  if(h==='localhost'||h==='127.0.0.1'||h.startsWith('192.168.'))return '';
  return 'https://status-production-b29c.up.railway.app';
})();

/* v1.2 — the Single-Focus Shell is a ROUTE, not a layer. /start renders the
   onboarding shell with no dashboard chrome mounted behind it. */
const IS_START_ROUTE=window.location.pathname==='/start';

/* ── ACCOUNT PRIVACY FENCE ────────────────────────────────────────────────
   Every browser-persisted key that holds account-specific state or content.
   These must NEVER survive a change of account (different org OR different
   user/email) in the same browser. Org switching is logout+login only (no
   in-app switcher), so a clean boundary is: purge these on sign-out, and purge
   again on load if the resolved account identity differs from last-seen.
   NOTE: 'stage_active_account' (the identity marker) is deliberately excluded. */
const ACCOUNT_SCOPED_KEYS=[
  'stage_first_finding','stage_onboarding_intent','stage_focus_state',
  'stage_spine_acks','stage_ack_drivers','stage_triage_week','stage_focus_scroll',
  'stage_focus_lastsev','stage_focus_pending','stage_guided_bypassed',
  'stage_survey_dismissed','stage_analytics_posture','stage_export_confirmed',
  'stage_first_name','stage_name_ask_dismissed',
];
function purgeAccountState(){
  try{ACCOUNT_SCOPED_KEYS.forEach(k=>{try{localStorage.removeItem(k);}catch(e){}try{sessionStorage.removeItem(k);}catch(e){}});}catch(e){}
}

/* useAuthFetch — shared authenticated fetch hook usable by any component */
function useAuthFetch(){
  const clerk=useClerk();
  return React.useCallback(async function doFetch(url,opts={},_retried=false){
    // On a 401 retry, force a fresh token (skipCache) in case the cached JWT expired.
    const token=await getToken(clerk,_retried?{skipCache:true}:undefined);
    const headers={...(opts.headers||{})};
    if(token)headers['Authorization']=`Bearer ${token}`;
    // Build body without mutating opts, so a retry re-sends the ORIGINAL object
    // (not an already-stringified one → no double-encode).
    let body=opts.body;
    if(body&&typeof body==='object'&&!(body instanceof FormData)){
      body=JSON.stringify(body);
      headers['Content-Type']='application/json';
    }
    const resolvedUrl=url.startsWith('/')?API_BASE+url:url;
    const r=await fetch(resolvedUrl,{...opts,body,headers});
    // Central token-expiry handling: a mid-session 401 usually means the JWT lapsed.
    // Refresh-and-retry ONCE; if it still 401s, tell AuthGate to re-prompt sign-in
    // instead of leaving the UI silently frozen on stale data.
    if(r.status===401&&clerk&&!_retried){
      return doFetch(url,opts,true);
    }
    if(!r.ok){
      if(r.status===401){try{window.dispatchEvent(new CustomEvent('stage:auth-expired'));}catch(e){}}
      const err=new Error(`${r.status} ${r.statusText}`);
      err.status=r.status;          // consumers check err.status instead of string-matching
      throw err;
    }
    return r.json();
  },[clerk]);
}

/* Stable id for Sage chat messages so React reconciles by identity, not array
   index. Index keys mis-associate per-message DOM state (the streaming cursor, the
   citations chip) when the list finalises/appends mid-stream. */
let _msgSeq=0; function _mid(){return 'm'+(++_msgSeq);}

/* ── CLERK AUTH CONTEXT ───────────────────────────────────── */
const ClerkCtx=React.createContext(null);
function useClerk(){return React.useContext(ClerkCtx);}

/* ── Identity derivation ──────────────────────────────────────
   The Clerk session JWT frequently omits name/email claims, so the backend
   /api/me can come back with no usable name. We therefore enrich currentUser
   from the Clerk *client* user object (see enrichIdentityFromClerk) and derive
   names from every available signal — first name, full name, username, email
   local-part — before ever falling back to "Servant leader". We only greet the
   fallback when NONE of these yields a real name. A raw Clerk id is never a name. */
function _isClerkId(s){return /^user_[A-Za-z0-9]{6,}$/.test((s||'').trim());}
function _nameStr(s){const t=(s||'').trim();return (t&&!_isClerkId(t))?t:'';}
function _emailLocalPart(s){const t=(s||'').trim();if(!t||t.indexOf('@')<0)return '';const lp=t.split('@')[0].trim();return _isClerkId(lp)?'':lp;}
// Best-effort FIRST name from an identity object.
function deriveFirstName(u){
  if(!u) return '';
  const fn=_nameStr(u.firstName); if(fn) return fn.split(/\s+/)[0];
  const nm=_nameStr(u.name); if(nm) return nm.split(/\s+/)[0];
  const un=_nameStr(u.username); if(un) return un;
  return _emailLocalPart(u.email);
}
// Best-effort FULL display name (sidebar footer); falls back to first name.
function deriveDisplayName(u){
  if(!u) return '';
  const nm=_nameStr(u.name); if(nm) return nm;
  const fl=[_nameStr(u.firstName),_nameStr(u.lastName)].filter(Boolean).join(' ').trim();
  if(fl) return fl;
  return deriveFirstName(u);
}
// Title-case the first letter only — "olushegun" → "Olushegun" — for greetings,
// without mangling names that are already cased ("McDonald", "Shegun").
function _titleFirst(s){const t=(s||'').trim();return t?t.charAt(0).toUpperCase()+t.slice(1):t;}
/* ── Trust-stage data gating ──────────────────────────────────
   Sage must never manufacture analysis from data it doesn't have — the failure
   that makes skeptical users distrust the whole product. The Trust Ladder ceiling
   is therefore DERIVED from how much grounded data the org actually holds; the
   org can sit at or below it, never above. Stage 1 (Observer) is the floor when
   there are zero records; each higher stage demands progressively more real
   grounding, and unlocks automatically as that grounding arrives. */
function computeTrustCeiling(portfolio,tws){
  const locs=Array.isArray(portfolio)?portfolio:[];
  if(locs.length===0) return 1;                          // Observer — nothing to analyse
  let stage=2;                                            // ≥1 real record → Hypothesis
  const grounded=locs.filter(l=>Number(l.rent)>0&&(l.leaseEnd||l.lease_end));
  if(grounded.length>=1) stage=3;                         // lease terms → Evidence
  const hasUtil=(tws&&tws.available)||locs.some(l=>Number(l.util)>0);
  if(stage>=3&&hasUtil) stage=4;                          // utilisation/TWS → Standard
  if(stage>=4&&grounded.length>=3&&tws&&tws.available) stage=5; // broad grounding → Advocate
  return stage;
}
// Plain-language requirement to unlock each gated stage — shown in ladder tooltips
// and the gate banner so the path out of Observer is concrete, never mysterious.
const TRUST_UNLOCK={
  2:'Add at least one location to the Portfolio Spine',
  3:'Add lease terms (annual rent + expiry) to a location',
  4:'Add utilization / occupancy data, or import experience signals',
  5:'Ground at least three locations with lease terms, plus utilization',
};

// Copy name/email off the Clerk client user object onto a plain identity object
// when the backend didn't provide them. Mutates and returns u.
function enrichIdentityFromClerk(u,clerk){
  try{
    const cu=(clerk&&clerk.user)||(typeof window!=='undefined'&&window.Clerk&&window.Clerk.user);
    if(!u||!cu) return u;
    const cEmail=(cu.primaryEmailAddress&&cu.primaryEmailAddress.emailAddress)
      ||(cu.emailAddresses&&cu.emailAddresses[0]&&cu.emailAddresses[0].emailAddress)||'';
    const cFull=(cu.fullName||[cu.firstName,cu.lastName].filter(Boolean).join(' ')||'').trim();
    if(!_nameStr(u.firstName)&&_nameStr(cu.firstName)) u.firstName=cu.firstName.trim();
    if(!_nameStr(u.lastName)&&_nameStr(cu.lastName)) u.lastName=cu.lastName.trim();
    if(!_nameStr(u.name)&&cFull) u.name=cFull;
    if(!_nameStr(u.username)&&_nameStr(cu.username)) u.username=cu.username.trim();
    if(!_emailLocalPart(u.email)&&cEmail) u.email=cEmail;
  }catch(e){}
  return u;
}

/**
 * getToken() — returns a fresh Clerk JWT for the current session,
 * or null in dev mode (auth disabled). Pass to all API fetch calls
 * and the WS upgrade as ?token=<value>.
 */
async function getToken(clerk,opts){
  if(!clerk||!clerk.session)return null;
  try{return await clerk.session.getToken(opts);}   // opts={skipCache:true} forces a fresh JWT
  catch(e){console.warn('getToken failed',e);return null;}
}

/**
 * AuthGate — wraps the entire app.
 *
 * 1. Fetches /api/client-config to discover whether auth is enabled
 *    and the Clerk publishable key.
 * 2. If auth is disabled (dev mode) → renders children immediately.
 * 3. If auth is enabled → waits for Clerk JS to load, initialises it,
 *    then either renders children (signed in) or the Clerk sign-in UI.
 */
function AuthGate({children}){
  const [state,setState]=useState({phase:'loading'}); // loading|auth-init|sign-in|ready|error
  const [clerk,setClerk]=useState(null);
  const signInRef=useRef(null);

  // ── Step 1: fetch server config ─────────────────────────────
  useEffect(()=>{
    fetch(API_BASE+'/api/client-config')
      .then(r=>r.json())
      .then(cfg=>{
        if(!cfg.authEnabled){
          setState({phase:'ready'});          // dev mode — skip auth
          return;
        }
        if(!cfg.clerkPublishableKey){
          setState({phase:'error',msg:'Auth enabled but no Clerk key configured.'});
          return;
        }
        setState({phase:'auth-init',pk:cfg.clerkPublishableKey});
      })
      .catch(()=>setState({phase:'ready'})); // API unreachable — render anyway
  },[]);

  // ── Step 2: load Clerk JS from the app's own Frontend API URL ──
  // The publishable key encodes the Frontend API hostname in base64.
  // Decoding it gives us the correct script URL — no generic CDN needed.
  useEffect(()=>{
    if(state.phase!=='auth-init')return;

    // Derive Frontend API URL from publishable key: pk_test_BASE64$ → hostname
    let frontendApiUrl;
    try{
      const b64=state.pk.replace(/^pk_(test|live)_/,'');
      const host=atob(b64).replace(/\$$/,'');
      frontendApiUrl=`https://${host}`;
    }catch(e){
      setState({phase:'error',msg:'Invalid Clerk publishable key format.'});
      return;
    }

    // Inject the Clerk script with data-clerk-publishable-key on the element.
    // This is the script-tag pattern: Clerk auto-configures and exposes
    // window.Clerk as a ready instance — NOT a class to instantiate with new().
    const script=document.createElement('script');
    script.src=`${frontendApiUrl}/npm/@clerk/clerk-js@latest/dist/clerk.browser.js`;
    script.setAttribute('data-clerk-publishable-key',state.pk);
    script.crossOrigin='anonymous';
    script.onload=()=>{
      // Give Clerk one tick to finish its own synchronous setup
      setTimeout(()=>{
        if(!window.Clerk){
          setState({phase:'error',msg:'Clerk JS loaded but window.Clerk is undefined.'});
          return;
        }
        // window.Clerk is the pre-configured instance — just call .load()
        // Use session (not user) for the ready check: session exists the moment
        // auth succeeds, while user can be null mid-flow when orgs are involved.
        window.Clerk.load().then(()=>{
          setClerk(window.Clerk);
          const isAuthed=!!(window.Clerk.session||window.Clerk.user);
          setState({phase:isAuthed?'ready':'sign-in'});
          window.Clerk.addListener(({session,user})=>setState(prev=>{
            const authed=!!(session||user);
            if(prev.phase==='ready'&&!authed){
              // Sign-out — wipe every account-scoped artifact and the identity
              // marker so the next login (same or DIFFERENT email/org) is clean.
              purgeAccountState();
              try{localStorage.removeItem('stage_active_account');localStorage.removeItem('stage_active_tenant');}catch(e){}
              return{phase:'sign-in'};
            }
            if(prev.phase==='sign-in'&&authed)return{phase:'ready'};
            return prev;
          }));
        }).catch(e=>{
          console.error('Clerk load error',e);
          setState({phase:'error',msg:'Could not connect to authentication service.'});
        });
      },0);
    };
    script.onerror=()=>{
      setState({phase:'error',msg:`Could not load Clerk JS from ${frontendApiUrl}.`});
    };
    document.head.appendChild(script);
    return()=>{try{document.head.removeChild(script);}catch(_){}};
  },[state.phase,state.pk]);

  // ── Central token-expiry handling ───────────────────────────
  // authFetch dispatches 'stage:auth-expired' after a refresh-and-retry still 401s.
  // Re-prompt sign-in instead of leaving the app frozen on stale data. Only acts
  // when Clerk is active (never in dev mode) and only from a 'ready' state.
  useEffect(()=>{
    if(!clerk)return;
    const onExpired=()=>setState(prev=>prev.phase==='ready'?{phase:'sign-in'}:prev);
    window.addEventListener('stage:auth-expired',onExpired);
    return()=>window.removeEventListener('stage:auth-expired',onExpired);
  },[clerk]);

  // ── Step 3: mount / unmount Clerk's built-in sign-in UI ─────
  useEffect(()=>{
    if(state.phase==='sign-in'&&clerk&&signInRef.current){
      clerk.mountSignIn(signInRef.current,{
        appearance:{variables:{colorPrimary:'#8b0020',colorBackground:'#F5EDE0',fontFamily:'Inter,sans-serif'}},
      });
      return()=>{try{clerk.unmountSignIn(signInRef.current);}catch(_){}};
    }
  },[state.phase,clerk]);

  // ── Render ──────────────────────────────────────────────────
  if(state.phase==='loading'||state.phase==='auth-init'){
    return(
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F5EDE0',flexDirection:'column',gap:16}}>
        <img src="/static/logo.svg" alt="Stage" style={{height:28,display:'block'}}
          onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='inline';}}
        />
        <span style={{display:'none',fontFamily:'Georgia,serif',fontSize:26,fontWeight:700,color:'#8b0020'}}>stage</span>
        <div style={{fontSize:13,color:'rgba(61,31,13,.4)',fontWeight:500}}>Loading Status…</div>
      </div>
    );
  }

  if(state.phase==='sign-in'){
    return(
      <div style={{display:'flex',height:'100vh',background:'#F5EDE0'}}>
        {/* Brand panel */}
        <div style={{width:420,background:'#3D1F0D',display:'flex',flexDirection:'column',justifyContent:'center',padding:'0 56px'}}>
          <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:40}}>
            <img src="/static/logo-white.svg" alt="Stage" style={{height:28,width:'auto',display:'block'}}/>
            <span style={{color:'#fff',fontWeight:700,fontSize:20,letterSpacing:'-0.3px'}}>Status</span>
          </div>
          <h1 style={{color:'#fff',fontSize:28,fontWeight:800,lineHeight:1.3,marginBottom:16}}>The calls are too big to guess.</h1>
          <p style={{color:'rgba(255,255,255,.5)',fontSize:14,lineHeight:1.7}}>Sage is the intelligence layer for your portfolio that compounds — watching how your buildings actually get used, translating it for every team, and turning observation into decisions you can defend.</p>
          <div style={{marginTop:48,display:'flex',flexDirection:'column',gap:14}}>
            {[['◈','Sage','the advisor that knows your portfolio better than anyone on your team'],['✎','Scribe','every answer sourced, every claim auditable'],['⛨','Sentry','every agent action logged, reviewable, reversible']].map(([icon,name,desc])=>(
              <div key={name} style={{display:'flex',alignItems:'flex-start',gap:12}}>
                <span style={{color:'#E8813A',fontSize:18,width:24,textAlign:'center',flexShrink:0,marginTop:1}}>{icon}</span>
                <span style={{color:'rgba(255,255,255,.55)',fontSize:13,lineHeight:1.5}}><span style={{color:'rgba(255,255,255,.85)',fontWeight:700}}>{name}</span> — {desc}</span>
              </div>
            ))}
          </div>
        </div>
        {/* Sign-in panel */}
        <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{width:'100%',maxWidth:480,padding:'0 32px'}}>
            <div ref={signInRef}/>
          </div>
        </div>
      </div>
    );
  }

  if(state.phase==='error'){
    return(
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F5EDE0',flexDirection:'column',gap:8}}>
        <div style={{color:'#dc2626',fontWeight:700}}>Authentication error</div>
        <div style={{color:'rgba(61,31,13,.5)',fontSize:13}}>{state.msg}</div>
      </div>
    );
  }

  // phase === 'ready'
  return(
    <ClerkCtx.Provider value={clerk}>
      {children}
    </ClerkCtx.Provider>
  );
}

/**
 * UserButton — renders Clerk's built-in avatar button in the topbar.
 * Shows a profile photo, opens a dropdown with sign-out option.
 * Renders nothing in dev mode (no clerk instance).
 */
function ClerkUserButton(){
  const clerk=useClerk();
  const ref=useRef(null);
  useEffect(()=>{
    if(!clerk||!ref.current)return;
    clerk.mountUserButton(ref.current,{
      appearance:{variables:{colorPrimary:'#8b0020'}},
      userProfileMode:'modal',  // opens profile in-place — no /profile route needed
    });
    return()=>{try{clerk.unmountUserButton(ref.current);}catch(_){}};
  },[clerk]);
  if(!clerk)return null;
  return <div ref={ref} style={{display:'flex',alignItems:'center'}}/>;
}

/* Privacy hardening — the in-app Clerk OrganizationSwitcher was REMOVED.
 * In-place org switching kept the same tab/session alive across a tenant
 * change, so client-persisted state (onboarding finding, focus state, acks)
 * could bleed from one org into the next. Switching org now requires a full
 * sign-out + sign-in: sign-out purges all account-scoped storage (see the
 * AuthGate listener) and the next login starts on a clean session. The active
 * org is shown read-only in the topbar (see Topbar). */

/**
 * TEAM-1 Gap T4 — notification-preferences bell.
 *
 * The bell opens a small popover to control notification volume: a master mute
 * plus a per-category in-app toggle. Persists via PUT /api/notifications/
 * preferences; the live-toast suppression reads the same prefs. Keeps the
 * channel set minimal (in-app) — email/slack routing is server-side foundation.
 */
const NOTIF_CAT_LABELS={collaborator_activity:'Teammate activity',sage_insight:'Sage insights',action_update:'Action updates',approval_request:'Approval requests'};
function NotifBell(){
  const authFetch=useAuthFetch();
  const [open,setOpen]=useState(false);
  const [state,setState]=useState(null);   // {muted, prefs, categories}
  const [saving,setSaving]=useState(false);
  useEffect(()=>{
    if(!open||state)return;
    authFetch('/api/notifications/preferences').then(d=>{ if(d&&d.prefs) setState(d); }).catch(()=>{});
  },[open]);
  const save=(next)=>{
    setSaving(true);
    authFetch('/api/notifications/preferences',{method:'PUT',body:{muted:next.muted,prefs:next.prefs}})
      .then(d=>{ if(d&&d.prefs) setState(d); }).catch(()=>{}).finally(()=>setSaving(false));
  };
  const toggleMute=()=>{ if(!state)return; save({muted:!state.muted,prefs:state.prefs}); };
  const toggleCat=(cat)=>{ if(!state)return;
    const cur=state.prefs[cat]?.in_app!==false;
    const prefs={...state.prefs,[cat]:{...(state.prefs[cat]||{}),in_app:!cur}};
    save({muted:state.muted,prefs});
  };
  return(
    <div style={{position:'relative'}}>
      <div style={{position:'relative',cursor:'pointer'}} onClick={()=>setOpen(o=>!o)} title="Notification preferences">
        <span style={{fontSize:18,color:'rgba(61,31,13,.5)'}}>🔔</span>
        <span style={{position:'absolute',top:-2,right:-2,width:8,height:8,background:'#ef4444',borderRadius:'50%',border:'1.5px solid #fff',animation:'ringPulse 2.2s ease-in-out infinite'}}/>
      </div>
      {open&&(
        <div style={{position:'absolute',top:30,right:0,width:260,background:'#fff',border:'1px solid rgba(61,31,13,.12)',borderRadius:12,boxShadow:'0 12px 36px rgba(0,0,0,.16)',padding:14,zIndex:50}}>
          <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginBottom:10}}>Notifications</div>
          {!state?<div style={{fontSize:12,color:'rgba(61,31,13,.5)'}}>Loading…</div>:(
            <div style={{opacity:saving?0.6:1}}>
              <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12,fontWeight:600,color:'#8b0020',padding:'6px 0',borderBottom:'1px solid rgba(61,31,13,.08)',cursor:'pointer'}}>
                Mute all
                <input type="checkbox" checked={!!state.muted} onChange={toggleMute}/>
              </label>
              {(state.categories||Object.keys(NOTIF_CAT_LABELS)).map(cat=>(
                <label key={cat} style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12,color:state.muted?'rgba(61,31,13,.35)':'#3D1F0D',padding:'7px 0',cursor:state.muted?'default':'pointer'}}>
                  {NOTIF_CAT_LABELS[cat]||cat}
                  <input type="checkbox" disabled={state.muted}
                    checked={!state.muted && (state.prefs[cat]?.in_app!==false)}
                    onChange={()=>toggleCat(cat)}/>
                </label>
              ))}
              <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:8}}>In-app alerts. Saved automatically.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── DATA ─────────────────────────────────────────────────── */
const PORTFOLIO=[
  {id:'l1',twsId:'nyc1',name:'1 Penn Plaza',market:'New York',sf:45000,rent:2700000,leaseEnd:'2026-09-30',optionDate:'2026-06-30',advisor:'Marcus Chen',bu:'Corporate HQ',util:54,status:'red',source:'Scribe',costPerExp:1650,headcount:270,
   documents:[{name:'Executed_Lease_1Penn.pdf',type:'Lease',date:'2020-03-01'},{name:'Certificate_of_Occupancy.pdf',type:'CofO',date:'2019-11-15'},{name:'Floorplan_Floors14-15.pdf',type:'Floorplan',date:'2022-06-01'},{name:'Lease_Abstract_1Penn.pdf',type:'Abstract',date:'2020-03-15'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Vornado Realty',active:true},{role:'Tenant Advisor',name:'Marcus Chen',active:true},{role:'Building Mgmt',name:'Vornado Property Services',active:true}]},
  {id:'l2',twsId:'nyc2',name:'30 Hudson Yards',market:'New York',sf:12000,rent:1200000,leaseEnd:'2028-05-31',optionDate:null,advisor:'Marcus Chen',bu:'Tech Hub',util:78,status:'green',source:'Scribe',costPerExp:580,headcount:88,
   documents:[{name:'Executed_Lease_30HY.pdf',type:'Lease',date:'2021-06-01'},{name:'CofO_30HudsonYards.pdf',type:'CofO',date:'2021-05-20'},{name:'Floorplan_30HY_FL12.pdf',type:'Floorplan',date:'2023-01-15'},{name:'Lease_Abstract_30HY.pdf',type:'Abstract',date:'2021-06-10'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Related Companies',active:true},{role:'Tenant Advisor',name:'Marcus Chen',active:true}]},
  {id:'l3',twsId:'chi1',name:'Willis Tower',market:'Chicago',sf:28500,rent:1425000,leaseEnd:'2026-12-31',optionDate:'2026-09-30',advisor:'Priya Nair',bu:'Midwest Ops',util:61,status:'yellow',source:'Scribe',costPerExp:890,headcount:200,
   documents:[{name:'Executed_Lease_Willis.pdf',type:'Lease',date:'2019-01-01'},{name:'CofO_Willis.pdf',type:'CofO',date:'2018-12-01'},{name:'Floorplan_Willis_FL42.pdf',type:'Floorplan',date:'2021-04-10'},{name:'Lease_Abstract_Willis.pdf',type:'Abstract',date:'2019-01-15'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Blackstone',active:true},{role:'Tenant Advisor',name:'Priya Nair',active:true},{role:'Legal Counsel',name:'Kirkland & Ellis',active:false}]},
  {id:'l4',twsId:'sf1',name:'444 Market Street',market:'San Francisco',sf:18000,rent:2070000,leaseEnd:'2027-08-31',optionDate:null,advisor:'Jordan Wells',bu:'West Coast',util:47,status:'yellow',source:'Sync',costPerExp:1380,headcount:92,
   documents:[{name:'Executed_Lease_444Market.pdf',type:'Lease',date:'2020-09-01'},{name:'CofO_444Market.pdf',type:'CofO',date:'2020-08-15'},{name:'Floorplan_444Market_FL18.pdf',type:'Floorplan',date:'2022-03-20'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Paramount Group',active:true},{role:'Tenant Advisor',name:'Jordan Wells',active:true}]},
  {id:'l5',twsId:'aus1',name:'2845 Domain Drive',market:'Austin',sf:22000,rent:792000,leaseEnd:'2029-03-31',optionDate:'2028-10-01',advisor:'Priya Nair',bu:'South-Central',util:82,status:'green',source:'Scribe',costPerExp:420,headcount:185,
   ti:{total:2800000,applied:700000,appliedMonthly:52000,expiry:'2028-09-30',mechanism:'credit',eligible:'Furniture, fixtures & equipment; AV systems; data infrastructure'},
   documents:[{name:'Executed_Lease_Domain.pdf',type:'Lease',date:'2022-04-01'},{name:'CofO_Domain.pdf',type:'CofO',date:'2022-03-15'},{name:'Floorplan_Domain_B2.pdf',type:'Floorplan',date:'2022-04-10'},{name:'Lease_Abstract_Domain.pdf',type:'Abstract',date:'2022-04-15'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Brandywine Realty',active:true},{role:'Tenant Advisor',name:'Priya Nair',active:true}]},
  {id:'l6',twsId:'hou1',name:'1000 Main Street',market:'Houston',sf:8500,rent:382500,leaseEnd:'2026-07-31',optionDate:null,advisor:'Marcus Chen',bu:'South Ops',util:38,status:'red',source:'Manual',costPerExp:980,headcount:40,
   documents:[{name:'Executed_Lease_Houston.pdf',type:'Lease',date:'2021-08-01'},{name:'Termination_Agreement_v4.docx',type:'Termination',date:'2026-05-01',delta:'Fee: 3 months gross rent · Move-out: Aug 1, 2026'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Brookfield Asset Mgmt',active:true},{role:'Tenant Advisor',name:'Marcus Chen',active:true},{role:'Legal Counsel',name:'Weil Gotshal',active:true}]},
  {id:'l7',twsId:'chi2',name:'350 N Orleans',market:'Chicago',sf:5200,rent:312000,leaseEnd:'2027-09-30',optionDate:null,advisor:'Priya Nair',bu:'Innovation Lab',util:91,status:'green',source:'Scribe',costPerExp:310,headcount:38,
   ti:{total:1560000,applied:520000,appliedMonthly:87000,expiry:'2028-04-15',mechanism:'credit',eligible:'Construction & fit-out; furniture; technology infrastructure'},
   documents:[{name:'Executed_Lease_350N.pdf',type:'Lease',date:'2026-04-15'},{name:'Abstract_350N.pdf',type:'Abstract',date:'2026-04-17'},{name:'Floorplan_350N.pdf',type:'Floorplan',date:'2025-12-01'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:false},{role:'Landlord',name:'Sterling Bay',active:false},{role:'Tenant Advisor',name:'Priya Nair',active:true}]},
  {id:'l8',twsId:'nyc3',name:'200 Park Avenue',market:'New York',sf:15000,rent:1800000,leaseEnd:'2026-06-30',optionDate:'2026-03-31',advisor:'Jordan Wells',bu:'Client Services',util:43,status:'red',source:'Scribe',costPerExp:2100,headcount:75,
   ti:{total:3750000,applied:450000,appliedMonthly:34000,expiry:'2027-06-30',mechanism:'credit',eligible:'Full fit-out; AV; furniture; data cabling'},
   documents:[{name:'Executed_Lease_200Park.pdf',type:'Lease',date:'2019-07-01'},{name:'CofO_200Park.pdf',type:'CofO',date:'2019-06-20'},{name:'Floorplan_200Park_FL8.pdf',type:'Floorplan',date:'2021-02-10'},{name:'Lease_Abstract_200Park.pdf',type:'Abstract',date:'2019-07-15'}],
   parties:[{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Tishman Speyer',active:true},{role:'Tenant Advisor',name:'Jordan Wells',active:true},{role:'Legal Counsel',name:'Sullivan & Cromwell',active:false}]},
];

const DEALS=[
  // Strategic Setting — persistent, recurring, never-closing
  {id:'d0',name:'Monthly Portfolio Review',property:'All Locations',market:'Portfolio-wide',sf:0,stage:'Persistent',settingType:'Strategic',lifecycle:'Persistent',velocity:10,nextDeadline:'2026-06-02',nextMilestone:'June accrual brief delivery',advisor:'Marcus Chen',status:'green',
   senseHandoff:{phase:1,status:'active',desc:'Culture brief pre-populates engagement scope at each Standard Setting creation'},
   parties:[{role:'Real Estate Lead',name:'Marcus Chen',active:true},{role:'CFO',name:'Sarah Kim',active:true},{role:'Finance Team',name:'Controller Office',active:true},{role:'RE Advisor',name:'Priya Nair',active:true}],
   documents:[{name:'May Accrual Brief',version:1,date:'2026-05-23',delta:'ASC 842 obligations current; 3 locations with June events',source:'SharePoint',url:'#'},{name:'Apr Review Minutes',version:1,date:'2026-04-28',source:'SharePoint',url:'#'}],
   sageInsights:[
     {type:'velocity',source:'Monthly Review · Sage Assembly',text:'June review package ready: 8 locations, ASC 842 obligations current. 3 events require finance attention — Penn Plaza base escalation +3% Jun 1, Willis Tower CAM true-up est. $42K, Houston termination fee accrual. Package assembled. Decision session can proceed without data prep.'},
     {type:'platform',source:'TI Monitoring · Strat',text:'$3.3M TI allowance at 200 Park Ave is at risk of lapsing by Jun 30, 2027 — 402 days remaining. Capex project initiation required within 60 days to utilise before expiry. This is the forward-looking risk the monthly review must address.'},
     {type:'market',source:'Strategic Setting · Decision Log',text:'Decision captured Apr 28: Hold on Boston for another quarter — headcount plan needs to firm up first. Austin expansion approved. Willis Tower drawdown authorized through Drywall & Finishes milestone ($5.24M cumulative).'},
     {type:'platform',source:'Setting Lifecycle · Sage',text:'200 Park Ave lease expires Jun 30, 2026 — 38 days remaining. No Standard Setting is open for this location. At close, the prior engagement context does not exist to pre-populate a new brief. Opening a Setting now preserves the decision history, experience baseline, and negotiation record for the renewal. Recommended action: open Standard Setting — 200 Park Ave Renewal or Exit.'},
   ]},
  {id:'d1',name:'1 Penn Plaza Renewal',property:'1 Penn Plaza',market:'New York',sf:45000,stage:'Active',settingType:'Standard',lifecycle:'Active',velocity:4,nextDeadline:'2026-06-30',nextMilestone:'Execute LOI',advisor:'Marcus Chen',status:'red',
   wexPhase:'design',phaseCompletion:{discovery:9,define:8,design:6,develop:0,delivery:0},
   experienceCommitment:'Community-First · CE-dominant. Cross-departmental collision zones on main circulation paths. Individual Produce with visual shielding (panel height ≥65"). Open collaboration ≥40% of floor plate. Engineering adjacency to Product team neighbourhood.',
   discoveryContext:{
     situation:'The Penn Plaza lease expires in 18 months. Engineering team adjacency to Product is essential and cannot be relocated. The June 30, 2026 option window creates urgency that is narrowing negotiating room.',
     senseSignals:['CE utilisation on Floor 14 has dropped from 61% to 19% following coffee station removal 6 weeks ago. Culture score is declining.','Trailing 90-day TWS: 68 — 12 points below portfolio median. Main drag: Community Exchange drift.','Technology team concentration sentiment: 7.1/10. Below 7.5 target — cognitive recovery need elevated.'],
     openQuestions:['Will Engineering accept a floor plate that separates them from Product by more than one floor?','What minimum CE zone area is required to restore pre-drift interaction rates?','Does the personal guarantee reflect a landlord risk posture change or a negotiating tactic?']
   },
   parties:[{role:'Tenant Advisor',name:'Marcus Chen',active:true},{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Vornado Realty',active:true},{role:'Legal Counsel',name:'Skadden LLP',active:false}],
   documents:[{name:'Draft Lease v3',version:3,date:'2026-04-28',delta:'Landlord added personal guarantee; free rent reduced from 6 to 4 months',source:'SharePoint',url:'#'},{name:'Draft Lease v2',version:2,date:'2026-04-14',source:'SharePoint',url:'#'},{name:'Term Sheet v1',version:1,date:'2026-03-22',source:'Drive',url:'#'}],
   sageInsights:[
     {type:'velocity',source:'Deal Velocity',text:'At current negotiation pace this deal misses the Jun 30 option window by ~11 days. Recommend 2× weekly advisor touchpoints and a landlord call this week to unblock LOI execution.'},
     {type:'market',source:'Market Data · NYC Midtown',text:'Class A asking rents in Midtown rose 4.2% YoY in Q1 2026. Your current $60/RSF position is 8% below market — negotiate from strength on free rent rather than base rent.'},
     {type:'platform',source:'Platform · Stage Members',text:'3 comparable Stage member renewals in Midtown (Q4 2025–Q1 2026) averaged 6.8 months free rent and $88/SF TI. Your current TI offer of $85/SF is below median — push for $92/SF to match peer outcomes.'},
     {type:'document',source:'Document Analysis · Draft Lease v3',text:'Personal guarantee added Apr 28 — Landlord added a personal guarantee clause not present in v2. Sage recommends rejection; substitute with a corporate parent guarantee to limit founder exposure.'},
   ],
   options:[
     {id:'opt-a',name:'Option A — Floors 14–15 Renewal',sf:22000,baseRent:60,ti:88,freeRent:'6 mo',
      fitScore:91,conformanceScore:87,culture:'Community-First',
      rbw:{communityExchange:88,communityReflect:73,groupExchange:94,groupProduce:89,individualProduce:71,individualReflect:66},
      highlight:'Strong Group Exchange and Community Exchange — Engineering adjacency and cross-team circulation well supported. Highest fit in shortlist.',
      flag:null},
     {id:'opt-b',name:'Option B — Floors 12–13 Alternative',sf:21500,baseRent:57,ti:80,freeRent:'4 mo',
      fitScore:74,conformanceScore:78,culture:'Community-First',
      rbw:{communityExchange:62,communityReflect:79,groupExchange:69,groupProduce:84,individualProduce:85,individualReflect:89},
      highlight:'Higher Individual Produce and Reflect — suited for focused work, lower community alignment.',
      flag:'Community Exchange zone placed off main circulation path — cross-departmental collision risk flagged by Sense.'},
     {id:'opt-c',name:'Option C — Floor 16 Expansion',sf:18000,baseRent:64,ti:72,freeRent:'3 mo',
      fitScore:58,conformanceScore:52,culture:'Community-First',
      rbw:{communityExchange:44,communityReflect:52,groupExchange:57,groupProduce:68,individualProduce:90,individualReflect:92},
      highlight:'Optimized for individual work — misaligned with Community-First archetype.',
      flag:'SF below brief minimum (18K vs 22K target). Conformance fails on density and collaboration zone requirements.'},
   ],
   decisionProposals:[
     {id:'dp1a',stage:'Scoping → Active',status:'confirmed',summary:'Proceed with Penn Plaza renewal on current floors — new market search suspended. Primary driver: Engineering team adjacency requirements and Jun 30, 2026 option window urgency.',confirmedAt:'2026-04-02',confirmedBy:'Marcus Chen'},
     {id:'dp1b',stage:'Active — current',status:'proposed',summary:'Option A (Floors 14–15) leads over Options B and C based on Sense fit scoring. Key factors: Community Exchange zone on main circulation (+29 pts vs Option B), $88/SF TI, 6 months free rent. Pending resolution on personal guarantee clause before LOI execution.',confirmedAt:null,confirmedBy:null},
   ],
   stratBrief:{headcount:450,targetSF:22000,costPerSFTarget:95,archetype:'Community-First',criteria:['Open collaboration zones: minimum 40% of floor plate','Engineering adjacency to Product — team neighbourhood design','Community Exchange placement on main circulation path','Natural light: ≥70% of workstations within 30ft of window']},
   },
  {id:'d2',name:'Austin HQ Expansion',property:'2845 Domain Drive',market:'Austin',sf:12000,stage:'Active',settingType:'Standard',lifecycle:'Active',velocity:7,nextDeadline:'2026-05-08',nextMilestone:'Countersign LOI',advisor:'Priya Nair',status:'yellow',
   wexPhase:'design',phaseCompletion:{discovery:9,define:9,design:7,develop:0,delivery:0},
   experienceCommitment:'Collaborative-Hybrid · CE and GE dominant. Central atrium as primary Community Exchange anchor. Open team bays ≥60% of floor plate. Minimum 800 SF bookable commons for flexible overflow. Dedicated focus rooms at 1 per 15 staff.',
   discoveryContext:{
     situation:'Rapid Austin headcount growth (180 people in 12 months) is outpacing current floor plate capacity. Overflow into conference rooms is suppressing Community Exchange interaction and creating booking pressure.',
     senseSignals:['Austin trailing 90-day utilisation: 84% — above threshold. Peak-day capacity breach on 3 of last 4 Tuesdays.','Group Produce booking rate: 91% across all rooms. Scheduled collaboration is crowding out unplanned exchange.','TWS score: 74 — above median. Primary risk is capacity, not settings quality.'],
     openQuestions:['Is the 12,000 SF target sufficient for the 18-month headcount plan or will a further tranche be required?','Does Domain Drive adjacency to existing HQ enable shared Community Exchange settings?']
   },
   parties:[{role:'Tenant Advisor',name:'Priya Nair',active:true},{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Brandywine Realty',active:true}],
   documents:[{name:'LOI Austin v2',version:2,date:'2026-04-30',delta:'TI allowance $65/SF confirmed; parking at 40 spaces',source:'Drive',url:'#'},{name:'LOI Austin v1',version:1,date:'2026-04-18',source:'Drive',url:'#'}],
   sageInsights:[
     {type:'market',source:'Market Data · Austin CBD',text:'Austin CBD vacancy reached 18.4% in Q1 2026 — highest since 2013. Tenant leverage is strong. Sage suggests requesting a 3-month free rent extension or a $5/SF TI increase before countersigning.'},
     {type:'document',source:'Document Analysis · LOI v2',text:'TI at $65/SF confirmed and parking secured at 40 spaces — both terms aligned with LOI v1 ask. No material adverse changes detected. Countersignature recommended.'},
     {type:'platform',source:'Platform · Stage Members',text:'4 recent Austin expansions by Stage members (6,000–15,000 SF) averaged $68/SF TI. Your current $65/SF is slightly below median; minor upside available if timeline permits.'},
   ],
   options:[
     {id:'opt-d',name:'North Wing — Primary Option',sf:12000,baseRent:42,ti:65,freeRent:'3 mo',
      fitScore:88,conformanceScore:92,culture:'Collaborative-Hybrid',
      rbw:{communityExchange:91,communityReflect:74,groupExchange:86,groupProduce:88,individualProduce:69,individualReflect:62},
      highlight:'Highest fit score of shortlisted options. Central atrium drives Community Exchange. Selected for LOI v2.',
      flag:null},
     {id:'opt-e',name:'South Wing — Alternative',sf:11500,baseRent:40,ti:65,freeRent:'3 mo',
      fitScore:76,conformanceScore:81,culture:'Collaborative-Hybrid',
      rbw:{communityExchange:71,communityReflect:80,groupExchange:77,groupProduce:83,individualProduce:78,individualReflect:75},
      highlight:'Balanced across setting types — good general-purpose floor plate. Lower Community Exchange alignment than North Wing.',
      flag:null},
   ],
   decisionProposals:[
     {id:'dp2a',stage:'Scoping → Active',status:'confirmed',summary:'Proceed with 2845 Domain Drive over 3 competing sites. Domain Drive wins on Community Exchange alignment, parking ratio (40 spaces), and proximity to Austin HQ cluster. CFO approved budget at $42/SF base.',confirmedAt:'2026-04-23',confirmedBy:'Dev Patel'},
     {id:'dp2b',stage:'Active — current',status:'proposed',summary:'North Wing selected over South Wing — Sense fit score 88 vs 76, superior Community Exchange placement. LOI v2 at $65/SF TI proceeds. Decision pending countersignature confirmation.',confirmedAt:null,confirmedBy:null},
   ],
   stratBrief:{headcount:180,targetSF:12000,costPerSFTarget:55,archetype:'Collaborative-Hybrid',criteria:['Open team bays: minimum 60% of floor plate','Dedicated focus rooms: 1 per 15 staff','Community Exchange on main entry path','Flexible overflow: minimum 800 SF bookable commons']},
   },
  {id:'d3',name:'SF Office Rightsizing',property:'444 Market Street',market:'San Francisco',sf:-7000,stage:'Scoping',settingType:'Standard',lifecycle:'Scoping',velocity:8,nextDeadline:'2026-06-01',nextMilestone:'Identify sublease candidates',advisor:'Jordan Wells',status:'green',
   wexPhase:'discovery',phaseCompletion:{discovery:5,define:0,design:0,develop:0,delivery:0},
   experienceCommitment:null,
   discoveryContext:{
     situation:'444 Market Street is carrying 100% of its lease obligation against 47% trailing utilisation. The lease has 14 months remaining. The question is whether to sublet, exit, or repurpose — not whether to retain the full floor plate.',
     senseSignals:['Trailing 90-day utilisation: 47% — lowest in portfolio.','Peak-day utilisation: 63% — 7,000 SF is never needed to meet peak demand.','TWS: 43, Settings Fidelity 28%. Space is not delivering on design intent even when occupied.'],
     openQuestions:['Is there appetite to repurpose the 7,000 SF for a specific team function before subletting?','What is the landlord\'s position on subletting — is consent straightforward in this market?','Does the rightsizing trigger a reassessment of the SF market presence strategy?']
   },
   parties:[{role:'Tenant Advisor',name:'Jordan Wells',active:true},{role:'Tenant',name:'Acme Corp',active:true}],
   documents:[{name:'Rightsizing Analysis',version:1,date:'2026-04-25',source:'Drive',url:'#'}],
   sageInsights:[
     {type:'market',source:'Market Data · SF Financial District',text:'SF Financial District sublease availability is at a record 28.2%. High supply means sublease pricing will be competitive — target $38–42/SF gross to attract qualified subtenants within 60 days.'},
     {type:'platform',source:'Platform · Stage Members',text:'Trailing 90-day utilisation at 444 Market is 47% — the lowest in your portfolio. Sage estimates disposing 7,000 SF saves $805K annually with no peak-day capacity impact based on observed utilisation patterns.'},
     {type:'velocity',source:'Deal Velocity',text:'Scout has identified 2 qualified sublease candidates. Both are Stage-platform occupiers in growth mode — warm introduction available. Recommend scheduling site visits within the next 10 days.'},
   ]},
  {id:'d4',name:'Houston Exit Strategy',property:'1000 Main Street',market:'Houston',sf:8500,stage:'Closing',settingType:'Standard',lifecycle:'Closing',velocity:6,nextDeadline:'2026-05-15',nextMilestone:'Execute Termination Agreement',advisor:'Marcus Chen',status:'yellow',
   wexPhase:'develop',phaseCompletion:{discovery:10,define:9,design:7,develop:6,delivery:0},
   experienceCommitment:'Exit engagement — no forward experience commitment. Prior Houston brief recorded: Individual Produce dominant, Energy Corridor cluster proximity. Lessons: inadequate Reflect settings contributed to sustained Culture score decline in final 6 months.',
   discoveryContext:{
     situation:'The Houston Energy Corridor location is being exited. Headcount has been frozen and relocated. The engagement is termination negotiation — not setting discovery.',
     senseSignals:['Houston utilisation: 22% over trailing 90 days — well below minimum viable threshold.','Team relocation to Austin substantially complete. No active occupants requiring settings coverage.'],
     openQuestions:['Will move-out extension to Aug 1 create any employee disruption for teams relocated post-close?']
   },
   parties:[{role:'Tenant Advisor',name:'Marcus Chen',active:true},{role:'Tenant',name:'Acme Corp',active:true},{role:'Landlord',name:'Brookfield Asset Mgmt',active:true},{role:'Legal Counsel',name:'Weil Gotshal',active:true}],
   documents:[{name:'Termination Agreement v4',version:4,date:'2026-05-01',delta:'Fee reduced to 3 months gross rent; move-out extended to Aug 1',source:'SharePoint',url:'#'},{name:'Termination Agreement v3',version:3,date:'2026-04-20',source:'SharePoint',url:'#'}],
   sageInsights:[
     {type:'document',source:'Document Analysis · Termination v4',text:'Fee reduced to 3 months gross rent (from 4 months in v3) and move-out extended to Aug 1 — both material improvements. 35 fields abstracted across parties, financials, options, and legal provisions. Sage recommends approval.'},
     {type:'market',source:'Market Data · Houston Energy Corridor',text:'Houston Energy Corridor vacancy is 22.1%. Landlord has strong incentive to conclude quickly — Sage confirms your negotiating position is strong heading into execution.'},
   ]},
  {id:'d5',name:'Chicago Lab Renewal',property:'350 N Orleans',market:'Chicago',sf:5200,stage:'Closed',settingType:'Standard',lifecycle:'Closed',velocity:9,nextDeadline:'2027-09-30',nextMilestone:'Lease commencement Oct 2027',advisor:'Priya Nair',status:'green',
   wexPhase:'delivery',phaseCompletion:{discovery:10,define:10,design:9,develop:9,delivery:5},
   experienceCommitment:'Community-First · CE and GE dominant. River-facing floor plate — cross-departmental collision on main circulation path. Individual Produce with natural light priority. No acoustic enclosures blocking CE sightlines.',
   discoveryContext:{
     situation:'Chicago Innovation Lab renewal was opened in March 2026 following lease expiry review. Strong TWS performance (82) and tight market made renewal the dominant strategy from Discovery.',
     senseSignals:['Chicago Innovation Lab TWS: 82 — strongest performer in portfolio.','CE utilisation: 71% — above 65% portfolio benchmark.','Headcount stable at 52 — footprint well-matched.'],
     openQuestions:[]
   },
   outcomeRecord:{
     occupancyDate:'2026-04-15',
     designHypothesisRef:'Community-First, CE-dominant configuration on river-facing floor plate. CE target: 75%. GP target: 78%.',
     initialSenseReadings:{CE:71,CR:68,GE:74,GP:81,IP:79,IR:65},
     capacityLeakageInitial:18,
     nudgeSchedule:[{day:30,status:'fired',firedAt:'2026-05-15',summary:'CE: 71% (target: 75%). GP: 81% — above target. Overall within expected range for first 30 days. No corrective action recommended.'},{day:90,status:'pending',benchmarkContext:'For Community-First occupiers in comparable floor plates (N=14): median CE at 90d is 74%. Top quartile achieved 85%+ by prioritising main-path CE placement. Your 30-day CE reading of 71% is 3pts below median — monitor circulation path activation.'},{day:180,status:'pending'},{day:365,status:'pending'}]
   },
   parties:[{role:'Tenant Advisor',name:'Priya Nair',active:true},{role:'Tenant',name:'Acme Corp',active:false},{role:'Landlord',name:'Sterling Bay',active:false}],
   documents:[{name:'Executed Lease — 350 N Orleans',version:1,date:'2026-04-15',source:'SharePoint',url:'#'},{name:'Scribe Abstract — 350 N Orleans',version:1,date:'2026-04-17',source:'Drive',url:'#'}],
   sageInsights:[
     {type:'platform',source:'Platform · Stage Members',text:'Chicago Innovation Lab renewal closed at $60/SF gross — 6% below the River North median. 12 months free rent achieved, the highest in any Stage member renewal this quarter. Strong outcome.'},
     {type:'velocity',source:'Deal Velocity',text:'This deal closed at velocity 9/10 — well above the Stage platform median of 6.2 for similar transactions. Sage attributes the outcome to early brief clarity and structured negotiation pacing.'},
   ]},
];

const CAPITAL_PROJECTS_V2=[
  {id:'cp1',name:'Willis Tower — Chicago Buildout',location:'Willis Tower',market:'Chicago',type:'Landlord Buildout',pm:'Layne Associates',totalBudget:8000000,committed:8000000,invoiced:5240000,paid:4800000,started:'2025-10-01',targetComplete:'2026-09-15',drawdownCadence:'Weekly',lastDrawdown:'2026-05-16',status:'on-track',
   burnAlert:false, // paid 60% / timeline 57% — on track; no alert triggered
   changeOrders:[
     {id:'co1',desc:'HVAC upgrade — tenant specification change (increased capacity Floor 42)',amount:142000,status:'approved',approvedAt:'2026-03-15',experienceImpact:false},
     {id:'co2',desc:'Additional data infrastructure cabling runs — Floor 42 expansion scope',amount:88000,status:'approved',approvedAt:'2026-04-20',experienceImpact:false},
     {id:'co3',desc:'Drywall scope increase — partition reconfiguration request',amount:34000,status:'pending',approvedAt:null,experienceImpact:true,experienceImpactDesc:'Partition reconfiguration in Zone 3 modifies Individual Produce visual shielding specified in the Define brief. IP panel height may fall below the 65" minimum committed in the experience brief. Three prior change orders of this type resulted in IP performance gaps at 90 days.',experienceAcknowledgmentRequired:true,affectedZones:['Zone 3 — Individual Produce']},
   ],
   dayOneReadiness:[
     {item:'Certificate of Occupancy issued',done:false,due:'2026-09-01',type:'operational'},
     {item:'MEP systems commissioned & signed off',done:false,due:'2026-08-15',type:'operational'},
     {item:'FF&E delivery schedule confirmed',done:true,due:'2026-08-01',type:'operational'},
     {item:'IT infrastructure & data cabling sign-off',done:false,due:'2026-08-20',type:'operational'},
     {item:'Punchlist reduced to ≤15 open items',done:false,due:'2026-09-10',type:'operational'},
     {item:'Lease commencement notice issued',done:false,due:'2026-09-15',type:'operational'},
     {item:'Community Exchange: social anchor features installed and activated',done:false,due:'2026-09-01',type:'experience'},
     {item:'Group Produce: AV tested at hybrid equity standard',done:false,due:'2026-09-01',type:'experience'},
     {item:'Individual Produce: visual screening confirmed at ≥65" panel height',done:false,due:'2026-09-01',type:'experience'},
     {item:'Individual Reflect: biophilic elements installed, daylight access confirmed',done:false,due:'2026-09-01',type:'experience'},
   ],
   milestones:[{name:'Permit & Design',due:'2025-11-30',status:'Done'},{name:'Structural Demo',due:'2026-01-15',status:'Done'},{name:'MEP Rough-In',due:'2026-03-31',status:'Done'},{name:'Drywall & Finishes',due:'2026-06-30',status:'In Progress'},{name:'FF&E Install',due:'2026-08-01',status:'Pending'},{name:'Punchlist & CO',due:'2026-09-15',status:'Pending'}]},
  {id:'cp2',name:'Austin HQ Expansion — Fit-Out',location:'2845 Domain Drive',market:'Austin',type:'TI Credit Buildout',pm:'Internal PM',totalBudget:2800000,committed:700000,invoiced:700000,paid:700000,started:'2026-04-01',targetComplete:'2026-12-31',drawdownCadence:'Monthly',lastDrawdown:'2026-04-30',status:'planning',
   burnAlert:false,
   changeOrders:[],
   dayOneReadiness:[
     {item:'Contractor selection complete',done:false,due:'2026-06-30'},
     {item:'Design drawings approved',done:false,due:'2026-05-31'},
     {item:'TI credit drawdown schedule agreed',done:false,due:'2026-06-15'},
   ],
   milestones:[{name:'Design & Planning',due:'2026-05-31',status:'In Progress'},{name:'Contractor Selection',due:'2026-06-30',status:'Pending'},{name:'Construction',due:'2026-10-31',status:'Pending'},{name:'FF&E & Commissioning',due:'2026-12-31',status:'Pending'}]},
];

const ACTIONS=[
  {id:'a1',type:'Alert',priority:'P1',title:'Option exercise window opens in 59 days — 1 Penn Plaza',desc:'Renewal option must be exercised by Jun 30, 2026. Negotiation pace is below velocity threshold. Immediate advisor action required.',due:'2026-06-30',owner:'Marcus Chen',asset:'1 Penn Plaza',agent:'Sage',status:'Open',daysOpen:3,resolutionNote:null,escalated:false},
  {id:'a2',type:'Decision',priority:'P1',title:'Counter personal guarantee clause — 1 Penn Plaza v3',desc:'Scribe detected personal guarantee added in Draft Lease v3. Strat recommends rejection with corporate guarantee alternative. Decision required before next landlord call.',due:'2026-05-06',owner:'Marcus Chen',asset:'1 Penn Plaza',agent:'Scribe',status:'Open',daysOpen:1,resolutionNote:null,escalated:false},
  {id:'a3',type:'Task',priority:'P1',title:'Countersign Austin LOI by May 8',desc:'LOI v2 from Brandywine received Apr 30. TI at $65/SF accepted. Parking confirmed. Advisor countersignature required.',due:'2026-05-08',owner:'Priya Nair',asset:'2845 Domain Drive',agent:'System',status:'In Progress',daysOpen:2,resolutionNote:'Countersigned and sent back to Brandywine on May 5. Awaiting their co-signature.',escalated:false},
  {id:'a4',type:'Alert',priority:'P1',title:'200 Park Ave option deadline MISSED — immediate action required',desc:'Option exercise deadline was March 31, 2026. No action recorded. Lease expires June 30, 2026. Escalated for legal review.',due:'2026-03-31',owner:'Jordan Wells',asset:'200 Park Avenue',agent:'System',status:'Overdue',daysOpen:32,resolutionNote:null,escalated:true},
  {id:'a5',type:'Review',priority:'P2',title:'Approve Scribe abstract — Houston Termination Agreement',desc:'Scribe abstracted Houston termination agreement. 35 fields extracted across parties, term, financials, options, and legal provisions. Human review required before Portfolio Spine publish.',due:'2026-05-07',owner:'Marcus Chen',asset:'1000 Main Street',agent:'Scribe',status:'Open',daysOpen:1},
  {id:'a6',type:'Task',priority:'P2',title:'Execute Houston termination via DocuSign',desc:'Termination Agreement v4 finalized. Brookfield accepted reduced fee and extended move-out to Aug 1. DocuSign routing required by May 15.',due:'2026-05-15',owner:'Marcus Chen',asset:'1000 Main Street',agent:'Human',status:'In Progress',daysOpen:4},
  {id:'a7',type:'Decision',priority:'P2',title:'Approve SF rightsizing sublease strategy',desc:'Strat scenario: disposing 7,000 SF at 444 Market saves $805K annually. Two sublease candidates identified by Scout. Approval required before outreach.',due:'2026-05-14',owner:'Jordan Wells',asset:'444 Market Street',agent:'Strat',status:'Open',daysOpen:0},
  {id:'a8',type:'Task',priority:'P2',title:'Refresh Willis Tower renewal scenario with Q1 market data',desc:'Strat renewal model last updated Feb 2026. Chicago CBD vacancy shifted +180bps. Refresh before Q3 decision.',due:'2026-05-20',owner:'Priya Nair',asset:'Willis Tower',agent:'Strat',status:'Open',daysOpen:0},
  {id:'a9',type:'Alert',priority:'P3',title:'SF utilization at 47% — right-sizing recommendation available',desc:'Trailing 90-day utilization at 444 Market is 47%. Strat estimates 20% SF reduction saves $414K annually with no peak-day capacity impact.',due:'2026-06-01',owner:'Jordan Wells',asset:'444 Market Street',agent:'Strat',status:'Open',daysOpen:5},
  {id:'a10',type:'Task',priority:'P3',title:'Schedule Q2 portfolio review with CFO',desc:'Sage flagged CFO quarterly briefing overdue by 2 weeks. Board deck available via Scribe.',due:'2026-05-16',owner:'Marcus Chen',asset:'Portfolio',agent:'Sage',status:'Open',daysOpen:8},
  {id:'a11',type:'Task',priority:'P1',title:'Restore social anchor — 1 Penn Plaza Floor 14 Community Exchange',desc:'Coffee station removal 6 weeks ago caused Settings Drift. CE utilization fell from 61% to 19%. Culture score decline followed. Restore coffee station and clear hoarding from circulation path before the next lease strategy call.',due:'2026-05-21',owner:'Marcus Chen',asset:'1 Penn Plaza',agent:'Sage',status:'Open',daysOpen:2},
  {id:'a12',type:'Decision',priority:'P1',title:'Create Individual Reflect setting — 1 Penn Plaza + 200 Park Ave',desc:'Both locations have zero functioning IR settings. Wellness areas converted to IP overflow. Technology and Finance populations average 4+ hours AI-assisted workflow/day — elevated cognitive recovery need. Minimum 380 SF each. Decision required before portfolio settings review.',due:'2026-05-28',owner:'Marcus Chen',asset:'Portfolio',agent:'Sage',status:'Open',daysOpen:0},
  {id:'a13',type:'Task',priority:'P2',title:'Replicate 30 Hudson Yards Floor 12 CE configuration',desc:'30 Hudson CE achieves 67% cross-departmental interaction (benchmark 45–60%), 11.4-min dwell median, 31% RWO draw. Replicate Settings Profile (kit-of-parts furniture, 9:15 AM cross-team ritual, writable wall) at 1 Penn Plaza and 444 Market St CE settings.',due:'2026-06-15',owner:'Jordan Wells',asset:'Portfolio',agent:'Sage',status:'Open',daysOpen:0},
  {id:'a14',type:'Decision',priority:'P2',title:'Export Settings Scorecard as TI brief — 200 Park Ave',desc:'Design Fidelity 28% (lowest in portfolio), TWS 43 and declining, lease expired. Settings evidence supports a TI allowance negotiation (restore settings to spec) or an evidence-based exit. Export scorecard before next landlord call.',due:'2026-05-30',owner:'Jordan Wells',asset:'200 Park Avenue',agent:'Sage',status:'Open',daysOpen:0},
  {id:'a15',type:'Alert',priority:'P3',title:'TI use window advisory — 200 Park Ave · $3.3M to plan',desc:'TI allowance of $3.75M at 200 Park Ave expires Jun 30, 2027 (13 months). Only $450K of $3.75M applied to date. At current trailing rate ($34K/mo), ~$3.3M will lapse unrecovered. P3 Advisory: initiate capex planning within the next quarter to protect the full allowance.',due:'2026-07-31',owner:'Jordan Wells',asset:'200 Park Avenue',agent:'Scribe',status:'Open',daysOpen:1,resolutionNote:null,escalated:false},
  {id:'a16',type:'Alert',priority:'P2',title:'TI allowance — 350 N Orleans · monitor application rate',desc:'$1.04M remaining TI allowance at 350 N Orleans. Use window expires Apr 15, 2028. $87K/month application rate required to fully utilise. Current renovation planning pace is adequate — monitor quarterly.',due:'2026-12-31',owner:'Priya Nair',asset:'350 N Orleans',agent:'Scribe',status:'Open',daysOpen:0,resolutionNote:null,escalated:false},
  {id:'a17',type:'Task',priority:'P2',title:'Willis Tower drawdown authorisation — week of May 19',desc:'Weekly drawdown request from Layne Associates: $440K against Drywall & Finishes milestone. Cumulative drawdown: $5.24M of $8M approved budget. On track. Authorisation required before Friday disbursement.',due:'2026-05-24',owner:'Priya Nair',asset:'Willis Tower',agent:'Scribe',status:'Open',daysOpen:0,resolutionNote:null,escalated:false},
  {id:'a18',type:'Review',priority:'P2',title:'Monthly lease review — May accrual brief ready',desc:'Strat assembled May accrual view: 8 locations, ASC 842 obligations current, 3 locations have events affecting June accrual (Penn Plaza escalation, Willis OpEx true-up, Houston termination fee). Brief available for finance team review before month-end close.',due:'2026-05-28',owner:'Marcus Chen',asset:'Portfolio',agent:'Strat',status:'Open',daysOpen:0,resolutionNote:null,escalated:false},
];

// ── Canonical portfolio TWS — SINGLE SOURCE OF TRUTH ──────────────────────────
// The composite is the RBW-I weighted average of the three pillars. Every surface
// (Status Brief hero + narrative, Workplace Analytics, weekly digest, Weight
// Studio) reads these, so the headline TWS can never disagree with its own
// pillars or drift across pages. 71·0.30 + 58·0.40 + 63·0.30 = 63.4 → 63.
// EW2/EW5 — the 6 measurable RBW-I standards (slug ↔ label), shared by the
// signal-import and outcome-verification UIs.
const EXPERIENCE_STDS=[['co_presence','Co-presence'],['belonging','Belonging'],['focus_fit','Focus space-to-intent fit'],['env_satisfaction','Environment satisfaction'],['programme_adoption','Programme adoption'],['utilisation','Space utilisation quality']];
const TWS_PILLARS={flow:71,culture:58,perf:63};
const TWS_WEIGHTS={flow:0.30,culture:0.40,perf:0.30};
const TWS_COMPOSITE=Math.round(
  TWS_PILLARS.flow*TWS_WEIGHTS.flow+TWS_PILLARS.culture*TWS_WEIGHTS.culture+TWS_PILLARS.perf*TWS_WEIGHTS.perf
); // 63

const CAPITAL_PROJECTS=[
  {id:'cp1',name:'Willis Tower — Chicago Buildout',location:'Willis Tower',market:'Chicago',type:'Landlord Buildout',pm:'Layne Associates',totalBudget:8000000,committed:8000000,invoiced:5240000,paid:4800000,started:'2025-10-01',targetComplete:'2026-09-15',drawdownCadence:'Weekly',lastDrawdown:'2026-05-16',status:'on-track',
   milestones:[{name:'Permit & Design',due:'2025-11-30',status:'Done'},{name:'Structural Demo',due:'2026-01-15',status:'Done'},{name:'MEP Rough-In',due:'2026-03-31',status:'Done'},{name:'Drywall & Finishes',due:'2026-06-30',status:'In Progress'},{name:'FF&E Install',due:'2026-08-01',status:'Pending'},{name:'Punchlist & CO',due:'2026-09-15',status:'Pending'}]},
  {id:'cp2',name:'Austin HQ Expansion — Fit-Out',location:'2845 Domain Drive',market:'Austin',type:'TI Credit Buildout',pm:'Internal PM',totalBudget:2800000,committed:700000,invoiced:700000,paid:700000,started:'2026-04-01',targetComplete:'2026-12-31',drawdownCadence:'Monthly',lastDrawdown:'2026-04-30',status:'planning',
   milestones:[{name:'Design & Planning',due:'2026-05-31',status:'In Progress'},{name:'Contractor Selection',due:'2026-06-30',status:'Pending'},{name:'Construction',due:'2026-10-31',status:'Pending'},{name:'FF&E & Commissioning',due:'2026-12-31',status:'Pending'}]},
];

const AGENTS=[
  {id:'scout',name:'Scout',fn:'Market research, comp analysis, site selection',status:'Active',color:'#2563EB',actions24:47,actions7:312,actions30:1204,confidence:91,credExpiry:'2026-06-01',lastAction:'2 min ago — Comp scan: Chicago CBD 25–35K SF',perms:[{domain:'Market Data',r:true,w:false,d:false},{domain:'Portfolio Spine',r:true,w:true,d:false},{domain:'Setting',r:true,w:false,d:false}]},
  {id:'strat',name:'Strat',fn:'Portfolio scenarios, occupancy forecasting, renewal economics',status:'Active',color:'#7C3AED',actions24:12,actions7:89,actions30:347,confidence:88,credExpiry:'2026-06-01',lastAction:'18 min ago — Occupancy forecast: 444 Market St, 6-month',perms:[{domain:'Portfolio Spine',r:true,w:true,d:false},{domain:'Financial Surface',r:true,w:false,d:false},{domain:'Analytics',r:true,w:true,d:false}]},
  {id:'sage',name:'Sage',fn:'Primary conversational interface — unified retrieval, decision capture, proactive surfacing across all Settings',status:'Active',color:'#059669',actions24:31,actions7:198,actions30:742,confidence:86,credExpiry:'2026-06-01',lastAction:'5 min ago — Alert: 1 Penn Plaza velocity below threshold',perms:[{domain:'Setting',r:true,w:true,d:false},{domain:'CKE',r:true,w:true,d:false},{domain:'Action Rail',r:true,w:true,d:false}]},
  {id:'scribe',name:'Scribe',fn:'Lease abstraction, document drafting, redline monitoring. All publish actions require human gate.',status:'Active',color:'#D97706',actions24:8,actions7:41,actions30:163,confidence:94,credExpiry:'2026-06-01',lastAction:'1 hr ago — Redline delta: Houston Termination v4',perms:[{domain:'Portfolio Spine',r:true,w:true,d:false},{domain:'Setting',r:true,w:true,d:false},{domain:'Documents',r:true,w:true,d:false}]},
];

const MEETINGS=[
  {id:'m1',title:'1 Penn Plaza Renewal Strategy Call',date:'2026-04-28',deal:'1 Penn Plaza Renewal',duration:'47 min',participants:['Marcus Chen','Sarah Kim (Acme)','Tom Vornado (Landlord)'],entities:['free rent','personal guarantee','market rent','45,000 SF'],decisions:['Reject personal guarantee — propose corporate guarantee','Counter with 5 months free rent (down from 6)'],commitments:['Marcus: Revised term sheet by May 3','Tom: Present to ownership by May 7'],sentiment:65,tags:['Negotiation','Critical','Landlord'],excerpt:'"The personal guarantee is a non-starter. We\'ve been here 6 years with zero defaults. A corporate guarantee satisfies Vornado\'s risk requirements." — Sarah Kim'},
  {id:'m2',title:'Austin Expansion — Site Tour Debrief',date:'2026-04-22',deal:'Austin HQ Expansion',duration:'35 min',participants:['Priya Nair','Jordan Wells','Dev Patel (Acme)'],entities:['Domain Drive','12,000 SF','TI allowance','parking'],decisions:['Proceed with 2845 Domain over 3 competing sites','Target $65/SF TI — non-negotiable per CFO'],commitments:['Priya: Submit LOI by Apr 25','Dev: Confirm headcount projections by Apr 24'],sentiment:88,tags:['Site Tour','Austin','Expansion'],excerpt:'"The Domain location wins on all three criteria. Let\'s move fast before we lose it." — Dev Patel'},
  {id:'m3',title:'Q1 Portfolio Review — CFO Briefing',date:'2026-04-15',deal:'Portfolio',duration:'62 min',participants:['Shegun Holder','Rachel Torres (CFO)','Marcus Chen'],entities:['lease liability','ASC 842','utilization','sublease'],decisions:['Approve SF sublease strategy (7,000 SF)','Freeze Houston headcount pending exit'],commitments:['Rachel: Board deck approval by Apr 30','Marcus: Houston timeline by Apr 22'],sentiment:72,tags:['CFO','Portfolio Review','Board'],excerpt:'"The 62% utilization against 100% obligation is the story I need for the board. That $4M gap is real money." — Rachel Torres'},
  {id:'m4',title:'Houston Exit — Landlord Negotiation',date:'2026-05-01',deal:'Houston Exit Strategy',duration:'28 min',participants:['Marcus Chen','Weil Gotshal','Brookfield Asset Mgmt'],entities:['termination fee','move-out date','3 months gross rent','Aug 1'],decisions:['Accept 3-month termination fee (down from 4)','Move-out extended to August 1'],commitments:['Brookfield: Countersigned by May 5','Marcus: DocuSign routing by May 15'],sentiment:79,tags:['Negotiation','Exit','Houston'],excerpt:'"Three months is acceptable. August 1 move-out works — gives us time to prep for the next tenant." — Brookfield Rep'},
];

/* ── HELPERS ────────────────────────────────────────────────── */
const fmt=n=>n>=1e9?`$${(n/1e9).toFixed(1)}B`:n>=1e6?`$${(n/1e6).toFixed(1)}M`:n>=1e3?`$${(n/1e3).toFixed(0)}K`:`$${n}`;
// F-CUR-01 — currency-aware money formatting. fmt() stays $-only (back-compat);
// fmtCur(n,ccy) prefixes the right symbol. Money is only meaningful within ONE
// currency — rentByCcy() groups a location list per currency so no surface sums
// across currencies (a USD-only portfolio still shows a single total, unchanged).
const CCY_SYM={USD:'$',GBP:'£',EUR:'€',JPY:'¥',SGD:'S$',AUD:'A$',CAD:'C$',BRL:'R$',AED:'AED ',INR:'₹',CHF:'CHF ',HKD:'HK$',CNY:'¥',NZD:'NZ$'};
const ccySym=c=>CCY_SYM[(c||'USD').toUpperCase()]||((c||'USD').toUpperCase()+' ');
const fmtCur=(n,c)=>{const s=ccySym(c),a=Math.abs(n||0);return a>=1e9?`${s}${(n/1e9).toFixed(1)}B`:a>=1e6?`${s}${(n/1e6).toFixed(1)}M`:a>=1e3?`${s}${(n/1e3).toFixed(0)}K`:`${s}${n||0}`;};
const rentByCcy=ls=>{const b={};(ls||[]).forEach(l=>{const c=(l.currency||'USD').toUpperCase();b[c]=b[c]||{rent:0,sf:0,n:0};b[c].rent+=(l.rent||0);b[c].sf+=(l.sf||0);b[c].n++;});const cs=Object.keys(b).sort();return{byCcy:b,currencies:cs,mixed:cs.length>1};};

// ── Canonical task helpers (SINGLE SOURCE OF TRUTH for action counts) ─────────
// Mirror src/services/action_metrics.py so every surface (sidebar badge, Status
// Brief, Action Rail, Sage) counts "outstanding tasks" the SAME way. Lifecycle
// (Done/In Progress/Open) is separate from timeliness; "overdue" is DERIVED
// (past-due AND not done), NOT a status — so counting status==='Open' no longer
// silently drops in-progress/overdue work.
const _TASK_DONE=new Set(['Done','Resolved','Closed','Completed']);
const taskIsDone=a=>_TASK_DONE.has(a.status);
const taskIsOutstanding=a=>!taskIsDone(a);                       // not done — headline
const _today=()=>new Date().toISOString().slice(0,10);
const taskIsOverdue=(a,today=_today())=>!taskIsDone(a)&&(a.status==='Overdue'||(!!a.due&&String(a.due).slice(0,10)<today));
const agentBadge=a=>`pill badge-${a.toLowerCase()}`;
const statusRingClass={red:'ring-red',yellow:'ring-yellow',green:'ring-green'};
const priorityColors={P1:{bg:'#ef4444',text:'#dc2626',light:'#fef2f2',border:'#fca5a5'},P2:{bg:'#f59e0b',text:'#d97706',light:'#fffbeb',border:'#fcd34d'},P3:{bg:'#3b82f6',text:'#2563eb',light:'#eff6ff',border:'#93c5fd'}};
const stageColors={Scoping:'#2563EB',Active:'#7C3AED',Closing:'#f59e0b',Closed:'#22c55e','Renewal Pending':'#E8813A',Persistent:'#059669'};
const STAGES=['Scoping','Active','Closing','Closed','Renewal Pending'];
// WEX-1 — Five-phase Discovery-to-Delivery architecture
const WEX_PHASES=[
  {id:'discovery',label:'Discovery',color:'#4A7FA5',desc:'Understand the situation before acting on it'},
  {id:'define',label:'Define',color:'#E8A838',desc:'Convert intentions into structured criteria'},
  {id:'design',label:'Design',color:'#7BAE8A',desc:'Evaluate options against brief and experience commitment'},
  {id:'develop',label:'Develop',color:'#1C3F6E',desc:'Track physical journey as an experience delivery tracker'},
  {id:'delivery',label:'Delivery',color:'#C1714A',desc:'Occupy the space and initiate the feedback loop'},
];
const wexPhaseColor=id=>(WEX_PHASES.find(p=>p.id===id)||{color:'#6B7280'}).color;
const wexPhaseLabel=id=>(WEX_PHASES.find(p=>p.id===id)||{label:id}).label;

/* ── EXP-FOCUS (PRD Addendum v1.1) — health & One Driver model (§4) ──────────
   "Focus by default. Escalate by exception." A Setting's health is the severity
   of its highest current driver. computeOneDriver is the deterministic ranking
   stub (§4.2/§8-2): Sentry > Strat > Sense > Scout > Sage, soonest-deadline
   tie-break. Pure client-side functions over EXISTING agent outputs only (§10 —
   no new agent behavior). Colors reuse the literal palette already in this file
   (§9 — no new tokens). */
const FOCUS_HEALTH={
  act:  {color:'#C84B2F',label:'Act'},
  watch:{color:'#B8860B',label:'Watch'},
  calm: {color:'#059669',label:'Calm'},
};
const _SEV_RANK={act:0,watch:1,calm:2};
const _SRC_RANK={sentry:0,strat:1,sense:2,scout:3,sage:4};
function daysUntilISO(d){
  if(!d)return null;
  const t=new Date(d+'T00:00:00');
  if(isNaN(t.getTime()))return null;
  return Math.ceil((t-Date.now())/86400000);
}
function _fmtShortDate(d){
  const t=new Date(d+'T00:00:00');
  return isNaN(t.getTime())?d:t.toLocaleDateString('en-US',{month:'short',day:'numeric'});
}
/* All candidate drivers for one Setting — each maps to the OneDriver shape. */
function computeDrivers(deal){
  if(!deal)return[];
  const out=[];
  const insights=deal.sageInsights||[];
  const closed=deal.stage==='Closed';
  const days=daysUntilISO(deal.nextDeadline);
  // 1) Sentry — irreversible / time-boxed risk: the next dated deadline.
  if(!closed&&days!==null){
    const sev=days<=21?'act':days<=45?'watch':'calm';
    out.push({settingId:deal.id,severity:sev,source:'sentry',
      sourceLabel:'Sentry · '+(days<=45?'deadline window':'timeline'),
      statement:days<0
        ?`${deal.nextMilestone||'Next milestone'} deadline (${_fmtShortDate(deal.nextDeadline)}) has passed with no completion logged.`
        :`${deal.nextMilestone||'Next milestone'} — the ${_fmtShortDate(deal.nextDeadline)} window closes in ${days} day${days===1?'':'s'}.`,
      confidence:'high',deadlineDays:days,phase:deal.wexPhase||'discovery',
      primaryAction:{label:days<=45?'Unblock milestone':'Review timeline',
        intent:`The "${deal.nextMilestone}" deadline for ${deal.name} is ${_fmtShortDate(deal.nextDeadline)} (${days} days out). What is blocking it, and draft the unblock plan.`},
      reasoning:`Time-boxed risk ranks first (§4.2): the ${_fmtShortDate(deal.nextDeadline)} date is irreversible once passed. ${days<=21?'Inside the 21-day act horizon.':days<=45?'Inside the 45-day watch horizon.':'Outside the watch horizon — informational.'}`});
  }
  // 2) Strat — financial loss-of-opportunity (TI lapse, authorization, drawdown).
  const strat=insights.find(i=>/strat|ti monitoring/i.test(i.source||'')||/TI allowance|allowance.*laps|authoriz|drawdown/i.test(i.text||''));
  if(!closed&&strat){
    out.push({settingId:deal.id,severity:'watch',source:'strat',
      sourceLabel:'Strat · '+(/(TI|allowance)/i.test(strat.text)?'TI lapse':'financial exposure'),
      statement:(strat.text.split(/(?<=[.!?])\s/)[0]||strat.text).slice(0,220),
      confidence:'medium',deadlineDays:days,phase:deal.wexPhase||'discovery',
      primaryAction:{label:'Open Strat analysis',intent:`Strat flagged on ${deal.name}: ${strat.text.slice(0,180)} — quantify the exposure and the decision needed.`},
      reasoning:'Financial loss-of-opportunity ranks second (§4.2). Sourced from an existing Strat insight; medium confidence pending live model refresh (D-05 caveat rule: high confidence on partial data is prevented).'});
  }
  // 3) Sense — experience/utilisation breach material to the deal thesis.
  const sig=(deal.discoveryContext?.senseSignals||[])[0];
  if(!closed&&sig&&/drop|declin|below|drift|collapse/i.test(sig)){
    out.push({settingId:deal.id,severity:'watch',source:'sense',
      sourceLabel:'Sense · utilisation',
      statement:(sig.split(/(?<=[.!?])\s/)[0]||sig).slice(0,220),
      confidence:'medium',deadlineDays:null,phase:'discovery',
      primaryAction:{label:'Review Sense signals',intent:`Sense signals on ${deal.name} show drift: ${sig.slice(0,160)} — is this material to the deal thesis?`},
      reasoning:'Experience breach ranks third (§4.2) — material to the thesis but not time-boxed. Reachable in full at L3; never the headline while a dated risk is open.'});
  }
  // 4) Scout — market-timing shift that changes negotiating leverage.
  const scout=insights.find(i=>i.type==='market'&&/rose|fell|rise|drop|shift|YoY/i.test(i.text||''));
  if(!closed&&scout){
    out.push({settingId:deal.id,severity:'calm',source:'scout',
      sourceLabel:'Scout · market shift',
      statement:(scout.text.split(/(?<=[.!?])\s/)[0]||scout.text).slice(0,220),
      confidence:'medium',deadlineDays:null,phase:'design',
      primaryAction:{label:'See market context',intent:`Scout market context for ${deal.name}: ${scout.text.slice(0,160)} — how does this change our leverage?`},
      reasoning:'Market timing informs leverage but rarely demands action today (§4.2 rank 4).'});
  }
  // 5) Sage — synthesized fallback: the most meaningful next step (§7 "no emergencies").
  out.push({settingId:deal.id,severity:'calm',source:'sage',
    sourceLabel:'Sage · next step',
    statement:closed?`${deal.name} is closed — no active drivers.`:
      `On track — next: ${deal.nextMilestone||'confirm the next milestone'}${deal.nextDeadline?` by ${_fmtShortDate(deal.nextDeadline)}`:''}.`,
    confidence:deal.nextDeadline?'high':'low',deadlineDays:days,phase:deal.wexPhase||'discovery',
    primaryAction:{label:'Ask Sage for next step',intent:`What is the single most meaningful next step on ${deal.name}?`},
    reasoning:'No single agent signal dominates; Sage synthesizes the most meaningful next step so the driver slot is never empty (§7).'});
  return out;
}
/* ── Driver acknowledgment (§5 friction doctrine) ────────────────────────────
   Dismissing an Act driver requires explicit acknowledgment (preserve-class
   friction). An acknowledged driver is demoted act → watch — it never vanishes,
   it just stops shouting. Cached set; invalidated on ack. */
const _ACK_KEY='stage_ack_drivers';
let _ackCache=null;
function _ackSet(){
  if(_ackCache)return _ackCache;
  try{_ackCache=new Set(JSON.parse(localStorage.getItem(_ACK_KEY)||'[]'));}catch(e){_ackCache=new Set();}
  return _ackCache;
}
const driverAckKey=d=>`${d.settingId}|${d.source}|${(d.statement||'').slice(0,48)}`;
function acknowledgeDriver(d){
  const s=_ackSet();s.add(driverAckKey(d));
  try{localStorage.setItem(_ACK_KEY,JSON.stringify([...s]));}catch(e){}
  _bumpWeekCleared();
}
const driverIsAcked=d=>_ackSet().has(driverAckKey(d));
/* Weekly cleared counter (§3.5): "3 of 3 critical drivers cleared this week." */
function _weekKey(){
  const d=new Date(),jan1=new Date(d.getFullYear(),0,1);
  return d.getFullYear()+'-W'+Math.ceil((((d-jan1)/86400000)+jan1.getDay()+1)/7);
}
function getWeekCleared(){
  try{const r=JSON.parse(localStorage.getItem('stage_triage_week')||'null');
    return r&&r.week===_weekKey()?r.cleared:0;}catch(e){return 0;}
}
function _bumpWeekCleared(){
  try{localStorage.setItem('stage_triage_week',JSON.stringify({week:_weekKey(),cleared:getWeekCleared()+1}));}catch(e){}
}
/* The One Driver (§3.3): exactly one statement per Setting. Acknowledged Act
   drivers are demoted to Watch before ranking (they remain reachable at L3). */
function computeOneDriver(deal){
  const c=computeDrivers(deal).map(d=>
    (d.severity==='act'&&driverIsAcked(d))
      ?{...d,severity:'watch',acked:true,reasoning:(d.reasoning||'')+' Acknowledged by the TM — demoted from Act to Watch.'}
      :d);
  c.sort((a,b)=>(_SEV_RANK[a.severity]-_SEV_RANK[b.severity])
    ||(_SRC_RANK[a.source]-_SRC_RANK[b.source])
    ||((a.deadlineDays??9999)-(b.deadlineDays??9999)));
  return c[0];
}
const settingHealth=deal=>computeOneDriver(deal).severity;
/* ── Focus-state persistence (§3.1) — tabs, pins, active, per-Setting phase ── */
const _FOCUS_KEY='stage_focus_state';
function loadFocusState(){
  try{const s=JSON.parse(localStorage.getItem(_FOCUS_KEY)||'null');
    if(s&&Array.isArray(s.tabs)&&Array.isArray(s.pinned))return s;}catch(e){}
  return{tabs:[],pinned:[],active:null,phases:{}};
}
function saveFocusState(s){try{localStorage.setItem(_FOCUS_KEY,JSON.stringify(s));}catch(e){}}

function Ring({status}){return <div className={statusRingClass[status]}/>}

function Chip({label,color}){
  return <span className="pill" style={{background:color+'18',color}}>{label}</span>;
}

/* ── SVG CHARTS ─────────────────────────────────────────────── */
function BarChart({data,keys,colors,height=180}){
  const max=Math.max(...data.flatMap(d=>keys.map(k=>d[k]||0)));
  const W=100/data.length;
  return(
    <svg viewBox={`0 0 ${data.length*60} ${height+30}`} style={{width:'100%',height:height+30}}>
      {data.map((d,i)=>keys.map((k,ki)=>{
        const v=d[k]||0;const h2=max>0?(v/max)*height:0;
        const bw=(W*0.7/keys.length);
        const x=i*60+4+(ki*(60*0.7/keys.length));
        return <rect key={ki} x={x} y={height-h2+5} width={bw*0.85} height={h2} fill={colors[ki]} rx="2" opacity="0.85"/>;
      }))}
      {data.map((d,i)=><text key={i} x={i*60+30} y={height+22} textAnchor="middle" fontSize="9" fill="#3D1F0D80">{d.label||d.week||d.period||i}</text>)}
    </svg>
  );
}

function LineChart({data,keys,colors,height=160}){
  const vals=data.flatMap(d=>keys.map(k=>d[k]||0));
  const min=Math.min(...vals)-5;const max=Math.max(...vals)+5;
  const W=data.length>1?100/(data.length-1):100;
  const toY=v=>height-((v-min)/(max-min))*height+10;
  const toX=(i)=>i*W/(100/500);// approx
  const N=data.length;
  return(
    <svg viewBox={`0 0 500 ${height+30}`} style={{width:'100%',height:height+30}}>
      <line x1="0" y1={height/2+10} x2="500" y2={height/2+10} stroke="#3D1F0D10" strokeWidth="1"/>
      {keys.map((k,ki)=>{
        const pts=data.map((d,i)=>`${i*(500/(N-1))},${toY(d[k]||0)}`).join(' ');
        return <polyline key={ki} points={pts} fill="none" stroke={colors[ki]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>;
      })}
      {data.map((d,i)=>(i%3===0)?<text key={i} x={i*(500/(N-1))} y={height+26} textAnchor="middle" fontSize="9" fill="#3D1F0D60">{d.week||d.label}</text>:null)}
    </svg>
  );
}

function UtilBlock({loc}){
  const c=loc.util>=70?'#22c55e':loc.util>=50?'#f59e0b':'#ef4444';
  return(
    <div style={{textAlign:'center'}}>
      <div style={{background:c+'44',border:`1px solid ${c}33`,borderRadius:10,padding:'14px 8px',color:'#3D1F0D',fontWeight:700,fontSize:15}}>{loc.util}%</div>
      <div style={{fontSize:10,color:'rgba(61,31,13,.55)',marginTop:4,fontWeight:500,lineHeight:1.2}}>{loc.name.split(' ').slice(0,2).join(' ')}</div>
      <div style={{fontSize:9,color:'rgba(61,31,13,.35)'}}>{loc.market}</div>
    </div>
  );
}

/* ── NAV ───────────────────────────────────────────────────── */
// Hierarchical nav. Three primary surfaces (Portfolio Spine, WX Delivery, WX
// Performance) each own one collapsed sub-surface; three group lines (Connectors,
// Collaborate, Customize) own the secondary surfaces. `group:true` = not a page
// itself (clicking opens its first child). Sub-items reveal on hover or when the
// active page is within the group. This keeps the daily focus on the three mains.
const NAV=[
  {id:'brief',label:'Status Brief',icon:'◈'},
  {id:'portfolio',label:'Portfolio Spine',icon:'⬡',children:[
    {id:'financial',label:'Financial Surface',icon:'$'},
  ]},
  {id:'setting',label:'WX Delivery',icon:'⟷',children:[
    {id:'dealbrief',label:'Deal Brief',icon:'▤'},
  ]},
  {id:'analytics',label:'WX Performance',icon:'▦',children:[
    {id:'scorecard',label:'Settings Scorecard',icon:'▥'},
    {id:'demand',label:'Space Demand',icon:'◳'},
  ]},
  {id:'connectors',label:'Connectors',icon:'⊕',group:true,children:[
    {id:'cke',label:'Knowledge Engine',icon:'◎'},
  ]},
  {id:'collaborate',label:'Collaborate',icon:'❖',group:true,children:[
    {id:'sage',label:'Sage Intelligence',icon:'◆'},
    {id:'actions',label:'Action Rail',icon:'✓'},
  ]},
  {id:'customize',label:'Customize',icon:'⚙',group:true,children:[
    {id:'governance',label:'Governance',icon:'⛨'},
    {id:'plans',label:'Plans & Credits',icon:'◑'},
  ]},
];

// One nav row (icon + label + optional badge). Shared by mains and children.
function NavRow({item,active,onClick,badge,child}){
  return(
    <button onClick={onClick} className={`nav-btn${active?' active':''}${child?' nav-child':''}`}>
      <span style={{fontSize:child?12:14,lineHeight:1,opacity:child?.85:1}}>{item.icon}</span>
      <span style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.label}</span>
      {badge>0&&<span style={{background:'#ef4444',color:'#fff',borderRadius:999,fontSize:10,fontWeight:700,padding:'1px 6px'}}>{badge}</span>}
    </button>
  );
}

// A primary surface OR a group line, with its collapsed children. Children reveal
// on hover (CSS) or when the active page is inside the group (the .open class).
// A group line (no page of its own) navigates to its first child when clicked.
function NavGroup({item,page,onNav,badges}){
  const childIds=item.children.map(c=>c.id);
  const activeWithin=page===item.id||childIds.includes(page);
  const isPage=!item.group;
  const rollup=childIds.reduce((s,id)=>s+(badges[id]||0),0);
  const onMain=()=>onNav(isPage?item.id:item.children[0].id);
  return(
    <div className={`nav-group${activeWithin?' open':''}`}>
      <button onClick={onMain} className={`nav-btn nav-main${page===item.id?' active':''}${activeWithin?' within':''}`}>
        <span style={{fontSize:14,lineHeight:1}}>{item.icon}</span>
        <span style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.label}</span>
        {isPage&&badges[item.id]>0&&<span style={{background:'#ef4444',color:'#fff',borderRadius:999,fontSize:10,fontWeight:700,padding:'1px 6px',marginRight:4}}>{badges[item.id]}</span>}
        {!activeWithin&&rollup>0&&<span title="Items needing attention inside" style={{background:'#ef4444',color:'#fff',borderRadius:999,fontSize:10,fontWeight:700,padding:'1px 6px',marginRight:4}}>{rollup}</span>}
        <span className="nav-chev" style={{fontSize:9,opacity:.45}}>▸</span>
      </button>
      <div className="nav-group-children">
        {item.children.map(c=>(
          <NavRow key={c.id} item={c} child active={page===c.id} badge={badges[c.id]||0} onClick={()=>onNav(c.id)}/>
        ))}
      </div>
    </div>
  );
}

function Sidebar({page,onNav}){
  const {portfolio:_p=PORTFOLIO,actions:_a=ACTIONS,billingEnabled,isDemo,currentUser}=useData();
  // Footer identity — full name + organisation, from the authenticated user.
  // Never show a raw Clerk id; fall back to the email local-part, then a friendly
  // default. Org falls back to a dash when the account isn't org-scoped yet.
  const _u=currentUser||{};
  // Shared identity derivation (consults first/full/username/email, all enriched
  // from the Clerk client object) — only "Servant leader" when nothing is known.
  const _userName=deriveDisplayName(_u)||'Servant leader';
  const _orgName=(_u.orgName||'').trim()||'—';
  const _initials=(_userName||'?').split(/[\s@._-]+/).filter(Boolean).slice(0,2).map(s=>s[0].toUpperCase()).join('')||'?';
  const redCount=_p.filter(l=>l.status==='red').length;
  const p1Count=_a.filter(a=>a.priority==='P1'&&taskIsOutstanding(a)).length;  // canonical: P1 not-done
  const frictionCount=isDemo?RBW_FRICTION.filter(f=>f.severity==='P1').length:0;
  // Alert counts keyed by surface id — surfaced on the row, or rolled up onto a
  // collapsed group header so nothing urgent hides inside a closed section.
  const badges={portfolio:redCount,actions:p1Count,sage:SAGE_ALERTS.length,analytics:frictionCount};
  return(
    <div className="sidebar">
      <div style={{padding:'16px 16px 14px',borderBottom:'1px solid rgba(255,255,255,.1)',display:'flex',alignItems:'center',gap:10}}>
        <img src="/static/logo-white.svg" alt="Stage" style={{height:18,width:'auto',display:'block'}}/>
        <div style={{color:'#fff',fontWeight:700,fontSize:14,lineHeight:1,letterSpacing:'-0.2px'}}>Status</div>
      </div>
      <nav style={{flex:1,overflowY:'auto',padding:'10px 10px'}}>
        {NAV.map(n=> n.children
          ? <NavGroup key={n.id} item={n} page={page} onNav={onNav} badges={badges}/>
          : <NavRow key={n.id} item={n} active={page===n.id} badge={badges[n.id]||0} onClick={()=>onNav(n.id)}/>
        )}
      </nav>
      <div style={{borderTop:'1px solid rgba(255,255,255,.1)',padding:'14px 16px',display:'flex',alignItems:'center',gap:10}}>
        <div style={{width:30,height:30,background:'#8b0020',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700,flexShrink:0}}>{_initials}</div>
        <div style={{minWidth:0}}>
          <div style={{color:'#fff',fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={_userName}>{_userName}</div>
          <div style={{color:'rgba(255,255,255,.4)',fontSize:10,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={_orgName}>{_orgName}</div>
        </div>
      </div>
    </div>
  );
}

// ── Billing-clock banner (capture-mode pricing; SHADOW — informational only) ──
// Reads /api/billing/state. free_floor → the free-period promise (founding orgs
// see the price lock); grace → billing-starts date after the team crossed the
// activation threshold; payment_due → choose-a-plan. free_tier and subscribed
// render nothing: the free tier is simply free, not a nag surface.
function BillingBanner(){
  const authFetch=useAuthFetch();
  const [bs,setBs]=useState(null);
  useEffect(()=>{let live=true;
    authFetch('/api/billing/state').then(r=>r&&r.ok?r.json():null)
      .then(d=>{if(live)setBs(d);}).catch(()=>{});
    return()=>{live=false;};
  },[]);
  if(!bs||!bs.enabled)return null;
  const fmt=iso=>{try{return new Date(iso).toLocaleDateString(undefined,{month:'long',day:'numeric',year:'numeric'});}catch(e){return iso;}};
  let text=null,tone='info';
  if(bs.state==='free_floor'){
    text=(bs.founding?'Founding org — Status is free for your team until '+fmt(bs.floorEndsAt)+', with your price locked.'
                     :'Status is free for your team until '+fmt(bs.floorEndsAt)+'.');
  }else if(bs.state==='grace'){
    tone='warn';
    text='Your team has grown to '+bs.activeUsers30d+' active members — Status stays free until '+fmt(bs.billingDueAt)+', then billing begins.';
  }else if(bs.state==='payment_due'){
    tone='due';
    text='Your team’s free period has ended — choose a plan to keep the whole team on Status.';
  }else{
    return null;
  }
  const bg=tone==='due'?'var(--brand, #8b0020)':tone==='warn'?'#B45309':'rgba(61,31,13,.88)';
  return (
    <div style={{background:bg,color:'#fff',fontSize:12,fontWeight:500,padding:'6px 16px',
                 display:'flex',alignItems:'center',justifyContent:'center',gap:12,flexShrink:0}}>
      <span>{text}</span>
      {(tone==='warn'||tone==='due')&&(
        <a href="/pricing" style={{color:'#fff',fontWeight:700,textDecoration:'underline'}}>See plans</a>
      )}
    </div>
  );
}

function Topbar({onSageOpen,onHelpOpen}){
  const {currentUser,presence=[]}=useData();
  const clerk=useClerk();  // TEAM-1 Gap T1 — drives the org switcher (null in dev)
  const roleColors={admin:{bg:'rgba(139,0,33,.08)',color:'#8b0020',label:'Admin'},advisor:{bg:'rgba(232,129,58,.08)',color:'#E8813A',label:'Advisor'},viewer:{bg:'rgba(37,99,235,.08)',color:'#2563EB',label:'Viewer'}};
  const rc=currentUser?.role?roleColors[currentUser.role]||roleColors.advisor:null;
  // TEAM-1 Phase E — deterministic avatar colour from the user id.
  const _avatarColor=(s)=>{const palette=['#8b0020','#E8813A','#2563EB','#059669','#7C3AED','#D97706'];let h=0;for(const c of (s||'')) h=(h*31+c.charCodeAt(0))>>>0;return palette[h%palette.length];};
  const _initials=(n)=>(n||'?').split(/[\s_@.]+/).filter(Boolean).slice(0,2).map(p=>p[0].toUpperCase()).join('')||'?';
  return(
    <div className="topbar">
      <a href="/" style={{display:'flex',alignItems:'center',gap:10,flexShrink:0,marginRight:16,textDecoration:'none'}}>
        <img src="/static/logo.svg" alt="Stage" style={{height:20,width:'auto',display:'block'}}/>
        <span style={{fontFamily:'Georgia,serif',fontSize:15,fontWeight:600,color:'#3D1F0D',letterSpacing:'-0.3px'}}>Status</span>
      </a>
      {/* Privacy hardening — the active org is shown as a DISPLAY-ONLY pill, not
          an in-app switcher. Switching org requires sign-out + sign-in, which
          gives a clean session boundary (no client state carried across orgs). */}
      {currentUser?.orgName&&currentUser.authEnabled&&(
        <span style={{fontSize:12,fontWeight:600,color:'#8b0020',background:'rgba(139,0,33,.07)',padding:'2px 8px',borderRadius:10,marginRight:12,flexShrink:0,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={'Organisation: '+currentUser.orgName+' — sign out to switch organisations'}>{currentUser.orgName}</span>
      )}
      {/* TEAM-1 Phase E — live presence: who else is in this workspace */}
      {presence.length>0&&(
        <div style={{display:'flex',alignItems:'center',marginRight:12,flexShrink:0}} title={presence.map(p=>`${p.name}${p.location?' · '+p.location:''}`).join('\n')}>
          {presence.slice(0,5).map((p,i)=>(
            <div key={p.userId} style={{width:24,height:24,borderRadius:'50%',background:_avatarColor(p.userId),color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',border:'2px solid #fff',marginLeft:i===0?0:-8,boxShadow:'0 1px 2px rgba(0,0,0,.15)'}}>{_initials(p.name)}</div>
          ))}
          {presence.length>5&&<span style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.5)',marginLeft:4}}>+{presence.length-5}</span>}
        </div>
      )}
      <div style={{flex:1,maxWidth:420,position:'relative'}}>
        <input
          placeholder="Ask Sage anything…"
          readOnly
          onClick={onSageOpen}
          style={{width:'100%',background:'#F5EDE0',border:'1px solid rgba(61,31,13,.1)',borderRadius:10,padding:'7px 14px 7px 36px',fontSize:13,color:'rgba(61,31,13,.5)',outline:'none',cursor:'pointer',transition:'border-color .2s,box-shadow .2s'}}
          onMouseEnter={e=>{e.target.style.borderColor='rgba(232,129,58,.4)';e.target.style.boxShadow='0 0 0 3px rgba(232,129,58,.08)';}}
          onMouseLeave={e=>{e.target.style.borderColor='rgba(61,31,13,.1)';e.target.style.boxShadow='none';}}
        />
        <div style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'rgba(61,31,13,.3)',fontSize:14,pointerEvents:'none'}}>⌕</div>
        <div style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.3)',pointerEvents:'none'}}>⌘K</div>
      </div>
      <div style={{marginLeft:'auto',display:'flex',alignItems:'center',gap:12}}>
        <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:999,border:'1px solid rgba(61,31,13,.18)',color:'rgba(61,31,13,.45)',letterSpacing:'.03em',whiteSpace:'nowrap'}}>Preview for Research Purposes</span>
        <span style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>{new Date().toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})}</span>
        {rc&&<span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999,background:rc.bg,color:rc.color,letterSpacing:'.04em'}}>{rc.label}</span>}
        {currentUser?.name&&!/^user_[A-Za-z0-9]{6,}$/.test(currentUser.name)&&currentUser.authEnabled&&<span style={{fontSize:12,color:'rgba(61,31,13,.55)',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{currentUser.name}</span>}
        <button onClick={onHelpOpen} aria-label="Help (⌘/)" title="Help · ⌘/" style={{width:30,height:30,borderRadius:'50%',border:'1px solid rgba(61,31,13,.18)',background:'#fff',color:'rgba(61,31,13,.6)',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'Georgia,serif',lineHeight:1,flexShrink:0,transition:'border-color .15s,color .15s'}}
          onMouseEnter={e=>{e.currentTarget.style.borderColor='#E8813A';e.currentTarget.style.color='#E8813A';}}
          onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(61,31,13,.18)';e.currentTarget.style.color='rgba(61,31,13,.6)';}}>?</button>
        <NotifBell/>
        <ClerkUserButton/>
      </div>
    </div>
  );
}

/* ── PAGES ──────────────────────────────────────────────────── */

/* Page entrance wrapper — fades up content on mount */
function PageTransition({children}){
  const [in_,setIn]=useState(false);
  useEffect(()=>{const r=requestAnimationFrame(()=>setIn(true));return()=>cancelAnimationFrame(r);},[]);
  return <div className={in_?'page-enter':''} style={{opacity:in_?1:0}}>{children}</div>;
}

/* ErrorBoundary — a render error on ONE surface must never blank the whole app
   (it previously did: an empty-portfolio crash on Portfolio Spine unmounted the
   entire root, taking the nav with it). Keyed by page in the shell, so switching
   surfaces resets it. Shows a recoverable card instead of a white screen. */
class ErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state={error:null}; }
  static getDerivedStateFromError(error){ return {error}; }
  componentDidCatch(error,info){ try{console.error('Surface render error:',error,info);}catch(e){} }
  render(){
    if(this.state.error){
      return (
        <div className="card" style={{padding:'22px 24px',borderLeft:'4px solid #C84B2F',maxWidth:680,margin:'24px auto',textAlign:'center'}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D',marginBottom:6}}>This view hit a snag</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.6)',lineHeight:1.6,marginBottom:16}}>
            Something went wrong rendering this surface — the rest of your workspace is unaffected. You can retry, or jump back to the Status Brief.
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
            <button className="btn-primary" onClick={()=>this.setState({error:null})}>Retry this view</button>
            {this.props.onHome&&<button className="btn-ghost" onClick={()=>{this.setState({error:null});this.props.onHome();}}>Go to Status Brief</button>}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Circular score ring — same data, visual form */
function ScoreRing({score,size=60,color='#E8813A',label,strokeWidth=5,delay=100}){
  const animated=useAnimatedNumber(score,900,delay);
  const r=(size-strokeWidth)/2;
  const circ=2*Math.PI*r;
  const offset=circ*(1-animated/100);
  return(
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <svg className="score-ring" width={size} height={size} style={{transform:'rotate(-90deg)',display:'block'}}>
        <circle className="track" cx={size/2} cy={size/2} r={r} fill="none" strokeWidth={strokeWidth}/>
        <circle className="fill" cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{transition:'stroke-dashoffset .04s',['--circ']:circ,['--offset']:offset}}/>
      </svg>
      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:1}}>
        <span style={{fontSize:size*.27,fontWeight:800,color,lineHeight:1}}>{animated}</span>
        {label&&<span style={{fontSize:size*.13,fontWeight:700,color:'rgba(61,31,13,.38)',textTransform:'uppercase',letterSpacing:'.05em',lineHeight:1}}>{label}</span>}
      </div>
    </div>
  );
}

/* ── WEX-1 Six-Point Radar Chart ─────────────────────────── */
const MK_AXES=[
  {key:'communityExchange',short:'CE',label:'Community Exchange'},
  {key:'communityReflect',short:'CR',label:'Community Reflect'},
  {key:'groupExchange',short:'GE',label:'Group Exchange'},
  {key:'groupProduce',short:'GP',label:'Group Produce'},
  {key:'individualProduce',short:'IP',label:'Individual Produce'},
  {key:'individualReflect',short:'IR',label:'Individual Reflect'},
];
const MK_TARGET_PROFILES={
  'Community-First':{communityExchange:85,communityReflect:60,groupExchange:80,groupProduce:72,individualProduce:55,individualReflect:50},
  'Collaborative-Hybrid':{communityExchange:80,communityReflect:65,groupExchange:85,groupProduce:85,individualProduce:62,individualReflect:55},
  'default':{communityExchange:72,communityReflect:65,groupExchange:72,groupProduce:72,individualProduce:65,individualReflect:62},
};
const MK_OPT_COLORS=['#7BAE8A','#E8A838','#E87A38','#4A7FA5'];

function RadarChart({options=[],archetype,size=260}){
  const target=MK_TARGET_PROFILES[archetype]||MK_TARGET_PROFILES['default'];
  const cx=size/2, cy=size/2, r=size*0.37;
  const n=MK_AXES.length;
  const toXY=(val,idx)=>{
    const angle=(idx*(360/n)-90)*Math.PI/180;
    const rv=(val/100)*r;
    return {x:cx+rv*Math.cos(angle), y:cy+rv*Math.sin(angle)};
  };
  const toPath=vals=>MK_AXES.map((ax,i)=>{const {x,y}=toXY(vals[ax.key]||0,i);return`${i===0?'M':'L'}${x.toFixed(1)},${y.toFixed(1)}`;}).join(' ')+'Z';
  const gridPath=pct=>MK_AXES.map((ax,i)=>{const {x,y}=toXY(pct,i);return`${i===0?'M':'L'}${x.toFixed(1)},${y.toFixed(1)}`;}).join(' ')+'Z';
  return(
    <svg viewBox={`0 0 ${size} ${size}`} style={{width:'100%',maxWidth:size,height:size,display:'block',margin:'0 auto'}}>
      {/* Grid rings */}
      {[20,40,60,80,100].map(p=>(
        <path key={p} d={gridPath(p)} fill="none" stroke={p===100?'rgba(61,31,13,.15)':'rgba(61,31,13,.07)'} strokeWidth={p===100?1.5:1}/>
      ))}
      {/* Axis lines */}
      {MK_AXES.map((ax,i)=>{const {x,y}=toXY(100,i);return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(61,31,13,.1)" strokeWidth={1}/>;} )}
      {/* Option profiles — semi-transparent fills */}
      {options.map((opt,oi)=>(
        <path key={opt.id} d={toPath(opt.rbw||{})} fill={MK_OPT_COLORS[oi%MK_OPT_COLORS.length]+'38'} stroke={MK_OPT_COLORS[oi%MK_OPT_COLORS.length]} strokeWidth={1.5} opacity={0.9}/>
      ))}
      {/* Target profile — amber dashed, on top */}
      <path d={toPath(target)} fill="rgba(232,168,56,.1)" stroke="#E8A838" strokeWidth={2} strokeDasharray="5,3"/>
      {/* Axis labels */}
      {MK_AXES.map((ax,i)=>{
        const lp=toXY(120,i);
        return <text key={i} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle" fontSize={9} fontWeight={700} fill="rgba(61,31,13,.55)">{ax.short}</text>;
      })}
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={2} fill="rgba(61,31,13,.2)"/>
    </svg>
  );
}

function RadarLegend({options=[],archetype}){
  return(
    <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:8,justifyContent:'center'}}>
      <div style={{display:'flex',alignItems:'center',gap:5,fontSize:10}}>
        <svg width={16} height={8}><line x1={0} y1={4} x2={16} y2={4} stroke="#E8A838" strokeWidth={2} strokeDasharray="4,2"/></svg>
        <span style={{color:'rgba(61,31,13,.55)',fontWeight:600}}>Target — {archetype||'Archetype'}</span>
      </div>
      {options.map((opt,i)=>(
        <div key={opt.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:10}}>
          <div style={{width:10,height:10,borderRadius:2,background:MK_OPT_COLORS[i%MK_OPT_COLORS.length]}}/>
          <span style={{color:'rgba(61,31,13,.55)'}}>{opt.name?.split('—')[0].trim()||`Opt ${i+1}`}</span>
        </div>
      ))}
    </div>
  );
}

/* Mini sparkbar — shows a value as a filled horizontal bar */
function SparkBar({pct,color='#E8813A',height=3}){
  const animated=useAnimatedNumber(pct,800,200);
  return(
    <div style={{height,background:'rgba(61,31,13,.08)',borderRadius:height,overflow:'hidden',marginTop:8}}>
      <div style={{height:'100%',width:`${animated}%`,background:color,borderRadius:height,transition:'width .04s'}}/>
    </div>
  );
}

/* KPI card — animated number + optional sparkbar for secondary visual */
function KPI({label,value,sub,warn,barPct=null,delay=0}){
  const raw=typeof value==='string'?parseFloat(value.replace(/[^0-9.]/g,''))||null:typeof value==='number'?value:null;
  const animated=useAnimatedNumber(raw||0,900);
  // Reconstruct display value preserving prefix/suffix ($, M, K, %, etc.)
  const display=raw!==null?value.toString().replace(/[\d,]+(\.\d+)?/,(m)=>{
    if(m.includes('.'))return animated.toFixed(1);
    const orig=parseInt(m.replace(/,/g,''));
    return orig>=1000?(animated).toLocaleString():String(animated);
  }):value;
  const barColor=warn?'#dc2626':'#E8813A';
  return(
    <div className="card card-flat" style={{padding:'18px 20px',animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${delay}ms`}}>
      <div style={{fontSize:11,fontWeight:600,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</div>
      <div style={{fontSize:26,fontWeight:800,color:warn?'#dc2626':'#3D1F0D',marginTop:4,fontVariantNumeric:'tabular-nums'}}>{display}</div>
      {barPct!==null&&<SparkBar pct={barPct} color={barColor}/>}
      {sub&&<div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginTop:barPct!==null?4:2}}>{sub}</div>}
    </div>
  );
}

// Doc 2 §3/§6 — "One Signal": the single highest-priority Sage insight, surfaced
// first every session as the primary visual anchor. Confidence + source + one
// action (Investigate / Dismiss). On-demand from current insights (D-02).
function OneSignal({onNav}){
  const {insights}=useData();
  const list=Array.isArray(insights)?insights:[];
  const [dismissed,setDismissed]=useState(false);
  const top=list.length?list.slice().sort((a,b)=>(b.conf||0)-(a.conf||0))[0]:null;
  if(!top||dismissed)return null;
  const label=top.conf>=85?'High':top.conf>=70?'Medium':'Low';
  const color=top.conf>=85?'#059669':top.conf>=70?'#B8860B':'#9CA3AF';
  const sources=(top.sources||[]).join(' · ');
  return(
    <div className="card joy-glow" style={{padding:24,marginBottom:18,borderLeft:'4px solid #E8813A'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:10}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:14,color:'#E8813A'}}>★</span>
          <span style={{fontSize:11,fontWeight:700,color:'#E8813A',textTransform:'uppercase',letterSpacing:'.08em'}}>One Signal · Sage</span>
        </div>
        <span style={{fontSize:10,fontFamily:'monospace',padding:'2px 8px',borderRadius:10,background:color+'1A',color}}>Confidence: {label}</span>
      </div>
      <div style={{fontFamily:'Georgia,serif',fontSize:20,color:'#3D1F0D',lineHeight:1.25,marginBottom:8}}>{top.title}</div>
      <div style={{fontSize:13.5,color:'#2D2A24',lineHeight:1.7,marginBottom:10}}>{(top.body||'').slice(0,260)}{(top.body||'').length>260?'…':''}</div>
      {sources&&<div style={{fontSize:11,color:'rgba(61,31,13,.45)',marginBottom:14}}>Based on {sources}</div>}
      <div style={{display:'flex',gap:10,alignItems:'center'}}>
        <button onClick={()=>onNav&&onNav('sage')} style={{padding:'9px 18px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Investigate</button>
        <span onClick={()=>setDismissed(true)} style={{fontSize:12.5,color:'rgba(61,31,13,.5)',cursor:'pointer'}}>Dismiss</span>
      </div>
    </div>
  );
}

// Gap Patch §Gap 01 — zero-data guided workflow. Three intents, each a 3-step
// task that produces a real Sage output from manual inputs (no upload), with a
// quiet bypass on every screen. Shown only to a blank, real (non-demo) account.
const GUIDED_WORKFLOWS={
  baseline:{label:'Portfolio Baseline Diagnostic',intent:'Portfolio Health',icon:'📊',
    hint:'See how my properties are performing',
    fields:[['propertyCount','Number of properties','e.g. 6'],['totalSf','Approx. total SF','e.g. 220,000'],['assetType','Primary asset type','e.g. Office'],['concern','Biggest current concern','e.g. upcoming rollover']]},
  expiration:{label:'Lease Expiration Scout',intent:'Lease Risk',icon:'⏱️',
    hint:'Find expiring leases before they become problems',
    fields:[['tenantName','Most at-risk tenant','e.g. Acme Corp'],['expirationDate','Approx. lease expiration','e.g. 2026-09-30'],['sqft','Their square footage','e.g. 12,000']]},
  friction:{label:'Operational Friction Finder',intent:'Operational Efficiency',icon:'⚙️',
    hint:"Identify what's costing time or money",
    fields:[['description','Your biggest operational headache','In a sentence or two — what takes the most time or causes the most errors?']]},
};
function GuidedWorkflow({onNav,onBypass}){
  const authFetch=useAuthFetch();
  const [wf,setWf]=useState(null);          // chosen workflow key
  const [inputs,setInputs]=useState({});
  const [loading,setLoading]=useState(false);
  const [output,setOutput]=useState(null);
  const [err,setErr]=useState('');
  // Gap 02 — invite state (deep-linkable finding)
  const [findingId,setFindingId]=useState(null);
  const [inviteEmail,setInviteEmail]=useState('');
  const [inviteState,setInviteState]=useState('');   // '' | 'sending' | 'sent' | 'skipped' | error msg
  const spec=wf?GUIDED_WORKFLOWS[wf]:null;
  const ready=spec&&spec.fields.every(([k])=>String(inputs[k]||'').trim());
  const Bypass=()=>(
    <div onClick={onBypass} style={{marginTop:14,fontFamily:'monospace',fontSize:11,letterSpacing:'.04em',color:'rgba(61,31,13,.45)',textDecoration:'underline',textUnderlineOffset:3,cursor:'pointer'}}>
      Skip guided setup — go to dashboard
    </div>
  );
  const generate=async()=>{
    setLoading(true);setErr('');
    try{
      const r=await authFetch('/api/onboarding/guided',{method:'POST',body:{workflow:wf,inputs}});
      if(r&&r.ok){setOutput(r.output);setFindingId(r.findingId||null);} else setErr((r&&r.error)||'Could not generate your workflow output.');
    }catch(e){
      const code=(String(e.message||'').match(/^(\d{3})/)||[])[1];
      setErr(code==='422'?'Please complete all fields and try again.':'Could not reach Sage — please retry.');
    }finally{setLoading(false);}
  };
  // ── Intent picker ──────────────────────────────────────────────────────────
  if(!wf)return(
    <div className="card" style={{padding:28,marginBottom:18,borderLeft:'4px solid #E8813A'}}>
      <div style={{fontSize:11,fontWeight:700,color:'#E8813A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Start here · No upload needed</div>
      <div style={{fontFamily:'Georgia,serif',fontSize:22,color:'#3D1F0D',marginBottom:4}}>What do you want to solve today?</div>
      <div style={{fontSize:13,color:'rgba(61,31,13,.55)',marginBottom:18}}>Sage sets up your first workflow from your answer — you'll get a real result before connecting any data.</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12}}>
        {Object.entries(GUIDED_WORKFLOWS).map(([k,w])=>(
          <div key={k} onClick={()=>{setWf(k);setInputs({});setOutput(null);setErr('');try{localStorage.setItem('stage_onboarding_intent',k);}catch(e){}}}
            style={{border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'18px 14px',textAlign:'center',cursor:'pointer',background:'#fff',transition:'border-color .2s,box-shadow .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor='#3D1F0D';e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,.08)';}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(61,31,13,.15)';e.currentTarget.style.boxShadow='none';}}>
            <div style={{fontSize:24,marginBottom:8}}>{w.icon}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:14,color:'#3D1F0D',marginBottom:4}}>{w.intent}</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.4}}>{w.hint}</div>
          </div>
        ))}
      </div>
      <Bypass/>
    </div>
  );
  // ── Step 3 — Sage output + upload-as-enhancement ───────────────────────────
  if(output)return(
    <div className="card" style={{padding:28,marginBottom:18,borderLeft:'4px solid #059669'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div style={{fontSize:11,fontWeight:700,color:'#059669',textTransform:'uppercase',letterSpacing:'.07em'}}>Sage · {spec.label} · Step 2 of 3</div>
        <div style={{fontSize:10,fontFamily:'monospace',padding:'2px 8px',borderRadius:10,background:'rgba(5,150,105,.1)',color:'#059669'}}>Confidence: {output.confidence}</div>
      </div>
      <div style={{fontFamily:'Georgia,serif',fontSize:20,color:'#3D1F0D',lineHeight:1.25,marginBottom:8}}>{output.headline}</div>
      <div style={{fontSize:13.5,color:'#2D2A24',lineHeight:1.7,marginBottom:14}}>{output.summary}</div>
      {output.points&&output.points.length>0&&(
        <ul style={{margin:'0 0 14px',paddingLeft:18}}>
          {output.points.map((p,i)=>(<li key={i} style={{fontSize:13,color:'#3A3530',lineHeight:1.6,marginBottom:4}}>{p}</li>))}
        </ul>
      )}
      {output.recommendedAction&&(
        <div style={{padding:'10px 14px',background:'rgba(232,129,58,.08)',borderLeft:'3px solid #E8813A',borderRadius:'0 6px 6px 0',fontSize:13,color:'#3D1F0D',marginBottom:12}}>
          <b>▶ Recommended:</b> {output.recommendedAction}
        </div>
      )}
      {output.caveat&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.5)',fontStyle:'italic',marginBottom:18}}>{output.caveat}</div>}
      {/* Step 3 — upload as enhancement, never a gate */}
      <div style={{borderTop:'1px solid rgba(61,31,13,.1)',paddingTop:16,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
        <div style={{fontSize:12.5,color:'rgba(61,31,13,.65)',maxWidth:420,lineHeight:1.5}}>This is a directional read from your inputs. <b>Connect your portfolio</b> to replace estimates with real numbers and extend this across every lease.</div>
        <button onClick={()=>onNav&&onNav('portfolio')} style={{padding:'10px 18px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>Connect your portfolio →</button>
      </div>
      {/* Gap 02 — collaborative win: invite a specific colleague to THIS finding */}
      {findingId&&inviteState!=='skipped'&&(
        inviteState==='sent'
        ? <div style={{marginTop:16,padding:'12px 16px',background:'rgba(5,150,105,.08)',borderLeft:'3px solid #059669',borderRadius:'0 6px 6px 0',fontSize:13,color:'#065f46'}}>✓ Invite sent — your {output.collaboratorRole} will land directly on this finding when they sign in.</div>
        : <div style={{marginTop:16,border:'1px solid rgba(61,31,13,.15)',borderRadius:8,overflow:'hidden'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 16px',background:'rgba(61,31,13,.04)',borderBottom:'1px solid rgba(61,31,13,.1)'}}>
              <div style={{fontFamily:'Georgia,serif',fontSize:15,color:'#3D1F0D'}}>Invite your {output.collaboratorRole} to this finding</div>
              <span onClick={()=>{if(findingId)authFetch(`/api/findings/${findingId}/skip-invite`,{method:'POST'}).catch(()=>{});setInviteState('skipped');}} style={{fontFamily:'monospace',fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',color:'rgba(61,31,13,.45)',textDecoration:'underline',cursor:'pointer'}}>Skip for now</span>
            </div>
            <div style={{padding:16}}>
              <div style={{fontSize:12.5,color:'rgba(61,31,13,.65)',lineHeight:1.5,marginBottom:12}}>This finding is more actionable with your {output.collaboratorRole} in the room — they'll land directly on it, not a blank dashboard.</div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={{flex:1,padding:'9px 12px',border:'1px solid rgba(61,31,13,.2)',borderRadius:5,fontSize:13,fontFamily:'inherit'}}/>
                <button disabled={!inviteEmail.trim()||inviteState==='sending'} onClick={async()=>{
                  setInviteState('sending');
                  try{
                    const r=await authFetch(`/api/findings/${findingId}/invite`,{method:'POST',body:{email:inviteEmail.trim(),role:output.collaboratorRole}});
                    setInviteState(r&&r.ok?'sent':'Could not send invite — try again.');
                  }catch(e){setInviteState('Could not send invite — try again.');}
                }} style={{padding:'9px 18px',background:inviteEmail.trim()?'#C84B2F':'rgba(61,31,13,.25)',color:'#fff',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:inviteEmail.trim()?'pointer':'not-allowed',fontFamily:'inherit',whiteSpace:'nowrap'}}>{inviteState==='sending'?'Sending…':'Send invite'}</button>
              </div>
              {inviteState&&!['sending','sent','skipped'].includes(inviteState)&&<div style={{fontSize:12,color:'#b91c1c',marginTop:8}}>{inviteState}</div>}
            </div>
          </div>
      )}
      <Bypass/>
    </div>
  );
  // ── Step 1 — manual inputs ─────────────────────────────────────────────────
  return(
    <div className="card" style={{padding:28,marginBottom:18,borderLeft:'4px solid #E8813A'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D'}}>{spec.icon} {spec.label}</div>
        <div style={{fontSize:11,fontFamily:'monospace',color:'rgba(61,31,13,.45)'}}>Step 1 of 3</div>
      </div>
      <div style={{display:'grid',gap:12,marginBottom:16}}>
        {spec.fields.map(([k,label,ph])=>(
          <div key={k}>
            <label style={{display:'block',fontSize:11,fontWeight:600,color:'rgba(61,31,13,.6)',marginBottom:4}}>{label}</label>
            {k==='description'
              ? <textarea value={inputs[k]||''} onChange={e=>setInputs({...inputs,[k]:e.target.value})} placeholder={ph} rows={3} style={{width:'100%',padding:'9px 12px',border:'1px solid rgba(61,31,13,.2)',borderRadius:6,fontSize:13,fontFamily:'inherit',resize:'vertical'}}/>
              : <input value={inputs[k]||''} onChange={e=>setInputs({...inputs,[k]:e.target.value})} placeholder={ph} style={{width:'100%',padding:'9px 12px',border:'1px solid rgba(61,31,13,.2)',borderRadius:6,fontSize:13,fontFamily:'inherit'}}/>}
          </div>
        ))}
      </div>
      {err&&<div style={{fontSize:12.5,color:'#b91c1c',marginBottom:12}}>{err}</div>}
      <div style={{display:'flex',alignItems:'center',gap:12}}>
        <button disabled={!ready||loading} onClick={generate} style={{padding:'10px 20px',background:ready&&!loading?'#3D1F0D':'rgba(61,31,13,.25)',color:'#F5EDE0',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:ready&&!loading?'pointer':'not-allowed',fontFamily:'inherit'}}>{loading?'Sage is thinking…':'Generate with Sage'}</button>
        <span onClick={()=>{setWf(null);setErr('');}} style={{fontSize:12,color:'rgba(61,31,13,.5)',cursor:'pointer'}}>← Choose a different focus</span>
      </div>
      <Bypass/>
    </div>
  );
}

// Gap Patch §Gap 02 — deep-linked invited-collaborator landing. An invited user
// opens ?finding=<id> and lands directly on the exact finding (role context +
// the full Sage output + the recommended action), not a blank dashboard.
function FindingLanding({findingId,onDismiss,onNav}){
  const authFetch=useAuthFetch();
  const [finding,setFinding]=useState(null);
  const [state,setState]=useState('loading');   // loading | ok | missing
  useEffect(()=>{
    let live=true;
    authFetch(`/api/findings/${findingId}`)
      .then(r=>{if(!live)return; if(r&&r.ok){setFinding(r.finding);setState('ok');}else setState('missing');})
      .catch(()=>{if(live)setState('missing');});
    return()=>{live=false;};
  },[findingId]);
  if(state==='loading')return(
    <div className="card" style={{padding:24,marginBottom:18}}><div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>Opening the finding you were invited to…</div></div>
  );
  if(state==='missing')return(
    <div className="card" style={{padding:24,marginBottom:18,borderLeft:'4px solid #C84B2F'}}>
      <div style={{fontSize:14,color:'#3D1F0D',marginBottom:6}}>That finding isn't available on this account.</div>
      <div style={{fontSize:12.5,color:'rgba(61,31,13,.6)',marginBottom:12}}>It may belong to a different organisation, or the link has expired.</div>
      <button onClick={onDismiss} style={{padding:'9px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Go to dashboard</button>
    </div>
  );
  const o=finding.output||{};
  return(
    <div className="card" style={{padding:28,marginBottom:18,borderLeft:'4px solid #059669'}}>
      <div style={{fontSize:11,fontWeight:700,color:'#059669',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>You were invited as {finding.collaboratorRole} · Here's what {finding.createdBy} found</div>
      <div style={{fontFamily:'Georgia,serif',fontSize:21,color:'#3D1F0D',lineHeight:1.25,marginBottom:8}}>{o.headline||finding.headline}</div>
      {(o.summary||finding.summary)&&<div style={{fontSize:13.5,color:'#2D2A24',lineHeight:1.7,marginBottom:12}}>{o.summary||finding.summary}</div>}
      {o.points&&o.points.length>0&&(
        <ul style={{margin:'0 0 14px',paddingLeft:18}}>{o.points.map((p,i)=>(<li key={i} style={{fontSize:13,color:'#3A3530',lineHeight:1.6,marginBottom:4}}>{p}</li>))}</ul>
      )}
      {o.recommendedAction&&(
        <div style={{padding:'10px 14px',background:'rgba(232,129,58,.08)',borderLeft:'3px solid #E8813A',borderRadius:'0 6px 6px 0',fontSize:13,color:'#3D1F0D',marginBottom:16}}>
          <b>▶ Recommended:</b> {o.recommendedAction}
        </div>
      )}
      <div style={{display:'flex',gap:10,alignItems:'center'}}>
        <button onClick={()=>{onNav&&onNav('sage');}} style={{padding:'10px 18px',background:'#C84B2F',color:'#fff',border:'none',borderRadius:5,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Investigate with Sage</button>
        {/* Dashboard is offered, never forced (PRD: "an invitation, not a redirect") */}
        <span onClick={onDismiss} style={{fontSize:12.5,color:'rgba(61,31,13,.6)',cursor:'pointer'}}>There's more across the portfolio — go to your dashboard →</span>
      </div>
    </div>
  );
}

function StatusBriefPage({onNav}){
  const {actions:_a=ACTIONS,deals:_d=DEALS,portfolio:_p=PORTFOLIO,tws:_tws,refresh,currentUser,billingEnabled,isDemo}=useData();
  const authFetch=useAuthFetch();
  // Gap Patch §Gap 01 — show the zero-data guided workflow to a blank, real
  // account until they bypass (per session). The bypass is an accelerator: it
  // just hides the panel and reveals the dashboard beneath.
  const [guidedBypassed,setGuidedBypassed]=useState(()=>{try{return sessionStorage.getItem('stage_guided_bypassed')==='1';}catch(e){return false;}});
  // v1.2 §Reveal — the first finding from the /start shell occupies the hero
  // slot, replacing the "No brief yet" empty state. Fidelity context persists.
  const [firstFinding,setFirstFinding]=useState(()=>{try{return JSON.parse(sessionStorage.getItem('stage_first_finding')||'null');}catch(e){return null;}});
  const dismissFirstFinding=()=>{try{sessionStorage.removeItem('stage_first_finding');}catch(e){} setFirstFinding(null);};
  // Grounded First Finding — re-derived FROM the org's real Portfolio Spine
  // records once they exist (supersedes the typed-answer onboarding output).
  // Cached per (tenant, record-count) so it regenerates when data changes.
  const [groundedFinding,setGroundedFinding]=useState(()=>{try{return JSON.parse(sessionStorage.getItem('stage_grounded_finding')||'null');}catch(e){return null;}});
  const [groundedBusy,setGroundedBusy]=useState(false);
  const [groundedDismissed,setGroundedDismissed]=useState(()=>{try{return sessionStorage.getItem('stage_grounded_dismissed')==='1';}catch(e){return false;}});
  const groundedBusyRef=React.useRef(false);
  // Defense-in-depth — the finding is STAMPED with the tenant that created it.
  // Never render one belonging to a different account (the account fence should
  // already have purged it, but a stale read must never surface another org's).
  React.useEffect(()=>{
    if(!firstFinding||!currentUser)return;
    const tenant=currentUser.tenantId||currentUser.orgId;
    if(!tenant)return;
    if(firstFinding.tenantId&&firstFinding.tenantId!==tenant){
      try{sessionStorage.removeItem('stage_first_finding');}catch(e){}
      setFirstFinding(null);
    }
  },[firstFinding,currentUser&&currentUser.tenantId,currentUser&&currentUser.orgId]);
  // Trust gate: the First Finding is a Directional diagnostic built from typed
  // onboarding answers. Presenting it as a headline analysis before any real
  // record exists reads as Sage inventing opinions from no data — the exact thing
  // that erodes trust. So we DEFER it until the portfolio holds ≥1 grounded record;
  // until then the org sees the guided "add your first location" prompt instead.
  const haveGrounding=Array.isArray(_p)&&_p.length>0;
  const _tenantKey=(currentUser&&(currentUser.tenantId||currentUser.orgId))||'';
  const groundedFresh=!!(groundedFinding&&groundedFinding.output&&groundedFinding.count===_p.length&&(groundedFinding.tenant||'')===_tenantKey);
  // When a real account first has grounded records (and whenever the record count
  // changes), ask Sage to generate the Baseline Diagnostic FROM those records.
  // The demo keeps its seeded behaviour; an empty org defers (guided prompt).
  useEffect(()=>{
    if(isDemo||!haveGrounding||groundedDismissed)return;
    if(groundedFresh||groundedBusyRef.current)return;
    groundedBusyRef.current=true;setGroundedBusy(true);
    let cancelled=false;
    authFetch('/api/onboarding/baseline-grounded',{method:'POST'})
      .then(r=>{ if(cancelled)return;
        if(r&&r.ok&&r.output){
          const rec={output:r.output,findingId:r.findingId||null,count:r.locationCount||_p.length,tenant:_tenantKey};
          setGroundedFinding(rec);
          try{sessionStorage.setItem('stage_grounded_finding',JSON.stringify(rec));}catch(e){}
        }
      })
      .catch(()=>{})
      .finally(()=>{groundedBusyRef.current=false;if(!cancelled)setGroundedBusy(false);});
    return ()=>{cancelled=true;};
  },[isDemo,haveGrounding,groundedDismissed,groundedFresh,_p.length,_tenantKey,authFetch]);
  // Prefer the grounded finding for real accounts; the demo falls back to its
  // seeded typed finding. The typed-answer finding is NEVER the headline on a
  // real account — it would reintroduce ungrounded estimates as a conclusion.
  const _demoFinding=(isDemo&&firstFinding&&firstFinding.output)?firstFinding:null;
  const _heroFinding=groundedFresh?{output:groundedFinding.output}:_demoFinding;
  const showFirstFinding=haveGrounding&&!!_heroFinding&&!groundedDismissed;
  const showGroundedLoading=!isDemo&&haveGrounding&&!groundedFresh&&!groundedDismissed&&groundedBusy;
  const showGuided=!isDemo&&!haveGrounding&&!guidedBypassed;
  const dismissHeroFinding=()=>{try{sessionStorage.setItem('stage_grounded_dismissed','1');}catch(e){}setGroundedDismissed(true);dismissFirstFinding();};
  const dismissGuided=()=>{try{sessionStorage.setItem('stage_guided_bypassed','1');}catch(e){} setGuidedBypassed(true);};
  // EW1 UI — import a completed Sense audit as the experience baseline.
  const [importId,setImportId]=useState('');
  const [importing,setImporting]=useState(false);
  const [importMsg,setImportMsg]=useState('');
  const doImportBaseline=async()=>{
    // Accept a raw UUID or a pasted Sense link containing one.
    const m=String(importId).match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    const sid=m?m[0]:importId.trim();
    if(!sid){setImportMsg('Paste your Sense audit link or ID.');return;}
    setImporting(true);setImportMsg('');
    try{
      const r=await authFetch('/api/experience/baseline',{method:'POST',body:{session_id:sid}});
      setImportMsg(`✓ Imported — ${r.safeAssetsCreated} findings, ${r.actionsCreated} actions, baseline TWS set. Refreshing…`);
      setImportId('');
      if(refresh)refresh();
    }catch(e){
      const code=(String(e.message||'').match(/^(\d{3})/)||[])[1];
      setImportMsg(code==='404'?'No Sense audit found for that link / ID.':code==='409'?'This audit was already imported.':code==='422'?'That audit isn’t complete yet — finish the Sense audit (Phases 1–3) first.':code==='400'?'That doesn’t look like a valid Sense audit link or ID.':'Could not import — please try again.');
    }finally{setImporting(false);}
  };
  // EW2 — record measured experience signals (flips standards Assumption → Fact).
  const SIGNAL_FIELDS=[['co_presence','Co-presence','Culture'],['belonging','Belonging','Culture'],['focus_fit','Focus space-to-intent fit','Flow'],['env_satisfaction','Environment satisfaction','Flow'],['programme_adoption','Programme adoption','Performance'],['utilisation','Space utilisation quality','Performance']];
  const [showSignals,setShowSignals]=useState(false);
  const [sigForm,setSigForm]=useState({});
  const [sigCsv,setSigCsv]=useState('');
  const [sigSaving,setSigSaving]=useState(false);
  const [sigMsg,setSigMsg]=useState('');
  const submitSignals=async()=>{
    const signals=Object.entries(sigForm).map(([metric,value])=>({metric,value})).filter(s=>s.value!==''&&s.value!=null&&!isNaN(Number(s.value)));
    // EW2 — merge pasted CSV rows: metric,value[,confidence]
    if(sigCsv&&sigCsv.trim()){
      sigCsv.trim().split('\n').forEach(line=>{
        const [m,v,c]=line.split(',').map(x=>(x||'').trim());
        if(m&&v!==''&&v!=null&&!isNaN(Number(v))){
          const sig={metric:m.toLowerCase().replace(/[ -]/g,'_'),value:Number(v)};
          if(c&&!isNaN(Number(c)))sig.confidence=Number(c);
          signals.push(sig);
        }
      });
    }
    if(!signals.length){setSigMsg('Enter a value above or paste CSV (metric,value).');return;}
    setSigSaving(true);setSigMsg('');
    try{
      const r=await authFetch('/api/experience/signal',{method:'POST',body:{signals}});
      const t=r.tws||{};
      setSigMsg(`✓ ${r.updated.length} standard(s) updated — TWS ${t.composite??'—'} · ${t.coverage??0}% measured.`);
      setSigForm({});setSigCsv('');
      if(refresh)refresh();
      setTimeout(()=>setShowSignals(false),900);
    }catch(e){ setSigMsg('Could not save — check the values and try again.'); }
    finally{ setSigSaving(false); }
  };
  // EW5 — verify the outcome of an intervention (re-measure → Safe Decision + TWS delta).
  const [showOutcome,setShowOutcome]=useState(false);
  const [outForm,setOutForm]=useState({});
  const [outSaving,setOutSaving]=useState(false);
  const [outMsg,setOutMsg]=useState('');
  const submitOutcome=async()=>{
    if(!outForm.standard||outForm.after===''||outForm.after==null||isNaN(Number(outForm.after))){setOutMsg('Pick a standard and enter the new measured value (0–100).');return;}
    setOutSaving(true);setOutMsg('');
    try{
      const r=await authFetch('/api/experience/outcome',{method:'POST',body:{standard:outForm.standard,after:Number(outForm.after),action:outForm.action||undefined,note:outForm.note||undefined}});
      const d=r.delta; const sign=d>0?'+':'';
      setOutMsg(`✓ Verified — ${r.before??'—'} → ${r.after}${d!=null?` (${sign}${d})`:''}, recorded as a Decision. TWS ${r.tws&&r.tws.composite!=null?r.tws.composite:'—'}.`);
      setOutForm({});
      if(refresh)refresh();
      setTimeout(()=>setShowOutcome(false),1100);
    }catch(e){ setOutMsg('Could not record outcome — try again.'); }
    finally{ setOutSaving(false); }
  };
  // EW3 — prefer the org's live TWS; fall back to demo constants (preview/default tenant).
  const twsLive=_tws&&_tws.available?_tws:null;
  const twsComposite=twsLive?twsLive.composite:TWS_COMPOSITE;
  const twsP={flow:twsLive?(twsLive.pillars.flow??0):TWS_PILLARS.flow,
              culture:twsLive?(twsLive.pillars.culture??0):TWS_PILLARS.culture,
              perf:twsLive?(twsLive.pillars.performance??0):TWS_PILLARS.perf};
  // Derive first name for greeting — falls back to 'Shegun' in dev mode (authEnabled=false)
  // A user-supplied first name (from the gentle Sage ask below) is stored locally
  // and takes precedence over the identity-derived name.
  const [firstNameOverride,setFirstNameOverride]=useState(()=>{try{return (localStorage.getItem('stage_first_name')||'').trim();}catch(e){return '';}});
  const [nameAskDismissed,setNameAskDismissed]=useState(()=>{try{return localStorage.getItem('stage_name_ask_dismissed')==='1';}catch(e){return false;}});
  // The identity-derived first name, or '' when we genuinely can't determine a
  // real one. currentUser is enriched from the Clerk client object upstream, so
  // this now sees first name / full name / username / email even when the JWT
  // carried none of them. Only a truly nameless account yields '' (→ Sage asks).
  const derivedFirstName=deriveFirstName(currentUser);
  const firstName=!currentUser?.authEnabled ? 'Shegun'
    : (firstNameOverride || derivedFirstName || 'Servant leader');
  // We have a real first name when in dev, when the user provided one, or when the
  // account carries one. Otherwise we greet "Servant leader" and let Sage ask.
  const haveRealFirstName=!currentUser?.authEnabled || !!firstNameOverride || !!derivedFirstName;
  const showNameAsk=currentUser&&!haveRealFirstName&&!nameAskDismissed;
  const [nameInput,setNameInput]=useState('');
  const saveFirstName=(v)=>{const t=(v||'').trim().slice(0,40);if(!t)return;try{localStorage.setItem('stage_first_name',t);}catch(e){}setFirstNameOverride(t);};
  const dismissNameAsk=()=>{try{localStorage.setItem('stage_name_ask_dismissed','1');}catch(e){}setNameAskDismissed(true);};

  // ── Canonical portfolio metrics (single source of truth) ───────────────────
  // Fetch the server-computed figures so the brief narrative AND the KPI cards
  // quote the SAME numbers (previously the narrative used hardcoded literals
  // while the cards summed live data — they diverged). Falls back to computing
  // from the live portfolio with the identical method until the fetch resolves.
  const [metrics,setMetrics]=useState(null);
  useEffect(()=>{ authFetch('/api/portfolio/metrics').then(d=>{ if(d&&typeof d.totalSf==='number') setMetrics(d); }).catch(()=>{}); },[authFetch]);
  const m=metrics||(()=>{
    const totalSf=_p.reduce((s,l)=>s+(l.sf||0),0);
    const annualRent=_p.reduce((s,l)=>s+(l.rent||0),0);
    const marketCount=[...new Set(_p.map(l=>l.market).filter(Boolean))].length;
    const portfolioUtilization=totalSf?Math.round(_p.reduce((s,l)=>s+(l.util||0)*(l.sf||0),0)/totalSf):0;
    const _rc=rentByCcy(_p);  // F-CUR-01 — currency fields mirror the API metrics
    return {totalSf,annualRent,locationCount:_p.length,marketCount,portfolioUtilization,
            mixedCurrency:_rc.mixed,currencies:_rc.currencies,primaryCurrency:_rc.currencies[0]||'USD'};
  })();
  // F-CUR-01 — the annual-rent headline is currency-safe: a single figure only
  // when the portfolio is one currency; otherwise "Multi-currency" (the per-
  // currency breakdown lives on the Financial Surface), never a summed-across total.
  const _mRent=(m.mixedCurrency)?'Multi-currency':fmtCur(m.annualRent,m.primaryCurrency||'USD');

  // ── Billing subscription state ─────────────────────────────────────────────
  const [billingData,setBillingData]=useState(null);   // null=loading, false=no sub
  const [portalLoading,setPortalLoading]=useState(false);
  useEffect(()=>{
    if(!billingEnabled)return;
    authFetch('/api/billing/subscription')
      .then(r=>setBillingData(r||false))
      .catch(()=>setBillingData(false));
  },[billingEnabled]);
  const openBillingPortal=async()=>{
    setPortalLoading(true);
    try{
      const r=await authFetch('/api/billing/portal',{method:'POST',body:{return_url:location.href}});
      if(r?.portalUrl)location.href=r.portalUrl;
    }catch(e){}
    finally{setPortalLoading(false);}
  };
  const todayAlerts=_a.filter(a=>a.priority==='P1'||a.status==='Overdue').slice(0,4);
  const horizon=_a.filter(a=>a.priority==='P2').slice(0,4);
  const activeDeals=_d.filter(d=>d.stage!=='Post-Close');

  // ── Live daily brief (generated by Claude at 07:00 UTC via _daily_brief_task) ──
  const [liveBrief,setLiveBrief]=useState(null);   // null = not yet fetched
  const [briefLoading,setBriefLoading]=useState(true);
  const [briefGenerating,setBriefGenerating]=useState(false);
  useEffect(()=>{
    authFetch('/api/brief/today')
      .then(r=>{if(r&&r.summary)setLiveBrief(r);})
      .catch(()=>{})  // 404 before 07:00 UTC — fall back to static text
      .finally(()=>setBriefLoading(false));
  },[]);
  const generateBrief=async(force=false)=>{
    setBriefGenerating(true);
    try{
      const r=await authFetch('/api/brief/generate',{method:'POST',body:{force}});
      if(r&&r.summary)setLiveBrief(r);
    }catch(e){}
    finally{setBriefGenerating(false);}
  };

  // Derive brief meta for the header chip
  const briefAge=(()=>{
    if(!liveBrief?.generatedAt)return null;
    const diff=Math.round((Date.now()-new Date(liveBrief.generatedAt).getTime())/60000);
    if(diff<2)return'just now';
    if(diff<60)return`${diff} min ago`;
    return`${Math.floor(diff/60)}h ago`;
  })();

  return(
    <div style={{maxWidth:1100,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:10}}>
        <div>
          <div className="page-header-label">Status Brief</div>
          <div className="page-title">{(()=>{const h=new Date().getHours();return(h<12?'Good morning':h<17?'Good afternoon':'Good evening')+', '+_titleFirst(firstName)})()}</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.45)',marginTop:3}}>{new Date().toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric'})} · {new Date().toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',timeZoneName:'short'})}</div>
          {/* Gentle Sage ask for the first name — only when we don't have a real one.
              Dismissible and one-shot (persisted): Sage offers, never insists. */}
          {showNameAsk&&(
            <div style={{display:'flex',alignItems:'center',gap:8,marginTop:10,padding:'8px 12px',background:'rgba(5,150,105,.06)',border:'1px solid rgba(5,150,105,.2)',borderRadius:10,flexWrap:'wrap',maxWidth:580}}>
              <span style={{fontSize:11,fontWeight:700,color:'#059669',whiteSpace:'nowrap'}}>✦ Sage</span>
              <span style={{fontSize:12.5,color:'rgba(61,31,13,.7)'}}>I didn’t catch your first name — what should I call you?</span>
              <input value={nameInput} onChange={e=>setNameInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter')saveFirstName(nameInput);}} placeholder="First name" maxLength={40}
                style={{flex:'0 1 150px',minWidth:100,border:'1px solid rgba(5,150,105,.3)',borderRadius:7,padding:'5px 9px',fontSize:12.5,fontFamily:'inherit',outline:'none'}}/>
              <button onClick={()=>saveFirstName(nameInput)} disabled={!nameInput.trim()}
                style={{padding:'5px 13px',border:'none',borderRadius:7,background:nameInput.trim()?'#059669':'rgba(61,31,13,.2)',color:'#fff',fontSize:12,fontWeight:700,cursor:nameInput.trim()?'pointer':'default',fontFamily:'inherit'}}>Save</button>
              <button onClick={dismissNameAsk} title="Maybe later" aria-label="Dismiss"
                style={{border:'none',background:'none',fontSize:16,color:'rgba(61,31,13,.35)',cursor:'pointer',lineHeight:1,marginLeft:2}}>×</button>
            </div>
          )}
        </div>
        <div className="card" style={{padding:'8px 14px',display:'flex',alignItems:'center',gap:8}}>
          <span style={{color:liveBrief?'#E8813A':'rgba(61,31,13,.3)',fontSize:14}}>⚡</span>
          <span style={{fontSize:12,color:'rgba(61,31,13,.6)'}}>
            {briefLoading
              ? 'Loading brief…'
              : liveBrief
                ? `AI Brief · ${briefAge}`
                : 'No brief yet for today'}
          </span>
          {liveBrief&&<span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:'#d1fae5',color:'#065f46'}}>LIVE</span>}
          <button
            disabled={briefGenerating||briefLoading}
            onClick={()=>generateBrief(!!liveBrief)}
            title={liveBrief?'Regenerate brief from current data':'Generate today\'s brief now'}
            style={{padding:'4px 10px',border:'1px solid rgba(232,129,58,.3)',borderRadius:6,background:briefGenerating?'rgba(232,129,58,.08)':'rgba(232,129,58,.1)',color:'#E8813A',fontSize:10,fontWeight:700,cursor:briefGenerating?'not-allowed':'pointer',fontFamily:'inherit',whiteSpace:'nowrap',transition:'background .15s'}}
          >{briefGenerating?'Generating…':liveBrief?'Refresh':'Generate'}</button>
        </div>
      </div>

      {/* v1.2 §Reveal — hero-slot continuity: their work is the first thing they see */}
      {showFirstFinding&&<FirstFindingHero finding={_heroFinding} onDismiss={dismissHeroFinding} onNav={onNav}/>}
      {showGroundedLoading&&(
        <div className="card joy-glow" style={{padding:22,marginBottom:18,borderLeft:'4px solid #059669',fontSize:13,color:'rgba(61,31,13,.6)',display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:15}}>✦</span> Sage is reading your Portfolio Spine records to prepare a grounded baseline…
        </div>
      )}

      {/* Doc 2 — One Signal: the primary anchor for a populated account */}
      {!showGuided&&!showFirstFinding&&!showGroundedLoading&&<OneSignal onNav={onNav}/>}

      {/* Gap Patch §Gap 01 — zero-data guided first workflow (blank real account) */}
      {showGuided&&<GuidedWorkflow onNav={onNav} onBypass={dismissGuided}/>}

      {/* Portfolio Pulse — live when brief is available, static fallback otherwise */}
      <div className="card" style={{padding:20,borderLeft:`4px solid ${liveBrief?'#059669':'#8b0020'}`,marginBottom:18,transition:'border-color .3s'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:14,color:liveBrief?'#059669':'#8b0020'}}>↗</span>
            <span style={{fontSize:11,fontWeight:700,color:liveBrief?'#059669':'#8b0020',textTransform:'uppercase',letterSpacing:'.07em'}}>Portfolio Pulse</span>
          </div>
          {liveBrief&&(
            <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:4,background:'rgba(5,150,105,.08)',color:'#059669',border:'1px solid rgba(5,150,105,.2)'}}>
              {liveBrief.actionCount} open · {liveBrief.overdueCount} overdue · {liveBrief.safeCount} Safe entries
            </span>
          )}
        </div>
        {liveBrief
          ? <p style={{fontSize:13.5,lineHeight:1.75,color:'rgba(61,31,13,.85)',margin:0,whiteSpace:'pre-wrap'}}>{liveBrief.summary}</p>
          : briefLoading
            ? <div style={{height:72,background:'rgba(61,31,13,.05)',borderRadius:8,animation:'pulse 1.5s ease-in-out infinite'}}/>
            : isDemo
              ? <p style={{fontSize:13.5,lineHeight:1.7,color:'rgba(61,31,13,.85)'}}>
                  Your portfolio of <b>{m.totalSf.toLocaleString()} SF across {m.locationCount} location{m.locationCount!==1?'s':''}</b> carries <b>{_mRent} in annual occupancy cost</b>. Three assets are critical — the 1 Penn Plaza renewal option expires in 59 days with negotiations below velocity threshold, the 200 Park Avenue lease has expired without action, and Houston 1000 Main St expires July 31. Portfolio TWS is <b>{twsComposite} and declining</b> — the Culture pillar at {twsP.culture} signals a human capital challenge at the same locations carrying the highest lease risk. Settings drift at 1 Penn Plaza (Community Exchange, Floor 14) directly precedes the Culture decline Sage has been flagging. <b style={{color:'#E8813A'}}>Recommended priority: resolve the 1 Penn Plaza negotiation deadlock before June 30.</b>
                </p>
              : <p style={{fontSize:13.5,lineHeight:1.7,color:'rgba(61,31,13,.55)'}}>
                  No brief yet — Sage assembles your daily Portfolio Pulse here as your portfolio data grows. Tap <b>Generate</b> above to create one from your current data.
                </p>
        }
      </div>

      {/* KPIs — live from DataCtx portfolio + actions */}
      {(()=>{
        // Canonical: critical = (P1 OR overdue) AND not done; overdue is derived.
        const _td=new Date().toISOString().slice(0,10);
        const critCount=_a.filter(a=>taskIsOutstanding(a)&&(a.priority==='P1'||taskIsOverdue(a,_td))).length;
        const overdueCount=_a.filter(a=>taskIsOverdue(a,_td)).length;
        // Read totals from the canonical metrics `m` — same source as the
        // narrative above, so the brief and the cards can never disagree.
        const fmtSF=m.totalSf>=1000?m.totalSf.toLocaleString():'—';
        const fmtRent=_mRent;
        return(
          <div className="grid-4" style={{marginBottom:18}}>
            <KPI label="Total SF" value={fmtSF} sub={`${m.locationCount} location${m.locationCount!==1?'s':''}, ${m.marketCount} market${m.marketCount!==1?'s':''}`} barPct={Math.round(m.totalSf/2000)}/>
            <KPI label="Annual Rent" value={fmtRent} sub={m.mixedCurrency?`${m.currencies.length} currencies — see Financial Surface`:"Total occupancy cost"} barPct={72}/>
            <KPI label="Critical Alerts" value={String(critCount)} sub={overdueCount>0?`${overdueCount} overdue`:'0 overdue'} warn={critCount>0} barPct={Math.min(critCount*20,100)}/>
            {/* TWS is a Sense-domain score without a canonical lease-data source yet
                (flagged for follow-up). Shown as portfolio utilisation from metrics. */}
            <KPI label="Portfolio Utilization" value={`${m.portfolioUtilization}%`} sub="SF-weighted occupancy" warn={m.portfolioUtilization<55} barPct={m.portfolioUtilization}/>
          </div>
        );
      })()}

      {/* Billing card — only shown when BILLING_ENABLED=true */}
      {billingEnabled&&(
        <div className="card" style={{padding:16,marginBottom:18,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:12,flex:1,minWidth:0}}>
            <div style={{width:36,height:36,borderRadius:8,background:'rgba(139,0,33,.08)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,fontSize:16}}>💳</div>
            {billingData===null?(
              <div style={{fontSize:13,color:'rgba(61,31,13,.4)'}}>Loading billing…</div>
            ):billingData===false||billingData?.error?(
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>No active subscription</div>
                  <div style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>Subscribe to unlock credit tracking and metered billing</div>
                </div>
                <a href="/pricing" style={{padding:'5px 12px',borderRadius:6,background:'#8b0020',color:'#fff',fontSize:12,fontWeight:700,textDecoration:'none',whiteSpace:'nowrap'}}>View plans →</a>
              </div>
            ):(
              <div style={{display:'flex',alignItems:'center',gap:20,flex:1,flexWrap:'wrap'}}>
                {/* Plan badge */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:2}}>Plan</div>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <span style={{fontSize:14,fontWeight:800,color:'#3D1F0D',textTransform:'capitalize'}}>{billingData.plan}</span>
                    <span style={{fontSize:10,padding:'1px 7px',borderRadius:20,fontWeight:700,background:billingData.status==='active'?'#d1fae5':billingData.status==='past_due'?'#fef3c7':'#fee2e2',color:billingData.status==='active'?'#065f46':billingData.status==='past_due'?'#92400e':'#991b1b'}}>{billingData.status}</span>
                  </div>
                </div>
                {/* Credit bar */}
                <div style={{flex:1,minWidth:140}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                    <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em'}}>Credits</span>
                    <span style={{fontSize:11,fontWeight:700,color:billingData.creditsRemaining===0?'#dc2626':'#3D1F0D'}}>{billingData.creditsUsed}/{billingData.creditsIncluded}</span>
                  </div>
                  <div style={{height:5,background:'rgba(61,31,13,.08)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${Math.min(100,(billingData.creditsUsed/Math.max(1,billingData.creditsIncluded))*100)}%`,background:billingData.creditsRemaining===0?'#dc2626':'#059669',borderRadius:3,transition:'width .4s'}}/>
                  </div>
                  <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:3}}>{billingData.creditsRemaining} remaining this period</div>
                </div>
                {/* Renewal */}
                {billingData.currentPeriodEnd&&(
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:2}}>Renews</div>
                    <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D'}}>{new Date(billingData.currentPeriodEnd).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
                    {billingData.cancelAtPeriodEnd&&<div style={{fontSize:10,color:'#dc2626',fontWeight:600}}>Cancels then</div>}
                  </div>
                )}
              </div>
            )}
          </div>
          {billingData&&billingData!==false&&!billingData.error&&(
            <button onClick={openBillingPortal} disabled={portalLoading} style={{padding:'7px 14px',borderRadius:7,border:'1px solid rgba(61,31,13,.15)',background:'#fff',color:'#3D1F0D',fontSize:12,fontWeight:700,cursor:portalLoading?'not-allowed':'pointer',whiteSpace:'nowrap',fontFamily:'inherit'}}>
              {portalLoading?'Opening…':'Manage billing →'}
            </button>
          )}
        </div>
      )}

      {/* EW2 — measured signal entry modal */}
      {showSignals && (
        <FormModal title="Update measured signals" onClose={()=>setShowSignals(false)} onSubmit={submitSignals} submitting={sigSaving}>
          <div style={{fontSize:12,color:'rgba(61,31,13,.6)',marginBottom:10,lineHeight:1.5}}>Enter measured values (0–100) from pulse, badge, or booking data. Blank = leave unchanged. A measured value upgrades that standard from <b>Assumption</b> to <b>Fact</b> and raises TWS confidence.</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            {SIGNAL_FIELDS.map(([k,label,pillar])=>(
              <Field key={k} label={`${label} · ${pillar}`}>
                <input type="number" min="0" max="100" style={inputStyle} value={sigForm[k]||''} placeholder="0–100" onChange={e=>{setSigMsg('');setSigForm(f=>({...f,[k]:e.target.value}));}}/>
              </Field>
            ))}
          </div>
          <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid rgba(61,31,13,.08)'}}>
            <div style={{fontSize:11,fontWeight:600,color:'rgba(61,31,13,.5)',marginBottom:4}}>…or paste CSV (metric,value[,confidence])</div>
            <textarea value={sigCsv} placeholder={"co_presence,62,70\nbelonging,55\nfocus_fit,71"} rows={3} onChange={e=>{setSigMsg('');setSigCsv(e.target.value);}} style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.18)',borderRadius:8,padding:'8px 11px',fontSize:12,fontFamily:'monospace',color:'#3D1F0D',background:'#fff',outline:'none',resize:'vertical'}}/>
          </div>
          {sigMsg&&<div style={{marginTop:10,fontSize:12,color:sigMsg.startsWith('✓')?'#059669':'#9A3412'}}>{sigMsg}</div>}
        </FormModal>
      )}

      {/* EW5 — verify outcome modal (re-measure → Safe Decision) */}
      {showOutcome && (
        <FormModal title="Verify outcome" onClose={()=>setShowOutcome(false)} onSubmit={submitOutcome} submitting={outSaving}>
          <div style={{fontSize:12,color:'rgba(61,31,13,.6)',marginBottom:10,lineHeight:1.5}}>Re-measure a standard after an intervention. The before→after delta is recorded as an immutable <b>Decision</b> in the Safe and moves TWS — "TWS moved +N after action Y, verified."</div>
          <Field label="Standard">
            <select style={inputStyle} value={outForm.standard||''} onChange={e=>{setOutMsg('');setOutForm(f=>({...f,standard:e.target.value}));}}>
              <option value="">Select…</option>
              {SIGNAL_FIELDS.map(([k,label])=>(<option key={k} value={k}>{label}</option>))}
            </select>
          </Field>
          <Field label="New measured value (0–100)">
            <input type="number" min="0" max="100" style={inputStyle} value={outForm.after||''} placeholder="0–100" onChange={e=>{setOutMsg('');setOutForm(f=>({...f,after:e.target.value}));}}/>
          </Field>
          <Field label="Linked action (optional)">
            <input style={inputStyle} value={outForm.action||''} placeholder="e.g. Add Community Exchange anchor" onChange={e=>setOutForm(f=>({...f,action:e.target.value}))}/>
          </Field>
          <Field label="Note (optional)">
            <input style={inputStyle} value={outForm.note||''} placeholder="What changed" onChange={e=>setOutForm(f=>({...f,note:e.target.value}))}/>
          </Field>
          {outMsg&&<div style={{marginTop:10,fontSize:12,color:outMsg.startsWith('✓')?'#059669':'#9A3412'}}>{outMsg}</div>}
        </FormModal>
      )}

      {/* EW1 — import a Sense audit as the experience baseline. Shown only for a
          fresh org: /api/tws resolved with no baseline AND no locations yet (so it
          never clutters the demo/preview tenant, which renders the EI panel). */}
      {_tws && !_tws.available && _p.length===0 && (
        <div className="card" style={{padding:18,marginBottom:18,borderLeft:'4px solid #E8813A'}}>
          <div style={{fontSize:11,fontWeight:700,color:'#E8813A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Set your experience baseline</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.7)',marginBottom:12,lineHeight:1.5}}>
            Already ran a <b>Sense</b> workplace audit? Paste its link or ID to seed your Time Well Spent baseline,
            key findings, and first actions. (Self-reported → recorded as Assumptions until live signals refine them.)
          </div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <input value={importId} onChange={e=>{setImportMsg('');setImportId(e.target.value);}}
              placeholder="Sense audit link or session ID"
              style={{flex:1,minWidth:240,border:'1px solid rgba(61,31,13,.18)',borderRadius:8,padding:'9px 12px',fontSize:13,color:'#3D1F0D',background:'#fff',fontFamily:'inherit',outline:'none'}}/>
            <button onClick={doImportBaseline} disabled={importing}
              style={{padding:'9px 16px',borderRadius:8,border:'none',background:'#E8813A',color:'#fff',fontSize:13,fontWeight:700,cursor:importing?'not-allowed':'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>
              {importing?'Importing…':'Import baseline'}
            </button>
          </div>
          {importMsg&&<div style={{marginTop:10,fontSize:12,color:importMsg.startsWith('✓')?'#059669':'#9A3412'}}>{importMsg}</div>}
        </div>
      )}

      {/* Experience Intelligence — TWS is a frontend demo metric with no live
          source yet, so hide the whole panel until the org has locations to avoid
          showing fabricated scores (e.g. "TWS 63 · 8 locations") to an empty org. */}
      {(_p.length>0 || twsLive) && (
      <div className="card" style={{padding:18,marginBottom:18,borderLeft:'4px solid #8b0020'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
          <span style={{fontSize:11,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em'}}>Experience Intelligence</span>
          <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>TWS · Settings · Standards</span>
          <button onClick={()=>{setSigMsg('');setShowSignals(true);}} style={{marginLeft:'auto',padding:'4px 10px',borderRadius:6,border:'1px solid rgba(139,0,33,.25)',background:'#fff',color:'#8b0020',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Update signals</button>
          <button onClick={()=>{setOutMsg('');setShowOutcome(true);}} style={{padding:'4px 10px',borderRadius:6,border:'1px solid rgba(5,150,105,.3)',background:'#fff',color:'#059669',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Verify outcome</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1px 1fr',gap:0}}>
          {/* TWS */}
          <div style={{paddingRight:20}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Time Well Spent</div>
            <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:10}}>
              <ScoreRing score={twsComposite} size={68} color='#8b0020' label="TWS" delay={200}/>
              <div>
                <div style={{display:'flex',alignItems:'baseline',gap:8}}>
                  <span style={{fontSize:36,fontWeight:900,color:'#8b0020',lineHeight:1}}>{twsComposite}</span>
                  <span style={{fontSize:12,fontWeight:600,color:'#dc2626'}}>↓3 wk</span>
                </div>
                <div style={{fontSize:11,color:'rgba(61,31,13,.35)',marginTop:2}}>composite score</div>
              </div>
            </div>
            <div style={{display:'flex',gap:8}}>
              {[['Flow',String(twsP.flow),'#2563EB',twsLive?'':'+1'],['Culture',String(twsP.culture),'#8b0020',twsLive?'':'−6 ▼'],['Perf',String(twsP.perf),'#059669',twsLive?'':'−1']].map(([l,v,c,d])=>(
                <div key={l} style={{flex:1,textAlign:'center',padding:'8px 4px',borderRadius:8,background:c==='#8b0020'?'#fef2f2':c==='#2563EB'?'#eff6ff':'#f0fdf4',border:`1px solid ${c}22`}}>
                  <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase'}}>{l}</div>
                  <div style={{fontSize:9,color:d.includes('▼')?'#dc2626':'rgba(61,31,13,.4)'}}>{d}</div>
                </div>
              ))}
            </div>
            <div style={{marginTop:8,fontSize:10,color:'rgba(61,31,13,.35)'}}>Standards met: <b style={{color:'#d97706'}}>67%</b> · Cost/experience: <b>$847</b>/qualifying visit</div>
          </div>
          {/* Divider */}
          <div style={{background:'rgba(61,31,13,.08)',margin:'0 20px'}}/>
          {/* RBW */}
          <div style={{paddingLeft:4}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Settings Scorecard</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
              {[
                ['Avg Coverage','69','/100','#d97706'],
                ['Design Fidelity','59%','of zones','#d97706'],
                ['Recovery Friction','P1','1 location','#dc2626'],
                ['Critical Zones','3','below hypothesis','#d97706'],
              ].map(([l,v,sub,c])=>(
                <div key={l} style={{padding:'8px 10px',borderRadius:8,background:'rgba(245,237,224,.6)',border:'1px solid rgba(61,31,13,.07)'}}>
                  <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',marginBottom:3}}>{l}</div>
                  <div style={{fontSize:18,fontWeight:800,color:c}}>{v}</div>
                  <div style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>{sub}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>{twsLive?`Live TWS · ${twsLive.coverage}% of standards measured · confidence ${twsLive.confidence}% · ${twsLive.basis}`:'Based on the relationship-based workplace framework · 8 locations · 6 setting types'}</div>
          </div>
        </div>
        {/* Strat Signals strip */}
        <div style={{borderTop:'1px solid rgba(61,31,13,.07)',marginTop:14,paddingTop:14}}>
          <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Experience Strat Signals</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8}}>
            {RBW_STRAT_INSIGHTS.filter((_,i)=>i<2).map((s,i)=>(
              <div key={i} style={{padding:'10px 12px',borderRadius:8,background:'rgba(232,129,58,.04)',border:'1px solid rgba(232,129,58,.14)',display:'flex',gap:10,alignItems:'flex-start'}}>
                <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:4,background:'rgba(139,0,33,.08)',color:'#8b0020',flexShrink:0,marginTop:1,whiteSpace:'nowrap'}}>{s.type.replace(' Alert','').toUpperCase()}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',lineHeight:1.35,marginBottom:2}}>{s.location}</div>
                  <div style={{fontSize:11,color:'rgba(61,31,13,.55)',lineHeight:1.45}}>→ {s.action.split('.')[0]}.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:18}}>
        {/* Today's Alerts */}
        <div className="card" style={{padding:18}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
            <span style={{fontSize:15}}>⚠️</span>
            <span style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>Today's Alerts</span>
            <span style={{marginLeft:'auto',fontSize:11,color:'rgba(61,31,13,.4)'}}>{todayAlerts.length} critical</span>
          </div>
          {todayAlerts.map((a,i)=>(
            <div key={a.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:a.status==='Overdue'?'#fef2f2':'#fff8f5',borderRadius:10,border:a.status==='Overdue'?'1px solid #fca5a5':'1px solid #fed7aa',marginBottom:8,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*65}ms`}}>
              <span className={agentBadge(a.agent)} style={{whiteSpace:'nowrap',marginTop:1}}>{a.agent}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:a.status==='Overdue'?'#dc2626':'#3D1F0D',lineHeight:1.4}}>{a.title}</div>
                <div style={{fontSize:11,color:'rgba(61,31,13,.45)',marginTop:2}}>{a.asset} · Due {a.due}</div>
              </div>
              {a.status==='Overdue'&&<span style={{background:'#ef4444',color:'#fff',fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:999,whiteSpace:'nowrap'}}>OVERDUE</span>}
            </div>
          ))}
        </div>

        {/* This Week's Horizon */}
        <div className="card" style={{padding:18}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
            <span style={{fontSize:15}}>🕐</span>
            <span style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>This Week's Horizon</span>
            <span style={{marginLeft:'auto',fontSize:11,color:'rgba(61,31,13,.4)'}}>7-day window</span>
          </div>
          {horizon.map((a,i)=>(
            <div key={a.id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',background:'#fffbeb',borderRadius:10,border:'1px solid #fcd34d',marginBottom:8,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*65}ms`}}>
              <span className={agentBadge(a.agent)} style={{whiteSpace:'nowrap',marginTop:1}}>{a.agent}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',lineHeight:1.4}}>{a.title}</div>
                <div style={{display:'flex',alignItems:'center',gap:8,marginTop:3}}>
                  <span style={{fontSize:10,fontWeight:700,background:priorityColors[a.priority].bg,color:'#fff',padding:'1px 6px',borderRadius:4}}>{a.priority}</span>
                  <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{a.due}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Deals */}
      <div className="card" style={{padding:18}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
          <span style={{fontWeight:700,fontSize:13}}>Active Transactions</span>
          <button onClick={()=>onNav('setting')} style={{fontSize:12,color:'#E8813A',background:'none',border:'none',cursor:'pointer',fontWeight:600}}>View Setting →</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
          {activeDeals.map(d=>(
            <div key={d.id} style={{padding:12,background:'#F5EDE0',borderRadius:10,cursor:'pointer',transition:'background .15s'}} onMouseOver={e=>e.currentTarget.style.background='#fff'} onMouseOut={e=>e.currentTarget.style.background='#F5EDE0'}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:10,fontWeight:700,color:stageColors[d.stage],background:stageColors[d.stage]+'18',padding:'2px 8px',borderRadius:999}}>{d.stage}</span>
                <span style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)'}}>{d.velocity}/10</span>
              </div>
              <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',lineHeight:1.3}}>{d.name}</div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginTop:2}}>{Math.abs(d.sf).toLocaleString()} SF · {d.market}</div>
              <div style={{fontSize:11,color:'#E8813A',marginTop:6,fontWeight:500}}>{d.nextMilestone}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Shared form modal ──────────────────────────────────────────────────────── */
function FormModal({title,onClose,onSubmit,submitting,children}){
  return(
    <div style={{position:'fixed',inset:0,zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(61,31,13,.35)',backdropFilter:'blur(2px)'}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:14,boxShadow:'0 24px 80px rgba(61,31,13,.18)',padding:28,width:480,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <div style={{fontSize:15,fontWeight:800,color:'#3D1F0D'}}>{title}</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:22,color:'rgba(61,31,13,.35)',lineHeight:1}}>×</button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {children}
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:22,paddingTop:16,borderTop:'1px solid rgba(61,31,13,.08)'}}>
          <button onClick={onClose} className="btn-ghost" style={{fontSize:13}}>Cancel</button>
          <button onClick={onSubmit} disabled={submitting} className="btn-primary" style={{fontSize:13,opacity:submitting?.5:1}}>
            {submitting?'Saving…':'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Labelled form field */
function Field({label,required,children}){
  return(
    <div>
      <label style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.55)',display:'block',marginBottom:5,textTransform:'uppercase',letterSpacing:'.04em'}}>
        {label}{required&&<span style={{color:'#dc2626',marginLeft:3}}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle={width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'8px 11px',fontSize:13,color:'#3D1F0D',background:'#F5EDE0',fontFamily:'inherit',outline:'none'};
const selectStyle={...inputStyle,cursor:'pointer'};

// Document link widget — PRD §6.9: documents are linked not hosted
function LinkDocumentWidget({dealId,onLinked}){
  const [open,setOpen]=useState(false);
  const [form,setForm]=useState({name:'',url:'',source:'Drive',type:'LOI'});
  const [saved,setSaved]=useState(false);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const authFetch=useAuthFetch();
  const setF=k=>e=>setForm(f=>({...f,[k]:e.target.value}));
  const submit=async()=>{
    if(!form.name.trim()||!form.url.trim())return;
    if(!dealId){setErr('No deal selected.');return;}
    setSaving(true);setErr('');
    try{
      const r=await authFetch(`/api/dealroom/deals/${dealId}/documents/link`,{method:'POST',body:{
        name:form.name.trim(),url:form.url.trim(),source:form.source,type:form.type,
      }});
      if(r&&r.ok){setSaved(true);onLinked&&onLinked();}
      else setErr((r&&(r.detail||r.error))||'Could not link the document.');
    }catch(e){setErr('Could not link the document — please retry.');}
    finally{setSaving(false);}
  };
  if(!open)return(
    <button onClick={()=>{setOpen(true);setSaved(false);}} style={{width:'100%',padding:'11px',border:'2px dashed rgba(61,31,13,.15)',borderRadius:10,background:'transparent',fontSize:12,color:'rgba(61,31,13,.4)',cursor:'pointer',fontFamily:'inherit',transition:'all .15s',display:'flex',alignItems:'center',justifyContent:'center',gap:8}} onMouseOver={e=>{e.currentTarget.style.borderColor='#D97706';e.currentTarget.style.color='#D97706';}} onMouseOut={e=>{e.currentTarget.style.borderColor='rgba(61,31,13,.15)';e.currentTarget.style.color='rgba(61,31,13,.4)';}}>
      <span style={{fontSize:14}}>⛓</span> Link document from Drive · SharePoint · Dropbox · Box
    </button>
  );
  if(saved)return(
    <div style={{padding:'12px',borderRadius:10,background:'#f0fdf4',border:'1px solid #bbf7d0',display:'flex',alignItems:'center',gap:10}}>
      <span style={{color:'#16a34a',fontSize:16}}>✓</span>
      <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:'#16a34a'}}>Document linked</div><div style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>Reference recorded in Setting — document custody remains with you.</div></div>
      <button onClick={()=>{setOpen(false);setSaved(false);setForm({name:'',url:'',source:'Drive',type:'LOI'});}} style={{border:'none',background:'none',fontSize:16,color:'rgba(61,31,13,.3)',cursor:'pointer'}}>×</button>
    </div>
  );
  return(
    <div style={{border:'1px solid rgba(61,31,13,.1)',borderRadius:10,padding:14,background:'rgba(245,237,224,.4)'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D'}}>Link a Document</span>
        <button onClick={()=>setOpen(false)} style={{border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.3)',cursor:'pointer',lineHeight:1}}>×</button>
      </div>
      <div style={{display:'grid',gap:10}}>
        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:8}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>Document name</div>
            <input value={form.name} onChange={setF('name')} placeholder="e.g. Draft Lease v4" style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'#3D1F0D',background:'#fff',fontFamily:'inherit',outline:'none'}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>Storage</div>
            <select value={form.source} onChange={setF('source')} style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'#3D1F0D',background:'#fff',fontFamily:'inherit',outline:'none',cursor:'pointer'}}>
              {['Drive','SharePoint','Dropbox','Box','Other'].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>Document URL</div>
          <input value={form.url} onChange={setF('url')} placeholder="https://docs.google.com/…" style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'#3D1F0D',background:'#fff',fontFamily:'inherit',outline:'none'}}/>
        </div>
        {err&&<div style={{fontSize:10.5,color:'#dc2626',marginTop:2}}>{err}</div>}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:2}}>
          <button onClick={()=>setOpen(false)} style={{padding:'7px 14px',border:'1px solid rgba(61,31,13,.15)',borderRadius:7,background:'transparent',fontSize:11,fontWeight:600,color:'rgba(61,31,13,.5)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
          <button disabled={!form.name.trim()||!form.url.trim()||saving} onClick={submit} style={{padding:'7px 14px',border:'none',borderRadius:7,background:form.name&&form.url&&!saving?'#D97706':'rgba(217,119,6,.4)',color:'#fff',fontSize:11,fontWeight:700,cursor:form.name&&form.url&&!saving?'pointer':'not-allowed',fontFamily:'inherit'}}>{saving?'Linking…':'Link Document'}</button>
        </div>
      </div>
    </div>
  );
}

/* Add a party/counterparty to a location or deal. `endpoint` is the POST target
   (…/parties); `roles` seeds the role dropdown. Persists server-side, then calls
   onAdded() so the parent re-reads and the new party appears in the list. */
function AddPartyWidget({endpoint,roles,onAdded}){
  const [open,setOpen]=useState(false);
  const [form,setForm]=useState({name:'',role:(roles&&roles[0])||'Contact'});
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const authFetch=useAuthFetch();
  const submit=async()=>{
    if(!form.name.trim()){return;}
    setSaving(true);setErr('');
    try{
      const r=await authFetch(endpoint,{method:'POST',body:{name:form.name.trim(),role:form.role}});
      if(r&&r.ok){setOpen(false);setForm({name:'',role:(roles&&roles[0])||'Contact'});onAdded&&onAdded();}
      else setErr((r&&(r.detail||r.error))||'Could not add the party.');
    }catch(e){setErr('Could not add the party — please retry.');}
    finally{setSaving(false);}
  };
  if(!open)return(
    <button onClick={()=>{setOpen(true);setErr('');}} style={{width:'100%',padding:'12px',border:'2px dashed rgba(61,31,13,.15)',borderRadius:10,background:'transparent',fontSize:12,color:'rgba(61,31,13,.4)',cursor:'pointer',fontFamily:'inherit'}} onMouseOver={e=>{e.currentTarget.style.borderColor='#E8813A';e.currentTarget.style.color='#E8813A';}} onMouseOut={e=>{e.currentTarget.style.borderColor='rgba(61,31,13,.15)';e.currentTarget.style.color='rgba(61,31,13,.4)';}}>+ Add Party</button>
  );
  const inp={boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'7px 10px',fontSize:12,color:'#3D1F0D',background:'#fff',fontFamily:'inherit',outline:'none'};
  return(
    <div style={{border:'1px solid rgba(61,31,13,.1)',borderRadius:10,padding:12,background:'rgba(245,237,224,.4)'}}>
      <div style={{display:'flex',gap:8,marginBottom:8}}>
        <input autoFocus value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} onKeyDown={e=>{if(e.key==='Enter')submit();}} placeholder="Party name (e.g. Tishman Speyer)" style={{...inp,flex:1}}/>
        <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} style={{...inp,cursor:'pointer'}}>
          {(roles||['Contact']).map(r=><option key={r}>{r}</option>)}
        </select>
      </div>
      {err&&<div style={{fontSize:10.5,color:'#dc2626',marginBottom:6}}>{err}</div>}
      <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
        <button onClick={()=>setOpen(false)} style={{padding:'6px 12px',border:'1px solid rgba(61,31,13,.15)',borderRadius:7,background:'transparent',fontSize:11,fontWeight:600,color:'rgba(61,31,13,.5)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
        <button disabled={!form.name.trim()||saving} onClick={submit} style={{padding:'6px 12px',border:'none',borderRadius:7,background:form.name.trim()&&!saving?'#E8813A':'rgba(232,129,58,.4)',color:'#fff',fontSize:11,fontWeight:700,cursor:form.name.trim()&&!saving?'pointer':'not-allowed',fontFamily:'inherit'}}>{saving?'Adding…':'Add Party'}</button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   SPINE-ARCH (Portfolio Spine PRD Addendum) — the verdict-first Spine.
   Two enemies: the assembly tax and the asymmetric miss. The default state is
   not a table; it is a verdict — the next irreversible decision leads, the
   grid recedes, and Sentry watches every date with zero PM configuration.
   ════════════════════════════════════════════════════════════════════════════ */

/* Critical-date model — D-01: rank by irreversibility-within-window, not by
   nearest date. score = (irreversibility × consequence) ÷ days-to-decision;
   ties break toward higher SF and rent. Notice windows are computed and
   surfaced as the DECISION date, not just the lease end date. */
function spineEvents(portfolio){
  const out=[];
  (portfolio||[]).forEach(l=>{
    const conseq=(l.rent||0)/1e6+(l.sf||0)/2e4;  // consequence: rent + SF weight
    if(l.optionDate){
      const d=daysUntilISO(l.optionDate);
      out.push({key:l.id+'|option',locId:l.id,locName:l.name,kind:'option',advisor:l.advisor,
        decisionDate:l.optionDate,days:d,expiry:l.leaseEnd,sf:l.sf,rent:l.rent,currency:l.currency,util:l.util,
        irreversible:true,
        label:d>0?`Option window closes ${_fmtShortDate(l.optionDate)}`:`Option window closed ${_fmtShortDate(l.optionDate)} — lapsed`,
        score:d>0?(1.0*conseq)/Math.max(d,1):0,
        tier:(d===null)?'monitored':(d<=90?'escalated':'monitored')});
    }
    if(l.leaseEnd){
      const dExp=daysUntilISO(l.leaseEnd);
      const notice=new Date(l.leaseEnd+'T00:00:00');notice.setDate(notice.getDate()-365);
      const noticeISO=notice.toISOString().slice(0,10);
      const dNotice=daysUntilISO(noticeISO);
      let label,decisionDate,days;
      if(dExp!==null&&dExp<=0){label='Lease expired '+_fmtShortDate(l.leaseEnd);decisionDate=l.leaseEnd;days=dExp;}
      else if(dNotice!==null&&dNotice<=0){label=`Inside notice period — expiry ${_fmtShortDate(l.leaseEnd)}`;decisionDate=l.leaseEnd;days=dExp;}
      else{label=`Renewal notice due ${_fmtShortDate(noticeISO)}`;decisionDate=noticeISO;days=dNotice;}
      out.push({key:l.id+'|expiry',locId:l.id,locName:l.name,kind:'expiry',advisor:l.advisor,
        decisionDate,days,expiry:l.leaseEnd,sf:l.sf,rent:l.rent,currency:l.currency,util:l.util,
        irreversible:dNotice!==null&&dNotice<=0,
        label,
        score:days>0?(0.7*conseq)/Math.max(days,1):0,
        tier:(days===null)?'monitored':(days<=90?'escalated':'monitored')});
    }
  });
  return out;
}
const spineUrgencyColor=days=>days===null?'#6B7280':days<=90?'#C84B2F':days<=180?'#B8860B':'#059669';
/* Acknowledgments & logged decisions — the asymmetric miss is designed out.
   Escalated dismissal requires a note; option decisions require typed confirm. */
const _SPINE_ACK_KEY='stage_spine_acks';
function loadSpineAcks(){try{const v=JSON.parse(localStorage.getItem(_SPINE_ACK_KEY)||'{}');return v&&typeof v==='object'?v:{};}catch(e){return{};}}
function saveSpineAcks(a){try{localStorage.setItem(_SPINE_ACK_KEY,JSON.stringify(a));}catch(e){}}
/* The verdict: exactly one item — the rest live in the Critical-Date Spine. */
function spineVerdict(events,acks){
  return events
    .filter(e=>e.days!==null&&e.days>0&&!acks[e.key])
    .sort((a,b)=>(b.score-a.score)||((b.rent+b.sf)-(a.rent+a.sf)))[0]||null;
}

/* §06-02 — inline editing with auto-save and gentle confirmation (no
   Edit/Save/Cancel form patterns). PATCHes the real record. */
function InlineEdit({value,type='text',field,slug,onSaved,placeholder}){
  const authFetch=useAuthFetch();
  const [editing,setEditing]=useState(false);
  const [val,setVal]=useState(value||'');
  const [state,setState]=useState('');   // '' | saving | saved | error
  useEffect(()=>{setVal(value||'');},[value]);
  const save=async()=>{
    setEditing(false);
    if(String(val)===String(value||''))return;
    setState('saving');
    try{
      await authFetch(`/api/portfolio/locations/${slug}`,{method:'PATCH',body:{[field]:val||null}});
      setState('saved');onSaved&&onSaved();
      setTimeout(()=>setState(''),1800);
    }catch(e){setState('error');setVal(value||'');setTimeout(()=>setState(''),2500);}
  };
  if(editing)return(
    <input autoFocus type={type} value={val} placeholder={placeholder}
      onChange={e=>setVal(e.target.value)}
      onBlur={save}
      onKeyDown={e=>{if(e.key==='Enter')save();if(e.key==='Escape'){setVal(value||'');setEditing(false);}}}
      style={{width:'100%',boxSizing:'border-box',border:'1px solid #E8813A',borderRadius:6,padding:'2px 6px',fontSize:13,fontWeight:600,fontFamily:'inherit',outline:'none',background:'#fff'}}/>
  );
  return(
    <span onClick={()=>setEditing(true)} title="Click to edit — saves automatically" style={{cursor:'text',borderBottom:'1px dashed rgba(61,31,13,.25)',display:'inline-flex',alignItems:'center',gap:6}}>
      {value||<span style={{color:'rgba(61,31,13,.35)'}}>{placeholder||'—'}</span>}
      {state==='saving'&&<span style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>saving…</span>}
      {state==='saved'&&<span className="joy-rise" style={{fontSize:9,color:'#059669',fontWeight:700}}>✓ saved</span>}
      {state==='error'&&<span style={{fontSize:9,color:'#C84B2F',fontWeight:700}}>couldn't save</span>}
    </span>
  );
}

/* Friction doctrine — logging an irreversible decision earns typed
   confirmation; acknowledging an escalated date requires a note. 'assign'
   (Journey D) routes the date to the right person via the Action Rail. */
function SpineDecisionModal({event,mode,onClose,onConfirm}){
  const [decision,setDecision]=useState(event.kind==='option'?'Exercise option':'Renew');
  const [note,setNote]=useState('');
  const [typed,setTyped]=useState('');
  const [owner,setOwner]=useState(event.advisor||'');
  const needsTyped=mode==='decide';   // irreversible — type the location name
  const ready=note.trim()&&(!needsTyped||typed.trim()===event.locName)&&(mode!=='assign'||owner.trim());
  const heading=mode==='decide'?'Log decision':mode==='assign'?'Assign this critical date':'Acknowledge critical date';
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(61,31,13,.4)',zIndex:950,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'#fff',borderRadius:14,width:460,maxWidth:'92vw',padding:'22px 24px',boxShadow:'0 20px 60px rgba(61,31,13,.25)'}}>
        <div style={{fontSize:14,fontWeight:800,color:'#3D1F0D',marginBottom:4}}>{heading}</div>
        <div style={{fontSize:12,color:'rgba(61,31,13,.55)',marginBottom:14}}>{event.locName} — {event.label}</div>
        {mode==='decide'&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Decision</div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {(event.kind==='option'?['Exercise option','Abandon option','Defer — escalate to CFO']:['Renew','Exit','Defer — escalate to CFO']).map(d=>(
                <button key={d} className={`seg-btn${decision===d?' on':''}`} onClick={()=>setDecision(d)}>{d}</button>
              ))}
            </div>
          </div>
        )}
        {mode==='assign'&&(
          <div style={{marginBottom:12}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Assign to — a P1 action lands on their Action Rail</div>
            <input value={owner} onChange={e=>setOwner(e.target.value)} placeholder="e.g. the location's advisor or your lease admin"
              style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.18)',borderRadius:8,padding:'9px 11px',fontSize:12.5,fontFamily:'inherit',outline:'none',background:'#F5EDE0'}}/>
          </div>
        )}
        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>{mode==='decide'?'Rationale — becomes the institutional record':mode==='assign'?'What they need to know':'Why this is being acknowledged'}</div>
        <textarea rows={3} value={note} onChange={e=>setNote(e.target.value)} placeholder={mode==='decide'?'e.g. Exercising — utilization recovering and the Sage brief supports holding the HQ.':mode==='assign'?'e.g. Prepare the renewal-vs-market brief before the notice date; loop in legal on the option language.':'e.g. Renewal brief in progress with the advisor; decision scheduled for the Jun 20 review.'}
          style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.18)',borderRadius:8,padding:'9px 11px',fontSize:12.5,fontFamily:'inherit',resize:'vertical',background:'#F5EDE0',outline:'none',marginBottom:12}}/>
        {needsTyped&&(
          <div style={{marginBottom:14,padding:'10px 12px',background:'rgba(200,75,47,.06)',border:'1px solid rgba(200,75,47,.25)',borderRadius:8}}>
            <div style={{fontSize:11,color:'rgba(61,31,13,.65)',lineHeight:1.5,marginBottom:8}}>This decision is <b>irreversible</b> once the window closes. Type <b>{event.locName}</b> to confirm you intend to log it.</div>
            <input value={typed} onChange={e=>setTyped(e.target.value)} placeholder={event.locName}
              style={{width:'100%',boxSizing:'border-box',border:'1px solid rgba(200,75,47,.4)',borderRadius:7,padding:'8px 11px',fontSize:12.5,fontFamily:'inherit',outline:'none'}}/>
          </div>
        )}
        <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button onClick={onClose} className="btn-ghost" style={{fontSize:12}}>Cancel</button>
          <button disabled={!ready} onClick={()=>onConfirm({decision:mode==='decide'?decision:mode==='assign'?('Assigned to '+owner.trim()):null,owner:mode==='assign'?owner.trim():'',note:note.trim()})}
            style={{padding:'9px 18px',border:'none',borderRadius:8,background:ready?(mode==='decide'?'#C84B2F':'#3D1F0D'):'rgba(61,31,13,.2)',color:'#fff',fontSize:12.5,fontWeight:700,cursor:ready?'pointer':'not-allowed',fontFamily:'inherit'}}>
            {mode==='decide'?'Log decision':mode==='assign'?'Assign':'Acknowledge'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* P1 — the 24-month Critical-Date Ribbon: every expiry and option plotted
   forward, color-coded by urgency (0–90d red · 91–180d amber · 180d+ green). */
function CriticalDateRibbon({events,selectedKey,onSelect}){
  const horizon=730;
  const future=events.filter(e=>e.days!==null&&e.days>0&&e.days<=horizon);
  const past=events.filter(e=>e.days!==null&&e.days<=0);
  return(
    <div>
      <div style={{position:'relative',height:58,margin:'6px 4px 0'}}>
        <div style={{position:'absolute',left:0,right:0,top:26,height:4,background:'rgba(61,31,13,.08)',borderRadius:2}}/>
        {/* quarter ticks */}
        {[0,90,180,365,548,730].map(d=>(
          <div key={d} style={{position:'absolute',left:`${(d/horizon)*100}%`,top:22,width:1,height:12,background:'rgba(61,31,13,.18)'}}>
            <span style={{position:'absolute',top:14,left:-12,fontSize:8.5,color:'rgba(61,31,13,.4)',fontFamily:'monospace',whiteSpace:'nowrap'}}>{d===0?'today':d<365?`${d}d`:`${Math.round(d/365*12)}mo`}</span>
          </div>
        ))}
        {future.map(e=>{
          const sel=selectedKey===e.key;
          return(
            <div key={e.key} title={`${e.locName} — ${e.label}`} onClick={()=>onSelect(sel?null:e.key)}
              style={{position:'absolute',left:`${(e.days/horizon)*100}%`,top:e.kind==='option'?8:30,transform:'translateX(-50%)',cursor:'pointer',textAlign:'center'}}>
              <div style={{width:sel?14:11,height:sel?14:11,borderRadius:e.kind==='option'?3:'50%',background:spineUrgencyColor(e.days),border:sel?'2px solid #3D1F0D':'2px solid #fff',boxShadow:'0 1px 4px rgba(61,31,13,.25)',margin:'0 auto'}}/>
            </div>
          );
        })}
      </div>
      <div style={{display:'flex',gap:14,flexWrap:'wrap',alignItems:'center',marginTop:4,fontSize:9.5,color:'rgba(61,31,13,.45)'}}>
        <span><span style={{display:'inline-block',width:8,height:8,borderRadius:2,background:'rgba(61,31,13,.3)',marginRight:4,verticalAlign:'-1px'}}/>option (square, upper)</span>
        <span><span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'rgba(61,31,13,.3)',marginRight:4,verticalAlign:'-1px'}}/>expiry / notice (round, lower)</span>
        <span style={{color:'#C84B2F',fontWeight:700}}>0–90d</span>
        <span style={{color:'#B8860B',fontWeight:700}}>91–180d</span>
        <span style={{color:'#059669',fontWeight:700}}>180d+</span>
        {past.length>0&&<span style={{marginLeft:'auto'}}>{past.length} window{past.length===1?'':'s'} already closed — listed below</span>}
      </div>
    </div>
  );
}

function PortfolioSpinePage({onSageAsk,onNav}){
  const {portfolio:_p=PORTFOLIO,refresh}=useData();
  const authFetch=useAuthFetch();
  const clerk=useClerk();
  const [search,setSearch]=useState('');
  const [mkt,setMkt]=useState('All');
  const [st,setSt]=useState('All');
  const [sel,setSel]=useState(null);
  const [detailTab,setDetailTab]=useState('overview');
  const [uploading,setUploading]=useState(false);
  const [uploadResult,setUploadResult]=useState(null);
  const fileInputRef=useRef(null);

  // ── SPINE-ARCH state — the verdict, the Critical-Date Spine, the frictions ─
  const [acks,setAcks]=useState(loadSpineAcks);
  const [spineModal,setSpineModal]=useState(null);     // {event, mode:'decide'|'ack'}
  const [ribbonSel,setRibbonSel]=useState(null);       // selected ribbon event key
  const [spineOpen,setSpineOpen]=useState(true);       // Critical-Date Spine section
  const [sageHow,setSageHow]=useState(false);          // "How did Sage reach this?"
  const [exportOpen,setExportOpen]=useState(false);    // exec export menu
  const [exportConfirm,setExportConfirm]=useState(null); // doc type awaiting typed first-send confirm
  const [exportTyped,setExportTyped]=useState('');
  const events=React.useMemo(()=>spineEvents(_p),[_p]);
  const verdict=spineVerdict(events,acks);
  const escalated=events.filter(e=>e.tier==='escalated');
  const addressed=escalated.filter(e=>acks[e.key]);
  const sage=msg=>onSageAsk&&onSageAsk(msg,'portfolio');
  const {addToast}=useToast();
  const recordAck=async(event,payload)=>{
    // Journey D 'Assign' — route the date to the right person: a real P1
    // action lands on their Action Rail before the ack is recorded.
    if(payload.owner){
      try{
        await authFetch('/api/actions',{method:'POST',body:{
          title:`Decide: ${event.locName} — ${event.label}`,
          owner:payload.owner,
          due:event.days!==null&&event.days>0?event.decisionDate:'',
          asset:event.locName,
          desc:payload.note,
          priority:'P1',
          action_type:'Decision',
        }});
        addToast(`✓ Assigned to ${payload.owner} — on their Action Rail`);
        refresh&&refresh();
      }catch(e){
        addToast('Could not create the action — recorded locally only','error');
      }
    }else if(payload.decision){
      // 'Log decision' — an irreversible decision must become the institutional
      // record, not just local triage state. Write it to the Safe layer as an
      // immutable Decision asset (append-only, audited). Falls back gracefully:
      // the local ack is still recorded so the UI reflects it even if offline.
      try{
        const r=await authFetch('/api/safe',{method:'POST',body:{
          title:`${event.locName} — ${payload.decision} (${event.label})`,
          provenance:payload.note,
          asset_class:'Decision',
          source:'Portfolio Spine — Log decision',
          asset_date:event.decisionDate||undefined,
        }});
        if(r&&r.ok){addToast('✓ Decision written to Safe — immutable record','success');refresh&&refresh();}
        else{addToast('Decision recorded locally — could not reach Safe','error');}
      }catch(e){
        addToast('Decision recorded locally — could not reach Safe','error');
      }
    }
    const next={...acks,[event.key]:{...payload,at:new Date().toISOString().slice(0,10)}};
    setAcks(next);saveSpineAcks(next);setSpineModal(null);
  };
  const briefPrompt=e=>`Prepare a decision brief for ${e.locName}: ${e.label}. Lease summary: ${e.sf.toLocaleString()} SF at ${fmtCur(e.rent,e.currency)}/yr, expiry ${e.expiry}, utilization ${e.util}%. Lay out the options, the notice math, and a recommendation.`;
  // Sage recommendation per event — plain language, grounded in the record (D-03)
  const sageRec=e=>{
    if(e.kind==='option')return e.util>=70
      ?`At ${e.util}% utilization this location is earning its keep — Sage leans toward exercising, but the brief should price the market alternative before the window closes.`
      :`At ${e.util}% utilization the option is not automatic — Sage recommends pricing a rightsizing alternative before committing. The window is the deadline, not the decision.`;
    return e.days<=0
      ?`The window here has closed — the remaining decision is how to exit or hold over on the best available terms.`
      :`Notice math: the decision date is ${_fmtShortDate(e.decisionDate)} — committing later means negotiating without leverage.`;
  };
  // §05 Executive Export — Scribe drafts; typed confirmation on first send per doc type
  const _exportDoc=(docType)=>{
    const lines=[];
    const today=new Date().toISOString().slice(0,10);
    lines.push(`# ${docType} — Acme Corp Portfolio`,`Prepared by Scribe · ${today} · Stage Status`,'');
    if(verdict)lines.push(`**The decision that can't wait:** ${verdict.locName} — ${verdict.label} (${verdict.days} days). ${sageRec(verdict)}`,'');
    {const _rc=rentByCcy(_p);lines.push(`## Portfolio`,`${_p.length} locations · ${_p.reduce((s,l)=>s+l.sf,0).toLocaleString()} SF · ${_rc.mixed?_rc.currencies.map(c=>fmtCur(_rc.byCcy[c].rent,c)).join(' · '):fmtCur(_p.reduce((s,l)=>s+l.rent,0),_rc.currencies[0]||'USD')} annual rent`,'');}
    lines.push('| Location | Market | SF | Annual rent | Expiry | Option | Status | Source |','|---|---|---|---|---|---|---|---|');
    _p.forEach(l=>lines.push(`| ${l.name} | ${l.market} | ${l.sf.toLocaleString()} | ${fmtCur(l.rent,l.currency)} | ${l.leaseEnd} | ${l.optionDate||'—'} | ${l.status==='red'?'Critical':l.status==='yellow'?'Action 90d':'Healthy'} | ${l.source} |`));
    lines.push('','## Critical dates (next 24 months)');
    events.filter(e=>e.days!==null).sort((a,b)=>a.days-b.days).forEach(e=>lines.push(`- ${e.locName}: ${e.label}${e.days>0?` — ${e.days} days`:''}${acks[e.key]?` · addressed (${acks[e.key].decision||'acknowledged'})`:''}`));
    if(docType==='Lender Summary')lines.push('','## Covenant posture','No covenants within 10% of trigger. Sentry monitors continuously; any approach is escalated with acknowledgment required.');
    lines.push('','## Assumptions appendix','- Status thresholds: Critical = decision inside 30 days or irreversible window closing; Action = inside 90 days.','- Notice period assumed 12 months before expiry where the lease is silent.','- Utilization is trailing 90-day badge data; occupancy is context, not headline.','- Sources: '+_p.filter(l=>l.source==='Scribe').length+' Scribe abstracts · '+_p.filter(l=>l.source==='Sync').length+' system sync · '+_p.filter(l=>l.source==='Manual').length+' manual entries.');
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([lines.join('\n')],{type:'text/markdown'}));
    a.download=`${docType.toLowerCase().replace(/\s+/g,'-')}-${today}.md`;
    a.click();
  };
  const runExport=(docType)=>{
    let confirmed={};
    try{confirmed=JSON.parse(localStorage.getItem('stage_export_confirmed')||'{}');}catch(e){}
    if(!confirmed[docType]){setExportConfirm(docType);setExportTyped('');return;}
    _exportDoc(docType);setExportOpen(false);
  };
  const confirmExport=()=>{
    if(exportTyped.trim().toUpperCase()!=='SEND')return;
    let confirmed={};
    try{confirmed=JSON.parse(localStorage.getItem('stage_export_confirmed')||'{}');}catch(e){}
    confirmed[exportConfirm]=true;
    try{localStorage.setItem('stage_export_confirmed',JSON.stringify(confirmed));}catch(e){}
    _exportDoc(exportConfirm);setExportConfirm(null);setExportOpen(false);
  };

  // New Location modal
  const [showLocModal,setShowLocModal]=useState(false);
  const [locForm,setLocForm]=useState({name:'',market:'',sf:'',rent:'',lease_end:'',option_date:'',advisor:'',bu:'Corporate',headcount:''});
  const [locSaving,setLocSaving]=useState(false);
  const [locError,setLocError]=useState('');
  const [locWarn,setLocWarn]=useState(''); // sanity-gate warning awaiting human verification
  const setLF=k=>e=>{ setLocWarn(''); setLocForm(f=>({...f,[k]:e.target.value})); };

  const handleCreateLocation=async(confirmed=false)=>{
    if(!locForm.name||!locForm.market||!locForm.sf||!locForm.rent||!locForm.lease_end){
      setLocError('Please fill in all required fields.');return;
    }
    const sfN=parseInt(locForm.sf,10), rentN=parseFloat(locForm.rent);
    // Sanity gate — pause for human verification on out-of-range figures before writing.
    if(!confirmed){
      const perSf=sfN>0?rentN/sfN:0;
      let warn='';
      if(rentN>1e9) warn=`Annual rent of ${fmt(rentN)} is unusually large for one location. Please double-check it isn't a typo before saving.`;
      else if(perSf>500) warn=`This works out to $${Math.round(perSf).toLocaleString()}/SF per year — well above the typical $10–$150 range. Please verify the Square Footage and Annual Rent.`;
      if(warn){ setLocWarn(warn); setLocError(''); return; }
    }
    setLocSaving(true);setLocError('');
    try{
      const r=await authFetch('/api/portfolio/locations',{
        method:'POST',
        body:{
          name:locForm.name.trim(),
          market:locForm.market.trim(),
          sf:sfN,
          rent:rentN,
          lease_end:locForm.lease_end,
          option_date:locForm.option_date||null,
          advisor:locForm.advisor.trim(),
          bu:locForm.bu.trim()||'Corporate',
          ...(locForm.headcount!==''?{headcount:parseInt(locForm.headcount,10)}:{}),
          ...(confirmed?{confirm_override:true}:{}),
        },
      });
      if(r?.ok){
        setShowLocModal(false);
        setLocWarn('');
        setLocForm({name:'',market:'',sf:'',rent:'',lease_end:'',option_date:'',advisor:'',bu:'Corporate',headcount:''});
        refresh(); // reload portfolio data into DataCtx
        setSel(r.location.id); // jump to the new location
        // Background insight refresh — fire and forget (auth-aware)
        authFetch('/api/insights/generate',{method:'POST'}).then(()=>refresh()).catch(()=>{});
      } else {
        setLocError(r?.detail||'Failed to create location.');
      }
    }catch(e){setLocError('Network error — please try again.');}
    finally{setLocSaving(false);}
  };
  const markets=['All',...new Set(_p.map(l=>l.market))];
  // SPINE-ARCH — default sort is status-first (Critical → Action 90d → Healthy),
  // then by days-to-decision within each tier. Never alphabetical or arbitrary.
  const _stRank={red:0,yellow:1,green:2};
  const _locDays=l=>{
    const ds=[l.optionDate,l.leaseEnd].map(d=>daysUntilISO(d)).filter(d=>d!==null&&d>0);
    return ds.length?Math.min(...ds):9999;
  };
  const filtered=_p
    .filter(l=>mkt==='All'||l.market===mkt)
    .filter(l=>st==='All'||l.status===st)
    .filter(l=>l.name.toLowerCase().includes(search.toLowerCase()))
    .slice().sort((a,b)=>(_stRank[a.status]-_stRank[b.status])||(_locDays(a)-_locDays(b)));
  const selected=sel?_p.find(l=>l.id===sel):null;
  const counts={red:_p.filter(l=>l.status==='red').length,yellow:_p.filter(l=>l.status==='yellow').length,green:_p.filter(l=>l.status==='green').length};
  const SOURCE_BADGE={Scribe:{color:'#D97706',title:'Parsed by Scribe from the executed lease — abstract on file'},Sync:{color:'#2563EB',title:'Synced from an external system of record'},Manual:{color:'#6B7280',title:'Manually entered — verify before relying on it'}};

  const handleUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file||!selected)return;
    setUploading(true);
    setUploadResult(null);
    try{
      const fd=new FormData();
      fd.append('file',file);
      fd.append('location_slug',selected.id);
      const token=await getToken(clerk);
      const headers={};
      if(token)headers['Authorization']=`Bearer ${token}`;
      const r=await fetch(API_BASE+'/api/scribe/upload',{method:'POST',headers,body:fd});
      const data=await r.json();
      setUploadResult(data);
    }catch(err){setUploadResult({ok:false,status:'Upload failed: '+err.message});}
    finally{setUploading(false);if(fileInputRef.current)fileInputRef.current.value='';}
  };

  const handleExport=()=>{
    const rows=[['Name','Market','Status','SF','Annual Rent','Utilization %','Headcount','Lease End','Option Date','Advisor','Business Unit']];
    _p.forEach(l=>rows.push([l.name,l.market,l.status,l.sf,l.rent,l.util,l.headcount??'',l.leaseEnd,l.optionDate||'',l.advisor,l.bu]));
    const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download=`portfolio-spine-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return(
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:14}}>
        <div>
          <div className="page-header-label">Portfolio Spine</div><div className="page-title">Master Location Record</div>
          {/* Baseline milestone — what was parsed, what is now monitored.
              Only meaningful once at least one location exists; an empty record
              shows the grounded empty state below instead of a "0 locations" claim. */}
          {_p.length>0&&(
          <div style={{fontSize:11.5,color:'rgba(61,31,13,.5)',marginTop:4}}>
            <span style={{color:'#059669',fontWeight:700}}>✓ Baseline live</span> — Sentry is monitoring {events.length} critical dates across {_p.length} locations · zero configuration
          </div>
          )}
          {/* D-03 — data gaps surface as actionable prompts, never errors.
              Weighted by status (D-02): a gap on a Critical location leads. */}
          {(()=>{
            const stRank={red:0,yellow:1,green:2};
            const gaps=_p.filter(l=>!l.optionDate&&daysUntilISO(l.leaseEnd)>0)
              .sort((a,b)=>stRank[a.status]-stRank[b.status]).slice(0,2);
            if(!gaps.length)return null;
            return(
              <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:7}}>
                {gaps.map(l=>(
                  <span key={l.id} onClick={()=>{setSel(l.id);setDetailTab('overview');}}
                    style={{fontSize:11,fontWeight:600,color:'#B8860B',background:'rgba(184,134,11,.08)',border:'1px dashed rgba(184,134,11,.4)',padding:'3px 11px',borderRadius:999,cursor:'pointer'}}
                    title="Opens the location — the Option Date field edits inline">
                    Add the option date for {l.name} to sharpen Sentry's watch →
                  </span>
                ))}
              </div>
            );
          })()}
        </div>
        <div style={{display:'flex',gap:8,position:'relative'}}>
          <button className="btn-ghost" onClick={()=>setExportOpen(o=>!o)}>↓ Export ▾</button>
          {exportOpen&&(
            <div style={{position:'absolute',top:40,right:120,background:'#fff',border:'1px solid rgba(61,31,13,.12)',borderRadius:10,boxShadow:'0 10px 32px rgba(61,31,13,.18)',zIndex:80,minWidth:250,padding:6}}>
              {[['Portfolio Baseline','The defensible picture — locations, dates, sources'],['Board Memo','Verdict-led narrative for leadership'],['Lender Summary','Covenant posture + critical dates'],['Raw CSV','The grid as data']].map(([t,d])=>(
                <div key={t} onClick={()=>t==='Raw CSV'?(handleExport(),setExportOpen(false)):runExport(t)} style={{padding:'9px 11px',borderRadius:7,cursor:'pointer'}}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(232,129,58,.07)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <div style={{fontSize:12.5,fontWeight:700,color:'#3D1F0D'}}>{t}</div>
                  <div style={{fontSize:10.5,color:'rgba(61,31,13,.45)'}}>{d}{t!=='Raw CSV'?' · Scribe drafts, you edit the voice':''}</div>
                </div>
              ))}
              {exportConfirm&&(
                <div style={{margin:6,padding:'9px 11px',background:'rgba(184,134,11,.07)',border:'1px solid rgba(184,134,11,.3)',borderRadius:8}}>
                  <div style={{fontSize:10.5,color:'rgba(61,31,13,.65)',lineHeight:1.5,marginBottom:6}}>First <b>{exportConfirm}</b> for this recipient group — type <b>SEND</b> to confirm. One tap thereafter.</div>
                  <div style={{display:'flex',gap:6}}>
                    <input value={exportTyped} onChange={e=>setExportTyped(e.target.value)} placeholder="SEND" style={{flex:1,minWidth:0,border:'1px solid rgba(184,134,11,.4)',borderRadius:6,padding:'5px 9px',fontSize:12,fontFamily:'inherit',outline:'none'}}/>
                    <button onClick={confirmExport} disabled={exportTyped.trim().toUpperCase()!=='SEND'} style={{padding:'5px 12px',border:'none',borderRadius:6,background:exportTyped.trim().toUpperCase()==='SEND'?'#B8860B':'rgba(61,31,13,.2)',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Confirm</button>
                  </div>
                </div>
              )}
            </div>
          )}
          <button className="btn-primary" onClick={()=>{setShowLocModal(true);setLocError('');}}>+ New Location</button>
        </div>
      </div>

      {/* ════ THE VERDICT — the next irreversible decision, before anything else ════ */}
      {verdict&&(
        <div style={{background:'linear-gradient(135deg,rgba(200,75,47,.07) 0%,rgba(200,75,47,.02) 100%)',border:'1px solid rgba(200,75,47,.28)',borderLeft:'4px solid #C84B2F',borderRadius:12,padding:'15px 19px',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7,flexWrap:'wrap'}}>
            <span style={{fontSize:10,fontWeight:800,color:'#C84B2F',textTransform:'uppercase',letterSpacing:'.08em'}}>⛨ Next irreversible decision</span>
            <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}} title="Sentry score = (irreversibility × consequence) ÷ days-to-decision; ties break toward higher SF and rent">Sentry · ranked by irreversibility within window ⓘ</span>
            <span style={{marginLeft:'auto',fontSize:11,fontFamily:'monospace',fontWeight:700,color:'#C84B2F'}}>{verdict.days} days</span>
          </div>
          <div style={{fontFamily:'Georgia,serif',fontSize:17,color:'#3D1F0D',lineHeight:1.4,marginBottom:4}}>
            {verdict.locName} — {verdict.label}.
          </div>
          <div style={{fontSize:12.5,color:'rgba(61,31,13,.65)',marginBottom:12}}>
            {verdict.sf.toLocaleString()} SF · {fmtCur(verdict.rent,verdict.currency)}/yr · utilization {verdict.util}% · {sageRec(verdict)}
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <button onClick={()=>sage(briefPrompt(verdict))} style={{padding:'8px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:7,fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Prepare decision brief</button>
            <button onClick={()=>setSpineModal({event:verdict,mode:'decide'})} className="btn-ghost" style={{fontSize:12}}>Log decision</button>
            <button onClick={()=>{setSel(verdict.locId);setDetailTab('overview');setTimeout(()=>{const el=document.getElementById('loc-detail-panel');el&&el.scrollIntoView({behavior:'smooth',block:'start'});},80);}} className="btn-ghost" style={{fontSize:12}}>View location ↓</button>
          </div>
        </div>
      )}
      {/* Grounded empty state — a real org that hasn't added a location yet.
          Without this the page would claim "0 critical dates" / "you're ahead",
          which is misleading (there's simply nothing here yet), and the verdict +
          synthesis views have nothing to stand on. Mirrors the "needs inputs"
          affordance the other surfaces show. */}
      {_p.length===0&&(
        <div className="card" style={{padding:'22px 24px',marginBottom:12,borderLeft:'4px solid #8b0020',textAlign:'center'}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D',marginBottom:6}}>Your Portfolio Spine is empty</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.6)',lineHeight:1.6,maxWidth:560,margin:'0 auto 16px'}}>
            Add your first location — or upload a lease and let Scribe abstract it — and this surface fills in: the next irreversible decision, the critical-date watch, and Sage's portfolio synthesis, each grounded in your own record.
          </div>
          <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
            <button className="btn-primary" onClick={()=>{setShowLocModal(true);setLocError('');}}>+ Add your first location</button>
            <a href="/start" className="btn-ghost" style={{textDecoration:'none',display:'inline-flex',alignItems:'center'}}>Continue guided setup →</a>
          </div>
        </div>
      )}
      {_p.length>0&&!verdict&&escalated.length===0&&(
        <div className="card joy-glow" style={{padding:'13px 18px',marginBottom:12,borderLeft:'4px solid #059669',fontSize:13,color:'#3D1F0D'}}>
          <b style={{color:'#059669'}}>✓ You're ahead of every critical date this quarter.</b> Sentry keeps watching — nothing requires a decision right now.
        </div>
      )}

      {/* ════ Sage on the Spine — proactive, cited synthesis (one per session) ════ */}
      {(()=>{
        // No portfolio yet → no synthesis (and nothing to dereference). Guards the
        // empty-org case: lowUtil below is undefined on an empty record.
        if(!_p.length)return null;
        const nyJun=_p.filter(l=>(l.optionDate==='2026-06-30'||l.leaseEnd==='2026-06-30'));
        const rentShare=Math.round(nyJun.reduce((s,l)=>s+l.rent,0)/_p.reduce((s,l)=>s+l.rent,0)*100);
        const srcCounts={Scribe:_p.filter(l=>l.source==='Scribe').length,Sync:_p.filter(l=>l.source==='Sync').length,Manual:_p.filter(l=>l.source==='Manual').length};
        const lowUtil=_p.slice().sort((a,b)=>a.util-b.util)[0];
        return(
          <div className="card card-flat" style={{padding:'13px 17px',marginBottom:12,borderLeft:'4px solid #8b0020'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,flexWrap:'wrap'}}>
              <span style={{fontSize:10,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em'}}>Sage · portfolio synthesis</span>
              <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>unprompted · this session</span>
              <span style={{marginLeft:'auto',fontSize:10,fontFamily:'monospace',color:'rgba(61,31,13,.5)',background:'rgba(61,31,13,.05)',padding:'2px 9px',borderRadius:999}}>confidence 88% · {_p.length} of {_p.length} locations</span>
            </div>
            <div style={{fontSize:13,color:'#3D1F0D',lineHeight:1.6,marginBottom:8}}>
              {nyJun.length>=2
                ?<>Two New York leases reach their decision point on the same day — Jun 30. Together they carry <b>{rentShare}% of portfolio rent</b>. One is a closing option window; the other's option has already lapsed, leaving expiry terms as the only lever. Treat them as one negotiation calendar, not two.</>
                :<>The portfolio's largest near-term exposure is {verdict?verdict.locName:'—'}; {lowUtil.name} is the weakest utilization signal at {lowUtil.util}%.</>}
            </div>
            <div style={{fontSize:10.5,color:'rgba(61,31,13,.5)',marginBottom:8}}>
              Based on {srcCounts.Scribe} Scribe lease abstracts · {srcCounts.Sync} system sync · {srcCounts.Manual} manual entry · your <span title="Trailing 12-month operating statement" style={{borderBottom:'1px dotted rgba(61,31,13,.4)',cursor:'help'}}>T-12</span>
              <span onClick={()=>setSageHow(h=>!h)} style={{marginLeft:10,color:'#8b0020',fontWeight:700,cursor:'pointer',textDecoration:'underline',textUnderlineOffset:2}}>{sageHow?'Hide':'How did Sage reach this?'}</span>
            </div>
            {sageHow&&(
              <div className="focus-fade" style={{padding:'9px 12px',background:'rgba(61,31,13,.04)',borderRadius:8,fontSize:11,color:'rgba(61,31,13,.65)',lineHeight:1.6,marginBottom:8}}>
                1 Penn Plaza option date and 200 Park Avenue lease end both read 2026-06-30 from their Scribe abstracts. Annual rents ({fmt(2700000)} + {fmt(1800000)}) over portfolio total {fmt(_p.reduce((s,l)=>s+l.rent,0))} = {rentShare}%. The 200 Park option window (2026-03-31) is in the past — that lever is gone. Confidence is capped below High because one location is Manual-sourced (D-03: no High confidence on partial data).
              </div>
            )}
            <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              {[`Why is ${lowUtil.name} underutilized at ${lowUtil.util}%?`,'Model consolidating the two Chicago sites','What does the Jun 30 double-decision mean for negotiating leverage?'].map(q=>(
                <button key={q} className="seg-btn" style={{fontSize:10}} onClick={()=>sage(q)}>✦ {q}</button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ════ Critical-Date Spine — the asymmetric-risk surface ════ */}
      <div className="card" style={{marginBottom:14,overflow:'hidden'}}>
        <div onClick={()=>setSpineOpen(o=>!o)} style={{padding:'12px 18px',display:'flex',alignItems:'center',gap:10,cursor:'pointer',userSelect:'none',borderBottom:spineOpen?'1px solid rgba(61,31,13,.07)':'none'}}>
          <span style={{fontSize:11,fontWeight:800,color:'#3D1F0D'}}>Critical-Date Spine</span>
          <span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>every expiry, option, and notice window — 24 months forward</span>
          <span style={{marginLeft:'auto',fontSize:10,fontWeight:700,color:addressed.length===escalated.length?'#059669':'#B8860B',background:addressed.length===escalated.length?'rgba(5,150,105,.08)':'rgba(184,134,11,.08)',padding:'2px 10px',borderRadius:999}}>
            Critical dates this week: {addressed.length} of {escalated.length} addressed
          </span>
          <span style={{fontSize:10,color:'rgba(61,31,13,.35)',transform:spineOpen?'rotate(90deg)':'none',transition:'transform .15s'}}>›</span>
        </div>
        {spineOpen&&(
          <div style={{padding:'10px 18px 16px'}}>
            <CriticalDateRibbon events={events} selectedKey={ribbonSel} onSelect={setRibbonSel}/>
            {/* tapped event — lease summary, Sage recommendation, decision brief */}
            {ribbonSel&&(()=>{
              const e=events.find(x=>x.key===ribbonSel);if(!e)return null;
              return(
                <div className="focus-fade" style={{marginTop:10,padding:'11px 14px',background:'rgba(245,237,224,.55)',borderRadius:10,border:`1px solid ${spineUrgencyColor(e.days)}44`,borderLeft:`4px solid ${spineUrgencyColor(e.days)}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                    <span style={{fontSize:12.5,fontWeight:800,color:'#3D1F0D'}}>{e.locName}</span>
                    <span style={{fontSize:11,color:spineUrgencyColor(e.days),fontWeight:700}}>{e.label}{e.days>0?` · ${e.days} days`:''}</span>
                    <span style={{marginLeft:'auto',fontSize:10.5,color:'rgba(61,31,13,.5)'}}>{e.sf.toLocaleString()} SF · {fmtCur(e.rent,e.currency)}/yr · expiry {e.expiry}</span>
                  </div>
                  <div style={{fontSize:11.5,color:'rgba(61,31,13,.65)',lineHeight:1.55,marginBottom:9}}><b style={{color:'#8b0020'}}>Sage · </b>{sageRec(e)}</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <button onClick={()=>sage(briefPrompt(e))} style={{padding:'6px 13px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Prepare decision brief</button>
                    {!acks[e.key]&&<button onClick={()=>setSpineModal({event:e,mode:'decide'})} className="seg-btn" style={{fontSize:10.5}}>Log decision</button>}
                    {!acks[e.key]&&<button onClick={()=>setSpineModal({event:e,mode:'assign'})} className="seg-btn" style={{fontSize:10.5}}>Assign</button>}
                    {!acks[e.key]&&e.tier==='escalated'&&<button onClick={()=>setSpineModal({event:e,mode:'ack'})} className="seg-btn" style={{fontSize:10.5}}>Acknowledge</button>}
                    {acks[e.key]&&<span style={{fontSize:10.5,color:'#059669',fontWeight:700,alignSelf:'center'}}>✓ {acks[e.key].decision||'Acknowledged'} · {acks[e.key].at} — "{acks[e.key].note}"</span>}
                  </div>
                </div>
              );
            })()}
            {/* Escalated tier — requires acknowledgment; Monitored never forces action */}
            <div style={{display:'flex',flexDirection:'column',gap:6,marginTop:12}}>
              {escalated.sort((a,b)=>(a.days??-999)-(b.days??-999)).map(e=>(
                <div key={e.key} style={{display:'flex',alignItems:'center',gap:9,padding:'8px 12px',borderRadius:9,background:acks[e.key]?'rgba(5,150,105,.04)':'rgba(200,75,47,.04)',border:`1px solid ${acks[e.key]?'rgba(5,150,105,.2)':'rgba(200,75,47,.2)'}`}}>
                  <span style={{width:8,height:8,borderRadius:e.kind==='option'?2:'50%',background:acks[e.key]?'#059669':spineUrgencyColor(e.days),flexShrink:0}}/>
                  <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D',flexShrink:0}}>{e.locName}</span>
                  <span style={{fontSize:11.5,color:'rgba(61,31,13,.6)',flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.label}{e.days>0?` — ${e.days} days`:''}</span>
                  {acks[e.key]
                    ?<span style={{fontSize:10,fontWeight:700,color:'#059669',flexShrink:0}} title={acks[e.key].note}>✓ {acks[e.key].decision||'Acknowledged'}</span>
                    :<>
                      <button onClick={()=>sage(briefPrompt(e))} className="seg-btn" style={{fontSize:10,flexShrink:0}}>Brief</button>
                      <button onClick={()=>setSpineModal({event:e,mode:'decide'})} className="seg-btn" style={{fontSize:10,flexShrink:0}}>Decide</button>
                      <button onClick={()=>setSpineModal({event:e,mode:'assign'})} className="seg-btn" style={{fontSize:10,flexShrink:0}}>Assign</button>
                      <button onClick={()=>setSpineModal({event:e,mode:'ack'})} className="seg-btn" style={{fontSize:10,flexShrink:0}}>Acknowledge</button>
                    </>}
                </div>
              ))}
              {escalated.length===0&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.45)',padding:'6px 2px'}}>Nothing escalated — every date is outside its notice window. Sentry keeps watching.</div>}
            </div>
          </div>
        )}
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        {[['red','Critical','#ef4444'],['yellow','Action 90d','#f59e0b'],['green','Healthy','#22c55e']].map(([s,label,c])=>(
          <button key={s} onClick={()=>setSt(st===s?'All':s)} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:10,border:`1px solid ${st===s?c:c+'33'}`,background:st===s?c+'15':'#fff',color:st===s?c:'rgba(61,31,13,.55)',fontWeight:600,fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>
            <span style={{width:8,height:8,borderRadius:'50%',background:c,display:'inline-block'}}/>
            {label} · {counts[s]}
          </button>
        ))}
      </div>
      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search locations…" style={{flex:1,minWidth:180,padding:'8px 14px',border:'1px solid rgba(61,31,13,.12)',borderRadius:10,fontSize:13,outline:'none',fontFamily:'inherit'}}/>
        <select value={mkt} onChange={e=>setMkt(e.target.value)} style={{padding:'8px 12px',border:'1px solid rgba(61,31,13,.12)',borderRadius:10,fontSize:13,outline:'none',fontFamily:'inherit',background:'#fff',cursor:'pointer'}}>
          {markets.map(m=><option key={m}>{m}</option>)}
        </select>
      </div>
      <div className="card" style={{overflow:'hidden',marginBottom:16}}>
        {/* TEAM-1 §5.3 — grid protections: frozen header + locked calculated column */}
        <div style={{display:'flex',alignItems:'center',gap:14,padding:'6px 14px',fontSize:10,color:'rgba(61,31,13,.45)'}}>
          <span>🔒 $/Guest is calculated (annual rent ÷ guest traffic) — read-only</span>
          <span style={{color:'rgba(61,31,13,.35)'}}>Header frozen on scroll</span>
        </div>
        <div style={{overflowX:'auto',maxHeight:440,overflowY:'auto'}}>
          <table>
            <thead>
              <tr style={{borderBottom:'1px solid rgba(61,31,13,.08)',background:'rgba(245,237,224,.6)'}}>
                {['','Location','Market','SF','Annual Rent','$/Guest','Expiry','Option','Util','Source'].map(h=>{
                  const locked=h==='$/Guest';
                  return(
                  <th key={h} title={locked?'Calculated field (annual rent ÷ guest traffic) — read-only':undefined} style={{position:'sticky',top:0,zIndex:1,fontSize:10,fontWeight:700,color:locked?'#8b0020':'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap',background:'#F5EDE0',boxShadow:'0 1px 0 rgba(61,31,13,.1)'}}>{locked?`🔒 ${h}`:h}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map(l=>(
                <tr key={l.id} onClick={()=>{const next=sel===l.id?null:l.id;setSel(next);if(next)setDetailTab('overview');}} style={{borderBottom:'1px solid rgba(61,31,13,.05)',cursor:'pointer',background:sel===l.id?'#F5EDE0':'transparent',transition:'background .1s'}} onMouseOver={e=>e.currentTarget.style.background='rgba(245,237,224,.6)'} onMouseOut={e=>e.currentTarget.style.background=sel===l.id?'#F5EDE0':'transparent'}>
                  <td><Ring status={l.status}/></td>
                  <td><div style={{fontWeight:600,fontSize:13,color:'#3D1F0D'}}>{l.name}</div><div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{l.bu}</div></td>
                  <td style={{fontSize:13,color:'rgba(61,31,13,.65)'}}>{l.market}</td>
                  <td style={{fontFamily:'monospace',fontSize:13}}>{l.sf.toLocaleString()}</td>
                  <td style={{fontFamily:'monospace',fontSize:13}}>{fmtCur(l.rent,l.currency)}</td>
                  <td title="Calculated field (annual rent ÷ guest traffic) — read-only" style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:l.costPerExp>1500?'#dc2626':l.costPerExp>800?'#d97706':'#16a34a',background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>${l.costPerExp.toLocaleString()}</td>
                  <td style={{fontSize:12,color:l.status!=='green'?priorityColors[l.status==='red'?'P1':'P2'].text:'rgba(61,31,13,.6)',fontWeight:l.status!=='green'?600:400}}>{l.leaseEnd}</td>
                  {/* Option windows inside the notice period carry accent — never a quiet date */}
                  <td>{(()=>{
                    if(!l.optionDate)return <span style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>—</span>;
                    const d=daysUntilISO(l.optionDate);
                    if(d!==null&&d<=0)return <span style={{fontSize:11,fontWeight:800,color:'#C84B2F'}} title="Option window has closed — the option lapsed">{l.optionDate} · closed</span>;
                    if(d!==null&&d<=90)return <span style={{fontSize:11,fontWeight:800,color:'#C84B2F',background:'rgba(200,75,47,.08)',padding:'2px 7px',borderRadius:5}} title={`Inside the notice window — ${d} days to decide`}>{l.optionDate} · {d}d</span>;
                    if(d!==null&&d<=180)return <span style={{fontSize:12,fontWeight:700,color:'#B8860B'}}>{l.optionDate}</span>;
                    return <span style={{fontSize:12,color:'rgba(61,31,13,.5)'}}>{l.optionDate}</span>;
                  })()}</td>
                  <td>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <div style={{width:52,height:5,background:'rgba(61,31,13,.1)',borderRadius:3}}>
                        <div style={{width:`${l.util}%`,height:'100%',borderRadius:3,background:l.util>=70?'#22c55e':l.util>=50?'#f59e0b':'#ef4444'}}/>
                      </div>
                      <span style={{fontSize:11,fontFamily:'monospace',color:'rgba(61,31,13,.55)'}}>{l.util}%</span>
                    </div>
                  </td>
                  {/* Data provenance — trust is legible per row */}
                  <td>{(()=>{const b=SOURCE_BADGE[l.source]||SOURCE_BADGE.Manual;return(
                    <span title={b.title} style={{fontSize:9.5,fontWeight:700,color:b.color,background:b.color+'14',border:`1px solid ${b.color}33`,padding:'2px 8px',borderRadius:999,textTransform:'uppercase',letterSpacing:'.04em'}}>{l.source}</span>
                  );})()}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{borderTop:'1px solid rgba(61,31,13,.1)',background:'rgba(61,31,13,.03)'}}>
                <td colSpan={3} style={{fontSize:11,fontWeight:600,color:'rgba(61,31,13,.5)'}}>{filtered.length} locations</td>
                <td style={{fontFamily:'monospace',fontWeight:700,fontSize:13}}>{filtered.reduce((s,l)=>s+l.sf,0).toLocaleString()}</td>
                <td style={{fontFamily:'monospace',fontWeight:700,fontSize:13}}>{(()=>{const rc=rentByCcy(filtered);return rc.mixed?`${rc.currencies.length} ccy`:fmtCur(filtered.reduce((s,l)=>s+l.rent,0),rc.currencies[0]||'USD');})()}</td>
                <td colSpan={5}/>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      {selected&&(
        <div id="loc-detail-panel" className="card" style={{padding:20,scrollMarginTop:16}}>
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:6}}>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                <span style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:999,background:selected.status==='red'?'#fef2f2':selected.status==='yellow'?'#fffbeb':'#f0fdf4',color:selected.status==='red'?'#dc2626':selected.status==='yellow'?'#d97706':'#16a34a'}}>
                  <span style={{width:6,height:6,borderRadius:'50%',background:'currentColor',display:'inline-block'}}/>
                  {selected.status==='red'?'Critical':selected.status==='yellow'?'Action Required':'Healthy'}
                </span>
                {/* Sentry badge — subtle, persistent, never a modal interrupt */}
                {(()=>{
                  const locEsc=events.filter(e=>e.locId===selected.id&&e.tier==='escalated'&&!acks[e.key]);
                  if(!locEsc.length)return null;
                  return <span title={locEsc.map(e=>e.label).join(' · ')} style={{fontSize:10,fontWeight:700,color:'#C84B2F',background:'rgba(200,75,47,.08)',border:'1px solid rgba(200,75,47,.25)',padding:'3px 10px',borderRadius:999,cursor:'default'}}>⛨ Sentry · {locEsc[0].label}</span>;
                })()}
              </div>
              <div style={{fontSize:20,fontWeight:800,color:'#3D1F0D'}}>{selected.name}</div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
              {/* Bridge to the TM pre-engagement Deal Brief for this location. */}
              <button onClick={()=>onNav&&onNav('dealbrief')} className="btn-ghost" style={{fontSize:12,whiteSpace:'nowrap'}} title="Open the pre-engagement Deal Brief">▤ Deal Brief →</button>
              <button onClick={()=>setSel(null)} style={{background:'none',border:'none',fontSize:20,color:'rgba(61,31,13,.3)',cursor:'pointer'}}>×</button>
            </div>
          </div>
          {/* Narrative summary bar — plain language, no field dump (§04-02) */}
          <div style={{fontSize:13,color:'rgba(61,31,13,.7)',lineHeight:1.55,marginBottom:14,padding:'9px 13px',background:'rgba(245,237,224,.55)',borderRadius:9}}>
            {(()=>{
              const word=selected.status==='red'?'Critical':selected.status==='yellow'?'Needs attention':'Healthy';
              const dOpt=selected.optionDate?daysUntilISO(selected.optionDate):null;
              const dExp=daysUntilISO(selected.leaseEnd);
              let leaseLine;
              if(dOpt!==null&&dOpt>0)leaseLine=`option window closes ${_fmtShortDate(selected.optionDate)} — ${dOpt} days`;
              else if(dOpt!==null&&dOpt<=0&&dExp!==null&&dExp<=365)leaseLine=`option window closed ${_fmtShortDate(selected.optionDate)}; lease expires ${_fmtShortDate(selected.leaseEnd)}`;
              else if(dExp!==null&&dExp<=365)leaseLine=`lease expires ${_fmtShortDate(selected.leaseEnd)}${dExp>0?` — ${dExp} days`:''}`;
              else leaseLine=`lease secure to ${String(selected.leaseEnd).slice(0,4)}`;
              return <><b style={{color:selected.status==='red'?'#C84B2F':selected.status==='yellow'?'#B8860B':'#059669'}}>{word}.</b> {selected.util}% utilized, ${selected.costPerExp.toLocaleString()} per qualifying visit, {leaseLine}.</>;
            })()}
          </div>
          {/* Tab bar */}
          <div style={{display:'flex',gap:4,background:'#F5EDE0',borderRadius:10,padding:4,marginBottom:16}}>
            {['overview','documents','parties'].map(t=>(
              <button key={t} onClick={()=>setDetailTab(t)} style={{flex:1,padding:'8px',borderRadius:8,border:'none',background:detailTab===t?'#3D1F0D':'transparent',color:detailTab===t?'#fff':'rgba(61,31,13,.5)',fontWeight:600,fontSize:12,cursor:'pointer',fontFamily:'inherit',textTransform:'capitalize',transition:'all .15s'}}>
                {t==='documents'?`Documents (${selected.documents?.length||0})`:t==='parties'?`Parties (${selected.parties?.length||0})`:t}
              </button>
            ))}
          </div>
          {detailTab==='overview'&&(()=>{
            const tws=TWS_ASSETS.find(a=>a.id===selected.twsId);
            const cpeColor=selected.costPerExp>1500?'#dc2626':selected.costPerExp>800?'#d97706':'#16a34a';
            const twsColor=tws?(tws.twsScore>=70?'#16a34a':tws.twsScore>=55?'#d97706':'#dc2626'):'rgba(61,31,13,.4)';
            // Cross-reference: pending items for this location
            const locKey=selected.name.toLowerCase().split(' ').slice(0,2).join(' ');
            const locActions=ACTIONS.filter(a=>a.status!=='Done'&&a.asset&&a.asset.toLowerCase().split(' ').slice(0,2).join(' ')===locKey);
            const locProjects=CAPITAL_PROJECTS_V2.filter(p=>p.location.toLowerCase().split(' ').slice(0,2).join(' ')===locKey);
            const rbwLoc=RBW_LOCATIONS.find(r=>r.id===selected.twsId);
            const pColors={'P1':'#dc2626','P2':'#d97706','P3':'#2563EB'};
            const pBg={'P1':'#fef2f2','P2':'#fffbeb','P3':'#eff6ff'};
            return(
              <>
                {/* Experience metrics highlight */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:14,padding:'14px 16px',background:'rgba(139,0,33,.04)',borderRadius:10,border:'1px solid rgba(139,0,33,.1)'}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>Cost / Experience</div>
                    <div style={{fontSize:22,fontWeight:900,color:cpeColor}}>${selected.costPerExp.toLocaleString()}</div>
                    <div style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>per qualifying visit</div>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>TWS Score</div>
                    {tws?(
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <ScoreRing score={tws.twsScore} size={52} color={twsColor} strokeWidth={4} delay={80}/>
                        <div>
                          <div style={{display:'flex',alignItems:'baseline',gap:4}}>
                            <span style={{fontSize:18,fontWeight:900,color:twsColor,lineHeight:1}}>{tws.twsScore}</span>
                            <span style={{fontSize:10,fontWeight:600,color:tws.trend<0?'#dc2626':'#16a34a'}}>{tws.trend>0?'+':''}{tws.trend}</span>
                          </div>
                          <div style={{display:'flex',gap:4,marginTop:4}}>{[['F',tws.flow],['C',tws.culture],['P',tws.perf]].map(([k,v])=><span key={k} style={{fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:4,background:(v>=70?'#f0fdf4':v>=55?'#fffbeb':'#fef2f2'),color:(v>=70?'#16a34a':v>=55?'#d97706':'#dc2626')}}>{k} {v}</span>)}</div>
                        </div>
                      </div>
                    ):(
                      <div style={{fontSize:22,fontWeight:900,color:'rgba(61,31,13,.4)'}}>—</div>
                    )}
                    {/* Score transparency — the "why" in plain language, evidence one tap away */}
                    {tws&&(
                      <div style={{fontSize:10,color:'rgba(61,31,13,.5)',marginTop:6,lineHeight:1.45}}>
                        {(()=>{const worst=[['Flow',tws.flow],['Culture',tws.culture],['Performance',tws.perf]].sort((a,b)=>a[1]-b[1])[0];
                          return worst[1]<60?<>Why: <b>{worst[0]} {worst[1]}</b> is the drag.</>:<>Pillars balanced.</>;})()}
                        {onNav&&<span onClick={()=>onNav('analytics')} style={{marginLeft:6,color:'#8b0020',fontWeight:700,cursor:'pointer',textDecoration:'underline',textUnderlineOffset:2}}>Evidence →</span>}
                      </div>
                    )}
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>Utilization</div>
                    <div style={{fontSize:22,fontWeight:900,color:'rgba(61,31,13,.7)'}}>{selected.util}%</div>
                    <div style={{height:4,background:'rgba(61,31,13,.1)',borderRadius:2,marginTop:6,width:80}}><div style={{width:`${selected.util}%`,height:'100%',borderRadius:2,background:selected.util>=70?'#22c55e':selected.util>=50?'#f59e0b':'#ef4444'}}/></div>
                  </div>
                </div>
                {/* Location details grid — editable fields save inline, no form modes (§04-02) */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
                  {[
                    ['Square Footage',`${selected.sf.toLocaleString()} SF`],
                    ['Annual Rent',fmtCur(selected.rent,selected.currency)],
                    ['$/Guest',`$${selected.costPerExp.toLocaleString()}/visit`],
                    ['Lease Type','Direct'],
                    ['Lease Start','2020-03-01'],
                    ['Lease Expiry',null,'lease_end','date',selected.leaseEnd],
                    ['Option Date',null,'option_date','date',selected.optionDate],
                    ['Business Unit',null,'bu','text',selected.bu],
                    ['Advisor',null,'advisor','text',selected.advisor],
                    ['Headcount',null,'headcount','number',selected.headcount],
                    ['Market',selected.market],
                    ['Data Source',selected.source],
                  ].map(([l,v,field,type,editVal])=>(
                    <div key={l} style={{background:'#F5EDE0',borderRadius:10,padding:'10px 12px'}}>
                      <div style={{fontSize:10,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em'}}>{l}{field?' ✎':''}</div>
                      <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D',marginTop:2}}>
                        {field
                          ?<InlineEdit value={editVal} field={field} type={type} slug={selected.id} placeholder="None" onSaved={refresh}/>
                          :v}
                      </div>
                    </div>
                  ))}
                </div>
                {/* ── Location Intelligence ── */}
                {(locActions.length>0||locProjects.length>0||rbwLoc)&&(
                  <div style={{borderTop:'1px solid rgba(61,31,13,.07)',paddingTop:16}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.38)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:12}}>Location Intelligence</div>
                    <div style={{display:'grid',gridTemplateColumns:locProjects.length>0&&rbwLoc?'1fr 1fr 1fr':locProjects.length>0||rbwLoc?'1fr 1fr':'1fr',gap:12}}>

                      {/* Pending Actions */}
                      {locActions.length>0&&(
                        <div style={{background:'rgba(61,31,13,.02)',border:'1px solid rgba(61,31,13,.08)',borderRadius:10,padding:'12px 14px'}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                            <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em'}}>Pending Actions</span>
                            <span style={{fontSize:9,fontWeight:700,background:'#dc2626',color:'#fff',borderRadius:999,padding:'1px 6px',lineHeight:1.5}}>{locActions.length}</span>
                          </div>
                          {locActions.slice(0,3).map((a,i)=>(
                            <div key={a.id} style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:i<Math.min(locActions.length,3)-1?8:0,paddingBottom:i<Math.min(locActions.length,3)-1?8:0,borderBottom:i<Math.min(locActions.length,3)-1?'1px solid rgba(61,31,13,.06)':'none'}}>
                              <span style={{fontSize:9,fontWeight:700,padding:'2px 5px',borderRadius:4,background:pBg[a.priority]||'#f3f4f6',color:pColors[a.priority]||'#6b7280',whiteSpace:'nowrap',flexShrink:0,marginTop:1}}>{a.priority}</span>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:11,fontWeight:600,color:'#3D1F0D',lineHeight:1.35,marginBottom:2}}>{a.title}</div>
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                  <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>{a.due}</span>
                                  {a.status==='Overdue'&&<span style={{fontSize:9,fontWeight:700,color:'#dc2626',background:'#fef2f2',padding:'1px 5px',borderRadius:3}}>OVERDUE</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                          {locActions.length>3&&<div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:8,fontWeight:600}}>+{locActions.length-3} more</div>}
                        </div>
                      )}

                      {/* Capital Projects */}
                      {locProjects.length>0&&(
                        <div style={{background:'rgba(61,31,13,.02)',border:'1px solid rgba(61,31,13,.08)',borderRadius:10,padding:'12px 14px'}}>
                          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Capital Projects</div>
                          {locProjects.map((p,i)=>{
                            const pctPaid=Math.round(p.paid/p.totalBudget*100);
                            const nextMil=p.milestones.find(m=>m.status!=='Done');
                            const pendingCOs=p.changeOrders.filter(co=>co.status==='pending');
                            return(
                              <div key={p.id} style={{marginBottom:i<locProjects.length-1?12:0}}>
                                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:4}}>
                                  <span style={{fontSize:11,fontWeight:700,color:'#3D1F0D'}}>{p.type}</span>
                                  <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:p.status==='on-track'?'#f0fdf4':p.status==='planning'?'#eff6ff':'#fffbeb',color:p.status==='on-track'?'#16a34a':p.status==='planning'?'#2563EB':'#d97706'}}>{p.status}</span>
                                </div>
                                <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'rgba(61,31,13,.5)',marginBottom:5}}>
                                  <span>{fmt(p.totalBudget)} budget</span>
                                  <span style={{fontWeight:700,color:p.burnAlert?'#dc2626':'rgba(61,31,13,.6)'}}>{pctPaid}% paid</span>
                                </div>
                                <div style={{height:4,background:'rgba(61,31,13,.08)',borderRadius:2,marginBottom:6}}>
                                  <div style={{width:`${pctPaid}%`,height:'100%',borderRadius:2,background:p.burnAlert?'#dc2626':'#059669'}}/>
                                </div>
                                {nextMil&&<div style={{fontSize:10,color:'rgba(61,31,13,.55)'}}>Next: <strong>{nextMil.name}</strong> · {nextMil.due}</div>}
                                {pendingCOs.length>0&&<div style={{fontSize:10,color:'#d97706',marginTop:3,fontWeight:600}}>⚠ {pendingCOs.length} change order{pendingCOs.length>1?'s':''} pending approval</div>}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* WX Delivery (RBW experience-setting scores) */}
                      {rbwLoc&&(
                        <div style={{background:'rgba(61,31,13,.02)',border:'1px solid rgba(61,31,13,.08)',borderRadius:10,padding:'12px 14px'}}>
                          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>WX Delivery</div>
                          <div style={{display:'flex',gap:10,marginBottom:10}}>
                            {[['Coverage',rbwLoc.coverageScore,rbwLoc.coverageScore>=75?'#16a34a':rbwLoc.coverageScore>=55?'#d97706':'#dc2626'],['Fidelity',rbwLoc.designFidelity,rbwLoc.designFidelity>=70?'#16a34a':rbwLoc.designFidelity>=50?'#d97706':'#dc2626']].map(([lbl,score,col])=>(
                              <div key={lbl} style={{flex:1,textAlign:'center'}}>
                                <ScoreRing score={score} size={46} color={col} strokeWidth={4} delay={200}/>
                                <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',marginTop:4}}>{lbl}</div>
                              </div>
                            ))}
                          </div>
                          {rbwLoc.topFriction!=='None'&&(
                            <div style={{fontSize:10,fontWeight:600,color:'#d97706',background:'#fffbeb',borderRadius:6,padding:'4px 8px',marginBottom:8}}>⚠ Top friction: {rbwLoc.topFriction}</div>
                          )}
                          <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                            {Object.entries(rbwLoc.settings).map(([k,v])=>(
                              <div key={k} style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.55)'}}>{k} {v}%</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {detailTab==='documents'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {(selected.documents||[]).map((doc,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:'#F5EDE0',borderRadius:10,border:'1px solid rgba(61,31,13,.06)'}}>
                  <div style={{width:36,height:36,background:doc.type==='Lease'?'#8b0020':doc.type==='CofO'?'#2563EB':doc.type==='Floorplan'?'#059669':doc.type==='Abstract'?'#7C3AED':'#d97706',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,fontWeight:800,flexShrink:0}}>{doc.type==='Floorplan'?'FP':doc.type==='Abstract'?'AB':doc.type==='CofO'?'CO':doc.type==='Termination'?'TM':'LS'}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D'}}>{doc.name}</div>
                    <div style={{display:'flex',gap:8,marginTop:3,alignItems:'center',flexWrap:'wrap'}}>
                      <span style={{fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:4,background:doc.type==='Lease'?'rgba(139,0,33,.08)':doc.type==='CofO'?'rgba(37,99,235,.08)':doc.type==='Floorplan'?'rgba(5,150,105,.08)':doc.type==='Termination'?'rgba(239,68,68,.08)':'rgba(124,58,237,.08)',color:doc.type==='Lease'?'#8b0020':doc.type==='CofO'?'#2563EB':doc.type==='Floorplan'?'#059669':doc.type==='Termination'?'#dc2626':'#7C3AED'}}>{doc.type}</span>
                      <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>Filed {doc.date}</span>
                    </div>
                    {doc.delta&&<div style={{fontSize:11,color:'#d97706',marginTop:4,fontStyle:'italic'}}>{doc.delta}</div>}
                  </div>
                  <button onClick={()=>{if(doc.filePath){const a=document.createElement('a');a.href='/'+doc.filePath;a.download=doc.name;a.click();}else{alert('File not available for download — no path recorded.');}}} style={{fontSize:11,fontWeight:600,color:'#3D1F0D',background:'#fff',border:'1px solid rgba(61,31,13,.12)',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>↓ Download</button>
                </div>
              ))}
              {/* Scribe upload — Portfolio Spine */}
              <input ref={fileInputRef} type="file" accept=".pdf,.xlsx,.csv,.png,.jpg,.jpeg" style={{display:'none'}} onChange={handleUpload}/>
              {uploadResult&&(
                <div style={{marginBottom:12,padding:'10px 12px',borderRadius:8,background:uploadResult.ok?'#f0fdf4':'rgba(239,68,68,.06)',border:`1px solid ${uploadResult.ok?'#bbf7d0':'rgba(239,68,68,.2)'}`}}>
                  <div style={{fontSize:12,fontWeight:600,color:uploadResult.ok?'#16a34a':'#dc2626'}}>{uploadResult.ok?'✓ Scribe abstraction complete':'✗ Upload failed'}</div>
                  {uploadResult.ok&&<div style={{fontSize:11,color:'rgba(61,31,13,.5)',marginTop:4}}>{uploadResult.status}</div>}
                  {uploadResult.abstract_preview&&<div style={{fontSize:11,color:'rgba(61,31,13,.5)',marginTop:6,lineHeight:1.5,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}}>{uploadResult.abstract_preview}</div>}
                  {!uploadResult.ok&&<div style={{fontSize:11,color:'#dc2626',marginTop:4}}>{uploadResult.status}</div>}
                </div>
              )}
              <button
                onClick={()=>{if(!selected){alert('Select a location first.');return;}fileInputRef.current?.click();}}
                disabled={uploading}
                style={{width:'100%',padding:'12px',border:`2px dashed ${uploading?'#D97706':'rgba(61,31,13,.15)'}`,borderRadius:10,background:uploading?'rgba(217,119,6,.04)':'transparent',fontSize:12,color:uploading?'#D97706':'rgba(61,31,13,.4)',cursor:uploading?'not-allowed':'pointer',fontFamily:'inherit',transition:'all .15s',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}
                onMouseOver={e=>{if(!uploading){e.currentTarget.style.borderColor='#D97706';e.currentTarget.style.color='#D97706';}}}
                onMouseOut={e=>{if(!uploading){e.currentTarget.style.borderColor='rgba(61,31,13,.15)';e.currentTarget.style.color='rgba(61,31,13,.4)';}}}
              >
                <span style={{fontSize:14}}>{uploading?'⟳':'⬆'}</span>
                {uploading?'Scribe is abstracting…':'Upload document for Scribe abstraction'}
              </button>
            </div>
          )}
          {detailTab==='parties'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {(selected.parties||[]).map((p,i)=>{
                const roleColors={'Tenant':'#2563EB','Landlord':'#7C3AED','Tenant Advisor':'#3D1F0D','Building Mgmt':'#059669','Legal Counsel':'#4B5563'};
                const rc=roleColors[p.role]||'#3D1F0D';
                return(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:'#F5EDE0',borderRadius:10,border:'1px solid rgba(61,31,13,.06)'}}>
                    <div style={{width:36,height:36,background:rc,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:13,fontWeight:700,flexShrink:0}}>{p.name.split(' ').map(w=>w[0]).join('').slice(0,2)}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D'}}>{p.name}</div>
                      <div style={{fontSize:11,color:'rgba(61,31,13,.5)',marginTop:1}}>{p.role}</div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:6,height:6,borderRadius:'50%',background:p.active?'#22c55e':'#9ca3af',display:'inline-block'}}/><span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{p.active?'Active':'Inactive'}</span></div>
                  </div>
                );
              })}
              <AddPartyWidget endpoint={`/api/portfolio/locations/${selected.id}/parties`} roles={['Landlord','Advisor','Building Mgmt','Subtenant','Broker','Counsel','Contact']} onAdded={refresh}/>
            </div>
          )}
        </div>
      )}

      {/* Friction doctrine — typed confirmation on irreversible decisions;
          escalated acknowledgment requires a note (never dismiss-without-note) */}
      {spineModal&&(
        <SpineDecisionModal event={spineModal.event} mode={spineModal.mode}
          onClose={()=>setSpineModal(null)}
          onConfirm={payload=>recordAck(spineModal.event,payload)}/>
      )}

      {/* New Location modal */}
      {showLocModal&&(
        <FormModal title="New Portfolio Location" onClose={()=>{setLocWarn('');setLocError('');setShowLocModal(false);}} onSubmit={handleCreateLocation} submitting={locSaving}>
          {locError&&<div style={{padding:'8px 12px',background:'rgba(239,68,68,.07)',border:'1px solid rgba(239,68,68,.2)',borderRadius:7,fontSize:12,color:'#dc2626'}}>{locError}</div>}
          {locWarn&&<div style={{padding:'10px 12px',background:'rgba(232,129,58,.08)',border:'1px solid rgba(232,129,58,.35)',borderRadius:7,fontSize:12,color:'#9A3412'}}>
            <div style={{fontWeight:700,marginBottom:4}}>⚠️ Please verify this entry</div>
            <div style={{lineHeight:1.45,marginBottom:8}}>{locWarn}</div>
            <button type="button" onClick={()=>handleCreateLocation(true)} disabled={locSaving} style={{padding:'6px 12px',borderRadius:7,border:'none',background:'#9A3412',color:'#fff',fontSize:12,fontWeight:700,cursor:locSaving?'not-allowed':'pointer',fontFamily:'inherit'}}>
              {locSaving?'Saving…':"I've verified — create anyway"}
            </button>
          </div>}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Location Name" required>
              <input style={inputStyle} placeholder="e.g. 100 Park Avenue" value={locForm.name} onChange={setLF('name')}/>
            </Field>
            <Field label="Market" required>
              <input style={inputStyle} placeholder="e.g. New York" value={locForm.market} onChange={setLF('market')}/>
            </Field>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Square Footage (SF)" required>
              <input style={inputStyle} type="number" placeholder="e.g. 45000" value={locForm.sf} onChange={setLF('sf')}/>
            </Field>
            <Field label="Annual Rent (USD)" required>
              <input style={inputStyle} type="number" placeholder="e.g. 2700000" value={locForm.rent} onChange={setLF('rent')}/>
            </Field>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Lease Expiry Date" required>
              <input style={inputStyle} type="date" value={locForm.lease_end} onChange={setLF('lease_end')}/>
            </Field>
            <Field label="Option Date">
              <input style={inputStyle} type="date" value={locForm.option_date} onChange={setLF('option_date')}/>
            </Field>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Business Unit">
              <input style={inputStyle} placeholder="e.g. Corporate" value={locForm.bu} onChange={setLF('bu')}/>
            </Field>
            <Field label="Advisor">
              <input style={inputStyle} placeholder="e.g. Jordan Wells" value={locForm.advisor} onChange={setLF('advisor')}/>
            </Field>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Headcount">
              <input style={inputStyle} type="number" min="0" placeholder="e.g. 270" value={locForm.headcount} onChange={setLF('headcount')}/>
            </Field>
            <div style={{fontSize:11,color:'rgba(61,31,13,.45)',alignSelf:'end',paddingBottom:8,lineHeight:1.4}}>
              People assigned here. Drives the Space Demand surface — leave blank if not yet known (it’s surfaced as “not loaded,” never guessed).
            </div>
          </div>
          <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginTop:2}}>
            📋 Once created, upload a lease PDF and Scribe will extract and sync rent, SF, and expiry automatically.
          </div>
        </FormModal>
      )}
    </div>
  );
}

function DecisionCapturePanel({proposals}){
  const [dcStates,setDcStates]=useState({});
  const [edits,setEdits]=useState({});
  const {addToast}=useToast();
  const authFetch=useAuthFetch();
  // Confirm a Sage-inferred decision → write it to the Safe layer as an
  // immutable Decision asset (the human gate of the trust funnel). Optimistic:
  // flip the chip immediately, then reconcile with the server result.
  const confirmToSafe=async(dp,summary)=>{
    setDcStates(s=>({...s,[dp.id]:'confirmed'}));
    try{
      const r=await authFetch('/api/safe',{method:'POST',body:{
        title:`${dp.stage} — ${summary}`,
        provenance:`Sage-inferred decision confirmed by advisor. ${summary}`,
        asset_class:'Decision',
        source:'Dealroom — Decision Capture',
      }});
      if(r&&r.ok)addToast('✓ Decision written to Safe — immutable record','success');
      else addToast('Confirmed locally — could not reach Safe','error');
    }catch(e){addToast('Confirmed locally — could not reach Safe','error');}
  };
  return(
    <div style={{background:'rgba(232,129,58,.04)',border:'1px solid rgba(232,129,58,.2)',borderRadius:10,padding:'14px 16px',marginTop:4}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <span style={{fontSize:9,fontWeight:700,color:'#E8813A',background:'rgba(232,129,58,.12)',border:'1px solid rgba(232,129,58,.3)',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.07em'}}>⚡ Decision Capture</span>
        <span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>Sage-inferred decisions · Confirm to write to Safe</span>
      </div>
      {proposals.map(dp=>{
        const st=dcStates[dp.id]||dp.status;
        return(
          <div key={dp.id} style={{padding:'10px 12px',borderRadius:8,background:st==='confirmed'?'rgba(22,163,74,.06)':st==='rejected'?'rgba(61,31,13,.04)':'#fff',border:`1px solid ${st==='confirmed'?'rgba(22,163,74,.2)':st==='rejected'?'rgba(61,31,13,.1)':'rgba(232,129,58,.15)'}`,marginBottom:6}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:6}}>
              <div>
                <span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em'}}>{dp.stage}</span>
                <div style={{fontSize:11,color:'#3D1F0D',lineHeight:1.5,marginTop:2}}>{dp.summary}</div>
              </div>
              {st==='confirmed'&&<span style={{fontSize:9,fontWeight:700,color:'#16a34a',whiteSpace:'nowrap',background:'rgba(22,163,74,.08)',padding:'2px 7px',borderRadius:4,flexShrink:0}}>✓ Confirmed → Safe</span>}
              {st==='rejected'&&<span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',whiteSpace:'nowrap',flexShrink:0}}>✕ Rejected</span>}
            </div>
            {st==='proposed'&&(
              <div style={{display:'flex',gap:6}}>
                <button onClick={()=>confirmToSafe(dp,dp.summary)} style={{fontSize:10,fontWeight:700,padding:'4px 12px',borderRadius:5,background:'#16a34a',color:'#fff',border:'none',cursor:'pointer',fontFamily:'inherit'}}>✓ Confirm → Safe</button>
                <button onClick={()=>setDcStates(s=>({...s,[dp.id]:'editing'}))} style={{fontSize:10,fontWeight:600,padding:'4px 12px',borderRadius:5,background:'transparent',color:'rgba(61,31,13,.5)',border:'1px solid rgba(61,31,13,.15)',cursor:'pointer',fontFamily:'inherit'}}>Edit</button>
                <button onClick={()=>{setDcStates(s=>({...s,[dp.id]:'rejected'}));addToast('Decision rejected','warn');}} style={{fontSize:10,fontWeight:600,padding:'4px 12px',borderRadius:5,background:'transparent',color:'#ef4444',border:'1px solid rgba(239,68,68,.2)',cursor:'pointer',fontFamily:'inherit'}}>✕ Reject</button>
              </div>
            )}
            {st==='editing'&&(
              <div style={{marginTop:6}}>
                <textarea defaultValue={dp.summary} onChange={e=>setEdits(s=>({...s,[dp.id]:e.target.value}))} style={{width:'100%',minHeight:60,fontSize:11,padding:'6px 8px',border:'1px solid rgba(232,129,58,.3)',borderRadius:6,outline:'none',fontFamily:'inherit',resize:'vertical'}}/>
                <button onClick={()=>confirmToSafe(dp,(edits[dp.id]||dp.summary).trim())} style={{marginTop:4,fontSize:10,fontWeight:700,padding:'4px 12px',borderRadius:5,background:'#16a34a',color:'#fff',border:'none',cursor:'pointer',fontFamily:'inherit'}}>✓ Confirm edited version → Safe</button>
              </div>
            )}
            {st==='confirmed'&&dp.confirmedAt&&<div style={{fontSize:9,color:'rgba(61,31,13,.35)',marginTop:4}}>Confirmed {dp.confirmedAt} · {dp.confirmedBy}</div>}
          </div>
        );
      })}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   EXP-FOCUS (PRD Addendum v1.1) — focus architecture components.
   "Focus by default. Escalate by exception." One Setting fills the workspace;
   the rest are tabs (L0 glance) or live in the on-demand Spine Overview.
   ════════════════════════════════════════════════════════════════════════════ */

/* L0 — the health glyph: the zero-click answer to "which Setting is on fire?" */
function HealthDot({health,size=8,pulse}){
  const h=FOCUS_HEALTH[health]||FOCUS_HEALTH.calm;
  return <span title={h.label} className={pulse?'joy-glow':''} style={{width:size,height:size,borderRadius:'50%',background:h.color,display:'inline-block',flexShrink:0,boxShadow:health==='act'?`0 0 0 2px ${h.color}33`:'none'}}/>;
}

/* §3.1 SettingTabBar — browser-style tab strip. Pinned tabs hold the left
   positions; ~7 visible, overflow collapses into a ⋯ menu. */
function SettingTabBar({deals,tabIds,pinned,activeId,onSelect,onClose,onPin,onReorder,onOpenOverview,actCount,onOpenTriage,improved}){
  const [dragId,setDragId]=useState(null);
  const [moreOpen,setMoreOpen]=useState(false);
  const byId=id=>deals.find(d=>d.id===id);
  // Pinned hold the left positions; recency order for the rest.
  const ordered=[...pinned.filter(id=>byId(id)),...tabIds.filter(id=>!pinned.includes(id)&&byId(id))];
  const visible=ordered.slice(0,7);
  const overflow=ordered.slice(7);
  const handleDrop=targetId=>{
    if(!dragId||dragId===targetId)return;
    const rest=tabIds.filter(id=>id!==dragId);
    const at=rest.indexOf(targetId);
    rest.splice(at<0?rest.length:at,0,dragId);
    onReorder(rest);setDragId(null);
  };
  return(
    <div style={{display:'flex',alignItems:'flex-end',gap:4,marginBottom:0,position:'relative'}}>
      <button onClick={onOpenOverview} title="All Settings (⌘O)" style={{width:32,height:31,borderRadius:'9px 9px 0 0',border:'1px solid rgba(61,31,13,.08)',borderBottom:'none',background:'rgba(255,255,255,.55)',cursor:'pointer',fontSize:14,color:'rgba(61,31,13,.55)',flexShrink:0,fontFamily:'inherit'}}>⊞</button>
      <div style={{display:'flex',gap:4,overflow:'hidden',flex:1,minWidth:0}}>
        {visible.map(id=>{
          const d=byId(id);if(!d)return null;
          const health=settingHealth(d);
          const isPinned=pinned.includes(id);
          return(
            <div key={id} draggable onDragStart={()=>setDragId(id)} onDragOver={e=>e.preventDefault()} onDrop={()=>handleDrop(id)}
              className={`stab${id===activeId?' active':''}${health==='act'?' act-tab':''}`}
              onClick={()=>onSelect(id)} title={d.name}>
              <HealthDot health={health} pulse={improved&&improved.has(id)}/>
              <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
              <span className="stab-x" title={isPinned?'Unpin (⌘P)':'Pin (⌘P)'} onClick={e=>{e.stopPropagation();onPin(id);}} style={{fontSize:10,cursor:'pointer',color:isPinned?'#E8813A':'rgba(61,31,13,.45)',opacity:isPinned?1:undefined}}>{isPinned?'⚲':'⚲'}</span>
              {!isPinned&&<span className="stab-x" title="Close tab" onClick={e=>{e.stopPropagation();onClose(id);}} style={{fontSize:12,cursor:'pointer',lineHeight:1}}>✕</span>}
            </div>
          );
        })}
      </div>
      {overflow.length>0&&(
        <div style={{position:'relative',flexShrink:0}}>
          <button onClick={()=>setMoreOpen(o=>!o)} style={{width:30,height:31,borderRadius:'9px 9px 0 0',border:'1px solid rgba(61,31,13,.08)',borderBottom:'none',background:'rgba(255,255,255,.55)',cursor:'pointer',fontSize:13,color:'rgba(61,31,13,.55)',fontFamily:'inherit'}}>⋯</button>
          {moreOpen&&(
            <div style={{position:'absolute',top:34,right:0,background:'#fff',border:'1px solid rgba(61,31,13,.1)',borderRadius:10,boxShadow:'0 8px 28px rgba(61,31,13,.18)',zIndex:50,minWidth:220,padding:6}}>
              {overflow.map(id=>{const d=byId(id);if(!d)return null;return(
                <div key={id} onClick={()=>{onSelect(id);setMoreOpen(false);}} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:600,color:'#3D1F0D'}}
                  onMouseEnter={e=>e.currentTarget.style.background='rgba(232,129,58,.07)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <HealthDot health={settingHealth(d)}/><span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.name}</span>
                </div>
              );})}
            </div>
          )}
        </div>
      )}
      {actCount>0&&(
        <button onClick={onOpenTriage} title="Open Triage (⌘⇧T) — Act Settings only" style={{flexShrink:0,marginLeft:4,marginBottom:3,display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:999,border:'1px solid rgba(200,75,47,.3)',background:'rgba(200,75,47,.08)',color:'#C84B2F',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
          <span style={{width:7,height:7,borderRadius:'50%',background:'#C84B2F'}}/>{actCount} Act
        </button>
      )}
    </div>
  );
}

/* §3.2 SpineOverview — the full card rail, now on-demand. Health-sorted, Act
   first: the fire floats to the top. The "shift priority with haste" surface. */
function SpineOverview({deals,pinned,onSelect,onPin,onClose}){
  const [q,setQ]=useState('');
  const [filter,setFilter]=useState('All');
  const inputRef=useRef(null);
  useEffect(()=>{inputRef.current&&inputRef.current.focus();},[]);
  const tiers={act:0,watch:1,calm:2};
  let list=deals.map(d=>({d,health:settingHealth(d),days:daysUntilISO(d.nextDeadline)??9999}));
  if(filter==='Pinned')list=list.filter(x=>pinned.includes(x.d.id));
  else if(filter!=='All')list=list.filter(x=>x.health===filter.toLowerCase());
  if(q.trim())list=list.filter(x=>(x.d.name+' '+x.d.property+' '+x.d.market).toLowerCase().includes(q.trim().toLowerCase()));
  list.sort((a,b)=>(tiers[a.health]-tiers[b.health])||(a.days-b.days));
  return(
    <div className="focus-overlay" onClick={onClose}>
      <div className="focus-panel" onClick={e=>e.stopPropagation()}>
        <div style={{padding:'16px 20px 12px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
          <div style={{fontSize:15,fontWeight:800,color:'#3D1F0D',flexShrink:0}}>All Settings</div>
          <input ref={inputRef} value={q} onChange={e=>setQ(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'&&list.length){onSelect(list[0].d.id);}if(e.key==='Escape'){e.stopPropagation();onClose();}}}
            placeholder="Type to filter… Enter focuses the top match" style={{flex:1,minWidth:180,background:'#fff',border:'1px solid rgba(61,31,13,.12)',borderRadius:9,padding:'7px 12px',fontSize:13,outline:'none'}}/>
          <div style={{display:'flex',gap:6,flexShrink:0}}>
            {['All','Act','Watch','Calm','Pinned'].map(f=>(
              <button key={f} className={`seg-btn${filter===f?' on':''}`} onClick={()=>setFilter(f)}>{f}</button>
            ))}
          </div>
          <button onClick={onClose} title="Close (Esc)" style={{border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.35)',cursor:'pointer',padding:'0 2px',lineHeight:1}}>✕</button>
        </div>
        <div style={{overflowY:'auto',padding:16,display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:10,alignContent:'start'}}>
          {list.map(({d,health})=>{
            const isStrategic=d.settingType==='Strategic';
            const phColor=isStrategic?'#059669':wexPhaseColor(d.wexPhase);
            const phLabel=isStrategic?'Strategic':wexPhaseLabel(d.wexPhase);
            const phScores=d.phaseCompletion||{};
            const completionScore=isStrategic?10:Math.round(Object.values(phScores).reduce((a,b)=>a+b,0)/5);
            const completionColor=completionScore>=7?'#22c55e':completionScore>=4?'#f59e0b':'#ef4444';
            const isPinned=pinned.includes(d.id);
            return(
              <div key={d.id} onClick={()=>onSelect(d.id)} className="card" style={{padding:14,cursor:'pointer',borderLeft:`3px solid ${FOCUS_HEALTH[health].color}`}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <div style={{display:'flex',gap:4,alignItems:'center',minWidth:0}}>
                    <HealthDot health={health}/>
                    <span style={{fontSize:9,fontWeight:700,color:FOCUS_HEALTH[health].color,textTransform:'uppercase',letterSpacing:'.05em'}}>{FOCUS_HEALTH[health].label}</span>
                    <span style={{fontSize:9,fontWeight:700,color:phColor,background:phColor+'1A',padding:'1px 6px',borderRadius:4,whiteSpace:'nowrap'}}>{phLabel}</span>
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
                    <span title={isPinned?'Unpin':'Pin'} onClick={e=>{e.stopPropagation();onPin(d.id);}} style={{fontSize:11,cursor:'pointer',color:isPinned?'#E8813A':'rgba(61,31,13,.3)'}}>⚲</span>
                    <span style={{fontSize:10,fontWeight:800,color:isStrategic?'#059669':completionColor}}>{completionScore}/10</span>
                  </div>
                </div>
                <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',lineHeight:1.3,marginBottom:4}}>{d.name}</div>
                <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:8}}>{isStrategic?d.market:(Math.abs(d.sf).toLocaleString()+' SF · '+d.market)}</div>
                {!isStrategic&&(
                  <div style={{display:'flex',gap:2,marginBottom:6}}>
                    {WEX_PHASES.map(ph=>{const s=phScores[ph.id]||0;
                      return <div key={ph.id} style={{flex:1,height:3,borderRadius:2,background:s>0?ph.color:'rgba(61,31,13,.08)'}} title={`${ph.label}: ${s}/10`}/>;})}
                  </div>
                )}
                <div style={{fontSize:11,color:'#E8813A',fontWeight:500}}>{d.nextMilestone}</div>
              </div>
            );
          })}
          {list.length===0&&<div style={{gridColumn:'1/-1',padding:30,textAlign:'center',fontSize:12,color:'rgba(61,31,13,.4)'}}>No Settings match.</div>}
        </div>
      </div>
    </div>
  );
}

/* §3.3 One Driver — the single Sage-curated statement of the one thing that
   matters on this Setting right now. One sentence. One action. One source. */
function OneDriverCard({deal,driver,onAsk,onAcked}){
  const [why,setWhy]=useState(false);
  const [confirmAck,setConfirmAck]=useState(false);
  const h=FOCUS_HEALTH[driver.severity]||FOCUS_HEALTH.calm;
  // §5 — `?` while the workspace is focused toggles the Sage reasoning (L3)
  useEffect(()=>{
    const t=()=>setWhy(w=>!w);
    window.addEventListener('stage-why',t);
    return()=>window.removeEventListener('stage-why',t);
  },[]);
  // D-04 joy — restrained affirmation when this Setting improved since last visit.
  const [improvedNote,setImprovedNote]=useState(false);
  useEffect(()=>{
    try{
      const m=JSON.parse(localStorage.getItem('stage_focus_lastsev')||'{}');
      const prev=m[deal.id];
      if(prev&&_SEV_RANK[driver.severity]>_SEV_RANK[prev]){setImprovedNote(true);setTimeout(()=>setImprovedNote(false),1200);}
      m[deal.id]=driver.severity;localStorage.setItem('stage_focus_lastsev',JSON.stringify(m));
    }catch(e){}
  },[deal.id]);
  return(
    <div className={improvedNote?'joy-glow':''} style={{marginTop:12,marginBottom:12,padding:'13px 16px',background:h.color+'0A',border:`1px solid ${h.color}33`,borderLeft:`4px solid ${h.color}`,borderRadius:10}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7,flexWrap:'wrap'}}>
        <span style={{fontSize:9,fontWeight:800,color:'#fff',background:h.color,padding:'2px 9px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.08em'}}>{h.label}</span>
        <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em'}}>One Driver</span>
        <span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>{driver.sourceLabel}</span>
        <span style={{marginLeft:'auto',fontSize:9,fontFamily:'monospace',padding:'1px 7px',borderRadius:8,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.5)'}} title="D-05: confidence is capped on partial data">conf: {driver.confidence}</span>
        {improvedNote&&<span className="joy-rise" style={{fontSize:10,fontWeight:700,color:'#059669'}}>✓ Improved since your last visit</span>}
      </div>
      <div style={{fontFamily:'Georgia,serif',fontSize:17,color:'#3D1F0D',lineHeight:1.35,marginBottom:10}}>{driver.statement}</div>
      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <button onClick={()=>onAsk&&onAsk(driver.primaryAction.intent)} style={{padding:'8px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>{driver.primaryAction.label}</button>
        <span onClick={()=>setWhy(w=>!w)} style={{fontSize:11.5,color:'rgba(61,31,13,.55)',cursor:'pointer',textDecoration:'underline',textUnderlineOffset:3}} title="Press ? while focused">{why?'Hide reasoning':'Why this?'}</span>
        {driver.severity==='act'&&!confirmAck&&(
          <span onClick={()=>setConfirmAck(true)} style={{marginLeft:'auto',fontSize:11,color:'rgba(61,31,13,.4)',cursor:'pointer'}}>Acknowledge</span>
        )}
        {confirmAck&&(
          <span style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center',fontSize:11}}>
            <span style={{color:'rgba(61,31,13,.55)'}}>Demote this Act driver to Watch?</span>
            <button onClick={()=>{acknowledgeDriver(driver);setConfirmAck(false);onAcked&&onAcked();}} style={{padding:'3px 10px',border:'1px solid #C84B2F',borderRadius:5,background:'transparent',color:'#C84B2F',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Acknowledge</button>
            <span onClick={()=>setConfirmAck(false)} style={{color:'rgba(61,31,13,.4)',cursor:'pointer'}}>Cancel</span>
          </span>
        )}
      </div>
      {why&&(
        <div className="focus-fade" style={{marginTop:10,padding:'9px 12px',background:'rgba(61,31,13,.04)',borderRadius:8,fontSize:11.5,color:'rgba(61,31,13,.65)',lineHeight:1.55}}>
          <span style={{fontWeight:700,color:'rgba(61,31,13,.5)'}}>Sage · why this driver — </span>{driver.reasoning}
        </div>
      )}
    </div>
  );
}

/* §3.4 Brief — an agent brief collapsed to one line by default; expands in
   place (L2→L3). Drilling never navigates away. */
function Brief({tag,tagColor,title,summary,open,onToggle,children}){
  return(
    <div className="card card-flat" style={{overflow:'hidden'}}>
      <div onClick={onToggle} style={{display:'flex',alignItems:'center',gap:9,padding:'10px 14px',cursor:'pointer',userSelect:'none'}}>
        {tag&&<span style={{fontSize:9,fontWeight:700,color:tagColor,background:tagColor+'18',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.06em',flexShrink:0}}>{tag}</span>}
        <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D',flexShrink:0}}>{title}</span>
        {!open&&<span style={{fontSize:11,color:'rgba(61,31,13,.45)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>{summary}</span>}
        <span style={{marginLeft:'auto',fontSize:10,color:'rgba(61,31,13,.35)',flexShrink:0,transform:open?'rotate(90deg)':'none',transition:'transform .15s'}}>›</span>
      </div>
      {open&&<div className="focus-fade" style={{padding:'0 14px 14px'}}>{children}</div>}
    </div>
  );
}

/* §3.5 TriageView — escalation by exception. Act-only; one screen; the empty
   state is a reward, not a void. */
function TriageView({deals,onFocus,onAsk,onClose,onAcked}){
  const [leaving,setLeaving]=useState(new Set());
  const rows=deals.map(d=>({d,driver:computeOneDriver(d)})).filter(x=>x.driver.severity==='act'||leaving.has(x.d.id));
  const cleared=getWeekCleared();
  const ack=(row)=>{
    acknowledgeDriver(row.driver);
    setLeaving(s=>new Set([...s,row.d.id]));
    setTimeout(()=>{setLeaving(s=>{const n=new Set(s);n.delete(row.d.id);return n;});onAcked&&onAcked();},380);
  };
  return(
    <div className="focus-overlay" onClick={onClose}>
      <div className="focus-panel" style={{width:680}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'16px 20px 12px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:10}}>
          <span style={{width:9,height:9,borderRadius:'50%',background:'#C84B2F'}}/>
          <div style={{fontSize:15,fontWeight:800,color:'#3D1F0D'}}>Triage</div>
          <div style={{fontSize:11,color:'rgba(61,31,13,.45)'}}>Act Settings only — nothing critical can hide here</div>
          {cleared>0&&<span style={{marginLeft:'auto',fontSize:10,fontWeight:700,color:'#059669',background:'rgba(5,150,105,.08)',padding:'2px 9px',borderRadius:999}}>{cleared} critical driver{cleared===1?'':'s'} cleared this week</span>}
          <button onClick={onClose} title="Close (Esc)" style={{marginLeft:cleared>0?0:'auto',border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.35)',cursor:'pointer',lineHeight:1}}>✕</button>
        </div>
        <div style={{overflowY:'auto',padding:16,display:'flex',flexDirection:'column',gap:10}}>
          {rows.map(row=>(
            <div key={row.d.id} className={leaving.has(row.d.id)?'triage-out':''} style={{padding:'12px 15px',background:'#fff',border:'1px solid rgba(200,75,47,.25)',borderLeft:'4px solid #C84B2F',borderRadius:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                <span style={{fontSize:12,fontWeight:800,color:'#3D1F0D'}}>{row.d.name}</span>
                <span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>{row.driver.sourceLabel}</span>
                <span onClick={()=>{onFocus(row.d.id);onClose();}} style={{marginLeft:'auto',fontSize:11,fontWeight:600,color:'#E8813A',cursor:'pointer',flexShrink:0}}>Focus →</span>
              </div>
              <div style={{fontFamily:'Georgia,serif',fontSize:14,color:'#3D1F0D',lineHeight:1.4,marginBottom:9}}>{row.driver.statement}</div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <button onClick={()=>{onAsk&&onAsk(row.driver.primaryAction.intent);onClose();}} style={{padding:'6px 13px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>{row.driver.primaryAction.label}</button>
                <span onClick={()=>ack(row)} style={{fontSize:11,color:'rgba(61,31,13,.45)',cursor:'pointer'}} title="Acknowledge — demotes to Watch">Acknowledge</span>
              </div>
            </div>
          ))}
          {rows.length===0&&(
            <div className="joy-rise" style={{padding:'42px 20px',textAlign:'center'}}>
              <div className="joy-check" style={{fontSize:30,color:'#059669',marginBottom:10}}>✓</div>
              <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D',marginBottom:6}}>All critical drivers addressed.</div>
              <div style={{fontSize:12,color:'rgba(61,31,13,.5)'}}>{cleared>0?`${cleared} cleared this week. `:''}Every Setting is at Watch or Calm.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* §3.6 CommandPalette — ⌘K: switch with haste. Settings by name, `>` for
   actions, Ask-Sage passthrough so the existing affordance is extended. */
function CommandPalette({open,onClose,deals,recentIds,onFocusSetting,onAction,onAskSage}){
  const [q,setQ]=useState('');
  const [idx,setIdx]=useState(0);
  const inputRef=useRef(null);
  useEffect(()=>{if(open){setQ('');setIdx(0);setTimeout(()=>inputRef.current&&inputRef.current.focus(),30);}},[open]);
  if(!open)return null;
  const ACTIONS=[
    {id:'triage',label:'Open Triage',hint:'⌘⇧T'},
    {id:'overview',label:'Open Spine Overview',hint:'⌘O'},
    {id:'pin',label:'Pin current Setting',hint:'⌘P'},
    {id:'new',label:'New Setting',hint:''},
    {id:'lp',label:'Generate LP Update',hint:'Sage'},
  ];
  let rows=[];
  if(q.startsWith('>')){
    const aq=q.slice(1).trim().toLowerCase();
    rows=ACTIONS.filter(a=>a.label.toLowerCase().includes(aq)).map(a=>({type:'action',...a}));
  }else{
    const sq=q.trim().toLowerCase();
    const order=id=>{const i=(recentIds||[]).indexOf(id);return i<0?999:i;};
    const matches=deals
      .filter(d=>!sq||(d.name+' '+(d.property||'')+' '+(d.market||'')).toLowerCase().includes(sq))
      .sort((a,b)=>order(a.id)-order(b.id))
      .slice(0,7)
      .map(d=>({type:'setting',id:d.id,label:d.name,health:settingHealth(d),deadline:d.nextDeadline}));
    rows=matches;
    if(sq)rows=[...rows,{type:'sage',label:`Ask Sage: "${q.trim()}"`}];
  }
  const run=row=>{
    if(!row)return;
    if(row.type==='setting')onFocusSetting(row.id);
    else if(row.type==='action')onAction(row.id);
    else if(row.type==='sage')onAskSage(q.trim());
    onClose();
  };
  return(
    <div className="focus-overlay" style={{paddingTop:'14vh'}} onClick={onClose}>
      <div className="cmdk-panel" onClick={e=>e.stopPropagation()}>
        <input ref={inputRef} value={q} onChange={e=>{setQ(e.target.value);setIdx(0);}}
          onKeyDown={e=>{
            if(e.key==='ArrowDown'){e.preventDefault();setIdx(i=>Math.min(i+1,rows.length-1));}
            else if(e.key==='ArrowUp'){e.preventDefault();setIdx(i=>Math.max(i-1,0));}
            else if(e.key==='Enter'){e.preventDefault();run(rows[idx]||rows[0]);}
            else if(e.key==='Escape'){e.stopPropagation();onClose();}
          }}
          placeholder="Switch to a Setting… ( > for actions, or ask Sage )"
          style={{width:'100%',border:'none',borderBottom:'1px solid rgba(61,31,13,.08)',padding:'14px 18px',fontSize:14,outline:'none',fontFamily:'inherit',background:'transparent'}}/>
        <div style={{maxHeight:330,overflowY:'auto',padding:'6px 0'}}>
          {rows.map((row,i)=>(
            <div key={(row.id||'')+row.label} className={`cmdk-row${i===idx?' sel':''}`} onMouseEnter={()=>setIdx(i)} onClick={()=>run(row)}>
              {row.type==='setting'&&<HealthDot health={row.health}/>}
              {row.type==='action'&&<span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>›</span>}
              {row.type==='sage'&&<span style={{fontSize:11,color:'#E8813A'}}>✦</span>}
              <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:row.type==='setting'?600:500}}>{row.label}</span>
              {row.type==='setting'&&row.deadline&&<span style={{fontSize:10,color:'rgba(61,31,13,.4)',fontFamily:'monospace'}}>{row.deadline}</span>}
              {row.hint&&<span style={{fontSize:10,color:'rgba(61,31,13,.3)',fontFamily:'monospace'}}>{row.hint}</span>}
            </div>
          ))}
          {rows.length===0&&<div style={{padding:'18px',fontSize:12,color:'rgba(61,31,13,.4)',textAlign:'center'}}>No matches.</div>}
        </div>
      </div>
    </div>
  );
}

/* ── Deal Brief (TM pre-engagement win) — GET /api/dealroom/{slug}/brief ─────── */
function DealBriefPage(){
  const authFetch=useAuthFetch();
  const [deals,setDeals]=useState([]);
  const [sel,setSel]=useState('');
  const [b,setB]=useState(null);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState('');
  useEffect(()=>{ authFetch('/api/dealroom').then(d=>{const l=Array.isArray(d)?d:[];setDeals(l);if(l.length)setSel(s=>s||l[0].id);}).catch(e=>setErr(String(e))); },[authFetch]);
  useEffect(()=>{ if(!sel)return; setLoading(true);setErr('');setB(null);
    authFetch(`/api/dealroom/${sel}/brief`).then(x=>{setB(x);setLoading(false);}).catch(e=>{setErr(String(e));setLoading(false);}); },[sel,authFetch]);
  const asset=b&&b.asset;
  const card={background:'#fff',border:'1px solid rgba(61,31,13,.1)',borderRadius:12,padding:'16px 18px'};
  const cardLabel={fontSize:10.5,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10};
  const prio=p=>({P1:'#dc2626',P2:'#E8813A',P3:'#6b7280'}[p]||'#6b7280');
  return(
    <div>
      <div className="page-header-label">Deal Brief</div>
      <h1 style={{fontSize:26,fontWeight:800,color:'#3D1F0D',margin:'2px 0 4px',letterSpacing:'-.5px'}}>Pre-engagement Brief</h1>
      <p style={{fontSize:13.5,color:'rgba(61,31,13,.6)',margin:'0 0 18px',maxWidth:620}}>Everything you need before you engage the broker — assembled from portfolio memory in one read, not four hours of research.</p>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:18}}>
        <span style={{fontSize:12,fontWeight:600,color:'rgba(61,31,13,.6)'}}>Deal</span>
        <select value={sel} onChange={e=>setSel(e.target.value)} style={{padding:'8px 12px',borderRadius:8,border:'1px solid rgba(61,31,13,.2)',background:'#fff',fontSize:13,fontFamily:'inherit',color:'#3D1F0D',minWidth:260}}>
          {deals.map(d=><option key={d.id} value={d.id}>{d.name} · {d.market}</option>)}
        </select>
      </div>
      {loading&&<div style={{...card,color:'rgba(61,31,13,.5)'}}>Assembling the brief…</div>}
      {err&&!loading&&<div style={{...card,borderColor:'rgba(220,38,38,.3)',color:'#dc2626'}}>Could not load brief: {err}</div>}
      {b&&!loading&&<div style={{display:'flex',flexDirection:'column',gap:14}}>
        {/* Headline */}
        <div style={{...card,borderLeft:'3px solid #8b0020'}}>
          <div style={cardLabel}>{b.deal&&b.deal.stage} · velocity {b.deal&&b.deal.velocity}/10</div>
          <div style={{fontSize:15,fontWeight:600,color:'#3D1F0D',lineHeight:1.45}}>{b.headline}</div>
        </div>
        {/* Suggested anchors — the synthesis, the hero */}
        <div style={{...card,background:'rgba(139,0,33,.04)',borderColor:'rgba(139,0,33,.2)'}}>
          <div style={{...cardLabel,color:'#8b0020'}}>◆ Suggested Negotiation Anchors</div>
          {(b.suggestedAnchors||[]).length?(
            <ul style={{margin:0,paddingLeft:18,display:'flex',flexDirection:'column',gap:7}}>
              {b.suggestedAnchors.map((a,i)=><li key={i} style={{fontSize:13,color:'#3D1F0D',lineHeight:1.5}}>{a}</li>)}
            </ul>
          ):<div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>No anchors derived — limited portfolio memory for this deal yet.</div>}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          {/* Linked asset */}
          <div style={card}>
            <div style={cardLabel}>Linked Lease / Asset</div>
            {asset?(
              <div style={{fontSize:13,color:'#3D1F0D',lineHeight:1.7}}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>{asset.name}</div>
                <div>{Number(asset.sf||0).toLocaleString()} SF · <b>{fmtCur(asset.rent,asset.currency)}</b>/yr</div>
                <div>Lease ends <b>{asset.leaseEnd||'—'}</b>{asset.optionDate?<> · option {asset.optionDate}</>:null}</div>
                {asset.headcount!=null&&<div style={{color:'rgba(61,31,13,.6)'}}>{asset.headcount} people on site</div>}
              </div>
            ):<div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>No linked lease in the Portfolio Spine yet.</div>}
          </div>
          {/* Open issues */}
          <div style={card}>
            <div style={cardLabel}>Open Issues ({(b.openIssues||[]).length})</div>
            {(b.openIssues||[]).length?(
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {b.openIssues.map(a=>(
                  <div key={a.id} style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                    <span style={{flexShrink:0,fontSize:9.5,fontWeight:800,color:'#fff',background:prio(a.priority),borderRadius:5,padding:'2px 5px',marginTop:1}}>{a.priority}</span>
                    <span style={{fontSize:12.5,color:'#3D1F0D',lineHeight:1.4}}>{a.title}</span>
                  </div>
                ))}
              </div>
            ):<div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>No open issues on this asset.</div>}
          </div>
        </div>
        {/* Insights + established facts */}
        {((b.insights||[]).length>0||(b.establishedFacts||[]).length>0)&&(
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div style={card}>
              <div style={cardLabel}>Strategy Insights</div>
              {(b.insights||[]).length?b.insights.map(i=>(
                <div key={i.id} style={{marginBottom:8}}>
                  <div style={{fontSize:12.5,fontWeight:700,color:'#3D1F0D'}}>{i.title}</div>
                  <div style={{fontSize:11.5,color:'rgba(61,31,13,.6)'}}>confidence {i.conf}%</div>
                </div>
              )):<div style={{fontSize:12.5,color:'rgba(61,31,13,.5)'}}>—</div>}
            </div>
            <div style={card}>
              <div style={cardLabel}>Established Facts (Safe)</div>
              {(b.establishedFacts||[]).length?b.establishedFacts.map(f=>(
                <div key={f.slug} style={{display:'flex',gap:8,alignItems:'center',marginBottom:7}}>
                  <span style={{fontSize:9.5,fontWeight:800,color:'#fff',background:f.assetClass==='Decision'?'#8b0020':f.assetClass==='Fact'?'#16a34a':'#E8813A',borderRadius:5,padding:'2px 6px'}}>{f.assetClass}</span>
                  <span style={{fontSize:12.5,color:'#3D1F0D',lineHeight:1.4}}>{f.title}</span>
                </div>
              )):<div style={{fontSize:12.5,color:'rgba(61,31,13,.5)'}}>—</div>}
            </div>
          </div>
        )}
        {/* Negotiation history */}
        {(b.negotiationHistory||[]).length>0&&(
          <div style={card}>
            <div style={cardLabel}>Negotiation History</div>
            {b.negotiationHistory.map((m,i)=>(
              <div key={i} style={{fontSize:12.5,color:'#3D1F0D',marginBottom:5}}>
                <b>{m.title}</b> <span style={{color:'rgba(61,31,13,.5)'}}>· {m.date}{m.sentiment!=null?` · sentiment ${m.sentiment}`:''}</span>
              </div>
            ))}
          </div>
        )}
        {/* Honest edges */}
        {(b.missing||[]).length>0&&(
          <div style={{fontSize:11.5,color:'rgba(61,31,13,.5)',lineHeight:1.6,padding:'2px 4px'}}>
            <b style={{color:'rgba(61,31,13,.6)'}}>Not yet in Status:</b> {b.missing.join('  ·  ')}
          </div>
        )}
      </div>}
    </div>
  );
}

/* ── Space Demand (WS supply-vs-demand win) — /api/workplace/demand[/scenario] ── */
function SpaceDemandPage(){
  const authFetch=useAuthFetch();
  const [data,setData]=useState(null);
  const [err,setErr]=useState('');
  const [market,setMarket]=useState('');
  const [delta,setDelta]=useState('150');
  const [scenario,setScenario]=useState(null);
  const [running,setRunning]=useState(false);
  useEffect(()=>{ authFetch('/api/workplace/demand').then(d=>{setData(d);}).catch(e=>setErr(String(e))); },[authFetch]);
  const locs=(data&&data.locations)||[];
  const summ=(data&&data.summary)||{};
  const byClass=summ.byClass||{};
  const markets=[...new Set(locs.map(l=>l.market))];
  useEffect(()=>{ if(markets.length&&!market)setMarket(markets[0]); },[markets.join(',')]); // eslint-disable-line
  const COL={'over-housed':'#16a34a','balanced':'#6b7280','under-resourced':'#dc2626'};
  const card={background:'#fff',border:'1px solid rgba(61,31,13,.1)',borderRadius:12,padding:'14px 16px'};
  const run=()=>{ if(!market)return; setRunning(true);setErr('');
    authFetch('/api/workplace/demand/scenario',{method:'POST',body:{signals:[{market,headcountDelta:Number(delta)||0,label:market+' growth'}]}})
      .then(s=>{setScenario(s);setRunning(false);}).catch(e=>{setErr(String(e));setRunning(false);}); };
  return(
    <div>
      <div className="page-header-label">Space Demand</div>
      <h1 style={{fontSize:26,fontWeight:800,color:'#3D1F0D',margin:'2px 0 4px',letterSpacing:'-.5px'}}>Where are we over-housed and under-resourced?</h1>
      <p style={{fontSize:13.5,color:'rgba(61,31,13,.6)',margin:'0 0 18px',maxWidth:660}}>Supply (the seats the space holds) vs. demand (the seats the people need) — per location, with the currency-correct cost of slack. Then model a headcount change and see which lease becomes a constraint.</p>
      {err&&<div style={{...card,borderColor:'rgba(220,38,38,.3)',color:'#dc2626',marginBottom:14}}>{err}</div>}
      {!data&&!err&&<div style={{...card,color:'rgba(61,31,13,.5)'}}>Loading demand model…</div>}
      {data&&<>
        <div className="grid-4" style={{marginBottom:16}}>
          <KPI label="Under-resourced" value={String(byClass['under-resourced']||0)} sub="need capacity" warn={(byClass['under-resourced']||0)>0} barPct={Math.min((byClass['under-resourced']||0)*25,100)}/>
          <KPI label="Over-housed" value={String(byClass['over-housed']||0)} sub="surplus to consolidate" barPct={Math.min((byClass['over-housed']||0)*25,100)}/>
          <KPI label="Balanced" value={String(byClass['balanced']||0)} sub="within tolerance" barPct={Math.min((byClass['balanced']||0)*20,100)}/>
          <KPI label="Surplus capacity" value={`${Math.round((summ.surplusSf||0)/1000)}K SF`} sub={`${Object.keys(summ.surplusCostByCurrency||{}).length} currencies`} barPct={60}/>
        </div>
        {/* Surplus cost per currency (F-CUR-01: never one summed figure) */}
        {Object.keys(summ.surplusCostByCurrency||{}).length>0&&(
          <div style={{...card,marginBottom:16}}>
            <div style={{fontSize:10.5,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Cost of surplus space (per currency)</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:14}}>
              {Object.entries(summ.surplusCostByCurrency).map(([c,v])=>(
                <div key={c} style={{fontSize:14,fontWeight:700,color:'#3D1F0D'}}>{fmtCur(v,c)}<span style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.45)',marginLeft:4}}>{c}</span></div>
              ))}
            </div>
          </div>
        )}
        {/* Per-location table */}
        <div style={{...card,padding:0,overflow:'hidden',marginBottom:18}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
            <thead><tr style={{background:'#F5EDE0'}}>
              {['Location','Market','People','Seats (need / have)','Gap','Occ','Cost of gap','Status'].map((h,i)=>(
                <th key={h} style={{textAlign:i>1?'right':'left',padding:'9px 14px',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.05em',whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {locs.map(l=>(
                <tr key={l.slug} style={{borderTop:'1px solid rgba(61,31,13,.07)'}}>
                  <td style={{padding:'9px 14px',fontWeight:600,color:'#3D1F0D'}}>{l.name}</td>
                  <td style={{padding:'9px 14px',color:'rgba(61,31,13,.6)'}}>{l.market}</td>
                  <td style={{padding:'9px 14px',textAlign:'right',fontFamily:'monospace'}}>{l.headcount}</td>
                  <td style={{padding:'9px 14px',textAlign:'right',fontFamily:'monospace'}}>{l.demandSeats} / {l.supplySeats}</td>
                  <td style={{padding:'9px 14px',textAlign:'right',fontFamily:'monospace',fontWeight:700,color:l.seatGap<0?'#dc2626':'rgba(61,31,13,.7)'}}>{l.seatGap>0?'+':''}{l.seatGap}</td>
                  <td style={{padding:'9px 14px',textAlign:'right',fontFamily:'monospace',color:l.occupancy>=85?'#dc2626':l.occupancy<55?'#16a34a':'#3D1F0D'}}>{l.occupancy}%</td>
                  <td style={{padding:'9px 14px',textAlign:'right',fontFamily:'monospace'}}>{fmtCur(l.costOfGap,l.currency)}</td>
                  <td style={{padding:'9px 14px',textAlign:'right'}}><span style={{fontSize:10,fontWeight:700,color:'#fff',background:COL[l.classification],borderRadius:6,padding:'2px 8px',whiteSpace:'nowrap'}}>{l.classification}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Scenario runner */}
        <div style={{...card}}>
          <div style={{fontSize:10.5,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>◆ Translate a business change into a spatial decision</div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'rgba(61,31,13,.6)'}}>Grow</span>
            <select value={market} onChange={e=>setMarket(e.target.value)} style={{padding:'7px 11px',borderRadius:8,border:'1px solid rgba(61,31,13,.2)',background:'#fff',fontSize:13,fontFamily:'inherit',color:'#3D1F0D'}}>
              {markets.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
            <span style={{fontSize:13,color:'rgba(61,31,13,.6)'}}>by</span>
            <input value={delta} onChange={e=>setDelta(e.target.value.replace(/[^0-9-]/g,''))} style={{width:70,padding:'7px 10px',borderRadius:8,border:'1px solid rgba(61,31,13,.2)',fontSize:13,fontFamily:'monospace',color:'#3D1F0D'}}/>
            <span style={{fontSize:13,color:'rgba(61,31,13,.6)'}}>people</span>
            <button onClick={run} disabled={running||!market} style={{padding:'8px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:running?'default':'pointer',fontFamily:'inherit',opacity:running?.6:1}}>{running?'Modeling…':'Run scenario'}</button>
          </div>
          {scenario&&<div style={{marginTop:14}}>
            {(scenario.affected||[]).length===0?(
              <div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>No matching location for that market.</div>
            ):scenario.affected.map(a=>(
              <div key={a.slug} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 0',borderTop:'1px solid rgba(61,31,13,.07)'}}>
                <span style={{flexShrink:0,fontSize:10,fontWeight:700,color:'#fff',background:a.becomesConstraint?'#dc2626':COL[a.after.classification],borderRadius:6,padding:'3px 8px',marginTop:1}}>{a.becomesConstraint?'CONSTRAINT':a.after.classification}</span>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D'}}>{a.name} — {a.headcountBefore} → {a.headcountAfter} people · seat gap {a.before.seatGap>0?'+':''}{a.before.seatGap} → <b style={{color:a.after.seatGap<0?'#dc2626':'inherit'}}>{a.after.seatGap>0?'+':''}{a.after.seatGap}</b></div>
                  <div style={{fontSize:12,color:'rgba(61,31,13,.65)',marginTop:2}}>{a.recommendation}</div>
                </div>
              </div>
            ))}
          </div>}
        </div>
      </>}
    </div>
  );
}

function DealroomPage({onSageAsk}){
  const authFetch=useAuthFetch();
  const clerk=useClerk();
  const {isDemo}=useData();   // demo DEALS render only on the demo account
  const [deals,setDeals]=useState([]);
  const [sel,setSel]=useState('d1');
  const [tab,setTab]=useState('discovery');
  const [uploading,setUploading]=useState(false);
  const [uploadResult,setUploadResult]=useState(null);
  const fileInputRef=React.useRef(null);

  // New Deal modal
  const [showDealModal,setShowDealModal]=useState(false);
  const [dealForm,setDealForm]=useState({name:'',property:'',market:'',sf:'',stage:'Scoping',velocity:'5',next_milestone:'',next_deadline:'',advisor:''});
  const [dealSaving,setDealSaving]=useState(false);
  const [dealError,setDealError]=useState('');
  const setDF=k=>e=>setDealForm(f=>({...f,[k]:e.target.value}));

  // Advisor/external participant invite modal (PRD §5: broker invitation, zero friction, magic link)
  const [showCPInvite,setShowCPInvite]=useState(false);
  const [cpForm,setCPForm]=useState({name:'',email:'',org:'',role:'Advisor',channel:'email'});
  const [cpSaving,setCPSaving]=useState(false);
  const [cpDone,setCPDone]=useState(false);
  const [magicLink,setMagicLink]=useState('');
  const [linkCopied,setLinkCopied]=useState(false);
  const setCP=k=>e=>setCPForm(f=>({...f,[k]:e.target.value}));

  // ── EXP-FOCUS (v1.1) — tab strip, overview, triage state ──────────────────
  const [focus,setFocus]=useState(loadFocusState);          // {tabs,pinned,active,phases}
  const [overviewOpen,setOverviewOpen]=useState(false);
  const [triageOpen,setTriageOpen]=useState(false);
  const [ackBump,setAckBump]=useState(0);                   // re-render after acknowledgments
  const [improved,setImproved]=useState(new Set());          // tab ids whose health just improved (joy pulse)
  // §3.4.2 — one agent brief open at a time; the brief tied to the One Driver
  // auto-expands, others collapse to a one-line summary (L2→L3 in place).
  const [openBrief,setOpenBrief]=useState(null);
  const toggleBrief=id=>setOpenBrief(b=>b===id?null:id);
  const prevHealthRef=useRef({});
  const patchFocus=React.useCallback(updates=>{
    setFocus(f=>{const n={...f,...updates};saveFocusState(n);return n;});
  },[]);
  // Focus a Setting: raise (or create) its tab, restore its last-open phase.
  // Switching is pure client state — no reload, <200ms by construction (§5).
  const focusSetting=React.useCallback((id)=>{
    setFocus(f=>{
      const tabs=f.tabs.includes(id)?f.tabs:[...f.tabs,id];
      const n={...f,tabs,active:id};saveFocusState(n);return n;
    });
    setSel(id);
  },[]);
  const closeTab=React.useCallback((id)=>{
    setFocus(f=>{
      const tabs=f.tabs.filter(t=>t!==id);
      let active=f.active;
      if(active===id)active=tabs[0]||null;
      const n={...f,tabs,active};saveFocusState(n);
      if(active)setSel(active);
      return n;
    });
  },[]);
  const togglePin=React.useCallback((id)=>{
    setFocus(f=>{
      const pinned=f.pinned.includes(id)?f.pinned.filter(p=>p!==id):[...f.pinned,id];
      const n={...f,pinned};saveFocusState(n);return n;
    });
  },[]);

  // Stage name migration: old Dealroom stage names → new Setting lifecycle stages
  const _stageMap={'Prospecting':'Scoping','LOI':'Active','Negotiation':'Active','Execution':'Closing','Post-Close':'Closed','Active':'Active','Scoping':'Scoping','Closing':'Closing','Closed':'Closed','Renewal Pending':'Renewal Pending','Persistent':'Persistent'};

  // Shared refresh for deals list
  const refreshDeals=React.useCallback(()=>{
    authFetch('/api/dealroom').then(apiDeals=>{
      if(Array.isArray(apiDeals)&&apiDeals.length){
        // Merge API deals with DEALS constant: normalise stage names → new lifecycle stages,
        // backfill settingType/lifecycle/sageInsights from constant matched by NAME
        // (IDs may have drifted between constant and DB seed; names are stable).
        // sageInsights from API takes priority (real data); constant is fallback for demo.
        const merged=apiDeals.map(apiDeal=>{
          const constant=DEALS.find(c=>c.name===apiDeal.name)||{};
          const stage=_stageMap[apiDeal.stage]||apiDeal.stage;
          // Prefer constant documents (curated linked-not-hosted) over DB documents (raw uploads)
          // unless the API documents have something the constant doesn't (DB Scribe abstracts)
          const apiDocs=apiDeal.documents||[];
          const constDocs=constant.documents||[];
          const documents=constDocs.length>0?constDocs:apiDocs;
          return{
            ...constant,
            ...apiDeal,
            stage,
            documents,
            settingType:constant.settingType||'Standard',
            lifecycle:constant.lifecycle||stage,
            sageInsights:(apiDeal.sageInsights&&apiDeal.sageInsights.length)?apiDeal.sageInsights:(constant.sageInsights||[]),
          };
        });
        // Append DEALS constant entries (demo only) not returned by the API (matched by name)
        const apiNames=new Set(merged.map(d=>d.name));
        const constantOnly=isDemo?DEALS.filter(d=>d.id!=='d0'&&!apiNames.has(d.name)):[];
        const withConstant=[...merged,...constantOnly];
        // d0 Monthly Portfolio Review is Strategic — never stored in DB; demo-only prepend
        const d0=isDemo?DEALS.find(d=>d.id==='d0'):null;
        setDeals(d0?[d0,...withConstant]:withConstant);
      } else {
        // No live deals: demo account shows the curated DEALS; real accounts stay empty.
        setDeals(isDemo?DEALS:[]);
      }
    }).catch(()=>{});
  },[authFetch]);

  // Load live deals from DB (fallback to DEALS constant)
  React.useEffect(()=>{ refreshDeals(); },[]);

  // ── EXP-FOCUS effects ──────────────────────────────────────────────────────
  // Seed/repair the tab strip when deals arrive: drop dead ids; if empty, seed
  // health-first (act → watch → calm, soonest deadline) capped at 7 (§3.1/§7).
  React.useEffect(()=>{
    if(!deals.length)return;
    setFocus(f=>{
      const valid=id=>deals.some(d=>d.id===id);
      let tabs=f.tabs.filter(valid);
      const pinned=f.pinned.filter(valid);
      if(tabs.length===0){
        const tiers={act:0,watch:1,calm:2};
        tabs=deals.slice()
          .sort((a,b)=>(tiers[settingHealth(a)]-tiers[settingHealth(b)])||((daysUntilISO(a.nextDeadline)??9999)-(daysUntilISO(b.nextDeadline)??9999)))
          .slice(0,7).map(d=>d.id);
      }
      let active=valid(f.active)?f.active:(tabs[0]||deals[0].id);
      if(!tabs.includes(active))tabs=[...tabs,active];
      const n={...f,tabs,pinned,active};saveFocusState(n);return n;
    });
  },[deals.length]);
  // Active tab persists across sessions — sel follows focus.active (§3.1).
  React.useEffect(()=>{
    if(focus.active&&focus.active!==sel&&deals.some(d=>d.id===focus.active))setSel(focus.active);
  },[focus.active,deals.length]);
  // §3.4 auto-route + §5 context restore: on focus change, save the outgoing
  // Setting's scroll, then open the stored last-open phase — or, on first
  // visit, the phase that owns the One Driver. Restore scroll in place.
  const prevSelRef=useRef(null);
  React.useEffect(()=>{
    const c=document.querySelector('.content');
    let scrolls={};try{scrolls=JSON.parse(sessionStorage.getItem('stage_focus_scroll')||'{}');}catch(e){}
    if(prevSelRef.current&&prevSelRef.current!==sel&&c){
      scrolls[prevSelRef.current]=c.scrollTop;
      try{sessionStorage.setItem('stage_focus_scroll',JSON.stringify(scrolls));}catch(e){}
    }
    prevSelRef.current=sel;
    const d=deals.find(x=>x.id===sel);
    if(!d)return;
    if(d.settingType==='Strategic')setTab('discovery');
    else{
      const storedPhase=loadFocusState().phases[sel];
      setTab(storedPhase||computeOneDriver(d).phase||d.wexPhase||'discovery');
    }
    if(c)requestAnimationFrame(()=>{c.scrollTop=scrolls[sel]||0;});
  },[sel,deals.length]);
  // Persist the last-open phase per Setting (§5: return the TM exactly where they left it).
  React.useEffect(()=>{
    if(!sel||!tab)return;
    setFocus(f=>{
      if(f.phases[sel]===tab)return f;
      const n={...f,phases:{...f.phases,[sel]:tab}};saveFocusState(n);return n;
    });
  },[tab,sel]);
  // §3.4.2 — auto-expand the brief tied to the current One Driver when its
  // phase is open; otherwise everything starts collapsed (Law 1: ≤5 expanded).
  React.useEffect(()=>{
    const d=deals.find(x=>x.id===sel);
    if(!d){setOpenBrief(null);return;}
    const drv=computeOneDriver(d);
    const map={strat:'strat',sense:'sense',sage:'sage',scout:'sage'};
    setOpenBrief(drv.phase===tab?(map[drv.source]||null):null);
  },[sel,tab,deals.length]);
  // §6 joy — a Setting transitioning Act → Watch/Calm pulses its tab glyph (≤1.2s).
  React.useEffect(()=>{
    if(!deals.length)return;
    const prev=prevHealthRef.current,cur={},imp=new Set();
    deals.forEach(d=>{const h=settingHealth(d);cur[d.id]=h;
      if(prev[d.id]&&_SEV_RANK[h]>_SEV_RANK[prev[d.id]])imp.add(d.id);});
    prevHealthRef.current=cur;
    if(imp.size){setImproved(imp);const t=setTimeout(()=>setImproved(new Set()),1200);return()=>clearTimeout(t);}
  },[deals,ackBump]);
  // §5 keyboard map. No dep array — re-registered per render so closures stay fresh.
  React.useEffect(()=>{
    const orderedTabs=()=>{
      const byId=id=>deals.find(d=>d.id===id);
      return[...focus.pinned.filter(byId),...focus.tabs.filter(id=>!focus.pinned.includes(id)&&byId(id))];
    };
    const onKey=e=>{
      const tag=(e.target.tagName||'').toLowerCase();
      const typing=tag==='input'||tag==='textarea'||tag==='select'||e.target.isContentEditable;
      const meta=e.metaKey||e.ctrlKey;
      if(meta&&e.shiftKey&&e.key.toLowerCase()==='t'){e.preventDefault();setTriageOpen(o=>!o);return;}
      if(meta&&!e.shiftKey&&e.key.toLowerCase()==='o'){e.preventDefault();setOverviewOpen(o=>!o);return;}
      if(meta&&!e.shiftKey&&e.key.toLowerCase()==='p'){e.preventDefault();if(sel)togglePin(sel);return;}
      if(meta&&(e.key===']'||e.key==='[')){
        e.preventDefault();
        const ord=orderedTabs();const i=ord.indexOf(sel);
        if(!ord.length)return;
        const ni=i<0?0:(e.key===']'?(i+1)%ord.length:(i-1+ord.length)%ord.length);
        focusSetting(ord[ni]);return;
      }
      if(meta&&/^[1-9]$/.test(e.key)){
        e.preventDefault();const t=orderedTabs()[Number(e.key)-1];if(t)focusSetting(t);return;
      }
      if(typing)return;
      if(e.key==='Escape'){
        if(triageOpen){setTriageOpen(false);return;}
        if(overviewOpen){setOverviewOpen(false);return;}
        setOverviewOpen(true);return;  // Esc from focus = zoom out to the spine (§5)
      }
      if(e.key==='?'){e.preventDefault();window.dispatchEvent(new CustomEvent('stage-why'));return;}
    };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  });
  // Cross-page focus handoff — the App-level ⌘K palette (and Triage shortcut)
  // navigates here, then signals intent via sessionStorage + a window event.
  React.useEffect(()=>{
    const handle=d=>{
      if(!d)return;
      if(d.settingId)focusSetting(d.settingId);
      if(d.triage)setTriageOpen(true);
      if(d.overview)setOverviewOpen(true);
      if(d.newSetting){setShowDealModal(true);setDealError('');}
      if(d.pin&&sel)togglePin(sel);
    };
    try{
      const p=JSON.parse(sessionStorage.getItem('stage_focus_pending')||'null');
      if(p){sessionStorage.removeItem('stage_focus_pending');handle(p);}
    }catch(e){}
    const onEvt=e=>handle(e.detail||{});
    window.addEventListener('stage-focus',onEvt);
    return()=>window.removeEventListener('stage-focus',onEvt);
  });

  const handleCreateDeal=async()=>{
    if(!dealForm.name||!dealForm.property||!dealForm.market||!dealForm.sf||!dealForm.next_milestone){
      setDealError('Please fill in all required fields.');return;
    }
    setDealSaving(true);setDealError('');
    try{
      const r=await authFetch('/api/dealroom/deals',{
        method:'POST',
        body:{
          name:dealForm.name.trim(),
          property:dealForm.property.trim(),
          market:dealForm.market.trim(),
          sf:parseInt(dealForm.sf,10),
          stage:dealForm.stage,
          velocity:parseInt(dealForm.velocity,10)||5,
          next_milestone:dealForm.next_milestone.trim(),
          next_deadline:dealForm.next_deadline||null,
          advisor:dealForm.advisor.trim(),
        },
      });
      if(r?.ok){
        setShowDealModal(false);
        setDealForm({name:'',property:'',market:'',sf:'',stage:'Scoping',velocity:'5',next_milestone:'',next_deadline:'',advisor:''});
        refreshDeals();
        focusSetting(r.deal.id);
        // Background insight refresh — fire and forget (auth-aware)
        authFetch('/api/insights/generate',{method:'POST'}).then(()=>refresh()).catch(()=>{});
      } else {
        setDealError(r?.detail||'Failed to create deal.');
      }
    }catch(e){setDealError('Network error — please try again.');}
    finally{setDealSaving(false);}
  };

  const deal=deals.find(d=>d.id===sel);
  const roleColors={'Tenant Advisor':'#3D1F0D','Tenant':'#2563EB','Landlord':'#7C3AED',"Landlord's Broker":'#6D28D9','Legal Counsel':'#4B5563','Lender':'#0D9488'};

  const handleUpload=async(e)=>{
    const file=e.target.files?.[0];
    if(!file||!deal)return;
    setUploading(true);
    setUploadResult(null);
    try{
      const fd=new FormData();
      fd.append('file',file);
      const token=await getToken(clerk);
      const headers={};
      if(token)headers['Authorization']=`Bearer ${token}`;
      const r=await fetch(`${API_BASE}/api/dealroom/${deal.id}/scribe/upload`,{method:'POST',headers,body:fd});
      const data=await r.json();
      setUploadResult(data);
      // Refresh deals to show new doc
      authFetch('/api/dealroom').then(d=>{
        if(Array.isArray(d)&&d.length) setDeals(d);
      }).catch(()=>{});
    }catch(err){setUploadResult({ok:false,status:'Upload failed: '+err.message});}
    finally{setUploading(false);if(fileInputRef.current)fileInputRef.current.value='';}
  };
  const actCount=deals.filter(d=>settingHealth(d)==='act').length;
  return(
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      <div style={{marginBottom:16,display:'flex',alignItems:'flex-start',justifyContent:'space-between'}}>
        <div><div className="page-header-label">WX Delivery</div><div className="page-title">Active Settings</div></div>
        <div style={{display:'flex',alignItems:'center',gap:12,marginTop:4}}>
          <span style={{fontSize:10,fontFamily:'monospace',color:'rgba(61,31,13,.35)'}} title="⌘K switch · ⌘O overview · ⌘⇧T triage · ⌘[ ⌘] cycle · ⌘P pin · Esc zoom out">⌘K switch · ⌘O all · ⌘⇧T triage</span>
          <button className="btn-primary" onClick={()=>{setShowDealModal(true);setDealError('');}}>+ New Setting</button>
        </div>
      </div>
      {/* §7 zero Settings — onboarding empty state with a single CTA, never a dead end */}
      {deals.length===0&&(
        <div className="card" style={{padding:'48px 32px',textAlign:'center'}}>
          <div style={{fontSize:26,marginBottom:10}}>⟷</div>
          <div style={{fontFamily:'Georgia,serif',fontSize:20,color:'#3D1F0D',marginBottom:6}}>Open your first Setting</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.55)',maxWidth:420,margin:'0 auto 18px',lineHeight:1.6}}>A Setting is one experience-delivery engagement — a renewal, expansion, rightsizing, or exit. Sage assembles the Discovery brief the moment it opens.</div>
          <button className="btn-primary" onClick={()=>{setShowDealModal(true);setDealError('');}}>+ New Setting</button>
        </div>
      )}
      {/* §3.1 tab strip — hidden with a single Setting (§7: go straight to focus) */}
      {deals.length>1&&(
        <SettingTabBar deals={deals} tabIds={focus.tabs} pinned={focus.pinned} activeId={sel}
          onSelect={focusSetting} onClose={closeTab} onPin={togglePin}
          onReorder={tabs=>patchFocus({tabs})}
          onOpenOverview={()=>setOverviewOpen(true)}
          actCount={actCount} onOpenTriage={()=>setTriageOpen(true)} improved={improved}/>
      )}
      {/* The focused workspace — one Setting owns it. key={sel} re-mounts with a
          ≤200ms cross-fade: the same workspace re-contextualizing, not a page load. */}
      <div>
        {deal&&(()=>{
          const isStrategic=deal.settingType==='Strategic';
          const phColor=isStrategic?'#059669':wexPhaseColor(deal.wexPhase);
          const phLabel=isStrategic?'Persistent':wexPhaseLabel(deal.wexPhase);
          const phScores=deal.phaseCompletion||{};
          const completionScore=isStrategic?10:Math.round(Object.values(phScores).reduce((a,b)=>a+b,0)/5);
          const completionColor=completionScore>=7?'#16a34a':completionScore>=4?'#d97706':'#dc2626';
          // §4 — all candidate drivers (phase badges) + the One Driver (L1 header)
          const drivers=computeDrivers(deal);
          const oneDriver=computeOneDriver(deal);
          return(
        <div key={sel+'_'+ackBump} className="focus-fade" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div className="card" style={{padding:18,borderLeft:`4px solid ${phColor}`}}>
            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:12}}>
              <div>
                <div style={{display:'flex',gap:6,marginBottom:6,flexWrap:'wrap'}}>
                  <span style={{fontSize:9,fontWeight:700,color:isStrategic?'#059669':'rgba(61,31,13,.5)',background:isStrategic?'rgba(5,150,105,.08)':'rgba(61,31,13,.06)',padding:'2px 8px',borderRadius:4}}>{isStrategic?'Strategic Setting':'Standard Setting'}</span>
                  <span style={{fontSize:9,fontWeight:700,color:phColor,background:phColor+'1A',padding:'2px 8px',borderRadius:4}}>{phLabel}</span>
                  {deal.stage&&<span style={{fontSize:9,fontWeight:700,color:stageColors[deal.stage]||'#6B7280',background:(stageColors[deal.stage]||'#6B7280')+'18',padding:'2px 8px',borderRadius:4}}>{deal.stage}</span>}
                </div>
                <div style={{fontSize:18,fontWeight:800,color:'#3D1F0D',marginTop:2}}>{deal.name}</div>
                <div style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>{deal.property} · {deal.market}{!isStrategic&&deal.sf?' · '+Math.abs(deal.sf).toLocaleString()+' SF':''}</div>
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                <ScoreRing score={completionScore*10} size={52} color={completionColor} strokeWidth={4} delay={0}/>
                <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',letterSpacing:'.05em'}}>Complete</div>
              </div>
            </div>
            {/* §3.3 L1 — The One Driver: one statement, one source, one action */}
            <OneDriverCard deal={deal} driver={oneDriver} onAsk={onSageAsk} onAcked={()=>setAckBump(b=>b+1)}/>
            {/* Vitals strip — deadline, milestone, compact 5-D progress */}
            {!isStrategic&&(
              <div style={{display:'flex',gap:3,marginBottom:12}}>
                {WEX_PHASES.map(ph=>{
                  const s=phScores[ph.id]||0;
                  const isCurrent=deal.wexPhase===ph.id;
                  return <div key={ph.id} style={{flex:1,cursor:'pointer'}} onClick={()=>setTab(ph.id)} title={`${ph.label}: ${s}/10`}>
                    <div style={{fontSize:8,fontWeight:700,color:isCurrent?ph.color:'rgba(61,31,13,.3)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:2,textAlign:'center'}}>{ph.label.slice(0,4)}</div>
                    <div style={{height:4,background:'rgba(61,31,13,.07)',borderRadius:2}}>
                      <div style={{height:'100%',width:`${s*10}%`,background:ph.color,borderRadius:2,opacity:isCurrent?1:.6}}/>
                    </div>
                  </div>;
                })}
              </div>
            )}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <div style={{background:'#F5EDE0',borderRadius:10,padding:'10px 12px'}}><div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>Next Deadline</div><div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginTop:2}}>{deal.nextDeadline}</div></div>
              <div style={{background:'#F5EDE0',borderRadius:10,padding:'10px 12px'}}><div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>Next Milestone</div><div style={{fontSize:13,fontWeight:700,color:'#E8813A',marginTop:2}}>{deal.nextMilestone}</div></div>
            </div>
          </div>
          {/* WEX-1 Five-Phase Tabs — §3.4.1: a phase holding an unresolved driver
              carries a health glyph so the TM knows where to drill without opening each */}
          <div style={{display:'flex',gap:2,background:'#fff',borderRadius:10,padding:4,border:'1px solid rgba(61,31,13,.08)'}}>
            {(isStrategic?WEX_PHASES.slice(0,1):WEX_PHASES).map(ph=>{
              const phDrivers=drivers.filter(x=>x.phase===ph.id&&x.severity!=='calm');
              const worst=phDrivers.length?phDrivers.slice().sort((a,b)=>_SEV_RANK[a.severity]-_SEV_RANK[b.severity])[0]:null;
              return(
              <button key={ph.id} onClick={()=>setTab(ph.id)} style={{flex:1,padding:'7px 3px',borderRadius:8,border:'none',background:tab===ph.id?ph.color:'transparent',color:tab===ph.id?'#fff':'rgba(61,31,13,.45)',fontWeight:600,fontSize:10,cursor:'pointer',fontFamily:'inherit',textTransform:'uppercase',letterSpacing:'.04em',transition:'all .15s',position:'relative'}}>
                {ph.label}
                {worst&&tab!==ph.id&&<span title={`Unresolved ${FOCUS_HEALTH[worst.severity].label} driver in ${ph.label}`} style={{position:'absolute',top:3,right:3,width:5,height:5,borderRadius:3,background:FOCUS_HEALTH[worst.severity].color}}/>}
                {!worst&&ph.id==='design'&&(deal.options||[]).length>0&&tab!==ph.id&&<span style={{position:'absolute',top:3,right:3,width:5,height:5,borderRadius:3,background:ph.color}}/>}
              </button>
            );})}
            <button onClick={()=>setTab('documents')} style={{padding:'7px 8px',borderRadius:8,border:'none',background:tab==='documents'?'#3D1F0D':'transparent',color:tab==='documents'?'#fff':'rgba(61,31,13,.35)',fontWeight:600,fontSize:10,cursor:'pointer',fontFamily:'inherit',transition:'all .15s'}}>Docs</button>
          </div>
          {/* ── DISCOVERY TAB ────────────────────────────────── */}
          {tab==='discovery'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 13px',background:'rgba(74,127,165,.06)',border:'1px solid rgba(74,127,165,.2)',borderRadius:10}}>
                <span style={{fontSize:9,fontWeight:700,color:'#4A7FA5',background:'rgba(74,127,165,.12)',border:'1px solid rgba(74,127,165,.3)',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.07em'}}>Sage · Discovery Brief</span>
                <span style={{fontSize:11,color:'rgba(61,31,13,.6)'}}>Assembled from Safe data before first engagement</span>
                <span style={{marginLeft:'auto',fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',letterSpacing:'.04em'}}>Phase 1 · Active</span>
              </div>
              {deal.discoveryContext?.situation&&(
                <Brief tag="Sage" tagColor="#4A7FA5" title="Situation Statement" summary={deal.discoveryContext.situation}
                  open={openBrief==='situation'} onToggle={()=>toggleBrief('situation')}>
                  <div style={{fontSize:13,color:'#3D1F0D',lineHeight:1.6,fontStyle:'italic',borderLeft:'3px solid #4A7FA5',paddingLeft:12}}>"{deal.discoveryContext.situation}"</div>
                </Brief>
              )}
              {(deal.discoveryContext?.senseSignals||[]).length>0&&(
                <Brief tag="Sense" tagColor="#E8813A" title="Sense Signals" summary={deal.discoveryContext.senseSignals[0]}
                  open={openBrief==='sense'} onToggle={()=>toggleBrief('sense')}>
                  {deal.discoveryContext.senseSignals.map((s,i)=>(
                    <div key={i} style={{display:'flex',gap:9,padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)',alignItems:'flex-start'}}>
                      <span style={{width:7,height:7,borderRadius:'50%',background:'#4A7FA5',flexShrink:0,marginTop:5}}/>
                      <span style={{fontSize:12,color:'rgba(61,31,13,.7)',lineHeight:1.5}}>{s}</span>
                    </div>
                  ))}
                </Brief>
              )}
              {deal.stratBrief&&(
                <Brief tag="Strat" tagColor="#7C3AED" title="Financial Parameters" summary={`${deal.stratBrief.headcount?.toLocaleString()} people · ${deal.stratBrief.targetSF?.toLocaleString()} SF · $${deal.stratBrief.costPerSFTarget}/SF · ${deal.stratBrief.archetype}`}
                  open={openBrief==='strat'} onToggle={()=>toggleBrief('strat')}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                    {[['Target Headcount',`${deal.stratBrief.headcount?.toLocaleString()} people`],['Target SF',`${deal.stratBrief.targetSF?.toLocaleString()} SF`],['Cost / SF Target',`$${deal.stratBrief.costPerSFTarget}/SF`],['Culture Archetype',deal.stratBrief.archetype]].map(([k,v])=>(
                      <div key={k} style={{background:'rgba(245,237,224,.6)',borderRadius:8,padding:'9px 11px'}}>
                        <div style={{fontSize:9,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{k}</div>
                        <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </Brief>
              )}
              {(deal.sageInsights||[]).length>0&&(
                <Brief tag="Sage" tagColor="#059669" title="Discovery Intelligence" summary={(deal.sageInsights[0]||{}).text}
                  open={openBrief==='sage'} onToggle={()=>toggleBrief('sage')}>
                  <div className="sage-intel-body" style={{padding:0}}>
                    {deal.sageInsights.map((insight,i)=>(
                      <div key={i} className="sage-row">
                        <span className={`sage-tag ${insight.type}`}>{insight.source}</span>
                        <span>{insight.text}</span>
                      </div>
                    ))}
                  </div>
                </Brief>
              )}
              {(deal.discoveryContext?.openQuestions||[]).length>0&&(
                <Brief tag="Open" tagColor="#6B7280" title="Open Questions" summary={deal.discoveryContext.openQuestions[0]}
                  open={openBrief==='questions'} onToggle={()=>toggleBrief('questions')}>
                  {deal.discoveryContext.openQuestions.map((q,i)=>(
                    <div key={i} style={{display:'flex',gap:8,padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)',alignItems:'flex-start'}}>
                      <span style={{fontSize:10,fontWeight:800,color:'rgba(61,31,13,.3)',flexShrink:0,marginTop:2}}>Q{i+1}</span>
                      <span style={{fontSize:12,color:'rgba(61,31,13,.7)',lineHeight:1.5}}>{q}</span>
                    </div>
                  ))}
                </Brief>
              )}
              {!deal.discoveryContext?.situation&&(
                <div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:12}}>
                  Discovery brief not yet assembled. <span style={{color:'#4A7FA5',cursor:'pointer',fontWeight:600}} onClick={()=>onSageAsk&&onSageAsk(`Assemble a Discovery brief for ${deal.name}. Summarise the situation, surface available Sense signals, and identify the key open questions.`)}>Ask Sage to generate it →</span>
                </div>
              )}
            </div>
          )}
          {/* ── DEFINE TAB ────────────────────────────────────── */}
          {tab==='define'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {/* Experience Commitment — permanent, follows the Setting */}
              {deal.experienceCommitment&&(
                <div style={{padding:'12px 16px',background:'rgba(232,168,56,.07)',border:'1px solid rgba(232,168,56,.25)',borderLeft:'4px solid #E8A838',borderRadius:10}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                    <span style={{fontSize:10,fontWeight:700,color:'#E8A838',textTransform:'uppercase',letterSpacing:'.06em'}}>Experience Commitment</span>
                    <span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:4,background:'rgba(232,168,56,.15)',color:'#92400E'}}>Permanent · follows all phases</span>
                  </div>
                  <div style={{fontSize:12,color:'#3D1F0D',lineHeight:1.6}}>{deal.experienceCommitment}</div>
                </div>
              )}
              {!deal.experienceCommitment&&(
                <div style={{padding:'12px 16px',background:'rgba(61,31,13,.03)',border:'1px dashed rgba(61,31,13,.15)',borderRadius:10,fontSize:12,color:'rgba(61,31,13,.4)',textAlign:'center'}}>
                  Experience commitment not yet confirmed. Define it here — it will anchor all subsequent phases.
                </div>
              )}
              {/* Culture & Spatial Brief */}
              {!isStrategic&&(
                <Brief tag="Sense" tagColor="#E8813A" title="Culture & Spatial Brief" summary="Archetype, relationship-based work mix, and spatial criteria from Sense RBW-I"
                  open={openBrief==='culture'} onToggle={()=>toggleBrief('culture')}>
                  <div style={{padding:'9px 12px',background:'rgba(232,129,58,.04)',border:'1px solid rgba(232,129,58,.12)',borderRadius:8,marginBottom:10,fontSize:11,color:'rgba(61,31,13,.6)',lineHeight:1.5}}>
                    Culture brief generated from Sense RBW-I data and pre-populated at Setting creation. Engagement scope, archetype, and spatial criteria sourced from active portfolio intelligence.
                  </div>
                  <div style={{display:'grid',gap:8}}>
                    {[['Culture Archetype',deal.id==='d1'||deal.id==='d2'?'Group Exchange dominant · Community Exchange supporting':'Individual Produce + Community Exchange'],['Relationship-based Work','Cross-functional collaboration (40%) · Focus/deep work (35%) · Social/informal (25%)'],['Spatial Criteria','Daylight adjacency for IP · Cross-departmental collision paths · Writable surfaces in CE zones'],['Target Headcount',deal.sf>0?`~${Math.round(Math.abs(deal.sf)/150)} people at 150 SF/person`:'Portfolio-wide']].map(([k,v])=>(
                      <div key={k} style={{background:'rgba(245,237,224,.6)',borderRadius:8,padding:'9px 11px'}}>
                        <div style={{fontSize:10,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{k}</div>
                        <div style={{fontSize:12,color:'#3D1F0D',lineHeight:1.4}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </Brief>
              )}
              {/* Strat Financial Brief */}
              {deal.stratBrief&&(
                <Brief tag="Strat" tagColor="#7C3AED" title="Financial Brief" summary={`${deal.stratBrief.headcount?.toLocaleString()} people · ${deal.stratBrief.targetSF?.toLocaleString()} SF · $${deal.stratBrief.costPerSFTarget}/SF target`}
                  open={openBrief==='strat'} onToggle={()=>toggleBrief('strat')}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:deal.stratBrief.criteria?.length?12:0}}>
                    {[['Target Headcount',`${deal.stratBrief.headcount?.toLocaleString()} people`],['Target SF',`${deal.stratBrief.targetSF?.toLocaleString()} SF`],['Cost / SF Target',`$${deal.stratBrief.costPerSFTarget}/SF`],['Culture Archetype',deal.stratBrief.archetype]].map(([k,v])=>(
                      <div key={k} style={{background:'rgba(245,237,224,.6)',borderRadius:8,padding:'9px 11px'}}>
                        <div style={{fontSize:9,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{k}</div>
                        <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {(deal.stratBrief.criteria||[]).length>0&&(
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Evaluation Criteria</div>
                      {deal.stratBrief.criteria.map((c,i)=>(
                        <div key={i} style={{display:'flex',gap:7,alignItems:'flex-start',fontSize:11,color:'rgba(61,31,13,.65)',lineHeight:1.4,padding:'4px 0',borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                          <span style={{color:'#7C3AED',fontWeight:700,flexShrink:0,marginTop:1}}>·</span><span>{c}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Brief>
              )}
              {/* Decision Capture — ⚡ Second Shock Moment */}
              {!isStrategic&&(deal.decisionProposals||[]).length>0&&(
                <DecisionCapturePanel proposals={deal.decisionProposals}/>
              )}
              {/* Roster */}
              <Brief tag="Team" tagColor="#0D9488" title="Roster" summary={(deal.parties||[]).map(p=>p.name).join(' · ')||'No participants yet'}
                open={openBrief==='roster'} onToggle={()=>toggleBrief('roster')}>
                {(deal.parties||[]).map(p=>{
                  const roleColors={'Tenant Advisor':'#3D1F0D','Tenant':'#2563EB','Landlord':'#7C3AED',"Landlord's Broker":'#6D28D9','Legal Counsel':'#4B5563','Lender':'#0D9488'};
                  const rc=roleColors[p.role]||'#3D1F0D';
                  return(
                    <div key={p.role} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:p.active?'#f0fdf4':'#F5EDE0',border:p.active?'1px solid #bbf7d0':'1px solid rgba(61,31,13,.07)',borderRadius:9,marginBottom:6,opacity:p.active?1:.6}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:rc,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:11}}>{p.name.charAt(0)}</div>
                      <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{p.name}</div><div style={{fontSize:10,color:'rgba(61,31,13,.5)'}}>{p.role}</div></div>
                      <span style={{width:6,height:6,borderRadius:'50%',background:p.active?'#22c55e':'#9ca3af',display:'inline-block'}}/>
                    </div>
                  );
                })}
              </Brief>
            </div>
          )}

          {/* ── DESIGN TAB ────────────────────────────────────── */}
          {tab==='design'&&!isStrategic&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              {/* Experience commitment reminder */}
              {deal.experienceCommitment&&(
                <div style={{padding:'8px 12px',background:'rgba(232,168,56,.06)',border:'1px solid rgba(232,168,56,.2)',borderLeft:'3px solid #E8A838',borderRadius:9,fontSize:11,color:'rgba(61,31,13,.65)',lineHeight:1.5}}>
                  <span style={{fontWeight:700,color:'#E8A838'}}>Experience Commitment · </span>{deal.experienceCommitment}
                </div>
              )}
              {/* ⚡ First Shock Moment — Floor plan fit scoring */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'rgba(123,174,138,.06)',border:'1px solid rgba(123,174,138,.25)',borderRadius:10}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:9,fontWeight:700,color:'#7BAE8A',background:'rgba(123,174,138,.15)',border:'1px solid rgba(123,174,138,.3)',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.07em'}}>⚡ First Shock Moment</span>
                  <span style={{fontSize:11,color:'rgba(61,31,13,.6)',fontWeight:500}}>Options scored against <b>{deal.stratBrief?.archetype||'culture archetype'}</b> via Sense</span>
                </div>
                <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)'}}>Design · Active</span>
              </div>
              {deal.stratBrief&&(
                <div style={{padding:'9px 13px',background:'rgba(124,58,237,.04)',border:'1px solid rgba(124,58,237,.12)',borderRadius:8,fontSize:10,color:'rgba(61,31,13,.6)',lineHeight:1.5}}>
                  <span style={{fontWeight:700,color:'#7C3AED'}}>Experience Commitment · </span>
                  Does this option support <b>{deal.stratBrief.archetype}</b> priorities established in the brief?{(deal.stratBrief.criteria||[]).length>0&&<> Evaluation criteria: {deal.stratBrief.criteria.slice(0,2).join(' · ')}{deal.stratBrief.criteria.length>2?` + ${deal.stratBrief.criteria.length-2} more`:''}</>}
                </div>
              )}
              {/* ── Multi-option Radar Overlay (Phase 2) ── */}
              {(deal.options||[]).length>1&&(
                <div className="card" style={{padding:16}}>
                  <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Setting Alignment — All Options</div>
                  <RadarChart options={deal.options||[]} archetype={deal.stratBrief?.archetype}/>
                  <RadarLegend options={deal.options||[]} archetype={deal.stratBrief?.archetype}/>
                  {(()=>{
                    const profile=MK_TARGET_PROFILES[deal.stratBrief?.archetype]||MK_TARGET_PROFILES['default'];
                    const topOpt=deal.options[0];
                    const gaps=MK_AXES.filter(ax=>{
                      const val=(topOpt?.rbw||{})[ax.key]||0;
                      return val<(profile[ax.key]||0)-10;
                    });
                    if(!gaps.length) return null;
                    return(
                      <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:5}}>
                        {gaps.map(ax=>{
                          const val=(topOpt?.rbw||{})[ax.key]||0;
                          const tgt=profile[ax.key]||0;
                          return(
                            <div key={ax.key} style={{fontSize:10,color:'#92400E',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:7,padding:'5px 9px',display:'flex',gap:6,alignItems:'flex-start'}}>
                              <span style={{flexShrink:0}}>⚠</span>
                              <span><b>{ax.short} · {ax.label}</b> scores {val} vs archetype target {tgt} — this gap conflicts with the Experience Commitment. Consider negotiating fit-out scope or re-weighting the brief.</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* ── Individual option cards ── */}
              {(deal.options||[]).map((opt,idx)=>{
                const fitColor=opt.fitScore>=85?'#16a34a':opt.fitScore>=70?'#E8813A':'#ef4444';
                const fitBorder=opt.fitScore>=85?'rgba(22,163,74,.25)':opt.fitScore>=70?'rgba(232,129,58,.25)':'rgba(239,68,68,.25)';
                const profile=MK_TARGET_PROFILES[deal.stratBrief?.archetype]||MK_TARGET_PROFILES['default'];
                return(
                  <div key={opt.id} className="card" style={{padding:16,border:`1px solid ${idx===0?fitBorder:'rgba(61,31,13,.08)'}`,position:'relative'}}>
                    {idx===0&&<div style={{position:'absolute',top:12,right:12,fontSize:9,fontWeight:700,color:'#059669',background:'rgba(5,150,105,.08)',border:'1px solid rgba(5,150,105,.2)',padding:'2px 7px',borderRadius:4}}>▲ Top Match</div>}
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12,paddingRight:idx===0?84:0}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginBottom:6}}>{opt.name}</div>
                        <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                          {[['SF',opt.sf?.toLocaleString()],['Base Rent',`$${opt.baseRent}/SF`],['TI',`$${opt.ti}/SF`],['Free Rent',opt.freeRent]].map(([k,v])=>(
                            <div key={k} style={{display:'flex',flexDirection:'column'}}>
                              <span style={{fontSize:9,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em'}}>{k}</span>
                              <span style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={{display:'flex',gap:10,flexShrink:0}}>
                        <ScoreRing score={opt.fitScore} size={62} color={fitColor} label="Fit" delay={idx*120}/>
                        <ScoreRing score={opt.conformanceScore} size={62} color='rgba(61,31,13,.4)' label="Conf" delay={idx*120+80}/>
                      </div>
                    </div>
                    <div style={{marginBottom:10}}>
                      <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:7}}>Setting Alignment</div>
                      <div style={{display:'grid',gap:5}}>
                        {MK_AXES.map(({key,short,label})=>{
                          const val=(opt.rbw||{})[key]||0;
                          const tgt=profile[key]||0;
                          const bc=val>=tgt?'#16a34a':val>=tgt-10?'#E8813A':'#ef4444';
                          return(
                            <div key={key} style={{display:'flex',alignItems:'center',gap:8}}>
                              <span title={label} style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',width:20,flexShrink:0,textAlign:'right'}}>{short}</span>
                              <div style={{flex:1,height:6,background:'rgba(61,31,13,.07)',borderRadius:3,overflow:'hidden',position:'relative'}}>
                                <div style={{height:'100%',width:`${val}%`,background:bc,borderRadius:3}}/>
                                <div style={{position:'absolute',top:0,left:`${tgt}%`,width:2,height:'100%',background:'#E8A838',transform:'translateX(-1px)'}}/>
                              </div>
                              <span style={{fontSize:10,fontWeight:700,color:bc,width:26,flexShrink:0,textAlign:'right'}}>{val}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:5,marginTop:6,fontSize:9,color:'rgba(61,31,13,.35)'}}>
                        <div style={{width:12,height:3,background:'#E8A838',borderRadius:1}}/><span>Amber tick = archetype target ({deal.stratBrief?.archetype||'default'})</span>
                      </div>
                    </div>
                    {opt.highlight&&<div style={{fontSize:11,color:'rgba(61,31,13,.6)',lineHeight:1.5,padding:'7px 10px',background:'rgba(245,237,224,.6)',borderRadius:7,marginBottom:opt.flag?8:0}}>{opt.highlight}</div>}
                    {opt.flag&&(
                      <div style={{fontSize:11,color:'#92400E',lineHeight:1.5,padding:'7px 10px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:7,display:'flex',gap:7,alignItems:'flex-start'}}>
                        <span style={{fontSize:13,flexShrink:0}}>⚠</span><span>{opt.flag}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {(deal.options||[]).length===0&&(
                <div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:12}}>No floor plans submitted yet. Options appear here once plans are uploaded and scored by Sense.</div>
              )}
            </div>
          )}

          {/* ── DEVELOP TAB ────────────────────────────────────── */}
          {tab==='develop'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 13px',background:'rgba(28,63,110,.05)',border:'1px solid rgba(28,63,110,.18)',borderRadius:10}}>
                <span style={{fontSize:9,fontWeight:700,color:'#1C3F6E',background:'rgba(28,63,110,.1)',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.07em'}}>Develop · Experience Delivery Tracker</span>
                <span style={{fontSize:11,color:'rgba(61,31,13,.55)'}}>This is not construction PM — Stage tracks only experience-relevant milestones</span>
              </div>
              {/* Experience commitment reminder in Develop */}
              {deal.experienceCommitment&&(
                <div style={{padding:'8px 12px',background:'rgba(232,168,56,.05)',border:'1px solid rgba(232,168,56,.18)',borderLeft:'3px solid #E8A838',borderRadius:9,fontSize:11,color:'rgba(61,31,13,.6)',lineHeight:1.5}}>
                  <span style={{fontWeight:700,color:'#E8A838'}}>Experience Commitment · </span>{deal.experienceCommitment}
                </div>
              )}
              {/* Milestone tracker */}
              {(()=>{
                const cp=CAPITAL_PROJECTS_V2.find(p=>p.market===deal.market||p.location===deal.property);
                if(!cp&&!deal.milestones) return(
                  <div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:12}}>
                    No active capital project linked. A Develop-phase project tracking record is created when a lease is executed.
                    {deal.wexPhase==='develop'&&<div style={{marginTop:8,fontSize:11,color:'#1C3F6E',fontWeight:600}}>This Setting is in Develop — link a capital project in Financial Surface to activate milestone tracking.</div>}
                  </div>
                );
                const proj=cp||{milestones:[],changeOrders:[],dayOneReadiness:[]};
                return(<>
                  <div className="card" style={{padding:16}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Delivery Milestones</div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {(proj.milestones||[]).map((m,i)=>{
                        const statusColor=m.status==='Done'?'#16a34a':m.status==='In Progress'?'#E8813A':'rgba(61,31,13,.25)';
                        const statusBg=m.status==='Done'?'#f0fdf4':m.status==='In Progress'?'rgba(232,129,58,.06)':'rgba(61,31,13,.03)';
                        return(
                          <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:statusBg,borderRadius:8,border:`1px solid ${statusColor}22`}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:statusColor,flexShrink:0}}/>
                            <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D',flex:1}}>{m.name}</span>
                            <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>{m.due}</span>
                            <span style={{fontSize:9,fontWeight:700,color:statusColor,background:statusColor+'18',padding:'1px 7px',borderRadius:4}}>{m.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {(proj.changeOrders||[]).length>0&&(
                    <div className="card" style={{padding:16}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em'}}>Change Orders</div>
                        {proj.changeOrders.some(co=>co.experienceImpact&&co.status==='pending')&&(
                          <span style={{fontSize:9,fontWeight:700,color:'#C1714A',background:'rgba(193,113,74,.1)',border:'1px solid rgba(193,113,74,.25)',padding:'2px 7px',borderRadius:4}}>⚠ Experience Impact Flagged</span>
                        )}
                      </div>
                      {proj.changeOrders.map((co,i)=>{
                        const isPending=co.status==='pending';
                        const hasExpImpact=co.experienceImpact&&isPending;
                        const borderColor=hasExpImpact?'rgba(193,113,74,.4)':isPending?'rgba(232,129,58,.25)':'rgba(61,31,13,.08)';
                        const bgColor=hasExpImpact?'rgba(193,113,74,.04)':isPending?'rgba(232,129,58,.03)':'rgba(245,237,224,.4)';
                        return(
                          <div key={i} style={{padding:'9px 11px',borderRadius:8,border:`1px solid ${borderColor}`,background:bgColor,marginBottom:6}}>
                            <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10}}>
                              <div style={{flex:1}}>
                                <span style={{fontSize:12,color:'#3D1F0D',lineHeight:1.4}}>{co.desc}</span>
                                {hasExpImpact&&co.affectedZones?.length>0&&(
                                  <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:5}}>
                                    {co.affectedZones.map((z,zi)=>(
                                      <span key={zi} style={{fontSize:9,fontWeight:700,color:'#C1714A',background:'rgba(193,113,74,.1)',border:'1px solid rgba(193,113,74,.2)',padding:'1px 6px',borderRadius:4}}>{z}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div style={{display:'flex',gap:5,flexShrink:0,alignItems:'center'}}>
                                <span style={{fontSize:11,fontWeight:700,color:'#3D1F0D'}}>${(co.amount/1000).toFixed(0)}K</span>
                                <span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:4,background:isPending?'rgba(232,129,58,.12)':'rgba(22,163,74,.1)',color:isPending?'#E8813A':'#16a34a'}}>{co.status==='approved'?'Approved':'Pending'}</span>
                              </div>
                            </div>
                            {hasExpImpact&&co.experienceImpactDesc&&(
                              <div style={{marginTop:7,fontSize:11,color:'#7C2D12',background:'rgba(193,113,74,.06)',border:'1px solid rgba(193,113,74,.25)',borderLeft:'3px solid #C1714A',borderRadius:6,padding:'6px 10px',lineHeight:1.5}}>
                                <span style={{fontWeight:700}}>Experience Impact · </span>{co.experienceImpactDesc}
                              </div>
                            )}
                            {hasExpImpact&&!co.experienceImpactDesc&&(
                              <div style={{marginTop:6,fontSize:11,color:'#92400E',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:'5px 9px'}}>⚠ Experience review required before authorization — Sense checking against Design Hypothesis</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(proj.dayOneReadiness||[]).length>0&&(()=>{
                    const opItems=proj.dayOneReadiness.filter(it=>it.type!=='experience');
                    const expItems=proj.dayOneReadiness.filter(it=>it.type==='experience');
                    const total=proj.dayOneReadiness.length;
                    const done=proj.dayOneReadiness.filter(i=>i.done).length;
                    const renderItem=(item,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                        <span style={{fontSize:13,color:item.done?'#16a34a':'rgba(61,31,13,.25)',flexShrink:0}}>{item.done?'✓':'○'}</span>
                        <span style={{fontSize:12,color:item.done?'rgba(61,31,13,.5)':'#3D1F0D',flex:1}}>{item.item}</span>
                        <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>{item.due}</span>
                      </div>
                    );
                    return(
                      <div className="card" style={{padding:16}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em'}}>Day-One Readiness</div>
                          <span style={{fontSize:11,fontWeight:800,color:'#1C3F6E'}}>{done}/{total}</span>
                        </div>
                        {opItems.length>0&&(
                          <>
                            <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Operational</div>
                            {opItems.map(renderItem)}
                          </>
                        )}
                        {expItems.length>0&&(
                          <div style={{marginTop:opItems.length?10:0}}>
                            <div style={{fontSize:9,fontWeight:700,color:'#C1714A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4,display:'flex',alignItems:'center',gap:5}}>
                              <span>Experience</span>
                              <span style={{fontSize:8,background:'rgba(193,113,74,.1)',border:'1px solid rgba(193,113,74,.25)',padding:'1px 5px',borderRadius:3,fontWeight:700}}>WEX-1</span>
                            </div>
                            {expItems.map(renderItem)}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>);
              })()}
            </div>
          )}

          {/* ── DELIVERY TAB ────────────────────────────────────── */}
          {tab==='delivery'&&(
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 13px',background:'rgba(193,113,74,.05)',border:'1px solid rgba(193,113,74,.2)',borderRadius:10}}>
                <span style={{fontSize:9,fontWeight:700,color:'#C1714A',background:'rgba(193,113,74,.12)',padding:'2px 8px',borderRadius:4,textTransform:'uppercase',letterSpacing:'.07em'}}>Delivery · Feedback Loop</span>
                <span style={{fontSize:11,color:'rgba(61,31,13,.55)'}}>Occupancy confirmation initiates post-occupancy monitoring and closes the feedback loop</span>
              </div>
              {deal.outcomeRecord?(
                <>
                  {/* Outcome record — populated for closed/delivery settings */}
                  <div className="card" style={{padding:16,borderLeft:'4px solid #C1714A'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Outcome Record</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                      {[['Occupancy Date',deal.outcomeRecord.occupancyDate],['Capacity Leakage',`${deal.outcomeRecord.capacityLeakageInitial}%`],['Design Hypothesis',deal.outcomeRecord.designHypothesisRef.slice(0,40)+'…'],['Monitoring Status','Active — Sense RBW-I']].map(([k,v])=>(
                        <div key={k} style={{background:'rgba(245,237,224,.6)',borderRadius:8,padding:'9px 11px'}}>
                          <div style={{fontSize:9,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:3}}>{k}</div>
                          <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {/* Initial Sense readings */}
                    <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:7}}>Initial Sense Readings — Day 1</div>
                    <div style={{display:'grid',gap:4}}>
                      {Object.entries(deal.outcomeRecord.initialSenseReadings).map(([k,v])=>{
                        const target=k==='CE'||k==='GE'?75:k==='GP'?78:65;
                        const color=v>=target?'#16a34a':v>=target-10?'#E8813A':'#ef4444';
                        return(
                          <div key={k} style={{display:'flex',alignItems:'center',gap:8}}>
                            <span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',width:22,textAlign:'right',flexShrink:0}}>{k}</span>
                            <div style={{flex:1,height:5,background:'rgba(61,31,13,.07)',borderRadius:3}}>
                              <div style={{height:'100%',width:`${v}%`,background:color,borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:10,fontWeight:700,color,width:26,textAlign:'right'}}>{v}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {/* Nudge schedule */}
                  <div className="card" style={{padding:16}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Post-Occupancy Nudge Schedule</div>
                    {(deal.outcomeRecord.nudgeSchedule||[]).map((n,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'8px 0',borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                        <div style={{width:32,height:32,borderRadius:'50%',background:n.status==='fired'?'rgba(193,113,74,.1)':'rgba(61,31,13,.05)',border:`1px solid ${n.status==='fired'?'#C1714A':'rgba(61,31,13,.1)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:800,color:n.status==='fired'?'#C1714A':'rgba(61,31,13,.3)',flexShrink:0}}>{n.day}d</div>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                            <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{n.day}-Day Post-Occupancy Nudge</span>
                            <span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:4,background:n.status==='fired'?'rgba(22,163,74,.1)':'rgba(61,31,13,.05)',color:n.status==='fired'?'#16a34a':'rgba(61,31,13,.35)'}}>{n.status==='fired'?`Sent ${n.firedAt}`:'Pending'}</span>
                          </div>
                          {n.summary&&<div style={{fontSize:11,color:'rgba(61,31,13,.6)',lineHeight:1.5,marginBottom:n.benchmarkContext?6:0}}>{n.summary}</div>}
                          {n.benchmarkContext&&(
                            <div style={{padding:'8px 11px',background:'rgba(74,127,165,.05)',border:'1px solid rgba(74,127,165,.2)',borderLeft:'3px solid #4A7FA5',borderRadius:8,fontSize:10,color:'rgba(61,31,13,.65)',lineHeight:1.55}}>
                              <div style={{fontSize:9,fontWeight:700,color:'#4A7FA5',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>RBW Benchmark · 90-Day Cohort</div>
                              {n.benchmarkContext}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* Feedback loop indicator */}
                    <div style={{marginTop:12,padding:'9px 12px',background:'rgba(74,127,165,.05)',border:'1px solid rgba(74,127,165,.15)',borderRadius:8,fontSize:11,color:'rgba(61,31,13,.6)',lineHeight:1.5}}>
                      <span style={{fontWeight:700,color:'#4A7FA5'}}>→ Feedback Loop Active · </span>
                      90-day nudge will compare live Sense readings against the Design Hypothesis and benchmark comparable Community-First Settings in the RBW library.
                    </div>
                  </div>
                </>
              ):(
                <div className="card" style={{padding:24}}>
                  <div style={{fontSize:12,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:8}}>Outcome Record — Pending</div>
                  <div style={{fontSize:12,color:'rgba(61,31,13,.4)',lineHeight:1.6,marginBottom:16}}>
                    The Outcome Record is written when the Setting confirms occupancy in Delivery. It captures initial Sense readings, Design Hypothesis baseline, and schedules the 30 / 90 / 180 / 365-day post-occupancy nudge cadence.
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {[['Day-one readiness confirmation',deal.wexPhase==='delivery'?'Required':'Blocked — complete Develop first'],['Occupancy launch record','Pending'],['Sense monitoring baseline','Pending — set at occupancy confirmation'],['30-day nudge','Scheduled automatically at confirmation']].map(([k,v])=>(
                      <div key={k} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',background:'rgba(61,31,13,.03)',borderRadius:8}}>
                        <span style={{fontSize:11,color:'rgba(61,31,13,.5)',flex:1}}>{k}</span>
                        <span style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.35)'}}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab==='documents'&&(
            <div className="card" style={{padding:18}}>
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'7px 10px',background:'rgba(61,31,13,.03)',borderRadius:8,border:'1px solid rgba(61,31,13,.07)',marginBottom:14,fontSize:10,color:'rgba(61,31,13,.45)'}}>
                <span style={{fontSize:12}}>⛓</span>
                <span>Documents are <b>linked, not hosted</b> — Stage holds the version record and review history. Document custody remains with the occupier in their existing storage.</span>
              </div>
              {(deal.documents||[]).map(doc=>{
                const srcIcon={'Drive':'📁','SharePoint':'🔷','Dropbox':'📦','Box':'📂'}[doc.source]||'📄';
                const srcColor={'Drive':'#1e88e5','SharePoint':'#0078d4','Dropbox':'#0061ff','Box':'#0061d5'}[doc.source]||'rgba(61,31,13,.4)';
                return(
                <div key={doc.id||doc.name} style={{borderBottom:'1px solid rgba(61,31,13,.05)',paddingBottom:10,marginBottom:10}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:34,height:34,background:'rgba(61,31,13,.05)',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',fontSize:15,flexShrink:0}}>{srcIcon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.name}</div>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginTop:2}}>
                        {doc.version&&<span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>v{doc.version}</span>}
                        <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>·</span>
                        <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>{doc.date}</span>
                        {doc.source&&<><span style={{fontSize:10,color:'rgba(61,31,13,.25)'}}>·</span><span style={{fontSize:10,fontWeight:600,color:srcColor}}>{doc.source}</span></>}
                      </div>
                    </div>
                    <div style={{display:'flex',gap:6,flexShrink:0}}>
                      {doc.delta&&<span style={{fontSize:9,fontWeight:700,background:'#fef3c7',color:'#92400E',padding:'2px 7px',borderRadius:999}}>Changes</span>}
                      {doc.scribeAbstract&&<span style={{fontSize:9,fontWeight:700,background:'#fef3c7',color:'#D97706',padding:'2px 7px',borderRadius:999}}>⟳ Gate</span>}
                      {doc.url&&<a href={doc.url} target="_blank" rel="noreferrer" style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.4)',textDecoration:'none',padding:'2px 8px',border:'1px solid rgba(61,31,13,.15)',borderRadius:6,display:'flex',alignItems:'center',gap:3}} onMouseOver={e=>{e.currentTarget.style.color='#3D1F0D';e.currentTarget.style.borderColor='rgba(61,31,13,.3)'}} onMouseOut={e=>{e.currentTarget.style.color='rgba(61,31,13,.4)';e.currentTarget.style.borderColor='rgba(61,31,13,.15)'}}>↗ Open</a>}
                    </div>
                  </div>
                  {doc.delta&&(
                    <div style={{marginTop:6,marginLeft:44,fontSize:11,color:'rgba(61,31,13,.55)',background:'#fffbeb',padding:'6px 10px',borderRadius:6,border:'1px solid #fde68a',lineHeight:1.5}}>{doc.delta}</div>
                  )}
                </div>
              );})}
              <LinkDocumentWidget dealId={deal.id} onLinked={refreshDeals}/>
            </div>
          )}
        </div>
        );})()}
      </div>

      {/* §3.2 — on-demand full-set switcher (⊞ / ⌘O / Esc) */}
      {overviewOpen&&(
        <SpineOverview deals={deals} pinned={focus.pinned}
          onSelect={id=>{focusSetting(id);setOverviewOpen(false);}}
          onPin={togglePin} onClose={()=>setOverviewOpen(false)}/>
      )}
      {/* §3.5 — escalation by exception (⌘⇧T / Act count) */}
      {triageOpen&&(
        <TriageView deals={deals}
          onFocus={focusSetting} onAsk={onSageAsk}
          onClose={()=>setTriageOpen(false)} onAcked={()=>setAckBump(b=>b+1)}/>
      )}

      {/* New Setting modal */}
      {showDealModal&&(
        <FormModal title="New Setting" onClose={()=>setShowDealModal(false)} onSubmit={handleCreateDeal} submitting={dealSaving}>
          {dealError&&<div style={{padding:'8px 12px',background:'rgba(239,68,68,.07)',border:'1px solid rgba(239,68,68,.2)',borderRadius:7,fontSize:12,color:'#dc2626'}}>{dealError}</div>}
          {/* Setting type selector — Standard (transaction-anchored) vs Strategic (non-transactional) */}
          <Field label="Setting Type" required>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {[['Standard','Transaction-anchored · lease renewal, expansion, exit'],['Strategic','Non-transactional · friction investigation, hybrid policy, M&A']].map(([type,desc])=>(
                <button key={type} type="button" onClick={()=>setDealForm(f=>({...f,settingType:type,stage:type==='Strategic'?'Persistent':(f.stage==='Persistent'?'Scoping':f.stage)}))} style={{padding:'10px 12px',border:`1px solid ${(dealForm.settingType||'Standard')===type?'#E8813A':'rgba(61,31,13,.15)'}`,borderRadius:9,background:(dealForm.settingType||'Standard')===type?'rgba(232,129,58,.06)':'transparent',cursor:'pointer',fontFamily:'inherit',textAlign:'left',transition:'all .15s'}}>
                  <div style={{fontSize:12,fontWeight:700,color:(dealForm.settingType||'Standard')===type?'#E8813A':'rgba(61,31,13,.7)',marginBottom:3}}>{type}</div>
                  <div style={{fontSize:10,color:'rgba(61,31,13,.45)',lineHeight:1.4}}>{desc}</div>
                </button>
              ))}
            </div>
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Setting Name" required>
              <input style={inputStyle} placeholder="e.g. Austin Expansion" value={dealForm.name} onChange={setDF('name')}/>
            </Field>
            <Field label="Property / Building" required>
              <input style={inputStyle} placeholder={dealForm.settingType==='Strategic'?'e.g. All Locations or Portfolio-wide':'e.g. 300 W 6th St'} value={dealForm.property} onChange={setDF('property')}/>
            </Field>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Market" required>
              <input style={inputStyle} placeholder="e.g. Austin" value={dealForm.market} onChange={setDF('market')}/>
            </Field>
            {(dealForm.settingType||'Standard')==='Standard'&&(
              <Field label="Target SF" required>
                <input style={inputStyle} type="number" placeholder="e.g. 25000" value={dealForm.sf} onChange={setDF('sf')}/>
              </Field>
            )}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Lifecycle Stage" required>
              <select style={selectStyle} value={dealForm.stage} onChange={setDF('stage')}>
                {((dealForm.settingType||'Standard')==='Strategic'?['Persistent']:['Scoping','Active','Closing','Closed','Renewal Pending']).map(s=><option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Velocity (1–10)">
              <input style={inputStyle} type="number" min="1" max="10" value={dealForm.velocity} onChange={setDF('velocity')}/>
            </Field>
          </div>
          <Field label="Next Milestone" required>
            <input style={inputStyle} placeholder="e.g. Tour shortlisted properties" value={dealForm.next_milestone} onChange={setDF('next_milestone')}/>
          </Field>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <Field label="Next Deadline">
              <input style={inputStyle} type="date" value={dealForm.next_deadline} onChange={setDF('next_deadline')}/>
            </Field>
            <Field label="Advisor">
              <input style={inputStyle} placeholder="e.g. Jordan Wells" value={dealForm.advisor} onChange={setDF('advisor')}/>
            </Field>
          </div>
        </FormModal>
      )}

      {/* Advisor / External Participant Invitation Modal — PRD §5: broker invitation, zero friction, magic link */}
      {showCPInvite&&(
        <div style={{position:'fixed',inset:0,background:'rgba(61,31,13,.4)',zIndex:900,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:16,width:460,maxWidth:'90vw',boxShadow:'0 20px 60px rgba(61,31,13,.18)',overflow:'hidden'}}>
            <div style={{padding:'18px 20px 14px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:28,height:28,borderRadius:8,background:'rgba(5,150,105,.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>⟷</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Invite External Participant</div>
                <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:1}}>Setting-scoped access · No portfolio data · Revocable at any time</div>
              </div>
              <button onClick={()=>setShowCPInvite(false)} style={{border:'none',background:'none',fontSize:20,color:'rgba(61,31,13,.3)',cursor:'pointer',padding:4,lineHeight:1}}>×</button>
            </div>
            {cpDone?(
              <div style={{padding:'28px 24px'}}>
                <div style={{textAlign:'center',marginBottom:20}}>
                  <div style={{fontSize:28,marginBottom:8}}>✓</div>
                  <div style={{fontSize:14,fontWeight:700,color:'#16a34a',marginBottom:4}}>{cpForm.channel==='link'?'Magic link generated':'Invitation sent'}</div>
                  <div style={{fontSize:12,color:'rgba(61,31,13,.5)'}}>{cpForm.name?`${cpForm.name} — `:''}scoped to this Setting only.</div>
                </div>
                {cpForm.channel==='link'&&(
                  <div style={{marginBottom:16}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.04em'}}>Magic Link</div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <input readOnly value={magicLink} style={{flex:1,padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:11,fontFamily:'monospace',color:'#3D1F0D',background:'#F5EDE0',outline:'none'}}/>
                      <button onClick={()=>{navigator.clipboard?.writeText(magicLink).catch(()=>{});setLinkCopied(true);setTimeout(()=>setLinkCopied(false),2000);}} style={{padding:'8px 14px',border:'none',borderRadius:8,background:'#059669',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>{linkCopied?'Copied!':'Copy'}</button>
                    </div>
                    <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:5}}>Single-use · Expires at Setting close + 30 days · No account required</div>
                  </div>
                )}
                <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                  <button onClick={()=>{setCPDone(false);setCPForm({name:'',email:'',org:'',role:'Advisor',channel:'email'});}} style={{padding:'8px 18px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,background:'transparent',fontSize:12,fontWeight:600,color:'rgba(61,31,13,.5)',cursor:'pointer',fontFamily:'inherit'}}>Invite another</button>
                  <button onClick={()=>setShowCPInvite(false)} style={{padding:'8px 18px',border:'none',borderRadius:8,background:'#E8813A',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Done</button>
                </div>
              </div>
            ):(
              <div style={{padding:20}}>
                {/* Delivery channel selector */}
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:8,textTransform:'uppercase',letterSpacing:'.04em'}}>Delivery channel</div>
                  <div style={{display:'flex',gap:8}}>
                    {[['email','✉ Email'],['slack','Slack'],['link','⛓ Copy link']].map(([ch,label])=>(
                      <button key={ch} onClick={()=>setCPForm(f=>({...f,channel:ch}))} style={{flex:1,padding:'8px',border:`1px solid ${cpForm.channel===ch?'#059669':'rgba(61,31,13,.15)'}`,borderRadius:8,background:cpForm.channel===ch?'rgba(5,150,105,.06)':'transparent',fontSize:11,fontWeight:cpForm.channel===ch?700:500,color:cpForm.channel===ch?'#059669':'rgba(61,31,13,.5)',cursor:'pointer',fontFamily:'inherit',transition:'all .15s'}}>{label}</button>
                    ))}
                  </div>
                </div>
                {cpForm.channel!=='link'&&(
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                    {[['Name','name','text','e.g. Jordan Chen'],['Email','email','email','jordan@brokerage.com']].map(([label,key,type,ph])=>(
                      <div key={key}>
                        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>{label}</div>
                        <input type={type} value={cpForm[key]} onChange={setCP(key)} placeholder={ph} style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{display:'grid',gridTemplateColumns:cpForm.channel==='link'?'1fr':'1fr 1fr',gap:10,marginBottom:16}}>
                  {cpForm.channel!=='link'&&(
                    <div>
                      <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>Organisation</div>
                      <input value={cpForm.org} onChange={setCP('org')} placeholder="e.g. CBRE" style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}/>
                    </div>
                  )}
                  <div style={{gridColumn:cpForm.channel==='link'?'1/-1':'auto'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',marginBottom:4,textTransform:'uppercase',letterSpacing:'.04em'}}>Role</div>
                    <select value={cpForm.role} onChange={setCP('role')} style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',outline:'none',background:'#fff',boxSizing:'border-box'}}>
                      {[['Advisor','Tenant Advisor — full Setting read access, can submit deliverables'],['Reviewer','Reviewer — can comment and approve deliverables, cannot see financials'],['Specialist','Specialist — scoped to specific deliverable thread only']].map(([r,d])=><option key={r} value={r} title={d}>{r}</option>)}
                    </select>
                    <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:4,lineHeight:1.4}}>{{Advisor:'Full Setting read · can submit deliverables · no portfolio data',Reviewer:'Comment + approve deliverables · no financials',Specialist:'Scoped to a specific deliverable thread'}[cpForm.role]}</div>
                  </div>
                </div>
                <div style={{background:'rgba(5,150,105,.04)',border:'1px solid rgba(5,150,105,.15)',borderRadius:8,padding:'9px 12px',marginBottom:16,fontSize:11,color:'rgba(61,31,13,.55)',lineHeight:1.5}}>
                  ⛨ Access is scoped to <b>{deal?.name||'this Setting'}</b> only. No portfolio data, financials, or other Settings will be visible. Access expires at Setting close + 30 days and can be revoked at any time.
                </div>
                <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                  <button onClick={()=>setShowCPInvite(false)} style={{padding:'9px 18px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,background:'transparent',fontSize:12,fontWeight:600,color:'rgba(61,31,13,.55)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                  <button disabled={cpSaving||(cpForm.channel!=='link'&&(!cpForm.name.trim()||!cpForm.email.trim()))} onClick={async()=>{
                    setCPSaving(true);
                    try{
                      const r=await authFetch('/api/invite',{method:'POST',body:{
                        deal_slug:deal?.id,
                        name:cpForm.name.trim()||undefined,
                        email:cpForm.email.trim()||undefined,
                        role:cpForm.role,
                        channel:cpForm.channel,
                      }});
                      if(r&&r.url){
                        setMagicLink(r.url);
                        setCPDone(true);
                      }
                    }catch(e){
                      // Fallback: surface error but still show a link field
                      setMagicLink('Error generating link — please try again.');
                      setCPDone(true);
                    }finally{setCPSaving(false);}
                  }} style={{padding:'9px 18px',border:'none',borderRadius:8,background:cpSaving||(cpForm.channel!=='link'&&(!cpForm.name||!cpForm.email))?'rgba(5,150,105,.4)':'#059669',color:'#fff',fontSize:12,fontWeight:700,cursor:cpSaving?'not-allowed':'pointer',fontFamily:'inherit'}}>
                    {cpSaving?'Generating…':cpForm.channel==='link'?'Generate Link':'Send Invite'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const UTIL_WEEKS=['Feb W1','Feb W2','Feb W3','Feb W4','Mar W1','Mar W2','Mar W3','Mar W4','Apr W1','Apr W2','Apr W3','Apr W4'];
const UTIL_DATA=UTIL_WEEKS.map((w,i)=>({week:w.replace(' ','·'),'New York':62+Math.sin(i*.7)*4,'Chicago':60+Math.cos(i*.5)*4,'San Francisco':47+Math.sin(i*.4)*2,'Austin':82+Math.sin(i*.3)*4,'Houston':38+Math.cos(i*.6)*2}));
const MKT_COLORS={'New York':'#8b0020','Chicago':'#E8813A','San Francisco':'#2563EB','Austin':'#059669','Houston':'#7C3AED'};

const TWS_ASSETS=[
  {id:'nyc1',name:'1 Penn Plaza',market:'New York',twsScore:54,flow:61,culture:44,perf:57,trend:-8,util:62,status:'red',conf:82},
  {id:'chi1',name:'Willis Tower',market:'Chicago',twsScore:71,flow:74,culture:66,perf:73,trend:+2,util:60,status:'yellow',conf:88},
  {id:'nyc2',name:'30 Hudson Yards',market:'New York',twsScore:82,flow:86,culture:79,perf:81,trend:+4,util:74,status:'green',conf:91},
  {id:'sf1',name:'444 Market St',market:'San Francisco',twsScore:61,flow:65,culture:55,perf:63,trend:-3,util:47,status:'yellow',conf:79},
  {id:'aus1',name:'2845 Domain Dr',market:'Austin',twsScore:79,flow:81,culture:77,perf:79,trend:+6,util:82,status:'green',conf:93},
  {id:'nyc3',name:'200 Park Ave',market:'New York',twsScore:43,flow:48,culture:36,perf:45,trend:-12,util:51,status:'red',conf:71},
  {id:'hou1',name:'1000 Main St',market:'Houston',twsScore:58,flow:62,culture:52,perf:60,trend:-2,util:38,status:'red',conf:76},
  {id:'chi2',name:'350 N Orleans',market:'Chicago',twsScore:77,flow:78,culture:76,perf:77,trend:+1,util:69,status:'green',conf:87},
];

const TWS_STANDARDS=[
  {id:'s1',name:'Co-presence commitment',pillar:'Culture',standard:75,actual:40,tier:'Corporate'},
  {id:'s2',name:'Focus space-to-intent fit',pillar:'Flow',standard:85,actual:71,tier:'Corporate'},
  {id:'s3',name:'Programme adoption',pillar:'Performance',standard:60,actual:44,tier:'Asset'},
  {id:'s4',name:'Belonging signal',pillar:'Culture',standard:70,actual:52,tier:'Corporate'},
  {id:'s5',name:'Environment satisfaction',pillar:'Flow',standard:75,actual:68,tier:'Asset'},
  {id:'s6',name:'Space utilisation quality',pillar:'Performance',standard:80,actual:67,tier:'Asset'},
];

const TWS_WEEKS=['Feb·W1','Feb·W2','Feb·W3','Feb·W4','Mar·W1','Mar·W2','Mar·W3','Mar·W4','Apr·W1','Apr·W2','Apr·W3','Apr·W4'];
const TWS_TREND=TWS_WEEKS.map((w,i)=>({week:w,'Flow':74-Math.sin(i*.3)*3+(i*.08),'Culture':65-i*.6+Math.sin(i*.5)*2,'Performance':66-Math.cos(i*.4)*2+(i*.04)}));

const SAGE_ALERTS=[
  {id:'sa1',sev:'high',title:'Culture decline at 1 Penn Plaza intersects lease decision',obs:'Culture score has fallen from 71 to 44 over 6 weeks, driven by co-presence fulfilment at 38% against a 75% corporate standard. Belonging signal at its lowest in 12 months.',action:'Review the renewal calculus before committing to lease strategy. Renewal window: Sep 2026.',conf:87,domain:'cross-domain'},
  {id:'sa2',sev:'medium',title:'Co-presence standard unmet — 8 consecutive weeks',obs:'Technology division co-presence gap has widened from 28% to 51%. Pattern suggests the standard may be miscalibrated, not the behaviour.',action:'Before flagging as underperformance, determine whether the standard reflects current hybrid norms.',conf:92,domain:'experience'},
  {id:'sa3',sev:'medium',title:'Belonging signal declining — pulse confidence falling',obs:'Portfolio-wide belonging signal down 18 points in 4 weeks. Post-visit pulse response rate dropped to 31%, reducing data confidence below the 50% threshold.',action:'Expand pulse question rotation and check for correlation with booking cancellation spike.',conf:64,domain:'experience'},
];

const SAGE_INSIGHTS=[
  {id:'si1',type:'cross-domain',title:'Renewal decision compounded by experience decline',body:"Building C's lease renewal is in 7 months. Its TWS score has declined from 71 to 54 over 6 weeks, driven primarily by a falling Culture score. The belonging signal on Floors 9 and 10 is at its lowest point in 12 months. This changes the renewal calculus — an asset scoring TWS 54 and declining warrants a different negotiating posture than one holding at 71.",sources:['Portfolio Spine','Experience Intelligence','Transaction Pipeline'],conf:87},
  {id:'si2',type:'rightsizing',title:'Floor 14 — heavily used, poorly fitted',body:'Floor 14 is at 82% average occupancy but has a TWS score of 43, driven by a space-to-intent fit score of 31. The floor is being used heavily for work types it was not designed to support. A typology reconfiguration may improve TWS before a physical expansion is justified.',sources:['Utilization Data','Space-to-Intent Signal','Shop Booking Log'],conf:81},
  {id:'si3',type:'signal coherence',title:'Co-presence score vs. booking patterns — discrepancy',body:'Status shows a moderately positive co-presence score for the Austin campus, but Shop booking data shows teams booking on different floors rather than clustering. Reporting a composite without surfacing this tension would overstate the signal. Sage is holding both truths.',sources:['Badge Data','Shop Booking Log'],conf:68},
  {id:'si4',type:'settings drift',title:'Settings drift explains the Culture decline at 1 Penn Plaza',body:"The Community Exchange setting on Floor 14 lost its coffee station during building works 6 weeks ago. Utilization dropped from 61% to 19% and dwell time fell to 3.1 minutes — pass-through only. No cross-departmental interaction is being generated. This physical change directly precedes and plausibly explains 60–70% of the Culture score decline Sage has been flagging. The setting drifted; the culture signal followed.",sources:['RBW-I Zone Performance','Shop Booking Log','Post-visit Pulse'],conf:84},
  {id:'si5',type:'lease strategy',title:'Design Fidelity at 200 Park Ave supports TI negotiation or exit',body:'200 Park Ave has a Settings Design Fidelity of 28% — the lowest in the portfolio — and a TWS score of 43. The settings are not delivering on their design intent. The lease has now expired. A space performing at 28% fidelity is not delivering on its occupancy cost. This evidence supports either a tenant improvement allowance negotiation (restore settings to spec) or an evidence-based exit decision.',sources:['RBW-I Settings Scorecard','Financial Surface','Portfolio Spine'],conf:89},
  {id:'si6',type:'coverage gap',title:'No functioning Reflect settings at two locations — elevated recovery risk',body:"Neither 1 Penn Plaza nor 200 Park Avenue has a functioning Individual Reflect setting. Both converted wellness areas serve as Individual Produce overflow — performance cues present, biophilic elements removed. Technology and Finance teams at both locations average 4+ hours of AI-assisted workflow per day. Current Wellbeing setting coverage across these assets: 3% of floor area against an 8–12% benchmark for this population type. The direct research link between insufficient cognitive recovery settings and sustained performance decline is well-established. This is not a comfort feature — it is a performance infrastructure gap.",sources:['RBW-I Settings Scorecard','Post-visit Pulse','Industry workplace research 2024'],conf:88},
  {id:'si7',type:'high-performing pattern',title:'30 Hudson Yards — replicable CE configuration outperforms portfolio benchmark',body:"The Floor 12 Community Exchange setting at 30 Hudson Yards achieves 67% cross-departmental interaction against a 45–60% benchmark, an 11.4-minute dwell median, and a 31% return-to-office draw — the highest in the portfolio. Settings Profile: kit-of-parts furniture, direct window access, a daily 9:15 AM cross-team coffee ritual, writable wall, no assigned ownership. Design Fidelity: 84%. Both 1 Penn Plaza and 444 Market Street have underperforming Community Exchange settings with identifiable settings drift as the root cause. The 30 Hudson configuration is available to replicate and has been validated across four weekly measurement cycles.",sources:['RBW-I Zone Performance','Shop Booking Log','Post-visit Pulse'],conf:91},
];

const RBW_SETTING_TYPES=[
  {id:'CE',name:'Community Exchange',level:'Community',mode:'Exchange',opp:'Connection',color:'#E8A838',desc:'Casual collisions, weak-tie formation, cultural belonging. Most uniquely suited to the office — least replicable at home.',key:'Central location on circulation path · social anchors · non-territorial · writable surfaces'},
  {id:'CR',name:'Community Reflect',level:'Community',mode:'Reflect',opp:'Wellbeing',color:'#7BAE8A',desc:'Open, accessible, restorative counterpoint to collaboration intensity. Meaningful daylight is non-negotiable.',key:'Soft furniture · strong daylight · biophilic elements · no performance cues · no screens'},
  {id:'GE',name:'Group Exchange',level:'Group',mode:'Exchange',opp:'Connection',color:'#4A7FA5',desc:'Team ideation, creative problem-solving, informal mentorship. Writable surfaces are the single most reliable physical predictor of Exchange behaviour.',key:'Writable walls · flexible furniture · team-scale enclosure · variable seating postures'},
  {id:'GP',name:'Group Produce',level:'Group',mode:'Produce',opp:'Connection + Change',color:'#1C3F6E',desc:'Structured meetings, hybrid equity, team accountability. Video equity is the critical modern variable — the office advantage is destroyed by poor AV.',key:'Screen orientation · AV equity · acoustic enclosure · ergonomic seating · room size variety'},
  {id:'IP',name:'Individual Produce',level:'Individual',mode:'Produce',opp:'Wellbeing',color:'#8C8C7A',desc:'37% of office vs. home preference variance. The office loses on every environmental metric here because employees have control at home. Settings must recover that control.',key:'Adequate space · visual privacy · allocated workpoint · acoustic control · personal lighting'},
  {id:'IR',name:'Individual Reflect',level:'Individual',mode:'Reflect',opp:'Wellbeing',color:'#A8C5A0',desc:'Most undervalued setting type. Genuine cognitive restoration — not focus, not meetings. Benchmark: 8–12% of floor area for knowledge-work populations.',key:'Daylight access · biophilic elements · soft enclosure · no performance cues · low ambient noise'},
];

// Per-location records carry their design archetype + the live setting-alignment
// experience profile (6 RBW axes, 0–100) and capacity leakage (lc %). The
// profile is consistent with designFidelity — low-fidelity locations sit well
// below their archetype target, with the gap concentrated on the friction axis
// (Connection→CE, Recovery→IR/CR, Density→IP over / IR under). These drive the
// per-property Setting Alignment radar inside the Settings Scorecard detail.
const RBW_LOCATIONS=[
  {id:'nyc1',name:'1 Penn Plaza',coverageScore:62,designFidelity:47,topFriction:'Connection',settings:{CE:12,CR:0,GE:18,GP:35,IP:32,IR:3},archetype:'Collaborative-Hybrid',lc:21.4,rbw:{communityExchange:58,communityReflect:40,groupExchange:70,groupProduce:78,individualProduce:72,individualReflect:38}},
  {id:'chi1',name:'Willis Tower',coverageScore:78,designFidelity:71,topFriction:'None',settings:{CE:15,CR:5,GE:20,GP:28,IP:27,IR:5},archetype:'Collaborative-Hybrid',lc:9.5,rbw:{communityExchange:74,communityReflect:60,groupExchange:82,groupProduce:84,individualProduce:64,individualReflect:52}},
  {id:'nyc2',name:'30 Hudson Yards',coverageScore:91,designFidelity:84,topFriction:'None',settings:{CE:18,CR:8,GE:22,GP:25,IP:22,IR:5},archetype:'Community-First',lc:8.1,rbw:{communityExchange:88,communityReflect:66,groupExchange:82,groupProduce:76,individualProduce:58,individualReflect:56}},
  {id:'sf1',name:'444 Market St',coverageScore:58,designFidelity:52,topFriction:'Connection',settings:{CE:8,CR:3,GE:15,GP:40,IP:30,IR:4},archetype:'Collaborative-Hybrid',lc:12.6,rbw:{communityExchange:60,communityReflect:48,groupExchange:72,groupProduce:80,individualProduce:70,individualReflect:44}},
  {id:'aus1',name:'2845 Domain Dr',coverageScore:85,designFidelity:79,topFriction:'None',settings:{CE:20,CR:7,GE:18,GP:22,IP:28,IR:5},archetype:'Community-First',lc:10.2,rbw:{communityExchange:84,communityReflect:62,groupExchange:78,groupProduce:74,individualProduce:57,individualReflect:53}},
  {id:'nyc3',name:'200 Park Ave',coverageScore:41,designFidelity:28,topFriction:'Density',settings:{CE:5,CR:0,GE:12,GP:45,IP:36,IR:2},archetype:'Community-First',lc:14.8,rbw:{communityExchange:52,communityReflect:30,groupExchange:58,groupProduce:88,individualProduce:90,individualReflect:28}},
  {id:'hou1',name:'1000 Main St',coverageScore:55,designFidelity:43,topFriction:'Recovery',settings:{CE:10,CR:2,GE:15,GP:38,IP:33,IR:2},archetype:'Collaborative-Hybrid',lc:16.0,rbw:{communityExchange:62,communityReflect:34,groupExchange:68,groupProduce:80,individualProduce:74,individualReflect:30}},
  {id:'chi2',name:'350 N Orleans',coverageScore:80,designFidelity:75,topFriction:'None',settings:{CE:16,CR:6,GE:20,GP:26,IP:27,IR:5},archetype:'Community-First',lc:8.8,rbw:{communityExchange:82,communityReflect:60,groupExchange:76,groupProduce:73,individualProduce:56,individualReflect:52}},
];

const RBW_ZONES=[
  {id:'z1',locId:'nyc1',name:'Main Social Hub',floor:'Floor 14',type:'CE',sqft:2400,capacity:80,utilTarget:'40–70%',utilActual:19,dwellTarget:'8–20 min',dwellActual:'3.1 min',crossDept:{target:'>45%',actual:'28%'},integrity:'DRIFT',driftNote:'Coffee station removed during building works 6 weeks ago. Setting placed off primary circulation by temporary hoarding.',frictionType:'Connection',frictionSignal:'"No real reason to stop here" — 4 sources',sentiment:5.8,rwoDraw:'4%',rwoBenchmark:'22%',status:'critical'},
  {id:'z2',locId:'nyc1',name:'Technology Team Workstations',floor:'Floor 14',type:'IP',sqft:4800,capacity:48,utilTarget:'65–85%',utilActual:71,dwellTarget:'240–480 min',dwellActual:'312 min',integrity:'DRIFT',driftNote:'Visual privacy screens specified in original Settings Profile have been removed. Direct sightlines to main circulation path.',frictionType:'Privacy',frictionSignal:'"Can\'t concentrate — too visible to the corridor" — 6 sources',sentiment:5.2,status:'watch'},
  {id:'z3',locId:'nyc1',name:'Client Presentation Suite',floor:'Floor 15',type:'GP',sqft:1200,capacity:12,utilTarget:'60–80%',utilActual:64,dwellTarget:'60–120 min',dwellActual:'78 min',noShow:{target:'<20%',actual:'31%'},integrity:'COMPLIANT',frictionType:'Technology',frictionSignal:'"Remote participants can\'t hear the room" — 3 sources',sentiment:6.4,status:'watch'},
  {id:'z4',locId:'nyc1',name:'Wellness Corner',floor:'Floor 14',type:'IR',sqft:380,capacity:6,utilTarget:'30–60%',utilActual:8,dwellTarget:'10–30 min',dwellActual:'4.2 min',integrity:'DRIFT',driftNote:'Converted to informal overflow desk space. No longer functioning as a Reflect setting — performance cues present, no biophilic elements remain.',frictionType:'Recovery',frictionSignal:'Functioning as Individual Produce overflow — Reflect intent lost',sentiment:4.9,status:'critical'},
  {id:'z5',locId:'nyc2',name:'Floor 12 Community Hub',floor:'Floor 12',type:'CE',sqft:3200,capacity:100,utilTarget:'40–70%',utilActual:68,dwellTarget:'8–20 min',dwellActual:'11.4 min',crossDept:{target:'>45%',actual:'67%'},integrity:'COMPLIANT',frictionType:'None',frictionSignal:null,sentiment:8.4,rwoDraw:'31%',rwoBenchmark:'22%',status:'healthy'},
  {id:'z6',locId:'nyc3',name:'Workstation Floor',floor:'Floor 8',type:'IP',sqft:6200,capacity:80,utilTarget:'65–85%',utilActual:89,dwellTarget:'240–480 min',dwellActual:'401 min',integrity:'DRIFT',driftNote:'Density above design capacity ceiling. No focus pods or visual privacy screening. Over-densification driving cascading friction.',frictionType:'Density',frictionSignal:'"No space to think — packed every day" — 8 sources',sentiment:4.3,status:'critical'},
];

const RBW_FRICTION=[
  {type:'Recovery',locations:3,signal:'Individual Reflect and Community Reflect settings absent or converted at 1 Penn Plaza, 200 Park Ave, and 1000 Main St. Zero functioning Reflect settings at two locations. Technology teams at affected locations average 4+ hours AI-assisted workflow/day — elevated cognitive recovery need.',action:'Settings Coverage review; restore or create Individual Reflect setting adjacent to highest-density tech teams.',severity:'P1'},
  {type:'Privacy',locations:3,signal:'"Can\'t concentrate — too visible" reported at 1 Penn Plaza (6 sources), 200 Park Ave (4), 444 Market St (3). Visual screening inadequate or removed in Individual Produce zones. Steelcase/UW-Madison: visual privacy has more cognitive impact than acoustic treatment at equivalent noise levels.',action:'Settings Integrity check on all IP zones. Was visual privacy in the original Settings Profile? If yes — drift. If no — implementation gap from day one.',severity:'P2'},
  {type:'Connection',locations:2,signal:'Community Exchange settings at 1 Penn Plaza and 444 Market St generating primarily within-team use. Cross-departmental interaction: 28% and 31% respectively (benchmark: 45–60%). Social anchors removed or displaced at both locations.',action:'Circulation analysis; restore social anchors; programming intervention — cross-team ritual.',severity:'P2'},
  {type:'Density',locations:2,signal:'Individual Produce settings at 200 Park Ave and 444 Market St at or above Design Hypothesis ceiling. Focus pod no-show rates rising — demand exceeds supply. Desk area, layout and density accounts for 37% of office preference variance (Enticing Workplace 2024).',action:'Settings Balance review; desk supply vs. demand analysis; model right-sizing options.',severity:'P2'},
  {type:'Technology',locations:1,signal:'"Remote participants can\'t hear the room" flagged 3× at 1 Penn Plaza Client Suite. No-show rate 31% (threshold: 20%) — avoidance pattern forming. The office\'s advantage in meetings is destroyed by poor AV equity.',action:'AV performance review; room-level technology audit. Room below hybrid equity standard.',severity:'P3'},
];

const RBW_STRAT_INSIGHTS=[
  {type:'Settings Drift Alert',location:'1 Penn Plaza — Floor 14',insight:"The Community Exchange setting (Main Social Hub) had its coffee station removed during building works 6 weeks ago and was placed off the primary circulation path by temporary hoarding. Utilization dropped from 61% to 19%. Dwell median: 3.1 minutes — pass-through only. Cross-departmental interaction: 28% (benchmark 45–60%). This settings drift directly precedes and plausibly explains the majority of the Culture score decline Sage has been flagging. The space changed; the experience followed.",action:'Restore social anchor (coffee station) and circulation access. Interim: introduce a cross-team programming ritual to generate dwell while restoration is planned. Reclassify if restoration is not feasible within 30 days.',decision:'Settings Restoration'},
  {type:'Coverage Gap Alert',location:'1 Penn Plaza + 200 Park Ave',insight:"Neither location has a functioning Individual Reflect setting. Both have converted wellness areas to phone booths (Individual Produce). Technology and Finance teams at both locations average 4+ hours of AI-assisted workflow per day — a population profile with elevated cognitive recovery needs. Current Wellbeing setting coverage: 3% of floor area (benchmark: 8–12% for this population type). The research is direct: insufficient recovery settings reduce sustained performance across the full workday.",action:'Restore at least one Individual Reflect setting per location. Prioritise proximity to highest-density technology teams. Even a small space (380 SF minimum) delivers measurable cognitive benefit.',decision:'Coverage Remediation'},
  {type:'High-Performing Pattern',location:'30 Hudson Yards — Floor 12',insight:"The Community Exchange setting on Floor 12 is the top-performing setting in the portfolio. Cross-departmental interaction: 67% (benchmark: 45–60%). Dwell median: 11.4 minutes. Return-to-office draw: 31% of daily attendees cite this setting as a reason for coming in. Settings Profile: kit-of-parts furniture, direct window access, daily cross-team coffee ritual at 9:15 AM, writable wall, no assigned ownership. Design Fidelity: 84%. This is the configuration that works.",action:'Replicate this Settings Profile at 1 Penn Plaza and 444 Market St — both have underperforming Community Exchange settings with identifiable drift as the root cause.',decision:'Design Pattern Replication'},
  {type:'Lease Strategy Input',location:'200 Park Ave',insight:"Design Fidelity at 200 Park Ave is 28% — the lowest in the portfolio. Coverage Score: 41%. TWS: 43 and declining. The lease has expired. A space at 28% design fidelity is not delivering on its occupancy cost — the settings were designed for one experience and are producing another. This evidence supports a meaningful tenant improvement allowance negotiation (restore the settings to spec) or an evidence-based exit decision. The Settings Scorecard is the brief.",action:"Export Settings Scorecard as TI negotiation evidence or input to exit decision. Cross-reference with TWS score of 43 and the Financial Surface's renewal cost exposure data.",decision:'Lease Strategy Input'},
];

/* ════════════════════════════════════════════════════════════════════════════
   ALT-ARCH — Workplace Analytics PRD Addendum: The Altitude Architecture.
   "We do not simplify by removing capability. We simplify by stratifying it."
   A0 Headline → A1 Vital Signs → A2 Drivers → A3 Evidence → A4 Workbench.
   Noise Doctrine: detail is deferred, never deleted — the six tabs survive as
   Altitude-3 evidence panels reached contextually from a signal or driver.
   ════════════════════════════════════════════════════════════════════════════ */

/* §4.1 Driver Chains — the causal path made a first-class object. Every link
   is labelled honestly (correlation / lagged-correlation / Sage-inferred
   causation) — never upgrade a correlation to a cause for cleaner UX (§7). */
const ALT_PILLAR_COLORS={Culture:'#8b0020',Flow:'#2563EB',Performance:'#059669'};
const ANALYTICS_DRIVERS=[
  {id:'dr1',zone:'drag',pillar:'Culture',cons:92,conf:84,assets:['1 Penn Plaza'],
   consWhy:'Consequence: −27 magnitude × Culture weight 40% × renewal in 4 months (closing window) × 84% confidence × still reversible',
   headline:'Setting drift (Community Exchange) → Culture',
   detail:'Floor 14 social anchor removed · co-presence collapsed · renewal calculus at stake',
   hypothesis:'Restoring the Floor 14 Community Exchange setting (coffee station + circulation access) will recover Co-presence fulfilment and arrest the Culture decline at 1 Penn Plaza.',
   predicted:'Co-presence fulfilment +12–18 pts within 8 weeks; Culture decline arrested by week 4.',
   chain:{nodes:[
     {label:'Setting change',text:'Floor 14 coffee station removed during building works',ev:{tab:'relationships',asset:'nyc1'}},
     {label:'Setting drift / SEI',text:'Community Exchange drift — utilization 61%→19% · dwell 3.1 min',ev:{tab:'alignment'}},
     {label:'Pillar sub-metric',text:'Co-presence fulfilment −18 pts (35% wt)',ev:{tab:'trends',pillar:'Culture'}},
     {label:'Pillar score',text:'Culture −27 pts (6 wks)',ev:{tab:'scores',asset:'nyc1',pillar:'Culture'}},
     {label:'Business consequence',text:'Lease renewal calculus — renewal in 4 months',sage:'The 1 Penn Plaza renewal is in 4 months and Culture has declined 27 points, driven by Community Exchange setting drift on Floor 14. How should this change our renewal negotiating posture?'},
   ],links:[
     {kind:'Sage-inferred causation',strength:'Strong',conf:84},
     {kind:'Lagged correlation',strength:'Strong',conf:81},
     {kind:'Correlation',strength:'Moderate',conf:78},
     {kind:'Sage-inferred causation',strength:'Strong',conf:87},
   ]}},
  {id:'dr2',zone:'drag',pillar:'Flow',cons:88,conf:89,assets:['200 Park Ave'],
   consWhy:'Consequence: lowest Design Fidelity in portfolio × lease already expired (open decision) × 89% confidence',
   headline:'Design Fidelity collapse (28%) → Flow',
   detail:'Settings not delivering design intent · density above ceiling · lease expired — TI or exit decision open',
   hypothesis:'A tenant improvement programme restoring 200 Park Ave settings to spec (focus pods, visual privacy, density relief) will recover Focus space-to-intent fit and lift Flow above 55.',
   predicted:'Focus fit +10 pts within one quarter of restoration; alternative: evidence-based exit.',
   chain:{nodes:[
     {label:'Setting change',text:'IP zones densified above Design Hypothesis ceiling · privacy screening removed',ev:{tab:'relationships',asset:'nyc3'}},
     {label:'Setting drift / SEI',text:'Design Fidelity 28% — lowest in portfolio · Coverage 41%',ev:{tab:'alignment'}},
     {label:'Pillar sub-metric',text:'Focus space-to-intent fit 71 vs 85 standard',ev:{tab:'gap',pillar:'Flow'}},
     {label:'Pillar score',text:'Flow 48 · TWS 43 and declining',ev:{tab:'scores',asset:'nyc3',pillar:'Flow'}},
     {label:'Business consequence',text:'TI allowance negotiation or evidence-based exit — lease expired',sage:'200 Park Ave sits at 28% Design Fidelity and TWS 43 with the lease expired. Build the case comparing a TI-allowance restoration against an evidence-based exit.'},
   ],links:[
     {kind:'Sage-inferred causation',strength:'Strong',conf:89},
     {kind:'Correlation',strength:'Strong',conf:85},
     {kind:'Correlation',strength:'Moderate',conf:80},
     {kind:'Sage-inferred causation',strength:'Strong',conf:89},
   ]}},
  {id:'dr3',zone:'drag',pillar:'Flow',cons:81,conf:88,assets:['1 Penn Plaza','200 Park Ave'],
   consWhy:'Consequence: coverage 3% vs 8–12% benchmark × two assets × population with elevated recovery need × 88% confidence',
   headline:'Reflect coverage gap (3% vs 8–12%) → Flow',
   detail:'Zero functioning Individual Reflect settings at two locations · 4+ hrs/day AI-assisted population',
   hypothesis:'Restoring one Individual Reflect setting per location (≥380 SF, daylight, no performance cues) will lift Environment satisfaction and arrest the sustained-performance risk.',
   predicted:'Environment satisfaction +6–10 pts within 8 weeks at affected locations.',
   chain:{nodes:[
     {label:'Setting change',text:'Wellness areas converted to Individual Produce overflow',ev:{tab:'relationships',asset:'nyc1'}},
     {label:'Setting drift / SEI',text:'Individual Reflect SEI 0.34 — critical · coverage 3% of floor area',ev:{tab:'alignment'}},
     {label:'Pillar sub-metric',text:'Environment satisfaction 68 vs 75 standard',ev:{tab:'gap',pillar:'Flow'}},
     {label:'Pillar score',text:'Flow drag concentrated at affected assets',ev:{tab:'scores',pillar:'Flow'}},
     {label:'Business consequence',text:'Sustained performance decline risk — performance infrastructure gap',sage:'Two locations have zero functioning Individual Reflect settings against an 8–12% coverage benchmark. Quantify the sustained-performance risk and the minimum restoration footprint.'},
   ],links:[
     {kind:'Sage-inferred causation',strength:'Strong',conf:88},
     {kind:'Correlation',strength:'Moderate',conf:74},
     {kind:'Correlation',strength:'Moderate',conf:70},
     {kind:'Lagged correlation',strength:'Moderate',conf:72},
   ]}},
  {id:'dr4',zone:'drag',pillar:'Culture',cons:74,conf:92,assets:['Portfolio'],
   consWhy:'Consequence: 8 consecutive weeks unmet × standard-integrity risk (miscalibration would distort Culture portfolio-wide) × 92% confidence',
   headline:'Co-presence standard possibly miscalibrated → Culture',
   detail:'Gap widened 28%→51% over 8 weeks · pattern suggests the standard, not the behaviour',
   hypothesis:'The 75% co-presence corporate standard predates current hybrid norms; recalibrating to a 55–60% standard will restore signal integrity without masking real drift.',
   predicted:'Re-based gap settles under 15%; genuine drift events remain detectable.',
   chain:{nodes:[
     {label:'Setting change',text:'Hybrid attendance norms shifted post-standard',ev:{tab:'utilization'}},
     {label:'Setting drift / SEI',text:'Co-presence gap 28%→51% over 8 consecutive weeks',ev:{tab:'gap',pillar:'Culture'}},
     {label:'Pillar sub-metric',text:'Co-presence fulfilment 40 vs 75 standard',ev:{tab:'trends',pillar:'Culture'}},
     {label:'Pillar score',text:'Culture portfolio drag — possibly overstated',ev:{tab:'scores',pillar:'Culture'}},
     {label:'Business consequence',text:'Standard revision decision — measure behaviour or recalibrate',sage:'The co-presence standard has been unmet for 8 consecutive weeks and the gap pattern suggests miscalibration, not behaviour. Should we revise the standard, and to what level?'},
   ],links:[
     {kind:'Correlation',strength:'Strong',conf:92},
     {kind:'Correlation',strength:'Strong',conf:88},
     {kind:'Sage-inferred causation',strength:'Moderate',conf:76},
     {kind:'Sage-inferred causation',strength:'Moderate',conf:71},
   ]}},
  {id:'dr6',zone:'lift',pillar:'Culture',cons:76,conf:91,assets:['30 Hudson Yards'],propagate:true,
   consWhy:'Consequence: replicable pattern × two candidate assets with the same drift signature × 91% confidence',
   headline:'30 Hudson CE configuration → Culture (replicable)',
   detail:'Cross-dept interaction 67% vs 45–60% benchmark · 31% RTO draw · Design Fidelity 84% — copy this',
   hypothesis:'Replicating the 30 Hudson Floor 12 CE Settings Profile (kit-of-parts furniture, 9:15 cross-team ritual, writable wall) at 1 Penn Plaza and 444 Market St will recover their underperforming Community Exchange settings.',
   predicted:'Cross-departmental interaction +15 pts at target assets within 8 weeks of replication.',
   chain:{nodes:[
     {label:'Setting change',text:'Kit-of-parts furniture · daily 9:15 cross-team ritual · writable wall · no assigned ownership',ev:{tab:'relationships',asset:'nyc2'}},
     {label:'Setting drift / SEI',text:'Design Fidelity 84% · COMPLIANT · CE SEI strongest in portfolio',ev:{tab:'alignment'}},
     {label:'Pillar sub-metric',text:'Cross-departmental interaction 67% (benchmark 45–60%) · dwell 11.4 min',ev:{tab:'trends',pillar:'Culture'}},
     {label:'Pillar score',text:'Culture 79 — highest in portfolio',ev:{tab:'scores',asset:'nyc2',pillar:'Culture'}},
     {label:'Business consequence',text:'31% return-to-office draw · validated pattern available to replicate',sage:'30 Hudson Yards Floor 12 is the top-performing Community Exchange setting in the portfolio. Model replicating its Settings Profile at 1 Penn Plaza and 444 Market St.'},
   ],links:[
     {kind:'Sage-inferred causation',strength:'Strong',conf:91},
     {kind:'Correlation',strength:'Strong',conf:90},
     {kind:'Correlation',strength:'Strong',conf:88},
     {kind:'Lagged correlation',strength:'Strong',conf:86},
   ]}},
  {id:'dr7',zone:'lift',pillar:'Culture',cons:58,conf:93,assets:['2845 Domain Dr'],
   consWhy:'Consequence: durable lift × expansion underway (informs the new floor plate) × 93% confidence',
   headline:'Austin atrium CE anchor → Culture',
   detail:'Central atrium as Community Exchange anchor · TWS 79 · utilization 82% — informs the expansion plate',
   hypothesis:'Carrying the atrium-anchor CE pattern into the Austin expansion floor plate will hold Culture above 75 at the enlarged site.',
   predicted:'Culture holds ≥75 through the expansion occupancy window.',
   chain:{nodes:[
     {label:'Setting change',text:'Central atrium designed as primary Community Exchange anchor',ev:{tab:'relationships',asset:'aus1'}},
     {label:'Setting drift / SEI',text:'Design Fidelity 79% · Coverage 85 — holding to intent',ev:{tab:'alignment'}},
     {label:'Pillar sub-metric',text:'Belonging + co-presence both above standard at this asset',ev:{tab:'trends',pillar:'Culture'}},
     {label:'Pillar score',text:'Culture 77 · TWS 79 · improving (+6)',ev:{tab:'scores',asset:'aus1',pillar:'Culture'}},
     {label:'Business consequence',text:'Expansion design confidence — pattern carries to the new plate',sage:'Austin is the portfolio’s strongest improving asset. What from its Settings Profile should carry into the HQ expansion floor plate?'},
   ],links:[
     {kind:'Correlation',strength:'Strong',conf:93},
     {kind:'Correlation',strength:'Strong',conf:90},
     {kind:'Correlation',strength:'Moderate',conf:84},
     {kind:'Sage-inferred causation',strength:'Moderate',conf:78},
   ]}},
];

/* §4.3 Signal triage — consequence-ranked; #1 takes Altitude 0, the rest queue
   in the Signal Rail (quiet, non-modal). */
const ALT_SIGNALS=[
  {id:'sg1',driverId:'dr1',rank:1,conf:84,window:'Renewal in 4 months',
   what:'Culture at 1 Penn Plaza has declined 27 points in 6 weeks — the lease renewal is in 4 months.',
   driver:'Community Exchange setting drift on Floor 14 (coffee station removed, circulation blocked).',
   stakes:'This changes the renewal calculus.'},
  {id:'sg2',driverId:'dr2',rank:2,conf:89,window:'Lease expired — decision open',
   what:'Design Fidelity at 200 Park Ave is 28% — the lowest in the portfolio — with TWS 43 and declining.',
   driver:'Settings produce a different experience than designed; density above hypothesis ceiling.',
   stakes:'Evidence supports a TI negotiation or an evidence-based exit.'},
  {id:'sg3',driverId:'dr4',rank:3,conf:92,window:'8 weeks persistent',
   what:'Co-presence standard unmet for 8 consecutive weeks; the gap widened 28%→51%.',
   driver:'Pattern suggests the standard is miscalibrated for hybrid norms, not the behaviour.',
   stakes:'A miscalibrated standard distorts Culture portfolio-wide.'},
  {id:'sg4',driverId:'dr3',rank:4,conf:88,window:'Compounding',
   what:'Zero functioning Individual Reflect settings at two locations; Wellbeing coverage 3% vs 8–12% benchmark.',
   driver:'Wellness areas converted to Individual Produce overflow.',
   stakes:'Sustained-performance infrastructure gap for high-AI-workflow teams.'},
];

/* §5 Workbench — hypothesis → experiment → evidence ledger (localStorage). */
const _WB_KEY='stage_workbench';
function loadWorkbench(){try{const v=JSON.parse(localStorage.getItem(_WB_KEY)||'[]');return Array.isArray(v)?v:[];}catch(e){return[];}}
function saveWorkbench(list){try{localStorage.setItem(_WB_KEY,JSON.stringify(list));}catch(e){}}

/* A1 vital sign — the delta is the loud element, the absolute recedes (§4.4). */
function VitalSign({label,value,delta,trend,color,sub,lead,delay=0}){
  const trendColor=trend==='Declining'?'#C84B2F':trend==='Improving'?'#059669':'rgba(61,31,13,.45)';
  return(
    <div className="card card-flat" style={{padding:'14px 16px',borderTop:`3px solid ${lead?color:'rgba(61,31,13,.1)'}`,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${delay}ms`}}>
      <div style={{fontSize:10,fontWeight:700,color:lead?color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>{label}</div>
      <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
        <span style={{fontSize:lead?34:26,fontWeight:900,color:'rgba(61,31,13,.55)',fontVariantNumeric:'tabular-nums'}}>{value}</span>
        {delta!==null&&delta!==undefined&&(
          <span style={{fontSize:lead?17:14,fontWeight:900,color:trendColor}}>{delta>0?'+':''}{delta}{typeof delta==='number'?' wk':''}</span>
        )}
      </div>
      <div style={{fontSize:10,fontWeight:700,color:trendColor,marginTop:3}}>{trend}</div>
      {sub&&<div style={{fontSize:9.5,color:'rgba(61,31,13,.35)',marginTop:3}}>{sub}</div>}
    </div>
  );
}

/* §4.1 — the Driver Chain: tappable nodes, honest links, fork-to-hypothesis. */
function DriverChain({driver,onEvidence,onSage,onFork}){
  const pcolor=ALT_PILLAR_COLORS[driver.pillar]||'#8b0020';
  return(
    <div className="focus-fade" style={{padding:'14px 16px',background:'rgba(245,237,224,.45)',borderRadius:10,border:'1px solid rgba(61,31,13,.07)'}}>
      <div style={{display:'flex',alignItems:'stretch',gap:0,overflowX:'auto',paddingBottom:6}}>
        {driver.chain.nodes.map((n,i)=>(
          <React.Fragment key={i}>
            <div className="chain-node" style={{minWidth:150,maxWidth:185,flexShrink:0,display:'flex',flexDirection:'column'}}
              title={n.ev?'Open evidence for this node':'Ask Sage about this consequence'}
              onClick={()=>n.ev?onEvidence(n.ev):onSage(n.sage)}>
              <div style={{fontSize:8.5,fontWeight:700,color:i===driver.chain.nodes.length-1?'#C84B2F':'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{n.label}</div>
              <div style={{fontSize:11,color:'#3D1F0D',lineHeight:1.45,flex:1}}>{n.text}</div>
              <div style={{fontSize:9,color:pcolor,fontWeight:700,marginTop:5}}>{n.ev?'View evidence ↓':'Ask Sage →'}</div>
            </div>
            {i<driver.chain.nodes.length-1&&(()=>{const lk=driver.chain.links[i]||{};return(
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'0 4px',flexShrink:0,minWidth:74}}>
                <div style={{fontSize:13,color:pcolor,lineHeight:1}}>──▶</div>
                <div title={`Link strength: ${lk.strength} · confidence ${lk.conf}%`} style={{fontSize:8,fontWeight:700,color:lk.kind==='Sage-inferred causation'?'#C84B2F':lk.kind==='Lagged correlation'?'#B8860B':'rgba(61,31,13,.45)',textAlign:'center',lineHeight:1.3,marginTop:3,maxWidth:80}}>{lk.kind}</div>
                <div style={{fontSize:8,color:'rgba(61,31,13,.35)'}}>{lk.strength} · {lk.conf}%</div>
                <div onClick={()=>onFork(driver,`Disputed link: ${driver.chain.nodes[i].label} → ${driver.chain.nodes[i+1].label}`)} style={{fontSize:8,color:'rgba(61,31,13,.4)',textDecoration:'underline',textUnderlineOffset:2,cursor:'pointer',marginTop:2}} title="Dispute this link — fork it into a testable hypothesis">dispute → hypothesis</div>
              </div>
            );})()}
          </React.Fragment>
        ))}
      </div>
      {/* §5.4 curiosity chips — scaffolded, tuned to this chain */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:8,alignItems:'center'}}>
        {[`Show me other assets with this ${driver.zone==='lift'?'pattern':'drift'}`,'What did we try last time?',`Model ${driver.zone==='lift'?'replicating':'restoring'} this setting`].map(q=>(
          <button key={q} onClick={()=>onSage(`${q} — context: ${driver.headline} (${driver.assets.join(', ')}).`)} className="seg-btn" style={{fontSize:10}}>✦ {q}</button>
        ))}
        <button onClick={()=>onFork(driver)} style={{marginLeft:'auto',fontSize:10,fontWeight:700,color:'#fff',background:'#3D1F0D',border:'none',borderRadius:999,padding:'5px 13px',cursor:'pointer',fontFamily:'inherit'}}>⚗ Flag as Hypothesis</button>
      </div>
    </div>
  );
}

/* §4.2 — one driver row on the board; expanded = its chain in place. */
function DriverRow({driver,expanded,onToggle,onEvidence,onSage,onFork,compact}){
  const zoneColor=driver.zone==='drag'?'#C84B2F':'#059669';
  const pcolor=ALT_PILLAR_COLORS[driver.pillar]||'#8b0020';
  return(
    <div style={{borderRadius:10,border:`1px solid ${expanded?zoneColor+'55':'rgba(61,31,13,.08)'}`,background:'#fff',overflow:'hidden'}}>
      <div onClick={onToggle} style={{display:'flex',alignItems:'center',gap:10,padding:compact?'8px 13px':'11px 14px',cursor:'pointer',userSelect:'none'}}>
        <span title={driver.consWhy} style={{fontSize:compact?10:11,fontWeight:900,color:'#fff',background:zoneColor,borderRadius:6,padding:compact?'2px 7px':'3px 8px',flexShrink:0,fontVariantNumeric:'tabular-nums'}}>{driver.cons}</span>
        <span style={{fontSize:9,fontWeight:700,color:pcolor,background:pcolor+'14',padding:'2px 8px',borderRadius:999,flexShrink:0}}>{driver.pillar}</span>
        <div style={{minWidth:0,flex:1}}>
          <div style={{fontSize:compact?12:13,fontWeight:700,color:'#3D1F0D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{driver.headline}</div>
          {!compact&&<div style={{fontSize:10.5,color:'rgba(61,31,13,.5)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{driver.detail}</div>}
        </div>
        {driver.propagate&&<span style={{fontSize:9,fontWeight:700,color:'#059669',background:'rgba(5,150,105,.1)',padding:'2px 8px',borderRadius:999,flexShrink:0}}>Worth propagating</span>}
        <span style={{fontSize:10,color:'rgba(61,31,13,.45)',flexShrink:0}}>{driver.assets.join(' · ')}</span>
        <span style={{fontSize:9,fontFamily:'monospace',color:'rgba(61,31,13,.4)',flexShrink:0}}>conf {driver.conf}%</span>
        <span style={{fontSize:10,color:'rgba(61,31,13,.35)',flexShrink:0,transform:expanded?'rotate(90deg)':'none',transition:'transform .15s'}}>›</span>
      </div>
      {expanded&&<div style={{padding:'0 12px 12px'}}><DriverChain driver={driver} onEvidence={onEvidence} onSage={onSage} onFork={onFork}/></div>}
    </div>
  );
}

/* §4.3 Signal Rail — queued signals, quiet right edge, never modal. */
function SignalRail({open,setOpen,onTrace,activeId}){
  const queued=ALT_SIGNALS.filter(s=>s.rank>1);
  return(
    <div className="sigrail">
      {open&&(
        <div className="sigrail-panel focus-fade">
          <div style={{padding:'12px 14px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:11,fontWeight:800,color:'#3D1F0D'}}>Signal Rail</span>
            <span style={{fontSize:9.5,color:'rgba(61,31,13,.45)'}}>ranked by consequence</span>
            <button onClick={()=>setOpen(false)} style={{marginLeft:'auto',border:'none',background:'none',fontSize:15,color:'rgba(61,31,13,.35)',cursor:'pointer',lineHeight:1}}>✕</button>
          </div>
          {queued.map(s=>(
            <div key={s.id} onClick={()=>{onTrace(s.driverId);setOpen(false);}} style={{padding:'11px 14px',borderBottom:'1px solid rgba(61,31,13,.05)',cursor:'pointer',background:activeId===s.driverId?'rgba(232,129,58,.06)':'transparent'}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(245,237,224,.6)'} onMouseLeave={e=>e.currentTarget.style.background=activeId===s.driverId?'rgba(232,129,58,.06)':'transparent'}>
              <div style={{display:'flex',gap:7,alignItems:'center',marginBottom:4}}>
                <span style={{fontSize:9,fontWeight:800,color:'rgba(61,31,13,.35)'}}>#{s.rank}</span>
                <span style={{fontSize:9,fontWeight:700,color:'#B8860B',background:'rgba(184,134,11,.1)',padding:'1px 7px',borderRadius:999}}>{s.window}</span>
                <span style={{marginLeft:'auto',fontSize:9,fontFamily:'monospace',color:'rgba(61,31,13,.4)'}}>conf {s.conf}%</span>
              </div>
              <div style={{fontSize:11.5,color:'#3D1F0D',lineHeight:1.45}}>{s.what}</div>
              <div style={{fontSize:10,fontWeight:700,color:'#8b0020',marginTop:4}}>Trace this →</div>
            </div>
          ))}
        </div>
      )}
      {!open&&<div className="sigrail-tab" onClick={()=>setOpen(true)} title="Queued signals — ranked by consequence">Signals · {queued.length}</div>}
    </div>
  );
}

/* §5 Workbench drawer — hypothesis → experiment (locked baseline) → ledger. */
function WorkbenchDrawer({open,onClose,prefill,items,setItems,onSage,baseline}){
  const [form,setForm]=useState({statement:'',intervention:'',predicted:'',window:'8 weeks',success:''});
  useEffect(()=>{
    if(open)setForm({
      statement:(prefill&&prefill.hypothesis)||'',
      intervention:(prefill&&prefill.intervention)||'',
      predicted:(prefill&&prefill.predicted)||'',
      window:'8 weeks',
      success:(prefill&&prefill.predicted)?('Achieved if: '+prefill.predicted):'',
    });
  },[open,prefill]);
  if(!open)return null;
  const save=()=>{
    if(!form.statement.trim())return;
    const item={id:'wb'+Date.now().toString(36),createdAt:new Date().toISOString().slice(0,10),status:'running',
      source:(prefill&&prefill.sourceLabel)||'Manual',
      statement:form.statement.trim(),intervention:form.intervention.trim(),predicted:form.predicted.trim(),
      window:form.window,success:form.success.trim(),
      cohort:'90-day benchmark cohort (N=14)',
      baseline:{snapshotAt:new Date().toISOString().slice(0,10),...baseline},
      outcome:null};
    const next=[item,...items];setItems(next);saveWorkbench(next);
    setForm({statement:'',intervention:'',predicted:'',window:'8 weeks',success:''});
  };
  const logOutcome=(id,verdict)=>{
    const note=window.prompt(verdict==='confirmed'?'Outcome note — what moved, by how much?':'Outcome note — what did we learn? (Honest losses are evidence, not failure.)');
    if(note===null)return;
    const next=items.map(x=>x.id===id?{...x,status:'complete',outcome:{verdict,note,at:new Date().toISOString().slice(0,10)}}:x);
    setItems(next);saveWorkbench(next);
  };
  const lab={fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:4,display:'block'};
  const inp={width:'100%',boxSizing:'border-box',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,padding:'8px 11px',fontSize:12,fontFamily:'inherit',background:'#F5EDE0',outline:'none',resize:'vertical'};
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(61,31,13,.35)',zIndex:900,display:'flex',justifyContent:'flex-end'}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:440,height:'100%',background:'#fff',boxShadow:'-4px 0 24px rgba(61,31,13,.12)',display:'flex',flexDirection:'column',overflowY:'auto'}}>
        <div style={{padding:'18px 20px 14px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:'#3D1F0D'}}>⚗ The Workbench</div>
            <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginTop:2}}>Hypothesis → Experiment → Evidence Ledger</div>
          </div>
          <button onClick={onClose} style={{border:'none',background:'none',fontSize:20,color:'rgba(61,31,13,.3)',cursor:'pointer',lineHeight:1}}>×</button>
        </div>
        <div style={{padding:20,borderBottom:'1px solid rgba(61,31,13,.08)'}}>
          {prefill&&prefill.sourceLabel&&<div style={{fontSize:10,fontWeight:700,color:'#8b0020',marginBottom:10}}>Forked from: {prefill.sourceLabel}</div>}
          <label style={lab}>Hypothesis — Sage pre-filled, editable</label>
          <textarea rows={3} value={form.statement} onChange={e=>setForm(f=>({...f,statement:e.target.value}))} style={inp}/>
          <label style={{...lab,marginTop:12}}>Proposed intervention</label>
          <textarea rows={2} value={form.intervention} onChange={e=>setForm(f=>({...f,intervention:e.target.value}))} style={inp} placeholder="What physically changes, and when?"/>
          <label style={{...lab,marginTop:12}}>Predicted movement</label>
          <textarea rows={2} value={form.predicted} onChange={e=>setForm(f=>({...f,predicted:e.target.value}))} style={inp}/>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginTop:12}}>
            <div><label style={lab}>Measurement window</label>
              <select value={form.window} onChange={e=>setForm(f=>({...f,window:e.target.value}))} style={{...inp,resize:'none'}}>{['4 weeks','8 weeks','12 weeks'].map(w=><option key={w}>{w}</option>)}</select></div>
            <div><label style={lab}>Comparison cohort</label>
              <div style={{...inp,background:'rgba(61,31,13,.04)',color:'rgba(61,31,13,.55)',fontSize:11}}>90-day benchmark (N=14)</div></div>
          </div>
          <label style={{...lab,marginTop:12}}>Success criteria — stated up front, locked at start</label>
          <textarea rows={2} value={form.success} onChange={e=>setForm(f=>({...f,success:e.target.value}))} style={inp}/>
          {/* §5.2 — baseline auto-snapshot: "before" can never be retro-fitted */}
          <div style={{marginTop:12,padding:'9px 12px',background:'rgba(184,134,11,.06)',border:'1px solid rgba(184,134,11,.25)',borderRadius:8,fontSize:10.5,color:'rgba(61,31,13,.65)',lineHeight:1.5}}>
            <b style={{color:'#B8860B'}}>🔒 Baseline snapshot (locked at start)</b> — Culture {baseline.culture} · Flow {baseline.flow} · Co-presence fulfilment {baseline.copresence} · CE SEI {baseline.ceSei}. Captured automatically; cannot be edited after the experiment starts.
          </div>
          <button onClick={save} disabled={!form.statement.trim()} style={{marginTop:14,width:'100%',padding:'10px',border:'none',borderRadius:8,background:form.statement.trim()?'#3D1F0D':'rgba(61,31,13,.2)',color:'#F5EDE0',fontSize:12.5,fontWeight:700,cursor:form.statement.trim()?'pointer':'not-allowed',fontFamily:'inherit'}}>Start experiment — lock baseline</button>
        </div>
        {/* §5.3 Evidence Ledger */}
        <div style={{padding:20,flex:1}}>
          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Evidence Ledger — wins and honest losses</div>
          {items.length===0&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.4)',lineHeight:1.6}}>No experiments yet. Fork a hypothesis from any Driver Chain link, gap, or signal — every experiment makes the next one smarter.</div>}
          {items.map(x=>(
            <div key={x.id} style={{border:'1px solid rgba(61,31,13,.1)',borderLeft:`4px solid ${x.status==='running'?'#B8860B':x.outcome&&x.outcome.verdict==='confirmed'?'#059669':'#C84B2F'}`,borderRadius:9,padding:'10px 13px',marginBottom:9}}>
              <div style={{display:'flex',gap:7,alignItems:'center',marginBottom:5,flexWrap:'wrap'}}>
                <span style={{fontSize:9,fontWeight:800,color:x.status==='running'?'#B8860B':x.outcome&&x.outcome.verdict==='confirmed'?'#059669':'#C84B2F',textTransform:'uppercase',letterSpacing:'.05em'}}>{x.status==='running'?'Running':x.outcome.verdict==='confirmed'?'Confirmed':'Disproven'}</span>
                <span style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>{x.createdAt} · {x.window} · {x.source}</span>
              </div>
              <div style={{fontSize:11.5,color:'#3D1F0D',lineHeight:1.5,marginBottom:5}}>{x.statement}</div>
              <div style={{fontSize:9.5,color:'rgba(61,31,13,.5)'}}>🔒 Baseline {x.baseline.snapshotAt}: Culture {x.baseline.culture} · Co-presence {x.baseline.copresence}</div>
              {x.outcome&&<div style={{fontSize:10.5,color:'rgba(61,31,13,.65)',marginTop:5,fontStyle:'italic'}}>Outcome ({x.outcome.at}): {x.outcome.note}</div>}
              {x.status==='running'&&(
                <div style={{display:'flex',gap:8,marginTop:8}}>
                  <button onClick={()=>logOutcome(x.id,'confirmed')} className="seg-btn" style={{fontSize:10,color:'#059669',borderColor:'rgba(5,150,105,.4)'}}>✓ Confirmed</button>
                  <button onClick={()=>logOutcome(x.id,'disproven')} className="seg-btn" style={{fontSize:10,color:'#C84B2F',borderColor:'rgba(200,75,47,.4)'}}>✗ Disproven</button>
                  <button onClick={()=>onSage(`Read the interim signal on this experiment: "${x.statement}" — baseline Culture ${x.baseline.culture}, window ${x.window}.`)} className="seg-btn" style={{fontSize:10}}>✦ Sage read</button>
                </div>
              )}
            </div>
          ))}
          {/* §5.3 — portfolio-portable learning */}
          <div style={{marginTop:6,padding:'10px 13px',background:'rgba(5,150,105,.05)',border:'1px dashed rgba(5,150,105,.35)',borderRadius:9,fontSize:10.5,color:'rgba(61,31,13,.6)',lineHeight:1.55}}>
            <b style={{color:'#059669'}}>Portable pattern · </b>444 Market St shows the same Community Exchange drift signature as 1 Penn Plaza. The 30 Hudson Yards CE Settings Profile (Design Fidelity 84%) is a validated restoration template — suggested as your next hypothesis.
          </div>
        </div>
      </div>
    </div>
  );
}

// Carries a focused asset across the page boundary when a signal/driver descends
// into Settings Scorecard (now its own surface). Consumed once by the scorecard
// AnalyticsPage instance on mount.
let _scorecardFocus=null;
function AnalyticsPage({onSageAsk,surface='analytics',onNav}){
  const authFetch=useAuthFetch();
  const {isDemo,currentUser}=useData();   // Workplace Analytics is demo-constant driven; gate for real accounts
  const [tab,setTab]=useState(surface==='scorecard'?'relationships':'scores');
  const [selRbwLoc,setSelRbwLoc]=useState(null);
  // Consume a cross-surface focus handoff (a descent navigated here from the main
  // analytics surface) so the requested property opens expanded on arrival.
  React.useEffect(()=>{
    if(surface==='scorecard'&&_scorecardFocus){setSelRbwLoc(_scorecardFocus);_scorecardFocus=null;}
  },[surface]);
  const [senseData,setSenseData]=useState(null);
  const [senseLoading,setSenseLoading]=useState(true);
  const [showWeightStudio,setShowWeightStudio]=useState(false);
  // Weight Studio — advisor-adjustable pillar weights (Flow/Culture/Perf)
  const [weights,setWeights]=useState({flow:30,culture:40,perf:30});
  // ── ALT-ARCH state — sticky context, descent, board, rail, workbench ──────
  const [ctx,setCtx]=useState({asset:null,pillar:null});   // §6: context is sticky across altitudes
  const [expandedDriver,setExpandedDriver]=useState(null);
  const [driverPillar,setDriverPillar]=useState('All');     // All | Culture | Flow
  const [driverDir,setDriverDir]=useState('balanced');      // balanced | drag | lift
  const [liftOpen,setLiftOpen]=useState(false);
  const [railOpen,setRailOpen]=useState(false);
  const [wbOpen,setWbOpen]=useState(false);
  const [wbPrefill,setWbPrefill]=useState(null);
  const [wbItems,setWbItems]=useState(loadWorkbench);
  // §3.1 audience-adaptive posture — emphasis only, never permission
  const [posture,setPosture]=useState(()=>{try{return localStorage.getItem('stage_analytics_posture')||'';}catch(e){return'';}});
  const effPosture=posture||((currentUser&&currentUser.role==='viewer')?'executive':'strategist');
  const setPosturePersist=p=>{setPosture(p);try{localStorage.setItem('stage_analytics_posture',p);}catch(e){}};
  const [boardOpen,setBoardOpen]=useState(true);
  useEffect(()=>{setBoardOpen(effPosture==='strategist');},[effPosture]);
  const evidenceRef=useRef(null);
  const driversRef=useRef(null);
  // TWS composite — live from API portfolioSummary when available, else computed from rbwLocs, else fallback
  // Flow/Culture/Perf pillar breakdown comes from sense_data constants (no live pillar API yet)
  const twsFlow=TWS_PILLARS.flow,twsCulture=TWS_PILLARS.culture,twsPerf=TWS_PILLARS.perf,twsDelta=-3,cultureDelta=-6;
  // WEX-1 Phase 2 — Capacity Leakage (Lc) and SEI
  // Lc = ((C_reserved - C_actual) / C_reserved) × 100 — portfolio weighted avg
  const lcPortfolio=14.2; // % — booking vs badge delta
  // SEI per Setting type — utilization effectiveness 0–1
  const seiByType=[
    {id:'CE',label:'Community Exchange',sei:0.82,color:'#4A7FA5'},
    {id:'GE',label:'Group Exchange',sei:0.71,color:'#7BAE8A'},
    {id:'GP',label:'Group Produce',sei:0.68,color:'#E8A838'},
    {id:'IP',label:'Individual Produce',sei:0.91,color:'#E87A38'},
    {id:'IR',label:'Individual Reflect',sei:0.34,color:'#6B7280'},
    {id:'CR',label:'Community Reflect',sei:0.55,color:'#7C3AED'},
  ];

  // ── Fetch live Sense / RBW-I data on mount ──────────────────────
  useEffect(()=>{
    authFetch('/api/sense?query_type=summary')
      .then(d=>{if(d&&d.locations)setSenseData(d);})
      .catch(()=>{})
      .finally(()=>setSenseLoading(false));
  },[authFetch]);

  // ── Merge live API data with JS constants (left-join on id) ─────
  // rbwLocs: live coverageScore/designFidelity/tws/status from API,
  //          topFriction and settings-mix % preserved from JS constant
  const rbwLocs=senseData?.locations
    ?RBW_LOCATIONS.map(loc=>{
        const live=senseData.locations.find(l=>l.id===loc.id);
        return live?{...loc,coverageScore:live.coverageScore,designFidelity:live.designFidelity,
          tws:live.tws,status:live.status,criticalZones:live.criticalZones,driftZones:live.driftZones}:loc;
      })
    :RBW_LOCATIONS;

  // rbwZones: full live zone objects (locId, type, sqft, sentiment, drift, etc.)
  const rbwZones=senseData?.zones||RBW_ZONES;

  // rbwFriction: live friction pattern analysis
  const rbwFriction=senseData?.frictionPatterns||RBW_FRICTION;

  // ── Live-derived composite metrics ──────────────────────────────
  // twsComposite: API portfolioSummary.avgTWS → rbwLocs avg → canonical TWS_COMPOSITE
  // Live API avgTWS wins when present & positive; else average only rbwLocs that
  // actually carry a numeric `tws`; else the canonical pillar-weighted composite.
  // (RBW_LOCATIONS has no `tws` field — the old code averaged undefined→0 and the
  //  64 fallback never fired because rbwLocs is non-empty, so the hero showed 0.)
  const _liveAvgTWS=senseData?.portfolioSummary?.avgTWS;
  const _rbwWithTws=rbwLocs.filter(l=>typeof l.tws==='number');
  const twsComposite=(typeof _liveAvgTWS==='number'&&_liveAvgTWS>0)
    ?Math.round(_liveAvgTWS)
    :(_rbwWithTws.length?Math.round(_rbwWithTws.reduce((s,l)=>s+l.tws,0)/_rbwWithTws.length):TWS_COMPOSITE);
  // stdsFulfilment: % zones without drift (live when zones available)
  const stdsFulfilment=rbwZones.length
    ?Math.round(rbwZones.filter(z=>z.integrity!=='DRIFT').length/rbwZones.length*100)
    :67;
  const costPerExp=847; // financial metric — no live source yet
  // Analytics week label — always show current trailing week
  const weekLabel=(()=>{
    const d=new Date();
    d.setDate(d.getDate()-7);
    return`Week ending ${d.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}`;
  })();
  // Live location count from senseData or rbwLocs
  const liveLocCount=senseData?.portfolioSummary?.locations||rbwLocs.length;

  const handleExportBrief=()=>{
    const rows=[['Location','Market','Coverage Score','Design Fidelity','TWS','Status','Critical Zones','Drift Zones']];
    rbwLocs.forEach(l=>rows.push([l.name,l.market,l.coverageScore,l.designFidelity,l.tws||'',l.status,l.criticalZones||0,l.driftZones||0]));
    const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    // P1 #13 — the brief carries the current consequence breadcrumb + Driver Chain
    const s0=ALT_SIGNALS[0];
    const drv=expandedDriver?ANALYTICS_DRIVERS.find(d=>d.id===expandedDriver):null;
    let summary=`HEADLINE (Sage · confidence ${s0.conf}%): ${s0.what} ${s0.stakes}\nDriver: ${s0.driver}\n`;
    if(drv){
      summary+=`\nTRACED DRIVER CHAIN — ${drv.headline} (${drv.assets.join(', ')} · confidence ${drv.conf}%):\n`;
      summary+=drv.chain.nodes.map((n,i)=>`  ${i+1}. [${n.label}] ${n.text}${i<drv.chain.links.length?` —(${drv.chain.links[i].kind}, ${drv.chain.links[i].conf}%)→`:''}`).join('\n')+'\n';
    }
    summary+=`\nVITAL SIGNS: Culture ${twsCulture} (${cultureDelta} wk, Declining) | Flow ${twsFlow} (Stable) | TWS Composite ${twsComposite} (${twsDelta} wk) | Standards Met ${stdsFulfilment}% | Critical zones ${criticalZones}\n\n`;
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([summary+csv],{type:'text/csv'}));
    a.download=`workplace-analytics-brief-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };
  const pc=v=>v>=70?'#16a34a':v>=55?'#d97706':'#dc2626';
  const pb=v=>v>=70?'#f0fdf4':v>=55?'#fffbeb':'#fef2f2';
  const pc2=v=>v>=75?'#16a34a':v>=55?'#d97706':'#dc2626';
  const tc=id=>RBW_SETTING_TYPES.find(t=>t.id===id)?.color||'#999';
  const tn=id=>RBW_SETTING_TYPES.find(t=>t.id===id)?.name||id;
  const avgCov=Math.round(rbwLocs.reduce((s,l)=>s+l.coverageScore,0)/rbwLocs.length);
  const avgFid=Math.round(rbwLocs.reduce((s,l)=>s+l.designFidelity,0)/rbwLocs.length);
  const p1Friction=rbwFriction.filter(f=>f.severity==='P1').length;
  const criticalZones=rbwZones.filter(z=>z.status==='critical').length;
  const selectedLoc=selRbwLoc?rbwLocs.find(l=>l.id===selRbwLoc):null;
  const selectedZones=selRbwLoc?rbwZones.filter(z=>z.locId===selRbwLoc):[];

  // Weight Studio — weighted TWS from advisor-set pillar weights
  const wTotal=weights.flow+weights.culture+weights.perf||100;
  const weightedTWS=Math.round((twsFlow*weights.flow+twsCulture*weights.culture+twsPerf*weights.perf)/wTotal);

  // ── ALT-ARCH helpers — descent is in-place, context is sticky (§6) ────────
  const sage=msg=>onSageAsk&&onSageAsk(msg,'analytics');
  const descendTo=ev=>{
    // Settings Scorecard + Setting Alignment now live on their own sidebar surface
    // (WX Performance ▸ Settings Scorecard); a descent into either navigates there,
    // carrying the focused asset, instead of switching an in-page evidence tab.
    if(ev.tab==='relationships'||ev.tab==='alignment'){
      _scorecardFocus=ev.asset||null;
      if(onNav)onNav('scorecard');
      return;
    }
    setTab(ev.tab);
    setCtx(c=>({asset:ev.asset!==undefined?ev.asset:c.asset,pillar:ev.pillar!==undefined?ev.pillar:c.pillar}));
    setTimeout(()=>{evidenceRef.current&&evidenceRef.current.scrollIntoView({behavior:'smooth',block:'start'});},60);
  };
  const traceDriver=id=>{
    setBoardOpen(true);
    setExpandedDriver(id);
    const d=ANALYTICS_DRIVERS.find(x=>x.id===id);
    if(d&&d.zone==='lift')setLiftOpen(true);
    setTimeout(()=>{driversRef.current&&driversRef.current.scrollIntoView({behavior:'smooth',block:'start'});},60);
  };
  const forkHypothesis=(driver,sourceNote)=>{
    setWbPrefill({hypothesis:driver.hypothesis,predicted:driver.predicted,
      sourceLabel:(sourceNote?sourceNote+' — ':'')+driver.headline+' · '+driver.assets.join(', ')});
    setWbOpen(true);
  };
  // §5.2 — the baseline that gets snapshotted (locked) when an experiment starts
  const wbBaseline={culture:twsCulture,flow:twsFlow,copresence:40,ceSei:'0.82'};
  const crumbDriver=expandedDriver?ANALYTICS_DRIVERS.find(d=>d.id===expandedDriver):null;
  const ctxAssetName=ctx.asset?((TWS_ASSETS.find(a=>a.id===ctx.asset)||{}).name||ctx.asset):null;
  // Settings Scorecard + Setting Alignment moved OUT of the WX Performance tab
  // strip into their own sidebar surface (page='scorecard'), where the alignment
  // is merged into each property's expanded detail. The main analytics surface
  // keeps the four evidence tabs.
  const EV_TABS=[['scores','TWS Scores'],['gap','Gap Panel'],['trends','12-Week Trends'],['utilization','Utilization']];
  const tabLabel=(EV_TABS.find(([id])=>id===tab)||[])[1]||'';

  // ── SETTINGS SCORECARD SURFACE (WX Performance ▸ Settings Scorecard) ───────
  // The former Settings Scorecard + Setting Alignment tabs, consolidated into one
  // sidebar child surface: a portfolio scorecard whose per-property detail merges
  // the zone detail with that location's Setting Alignment (live setting-alignment
  // profile vs design archetype + capacity leakage).
  if(surface==='scorecard'){
    if(!isDemo)return(
      <div style={{maxWidth:1100,margin:'0 auto'}}>
        <div className="page-header-label">WX Performance</div>
        <div className="page-title" style={{marginBottom:16}}>Settings Scorecard</div>
        <div className="card" style={{padding:'32px 28px',textAlign:'center',borderLeft:'4px solid #E8813A'}}>
          <div style={{fontSize:28,marginBottom:10}}>▥</div>
          <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D',marginBottom:6}}>No settings scorecard yet</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.6)',lineHeight:1.6,maxWidth:460,margin:'0 auto'}}>Connect a Sense audit or experience signals to populate coverage, design fidelity, and per-property setting alignment for your portfolio.</div>
        </div>
      </div>
    );
    return(
      <div style={{maxWidth:1200,margin:'0 auto'}}>
        <div style={{marginBottom:16}}>
          <div className="page-header-label">WX Performance</div>
          <div className="page-title">Settings Scorecard</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.45)',marginTop:3}}>Coverage, design fidelity, and per-property setting alignment across {rbwLocs.length} assets · click a location for zone detail and its alignment radar</div>
        </div>
        {/* KPIs */}
        <div className="grid-4" style={{marginBottom:16}}>
          <KPI label="Avg Coverage Score" value={String(avgCov)} sub={`/100 · ${avgCov>=75?'Full coverage':'Partial coverage'}`} barPct={avgCov} warn={avgCov<75} delay={0}/>
          <KPI label="Avg Design Fidelity" value={`${avgFid}%`} sub="of zones within hypothesis" barPct={avgFid} warn={avgFid<70} delay={70}/>
          <KPI label="Recovery Friction" value={String(p1Friction)} sub={`P1 location${p1Friction!==1?'s':''}`} warn={p1Friction>0} barPct={Math.min(p1Friction*25,100)} delay={140}/>
          <KPI label="Critical Zones" value={String(criticalZones)} sub="below hypothesis floor" warn={criticalZones>0} barPct={Math.min(criticalZones*20,100)} delay={210}/>
        </div>
        {/* Portfolio SEI distribution (moved here from the former Setting Alignment tab) */}
        <div className="card" style={{padding:16,marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Portfolio SEI · Setting Type Distribution</div>
          <div style={{display:'grid',gap:6}}>
            {seiByType.map(({id,label,sei,color})=>{
              const status=sei>=0.75?'Effective':sei>=0.55?'Underperforming':'Critical';
              const statusColor=sei>=0.75?'#16a34a':sei>=0.55?'#d97706':'#dc2626';
              return(
                <div key={id} style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:10,fontWeight:700,color,width:24,flexShrink:0}}>{id}</span>
                  <span style={{fontSize:11,color:'rgba(61,31,13,.55)',width:160,flexShrink:0}}>{label}</span>
                  <div style={{flex:1,height:8,background:'rgba(61,31,13,.06)',borderRadius:4,overflow:'hidden',position:'relative'}}>
                    <div style={{height:'100%',width:`${sei*100}%`,background:color,borderRadius:4,opacity:.85}}/>
                    <div style={{position:'absolute',top:0,left:'75%',width:1.5,height:'100%',background:'rgba(61,31,13,.25)'}}/>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,color:statusColor,width:32,flexShrink:0,textAlign:'right'}}>{sei.toFixed(2)}</span>
                  <span style={{fontSize:9,fontWeight:700,color:statusColor,background:statusColor+'18',padding:'1px 7px',borderRadius:4,width:90,textAlign:'center'}}>{status}</span>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:8,fontSize:9,color:'rgba(61,31,13,.3)'}}>Tick at 0.75 = effectiveness threshold</div>
        </div>
        {/* Portfolio Settings Scorecard — property detail merges zone detail + alignment */}
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'14px 18px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span style={{fontWeight:700,fontSize:13}}>Portfolio Settings Scorecard</span>
            <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>Coverage = all 6 types present &amp; performing · Click a location to explore zones + alignment</span>
          </div>
          <div style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr style={{background:'rgba(245,237,224,.6)',borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                  {['Location','Coverage','Design Fidelity','Settings Mix','Top Friction','TWS'].map(h=>(
                    <th key={h} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.05em',textAlign:'left',padding:'10px 14px',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rbwLocs.flatMap(loc=>{
                  const tws=TWS_ASSETS.find(a=>a.id===loc.id);
                  const cc=pc2(loc.coverageScore);
                  const fc=loc.designFidelity>=70?'#16a34a':loc.designFidelity>=50?'#d97706':'#dc2626';
                  const isSelected=selRbwLoc===loc.id;
                  const zoneRows=rbwZones.filter(z=>z.locId===loc.id);
                  const rows=[
                    <tr key={loc.id} onClick={()=>setSelRbwLoc(isSelected?null:loc.id)} style={{borderBottom:isSelected?'none':'1px solid rgba(61,31,13,.05)',cursor:'pointer',background:isSelected?'rgba(245,237,224,.6)':'transparent'}} onMouseOver={e=>e.currentTarget.style.background='rgba(245,237,224,.4)'} onMouseOut={e=>e.currentTarget.style.background=isSelected?'rgba(245,237,224,.6)':'transparent'}>
                      <td style={{padding:'11px 14px',fontWeight:700,fontSize:13}}>
                        <span style={{marginRight:6,color:isSelected?'#8b0020':'rgba(61,31,13,.25)',fontSize:11}}>{isSelected?'▾':'›'}</span>
                        {loc.name}
                      </td>
                      <td style={{padding:'11px 14px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:7}}>
                          <span style={{fontSize:18,fontWeight:800,color:cc}}>{loc.coverageScore}</span>
                          <div style={{width:32,height:4,background:'rgba(61,31,13,.08)',borderRadius:2}}><div style={{width:`${loc.coverageScore}%`,height:'100%',background:cc,borderRadius:2}}/></div>
                        </div>
                      </td>
                      <td style={{padding:'11px 14px'}}><span style={{fontSize:16,fontWeight:800,color:fc}}>{loc.designFidelity}%</span></td>
                      <td style={{padding:'11px 14px'}}>
                        <div style={{display:'flex',height:10,borderRadius:3,overflow:'hidden',width:120,gap:1,marginBottom:3}}>
                          {Object.entries(loc.settings).map(([type,pct])=>(
                            <div key={type} title={`${tn(type)}: ${pct}%`} style={{width:`${pct}%`,background:tc(type),flexShrink:0}}/>
                          ))}
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:2}}>
                          {Object.entries(loc.settings).map(([type,pct])=>(
                            <span key={type} style={{fontSize:8,padding:'1px 4px',borderRadius:3,background:tc(type)+'28',color:'#3D1F0D',fontWeight:600}}>{type} {pct}%</span>
                          ))}
                        </div>
                      </td>
                      <td style={{padding:'11px 14px'}}>
                        {loc.topFriction!=='None'
                          ?<span style={{fontSize:11,fontWeight:700,color:'#dc2626',padding:'2px 9px',borderRadius:999,background:'#fef2f2',border:'1px solid #fca5a5'}}>{loc.topFriction}</span>
                          :<span style={{fontSize:11,color:'rgba(61,31,13,.3)'}}>None detected</span>}
                      </td>
                      <td style={{padding:'11px 14px'}}>
                        <span style={{fontSize:16,fontWeight:800,color:tws?pc(tws.twsScore):'rgba(61,31,13,.3)'}}>{tws?.twsScore||'—'}</span>
                      </td>
                    </tr>
                  ];
                  if(isSelected){
                    const algn=MK_TARGET_PROFILES[loc.archetype]||MK_TARGET_PROFILES['default'];
                    const lcColor=loc.lc<10?'#16a34a':loc.lc<20?'#d97706':'#dc2626';
                    rows.push(
                      <tr key={loc.id+'-detail'}>
                        <td colSpan={6} style={{padding:0,borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                          <div style={{background:'rgba(232,168,56,.04)',borderTop:'2px solid rgba(232,168,56,.28)'}}>
                            <div style={{padding:'12px 18px',background:'rgba(232,168,56,.08)',borderBottom:'1px solid rgba(232,168,56,.16)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                              <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                                <span style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>{loc.name} — Zone Detail &amp; Setting Alignment</span>
                                {[['Coverage',loc.coverageScore,cc],['Fidelity',loc.designFidelity+'%',fc],['TWS',tws?.twsScore||'—',tws?pc(tws.twsScore):'rgba(61,31,13,.3)']].map(([l,v,c])=>(
                                  <span key={l} style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{l}: <b style={{color:c}}>{v}</b></span>
                                ))}
                              </div>
                              <button onClick={e=>{e.stopPropagation();setSelRbwLoc(null);}} style={{fontSize:18,color:'rgba(61,31,13,.3)',background:'transparent',border:'none',cursor:'pointer',lineHeight:1}}>×</button>
                            </div>
                            {/* Setting Alignment — live profile vs design archetype (merged from the former tab) */}
                            <div style={{padding:'16px 18px',borderBottom:'1px solid rgba(232,168,56,.16)'}}>
                              <div style={{fontSize:10,fontWeight:700,color:'#C1714A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:10}}>Setting Alignment — live profile vs {loc.archetype} archetype</div>
                              <div style={{display:'grid',gridTemplateColumns:'minmax(0,210px) 1fr',gap:18,alignItems:'start'}}>
                                <div><RadarChart options={[{id:loc.id,name:loc.name,rbw:loc.rbw}]} archetype={loc.archetype} size={200}/></div>
                                <div>
                                  <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Axis Readings vs Target</div>
                                  {MK_AXES.map(({key,short})=>{
                                    const val=(loc.rbw||{})[key]||0;
                                    const tgt=algn[key]||0;
                                    const delta=val-tgt;
                                    const color=delta>=0?'#16a34a':delta>=-5?'#d97706':'#dc2626';
                                    return(
                                      <div key={key} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                                        <span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',width:20,flexShrink:0}}>{short}</span>
                                        <div style={{flex:1,height:5,background:'rgba(61,31,13,.06)',borderRadius:3,overflow:'hidden',position:'relative'}}>
                                          <div style={{height:'100%',width:`${val}%`,background:color,borderRadius:3}}/>
                                          <div style={{position:'absolute',top:0,left:`${tgt}%`,width:2,height:'100%',background:'#E8A838',transform:'translateX(-1px)'}}/>
                                        </div>
                                        <span style={{fontSize:10,fontWeight:700,color,width:26,textAlign:'right'}}>{val}</span>
                                        <span style={{fontSize:9,fontWeight:700,color,width:32,textAlign:'right'}}>{delta>=0?'+':''}{delta}</span>
                                      </div>
                                    );
                                  })}
                                  <div style={{marginTop:10,display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:8,background:'rgba(61,31,13,.03)',border:'1px solid rgba(61,31,13,.06)'}}>
                                    <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',flexShrink:0}}>Capacity Leakage</span>
                                    <div style={{flex:1,height:5,background:'rgba(61,31,13,.06)',borderRadius:3,overflow:'hidden'}}><div style={{height:'100%',width:`${Math.min(loc.lc*4,100)}%`,background:lcColor,borderRadius:3}}/></div>
                                    <span style={{fontSize:11,fontWeight:700,color:lcColor,flexShrink:0}}>{loc.lc}%</span>
                                  </div>
                                  <div style={{marginTop:6,fontSize:9,color:'rgba(61,31,13,.35)'}}>Amber tick = {loc.archetype} target · delta vs target · Lc &lt;10% healthy · 10–20% moderate · &gt;20% critical</div>
                                </div>
                              </div>
                            </div>
                            {/* Zone detail */}
                            <div style={{padding:16,display:'flex',flexDirection:'column',gap:10}}>
                              {zoneRows.length>0?zoneRows.map(zone=>{
                                const st=RBW_SETTING_TYPES.find(t=>t.id===zone.type);
                                const isCrit=zone.status==='critical';
                                const isWatch=zone.status==='watch';
                                const borderC=isCrit?'#dc2626':isWatch?'#d97706':st?.color||'#999';
                                return(
                                  <div key={zone.id} className="card" style={{padding:16,borderLeft:`4px solid ${borderC}`}}>
                                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:10,flexWrap:'wrap'}}>
                                      <div>
                                        <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:3,flexWrap:'wrap'}}>
                                          <span style={{width:8,height:8,borderRadius:'50%',background:st?.color,display:'inline-block',flexShrink:0}}/>
                                          <span style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>{zone.name}</span>
                                          <span style={{fontSize:10,padding:'2px 7px',borderRadius:999,background:(st?.color||'#999')+'22',color:'#3D1F0D',fontWeight:600}}>{st?.name}</span>
                                          <span style={{fontSize:9,padding:'2px 6px',borderRadius:4,background:'rgba(61,31,13,.05)',color:'rgba(61,31,13,.45)'}}>{zone.floor}</span>
                                        </div>
                                        <div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{zone.sqft.toLocaleString()} SF · Cap: {zone.capacity}</div>
                                      </div>
                                      <div style={{display:'flex',gap:5,flexShrink:0}}>
                                        <span style={{fontSize:9,fontWeight:700,padding:'3px 8px',borderRadius:999,background:isCrit?'#fef2f2':isWatch?'#fffbeb':'#f0fdf4',color:isCrit?'#dc2626':isWatch?'#d97706':'#16a34a',textTransform:'uppercase',border:`1px solid ${isCrit?'#fca5a5':isWatch?'#fcd34d':'#bbf7d0'}`}}>{zone.status}</span>
                                        {zone.integrity&&<span style={{fontSize:9,fontWeight:700,padding:'3px 8px',borderRadius:999,background:zone.integrity==='DRIFT'?'#fef3c7':'#f0fdf4',color:zone.integrity==='DRIFT'?'#92400e':'#166534',textTransform:'uppercase'}}>{zone.integrity}</span>}
                                      </div>
                                    </div>
                                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:zone.driftNote||zone.frictionSignal?10:0}}>
                                      {[['Utilization',zone.utilActual+'%',zone.utilTarget,zone.utilActual<35?'#dc2626':'#3D1F0D'],['Dwell',zone.dwellActual,zone.dwellTarget,'#3D1F0D'],['Sentiment',zone.sentiment+'/10','30-day NPS',zone.sentiment>=7?'#16a34a':zone.sentiment>=5.5?'#d97706':'#dc2626']].map(([l,v,tgt,c])=>(
                                        <div key={l} style={{background:'rgba(245,237,224,.5)',borderRadius:8,padding:'9px 11px'}}>
                                          <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',marginBottom:2}}>{l}</div>
                                          <div style={{fontSize:17,fontWeight:800,color:c}}>{v}</div>
                                          <div style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>Target: {tgt}</div>
                                        </div>
                                      ))}
                                    </div>
                                    {zone.driftNote&&<div style={{background:'#fef3c7',borderRadius:8,padding:'8px 12px',marginBottom:6,borderLeft:'3px solid #d97706'}}><div style={{fontSize:9,fontWeight:700,color:'#92400e',textTransform:'uppercase',marginBottom:2}}>Settings Drift</div><div style={{fontSize:11.5,color:'rgba(61,31,13,.75)',lineHeight:1.5}}>{zone.driftNote}</div></div>}
                                    {zone.frictionSignal&&<div style={{background:'#fef2f2',borderRadius:8,padding:'8px 12px',borderLeft:'3px solid #dc2626'}}><div style={{fontSize:9,fontWeight:700,color:'#dc2626',textTransform:'uppercase',marginBottom:2}}>{zone.frictionType} Friction</div><div style={{fontSize:11.5,color:'rgba(61,31,13,.7)',lineHeight:1.5}}>{zone.frictionSignal}</div></div>}
                                  </div>
                                );
                              }):(
                                <div style={{textAlign:'center',padding:'30px 20px',color:'rgba(61,31,13,.3)',fontSize:13}}><div style={{fontSize:24,marginBottom:8}}>⬡</div>No zones mapped for this location yet.</div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Real accounts see no demo workplace analytics — the RBW-I surface activates
  // once Sense / experience data is connected. (Demo account keeps the full view.)
  if(!isDemo)return(
    <div style={{maxWidth:1100,margin:'0 auto'}}>
      <div className="page-header-label">WX Performance</div>
      <div className="page-title" style={{marginBottom:16}}>Experience Intelligence</div>
      <div className="card" style={{padding:'32px 28px',textAlign:'center',borderLeft:'4px solid #E8813A'}}>
        <div style={{fontSize:28,marginBottom:10}}>▦</div>
        <div style={{fontFamily:'Georgia,serif',fontSize:18,color:'#3D1F0D',marginBottom:6}}>No workplace analytics yet</div>
        <div style={{fontSize:13,color:'rgba(61,31,13,.6)',lineHeight:1.6,maxWidth:460,margin:'0 auto'}}>Connect a Sense audit or experience signals to populate TWS, settings scorecards, and RBW-I zone analytics for your portfolio.</div>
      </div>
    </div>
  );
  return(
    <React.Fragment>
    {/* Weight Studio panel */}
    {showWeightStudio&&(
      <div style={{position:'fixed',inset:0,background:'rgba(61,31,13,.35)',zIndex:900,display:'flex',alignItems:'flex-start',justifyContent:'flex-end'}} onClick={e=>{if(e.target===e.currentTarget)setShowWeightStudio(false);}}>
        <div style={{width:380,height:'100%',background:'#fff',boxShadow:'-4px 0 24px rgba(61,31,13,.12)',display:'flex',flexDirection:'column',overflowY:'auto'}}>
          <div style={{padding:'20px 20px 16px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>⚙ Weight Studio</div>
              <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:2}}>Adjust TWS pillar weights for your portfolio context</div>
            </div>
            <button onClick={()=>setShowWeightStudio(false)} style={{border:'none',background:'none',fontSize:20,color:'rgba(61,31,13,.3)',cursor:'pointer',padding:4,lineHeight:1}}>×</button>
          </div>
          <div style={{padding:20,flex:1}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:16}}>Pillar Weights</div>
            {[['flow','Flow','#2563EB',twsFlow],['culture','Culture','#8b0020',twsCulture],['perf','Performance','#059669',twsPerf]].map(([k,label,color,score])=>(
              <div key={k} style={{marginBottom:20}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                  <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{label}</span>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:11,color:color,fontWeight:700}}>{score} pts</span>
                    <span style={{fontSize:12,fontWeight:700,color:color,minWidth:32,textAlign:'right'}}>{weights[k]}%</span>
                  </div>
                </div>
                <input type="range" min="10" max="60" value={weights[k]} onChange={e=>setWeights(p=>({...p,[k]:+e.target.value}))}
                  style={{width:'100%',accentColor:color}}/>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:9,color:'rgba(61,31,13,.3)',marginTop:2}}>
                  <span>10%</span><span>60%</span>
                </div>
              </div>
            ))}
            <div style={{background:'rgba(245,237,224,.6)',borderRadius:10,padding:'14px 16px',marginTop:8}}>
              <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>Weighted TWS Score</div>
              <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:4}}>
                <span style={{fontSize:36,fontWeight:900,color:'#8b0020'}}>{weightedTWS}</span>
                <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>vs standard {twsComposite}</span>
              </div>
              <div style={{fontSize:10,color:'rgba(61,31,13,.4)',lineHeight:1.5}}>
                Weights: Flow {weights.flow}% · Culture {weights.culture}% · Perf {weights.perf}%
                {wTotal!==100&&<span style={{color:'#d97706'}}> (total {wTotal}% — adjusting…)</span>}
              </div>
            </div>
            {/* §5.4 "what if" — preview the asset ranking under these weights
                before committing. Exploratory, reversible, non-destructive. */}
            <div style={{marginTop:16,borderTop:'1px solid rgba(61,31,13,.06)',paddingTop:14}}>
              <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>What if — asset ranking under these weights</div>
              {TWS_ASSETS
                .map(a=>({a,score:Math.round((a.flow*weights.flow+a.culture*weights.culture+a.perf*weights.perf)/wTotal)}))
                .sort((x,y)=>y.score-x.score)
                .map(({a,score},i)=>{
                  const moved=score-a.twsScore;
                  return(
                    <div key={a.id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                      <span style={{fontSize:10,fontWeight:800,color:'rgba(61,31,13,.3)',width:18}}>#{i+1}</span>
                      <span style={{fontSize:11,color:'#3D1F0D',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</span>
                      <span style={{fontSize:12,fontWeight:800,color:pc(score),fontVariantNumeric:'tabular-nums'}}>{score}</span>
                      <span style={{fontSize:9.5,fontWeight:700,color:moved>0?'#059669':moved<0?'#C84B2F':'rgba(61,31,13,.3)',width:30,textAlign:'right'}}>{moved>0?'+':''}{moved!==0?moved:'—'}</span>
                    </div>
                  );
                })}
              <div style={{fontSize:9,color:'rgba(61,31,13,.35)',marginTop:6}}>Δ vs standard weighting — interrogate your company's definition of a well-spent workplace.</div>
            </div>
            <div style={{marginTop:14,fontSize:10,color:'rgba(61,31,13,.35)',lineHeight:1.6}}>
              Weight Studio lets you prioritise the TWS pillars most relevant to your portfolio. Default weights (Flow 30% · Culture 40% · Perf 30%) are set by Stage's RBW-I framework. Changes apply to this session only.
            </div>
          </div>
          <div style={{padding:'16px 20px',borderTop:'1px solid rgba(61,31,13,.08)',display:'flex',gap:8}}>
            <button onClick={()=>setWeights({flow:30,culture:40,perf:30})} style={{flex:1,padding:'9px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,background:'transparent',fontSize:12,fontWeight:600,color:'rgba(61,31,13,.55)',cursor:'pointer',fontFamily:'inherit'}}>Reset Defaults</button>
            <button onClick={()=>setShowWeightStudio(false)} style={{flex:1,padding:'9px',border:'none',borderRadius:8,background:'#8b0020',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Apply</button>
          </div>
        </div>
      </div>
    )}
    {/* §5 Altitude 4 — the Workbench (hypothesis → experiment → evidence ledger) */}
    {wbOpen&&<WorkbenchDrawer open onClose={()=>setWbOpen(false)} prefill={wbPrefill} items={wbItems} setItems={setWbItems} onSage={sage} baseline={wbBaseline}/>}
    {/* §4.3 — queued signals: quiet right edge, reachable, never modal */}
    <SignalRail open={railOpen} setOpen={setRailOpen} onTrace={traceDriver} activeId={expandedDriver}/>
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <div className="page-header-label">WX Performance</div>
          <div className="page-title">Time Well Spent</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.45)',marginTop:3,display:'flex',alignItems:'center',gap:8}}>
            Experience intelligence across {liveLocCount} asset{liveLocCount!==1?'s':''} · {weekLabel}
            {senseLoading
              ?<span style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.3)',background:'rgba(61,31,13,.06)',padding:'2px 8px',borderRadius:999}}>⟳ Loading…</span>
              :senseData
                ?<span style={{fontSize:10,fontWeight:600,color:'#059669',background:'rgba(5,150,105,.08)',padding:'2px 8px',borderRadius:999}}>● LIVE</span>
                :<span style={{fontSize:10,fontWeight:600,color:'#d97706',background:'rgba(217,119,6,.08)',padding:'2px 8px',borderRadius:999}}>Demo data</span>
            }
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {/* §3.1 posture — changes defaults and emphasis, never permissions */}
          <div style={{display:'flex',gap:4,marginRight:4}} title="Audience posture — emphasis only; descent is always available">
            {[['executive','Executive'],['strategist','Strategist']].map(([v,l])=>(
              <button key={v} className={`seg-btn${effPosture===v?' on':''}`} style={{fontSize:10,padding:'4px 11px'}} onClick={()=>setPosturePersist(v)}>{l}</button>
            ))}
          </div>
          <button className="btn-ghost" style={{fontSize:12}} onClick={handleExportBrief}>↓ Export Brief</button>
          <button className="btn-ghost" style={{fontSize:12}} onClick={()=>{setWbPrefill(null);setWbOpen(true);}}>⚗ Workbench{wbItems.length>0?` (${wbItems.length})`:''}</button>
          <button className="btn-primary" style={{fontSize:12}} onClick={()=>setShowWeightStudio(true)}>⚙ Weight Studio</button>
        </div>
      </div>

      {/* ════ ALTITUDE 0 · THE HEADLINE — "What changes a decision today?" ════
          One ranked Sage signal, promoted to the literal top of the page.
          Two actions only; confidence always inline; no dismiss-without-note. */}
      {(()=>{const s0=ALT_SIGNALS[0];return(
        <div style={{background:'linear-gradient(135deg,rgba(139,0,33,.07) 0%,rgba(139,0,33,.02) 100%)',border:'1px solid rgba(139,0,33,.2)',borderLeft:'4px solid #8b0020',borderRadius:12,padding:'16px 20px',marginBottom:12}}>
          <div style={{display:'flex',alignItems:'center',gap:9,marginBottom:8,flexWrap:'wrap'}}>
            <div style={{width:30,height:30,background:'#8b0020',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:800,flexShrink:0}}>Sg</div>
            <span style={{fontSize:10,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.08em'}}>The Headline</span>
            <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>Sage cross-domain synthesis · ranked by consequence</span>
            <span style={{marginLeft:'auto',fontSize:10,fontFamily:'monospace',color:'rgba(61,31,13,.55)',background:'rgba(61,31,13,.05)',padding:'2px 9px',borderRadius:999}}>Signal confidence: {s0.conf}%</span>
          </div>
          <div style={{fontFamily:'Georgia,serif',fontSize:17.5,color:'#3D1F0D',lineHeight:1.45,marginBottom:5}}>{s0.what} <b style={{color:'#8b0020'}}>{s0.stakes}</b></div>
          <div style={{fontSize:12.5,color:'rgba(61,31,13,.65)',marginBottom:12}}><b style={{color:'rgba(61,31,13,.5)'}}>Driver · </b>{s0.driver}</div>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>traceDriver(s0.driverId)} style={{padding:'8px 18px',background:'#8b0020',color:'#fff',border:'none',borderRadius:7,fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Trace this →</button>
            <button onClick={()=>descendTo({tab:'relationships',asset:'nyc1',pillar:'Culture'})} className="btn-ghost" style={{fontSize:12}}>View evidence</button>
          </div>
        </div>
      );})()}

      {/* Breadcrumb of consequence — sticky; each crumb ascends one altitude (§6) */}
      {(crumbDriver||ctxAssetName||ctx.pillar)&&(
        <div className="alt-crumb">
          {crumbDriver&&<span className="crumb-chip" style={{borderColor:'rgba(139,0,33,.3)',color:'#8b0020'}} onClick={()=>{document.querySelector('.content')&&(document.querySelector('.content').scrollTop=0);}}>{crumbDriver.pillar} · {crumbDriver.assets.join(', ')}</span>}
          {crumbDriver&&<span style={{fontSize:11,color:'rgba(61,31,13,.35)'}}>›</span>}
          {crumbDriver&&<span className="crumb-chip" onClick={()=>traceDriver(crumbDriver.id)}>{crumbDriver.headline}</span>}
          {(ctxAssetName||ctx.pillar)&&<span style={{fontSize:11,color:'rgba(61,31,13,.35)'}}>›</span>}
          {(ctxAssetName||ctx.pillar)&&<span className="crumb-chip" onClick={()=>descendTo({tab})}>{[ctxAssetName,ctx.pillar].filter(Boolean).join(' · ')} — {tabLabel}</span>}
          <span onClick={()=>{setExpandedDriver(null);setCtx({asset:null,pillar:null});setSelRbwLoc(null);}} style={{fontSize:10,color:'rgba(61,31,13,.45)',cursor:'pointer',textDecoration:'underline',textUnderlineOffset:2,marginLeft:4}}>clear</span>
        </div>
      )}

      {/* ════ ALTITUDE 1 · VITAL SIGNS — "Is the portfolio healthy?" ════
          ≤5 KPIs, Culture & Flow leading, deltas loud, absolutes recede.
          Cost/Experience, Lc, and the SEI grid descend to Altitude 2/3. */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:4}}>
        <VitalSign lead label="Culture" value={twsCulture} delta={cultureDelta} trend="Declining" color="#8b0020" sub="40% wt · concentrated in 2 NY assets" delay={0}/>
        <VitalSign lead label="Flow" value={twsFlow} delta={0} trend="Stable" color="#2563EB" sub="30% wt" delay={60}/>
        <VitalSign label="TWS Composite" value={twsComposite} delta={twsDelta} trend="Declining" color="#8b0020" sub="conf 79% · booking · badge · pulse" delay={120}/>
        <VitalSign label="Standards Met" value={`${stdsFulfilment}%`} delta={null} trend="Stable" color="#B8860B" sub="4 of 6 active settings" delay={180}/>
        <VitalSign label="Sentry · Critical Zones" value={criticalZones} delta={null} trend={criticalZones>0?'Declining':'Stable'} color="#C84B2F" sub="below hypothesis floor" delay={240}/>
      </div>
      <div style={{fontSize:12.5,color:'rgba(61,31,13,.68)',lineHeight:1.6,padding:'6px 2px',marginBottom:16}}>
        <b style={{color:'#8b0020'}}>Sage · </b>Portfolio Time Well Spent is {twsComposite}, down 3 this week. Flow is steady; Culture is dragging the composite — concentrated in two New York assets.
      </div>

      {/* ════ ALTITUDE 2 · DRIVERS — "What is moving Culture and Flow?" ════
          The Driver Board: two zones, asymmetric by design — loud on the drag,
          quietly confident on the lift. Ranked by consequence, not magnitude. */}
      <div ref={driversRef} style={{scrollMarginTop:60,marginBottom:18}}>
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,flexWrap:'wrap'}}>
          <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.08em'}}>Drivers — what is moving Culture &amp; Flow</span>
          {boardOpen&&<>
            <div style={{display:'flex',gap:5}}>
              {['All','Culture','Flow'].map(p=>(
                <button key={p} className={`seg-btn${driverPillar===p?' on':''}`} style={{fontSize:10,padding:'3px 10px'}} onClick={()=>setDriverPillar(p)}>{p}</button>
              ))}
            </div>
            <div style={{display:'flex',gap:5}}>
              {[['balanced','Balanced'],['drag','Hurting only'],['lift','Helping only']].map(([v,l])=>(
                <button key={v} className={`seg-btn${driverDir===v?' on':''}`} style={{fontSize:10,padding:'3px 10px'}} onClick={()=>setDriverDir(v)}>{l}</button>
              ))}
            </div>
          </>}
          <span style={{marginLeft:'auto',fontSize:9.5,color:'rgba(61,31,13,.35)'}} title="Consequence = magnitude × pillar weight × time-criticality × confidence × reversibility window">ranked by consequence score ⓘ</span>
        </div>
        {!boardOpen?(
          <div className="card" style={{padding:'14px 18px',display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:12.5,color:'rgba(61,31,13,.6)'}}>Drivers are one tap away — the descent is available whenever you're curious.</span>
            <button onClick={()=>setBoardOpen(true)} style={{marginLeft:'auto',padding:'7px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:7,fontSize:12,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Descend to drivers ↓</button>
          </div>
        ):(()=>{
          const filtered=ANALYTICS_DRIVERS.filter(d=>driverPillar==='All'||d.pillar===driverPillar);
          const drag=filtered.filter(d=>d.zone==='drag').sort((a,b)=>b.cons-a.cons);
          const lift=filtered.filter(d=>d.zone==='lift').sort((a,b)=>b.cons-a.cons);
          const promoted=lift.find(d=>d.propagate)||lift[0];
          return(<>
            {driverDir!=='lift'&&(
              <div style={{border:'1px solid rgba(200,75,47,.28)',borderRadius:12,padding:13,background:'rgba(200,75,47,.03)',marginBottom:10}}>
                <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:9}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:'#C84B2F'}}/>
                  <span style={{fontSize:11,fontWeight:800,color:'#C84B2F',textTransform:'uppercase',letterSpacing:'.06em'}}>Dragging Culture &amp; Flow</span>
                  <span style={{fontSize:9.5,color:'rgba(61,31,13,.4)'}}>time-sensitive and actionable — risk leads</span>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:7}}>
                  {drag.map(d=>(
                    <DriverRow key={d.id} driver={d} expanded={expandedDriver===d.id}
                      onToggle={()=>setExpandedDriver(e=>e===d.id?null:d.id)}
                      onEvidence={descendTo} onSage={sage} onFork={forkHypothesis}/>
                  ))}
                  {drag.length===0&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.45)',padding:'8px 4px'}}>Nothing is dragging {driverPillar==='All'?'Culture or Flow':driverPillar} right now — here's what's working: see the lift zone below.</div>}
                </div>
              </div>
            )}
            {driverDir!=='drag'&&(
              <div style={{border:'1px solid rgba(5,150,105,.25)',borderRadius:12,padding:'10px 13px',background:'rgba(5,150,105,.03)'}}>
                <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:liftOpen||promoted?9:0}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:'#059669'}}/>
                  <span style={{fontSize:11,fontWeight:800,color:'#059669',textTransform:'uppercase',letterSpacing:'.06em'}}>Lifting Culture &amp; Flow</span>
                  <span style={{fontSize:9.5,color:'rgba(61,31,13,.4)'}}>durable — learn from it, propagate it</span>
                  <button onClick={()=>setLiftOpen(o=>!o)} className="seg-btn" style={{marginLeft:'auto',fontSize:10,padding:'2px 10px'}}>{liftOpen?'Collapse':`Expand all (${lift.length})`}</button>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:7}}>
                  {(liftOpen?lift:(promoted?[promoted]:[])).map(d=>(
                    <DriverRow key={d.id} driver={d} compact={!liftOpen&&driverDir!=='lift'} expanded={expandedDriver===d.id}
                      onToggle={()=>setExpandedDriver(e=>e===d.id?null:d.id)}
                      onEvidence={descendTo} onSage={sage} onFork={forkHypothesis}/>
                  ))}
                </div>
              </div>
            )}
          </>);
        })()}
      </div>

      {/* ════ ALTITUDE 3 · EVIDENCE — "Show me the data behind that." ════
          The six tabs survive in full, reached contextually from a signal or
          driver; the strip stays for power users, deliberately quiet (§3/§7). */}
      <div ref={evidenceRef} style={{scrollMarginTop:60,display:'flex',alignItems:'center',gap:10,marginBottom:6,flexWrap:'wrap'}}>
        <span style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.08em'}}>Evidence</span>
        {ctxAssetName&&<span className="crumb-chip" title="Sticky context — click to clear" onClick={()=>{setCtx(c=>({...c,asset:null}));setSelRbwLoc(null);}}>{ctxAssetName} ✕</span>}
        {ctx.pillar&&<span className="crumb-chip" title="Sticky context — click to clear" onClick={()=>setCtx(c=>({...c,pillar:null}))} style={{color:ALT_PILLAR_COLORS[ctx.pillar]||'#3D1F0D'}}>{ctx.pillar} ✕</span>}
        {!ctxAssetName&&!ctx.pillar&&<span style={{fontSize:10,color:'rgba(61,31,13,.3)'}}>arrive from a signal or driver — context follows you down</span>}
      </div>
      <div style={{display:'flex',gap:0,marginBottom:14,borderBottom:'1px solid rgba(61,31,13,.08)'}}>
        {EV_TABS.map(([id,label])=>(
          <button key={id} onClick={()=>{setTab(id);if(id!=='relationships')setSelRbwLoc(null);}} style={{padding:'7px 13px',border:'none',background:'transparent',fontSize:11,fontWeight:tab===id?700:500,color:tab===id?'#8b0020':'rgba(61,31,13,.4)',borderBottom:tab===id?'2px solid #8b0020':'2px solid transparent',cursor:'pointer',fontFamily:'inherit',transition:'all .15s',marginBottom:-1,display:'flex',alignItems:'center',gap:5}}>
            {label}
            {id==='alignment'&&<span style={{fontSize:8,fontWeight:700,color:'#C1714A',background:'rgba(193,113,74,.1)',border:'1px solid rgba(193,113,74,.2)',padding:'1px 4px',borderRadius:3}}>WEX-1</span>}
            {id==='relationships'&&p1Friction>0&&<span style={{background:'#dc2626',color:'#fff',borderRadius:999,fontSize:9,fontWeight:700,padding:'1px 5px'}}>{p1Friction}</span>}
          </button>
        ))}
      </div>

      {/* SCORES TAB */}
      {tab==='scores'&&(
        <div>
          <div style={{fontSize:12,color:'rgba(61,31,13,.4)',marginBottom:12}}>
            TWS composite and pillar scores by asset. Occupancy shown as context — not headline.
            {ctx.asset&&<span style={{marginLeft:8,fontWeight:700,color:'#8b0020'}}>Filtered to {ctxAssetName} — <span onClick={()=>setCtx(c=>({...c,asset:null}))} style={{textDecoration:'underline',cursor:'pointer'}}>show all</span></span>}
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%'}}>
              <thead>
                <tr style={{background:'rgba(245,237,224,.6)',borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                  {['','Asset','Market','TWS','Flow','Culture','Perf','Confidence','Utilization','Trend'].map(h=>(
                    <th key={h} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.05em',whiteSpace:'nowrap',textAlign:'left',padding:'10px 12px'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TWS_ASSETS.filter(a=>!ctx.asset||a.id===ctx.asset).map(a=>(
                  <tr key={a.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)',cursor:'pointer'}} onMouseOver={e=>e.currentTarget.style.background='rgba(245,237,224,.4)'} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{padding:'10px 12px'}}><Ring status={a.status}/></td>
                    <td style={{padding:'10px 12px',fontWeight:700,fontSize:13}}>{a.name}</td>
                    <td style={{padding:'10px 12px',fontSize:12,color:'rgba(61,31,13,.5)'}}>{a.market}</td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                        <span style={{fontSize:20,fontWeight:900,color:pc(a.twsScore)}}>{a.twsScore}</span>
                        <span style={{fontSize:10,fontWeight:600,color:a.trend<0?'#dc2626':'#16a34a'}}>{a.trend>0?'+':''}{a.trend}</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 12px'}}><span style={{fontSize:14,fontWeight:700,color:pc(a.flow)}}>{a.flow}</span></td>
                    <td style={{padding:'10px 12px'}}><span style={{fontSize:14,fontWeight:700,color:pc(a.culture)}}>{a.culture}</span></td>
                    <td style={{padding:'10px 12px'}}><span style={{fontSize:14,fontWeight:700,color:pc(a.perf)}}>{a.perf}</span></td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{display:'flex',alignItems:'center',gap:4}}>
                        <div style={{width:48,height:4,background:'rgba(61,31,13,.08)',borderRadius:2}}><div style={{width:`${a.conf}%`,height:'100%',background:'#8b0020',borderRadius:2,opacity:.7}}/></div>
                        <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>{a.conf}%</span>
                      </div>
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,color:'rgba(61,31,13,.5)'}}>{a.util}%</td>
                    <td style={{padding:'10px 12px',fontSize:11,fontWeight:600,color:a.trend<=-4?'#dc2626':a.trend>=4?'#16a34a':'rgba(61,31,13,.4)'}}>{a.trend<=-4?'↓ Declining':a.trend>=4?'↑ Improving':'→ Stable'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{marginTop:12,padding:'10px 14px',background:'rgba(245,237,224,.5)',borderRadius:8,fontSize:11,color:'rgba(61,31,13,.45)',display:'flex',gap:16,flexWrap:'wrap'}}>
            <span><b style={{color:'#16a34a'}}>≥70 Healthy</b> · <b style={{color:'#d97706'}}>55–69 Watch</b> · <b style={{color:'#dc2626'}}>&lt;55 At Risk</b></span>
            <span>Weights: Flow 30% · Culture 40% · Performance 30%</span>
            <span>A well-occupied office with a low TWS score is not a success.</span>
          </div>
        </div>
      )}

      {/* GAP PANEL TAB */}
      {tab==='gap'&&(
        <div>
          <div style={{fontSize:12,color:'rgba(61,31,13,.4)',marginBottom:12}}>Living Office Standards — gap between stated commitment and current performance. Sage surfaces when gap exceeds 30%.</div>
          {TWS_STANDARDS.map(s=>{
            const gap=s.standard-s.actual;
            const gapPct=Math.round((gap/s.standard)*100);
            const isLarge=gapPct>=25;
            const pc2=s.pillar==='Flow'?'#2563EB':s.pillar==='Culture'?'#8b0020':'#059669';
            // §4.4 — any difference past threshold carries a live trace affordance
            const gapDriver={s1:'dr1',s2:'dr2',s4:'dr1',s5:'dr3'}[s.id];
            return(
              <div key={s.id} className="card" style={{padding:18,marginBottom:10,borderLeft:`4px solid ${gapPct>=35?'#dc2626':isLarge?'#d97706':'rgba(61,31,13,.12)'}`}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:12}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                      <span style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>{s.name}</span>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:999,background:pc2+'18',color:pc2,textTransform:'uppercase'}}>{s.pillar}</span>
                      <span style={{fontSize:9,fontWeight:600,color:'rgba(61,31,13,.4)',padding:'2px 7px',borderRadius:999,background:'rgba(61,31,13,.06)'}}>{s.tier}</span>
                    </div>
                  </div>
                  <div style={{textAlign:'right',flexShrink:0}}>
                    <div style={{fontSize:16,fontWeight:800,color:gapPct>=35?'#dc2626':'#d97706'}}>{gapPct}% gap</div>
                    {isLarge&&<div style={{fontSize:10,color:'#8b0020',fontWeight:600}}>Sage insight available</div>}
                  </div>
                </div>
                <div style={{marginBottom:isLarge?10:0}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'rgba(61,31,13,.4)',marginBottom:5}}>
                    <span>Standard: <b>{s.standard}</b></span><span>Actual: <b style={{color:gapPct>=35?'#dc2626':'#d97706'}}>{s.actual}</b></span>
                  </div>
                  <div style={{position:'relative',height:10,background:'rgba(61,31,13,.06)',borderRadius:5}}>
                    <div style={{width:`${s.standard}%`,height:'100%',background:'rgba(61,31,13,.1)',borderRadius:5,position:'absolute'}}/>
                    <div style={{width:`${s.actual}%`,height:'100%',background:gapPct>=35?'#dc2626':'#d97706',borderRadius:5,position:'absolute',opacity:.8}}/>
                  </div>
                  <div style={{display:'flex',gap:10,marginTop:5}}>
                    <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:3,background:'rgba(61,31,13,.15)',borderRadius:1,display:'inline-block'}}/><span style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>Standard</span></div>
                    <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:3,background:gapPct>=35?'#dc2626':'#d97706',borderRadius:1,display:'inline-block',opacity:.8}}/><span style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>Actual</span></div>
                  </div>
                </div>
                {isLarge&&(
                  <div style={{display:'flex',gap:8,paddingTop:10,borderTop:'1px solid rgba(61,31,13,.06)',flexWrap:'wrap'}}>
                    <button className="btn-primary" style={{fontSize:11,padding:'5px 12px',background:'#8b0020'}} onClick={()=>sage(`The "${s.name}" standard (${s.pillar}) shows a ${gapPct}% gap — standard ${s.standard}, actual ${s.actual}. What is driving it, and what would close it?`)}>Ask Sage</button>
                    <button className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}} onClick={()=>sage(`Should the "${s.name}" standard of ${s.standard} be revised? The gap has persisted — assess whether the standard reflects current hybrid norms or the behaviour genuinely lags it.`)}>Revise Standard</button>
                    <button className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}} onClick={()=>{setWbPrefill({hypothesis:`Closing the "${s.name}" gap (actual ${s.actual} vs ${s.standard} standard) through a targeted setting intervention will lift the ${s.pillar} pillar within one measurement window.`,predicted:`${s.name} recovers toward ${s.standard} within 8 weeks; ${s.pillar} pillar responds within the window.`,sourceLabel:`Gap Panel · ${s.name} (${gapPct}% gap)`});setWbOpen(true);}}>⚗ Flag as Hypothesis</button>
                    {gapDriver&&<button onClick={()=>traceDriver(gapDriver)} style={{fontSize:11,padding:'5px 12px',border:'1px solid rgba(139,0,33,.3)',background:'rgba(139,0,33,.05)',color:'#8b0020',borderRadius:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Trace driver →</button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* TRENDS TAB */}
      {tab==='trends'&&(
        <div>
          <div style={{fontSize:12,color:'rgba(61,31,13,.4)',marginBottom:16}}>12-week pillar score trend. Culture declining — sub-metrics shown for diagnostic context. Occupancy available as overlay (off by default).</div>
          <div className="card" style={{padding:18,marginBottom:16,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:'0ms'}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>Portfolio Composite · Trailing 12 Weeks</div>
            <div style={{fontWeight:700,fontSize:14,marginBottom:2,color:'#3D1F0D'}}>Flow · Culture · Performance</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>Weighted average across all assets</div>
            <LineChart data={TWS_TREND} keys={['Flow','Culture','Performance']} colors={['#2563EB','#8b0020','#059669']} height={180}/>
            <div style={{display:'flex',gap:20,marginTop:12,flexWrap:'wrap'}}>
              {[['Flow','#2563EB','Productivity environment · Stable'],['Culture','#8b0020','Human capital ROI · Declining ▼'],['Performance','#059669','Operational effectiveness · Stable']].map(([l,c,sub])=>(
                <div key={l} style={{display:'flex',alignItems:'flex-start',gap:6}}>
                  <span style={{width:14,height:3,background:c,borderRadius:2,display:'inline-block',marginTop:6}}/>
                  <div><div style={{fontSize:12,fontWeight:600,color:'rgba(61,31,13,.7)'}}>{l}</div><div style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>{sub}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div className="card" style={{padding:18,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:'80ms'}}>
            <div style={{fontSize:10,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>Declining Pillar · Diagnostic View</div>
            <div style={{fontWeight:700,fontSize:14,marginBottom:2,color:'#3D1F0D'}}>Culture Pillar — Sub-Metric Breakdown</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:14}}>Weights sum to 100% · 12-week trailing</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
              {[
                {name:'Co-presence fulfilment',score:40,weight:'35%',trend:'↓ −18 pts',conf:'High'},
                {name:'Belonging signal',score:52,weight:'40%',trend:'↓ −12 pts',conf:'Medium'},
                {name:'Spontaneous collision rate',score:31,weight:'25%',trend:'↓ −7 pts',conf:'Low'},
              ].map((m,i)=>(
                <div key={m.name} className="card card-flat" style={{padding:'16px 18px',borderLeft:'4px solid #dc2626',animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*80}ms`}}>
                  <div style={{fontSize:10,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>Culture · Sub-metric</div>
                  <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',marginBottom:10,lineHeight:1.35}}>{m.name}</div>
                  <div style={{display:'flex',alignItems:'baseline',gap:8,marginBottom:6}}>
                    <span style={{fontSize:32,fontWeight:900,color:'#dc2626',fontVariantNumeric:'tabular-nums'}}>{m.score}</span>
                    <span style={{fontSize:11,fontWeight:700,color:'#dc2626'}}>{m.trend}</span>
                  </div>
                  <div style={{height:4,background:'rgba(61,31,13,.08)',borderRadius:2,marginBottom:6}}>
                    <div style={{width:`${m.score}%`,height:'100%',background:'#dc2626',borderRadius:2,opacity:.7}}/>
                  </div>
                  <div style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>Weight: <b>{m.weight}</b> · Signal confidence: <b>{m.conf}</b></div>
                  {/* §6 no dead ends — every detail view offers a forward move */}
                  <div onClick={()=>traceDriver('dr1')} style={{fontSize:10,fontWeight:700,color:'#8b0020',marginTop:7,cursor:'pointer'}}>Trace driver →</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* UTILIZATION TAB */}
      {tab==='utilization'&&(
        <div>
          <div style={{padding:'10px 14px',background:'rgba(245,237,224,.5)',borderRadius:8,fontSize:12,color:'rgba(61,31,13,.55)',marginBottom:16,border:'1px solid rgba(61,31,13,.08)'}}>
            Occupancy shown as context — not headline. A 90% occupied office with a TWS score of 38 is not a success. A 60% occupied office with a TWS of 79 is generating exceptional value per visit.
            <span style={{display:'block',marginTop:5,fontSize:11,color:'rgba(61,31,13,.45)'}}>Context metrics (demoted from the headline): Cost / Experience <b>${costPerExp}</b> per qualifying visit ≥TWS 60 · Capacity Leakage <b>{lcPortfolio.toFixed(1)}%</b> portfolio avg (threshold &lt;10% — full breakdown in Setting Alignment).</span>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:18}}>
            <div className="card" style={{padding:18,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:'0ms'}}>
              <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>Utilization by Market</div>
              <div style={{fontWeight:700,fontSize:14,marginBottom:2,color:'#3D1F0D'}}>Trailing 12 Weeks</div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>% of leased SF occupied during core hours</div>
              <LineChart data={UTIL_DATA} keys={Object.keys(MKT_COLORS)} colors={Object.values(MKT_COLORS)} height={160}/>
              <div style={{display:'flex',flexWrap:'wrap',gap:12,marginTop:8}}>
                {Object.entries(MKT_COLORS).map(([m,c])=>(
                  <div key={m} style={{display:'flex',alignItems:'center',gap:5}}>
                    <span style={{width:12,height:4,background:c,borderRadius:2,display:'inline-block'}}/>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{m}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{padding:18,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:'80ms'}}>
              <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>Experience vs Occupancy</div>
              <div style={{fontWeight:700,fontSize:14,marginBottom:2,color:'#3D1F0D'}}>Utilization vs. TWS — All Assets</div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:14}}>High utilization ≠ high experience value</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
                {TWS_ASSETS.map(a=>(
                  <div key={a.id} style={{textAlign:'center'}}>
                    <div style={{background:'rgba(61,31,13,.05)',border:'1px solid rgba(61,31,13,.08)',borderRadius:10,padding:'10px 6px',marginBottom:4}}>
                      <div style={{fontSize:13,fontWeight:800,color:'#3D1F0D'}}>{a.util}%</div>
                      <div style={{fontSize:9,color:'rgba(61,31,13,.35)',margin:'2px 0'}}>util</div>
                      <div style={{fontSize:13,fontWeight:800,color:pc(a.twsScore)}}>{a.twsScore}</div>
                      <div style={{fontSize:9,color:'rgba(61,31,13,.35)'}}>TWS</div>
                    </div>
                    <div style={{fontSize:9,color:'rgba(61,31,13,.5)',lineHeight:1.2}}>{a.name.split(' ').slice(0,2).join(' ')}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
    </React.Fragment>
  );
}

/* ── TEAM-1 Phase D frontend — Action Rail Kanban view ──────────────────────
   PRD §5.1: the same Action records, rendered as a drag-free status board.
   Columns map to the action lifecycle; cards are click-to-complete. */
/* ── TEAM-1 Phase D frontend — Action Calendar view (PRD §5.1) ──────────────
   Actions plotted on a month grid by due date. Read-only month navigation. */
function ActionCalendar({actions}){
  const [cursor,setCursor]=useState(()=>{const d=new Date();return {y:d.getFullYear(),m:d.getMonth()};});
  const first=new Date(cursor.y,cursor.m,1);
  const startDow=first.getDay();
  const daysInMonth=new Date(cursor.y,cursor.m+1,0).getDate();
  const monthLabel=first.toLocaleDateString('en-US',{month:'long',year:'numeric'});
  const byDay={};
  actions.forEach(a=>{
    if(!a.due)return;
    const d=new Date(a.due+'T00:00:00');
    if(d.getFullYear()===cursor.y&&d.getMonth()===cursor.m) (byDay[d.getDate()]=byDay[d.getDate()]||[]).push(a);
  });
  const cells=[];
  for(let i=0;i<startDow;i++)cells.push(null);
  for(let d=1;d<=daysInMonth;d++)cells.push(d);
  const move=delta=>setCursor(c=>{let m=c.m+delta,y=c.y;if(m<0){m=11;y--;}if(m>11){m=0;y++;}return {y,m};});
  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
        <button onClick={()=>move(-1)} className="btn-ghost" style={{fontSize:12,padding:'4px 10px'}}>‹</button>
        <span style={{fontSize:14,fontWeight:700,color:'#3D1F0D',minWidth:160,textAlign:'center'}}>{monthLabel}</span>
        <button onClick={()=>move(1)} className="btn-ghost" style={{fontSize:12,padding:'4px 10px'}}>›</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d=>(
          <div key={d} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textAlign:'center',padding:'4px 0'}}>{d}</div>
        ))}
        {cells.map((d,i)=>(
          <div key={i} style={{minHeight:72,border:'1px solid rgba(61,31,13,.07)',borderRadius:6,padding:4,background:d?'#fff':'transparent'}}>
            {d&&<div style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.45)',marginBottom:2}}>{d}</div>}
            {d&&(byDay[d]||[]).slice(0,3).map(a=>{
              const p=priorityColors[a.priority];
              return <div key={a.id} title={a.title} style={{fontSize:9,fontWeight:600,color:'#fff',background:a.status==='Overdue'?'#dc2626':p.bg,borderRadius:3,padding:'1px 4px',marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.title}</div>;
            })}
            {d&&(byDay[d]||[]).length>3&&<div style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>+{(byDay[d]||[]).length-3} more</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── TEAM-1 Phase D frontend — Action Timeline view (PRD §5.1) ──────────────
   Gantt-style horizontal bars positioned by due date across a date window. */
function ActionTimeline({actions}){
  const dated=actions.filter(a=>a.due).map(a=>({...a,_d:new Date(a.due+'T00:00:00')})).sort((a,b)=>a._d-b._d);
  if(dated.length===0)return <div style={{fontSize:12,color:'rgba(61,31,13,.4)',padding:20,textAlign:'center'}}>No dated actions to plot.</div>;
  const min=dated[0]._d.getTime();
  const max=dated[dated.length-1]._d.getTime();
  const span=Math.max(max-min,1);
  const pct=t=>((t-min)/span)*100;
  return(
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'rgba(61,31,13,.4)',marginBottom:4}}>
        <span>{dated[0]._d.toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
        <span>{dated[dated.length-1]._d.toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
      </div>
      {dated.map(a=>{
        const p=priorityColors[a.priority];
        const left=pct(a._d.getTime());
        return(
          <div key={a.id} style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:160,fontSize:11,color:'#3D1F0D',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flexShrink:0}} title={a.title}>{a.title}</div>
            <div style={{flex:1,position:'relative',height:20,background:'rgba(61,31,13,.04)',borderRadius:4}}>
              <div title={`${a.title} · due ${a.due}`} style={{position:'absolute',left:`calc(${left}% - 6px)`,top:3,width:14,height:14,borderRadius:3,background:a.status==='Overdue'?'#dc2626':p.bg,border:'1px solid #fff'}}/>
            </div>
            <span style={{width:84,fontSize:10,color:'rgba(61,31,13,.45)',flexShrink:0,textAlign:'right'}}>{a.due}</span>
          </div>
        );
      })}
    </div>
  );
}

function ActionKanban({actions,onPatch,updating}){
  const COLS=[
    ['Overdue','#dc2626','#fef2f2'],
    ['Open','#d97706','#fffbeb'],
    ['In Progress','#2563eb','#eff6ff'],
    ['Done','#16a34a','#f0fdf4'],
  ];
  const byStatus=s=>actions.filter(a=>a.status===s);
  return(
    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,alignItems:'start'}}>
      {COLS.map(([status,accent,bg])=>{
        const items=byStatus(status);
        return(
          <div key={status} style={{background:bg,borderRadius:10,padding:10,minHeight:120}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <span style={{fontSize:11,fontWeight:700,color:accent,textTransform:'uppercase',letterSpacing:'.04em'}}>{status}</span>
              <span style={{fontSize:11,fontWeight:700,color:accent,background:'#fff',borderRadius:999,padding:'1px 7px'}}>{items.length}</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {items.map(a=>{
                const p=priorityColors[a.priority];
                return(
                  <div key={a.id} className="card" style={{padding:'10px 12px',borderLeft:`3px solid ${p.bg}`}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:5}}>
                      <span style={{fontSize:9,fontWeight:800,padding:'1px 5px',borderRadius:3,background:p.bg,color:'#fff'}}>{a.priority}</span>
                      <span className={agentBadge(a.agent)} style={{fontSize:9}}>{a.agent}</span>
                    </div>
                    <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',lineHeight:1.4,marginBottom:6}}>{a.title}</div>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                      <span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>👤 {a.assigneeUserId||a.owner||'Unassigned'}</span>
                      {a.commentCount>0&&<span style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>💬 {a.commentCount}</span>}
                      <span style={{fontSize:10,color:'rgba(61,31,13,.4)',marginLeft:'auto'}}>{a.due}</span>
                    </div>
                    {status!=='Done'&&(
                      <button onClick={()=>onPatch(a.id,'Done')} disabled={updating.has(a.id)} className="btn-ghost" style={{fontSize:10,padding:'4px 10px',marginTop:8,width:'100%'}}>{updating.has(a.id)?'…':'✓ Done'}</button>
                    )}
                  </div>
                );
              })}
              {items.length===0&&<div style={{fontSize:11,color:'rgba(61,31,13,.3)',textAlign:'center',padding:'12px 0'}}>—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── TEAM-1 Phase D frontend — Action collaboration (assignee + comments) ────
   Edits the single assignee (PRD §5.1) and the comment thread, wired to the
   D-1 endpoints. Self-contained: lazily loads comments when an action row is
   expanded so we don't fetch threads for collapsed rows. */
function ActionCollab({action,authFetch,refresh,addToast}){
  const [assignee,setAssignee]=useState(action.assigneeUserId||'');
  const [savingA,setSavingA]=useState(false);
  const [comments,setComments]=useState(null); // null = not loaded yet
  const [draft,setDraft]=useState('');
  const [posting,setPosting]=useState(false);

  useEffect(()=>{
    let alive=true;
    authFetch(`/api/actions/${action.id}/comments`)
      .then(c=>{ if(alive)setComments(Array.isArray(c)?c:[]); })
      .catch(()=>{ if(alive)setComments([]); });
    return()=>{alive=false;};
  },[action.id,authFetch]);

  const saveAssignee=async()=>{
    setSavingA(true);
    try{
      await authFetch(`/api/actions/${action.id}/assignee`,{method:'PATCH',body:{assignee_user_id:assignee.trim()}});
      if(refresh)refresh();
      addToast(assignee.trim()?`Assigned to ${assignee.trim()}`:'Unassigned','info');
    }catch(e){ addToast('Assign failed — check connection','error'); }
    finally{ setSavingA(false); }
  };

  const postComment=async()=>{
    if(!draft.trim())return;
    setPosting(true);
    try{
      const c=await authFetch(`/api/actions/${action.id}/comments`,{method:'POST',body:{body:draft.trim()}});
      setComments(prev=>[...(prev||[]),c]);
      setDraft('');
      if(refresh)refresh();
    }catch(e){ addToast('Comment failed — check connection','error'); }
    finally{ setPosting(false); }
  };

  return(
    <div onClick={e=>e.stopPropagation()} style={{marginTop:12,paddingTop:12,borderTop:'1px dashed rgba(61,31,13,.12)'}}>
      {/* Assignee */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.04em'}}>Assignee</span>
        <input value={assignee} onChange={e=>setAssignee(e.target.value)} placeholder="user id / name"
          style={{flex:1,minWidth:140,maxWidth:260,padding:'6px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9'}}/>
        <button onClick={saveAssignee} disabled={savingA||assignee.trim()===(action.assigneeUserId||'')} className="btn-ghost"
          style={{fontSize:11,padding:'6px 12px',opacity:(savingA||assignee.trim()===(action.assigneeUserId||''))?.5:1}}>{savingA?'Saving…':'Save'}</button>
        <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>Only the assignee can mark Done</span>
      </div>
      {/* Comments */}
      <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8}}>
        Comments{comments?` (${comments.length})`:''}
      </div>
      {comments===null&&<div style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>Loading…</div>}
      {comments&&comments.length===0&&<div style={{fontSize:12,color:'rgba(61,31,13,.35)',marginBottom:8}}>No comments yet.</div>}
      {comments&&comments.map(c=>(
        <div key={c.id} style={{marginBottom:8,padding:'8px 10px',background:'rgba(61,31,13,.03)',borderRadius:8}}>
          <div style={{display:'flex',gap:8,alignItems:'baseline',marginBottom:2}}>
            <span style={{fontSize:11,fontWeight:700,color:'#3D1F0D'}}>{c.authorName||c.authorUserId||'User'}</span>
            <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>{c.createdAt?new Date(c.createdAt).toLocaleString():''}</span>
          </div>
          <div style={{fontSize:12,color:'rgba(61,31,13,.75)',lineHeight:1.5,whiteSpace:'pre-wrap'}}>{c.body}</div>
        </div>
      ))}
      <div style={{display:'flex',gap:8,marginTop:8,alignItems:'flex-end'}}>
        <textarea value={draft} onChange={e=>setDraft(e.target.value)} rows={2} placeholder="Add a comment…"
          style={{flex:1,padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9',resize:'vertical',boxSizing:'border-box',lineHeight:1.5}}/>
        <button onClick={postComment} disabled={posting||!draft.trim()} className="btn-primary"
          style={{fontSize:12,padding:'8px 14px',opacity:(posting||!draft.trim())?.5:1}}>{posting?'…':'Post'}</button>
      </div>
    </div>
  );
}

function ActionRailPage(){
  const {actions:_a=ACTIONS,refresh}=useData();
  const authFetch=useAuthFetch();
  const {addToast}=useToast();
  const [expanded,setExpanded]=useState(new Set(['a1','a4']));
  const [updating,setUpdating]=useState(new Set());
  const [fType,setFType]=useState('All');
  const [fPri,setFPri]=useState('All');
  const [notesModal,setNotesModal]=useState(null);
  const [notesDraft,setNotesDraft]=useState('');
  const [savingNote,setSavingNote]=useState(false);
  const [view,setView]=useState('list'); // TEAM-1 Phase D — list | kanban
  // EW5 — on closing an experience action, prompt a re-measure → Safe Decision.
  const [verifyAction,setVerifyAction]=useState(null);
  const [voForm,setVoForm]=useState({});
  const [voSaving,setVoSaving]=useState(false);
  const [voMsg,setVoMsg]=useState('');
  const submitActionOutcome=async()=>{
    if(!voForm.standard||voForm.after===''||voForm.after==null||isNaN(Number(voForm.after))){setVoMsg('Pick a standard and enter the new measured value (0–100).');return;}
    setVoSaving(true);setVoMsg('');
    try{
      const r=await authFetch('/api/experience/outcome',{method:'POST',body:{standard:voForm.standard,after:Number(voForm.after),action:verifyAction&&verifyAction.title,note:voForm.note||undefined}});
      const d=r.delta;const sign=d>0?'+':'';
      addToast(`Outcome verified — ${r.before??'—'}→${r.after}${d!=null?` (${sign}${d})`:''}, recorded as a Decision`,'success');
      setVerifyAction(null);setVoForm({});
      if(refresh)refresh();
    }catch(e){ setVoMsg('Could not record outcome — try again.'); }
    finally{ setVoSaving(false); }
  };

  const sorted=[..._a].filter(taskIsOutstanding).filter(a=>fType==='All'||a.type===fType).filter(a=>fPri==='All'||a.priority===fPri).sort((a,b)=>{
    if(a.status==='Overdue'&&b.status!=='Overdue')return -1;
    if(b.status==='Overdue'&&a.status!=='Overdue')return 1;
    return{P1:0,P2:1,P3:2}[a.priority]-{P1:0,P2:1,P3:2}[b.priority];
  });

  const toggle=id=>setExpanded(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});

  const patchAction=useCallback(async(id,status)=>{
    setUpdating(prev=>{const n=new Set(prev);n.add(id);return n;});
    try{
      await authFetch(`/api/actions/${id}`,{method:'PATCH',body:{status}});
      if(refresh)refresh();
      if(status==='Done'){
        addToast('Action marked complete','success');
        // EW5 — if this was an experience action, prompt the outcome re-measure.
        const act=_a.find(x=>x.id===id);
        if(act&&(act.asset==='Experience'||act.agent==='Sense')){ setVoForm({});setVoMsg('');setVerifyAction({id,title:act.title}); }
      }
      else if(status==='In Progress')addToast('Action moved to In Progress','info');
      else addToast(`Status updated to ${status}`,'info');
    }catch(e){
      console.warn('Action update failed',e);
      addToast('Update failed — check connection','error');
    }
    finally{setUpdating(prev=>{const n=new Set(prev);n.delete(id);return n;});}
  },[refresh,authFetch,addToast,_a]);

  const openNotesModal=(a)=>{
    setNotesDraft(a.resolutionNote||'');
    setNotesModal({id:a.id,title:a.title,currentNote:a.resolutionNote||''});
  };

  const saveNote=async()=>{
    if(!notesModal||!notesDraft.trim())return;
    setSavingNote(true);
    try{
      await authFetch(`/api/actions/${notesModal.id}`,{method:'PATCH',body:{resolution_note:notesDraft}});
      if(refresh)refresh();
      setNotesModal(null);
    }catch(e){alert(`Note save failed: ${e.message}`);}
    finally{setSavingNote(false);}
  };

  // Canonical task counts (matches /api/actions/metrics + Sage). "Outstanding" =
  // not-done (the headline), which equals the filtered list length below — fixes
  // the old "Total Open: 12" card sitting next to "16 items".
  const _td=new Date().toISOString().slice(0,10);
  const counts={overdue:_a.filter(a=>taskIsOverdue(a,_td)).length,p1:_a.filter(a=>a.priority==='P1'&&taskIsOutstanding(a)).length,inProgress:_a.filter(a=>a.status==='In Progress'&&taskIsOutstanding(a)).length,outstanding:_a.filter(taskIsOutstanding).length};

  return(
    <React.Fragment>
    <div style={{maxWidth:900,margin:'0 auto'}}>
      <div style={{marginBottom:20}}><div className="page-header-label">Action Rail</div><div className="page-title">Tasks & Decisions</div></div>
      <div className="grid-4" style={{marginBottom:18}}>
        {[['Overdue',counts.overdue,'#fef2f2','#dc2626','#fca5a5'],['P1 Open',counts.p1,'#fffbeb','#d97706','#fcd34d'],['In Progress',counts.inProgress,'#eff6ff','#2563eb','#93c5fd'],['Outstanding',counts.outstanding,'#fff','#3D1F0D','rgba(61,31,13,.12)']].map(([l,v,bg,tc,bc],i)=>{
          const animated=useAnimatedNumber(v,800,i*80);
          return(
            <div key={l} className="card card-flat" style={{padding:'16px 18px',background:bg,borderColor:bc,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*60}ms`}}>
              <div style={{fontSize:28,fontWeight:800,color:tc,fontVariantNumeric:'tabular-nums'}}>{animated}</div>
              <div style={{fontSize:11,fontWeight:600,color:tc,opacity:.7,marginTop:2}}>{l}</div>
            </div>
          );
        })}
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:16,alignItems:'center'}}>
        <span style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>Filter:</span>
        {['All','Alert','Task','Decision','Review'].map(t=>(
          <button key={t} onClick={()=>setFType(t)} style={{padding:'6px 12px',borderRadius:8,border:'1px solid rgba(61,31,13,.12)',background:fType===t?'#3D1F0D':'#fff',color:fType===t?'#fff':'rgba(61,31,13,.55)',fontWeight:600,fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>{t}</button>
        ))}
        <span style={{marginLeft:8}}/>
        {['All','P1','P2','P3'].map(p=>(
          <button key={p} onClick={()=>setFPri(p)} style={{padding:'6px 12px',borderRadius:8,border:'1px solid rgba(61,31,13,.12)',background:fPri===p?'#3D1F0D':'#fff',color:fPri===p?'#fff':'rgba(61,31,13,.55)',fontWeight:600,fontSize:12,cursor:'pointer',fontFamily:'inherit'}}>{p}</button>
        ))}
        {/* TEAM-1 Phase D — view switcher (PRD §5.1) */}
        <span style={{marginLeft:'auto',display:'inline-flex',gap:4,background:'rgba(61,31,13,.05)',borderRadius:8,padding:3}}>
          {[['list','☰ List'],['kanban','▤ Kanban'],['calendar','▦ Calendar'],['timeline','▭ Timeline']].map(([v,lbl])=>(
            <button key={v} onClick={()=>setView(v)} style={{padding:'4px 10px',borderRadius:6,border:'none',background:view===v?'#fff':'transparent',color:view===v?'#3D1F0D':'rgba(61,31,13,.5)',fontWeight:600,fontSize:11,cursor:'pointer',fontFamily:'inherit',boxShadow:view===v?'0 1px 3px rgba(0,0,0,.1)':'none'}}>{lbl}</button>
          ))}
        </span>
        <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{sorted.length} items</span>
      </div>
      {view==='kanban'&&<ActionKanban actions={_a.filter(a=>fType==='All'||a.type===fType).filter(a=>fPri==='All'||a.priority===fPri)} onPatch={patchAction} updating={updating}/>}
      {view==='calendar'&&<ActionCalendar actions={_a.filter(a=>fType==='All'||a.type===fType).filter(a=>fPri==='All'||a.priority===fPri)}/>}
      {view==='timeline'&&<ActionTimeline actions={sorted}/>}
      {view==='list'&&<div style={{display:'flex',flexDirection:'column',gap:10}}>
        {sorted.map(a=>{
          const p=priorityColors[a.priority];
          const isOD=a.status==='Overdue';
          const isExp=expanded.has(a.id);
          const agingColor=a.daysOpen>=7?'#dc2626':a.daysOpen>=3?'#d97706':'rgba(61,31,13,.35)';
          return(
            <div key={a.id} className="card" style={{borderLeft:`4px solid ${isOD?'#dc2626':p.bg}`,overflow:'hidden',background:isOD?'#fef9f9':'#fff'}}>
              <div onClick={()=>toggle(a.id)} style={{display:'flex',alignItems:'flex-start',gap:12,padding:'12px 16px',cursor:'pointer'}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:4,flexShrink:0}}>
                  <span style={{fontSize:10,fontWeight:800,padding:'2px 6px',borderRadius:4,background:p.bg,color:'#fff'}}>{a.priority}</span>
                  <span style={{fontSize:11,color:p.text,marginTop:2}}>{a.type==='Alert'?'⚠':a.type==='Task'?'✓':a.type==='Decision'?'💬':'🕐'}</span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:8,flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:700,color:isOD?'#dc2626':'#3D1F0D',lineHeight:1.4,flex:1}}>{a.title}</span>
                    {isOD&&<span className="overdue-pulse" style={{background:'#ef4444',color:'#fff',fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:999,flexShrink:0}}>OVERDUE</span>}
                    {a.escalated&&!isOD&&<span style={{background:'rgba(220,38,38,.1)',color:'#dc2626',fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:999,flexShrink:0,border:'1px solid rgba(220,38,38,.2)'}}>ESCALATED</span>}
                  </div>
                  <div style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:10,marginTop:5}}>
                    <span className={agentBadge(a.agent)}>{a.agent}</span>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>👤 {a.owner}</span>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{a.asset}</span>
                    {a.daysOpen>0&&<span style={{fontSize:10,fontWeight:600,color:agingColor,background:`${agingColor}18`,padding:'1px 6px',borderRadius:4,border:`1px solid ${agingColor}33`}}>{a.daysOpen}d open</span>}
                    {a.resolutionNote&&<span style={{fontSize:10,color:'#16a34a',fontWeight:600}}>📝 Note</span>}
                    <span style={{marginLeft:'auto',fontSize:11,fontWeight:600,color:isOD?'#dc2626':'rgba(61,31,13,.4)'}}>Due {a.due}</span>
                  </div>
                </div>
                <span style={{fontSize:14,color:'rgba(61,31,13,.3)',flexShrink:0,marginTop:2}}>{isExp?'▾':'›'}</span>
              </div>
              {isExp&&(
                <div style={{padding:'0 16px 14px',borderTop:'1px solid rgba(61,31,13,.07)'}}>
                  <p style={{fontSize:13,color:'rgba(61,31,13,.7)',lineHeight:1.6,marginTop:12,marginBottom:12}}>{a.desc}</p>
                  {a.resolutionNote&&(
                    <div style={{marginBottom:12,padding:'10px 12px',background:'rgba(22,163,74,.05)',border:'1px solid rgba(22,163,74,.2)',borderRadius:8}}>
                      <div style={{fontSize:10,fontWeight:700,color:'#16a34a',marginBottom:4,textTransform:'uppercase',letterSpacing:'.05em'}}>Resolution Note</div>
                      <div style={{fontSize:12,color:'rgba(61,31,13,.7)',lineHeight:1.5}}>{a.resolutionNote}</div>
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                    <button onClick={e=>{e.stopPropagation();patchAction(a.id,'Done');}} disabled={updating.has(a.id)} className="btn-primary" style={{fontSize:12,padding:'7px 14px',opacity:updating.has(a.id)?.5:1}}>{updating.has(a.id)?'Updating…':'✓ Mark Done'}</button>
                    {a.status!=='In Progress'&&<button onClick={e=>{e.stopPropagation();patchAction(a.id,'In Progress');}} disabled={updating.has(a.id)} className="btn-ghost" style={{fontSize:12,padding:'7px 14px'}}>Start</button>}
                    <button onClick={e=>{e.stopPropagation();openNotesModal(a);}} className="btn-ghost" style={{fontSize:12,padding:'7px 14px'}}>📝 {a.resolutionNote?'Edit Note':'Add Note'}</button>
                    <span style={{marginLeft:'auto',fontSize:11,color:agingColor,fontWeight:a.daysOpen>=7?700:400}}>{a.daysOpen>0?`Open ${a.daysOpen}d`:a.status}</span>
                  </div>
                  {/* TEAM-1 Phase D — assignee + comments */}
                  <ActionCollab action={a} authFetch={authFetch} refresh={refresh} addToast={addToast}/>
                </div>
              )}
            </div>
          );
        })}
      </div>}
    </div>

    {/* ── EW5 — outcome verification prompt (after closing an experience action) ── */}
    {verifyAction&&(
      <FormModal title="Verify outcome" onClose={()=>setVerifyAction(null)} onSubmit={submitActionOutcome} submitting={voSaving}>
        <div style={{fontSize:12,color:'rgba(61,31,13,.6)',marginBottom:10,lineHeight:1.5}}>“<b>{verifyAction.title}</b>” is complete. Re-measure the standard it was meant to move — the before→after delta is recorded as an immutable <b>Decision</b> and updates TWS.</div>
        <Field label="Standard">
          <select style={inputStyle} value={voForm.standard||''} onChange={e=>{setVoMsg('');setVoForm(f=>({...f,standard:e.target.value}));}}>
            <option value="">Select…</option>
            {EXPERIENCE_STDS.map(([k,label])=>(<option key={k} value={k}>{label}</option>))}
          </select>
        </Field>
        <Field label="New measured value (0–100)">
          <input type="number" min="0" max="100" style={inputStyle} value={voForm.after||''} placeholder="0–100" onChange={e=>{setVoMsg('');setVoForm(f=>({...f,after:e.target.value}));}}/>
        </Field>
        <Field label="Note (optional)">
          <input style={inputStyle} value={voForm.note||''} placeholder="What changed" onChange={e=>setVoForm(f=>({...f,note:e.target.value}))}/>
        </Field>
        {voMsg&&<div style={{marginTop:10,fontSize:12,color:'#9A3412'}}>{voMsg}</div>}
      </FormModal>
    )}

    {/* ── Resolution Notes Modal ──────────────────────────────── */}
    {notesModal&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setNotesModal(null);}}>
        <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:460,boxShadow:'0 24px 60px rgba(0,0,0,.18)',overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:28,height:28,borderRadius:8,background:'rgba(22,163,74,.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>📝</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Resolution Note</div>
              <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{notesModal.title}</div>
            </div>
            <button onClick={()=>setNotesModal(null)} style={{border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.35)',cursor:'pointer',padding:4,lineHeight:1}}>×</button>
          </div>
          <div style={{padding:'20px'}}>
            <div style={{fontSize:11,color:'rgba(61,31,13,.45)',marginBottom:12,lineHeight:1.5}}>Describe the resolution, blocker, or next step. Logged to Sentry audit trail.</div>
            <textarea
              value={notesDraft}
              onChange={e=>setNotesDraft(e.target.value)}
              rows={5}
              autoFocus
              style={{width:'100%',padding:'10px 12px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:13,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9',resize:'vertical',boxSizing:'border-box',lineHeight:1.5}}
              placeholder="e.g. Waiting on landlord response to counter. Marcus to follow up Monday."
            />
            <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:16}}>
              <button onClick={()=>setNotesModal(null)} style={{padding:'9px 18px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,background:'transparent',fontSize:12,fontWeight:600,color:'rgba(61,31,13,.55)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
              <button onClick={saveNote} disabled={savingNote||!notesDraft.trim()} style={{padding:'9px 18px',border:'none',borderRadius:8,background:savingNote||!notesDraft.trim()?'rgba(22,163,74,.4)':'#16a34a',color:'#fff',fontSize:12,fontWeight:700,cursor:savingNote||!notesDraft.trim()?'not-allowed':'pointer',fontFamily:'inherit'}}>{savingNote?'Saving…':'Save Note'}</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </React.Fragment>
  );
}

// Doc 2 §3/§6 — 24-month lease rollover ribbon. Tenants plotted by expiry on a
// 24-month axis, colour-coded by urgency (Red ≤90d · Amber ≤180d · Green >180d).
// Click a bar → lease summary + Sage renewal-probability estimate + Prepare brief.
function leaseUrgency(days){
  if(days<=90)return{tier:'red',color:'#dc2626',prob:'Low — act now',risk:'High'};
  if(days<=180)return{tier:'amber',color:'#B8860B',prob:'Medium',risk:'Medium'};
  return{tier:'green',color:'#059669',prob:'Higher',risk:'Low'};
}
function LeaseRibbon({locations,onNav}){
  const now=Date.now();
  const rows=(Array.isArray(locations)?locations:[])
    .map(l=>({l,days:Math.round((new Date(l.leaseEnd)-now)/864e5)}))
    .filter(x=>x.days>0&&x.days<=730)             // within the 24-month window
    .sort((a,b)=>a.days-b.days);
  const [sel,setSel]=useState(null);
  const beyond=(Array.isArray(locations)?locations:[]).filter(l=>{const d=(new Date(l.leaseEnd)-now)/864e5;return d>730;}).length;
  return(
    <div className="card" style={{padding:18,marginBottom:18}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:4}}>
        <div style={{fontWeight:700,fontSize:13}}>Lease Rollover — 24-Month Ribbon</div>
        <div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{rows.length} expiring within 24 months{beyond?` · ${beyond} beyond`:''}</div>
      </div>
      <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:14}}>Colour = urgency · click a tenant for the renewal read</div>
      {rows.length===0
        ? <div style={{fontSize:12.5,color:'rgba(61,31,13,.5)',padding:'12px 0'}}>No leases expiring in the next 24 months.</div>
        : <div style={{position:'relative',paddingTop:6}}>
            {/* month axis */}
            <div style={{position:'relative',height:18,borderBottom:'1px solid rgba(61,31,13,.12)',marginBottom:8}}>
              {[0,6,12,18,24].map(m=>(<span key={m} style={{position:'absolute',left:`${m/24*100}%`,fontSize:9,fontFamily:'monospace',color:'rgba(61,31,13,.4)',transform:'translateX(-50%)'}}>{m}mo</span>))}
            </div>
            {rows.map(({l,days})=>{
              const u=leaseUrgency(days),left=Math.min(98,Math.max(0,days/730*100));
              return(
                <div key={l.id||l.name} onClick={()=>setSel(sel&&sel.l===l?null:{l,days,u})} style={{position:'relative',height:26,marginBottom:6,cursor:'pointer'}} title={`${l.name} · ${days}d`}>
                  <span style={{position:'absolute',left:0,top:5,fontSize:11,color:'rgba(61,31,13,.6)',width:'30%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.name}</span>
                  <span style={{position:'absolute',left:`calc(32% + ${left*0.66}%)`,top:6,width:12,height:12,borderRadius:3,background:u.color,boxShadow:`0 0 0 3px ${u.color}22`}}/>
                </div>
              );
            })}
            <div style={{display:'flex',gap:14,marginTop:10}}>
              {[['#dc2626','0–90 days'],['#B8860B','91–180 days'],['#059669','180+ days']].map(([c,t])=>(<div key={t} style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:10,height:10,borderRadius:2,background:c}}/><span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{t}</span></div>))}
            </div>
          </div>}
      {sel&&(
        <div className="joy-rise" style={{marginTop:14,padding:'14px 16px',border:`1px solid ${sel.u.color}44`,borderLeft:`4px solid ${sel.u.color}`,borderRadius:'0 6px 6px 0',background:sel.u.color+'08'}}>
          <div style={{fontFamily:'Georgia,serif',fontSize:16,color:'#3D1F0D',marginBottom:6}}>{sel.l.name}</div>
          <div style={{display:'flex',gap:18,flexWrap:'wrap',fontSize:12,color:'rgba(61,31,13,.7)',marginBottom:10}}>
            <span>Expires in <b>{sel.days} days</b> ({sel.l.leaseEnd})</span>
            {sel.l.sf&&<span><b>{Number(sel.l.sf).toLocaleString()}</b> SF</span>}
            {sel.l.rent&&<span><b>{ccySym(sel.l.currency)}{Number(sel.l.rent).toLocaleString()}</b>/yr</span>}
            <span>Renewal probability: <b style={{color:sel.u.color}}>{sel.u.prob}</b></span>
          </div>
          <button onClick={()=>onNav&&onNav('sage')} style={{padding:'8px 16px',background:'#7C3AED',color:'#fff',border:'none',borderRadius:5,fontSize:12.5,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Prepare Renewal Brief</button>
        </div>
      )}
    </div>
  );
}

function FinancialPage({onNav}){
  const {portfolio:_p=PORTFOLIO,portfolioIsLive,isDemo}=useData();
  const totalRent=_p.reduce((s,l)=>s+l.rent,0);
  const totalBase=Math.round(totalRent*.78);const totalOpex=Math.round(totalRent*.22);
  const totalMkt=Math.round(totalRent*1.18);const totalDelta=totalMkt-totalRent;
  const locCount=_p.length;

  // Renewal exposure — leases expiring within 24 months
  const now=new Date();
  const expiringRent=_p.filter(l=>{
    if(!l.leaseEnd)return false;
    const days=(new Date(l.leaseEnd)-now)/(1000*60*60*24);
    return days>0&&days<=730;
  }).reduce((s,l)=>s+l.rent,0);
  const renewalDelta=Math.round(expiringRent*0.18);
  const renewalPct=expiringRent>0?18:0;

  // ASC 842 lease liability schedule — derived from live lease data
  // IBR = 6%; ROU asset amortises at 95% of liability rate (straight-line vs effective interest)
  const IBR=0.06;
  const LIABILITY=React.useMemo(()=>{
    const YEARS=[2026,2027,2028,2029,2030];
    return YEARS.map((yr,i)=>{
      // Undiscounted remaining obligation: Σ rent × years remaining from yr
      const undiscounted=_p.reduce((sum,loc)=>{
        if(!loc.leaseEnd)return sum;
        const endYr=parseInt(loc.leaseEnd.slice(0,4),10);
        const yrsLeft=Math.max(0,endYr-yr+1);
        return sum+loc.rent*yrsLeft;
      },0);
      // PV at IBR — discount back from each future year
      const pv=_p.reduce((sum,loc)=>{
        if(!loc.leaseEnd)return sum;
        const endYr=parseInt(loc.leaseEnd.slice(0,4),10);
        let locPV=0;
        for(let y=yr;y<=Math.min(endYr,yr+15);y++){
          locPV+=loc.rent/Math.pow(1+IBR,y-yr+1);
        }
        return sum+locPV;
      },0);
      const l=Math.round(pv/100000)/10;          // $M, 1dp
      const r=Math.round(l*(1-i*0.04)*0.92*10)/10; // ROU asset amortises slightly faster
      return{yr:String(yr),l:Math.max(l,0),r:Math.max(r,0)};
    });
  },[_p]);

  const [scribeExporting,setScribeExporting]=useState(false);
  const [scribeReport,setScribeReport]=useState(null); // {report, generatedAt, locCount, totalSF}
  const authFetchFin=useAuthFetch();

  // Derived TI data — always sourced from PORTFOLIO constant (ti field not yet in DB)
  // PRD SETTING-SENSE-1 §UX-1 thresholds: P3 Advisory ≤365 days, P2 Watch ≤180 days, P1 Critical ≤90 days
  const tiLocations=React.useMemo(()=>{
    // Demo account uses the curated PORTFOLIO (rich TI data); real accounts read
    // their own gated portfolio — so the TI tracker is empty until they have TI data.
    return (isDemo?PORTFOLIO:_p).filter(l=>l.ti).map(l=>{
      const remaining=l.ti.total-l.ti.applied;
      const daysLeft=Math.round((new Date(l.ti.expiry)-now)/(864e5));
      const monthsLeft=Math.max(daysLeft/30,0.1);
      const rateRequired=Math.round(remaining/monthsLeft);
      const curRate=l.ti.appliedMonthly||0;
      const projectedUnused=Math.max(remaining-(curRate*monthsLeft),0);
      const p1Critical=remaining>200000&&daysLeft<90;
      const p2Watch=remaining>200000&&daysLeft<180&&!p1Critical;
      const p3Advisory=remaining>200000&&daysLeft<365&&!p1Critical&&!p2Watch;
      const tier=p1Critical?'p1':p2Watch?'p2':p3Advisory?'p3':'ok';
      return{...l,tiRemaining:remaining,tiDaysLeft:daysLeft,tiRateRequired:rateRequired,tiCurRate:curRate,tiProjectedUnused:projectedUnused,tiTier:tier,tiAtRisk:tier!=='ok',tiCritical:p1Critical};
    });
  },[now,isDemo,_p]);

  // CSV fallback export (data-only, finance-team-readable)
  const handleCSVExport=()=>{
    const rows=[
      ['STAGE STATUS — FINANCIAL SURFACE DATA EXPORT'],
      [`Generated: ${new Date().toLocaleDateString()}`],[],
      ['PORTFOLIO SUMMARY'],
      ['Total Annual Rent','Base Rent','Operating Expenses','Market Rate (est)','Renewal Exposure Delta'],
      [totalRent,totalBase,totalOpex,totalMkt,totalDelta],[],
      ['LOCATION DETAIL — RENT ROLL'],
      ['Location','Market','SF','Base Rent','OpEx','Total Rent','$/SF','Lease End'],
      ..._p.map(l=>{const b=Math.round(l.rent*.78);const o=Math.round(l.rent*.22);return[l.name,l.market,l.sf,b,o,l.rent,Math.round(l.rent/l.sf),l.leaseEnd];}),
      [],
      ['ASC 842 LEASE LIABILITY SCHEDULE (USD M) — IBR 6%'],
      ['Year','Lease Liability ($M)','ROU Asset ($M)'],
      ...LIABILITY.map(r=>[r.yr,r.l,r.r]),
      [],
      ['TI ALLOWANCE TRACKER'],
      ['Location','TI Total ($)','Applied to Date ($)','Remaining ($)','Use Window Expires','Days Remaining','Rate Required ($/mo)','Cur. Rate 3-mo avg ($/mo)','Proj. Unused at Expiry ($)','Status'],
      ...tiLocations.map(l=>[l.name,l.ti.total,l.ti.applied,l.tiRemaining,l.ti.expiry,l.tiDaysLeft,l.tiRateRequired,l.tiCurRate||'',Math.round(l.tiProjectedUnused)||0,{p1:'P1 Critical',p2:'P2 Watch',p3:'P3 Advisory',ok:'On Track'}[l.tiTier]]),
      [],
      ['CAPITAL PROJECTS'],
      ['Project','Location','Type','Total Budget ($)','Committed ($)','Invoiced ($)','Paid ($)','Variance ($)','Target Complete','Status'],
      ...CAPITAL_PROJECTS.map(p=>[p.name,p.location,p.type,p.totalBudget,p.committed,p.invoiced,p.paid,p.committed-p.invoiced,p.targetComplete,p.status]),
    ];
    const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    a.download=`financial-data-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // Scribe board brief — calls Claude via /api/financial/report
  const handleScribeExport=async()=>{
    setScribeExporting(true);
    setScribeReport(null);
    try{
      const r=await authFetchFin('/api/financial/report',{method:'POST'});
      if(r?.ok&&r?.report){
        setScribeReport(r);
      }else{
        alert('Scribe brief generation failed. Try again.');
      }
    }catch(e){alert(`Scribe export failed: ${e.message}`);}
    finally{setScribeExporting(false);}
  };

  const downloadScribeReport=()=>{
    if(!scribeReport)return;
    const txt=`STAGE FINANCIAL BRIEF — SCRIBE\nGenerated: ${new Date(scribeReport.generatedAt).toLocaleString()}\n\n${scribeReport.report}\n\n---\nPortfolio: ${scribeReport.locCount} locations · ${scribeReport.totalSF?.toLocaleString()} SF · $${(scribeReport.totalRent/1e6).toFixed(1)}M annual rent`;
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([txt],{type:'text/plain'}));
    a.download=`scribe-financial-brief-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
  };

  return(
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      {isDemo&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,background:'rgba(217,119,6,.08)',border:'1px solid rgba(217,119,6,.25)',marginBottom:16,fontSize:12,color:'#92400e'}}>
          <span style={{fontWeight:700,background:'#D97706',color:'#fff',borderRadius:4,padding:'1px 7px',fontSize:10,letterSpacing:'.06em'}}>DEMO DATA</span>
          <span>Showing illustrative portfolio — figures are not live. This is the Stage research/demo workspace.</span>
        </div>
      )}
      {!isDemo&&!_p.length&&(
        <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,background:'rgba(61,31,13,.05)',border:'1px solid rgba(61,31,13,.12)',marginBottom:16,fontSize:12,color:'rgba(61,31,13,.65)'}}>
          <span>No portfolio data yet — connect your portfolio in Portfolio Spine to see real cost figures here.</span>
        </div>
      )}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20}}>
        <div><div className="page-header-label">Financial Surface</div><div className="page-title">Total Cost of Occupancy</div></div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn-ghost" style={{fontSize:12}} onClick={handleCSVExport}>↓ CSV Data</button>
          <button className="btn-primary" style={{fontSize:12,display:'flex',alignItems:'center',gap:6}} onClick={handleScribeExport} disabled={scribeExporting}>
            {scribeExporting?<span style={{display:'inline-block',width:12,height:12,border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>:'✦'}
            {scribeExporting?'Generating…':'Scribe Brief'}
          </button>
        </div>
      </div>
      {/* Scribe board brief — appears after generation */}
      {scribeReport&&(
        <div className="card" style={{padding:20,borderLeft:'4px solid #D97706',background:'rgba(217,119,6,.03)',marginBottom:18}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:14}}>✦</span>
              <span style={{fontSize:11,fontWeight:700,color:'#D97706',textTransform:'uppercase',letterSpacing:'.07em'}}>Scribe Board Brief</span>
              <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:4,background:'rgba(217,119,6,.1)',color:'#D97706',border:'1px solid rgba(217,119,6,.2)'}}>GENERATED</span>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>Generated {new Date(scribeReport.generatedAt).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'})}</span>
              <button onClick={downloadScribeReport} style={{border:'1px solid rgba(217,119,6,.3)',borderRadius:6,background:'transparent',fontSize:11,color:'#D97706',padding:'3px 10px',cursor:'pointer',fontFamily:'inherit',fontWeight:600}}>↓ Download</button>
              <button onClick={()=>setScribeReport(null)} style={{border:'none',background:'none',fontSize:16,color:'rgba(61,31,13,.3)',cursor:'pointer',padding:'0 4px',lineHeight:1}}>×</button>
            </div>
          </div>
          <div style={{fontSize:13,lineHeight:1.75,color:'rgba(61,31,13,.85)',whiteSpace:'pre-wrap'}}>{scribeReport.report}</div>
        </div>
      )}
      <div className="grid-4" style={{marginBottom:18}}>
        <KPI label="Total Annual Rent" value={fmt(totalRent)} sub={`All ${locCount} location${locCount!==1?'s':''}`} delay={0}/>
        <KPI label="Base Rent" value={fmt(totalBase)} sub="78% of total" delay={70}/>
        <KPI label="Operating Expenses" value={fmt(totalOpex)} sub="22% of total" delay={140}/>
        <KPI label="Renewal Exposure" value={`+${fmt(totalDelta)}`} sub="vs current at market" warn delay={210}/>
      </div>
      {expiringRent>0&&<div className="card" style={{padding:16,borderLeft:'4px solid #ef4444',background:'rgba(239,68,68,.04)',marginBottom:18}}>
        <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>
          <span style={{fontSize:16}}>⚠️</span>
          <div><b style={{fontSize:13,color:'#3D1F0D'}}>Renewal cost exposure: <span style={{color:'#dc2626'}}>+{fmt(renewalDelta)} above current</span></b><p style={{fontSize:12,color:'rgba(61,31,13,.6)',marginTop:3}}>At market rates, renewing leases expiring within 24 months will cost ~{renewalPct}% more. Based on {_p.filter(l=>{const d=(new Date(l.leaseEnd)-now)/(864e5);return d>0&&d<=730;}).length} expiring location{_p.filter(l=>{const d=(new Date(l.leaseEnd)-now)/(864e5);return d>0&&d<=730;}).length!==1?'s':''}.</p></div>
        </div>
      </div>}
      {/* Charts */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:18}}>
        <div className="card" style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>Rent Roll by Location ($K/yr)</div>
          <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>Base rent + operating expenses</div>
          <BarChart data={_p.map(l=>({label:l.name.split(' ')[0],Base:Math.round(l.rent*.78/1000),OpEx:Math.round(l.rent*.22/1000)}))} keys={['Base','OpEx']} colors={['#3D1F0D','#E8813A']} height={160}/>
          <div style={{display:'flex',gap:14,marginTop:8}}>
            {[['#3D1F0D','Base Rent'],['#E8813A','OpEx']].map(([c,l])=><div key={l} style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:10,height:10,borderRadius:2,background:c,display:'inline-block'}}/><span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{l}</span></div>)}
          </div>
        </div>
        <div className="card" style={{padding:18}}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>ASC 842 Lease Liability Schedule ($M)</div>
          <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>Present value of future obligations</div>
          <LineChart data={LIABILITY.map(d=>({week:d.yr,'Liability':d.l,'ROU Asset':d.r}))} keys={['Liability','ROU Asset']} colors={['#8b0020','#E8813A']} height={160}/>
          <div style={{display:'flex',gap:14,marginTop:8}}>
            {[['#8b0020','Lease Liability'],['#E8813A','ROU Asset']].map(([c,l])=><div key={l} style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:12,height:3,background:c,borderRadius:2,display:'inline-block'}}/><span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{l}</span></div>)}
          </div>
        </div>
      </div>
      {/* Doc 2 — 24-month lease rollover ribbon */}
      <LeaseRibbon locations={_p} onNav={onNav}/>

      {/* ASC 842 Accrual Brief — demo-only hardcoded narrative */}
      {isDemo&&<div className="card" style={{padding:16,marginBottom:18,borderLeft:'4px solid #7C3AED',background:'rgba(124,58,237,.03)'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'#7C3AED',letterSpacing:'.1em',textTransform:'uppercase',marginBottom:2}}>Monthly Review · Strat</div>
            <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>ASC 842 / IFRS 16 Accrual Snapshot — May 2026</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.55)',marginTop:2}}>3 locations have events affecting June accrual · 1 Penn Plaza escalation · Willis OpEx true-up · Houston termination fee</div>
          </div>
          <div style={{display:'flex',gap:10}}>
            {[['1 Penn Plaza','Base escalation +3% Jun 1','#ef4444'],['Willis Tower','CAM true-up est. $42K','#d97706'],['1000 Main St','Termination fee: 3 mo. rent','#2563EB']].map(([loc,note,col])=>(
              <div key={loc} style={{background:'rgba(245,237,224,.8)',borderRadius:8,padding:'8px 12px',fontSize:11,minWidth:140}}>
                <div style={{fontWeight:700,color:'#3D1F0D',marginBottom:2}}>{loc}</div>
                <div style={{color:col,fontWeight:600,fontSize:10}}>{note}</div>
              </div>
            ))}
          </div>
        </div>
      </div>}

      {/* TI Allowance Tracker */}
      {tiLocations.length>0&&(
        <div style={{marginBottom:18}}>
          <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',marginBottom:10}}>
            <div>
              <div className="page-header-label">TI Allowance Tracker</div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.45)',marginTop:1}}>Scribe-monitored use windows · balance tracked by Strat · exported in CSV</div>
            </div>
            <div style={{display:'flex',gap:6}}>
              {['p1','p2','p3'].map(tier=>{
                const n=tiLocations.filter(l=>l.tiTier===tier).length;
                if(!n)return null;
                const cfg={p1:{color:'#ef4444',label:'P1 Critical'},p2:{color:'#f97316',label:'P2 Watch'},p3:{color:'#d97706',label:'P3 Advisory'}}[tier];
                return <span key={tier} style={{fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:20,background:`${cfg.color}14`,color:cfg.color,border:`1px solid ${cfg.color}44`}}>{n} {cfg.label}</span>;
              })}
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:12}}>
            {tiLocations.map(l=>{
              const pct=Math.round(l.ti.applied/l.ti.total*100);
              const tierCfg={
                p1:{color:'#ef4444',bg:'#fef2f2',border:'rgba(239,68,68,.3)',label:'P1 Critical'},
                p2:{color:'#f97316',bg:'rgba(249,115,22,.08)',border:'rgba(249,115,22,.25)',label:'P2 Watch'},
                p3:{color:'#d97706',bg:'rgba(217,119,6,.08)',border:'rgba(217,119,6,.25)',label:'P3 Advisory'},
                ok:{color:'#16a34a',bg:'rgba(22,163,74,.08)',border:'rgba(22,163,74,.2)',label:'On Track'},
              }[l.tiTier];
              return(
                <div key={l.id} className="card" style={{padding:16,borderLeft:`4px solid ${tierCfg.color}`}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>{l.name}</div>
                      <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginTop:1}}>{l.ti.mechanism==='credit'?'Rent credit':'Cash disbursement'} · {l.market}</div>
                    </div>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:4,background:tierCfg.bg,color:tierCfg.color,border:`1px solid ${tierCfg.border}`,whiteSpace:'nowrap'}}>{l.tiTier==='p1'&&'⚠ '}{tierCfg.label}</span>
                  </div>
                  <div style={{marginBottom:8}}>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'rgba(61,31,13,.5)',marginBottom:4}}>
                      <span>Applied: {fmt(l.ti.applied)} of {fmt(l.ti.total)}</span>
                      <span>{pct}%</span>
                    </div>
                    <div style={{height:5,background:'rgba(61,31,13,.08)',borderRadius:3,overflow:'hidden'}}>
                      <div style={{height:'100%',width:`${pct}%`,background:tierCfg.color,borderRadius:3,transition:'width .4s'}}/>
                    </div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                    {[
                      ['Remaining',fmt(l.tiRemaining)],
                      ['Expiry',l.ti.expiry],
                      ['Days Left',l.tiDaysLeft.toLocaleString()],
                      ['Rate Required',`${fmt(l.tiRateRequired)}/mo`],
                      ['Cur. Rate (3-mo avg)',l.tiCurRate>0?`${fmt(l.tiCurRate)}/mo`:'—'],
                      ['Proj. Unused at Expiry',l.tiProjectedUnused>0?fmt(Math.round(l.tiProjectedUnused)):'$0'],
                    ].map(([k,v])=>(
                      <div key={k} style={{background:'rgba(245,237,224,.6)',borderRadius:6,padding:'6px 9px'}}>
                        <div style={{fontSize:9,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{k}</div>
                        <div style={{fontSize:12,fontWeight:700,color:(k==='Rate Required'||k==='Proj. Unused at Expiry')&&l.tiAtRisk?tierCfg.color:'#3D1F0D',fontFamily:'monospace'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {l.ti.eligible&&<div style={{marginTop:8,fontSize:10,color:'rgba(61,31,13,.4)',borderTop:'1px solid rgba(61,31,13,.06)',paddingTop:6}}>Eligible: {l.ti.eligible}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Capital Projects — Phase 2 · Scope Intelligence (demo-only: hidden for real accounts) */}
      <div style={{marginBottom:18,display:isDemo?undefined:'none'}}>
        <div className="page-header-label" style={{marginBottom:10}}>Capital Projects</div>
        {/* Budget Risk Alert banner */}
        {CAPITAL_PROJECTS_V2.some(p=>p.burnAlert)&&(
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'rgba(239,68,68,.06)',border:'1px solid rgba(239,68,68,.2)',borderRadius:8,marginBottom:10}}>
            <span style={{fontSize:16}}>🔴</span>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:'#dc2626'}}>Budget Risk Alert · Scope</div>
              <div style={{fontSize:10,color:'rgba(61,31,13,.55)',lineHeight:1.4}}>{CAPITAL_PROJECTS_V2.filter(p=>p.burnAlert).map(p=>p.name).join(', ')} — burn rate exceeds drawdown schedule. Scope review required before next invoice.</div>
            </div>
          </div>
        )}
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'12px 18px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div>
              <span style={{fontWeight:700,fontSize:13}}>Active Capital Projects</span>
              <span style={{marginLeft:8,fontSize:10,color:'rgba(61,31,13,.4)'}}>Scope Phase 2 · Milestone tracking, change orders, day-one readiness</span>
            </div>
            <span style={{fontSize:10,background:'rgba(5,150,105,.08)',color:'#059669',border:'1px solid rgba(5,150,105,.2)',borderRadius:4,padding:'2px 8px',fontWeight:700}}>Scope · Active</span>
          </div>
          {/* TEAM-1 Phase D §5.3 — grid protections legend */}
          <div style={{display:'flex',alignItems:'center',gap:14,padding:'6px 18px',fontSize:10,color:'rgba(61,31,13,.45)'}}>
            <span>🔒 Variance is calculated (Committed − Invoiced) — read-only</span>
            <span style={{color:'rgba(61,31,13,.35)'}}>Header frozen on scroll</span>
          </div>
          {/* §5.3: frozen header (sticky thead) + locked calculated Variance column */}
          <div style={{overflowX:'auto',maxHeight:360,overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'rgba(245,237,224,.6)',borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                  {['Project','Type','Total Budget','Committed','Invoiced','Paid','Variance','PM','Target Complete','Status'].map(h=>{
                    const locked=h==='Variance';
                    return(
                    <th key={h} title={locked?'Calculated field (Committed − Invoiced) — read-only':undefined} style={{position:'sticky',top:0,zIndex:1,background:'#F5EDE0',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.05em',whiteSpace:'nowrap',boxShadow:'0 1px 0 rgba(61,31,13,.1)'}}>{locked?`🔒 ${h}`:h}</th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {CAPITAL_PROJECTS_V2.map(p=>{
                  const variance=p.committed-p.invoiced;
                  const burnPct=Math.round(p.paid/p.totalBudget*100);
                  const statusColor=p.status==='on-track'?'#16a34a':p.status==='at-risk'?'#f97316':'#6b7280';
                  return(
                    <tr key={p.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <td style={{fontWeight:600,fontSize:12}}>{p.name}{p.burnAlert&&<span style={{marginLeft:6,fontSize:9,color:'#dc2626',fontWeight:700}}>⚠ Alert</span>}</td>
                      <td><span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:p.type==='Landlord Buildout'?'rgba(37,99,235,.08)':'rgba(217,119,6,.08)',color:p.type==='Landlord Buildout'?'#2563EB':'#D97706',fontWeight:600,border:`1px solid ${p.type==='Landlord Buildout'?'rgba(37,99,235,.2)':'rgba(217,119,6,.2)'}`}}>{p.type}</span></td>
                      <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(p.totalBudget)}</td>
                      <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(p.committed)}</td>
                      <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(p.invoiced)}</td>
                      <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(p.paid)}</td>
                      <td title="Calculated field (Committed − Invoiced) — read-only" style={{fontFamily:'monospace',fontSize:12,color:variance>0?'#16a34a':'#ef4444',fontWeight:600,background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>{variance>=0?'+':''}{fmt(variance)} <span style={{fontSize:9,opacity:.5}}>🔒</span></td>
                      <td style={{fontSize:11,color:'rgba(61,31,13,.6)'}}>{p.pm}</td>
                      <td style={{fontSize:12,whiteSpace:'nowrap',color:'rgba(61,31,13,.6)'}}>{p.targetComplete}</td>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:6}}>
                          <div style={{height:4,width:48,background:'rgba(61,31,13,.08)',borderRadius:2,overflow:'hidden'}}>
                            <div style={{height:'100%',width:`${burnPct}%`,background:statusColor,borderRadius:2}}/>
                          </div>
                          <span style={{fontSize:10,fontWeight:700,color:statusColor}}>{p.status==='on-track'?'On Track':p.status==='at-risk'?'At Risk':'Planning'}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Per-project expanded detail: milestones + change orders + day-one readiness */}
          {CAPITAL_PROJECTS_V2.map((p,pi)=>{
            const pendingCOs=(p.changeOrders||[]).filter(co=>co.status==='pending');
            const approvedCOs=(p.changeOrders||[]).filter(co=>co.status==='approved');
            const coTotal=(p.changeOrders||[]).filter(co=>co.status==='approved').reduce((s,co)=>s+co.amount,0);
            const d1Done=(p.dayOneReadiness||[]).filter(d=>d.done).length;
            const d1Total=(p.dayOneReadiness||[]).length;
            return(
              <div key={p.id} style={{borderTop:'1px solid rgba(61,31,13,.06)',background:pi%2===0?'rgba(245,237,224,.3)':'rgba(245,237,224,.15)',padding:'14px 18px'}}>
                {/* Section header */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.6)',textTransform:'uppercase',letterSpacing:'.06em'}}>{p.name} · {p.drawdownCadence} drawdown · Last: {p.lastDrawdown}</div>
                  <div style={{display:'flex',gap:8}}>
                    {pendingCOs.length>0&&<span style={{fontSize:9,fontWeight:700,color:'#D97706',background:'rgba(217,119,6,.08)',border:'1px solid rgba(217,119,6,.2)',padding:'2px 7px',borderRadius:4}}>{pendingCOs.length} CO Pending</span>}
                    <span style={{fontSize:9,fontWeight:700,color:d1Done===d1Total&&d1Total>0?'#16a34a':'rgba(61,31,13,.4)',background:d1Done===d1Total&&d1Total>0?'rgba(22,163,74,.08)':'rgba(61,31,13,.04)',border:`1px solid ${d1Done===d1Total&&d1Total>0?'rgba(22,163,74,.2)':'rgba(61,31,13,.1)'}`,padding:'2px 7px',borderRadius:4}}>Day-One {d1Done}/{d1Total}</span>
                  </div>
                </div>
                {/* Milestones */}
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:7}}>Milestones</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {p.milestones.map((m,i)=>(
                      <div key={i} style={{borderRadius:6,padding:'6px 10px',background:m.status==='Done'?'rgba(22,163,74,.08)':m.status==='In Progress'?'rgba(232,129,58,.08)':'rgba(61,31,13,.04)',border:`1px solid ${m.status==='Done'?'rgba(22,163,74,.2)':m.status==='In Progress'?'rgba(232,129,58,.3)':'rgba(61,31,13,.1)'}`,minWidth:110}}>
                        <div style={{fontSize:10,fontWeight:700,color:m.status==='Done'?'#16a34a':m.status==='In Progress'?'#E8813A':'rgba(61,31,13,.35)'}}>{m.status==='Done'?'✓ ':m.status==='In Progress'?'● ':''}{m.name}</div>
                        <div style={{fontSize:9,color:'rgba(61,31,13,.4)',marginTop:2}}>{m.due}</div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Change Orders */}
                {(p.changeOrders||[]).length>0&&(
                  <div style={{marginBottom:12}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7}}>
                      <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em'}}>Change Orders</div>
                      {coTotal>0&&<span style={{fontSize:9,fontWeight:700,color:'#D97706'}}>+{fmt(coTotal)} approved</span>}
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:5}}>
                      {p.changeOrders.map(co=>(
                        <div key={co.id} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 10px',background:co.status==='approved'?'rgba(22,163,74,.04)':'rgba(217,119,6,.04)',border:`1px solid ${co.status==='approved'?'rgba(22,163,74,.15)':'rgba(217,119,6,.2)'}`,borderRadius:7}}>
                          <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:3,background:co.status==='approved'?'rgba(22,163,74,.1)':'rgba(217,119,6,.1)',color:co.status==='approved'?'#16a34a':'#D97706',flexShrink:0,whiteSpace:'nowrap'}}>{co.status==='approved'?'✓ Approved':'⏳ Pending'}</span>
                          <span style={{flex:1,fontSize:11,color:'rgba(61,31,13,.7)',lineHeight:1.4}}>{co.desc}</span>
                          <span style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.7)',fontFamily:'monospace',flexShrink:0}}>+{fmt(co.amount)}</span>
                          {co.approvedAt&&<span style={{fontSize:9,color:'rgba(61,31,13,.35)',flexShrink:0}}>{co.approvedAt}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Day-One Readiness */}
                {(p.dayOneReadiness||[]).length>0&&(
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:7}}>
                      <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em'}}>Day-One Readiness</div>
                      <div style={{height:4,width:80,background:'rgba(61,31,13,.08)',borderRadius:2,overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${d1Total>0?Math.round(d1Done/d1Total*100):0}%`,background:d1Done===d1Total&&d1Total>0?'#16a34a':'#E8813A',borderRadius:2}}/>
                      </div>
                      <span style={{fontSize:9,color:'rgba(61,31,13,.4)'}}>{d1Done}/{d1Total} complete</span>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      {p.dayOneReadiness.map((d,i)=>(
                        <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 10px',background:d.done?'rgba(22,163,74,.04)':'rgba(61,31,13,.02)',borderRadius:6,border:`1px solid ${d.done?'rgba(22,163,74,.12)':'rgba(61,31,13,.07)'}`}}>
                          <span style={{fontSize:12,flexShrink:0,color:d.done?'#16a34a':'rgba(61,31,13,.25)'}}>{d.done?'✓':'○'}</span>
                          <span style={{flex:1,fontSize:11,color:d.done?'rgba(61,31,13,.55)':'#3D1F0D',lineHeight:1.3,textDecoration:d.done?'line-through':''}}>{d.item}</span>
                          <span style={{fontSize:9,color:'rgba(61,31,13,.35)',flexShrink:0,whiteSpace:'nowrap'}}>Due {d.due}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rent roll table */}
      <div className="card" style={{overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:'1px solid rgba(61,31,13,.08)',fontWeight:700,fontSize:13}}>Rent Roll Detail</div>
        {/* TEAM-1 §5.3 — grid protections: frozen header + locked calculated columns */}
        <div style={{display:'flex',alignItems:'center',gap:14,padding:'6px 18px',fontSize:10,color:'rgba(61,31,13,.45)',flexWrap:'wrap'}}>
          <span>🔒 $/SF, $/Guest, Mkt Rent and Delta are calculated — read-only</span>
          <span style={{color:'rgba(61,31,13,.35)'}}>Header frozen on scroll</span>
        </div>
        <div style={{overflowX:'auto',maxHeight:420,overflowY:'auto'}}>
          <table>
            <thead>
              <tr style={{background:'rgba(245,237,224,.6)',borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                {['','Location','Market','SF','Base Rent','OpEx','Total','$/SF','$/Guest','Mkt Rent','Delta','Expiry'].map(h=>{
                  const locked=['$/SF','$/Guest','Mkt Rent','Delta'].includes(h);
                  return(
                  <th key={h} title={locked?'Calculated field — read-only':undefined} style={{position:'sticky',top:0,zIndex:1,fontSize:10,fontWeight:700,color:locked?'#8b0020':'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.05em',whiteSpace:'nowrap',background:'#F5EDE0',boxShadow:'0 1px 0 rgba(61,31,13,.1)'}}>{locked?`🔒 ${h}`:h}</th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {_p.map(l=>{
                const base=Math.round(l.rent*.78);const opex=Math.round(l.rent*.22);
                const mkt=Math.round(l.rent*1.18);const delta=mkt-l.rent;
                return(
                  <tr key={l.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <td><Ring status={l.status}/></td>
                    <td style={{fontWeight:600,fontSize:13}}>{l.name}</td>
                    <td style={{fontSize:12,color:'rgba(61,31,13,.6)'}}>{l.market}</td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{l.sf.toLocaleString()}</td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(base)}</td>
                    <td style={{fontFamily:'monospace',fontSize:12}}>{fmt(opex)}</td>
                    <td style={{fontFamily:'monospace',fontSize:12,fontWeight:700}}>{fmtCur(l.rent,l.currency)}</td>
                    <td title="Calculated (Total ÷ SF) — read-only" style={{fontFamily:'monospace',fontSize:12,color:'rgba(61,31,13,.5)',background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>${Math.round(l.rent/l.sf)}</td>
                    <td title="Calculated (cost per guest visit) — read-only" style={{padding:'10px 14px',background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>
                      <div style={{display:'flex',alignItems:'baseline',gap:4}}>
                        <span style={{fontFamily:'monospace',fontSize:13,fontWeight:700,color:l.costPerExp>1500?'#dc2626':l.costPerExp>800?'#d97706':'#16a34a'}}>${l.costPerExp.toLocaleString()}</span>
                      </div>
                      <div style={{fontSize:9,color:'rgba(61,31,13,.35)',marginTop:1}}>per visit</div>
                    </td>
                    <td title="Market rent estimate — read-only" style={{fontFamily:'monospace',fontSize:12,color:'rgba(61,31,13,.5)',background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>{fmt(mkt)}</td>
                    <td title="Calculated (Mkt Rent − Total) — read-only" style={{fontFamily:'monospace',fontSize:12,color:'#dc2626',fontWeight:600,background:'rgba(61,31,13,.03)',cursor:'not-allowed'}}>+{fmt(delta)}</td>
                    <td style={{fontSize:12,color:'rgba(61,31,13,.5)',whiteSpace:'nowrap'}}>{l.leaseEnd}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background:'#3D1F0D'}}>
                <td colSpan={3} style={{fontSize:12,fontWeight:700,color:'#fff'}}>Total Portfolio</td>
                <td style={{fontFamily:'monospace',fontWeight:700,fontSize:13,color:'#fff'}}>{_p.reduce((s,l)=>s+l.sf,0).toLocaleString()}</td>
                <td style={{fontFamily:'monospace',fontWeight:700,color:'#fff'}}>{fmt(totalBase)}</td>
                <td style={{fontFamily:'monospace',fontWeight:700,color:'#fff'}}>{fmt(totalOpex)}</td>
                <td style={{fontFamily:'monospace',fontWeight:700,color:'#E8813A',fontSize:14}}>{fmt(totalRent)}</td>
                <td style={{fontFamily:'monospace',color:'rgba(255,255,255,.5)'}}>${Math.round(totalRent/(_p.reduce((s,l)=>s+l.sf,0)||1))}</td>
                <td style={{padding:'10px 14px'}}>
                  <div style={{fontFamily:'monospace',fontWeight:700,color:'#E8813A',fontSize:13}}>${Math.round(_p.reduce((s,l)=>s+l.costPerExp,0)/(_p.length||1)).toLocaleString()}</div>
                  <div style={{fontSize:9,color:'rgba(255,255,255,.4)',marginTop:1}}>portfolio avg</div>
                </td>
                <td style={{fontFamily:'monospace',color:'rgba(255,255,255,.7)'}}>{fmt(totalMkt)}</td>
                <td style={{fontFamily:'monospace',fontWeight:700,color:'#fca5a5'}}>+{fmt(totalDelta)}</td>
                <td/>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

function CKEPage(){
  const authFetch=useAuthFetch();
  const clerk=useClerk();

  // ── Live data ────────────────────────────────────────────────
  const [meetings,setMeetings]=useState([]);
  const [loading,setLoading]=useState(true);
  const [sel,setSel]=useState(null);
  const [tab,setTab]=useState('detail'); // detail|positions|actions|entities|brief

  // ── Search ───────────────────────────────────────────────────
  const [q,setQ]=useState('');
  const [searchResults,setSearchResults]=useState(null);
  const [searching,setSearching]=useState(false);

  // ── Ingest panel ─────────────────────────────────────────────
  const [showIngest,setShowIngest]=useState(false);
  const [ingestMode,setIngestMode]=useState('text'); // 'text' | 'audio'
  const [ingestTitle,setIngestTitle]=useState('');
  const [ingestDate,setIngestDate]=useState('');
  const [ingestDeal,setIngestDeal]=useState('');
  const [ingestTranscript,setIngestTranscript]=useState('');
  const [ingesting,setIngesting]=useState(false);
  const [ingestResult,setIngestResult]=useState(null);
  // Audio-specific
  const [audioFile,setAudioFile]=useState(null);
  const [audioSource,setAudioSource]=useState('upload'); // upload|zoom|meet|teams|otter
  const fileInputRef=React.useRef(null);

  // ── Confirm gate ─────────────────────────────────────────────
  const [confirming,setConfirming]=useState(new Set());
  const [confirmedIds,setConfirmedIds]=useState(new Set());

  // ── Privacy gate (CKE-1 §2.1) ────────────────────────────────
  const [showPrivacyGate,setShowPrivacyGate]=useState(false);
  const [privacyChecked,setPrivacyChecked]=useState(false);
  const [exclusionList,setExclusionList]=useState('');
  const [pendingAudioIngest,setPendingAudioIngest]=useState(false);

  // ── Per-item review (CKE-1 §6.2) ─────────────────────────────
  // key: `${meetingId}:${category}:${index}` → {status, editedText}
  const [itemStatuses,setItemStatuses]=useState({});
  const [editingItem,setEditingItem]=useState(null);
  const [editDraft,setEditDraft]=useState('');

  // ── Pre-meeting brief ────────────────────────────────────────
  const [brief,setBrief]=useState(null);
  const [briefLoading,setBriefLoading]=useState(false);

  // Load meetings on mount
  const loadMeetings=useCallback(async()=>{
    try{
      const data=await authFetch('/api/cke/meetings');
      setMeetings(data||[]);
      if(!sel&&data&&data.length>0)setSel(data[0].id);
    }catch(e){console.warn('CKE load failed',e);}
    finally{setLoading(false);}
  },[authFetch]);

  useEffect(()=>{loadMeetings();},[loadMeetings]);

  // Initialise per-item review state from server data when meeting selection changes
  const meeting=meetings.find(m=>m.id===sel);
  useEffect(()=>{
    if(!meeting)return;
    const ext=(meeting.extractions||[])[0]||{};
    const serverReviews=ext.itemReviews||{};
    const newStatuses={};
    ['commitments','decisions','action_items','flagged_items','positions'].forEach(cat=>{
      const catItems=(ext[cat==='action_items'?'actionItems':cat==='flagged_items'?'flaggedItems':cat])||[];
      catItems.forEach((_,i)=>{
        const key=`${meeting.id}:${cat}:${i}`;
        const sv=(serverReviews[cat]||{})[String(i)];
        newStatuses[key]=sv?{status:sv.status,editedText:sv.edited_text||''}:{status:'pending',editedText:''};
      });
    });
    setItemStatuses(prev=>({...prev,...newStatuses}));
  },[sel,meetings]);

  // Load pre-meeting brief when deal tab selected
  useEffect(()=>{
    if(tab!=='brief'||!meeting?.dealSlug)return;
    setBriefLoading(true);
    setBrief(null);
    authFetch(`/api/cke/brief/${meeting.dealSlug}`)
      .then(b=>setBrief(b))
      .catch(e=>console.warn('Brief load failed',e))
      .finally(()=>setBriefLoading(false));
  },[tab,meeting?.dealSlug,authFetch]);

  // Search
  useEffect(()=>{
    if(!q||q.length<2){setSearchResults(null);return;}
    const t=setTimeout(async()=>{
      setSearching(true);
      try{
        const r=await authFetch(`/api/cke/search?q=${encodeURIComponent(q)}`);
        setSearchResults(r);
      }catch(e){setSearchResults(null);}
      finally{setSearching(false);}
    },400);
    return()=>clearTimeout(t);
  },[q,authFetch]);

  // Ingest text transcript
  const handleIngest=async()=>{
    if(!ingestTitle.trim()||!ingestDate||!ingestTranscript.trim())return;
    setIngesting(true);
    setIngestResult(null);
    try{
      const r=await authFetch('/api/cke/meetings/ingest',{
        method:'POST',
        body:{
          title:ingestTitle,
          date:ingestDate,
          transcript:ingestTranscript,
          deal_slug:ingestDeal||null,
          source:'manual',
        },
      });
      setIngestResult({ok:true,...r});
      setShowIngest(false);
      setIngestTitle('');setIngestDate('');setIngestDeal('');setIngestTranscript('');
      await loadMeetings();
    }catch(e){
      setIngestResult({ok:false,error:e.message});
    }finally{setIngesting(false);}
  };

  // Ingest audio recording via AssemblyAI
  const handleAudioIngest=async(exclusions)=>{
    if(!ingestTitle.trim()||!ingestDate||!audioFile)return;
    setIngesting(true);
    setIngestResult(null);
    try{
      const fd=new FormData();
      fd.append('title',ingestTitle);
      fd.append('date',ingestDate);
      fd.append('deal_slug',ingestDeal||'');
      fd.append('source',audioSource);
      fd.append('audio_file',audioFile,audioFile.name);
      // Pass exclusion list if present (CKE-1 §2.1)
      if(exclusions){
        const excArr=exclusions.split(',').map(s=>s.trim()).filter(Boolean);
        if(excArr.length)fd.append('excluded_participants_json',JSON.stringify(excArr));
      }
      // Pass FormData directly — authFetch detects it and skips JSON serialisation
      const r=await authFetch('/api/cke/meetings/ingest-audio',{method:'POST',body:fd});
      setIngestResult({ok:true,...r,isAudio:true});
      setShowIngest(false);
      setIngestTitle('');setIngestDate('');setIngestDeal('');setAudioFile(null);
      if(fileInputRef.current)fileInputRef.current.value='';
      await loadMeetings();
    }catch(e){
      setIngestResult({ok:false,error:e.message});
    }finally{setIngesting(false);}
  };

  // Privacy gate — check consent before audio ingest
  const handleAudioIngestClick=()=>{
    if(!ingestTitle.trim()||!ingestDate||!audioFile)return;
    // Check sessionStorage for recent consent (valid for 8h)
    const stored=sessionStorage.getItem('cke_privacy_consent');
    if(stored){
      const {ts}=JSON.parse(stored);
      if(Date.now()-ts<8*60*60*1000){
        handleAudioIngest(exclusionList);
        return;
      }
    }
    setPrivacyChecked(false);
    setShowPrivacyGate(true);
    setPendingAudioIngest(true);
  };
  const handlePrivacyAgree=()=>{
    if(!privacyChecked)return;
    sessionStorage.setItem('cke_privacy_consent',JSON.stringify({ts:Date.now()}));
    setShowPrivacyGate(false);
    if(pendingAudioIngest){setPendingAudioIngest(false);handleAudioIngest(exclusionList);}
  };

  // Per-item review: set status for a single item
  const setItemReview=async(meetingId,category,index,status,editedText)=>{
    const key=`${meetingId}:${category}:${index}`;
    setItemStatuses(prev=>({...prev,[key]:{status,editedText:editedText||''}}));
    try{
      await authFetch(`/api/cke/meetings/${meetingId}/items`,{
        method:'PATCH',
        body:{category,index,status,edited_text:editedText||undefined},
      });
    }catch(e){
      // Revert optimistic update on error
      setItemStatuses(prev=>({...prev,[key]:{status:'pending',editedText:''}}));
      console.warn('Item review update failed',e);
    }
  };

  // Confirm extraction → Safe write
  const handleConfirm=async(meetingId)=>{
    setConfirming(prev=>new Set([...prev,meetingId]));
    try{
      await authFetch(`/api/cke/meetings/${meetingId}/confirm`,{
        method:'POST',
        body:{approver:'Advisor'},
      });
      setConfirmedIds(prev=>new Set([...prev,meetingId]));
      await loadMeetings();
    }catch(e){console.warn('Confirm failed',e);}
    finally{setConfirming(prev=>{const s=new Set(prev);s.delete(meetingId);return s;});}
  };

  // ── Helpers ──────────────────────────────────────────────────
  const sColor=s=>s>=80?'#16a34a':s>=60?'#d97706':'#dc2626';
  const statusBadge=s=>{
    if(s==='confirmed')return{bg:'#d1fae5',color:'#065f46',label:'✓ Confirmed'};
    if(s==='review')return{bg:'#fef3c7',color:'#92400e',label:'⟳ Pending Review'};
    if(s==='extracting')return{bg:'#dbeafe',color:'#1d4ed8',label:'◌ Extracting'};
    return{bg:'#f3f4f6',color:'#4b5563',label:'● Pending'};
  };

  const displayMeetings=q&&searchResults
    ? (searchResults.results||[]).map(r=>meetings.find(m=>m.id===r.meetingId)).filter(Boolean)
    : meetings;

  const totalCommitments=meetings.reduce((n,m)=>n+(m.extractions||[]).reduce((a,e)=>a+(e.commitments||[]).length,0),0);
  const totalDecisions=meetings.reduce((n,m)=>n+(m.extractions||[]).reduce((a,e)=>a+(e.decisions||[]).length,0),0);
  const totalMin=meetings.reduce((n,m)=>n+(m.durationMin||0),0);
  const pendingReview=meetings.filter(m=>m.status==='review').length;

  return(
    <div style={{maxWidth:1200,margin:'0 auto'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <div className="page-header-label">Conversational Knowledge Engine</div>
          <div className="page-title">Meeting Intelligence Graph</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.45)',marginTop:3}}>
            {loading?'Loading…':`${meetings.length} sessions · ${totalMin} min · ${totalDecisions} decisions · ${totalCommitments} commitments`}
            {pendingReview>0&&<span style={{marginLeft:10,background:'#fef3c7',color:'#92400e',borderRadius:6,padding:'1px 8px',fontSize:11,fontWeight:700}}>⟳ {pendingReview} pending review</span>}
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>{setShowIngest(v=>!v);setIngestMode('audio');}} className="btn-ghost" style={{fontSize:12}}>🎙 Ingest Recording</button>
          <button onClick={()=>{setShowIngest(v=>!v);setIngestMode('text');}} className="btn-ghost" style={{fontSize:12}}>↑ Paste Transcript</button>
        </div>
      </div>

      {/* Privacy Gate modal (CKE-1 §2.1) */}
      {showPrivacyGate&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
          <div style={{background:'#fff',borderRadius:16,padding:32,maxWidth:520,width:'100%',boxShadow:'0 24px 80px rgba(0,0,0,.22)'}}>
            <div style={{fontSize:11,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>🔒 CKE Privacy Gate</div>
            <div style={{fontSize:18,fontWeight:800,color:'#3D1F0D',marginBottom:16}}>Recording consent required</div>
            <div style={{fontSize:13,color:'rgba(61,31,13,.7)',lineHeight:1.7,marginBottom:20}}>
              Before transcribing this recording, confirm the following:
              <ul style={{marginTop:10,paddingLeft:20,display:'flex',flexDirection:'column',gap:6}}>
                <li>All participants were informed that this session may be recorded and analysed by AI systems.</li>
                <li>Extracted commitments, positions, and action items will be written to the Stage Safe knowledge layer after your review.</li>
                <li>No recording content will be shared externally without further human approval.</li>
              </ul>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:'rgba(61,31,13,.6)',marginBottom:6}}>Exclude participants from extraction (optional)</div>
              <input
                value={exclusionList}
                onChange={e=>setExclusionList(e.target.value)}
                placeholder="e.g. Sarah Chen, External Counsel — comma-separated"
                style={{width:'100%',padding:'8px 12px',border:'1px solid rgba(61,31,13,.2)',borderRadius:8,fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box'}}
              />
              <div style={{fontSize:11,color:'rgba(61,31,13,.35)',marginTop:4}}>These names will be excluded from entity extraction for 24 hours.</div>
            </div>
            <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',marginBottom:20}}>
              <input type="checkbox" checked={privacyChecked} onChange={e=>setPrivacyChecked(e.target.checked)} style={{marginTop:3,accentColor:'#8b0020',width:16,height:16,flexShrink:0}}/>
              <span style={{fontSize:13,color:'#3D1F0D',lineHeight:1.6,fontWeight:600}}>I confirm that all participants have been notified, or this is an internal recording with no expectation of privacy.</span>
            </label>
            <div style={{display:'flex',gap:10}}>
              <button
                onClick={handlePrivacyAgree}
                disabled={!privacyChecked}
                style={{flex:1,background:privacyChecked?'#8b0020':'rgba(61,31,13,.15)',color:privacyChecked?'#fff':'rgba(61,31,13,.4)',border:'none',borderRadius:10,padding:'10px 16px',fontSize:13,fontWeight:700,cursor:privacyChecked?'pointer':'not-allowed',fontFamily:'inherit',transition:'all .15s'}}>
                Agree &amp; Transcribe →
              </button>
              <button onClick={()=>{setShowPrivacyGate(false);setPendingAudioIngest(false);}} style={{background:'transparent',border:'1px solid rgba(61,31,13,.2)',borderRadius:10,padding:'10px 16px',fontSize:13,color:'rgba(61,31,13,.6)',cursor:'pointer',fontFamily:'inherit'}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ingest panel */}
      {showIngest&&(
        <div className="card" style={{padding:20,marginBottom:16,borderLeft:'4px solid #8b0020'}}>
          {/* Mode selector */}
          <div style={{display:'flex',gap:2,marginBottom:16,background:'rgba(61,31,13,.05)',borderRadius:8,padding:3}}>
            {[['text','↑ Paste Transcript'],['audio','🎙 Upload Recording']].map(([m,label])=>(
              <button key={m} onClick={()=>setIngestMode(m)} style={{flex:1,padding:'7px 12px',border:'none',borderRadius:7,cursor:'pointer',fontSize:12,fontWeight:ingestMode===m?700:500,background:ingestMode===m?'#8b0020':'transparent',color:ingestMode===m?'#fff':'rgba(61,31,13,.55)',fontFamily:'inherit',transition:'all .15s'}}>
                {label}
              </button>
            ))}
          </div>

          {/* Shared fields */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:10}}>
            <input value={ingestTitle} onChange={e=>setIngestTitle(e.target.value)} placeholder="Meeting title *" style={{padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
            <input type="date" value={ingestDate} onChange={e=>setIngestDate(e.target.value)} style={{padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
            <input value={ingestDeal} onChange={e=>setIngestDeal(e.target.value)} placeholder="Deal slug (d1, d2…) — optional" style={{padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:13,fontFamily:'inherit',outline:'none'}}/>
          </div>

          {/* TEXT MODE */}
          {ingestMode==='text'&&(
            <>
              <textarea value={ingestTranscript} onChange={e=>setIngestTranscript(e.target.value)} placeholder="Paste meeting transcript here… Speaker-attributed format preferred (e.g. 'Marcus Chen: We need to…')" style={{width:'100%',minHeight:140,padding:'10px 12px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',resize:'vertical',outline:'none',marginBottom:10}}/>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <button onClick={handleIngest} disabled={ingesting||!ingestTitle.trim()||!ingestDate||!ingestTranscript.trim()} className="btn-primary" style={{background:'#8b0020',fontSize:13,opacity:(ingesting||!ingestTitle.trim()||!ingestDate||!ingestTranscript.trim())?0.5:1}}>
                  {ingesting?'Extracting…':'Run Haiku Extraction →'}
                </button>
                <button onClick={()=>setShowIngest(false)} className="btn-ghost" style={{fontSize:13}}>Cancel</button>
                {ingestTranscript.length>0&&<span style={{fontSize:11,color:'rgba(61,31,13,.4)',marginLeft:4}}>{ingestTranscript.length.toLocaleString()} chars</span>}
              </div>
            </>
          )}

          {/* AUDIO MODE */}
          {ingestMode==='audio'&&(
            <>
              <div style={{marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Recording source</div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {[['upload','Upload File'],['zoom','Zoom'],['meet','Google Meet'],['teams','MS Teams'],['otter','Otter.ai']].map(([v,l])=>(
                    <button key={v} onClick={()=>setAudioSource(v)} style={{padding:'5px 12px',border:`1px solid ${audioSource===v?'#8b0020':'rgba(61,31,13,.15)'}`,borderRadius:20,fontSize:11,fontWeight:audioSource===v?700:500,background:audioSource===v?'rgba(139,0,33,.07)':'transparent',color:audioSource===v?'#8b0020':'rgba(61,31,13,.55)',cursor:'pointer',fontFamily:'inherit',transition:'all .12s'}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              {/* File picker */}
              <div
                onClick={()=>fileInputRef.current&&fileInputRef.current.click()}
                style={{border:`2px dashed ${audioFile?'#8b0020':'rgba(61,31,13,.2)'}`,borderRadius:12,padding:'24px 16px',textAlign:'center',cursor:'pointer',marginBottom:12,background:audioFile?'rgba(139,0,33,.03)':'transparent',transition:'all .15s'}}>
                <div style={{fontSize:28,marginBottom:8}}>{audioFile?'🎙':'🎵'}</div>
                {audioFile
                  ? <><div style={{fontSize:13,fontWeight:700,color:'#8b0020',marginBottom:3}}>{audioFile.name}</div>
                      <div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{(audioFile.size/1024/1024).toFixed(1)} MB · click to replace</div></>
                  : <><div style={{fontSize:13,fontWeight:600,color:'rgba(61,31,13,.6)',marginBottom:3}}>Drop recording or click to select</div>
                      <div style={{fontSize:11,color:'rgba(61,31,13,.35)'}}>MP3, MP4, M4A, WAV, OGG, FLAC — up to 500 MB</div></>
                }
                <input ref={fileInputRef} type="file" accept="audio/*,video/mp4,video/quicktime" style={{display:'none'}} onChange={e=>setAudioFile(e.target.files[0]||null)}/>
              </div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12,lineHeight:1.6}}>
                🎙 Speaker diarization, entity detection, and topic chapters extracted automatically.<br/>
                Processing takes ~1 min per 5 min of audio. Results reviewed in CKE before writing to Safe.
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <button onClick={handleAudioIngestClick} disabled={ingesting||!ingestTitle.trim()||!ingestDate||!audioFile} className="btn-primary" style={{background:'#8b0020',fontSize:13,opacity:(ingesting||!ingestTitle.trim()||!ingestDate||!audioFile)?0.5:1}}>
                  {ingesting?'Transcribing… (takes ~1 min/5 min audio)':'Transcribe + Extract →'}
                </button>
                <button onClick={()=>setShowIngest(false)} className="btn-ghost" style={{fontSize:13}}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Ingest result flash */}
      {ingestResult&&(
        <div style={{padding:'12px 16px',borderRadius:10,marginBottom:12,background:ingestResult.ok?'#d1fae5':'#fee2e2',border:`1px solid ${ingestResult.ok?'#6ee7b7':'#fca5a5'}`,color:ingestResult.ok?'#065f46':'#dc2626',fontSize:13}}>
          {ingestResult.ok
            ? ingestResult.isAudio
              ? `🎙 ✓ Audio transcribed — ${ingestResult.speakers||'?'} speakers detected, ${ingestResult.commitments} commitments, ${ingestResult.decisions} decisions, ${ingestResult.entities} entities${ingestResult.chapters>0?`, ${ingestResult.chapters} topic chapters`:''}.${ingestResult.flaggedItems>0?` ⚠ ${ingestResult.flaggedItems} items flagged for review.`:''}`
              : `✓ Extraction complete — ${ingestResult.commitments} commitments, ${ingestResult.decisions} decisions, ${ingestResult.actionItems} actions.${ingestResult.flaggedItems>0?` ⚠ ${ingestResult.flaggedItems} items flagged for review.`:''}`
            : `✗ Ingest failed — ${ingestResult.error}`}
        </div>
      )}

      {/* Search */}
      <div className="card" style={{padding:'12px 16px',borderLeft:'4px solid #8b0020',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:16,color:'#8b0020'}}>⌕</span>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder='Search CKE knowledge graph… "free rent", "personal guarantee", "CFO"' style={{flex:1,border:'none',outline:'none',fontSize:13,color:'#3D1F0D',fontFamily:'inherit',background:'transparent'}}/>
        {searching&&<span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>Searching…</span>}
        {q&&searchResults&&<span style={{fontSize:10,background:'#8b0020',color:'#fff',padding:'2px 8px',borderRadius:999,fontWeight:700}}>{searchResults.total} match{searchResults.total!==1?'es':''}</span>}
        {q&&<button onClick={()=>setQ('')} style={{background:'none',border:'none',cursor:'pointer',color:'rgba(61,31,13,.3)',fontSize:16,lineHeight:1}}>×</button>}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:16}}>
        {/* Meeting list */}
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {loading&&<div style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.35)',fontSize:13}}>Loading meetings…</div>}
          {!loading&&displayMeetings.map(m=>{
            const sc=m.sentiment||70;
            const badge=statusBadge(m.status);
            return(
              <div key={m.id} onClick={()=>{setSel(m.id);setTab('detail');}} className="card" style={{padding:14,cursor:'pointer',border:`1px solid ${sel===m.id?'#8b0020':'rgba(61,31,13,.08)'}`,outline:sel===m.id?'2px solid rgba(139,0,33,.2)':'none',transition:'all .15s'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                  <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>📅 {m.date}</span>
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999,background:badge.bg,color:badge.color}}>{badge.label}</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',lineHeight:1.3,flex:1}}>{m.title}</div>
                  {m.source&&m.source!=='manual'&&<span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:'rgba(139,0,33,.08)',color:'#8b0020',flexShrink:0}}>{m.source==='upload'?'🎙 audio':m.source}</span>}
                </div>
                {m.dealSlug&&<div style={{fontSize:11,color:'#E8813A',fontWeight:500,marginBottom:4}}>{m.dealSlug}</div>}
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                  <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:999,background:sColor(sc)+'18',color:sColor(sc)}}>{sc}% sentiment</span>
                  {m.durationMin&&<span style={{fontSize:10,color:'rgba(61,31,13,.35)'}}>{m.durationMin} min</span>}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                  {(m.tags||[]).slice(0,3).map(t=><span key={t} className="tag">{t}</span>)}
                </div>
              </div>
            );
          })}
          {!loading&&!displayMeetings.length&&(
            <div style={{textAlign:'center',padding:32,color:'rgba(61,31,13,.3)',fontSize:13}}>
              {q?`No meetings match "${q}"`:'No meetings yet — ingest a transcript to start'}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {meeting?(
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {/* Tab bar */}
            <div className="card" style={{padding:'0 4px',display:'flex',gap:2}}>
              {[['detail','Overview'],['positions','Positions'],['actions','Action Items'],['entities','Entities'],['brief','Pre-Meeting Brief']].map(([tid,label])=>(
                <button key={tid} onClick={()=>setTab(tid)} style={{flex:1,padding:'10px 4px',border:'none',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:tab===tid?700:500,background:tab===tid?'#8b0020':'transparent',color:tab===tid?'#fff':'rgba(61,31,13,.55)',fontFamily:'inherit',transition:'all .15s'}}>
                  {label}
                </button>
              ))}
            </div>

            {/* Confirm banner (for meetings pending review) */}
            {(meeting.status==='review')&&tab==='detail'&&(
              <div style={{padding:'12px 16px',background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div style={{fontSize:13,color:'#92400e',fontWeight:600}}>⟳ Extraction pending review — confirm to write to Safe layer</div>
                <button
                  onClick={()=>handleConfirm(meeting.id)}
                  disabled={confirming.has(meeting.id)}
                  style={{background:'#8b0020',color:'#fff',border:'none',borderRadius:8,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:confirming.has(meeting.id)?'not-allowed':'pointer',opacity:confirming.has(meeting.id)?0.6:1,fontFamily:'inherit',whiteSpace:'nowrap'}}>
                  {confirming.has(meeting.id)?'Confirming…':'Confirm → Safe ✓'}
                </button>
              </div>
            )}

            {/* OVERVIEW TAB */}
            {tab==='detail'&&(()=>{
              const ext=(meeting.extractions||[])[0]||{};
              const canReview=meeting.status==='review';
              // Per-item review button strip (CKE-1 §6.2)
              const ReviewBtns=({cat,i})=>{
                const key=`${meeting.id}:${cat}:${i}`;
                const rv=itemStatuses[key]||{status:'pending'};
                if(!canReview)return null;
                return(
                  <div style={{display:'flex',gap:4,marginTop:6}}>
                    {[['approved','✓','#16a34a'],['discarded','✕','#dc2626'],['edited','✏','#2563EB']].map(([st,icon,col])=>(
                      <button key={st} onClick={()=>{
                        if(st==='edited'){
                          const items=cat==='decisions'?(ext.decisions||[]):cat==='commitments'?(ext.commitments||[]):cat==='action_items'?(ext.actionItems||[]):cat==='flagged_items'?(ext.flaggedItems||[]):(ext.positions||[]);
                          const it=items[i]||{};
                          const tf=cat==='decisions'?'decision_text':cat==='commitments'?'commitment_text':cat==='action_items'?'action_text':cat==='positions'?'position_text':'description';
                          setEditingItem({meetingId:meeting.id,category:cat,index:i});
                          setEditDraft(it[tf]||'');
                        }else if(rv.status===st){
                          setItemReview(meeting.id,cat,i,'pending','');
                        }else{
                          setItemReview(meeting.id,cat,i,st,'');
                        }
                      }} title={st==='approved'?'Approve':st==='discarded'?'Discard':'Edit'}
                      style={{padding:'2px 8px',border:`1px solid ${rv.status===st?col:'rgba(61,31,13,.15)'}`,borderRadius:5,fontSize:11,fontWeight:700,background:rv.status===st?col+'18':'transparent',color:rv.status===st?col:'rgba(61,31,13,.4)',cursor:'pointer',fontFamily:'inherit',transition:'all .1s'}}>
                        {icon}
                      </button>
                    ))}
                  </div>
                );
              };
              // Inline editor
              const InlineEdit=({cat,i,onSave})=>{
                const isOpen=editingItem&&editingItem.meetingId===meeting.id&&editingItem.category===cat&&editingItem.index===i;
                if(!isOpen)return null;
                return(
                  <div style={{marginTop:8}}>
                    <textarea value={editDraft} onChange={e=>setEditDraft(e.target.value)}
                      style={{width:'100%',minHeight:60,padding:'6px 8px',border:'1px solid #2563EB',borderRadius:6,fontSize:12,fontFamily:'inherit',outline:'none',resize:'vertical'}}
                      autoFocus/>
                    <div style={{display:'flex',gap:6,marginTop:4}}>
                      <button onClick={()=>{setItemReview(meeting.id,cat,i,'edited',editDraft);setEditingItem(null);}} style={{padding:'3px 10px',background:'#2563EB',color:'#fff',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Save</button>
                      <button onClick={()=>setEditingItem(null)} style={{padding:'3px 10px',background:'transparent',border:'1px solid rgba(61,31,13,.15)',borderRadius:5,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                    </div>
                  </div>
                );
              };
              const itemStyle=(cat,i,baseStyle)=>{
                const key=`${meeting.id}:${cat}:${i}`;
                const rv=itemStatuses[key]||{status:'pending'};
                if(rv.status==='discarded')return{...baseStyle,opacity:.35,textDecoration:'line-through'};
                if(rv.status==='approved')return{...baseStyle,borderColor:'#22c55e'};
                if(rv.status==='edited')return{...baseStyle,borderColor:'#2563EB'};
                return baseStyle;
              };
              const editedText=(cat,i,field,orig)=>{
                const key=`${meeting.id}:${cat}:${i}`;
                const rv=itemStatuses[key]||{};
                return(rv.status==='edited'&&rv.editedText)?rv.editedText:orig;
              };
              return(
                <>
                  {meeting.excerpt&&(
                    <div className="card" style={{padding:18,borderLeft:'4px solid #8b0020'}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:8}}>💬 Key Moment</div>
                      <p style={{fontSize:13.5,color:'rgba(61,31,13,.8)',fontStyle:'italic',lineHeight:1.7}}>{meeting.excerpt}</p>
                    </div>
                  )}
                  {canReview&&<div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:4,textAlign:'right'}}>
                    ✓ approve · ✕ discard · ✏ edit each item before confirming
                  </div>}
                  <div className="grid-2">
                    <div className="card" style={{padding:18}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:12}}>⚡ Decisions ({(ext.decisions||[]).length})</div>
                      {(ext.decisions||[]).map((d,i)=>(
                        <div key={i} style={itemStyle('decisions',i,{display:'flex',flexDirection:'column',gap:4,padding:'9px 11px',background:d.decision_type==='resolved'?'#f0fdf4':'#fffbeb',border:`1px solid ${d.decision_type==='resolved'?'#bbf7d0':'#fde68a'}`,borderRadius:9,marginBottom:7})}>
                          <div style={{display:'flex',gap:10}}>
                            <span style={{width:18,height:18,borderRadius:'50%',background:d.decision_type==='resolved'?'#22c55e':'#f59e0b',color:'#fff',fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>{d.decision_type==='resolved'?'✓':'…'}</span>
                            <div>
                              <div style={{fontSize:12,color:'#3D1F0D',fontWeight:500}}>{editedText('decisions',i,'decision_text',d.decision_text)}</div>
                              {d.source_text&&<div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:3,fontStyle:'italic'}}>"{d.source_text}"</div>}
                            </div>
                          </div>
                          <ReviewBtns cat="decisions" i={i}/>
                          <InlineEdit cat="decisions" i={i}/>
                        </div>
                      ))}
                      {!(ext.decisions||[]).length&&<div style={{fontSize:12,color:'rgba(61,31,13,.35)'}}>No decisions extracted</div>}
                    </div>
                    <div className="card" style={{padding:18}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:12}}>→ Commitments ({(ext.commitments||[]).length})</div>
                      {(ext.commitments||[]).map((c,i)=>(
                        <div key={i} style={itemStyle('commitments',i,{display:'flex',flexDirection:'column',gap:4,padding:'9px 11px',background:c.confidence==='explicit'?'#eff6ff':'#fafafa',border:`1px solid ${c.confidence==='explicit'?'#93c5fd':'#e5e7eb'}`,borderRadius:9,marginBottom:7})}>
                          <div style={{display:'flex',gap:10}}>
                            <span style={{width:18,height:18,borderRadius:'50%',background:'#2563EB',color:'#fff',fontSize:11,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>→</span>
                            <div>
                              <div style={{fontSize:11,fontWeight:700,color:'#1d4ed8',marginBottom:2}}>{c.speaker}</div>
                              <div style={{fontSize:12,color:'#3D1F0D'}}>{editedText('commitments',i,'commitment_text',c.commitment_text)}</div>
                              <div style={{display:'flex',gap:6,marginTop:4}}>
                                <span style={{fontSize:9,background:'#dbeafe',color:'#1d4ed8',padding:'1px 6px',borderRadius:4,fontWeight:600}}>{c.commitment_type}</span>
                                <span style={{fontSize:9,background:c.confidence==='explicit'?'#d1fae5':'#fef3c7',color:c.confidence==='explicit'?'#065f46':'#92400e',padding:'1px 6px',borderRadius:4,fontWeight:600}}>{c.confidence}</span>
                              </div>
                            </div>
                          </div>
                          <ReviewBtns cat="commitments" i={i}/>
                          <InlineEdit cat="commitments" i={i}/>
                        </div>
                      ))}
                      {!(ext.commitments||[]).length&&<div style={{fontSize:12,color:'rgba(61,31,13,.35)'}}>No commitments extracted</div>}
                    </div>
                  </div>
                  {/* Flagged items */}
                  {(ext.flaggedItems||[]).length>0&&(
                    <div className="card" style={{padding:16,borderLeft:'4px solid #f59e0b'}}>
                      <div style={{fontWeight:700,fontSize:13,marginBottom:10,color:'#92400e'}}>⚠ Flagged for Review ({ext.flaggedItems.length})</div>
                      {ext.flaggedItems.map((f,i)=>(
                        <div key={i} style={itemStyle('flagged_items',i,{padding:'8px 12px',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,marginBottom:6,fontSize:12})}>
                          <div>
                            <span style={{fontWeight:700,color:'#92400e',marginRight:6}}>{f.category}:</span>
                            <span style={{color:'#3D1F0D'}}>{f.description}</span>
                          </div>
                          <ReviewBtns cat="flagged_items" i={i}/>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {/* POSITIONS TAB */}
            {tab==='positions'&&(()=>{
              const ext=(meeting.extractions||[])[0]||{};
              const positions=ext.positions||[];
              const canReview=meeting.status==='review';
              const itemStyle=(i,base)=>{
                const key=`${meeting.id}:positions:${i}`;
                const rv=itemStatuses[key]||{status:'pending'};
                if(rv.status==='discarded')return{...base,opacity:.35,textDecoration:'line-through'};
                if(rv.status==='approved')return{...base,borderColor:'#22c55e',borderLeftColor:'#22c55e'};
                if(rv.status==='edited')return{...base,borderColor:'#2563EB',borderLeftColor:'#2563EB'};
                return base;
              };
              return(
                <div className="card" style={{padding:18}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Negotiating Positions ({positions.length})</div>
                  {positions.map((p,i)=>{
                    const key=`${meeting.id}:positions:${i}`;
                    const rv=itemStatuses[key]||{status:'pending'};
                    const isEditing=editingItem&&editingItem.meetingId===meeting.id&&editingItem.category==='positions'&&editingItem.index===i;
                    return(
                      <div key={i} style={itemStyle(i,{padding:'12px 14px',border:'1px solid rgba(61,31,13,.1)',borderRadius:10,marginBottom:10,borderLeft:`4px solid ${p.position_type==='floor'?'#2563EB':p.position_type==='ceiling'?'#dc2626':p.position_type==='constraint'?'#7c3aed':'#f59e0b'}`})}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                          <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D'}}>{p.speaker}</span>
                          <div style={{display:'flex',gap:6}}>
                            <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:4,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.6)'}}>{p.topic}</span>
                            <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:4,background:p.position_type==='floor'?'#dbeafe':p.position_type==='ceiling'?'#fee2e2':p.position_type==='constraint'?'#ede9fe':'#fef3c7',color:p.position_type==='floor'?'#1d4ed8':p.position_type==='ceiling'?'#dc2626':p.position_type==='constraint'?'#6d28d9':'#92400e'}}>{p.position_type}</span>
                          </div>
                        </div>
                        <div style={{fontSize:13,color:'rgba(61,31,13,.8)',marginBottom:4}}>{(rv.status==='edited'&&rv.editedText)?rv.editedText:p.position_text}</div>
                        {p.source_text&&<div style={{fontSize:11,color:'rgba(61,31,13,.4)',fontStyle:'italic'}}>"{p.source_text}"</div>}
                        {canReview&&<div style={{display:'flex',gap:4,marginTop:8}}>
                          {[['approved','✓','#16a34a'],['discarded','✕','#dc2626'],['edited','✏','#2563EB']].map(([st,icon,col])=>(
                            <button key={st} onClick={()=>{
                              if(st==='edited'){setEditingItem({meetingId:meeting.id,category:'positions',index:i});setEditDraft(p.position_text);}
                              else if(rv.status===st)setItemReview(meeting.id,'positions',i,'pending','');
                              else setItemReview(meeting.id,'positions',i,st,'');
                            }}
                            style={{padding:'2px 8px',border:`1px solid ${rv.status===st?col:'rgba(61,31,13,.15)'}`,borderRadius:5,fontSize:11,fontWeight:700,background:rv.status===st?col+'18':'transparent',color:rv.status===st?col:'rgba(61,31,13,.4)',cursor:'pointer',fontFamily:'inherit'}}>
                              {icon}
                            </button>
                          ))}
                        </div>}
                        {isEditing&&(
                          <div style={{marginTop:8}}>
                            <textarea value={editDraft} onChange={e=>setEditDraft(e.target.value)} style={{width:'100%',minHeight:60,padding:'6px 8px',border:'1px solid #2563EB',borderRadius:6,fontSize:12,fontFamily:'inherit',outline:'none',resize:'vertical'}} autoFocus/>
                            <div style={{display:'flex',gap:6,marginTop:4}}>
                              <button onClick={()=>{setItemReview(meeting.id,'positions',i,'edited',editDraft);setEditingItem(null);}} style={{padding:'3px 10px',background:'#2563EB',color:'#fff',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Save</button>
                              <button onClick={()=>setEditingItem(null)} style={{padding:'3px 10px',background:'transparent',border:'1px solid rgba(61,31,13,.15)',borderRadius:5,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!positions.length&&<div style={{fontSize:13,color:'rgba(61,31,13,.35)',textAlign:'center',padding:24}}>No negotiating positions extracted from this meeting.</div>}
                </div>
              );
            })()}

            {/* ACTION ITEMS TAB */}
            {tab==='actions'&&(()=>{
              const ext=(meeting.extractions||[])[0]||{};
              const items=ext.actionItems||[];
              const canReview=meeting.status==='review';
              return(
                <div className="card" style={{padding:18}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Action Items ({items.length})</div>
                  {items.map((a,i)=>{
                    const key=`${meeting.id}:action_items:${i}`;
                    const rv=itemStatuses[key]||{status:'pending'};
                    const isEditing=editingItem&&editingItem.meetingId===meeting.id&&editingItem.category==='action_items'&&editingItem.index===i;
                    const actionText=(rv.status==='edited'&&rv.editedText)?rv.editedText:a.action_text;
                    return(
                      <div key={i} style={{display:'flex',gap:12,padding:'12px 14px',border:`1px solid ${rv.status==='approved'?'#22c55e':rv.status==='discarded'?'rgba(61,31,13,.06)':'rgba(61,31,13,.1)'}`,borderRadius:10,marginBottom:8,opacity:rv.status==='discarded'?.35:1,textDecoration:rv.status==='discarded'?'line-through':'none'}}>
                        <div style={{width:32,height:32,borderRadius:'50%',background:'#E8813A',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700,flexShrink:0}}>!</div>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                            <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D'}}>{a.assignee}</span>
                            <span style={{fontSize:11,color:a.deadline==='unspecified'?'rgba(61,31,13,.35)':'#dc2626',fontWeight:600}}>Due: {a.deadline}</span>
                          </div>
                          <div style={{fontSize:13,color:'rgba(61,31,13,.8)',marginBottom:3}}>{actionText}</div>
                          {a.source_text&&<div style={{fontSize:11,color:'rgba(61,31,13,.4)',fontStyle:'italic'}}>"{a.source_text}"</div>}
                          {canReview&&<div style={{display:'flex',gap:4,marginTop:6}}>
                            {[['approved','✓','#16a34a'],['discarded','✕','#dc2626'],['edited','✏','#2563EB']].map(([st,icon,col])=>(
                              <button key={st} onClick={()=>{
                                if(st==='edited'){setEditingItem({meetingId:meeting.id,category:'action_items',index:i});setEditDraft(a.action_text);}
                                else if(rv.status===st)setItemReview(meeting.id,'action_items',i,'pending','');
                                else setItemReview(meeting.id,'action_items',i,st,'');
                              }}
                              style={{padding:'2px 8px',border:`1px solid ${rv.status===st?col:'rgba(61,31,13,.15)'}`,borderRadius:5,fontSize:11,fontWeight:700,background:rv.status===st?col+'18':'transparent',color:rv.status===st?col:'rgba(61,31,13,.4)',cursor:'pointer',fontFamily:'inherit'}}>
                                {icon}
                              </button>
                            ))}
                          </div>}
                          {isEditing&&(
                            <div style={{marginTop:8}}>
                              <textarea value={editDraft} onChange={e=>setEditDraft(e.target.value)} style={{width:'100%',minHeight:60,padding:'6px 8px',border:'1px solid #2563EB',borderRadius:6,fontSize:12,fontFamily:'inherit',outline:'none',resize:'vertical'}} autoFocus/>
                              <div style={{display:'flex',gap:6,marginTop:4}}>
                                <button onClick={()=>{setItemReview(meeting.id,'action_items',i,'edited',editDraft);setEditingItem(null);}} style={{padding:'3px 10px',background:'#2563EB',color:'#fff',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Save</button>
                                <button onClick={()=>setEditingItem(null)} style={{padding:'3px 10px',background:'transparent',border:'1px solid rgba(61,31,13,.15)',borderRadius:5,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!items.length&&<div style={{fontSize:13,color:'rgba(61,31,13,.35)',textAlign:'center',padding:24}}>No action items extracted.</div>}
                </div>
              );
            })()}

            {/* ENTITIES TAB */}
            {tab==='entities'&&(()=>{
              const ext=(meeting.extractions||[])[0]||{};
              const entities=ext.entities||[];
              const byType={};
              entities.forEach(e=>{if(!byType[e.entity_type])byType[e.entity_type]=[];byType[e.entity_type].push(e);});
              const typeColors={person:'#dbeafe',org:'#ede9fe',location:'#d1fae5',money_amount:'#fef3c7',date:'#fee2e2',property:'#fce7f3',other:'#f3f4f6'};
              const typeText={person:'#1d4ed8',org:'#6d28d9',location:'#065f46',money_amount:'#92400e',date:'#dc2626',property:'#9d174d',other:'#4b5563'};
              return(
                <div className="card" style={{padding:18}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:14}}>Named Entities ({entities.length})</div>
                  {Object.entries(byType).map(([type,items])=>(
                    <div key={type} style={{marginBottom:14}}>
                      <div style={{fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'rgba(61,31,13,.5)',marginBottom:8}}>{type.replace('_',' ')}</div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                        {items.map((e,i)=>(
                          <div key={i} style={{background:typeColors[type]||'#f3f4f6',color:typeText[type]||'#4b5563',padding:'5px 12px',borderRadius:8,fontSize:12,fontWeight:600}}>
                            {e.entity_text}
                            {e.normalized_value&&<span style={{fontWeight:400,opacity:.7,marginLeft:4}}>→ {e.normalized_value}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!entities.length&&<div style={{fontSize:13,color:'rgba(61,31,13,.35)',textAlign:'center',padding:24}}>No entities extracted.</div>}
                </div>
              );
            })()}

            {/* PRE-MEETING BRIEF TAB */}
            {tab==='brief'&&(
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                {!meeting.dealSlug&&(
                  <div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:13}}>
                    Pre-Meeting Brief requires a deal link. Associate this meeting with a deal slug when ingesting.
                  </div>
                )}
                {meeting.dealSlug&&briefLoading&&(
                  <div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:13}}>Generating brief with Sonnet…</div>
                )}
                {brief&&!briefLoading&&(()=>{
                  const signalColors={leverage:'#dbeafe',risk:'#fee2e2',opportunity:'#d1fae5',relationship:'#ede9fe'};
                  const signalText={leverage:'#1d4ed8',risk:'#dc2626',opportunity:'#065f46',relationship:'#6d28d9'};
                  return(
                    <>
                      <div className="card" style={{padding:16,background:'#3D1F0D',borderRadius:12}}>
                        <div style={{fontSize:11,fontWeight:700,color:'#E8813A',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>◎ Pre-Meeting Brief</div>
                        <div style={{fontSize:15,fontWeight:800,color:'#fff',marginBottom:2}}>{brief.deal}</div>
                        <div style={{fontSize:12,color:'rgba(255,255,255,.45)'}}>{brief.generated_for}</div>
                      </div>
                      {brief.last_meeting_summary&&(
                        <div className="card" style={{padding:16}}>
                          <div style={{fontWeight:700,fontSize:12,marginBottom:10,color:'rgba(61,31,13,.6)',textTransform:'uppercase',letterSpacing:'.05em'}}>Last Meeting — {brief.last_meeting_summary.date}</div>
                          <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>{brief.last_meeting_summary.title}</div>
                          {(brief.last_meeting_summary.key_points||[]).map((p,i)=>(
                            <div key={i} style={{display:'flex',gap:8,padding:'7px 10px',background:'rgba(61,31,13,.03)',borderRadius:7,marginBottom:5,fontSize:12,color:'rgba(61,31,13,.8)'}}>
                              <span style={{color:'#E8813A',flexShrink:0}}>•</span>{p}
                            </div>
                          ))}
                        </div>
                      )}
                      {(brief.active_signals||[]).length>0&&(
                        <div className="card" style={{padding:16}}>
                          <div style={{fontWeight:700,fontSize:12,marginBottom:10,color:'rgba(61,31,13,.6)',textTransform:'uppercase',letterSpacing:'.05em'}}>Active Signals</div>
                          {brief.active_signals.map((s,i)=>(
                            <div key={i} style={{padding:'10px 12px',background:signalColors[s.signal_type]||'#f3f4f6',border:`1px solid ${signalText[s.signal_type]||'#4b5563'}22`,borderRadius:9,marginBottom:8}}>
                              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                                <span style={{fontSize:10,fontWeight:700,color:signalText[s.signal_type]||'#4b5563',textTransform:'uppercase',letterSpacing:'.06em'}}>{s.signal_type}</span>
                                <span style={{fontSize:9,color:'rgba(61,31,13,.4)',fontWeight:600}}>{s.strength}</span>
                              </div>
                              <div style={{fontSize:12,color:'rgba(61,31,13,.8)'}}>{s.description}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {(brief.recommended_framing||[]).length>0&&(
                        <div className="card" style={{padding:16,borderLeft:'4px solid #E8813A'}}>
                          <div style={{fontWeight:700,fontSize:12,marginBottom:10,color:'rgba(61,31,13,.6)',textTransform:'uppercase',letterSpacing:'.05em'}}>Recommended Framing</div>
                          {brief.recommended_framing.map((r,i)=>(
                            <div key={i} style={{display:'flex',gap:8,padding:'8px 10px',background:'rgba(232,129,58,.04)',border:'1px solid rgba(232,129,58,.12)',borderRadius:8,marginBottom:6,fontSize:12}}>
                              <span style={{color:'#E8813A',fontWeight:700,flexShrink:0}}>{i+1}.</span>
                              <span style={{color:'#3D1F0D'}}>{r}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {(brief.watch_for||[]).length>0&&(
                        <div className="card" style={{padding:16,borderLeft:'4px solid #8b0020'}}>
                          <div style={{fontWeight:700,fontSize:12,marginBottom:10,color:'#8b0020',textTransform:'uppercase',letterSpacing:'.05em'}}>Watch For</div>
                          {brief.watch_for.map((w,i)=>(
                            <div key={i} style={{display:'flex',gap:8,padding:'8px 10px',background:'rgba(139,0,33,.03)',border:'1px solid rgba(139,0,33,.1)',borderRadius:8,marginBottom:6,fontSize:12,color:'rgba(61,31,13,.8)'}}>
                              <span style={{color:'#8b0020',flexShrink:0}}>⚑</span>{w}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        ):(
          !loading&&<div className="card" style={{padding:48,textAlign:'center',color:'rgba(61,31,13,.35)',fontSize:14}}>
            Select a meeting to view details, or ingest your first transcript.
          </div>
        )}
      </div>
    </div>
  );
}

function SagePage({trustStage,setTrustStage,onSageAsk,maxStage}){
  const _ceil=(typeof maxStage==='number'&&maxStage>=1)?maxStage:5; // data-supported ceiling
  const {insights:_liveInsights,insightsIsLive,tws:_tws,isDemo}=useData();
  const authFetch=useAuthFetch();
  // EW3 — weekly digest reads the org's live TWS, falling back to the canonical
  // constant. Keeps the digest consistent with the Status Brief ring/number.
  const twsComposite=(_tws&&_tws.available)?_tws.composite:TWS_COMPOSITE;
  // Demo data renders ONLY on the demo account; real accounts get live or empty.
  const _insights=(_liveInsights&&_liveInsights.length)?_liveInsights:(isDemo?SAGE_INSIGHTS:[]);

  // Map live insights → SAGE_ALERTS shape for Threshold Alerts section
  // Falls back to hardcoded SAGE_ALERTS if no live insights yet
  const alertsAreLive=insightsIsLive;
  const sageAlerts=(alertsAreLive)
    ?_liveInsights.map((ins,i)=>({
        id:ins.id||'ins-'+i,
        sev:ins.conf>=85?'high':ins.conf>=70?'medium':'low',
        title:ins.title,
        obs:ins.body,
        action:ins.action||'Review in Sage Intelligence panel.',
        conf:ins.conf,
        domain:ins.type||'cross-domain',
      }))
    :(isDemo?SAGE_ALERTS:[]);

  const [prompt,setPrompt]=useState('');
  const [dismissedAlertIds,setDismissedAlertIds]=useState(new Set());
  const [evidenceOpen,setEvidenceOpen]=useState(new Set());   // alert ids with evidence expanded
  const [regenerating,setRegenerating]=useState(false);
  const [regenNote,setRegenNote]=useState('');
  const {refresh}=useData();
  // Route a question into the Sage panel (the page-level orchestrator handles it).
  const sage=msg=>onSageAsk&&onSageAsk(msg,'sage');
  const toggleEvidence=id=>setEvidenceOpen(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});

  const handleRegenerate=async()=>{
    setRegenerating(true);
    setRegenNote('');
    try{
      // authFetch adds Bearer token (required when Clerk auth is enabled on Railway)
      const data=await authFetch('/api/insights/generate',{method:'POST'});
      if(data&&data.ok){
        await refresh();
        setRegenNote(`✓ ${data.generated} insights generated from live data`);
      } else if(data&&data.detail){
        setRegenNote(`⚠ ${data.detail}`);
      } else {
        setRegenNote('⚠ Generation failed — using existing insights');
      }
    }catch(e){
      // authFetch throws on network failure; 4xx/5xx bodies are returned as parsed JSON
      const msg=e?.message||'';
      if(msg.includes('401')||msg.includes('403')){
        setRegenNote('⚠ Not authorised — please sign in again');
      } else if(msg.includes('429')){
        setRegenNote('⚠ Rate limit — wait 5 min between regenerations');
      } else {
        setRegenNote('⚠ Could not reach server — check connection');
      }
    }finally{
      setRegenerating(false);
      setTimeout(()=>setRegenNote(''),8000);
    }
  };

  const [synthesizing,setSynthesizing]=useState(false);
  const handleCrossDomain=async()=>{
    setSynthesizing(true);
    setRegenNote('');
    try{
      // Grounded cross-domain synthesis: collisions across portfolio · deals · meetings · Safe
      const data=await authFetch('/api/insights/cross-domain',{method:'POST'});
      if(data&&data.ok){
        await refresh();
        setRegenNote(data.count?`✓ ${data.count} cross-domain insight${data.count>1?'s':''} synthesised`
                              :'· No new cross-domain collisions surfaced');
      } else if(data&&data.detail){
        setRegenNote(`⚠ ${data.detail}`);
      } else {
        setRegenNote('⚠ Synthesis failed — using existing insights');
      }
    }catch(e){
      const msg=e?.message||'';
      if(msg.includes('401')||msg.includes('403')) setRegenNote('⚠ Not authorised — please sign in again');
      else if(msg.includes('429')) setRegenNote('⚠ Rate limit — wait a few minutes between syntheses');
      else setRegenNote('⚠ Could not reach server — check connection');
    }finally{
      setSynthesizing(false);
      setTimeout(()=>setRegenNote(''),8000);
    }
  };

  // Dismiss an alert by ID (client-side, ephemeral within session)
  const dismissAlert=(id)=>setDismissedAlertIds(prev=>new Set([...prev,id]));

  // Filtered alert list (excludes dismissed)
  const visibleAlerts=sageAlerts.filter(a=>!dismissedAlertIds.has(a.id));

  const handleScribeExport=()=>{
    const lines=['STAGE STATUS — SAGE INTELLIGENCE BRIEF','Generated: '+new Date().toLocaleDateString(),''];
    _insights.forEach(ins=>{
      lines.push(`[${ins.agent||'Sage'}] ${ins.title}`);
      lines.push(`Confidence: ${ins.conf}%`);
      if(ins.body)lines.push(ins.body);
      if(ins.action)lines.push('Action: '+ins.action);
      lines.push('');
    });
    const a=document.createElement('a');
    a.href=URL.createObjectURL(new Blob([lines.join('\n')],{type:'text/plain'}));
    a.download=`sage-intelligence-brief-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
  };

  const handleExportPDF=()=>window.print();

  // Inline Ask Sage panel: route to the real WS overlay instead of a mock
  const handleAsk=()=>{
    if(!prompt.trim())return;
    onSageAsk(prompt);
    setPrompt('');
  };

  const TRUST=[
    {n:1,label:'Observer',desc:'Explore Mode — raw signals only'},
    {n:2,label:'Hypothesis',desc:'Beliefs tracked, no gap scoring'},
    {n:3,label:'Evidence',desc:'Insight strips + drill-through'},
    {n:4,label:'Standard',desc:'Full TWS + Weight Studio'},
    {n:5,label:'Advocate',desc:'Evidence briefs + Scribe export'},
  ];
  const SAMPLES=['Why did our Culture score drop this week?','Which asset has the best TWS score?','What should we know before renewing 1 Penn Plaza?','How does co-presence compare to our standard?'];
  return(
    <div style={{maxWidth:1100,margin:'0 auto'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <div className="page-header-label">Sage Intelligence</div>
          <div className="page-title">Portfolio Intelligence Brief</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.45)',marginTop:3}}>Week ending May 5, 2026 · Cross-domain synthesis across all Status panels</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {regenNote&&<span style={{fontSize:11,color:regenNote.startsWith('✓')?'#059669':'#d97706',fontWeight:600}}>{regenNote}</span>}
          <button className="btn-ghost" style={{fontSize:12,opacity:regenerating?.6:1}} onClick={handleRegenerate} disabled={regenerating}>
            {regenerating?'⟳ Generating…':'⟳ Regenerate'}
          </button>
          <button className="btn-ghost" style={{fontSize:12,opacity:synthesizing?.6:1}} onClick={handleCrossDomain} disabled={synthesizing}
            title="Find grounded collisions across portfolio, deals, meetings & the Safe ledger — every insight cites real records">
            {synthesizing?'⟳ Synthesising…':'⊕ Cross-domain'}
          </button>
          {trustStage>=5
            ?<button className="btn-ghost" style={{fontSize:12}} onClick={handleScribeExport}>↓ Scribe Export</button>
            :<button className="btn-ghost" style={{fontSize:12,opacity:.35,cursor:'not-allowed'}} title={`Scribe Export unlocks at Stage 5 (Advocate). Current: Stage ${trustStage}`} disabled>↓ Scribe Export</button>
          }
        </div>
      </div>

      {/* Trust Ladder */}
      <div className="card" style={{padding:16,marginBottom:16}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
          <span style={{fontSize:12,fontWeight:700,color:'rgba(61,31,13,.55)',textTransform:'uppercase',letterSpacing:'.06em'}}>Trust Ladder — Organization Stage</span>
          <span style={{fontSize:11,color:'rgba(61,31,13,.35)'}}>Stage {trustStage} of 5</span>
        </div>
        <div style={{display:'flex',gap:4}}>
          {TRUST.map(s=>{
            // Stages above the data ceiling are LOCKED — not clickable, with a
            // concrete "add X to unlock" tooltip. Sage's stage tracks the org's
            // grounded data, never the user's optimism.
            const locked=s.n>_ceil;
            return(
            <div key={s.n} onClick={()=>{if(!locked)setTrustStage(s.n);}} title={locked?`Locked — ${TRUST_UNLOCK[s.n]||'add more grounded data to unlock'}`:''} style={{flex:1,padding:'8px 10px',borderRadius:8,cursor:locked?'not-allowed':'pointer',opacity:locked?.5:1,border:`1px solid ${s.n===trustStage?'#8b0020':'rgba(61,31,13,.1)'}`,background:s.n===trustStage?'rgba(139,0,33,.06)':s.n<trustStage?'rgba(61,31,13,.02)':'transparent',transition:'all .15s'}}>
              <div style={{fontSize:11,fontWeight:700,color:s.n===trustStage?'#8b0020':'rgba(61,31,13,.45)'}}>{locked?'🔒 ':''}{s.n}. {s.label}</div>
              <div style={{fontSize:9,color:'rgba(61,31,13,.3)',marginTop:2,lineHeight:1.3}}>{s.desc}</div>
            </div>
            );
          })}
        </div>
        {_ceil<5&&(
          <div style={{fontSize:10.5,color:'rgba(61,31,13,.45)',marginTop:8,lineHeight:1.5}}>
            Sage is grounded at <b style={{color:'#8b0020'}}>Stage {_ceil}</b> by your current data. To unlock <b>Stage {_ceil+1} — {TRUST[_ceil]?TRUST[_ceil].label:''}</b>: {TRUST_UNLOCK[_ceil+1]||'add more grounded data'}.
          </div>
        )}
      </div>

      {/* Stage gate banner for stages 1–2 */}
      {trustStage<3&&(
        <div style={{padding:'12px 16px',marginBottom:16,background:'rgba(139,0,33,.04)',border:'1px solid rgba(139,0,33,.15)',borderRadius:8,display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:16}}>🔒</span>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:'#8b0020'}}>
              {trustStage===1?'Observer Mode — raw signals only':'Hypothesis Mode — pattern tracking only'}
            </div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.55)',marginTop:2}}>
              {trustStage>=_ceil
                ? <>Sage won't infer beyond your data. {trustStage===1?'Cross-Domain Insights, confidence scoring, and Ask Sage':'Cross-Domain Insights and full Sage analysis'} unlock once there's grounded data to support them — {TRUST_UNLOCK[Math.min(trustStage+1,5)]||'add more data'}.</>
                : <>Your data supports up to <b>Stage {_ceil}</b>. Select it in the Trust Ladder above to unlock {trustStage===1?'Cross-Domain Insights, confidence scoring, and Ask Sage':'full Sage analysis'}.</>}
            </div>
          </div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:16}}>
        <div>
          {/* Threshold Alerts — visible at all stages */}
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>Threshold Alerts</div>
            {isDemo&&!alertsAreLive&&(
              <span style={{fontSize:9,fontWeight:700,background:'#D97706',color:'#fff',borderRadius:4,padding:'1px 6px',letterSpacing:'.06em'}}>DEMO</span>
            )}
          </div>
          {!isDemo&&visibleAlerts.length===0&&_insights.length===0&&(
            <div className="card" style={{padding:'24px 20px',textAlign:'center',color:'rgba(61,31,13,.55)',fontSize:13}}>
              No alerts or insights yet — Sage surfaces them here as it observes your portfolio and experience data.
            </div>
          )}
          {visibleAlerts.map((alert,i)=>(
            <div key={alert.id} className="card" style={{padding:18,marginBottom:10,borderLeft:`4px solid ${alert.sev==='high'?'#dc2626':'#d97706'}`,animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*80}ms`}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:8}}>
                <div style={{flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:999,background:alert.sev==='high'?'#fef2f2':'#fffbeb',color:alert.sev==='high'?'#dc2626':'#d97706',textTransform:'uppercase'}}>{alert.sev}</span>
                    <span style={{fontSize:9,fontWeight:600,color:'rgba(61,31,13,.4)',padding:'2px 7px',borderRadius:999,background:'rgba(61,31,13,.06)'}}>{alert.domain}</span>
                  </div>
                  <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>{alert.title}</div>
                </div>
                {/* Confidence score: hidden at stages 1–2 */}
                {trustStage>=3
                  ?<div style={{fontSize:11,color:'rgba(61,31,13,.35)',flexShrink:0}}>Conf: {alert.conf}%</div>
                  :<div style={{fontSize:11,color:'rgba(61,31,13,.2)',flexShrink:0}}>Conf: —</div>
                }
              </div>
              <p style={{fontSize:12.5,color:'rgba(61,31,13,.65)',lineHeight:1.65,margin:'0 0 10px'}}>{alert.obs}</p>
              {/* Recommended action: only at stage 3+ */}
              {trustStage>=3&&(
                <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',padding:'8px 12px',background:'rgba(61,31,13,.04)',borderRadius:8,marginBottom:10,borderLeft:'3px solid rgba(61,31,13,.12)'}}>→ {alert.action}</div>
              )}
              <div style={{display:'flex',gap:8}}>
                {trustStage>=3&&<button onClick={()=>toggleEvidence(alert.id)} className="btn-primary" style={{fontSize:11,padding:'5px 12px',background:'#8b0020'}}>{evidenceOpen.has(alert.id)?'Hide Evidence':'View Evidence'}</button>}
                {trustStage>=2&&<button onClick={()=>sage(`Walk me through the evidence behind this Sage alert: "${alert.title}". ${alert.obs} What's the recommended next step?`)} className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}}>Ask Sage</button>}
                <button onClick={()=>dismissAlert(alert.id)} className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}}>Dismiss</button>
              </div>
              {trustStage>=3&&evidenceOpen.has(alert.id)&&(
                <div className="focus-fade" style={{marginTop:10,padding:'10px 12px',background:'rgba(61,31,13,.03)',border:'1px solid rgba(61,31,13,.08)',borderRadius:8}}>
                  <div style={{fontSize:9.5,fontWeight:700,color:'rgba(61,31,13,.45)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>Evidence</div>
                  <div style={{fontSize:11.5,color:'rgba(61,31,13,.7)',lineHeight:1.6,marginBottom:8}}>{alert.obs}</div>
                  <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:10.5,color:'rgba(61,31,13,.5)'}}>
                    <span>Confidence <b style={{color:'#3D1F0D'}}>{alert.conf}%</b></span>
                    <span>Domain <b style={{color:'#3D1F0D'}}>{alert.domain}</b></span>
                    <span>Severity <b style={{color:alert.sev==='high'?'#dc2626':'#d97706'}}>{alert.sev}</b></span>
                  </div>
                  {alert.action&&<div style={{marginTop:8,fontSize:11,color:'#3D1F0D'}}>→ {alert.action}</div>}
                </div>
              )}
            </div>
          ))}

          {/* Cross-Domain Insights — stage 3+ only */}
          {trustStage>=3?(
            <>
              <div style={{fontWeight:700,fontSize:13,color:'#3D1F0D',marginBottom:10,marginTop:20}}>Cross-Domain Insights</div>
              {_insights.map((insight,i)=>(
                <div key={insight.id} className="card" style={{padding:18,marginBottom:10,borderLeft:'4px solid #8b0020',animation:'fadeUp .3s cubic-bezier(.22,1,.36,1) both',animationDelay:`${i*80}ms`}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:10,marginBottom:8}}>
                    <div>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 7px',borderRadius:999,background:'rgba(139,0,33,.08)',color:'#8b0020',textTransform:'uppercase'}}>{insight.type}</span>
                      <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginTop:5}}>{insight.title}</div>
                    </div>
                    <div style={{fontSize:10,color:'rgba(61,31,13,.35)',flexShrink:0}}>Conf: {insight.conf}%</div>
                  </div>
                  <p style={{fontSize:12.5,color:'rgba(61,31,13,.65)',lineHeight:1.65,margin:'0 0 10px'}}>{insight.body}</p>
                  <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:10}}>
                    {insight.sources.map(s=><span key={s} style={{fontSize:9,color:'rgba(61,31,13,.5)',background:'rgba(61,31,13,.06)',padding:'2px 8px',borderRadius:4}}>{s}</span>)}
                  </div>
                  <div style={{display:'flex',gap:8}}>
                    <button onClick={()=>sage(`Drill through this cross-domain insight: "${insight.title}". ${insight.body} Show me the underlying records${insight.sources&&insight.sources.length?` across ${insight.sources.join(', ')}`:''}.`)} className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}}>Drill Through</button>
                    {trustStage>=5&&<button onClick={()=>sage(`Draft a Scribe memo capturing this insight for the record: "${insight.title}". ${insight.body}`)} className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}}>Draft with Scribe</button>}
                  </div>
                </div>
              ))}
            </>
          ):(
            <div style={{marginTop:20,padding:'20px 16px',background:'rgba(61,31,13,.02)',border:'1px dashed rgba(61,31,13,.12)',borderRadius:8,textAlign:'center'}}>
              <div style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>Cross-Domain Insights unlock at <strong>Stage 3 — Evidence</strong></div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.3)',marginTop:4}}>Click Evidence in the Trust Ladder above to unlock full insight strips and drill-through.</div>
            </div>
          )}
        </div>

        {/* Right panel */}
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {/* Ask Sage — stage 2+ only; stage 1 shows locked state */}
          {trustStage>=2?(
          <div className="card" style={{padding:18}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>Ask Sage</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>Opens Sage chat with your question. Evidence-first, never longer than 200 words.</div>
            <div style={{display:'flex',gap:8,marginBottom:10}}>
              <input value={prompt} onChange={e=>setPrompt(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleAsk()} placeholder="Ask about your portfolio…" style={{flex:1,background:'#F5EDE0',border:'1px solid rgba(61,31,13,.1)',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#3D1F0D',outline:'none',fontFamily:'inherit'}}/>
              <button onClick={handleAsk} style={{background:'#8b0020',color:'#fff',border:'none',borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>→</button>
            </div>
            {!prompt&&(
              <div style={{display:'flex',flexDirection:'column',gap:4}}>
                {SAMPLES.map(q=>(
                  <button key={q} onClick={()=>onSageAsk(q)} style={{textAlign:'left',fontSize:11,color:'rgba(61,31,13,.55)',background:'rgba(61,31,13,.04)',border:'1px solid rgba(61,31,13,.08)',borderRadius:6,padding:'6px 10px',cursor:'pointer',fontFamily:'inherit'}} onMouseOver={e=>e.currentTarget.style.background='rgba(61,31,13,.08)'} onMouseOut={e=>e.currentTarget.style.background='rgba(61,31,13,.04)'}>{q}</button>
                ))}
              </div>
            )}
          </div>
          ):(
          <div className="card" style={{padding:18,opacity:.55}}>
            <div style={{fontWeight:700,fontSize:13,marginBottom:4}}>Ask Sage</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.4)',marginBottom:12}}>Unlocks at Stage 2 — Hypothesis</div>
            <div style={{padding:'28px 0',textAlign:'center',color:'rgba(61,31,13,.3)',fontSize:12}}>🔒 Advance to Stage 2 to ask Sage questions</div>
          </div>
          )}

          {/* Weekly Intelligence Brief — demo-only narrative (stage 3+) */}
          {isDemo&&trustStage>=3&&(
          <div className="card" style={{padding:18}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
              <div style={{fontWeight:700,fontSize:13}}>Weekly Intelligence Brief</div>
              <span style={{fontSize:9,fontWeight:700,background:'rgba(139,0,33,.08)',color:'#8b0020',padding:'2px 8px',borderRadius:999}}>May W1 2026</span>
            </div>
            <p style={{fontSize:12.5,color:'rgba(61,31,13,.7)',lineHeight:1.7,margin:'0 0 10px'}}>The portfolio's TWS composite held at {twsComposite} this week — steady on the surface, but the Culture pillar is the signal that demands attention. A six-week declining trend driven by co-presence and belonging signals at 1 Penn Plaza now intersects with a lease renewal decision 4 months away.</p>
            <p style={{fontSize:12.5,color:'rgba(61,31,13,.7)',lineHeight:1.7,margin:'0 0 12px'}}>The Technology division's co-presence standard has not been met in 8 consecutive weeks. Before flagging this as underperformance, Sage recommends reviewing whether the standard remains correctly calibrated for post-2025 hybrid patterns.</p>
            <div style={{display:'flex',gap:8,paddingTop:10,borderTop:'1px solid rgba(61,31,13,.08)'}}>
              {trustStage>=5&&<button onClick={()=>sage(`Draft this week's Weekly Intelligence Brief with Scribe — TWS composite ${twsComposite}, Culture pillar declining (co-presence + belonging at 1 Penn Plaza), Technology co-presence standard missed 8 weeks. Produce a board-ready narrative.`)} className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}}>Draft with Scribe</button>}
              <button className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}} onClick={handleExportPDF}>↓ Export PDF</button>
            </div>
          </div>
          )}

          {/* Epistemic posture note */}
          <div style={{padding:'12px 14px',background:'rgba(245,237,224,.5)',borderRadius:8,border:'1px solid rgba(61,31,13,.08)'}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:6}}>How Sage Works</div>
            <div style={{display:'flex',flexDirection:'column',gap:5}}>
              {['States confidence alongside every claim','Leads with signal — you draw the conclusion','Never presents inferences as facts','Suggests interventions, waits to be asked to act','References integration gaps when affecting score quality'].map(p=>(
                <div key={p} style={{fontSize:11,color:'rgba(61,31,13,.55)',display:'flex',alignItems:'flex-start',gap:5}}><span style={{color:'#8b0020',flexShrink:0}}>·</span>{p}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const AUDIT_LOG=[
  {time:'07:04',agent:'Sage',action:'Brief generation — portfolio digest',conf:92,pending:false},
  {time:'07:02',agent:'Scribe',action:'Abstract review queued — Houston Termination v4',conf:97,pending:true},
  {time:'06:48',agent:'Scout',action:'Comp scan — Chicago CBD 25–35K SF (47 results)',conf:89,pending:false},
  {time:'06:31',agent:'Strat',action:'Occupancy forecast — 444 Market St, 6-month',conf:84,pending:false},
  {time:'05:15',agent:'Sage',action:'Alert: 1 Penn Plaza velocity below threshold',conf:88,pending:false},
  {time:'04:02',agent:'Scout',action:'Market benchmark refresh — all markets',conf:91,pending:false},
  {time:'03:44',agent:'Scribe',action:'Redline delta — Houston Termination Agreement v4',conf:96,pending:false},
  {time:'02:10',agent:'Strat',action:'Renewal scenario — Willis Tower Q3 2026',conf:86,pending:false},
];

/* ── AA-1 TRUST ARCHITECTURE DATA ───────────────────────── */
const AA1_AGENTS=[
  {id:'sense',name:'Sense',role:'Experience Intelligence',phase:'P1',status:'Active',sandbox:false,
   domains:['RBW-I','Settings Scorecard','Pulse','Utilisation'],
   safePerms:{facts:'R',assumptions:'RW',decisions:'R'},
   desc:'Processes RBW-I sensor data, experience settings, and occupancy signals. Surfaces drift and friction patterns.',
   handoffs:[
     {phase:1,label:'Culture brief → Setting brief',status:'active',desc:'At Standard Setting creation, Sage checks for a Sense culture brief and auto-populates engagement scope: culture archetype, relationship-based work priorities, spatial requirements.'},
     {phase:2,label:'Floor plan → option fit scoring',status:'active',desc:'Sense reads every submitted floor plan as a relationship map and scores it against the occupier\'s culture archetype. ⚡ First Shock Moment — options evaluated on culture fit, not just SF and cost. Decision capture (⚡ Second Shock Moment) follows: Sage infers decisions at stage close and prompts confirmation — the occupier sees, for the first time, a structured record of every choice they made.'},
     {phase:3,label:'Post-execution → experience baseline',status:'planned',desc:'At Standard Setting close, Sense establishes experience monitoring against the brief\'s spatial commitments. Friction signals surface against original intent. ⚡ Third Shock Moment (Sage): one Setting generates role-aware stakeholder summaries — CFO, legal, HR, executive sponsor — without the team writing five separate documents.'},
   ]},
  {id:'strat',name:'Strat',role:'Finance & FP&A',phase:'P1',status:'Active',sandbox:false,
   domains:['Portfolio Spine','Financial Surface','Renewal Scenarios'],
   safePerms:{facts:'RW',assumptions:'RW',decisions:'R'},
   desc:'Portfolio economics, occupancy forecasting, renewal modelling, and capital scenario analysis.'},
  {id:'scope',name:'Scope',role:'Delivery & Project',phase:'P2',status:'Active',sandbox:false,
   domains:['TI Projects','Delivery Milestones','Change Orders'],
   safePerms:{facts:'RW',assumptions:'RW',decisions:'R'},
   desc:'Construction delivery, TI milestone tracking, change order intelligence, Budget Risk Alerts, and day-one readiness. Phase 2 active.'},
  {id:'scribe',name:'Scribe',role:'Legal & Lease Abstraction',phase:'P1',status:'Active',sandbox:false,
   domains:['Lease Documents','Abstracts','Redlines','Terminations'],
   safePerms:{facts:'RW',assumptions:'R',decisions:'none'},
   desc:'Lease abstraction, document drafting, redline monitoring. All publish actions require human gate.'},
  {id:'scout',name:'Scout',role:'Market Intelligence',phase:'P1',status:'Active',sandbox:true,
   domains:['Market Comps','Site Selection','Landlord Data'],
   safePerms:{facts:'R',assumptions:'none',decisions:'none'},
   desc:'Market research, comp analysis, site selection. Sandboxed — read-only access to Safe, no write to operational data.'},
  {id:'shop',name:'Shop',role:'Transactional',phase:'P3',status:'Planned',sandbox:false,
   domains:['Procurement','Vendor Bids','Purchase Orders'],
   safePerms:{facts:'R',assumptions:'none',decisions:'none'},
   desc:'Transactional operations, vendor procurement, purchase authorisation. Phase 3 target.'},
];

const SAFE_ASSETS=[
  {id:'f1',slug:'fact-lease-term-1-penn',class:'Fact',title:'Lease term — 1 Penn Plaza',source:'Scribe',agent:'Scribe',date:'2026-04-28',conf:99,version:1,isActive:true,provenance:'Extracted from Executed_Lease_1Penn.pdf p.3 §2.1. Reviewed and approved by advisor.',approved:true,approver:'Marcus Chen'},
  {id:'f2',slug:'fact-headcount-hq',class:'Fact',title:'Headcount — Corporate HQ floors 14–15',source:'HRIS',agent:'Sense',date:'2026-05-01',conf:97,version:1,isActive:true,provenance:'Synced from Workday HRIS API 2026-05-01T06:00Z. Rolling 30-day avg.',approved:true,approver:'System'},
  {id:'f3',slug:'fact-q1-utilisation',class:'Fact',title:'Q1 2026 utilisation — all locations',source:'RBW-I',agent:'Sense',date:'2026-04-30',conf:95,version:1,isActive:true,provenance:'RBW-I sensor aggregate export. 90-day rolling average across 8 locations.',approved:true,approver:'System'},
  {id:'f4',slug:'fact-rent-30-hudson',class:'Fact',title:'Annual base rent — 30 Hudson Yards',source:'Scribe',agent:'Scribe',date:'2026-04-15',conf:99,version:1,isActive:true,provenance:'Executed_Lease_30HY.pdf §3 Rent Schedule. Confirmed by Jordan Wells.',approved:true,approver:'Jordan Wells'},
  {id:'a1',slug:'assumption-headcount-growth',class:'Assumption',title:'Headcount growth +12% by Q4 2026',source:'FP&A Model',agent:'Strat',date:'2026-04-10',conf:72,version:1,isActive:true,provenance:'Based on Q1 hiring plan + FP&A Q4 projection (v3.2). Not yet validated by CHRO.',approved:false,approver:null},
  {id:'a2',slug:'assumption-market-rent-nyc',class:'Assumption',title:'Market rent compression -8% NYC 2026',source:'Scout',agent:'Scout',date:'2026-04-22',conf:67,version:1,isActive:true,provenance:'Scout analysis: 14 NYC comps, CBRE Q1 2026 report. Sandboxed — pending advisor review.',approved:false,approver:null},
  {id:'a3',slug:'assumption-willis-tower-renewal',class:'Assumption',title:'Willis Tower renewal — landlord accepts market rate',source:'Strat',agent:'Strat',date:'2026-04-29',conf:58,version:1,isActive:true,provenance:'Strat scenario model v2.1. Assumption not yet validated by Priya Nair or landlord contact.',approved:false,approver:null},
  {id:'d1',slug:'decision-houston-exit',class:'Decision',title:'Reject Houston renewal — pursue exit strategy',source:'Human',agent:'Human',date:'2026-04-01',conf:100,version:1,isActive:true,provenance:'CFO approval via DocuSign. Board minutes 2026-04-01. Rachel Torres authorisation on file.',approved:true,approver:'Rachel Torres (CFO)'},
  {id:'d2',slug:'decision-austin-loi',class:'Decision',title:'Proceed Austin expansion — 2845 Domain Drive LOI',source:'Human',agent:'Human',date:'2026-04-25',conf:100,version:1,isActive:true,provenance:'VP Real Estate sign-off. Priya Nair email 2026-04-25. LOI submitted April 25.',approved:true,approver:'Priya Nair'},
  {id:'d3',slug:'decision-sf-sublease',class:'Decision',title:'Sublease 7,000 SF SF headquarters',source:'Human',agent:'Human',date:'2026-04-30',conf:100,version:1,isActive:true,provenance:'CFO Board deck approval. Rachel Torres. Board deck Apr-2026. Broker engaged.',approved:true,approver:'Rachel Torres (CFO)'},
];

const SENTRY_ARTIFACTS=[
  {id:'sa1',tier:'Pre-action brief',agent:'Scribe',action:'Publish lease abstract — 1 Penn Plaza to Portfolio Spine',ts:'2026-05-07T07:02',conf:97,status:'Pending Gate',gateOwner:'Marcus Chen'},
  {id:'sa2',tier:'Human gate',agent:'Scribe',action:'Lease abstract approved — 1 Penn Plaza (previous cycle)',ts:'2026-05-06T14:30',conf:97,status:'Approved',gateOwner:'Marcus Chen',approvedAt:'2026-05-06T14:35'},
  {id:'sa3',tier:'Post-action confirmation',agent:'Scribe',action:'Portfolio Spine updated — 1 Penn Plaza lease data written to Safe',ts:'2026-05-06T14:36',conf:99,status:'Confirmed',gateOwner:'System'},
  {id:'sa4',tier:'Pre-action brief',agent:'Strat',action:'Recommend Willis Tower renewal at market -5%',ts:'2026-05-06T11:15',conf:84,status:'Pending Gate',gateOwner:'Priya Nair'},
  {id:'sa5',tier:'Escalation record',agent:'Sage',action:'1 Penn Plaza deal velocity below threshold — advisor alert triggered',ts:'2026-05-07T05:15',conf:88,status:'Escalated',gateOwner:'Marcus Chen'},
  {id:'sa6',tier:'Post-action confirmation',agent:'Scout',action:'Market comp scan — Chicago CBD 25–35K SF (sandboxed, read-only)',ts:'2026-05-07T06:48',conf:89,status:'Confirmed',gateOwner:'System'},
  {id:'sa7',tier:'Human gate',agent:'Strat',action:'Occupancy forecast approved — 444 Market St 6-month projection',ts:'2026-05-06T09:00',conf:84,status:'Approved',gateOwner:'Jordan Wells',approvedAt:'2026-05-06T09:14'},
];

const INTEGRATIONS=[
  {id:'hris',name:'HR · Workday HRIS',category:'HR',status:'Connected',lastSync:'2026-05-07T06:00Z',
   dataIn:['Headcount by location','Dept org structure','Hire plan Q3–Q4'],
   dataOut:['Space allocation targets','Return-to-office schedule'],
   agent:'Sense',compliance:'SOC 2 Type II'},
  {id:'fpa',name:'Finance · Adaptive Insights',category:'Finance',status:'Connected',lastSync:'2026-05-07T06:15Z',
   dataIn:['Real estate budget','CapEx plan','FP&A headcount model'],
   dataOut:['Renewal scenario outputs','Lease liability ASC 842','Portfolio cost model'],
   agent:'Strat',compliance:'SOC 2 Type II'},
  {id:'legal',name:'Legal · DocuSign CLM',category:'Legal',status:'Connected',lastSync:'2026-05-06T18:00Z',
   dataIn:['Executed lease agreements','Amendment docs','Termination notices'],
   dataOut:['Scribe abstracts','Redline alerts','Signature routing'],
   agent:'Scribe',compliance:'SOC 2 Type II · ISO 27001'},
  {id:'leasing',name:'Leasing · TWS Platform',category:'Leasing',status:'Connected',lastSync:'2026-05-07T07:00Z',
   dataIn:['Market comps','Available inventory','Landlord terms'],
   dataOut:['LOI drafts','Deal velocity signals','Advisor briefs'],
   agent:'Scout',compliance:'SOC 2 Type II'},
  {id:'capital',name:'Capital · Argus Enterprise',category:'Capital',status:'Pending',lastSync:null,
   dataIn:['Asset valuations','DCF models','Cap rate benchmarks'],
   dataOut:['Hold/sell signals','Portfolio IRR','Capital allocation'],
   agent:'Strat',compliance:'SOC 2 Type II'},
];

/* ── TEAM-1 Phase D frontend — Version History panel (PRD §5.5) ─────────────
   Reads the D-2 unified audit log (/api/audit) and renders a chronological
   timeline with before/after deltas for update events. Role-scoped server-side
   (admin/auditor see all, others see their own). */
function VersionHistoryPanel({authFetch}){
  const [entries,setEntries]=useState(null);
  const [err,setErr]=useState('');
  const [filter,setFilter]=useState('all');
  const load=useCallback(()=>{
    setEntries(null); setErr('');
    const q=filter==='all'?'':`?resource_type=${encodeURIComponent(filter)}`;
    authFetch(`/api/audit${q}`)
      .then(d=>setEntries(d&&Array.isArray(d.entries)?d.entries:[]))
      .catch(()=>setErr('Could not load audit history.'));
  },[authFetch,filter]);
  useEffect(()=>{load();},[load]);

  const TYPE_COLOR={create:'#16a34a',update:'#d97706',delete:'#dc2626'};
  const fmtVal=v=>v===null||v===undefined?'∅':(typeof v==='object'?JSON.stringify(v):String(v));

  const Delta=({before,after})=>{
    if(!before&&!after)return null;
    const keys=[...new Set([...Object.keys(before||{}),...Object.keys(after||{})])];
    return(
      <div style={{marginTop:6,fontSize:11,fontFamily:'monospace',background:'rgba(61,31,13,.03)',borderRadius:6,padding:'6px 8px'}}>
        {keys.map(k=>{
          const b=before?before[k]:undefined, a=after?after[k]:undefined;
          const changed=fmtVal(b)!==fmtVal(a);
          return(
            <div key={k} style={{display:'flex',gap:6,flexWrap:'wrap'}}>
              <span style={{color:'rgba(61,31,13,.5)'}}>{k}:</span>
              {before&&<span style={{color:'#dc2626',textDecoration:changed?'line-through':'none'}}>{fmtVal(b)}</span>}
              {before&&after&&changed&&<span style={{color:'rgba(61,31,13,.3)'}}>→</span>}
              {after&&changed&&<span style={{color:'#16a34a'}}>{fmtVal(a)}</span>}
            </div>
          );
        })}
      </div>
    );
  };

  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Version History</div>
        <span style={{fontSize:11,color:'rgba(61,31,13,.45)'}}>Audit trail of consequential workspace changes</span>
        <span style={{marginLeft:'auto',display:'inline-flex',gap:4}}>
          {[['all','All'],['action','Actions'],['workflow','Workflows'],['deal','Deals']].map(([v,lbl])=>(
            <button key={v} onClick={()=>setFilter(v)} style={{padding:'4px 10px',borderRadius:6,border:'1px solid rgba(61,31,13,.12)',background:filter===v?'#3D1F0D':'#fff',color:filter===v?'#fff':'rgba(61,31,13,.55)',fontWeight:600,fontSize:11,cursor:'pointer',fontFamily:'inherit'}}>{lbl}</button>
          ))}
        </span>
      </div>
      {err&&<div style={{fontSize:12,color:'#dc2626'}}>{err}</div>}
      {entries===null&&!err&&<div style={{fontSize:12,color:'rgba(61,31,13,.4)'}}>Loading…</div>}
      {entries&&entries.length===0&&<div style={{fontSize:12,color:'rgba(61,31,13,.35)'}}>No audit entries yet.</div>}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {entries&&entries.map(e=>(
          <div key={e.id} className="card" style={{padding:'10px 14px',borderLeft:`3px solid ${TYPE_COLOR[e.actionType]||'#888'}`}}>
            <div style={{display:'flex',alignItems:'baseline',gap:8,flexWrap:'wrap'}}>
              <span style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:'.04em',color:TYPE_COLOR[e.actionType]||'#888'}}>{e.actionType}</span>
              <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{e.verb}</span>
              <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{e.resourceType}{e.resourceId?` · ${e.resourceId}`:''}</span>
              <span style={{marginLeft:'auto',fontSize:10,color:'rgba(61,31,13,.4)'}}>{e.createdAt?new Date(e.createdAt).toLocaleString():''}</span>
            </div>
            <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:2}}>by {e.actorName||e.actorUserId||'system'}{e.actorRole?` (${e.actorRole})`:''}</div>
            {(e.before||e.after)&&<Delta before={e.before} after={e.after}/>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * TEAM-1 Gap T5 — workflow template authoring UI.
 * Lists built-in + custom templates and (admin) lets you author a custom one:
 * name + ordered approval steps (label + approver role). Saves via
 * POST /api/workflows/templates; custom templates can be deleted.
 */
function WorkflowTemplatesPanel({isAdmin}){
  const authFetch=useAuthFetch();
  const [templates,setTemplates]=useState(null);
  const [roles,setRoles]=useState([]);
  const [name,setName]=useState('');
  const [nodes,setNodes]=useState([{label:'',approver_role:'manager'}]);
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const load=()=>authFetch('/api/workflows/templates').then(d=>{ if(d){setTemplates(d.templates||[]); if(d.approverRoles)setRoles(d.approverRoles);} }).catch(()=>setTemplates([]));
  useEffect(()=>{load();},[]);
  const addNode=()=>setNodes(n=>[...n,{label:'',approver_role:roles[0]||'manager'}]);
  const rmNode=(i)=>setNodes(n=>n.filter((_,j)=>j!==i));
  const setNode=(i,k,v)=>setNodes(n=>n.map((x,j)=>j===i?{...x,[k]:v}:x));
  const save=()=>{
    setErr('');
    if(!name.trim()){setErr('Give the template a name.');return;}
    if(nodes.some(n=>!n.label.trim())){setErr('Every step needs a label.');return;}
    setSaving(true);
    authFetch('/api/workflows/templates',{method:'POST',body:{name:name.trim(),nodes}})
      .then(d=>{ if(d&&d.error){setErr(typeof d.error==='string'?d.error:'Could not save.');return;}
        if(d&&d.detail){setErr(d.detail);return;}
        setName('');setNodes([{label:'',approver_role:roles[0]||'manager'}]);load();})
      .catch(()=>setErr('Could not save the template.')).finally(()=>setSaving(false));
  };
  const del=(t)=>{ if(!window.confirm(`Delete custom template "${t.name}"?`))return;
    authFetch('/api/workflows/templates/'+t.id,{method:'DELETE'}).then(()=>load()).catch(()=>{}); };
  const card={background:'#fff',border:'1px solid rgba(61,31,13,.08)',borderRadius:12,padding:16,marginBottom:14};
  const inp={padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:13,fontFamily:'inherit',outline:'none'};
  return(
    <div>
      <div style={card}>
        <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>Workflow Templates</div>
        <div style={{fontSize:12,color:'rgba(61,31,13,.5)',marginBottom:14}}>Reusable multi-step approval flows. Built-in templates ship with Stage; your team can author custom ones.</div>
        {templates===null?<div style={{fontSize:12,color:'rgba(61,31,13,.5)'}}>Loading…</div>:templates.map(t=>(
          <div key={t.key+(t.id||'')} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderTop:'1px solid rgba(61,31,13,.06)'}}>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600}}>{t.name} {t.builtin
                ?<span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',background:'rgba(61,31,13,.06)',padding:'1px 6px',borderRadius:4,marginLeft:4}}>BUILT-IN</span>
                :<span style={{fontSize:9,fontWeight:700,color:'#8b0020',background:'rgba(139,0,33,.07)',padding:'1px 6px',borderRadius:4,marginLeft:4}}>CUSTOM v{t.version}</span>}</div>
              <div style={{fontSize:11,color:'rgba(61,31,13,.5)',marginTop:2}}>{(t.nodes||[]).map(n=>`${n.label} (${n.approver_role})`).join('  →  ')}</div>
            </div>
            {!t.builtin&&isAdmin&&<button onClick={()=>del(t)} style={{...inp,cursor:'pointer',color:'#dc2626',border:'1px solid rgba(220,38,38,.25)',background:'#fff',fontSize:11,padding:'5px 9px'}}>Delete</button>}
          </div>
        ))}
      </div>
      {isAdmin&&(
        <div style={card}>
          <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Author a custom template</div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Template name (e.g. Vendor Onboarding)" maxLength={80} style={{...inp,width:'100%',marginBottom:10}}/>
          {nodes.map((n,i)=>(
            <div key={i} style={{display:'flex',gap:8,marginBottom:8,alignItems:'center'}}>
              <span style={{fontSize:11,color:'rgba(61,31,13,.4)',width:18}}>{i+1}.</span>
              <input value={n.label} onChange={e=>setNode(i,'label',e.target.value)} placeholder="Step label (e.g. Manager review)" maxLength={80} style={{...inp,flex:1}}/>
              <select value={n.approver_role} onChange={e=>setNode(i,'approver_role',e.target.value)} style={{...inp,cursor:'pointer'}}>
                {(roles.length?roles:['manager','admin','member','auditor']).map(r=><option key={r} value={r}>{r}</option>)}
              </select>
              {nodes.length>1&&<button onClick={()=>rmNode(i)} style={{...inp,cursor:'pointer',padding:'6px 9px',color:'rgba(61,31,13,.5)'}}>✕</button>}
            </div>
          ))}
          <button onClick={addNode} style={{...inp,cursor:'pointer',background:'#F5EDE0',border:'1px dashed rgba(61,31,13,.2)',fontSize:12,marginBottom:12}}>+ Add step</button>
          {err&&<div style={{fontSize:12,color:'#dc2626',marginBottom:10}}>{err}</div>}
          <div>
            <button onClick={save} disabled={saving} style={{...inp,cursor:'pointer',background:'#8b0020',color:'#fff',border:'none',fontWeight:600,padding:'9px 18px'}}>{saving?'Saving…':'Save template'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GovernancePage(){
  const {safeAssets:_sa=SAFE_ASSETS,sentry:_sn=SENTRY_ARTIFACTS,currentUser}=useData();
  const isAdmin=currentUser?.role==='admin'||!currentUser; // open in dev before /api/me resolves
  const [govTab,setGovTab]=useState('overview');
  const [safeClass,setSafeClass]=useState('Fact');
  const [orchMode,setOrchMode]=useState('copilot');
  const [orchModeLoading,setOrchModeLoading]=useState(false);
  const [liveTraces,setLiveTraces]=useState([]);
  const [tracesVersion,setTracesVersion]=useState(0);
  const [selAgent,setSelAgent]=useState(null);
  const [selIntegration,setSelIntegration]=useState(null);
  const [pendingGates,setPendingGates]=useState([]);
  const [clearingGate,setClearingGate]=useState(new Set());
  const [showUpdateModal,setShowUpdateModal]=useState(null); // {slug,title,conf,provenance}
  const [updateDraft,setUpdateDraft]=useState({title:'',provenance:'',conf:''});
  const [gatesVersion,setGatesVersion]=useState(0); // bump to re-fetch
  // ── Corporate Profile (learning loop) ────────────────────────
  const [profileData,setProfileData]=useState(null); // {dimensions,history,pendingProposals}
  const [profileVersion,setProfileVersion]=useState(0); // bump to re-fetch
  const [resolvingProp,setResolvingProp]=useState(new Set());
  const [expandedDim,setExpandedDim]=useState(null); // slug of expanded version history

  // ── Scribe pending approvals ─────────────────────────────────
  const authFetch=useAuthFetch();
  const {addToast}=useToast();
  // Data-deletion request — a formal, audited compliance request (NOT an
  // immediate erasure). Two-step confirm, then a P1 Review action is filed.
  const [delReq,setDelReq]=useState('idle'); // idle | confirm | sending | sent
  const requestDataDeletion=async()=>{
    setDelReq('sending');
    try{
      const r=await authFetch('/api/actions',{method:'POST',body:{
        title:'Data deletion request — tenant data erasure',
        owner:(currentUser&&currentUser.name)||'Compliance',
        asset:'Tenant data',
        desc:'Advisor requested data deletion via Governance → Safe. Review against the retention policy, then process erasure through the offboarding cascade.',
        priority:'P1',
        action_type:'Review',
      }});
      if(r&&r.ok){setDelReq('sent');addToast('✓ Data-deletion request filed for compliance review','success');}
      else{setDelReq('idle');addToast('Could not file the request — please retry','error');}
    }catch(e){setDelReq('idle');addToast('Could not file the request — please retry','error');}
  };
  const [pendingDocs,setPendingDocs]=useState([]);
  const [approving,setApproving]=useState(new Set());
  const [approvedIds,setApprovedIds]=useState(new Set());
  // Multi-stage review state: {[docId]: {business:'confirmed'|'flagged'|null, legal:null, rePro:null}}
  const [reviewStages,setReviewStages]=useState({});
  const REVIEW_STAGES=[
    {key:'business',label:'Business Team',desc:'Operational terms, permitted use, key dates'},
    {key:'legal',label:'Legal Counsel',desc:'Risk, obligations, guarantees, default provisions'},
    {key:'rePro',label:'RE Professional',desc:'Market context, TI structure, renewal economics'},
  ];
  const getDocStage=docId=>{
    const s=reviewStages[docId]||{business:null,legal:null,rePro:null};
    if(s.rePro)return 3;
    if(s.legal)return 2;
    if(s.business)return 1;
    return 0;
  };
  const setDocStageAction=(docId,stageKey,action)=>{
    setReviewStages(prev=>({...prev,[docId]:{...(prev[docId]||{business:null,legal:null,rePro:null}),[stageKey]:action}}));
  };
  const [expandedAbstract,setExpandedAbstract]=useState(null); // doc.id or null

  // Corporate Profile — fetch when the tab opens (and on version bump / SSE invalidate)
  React.useEffect(()=>{
    if(govTab!=='profile')return;
    authFetch('/api/profile').then(d=>{if(d&&d.dimensions)setProfileData(d);}).catch(()=>{});
  },[govTab,profileVersion]);
  const resolveProposal=async(propId,decision)=>{
    setResolvingProp(prev=>new Set([...prev,propId]));
    try{
      const r=await authFetch(`/api/profile/proposals/${propId}/resolve`,{method:'POST',body:{decision}});
      if(r&&r.ok){addToast(decision==='accept'?'✓ Proposal accepted — profile updated':'✓ Current belief kept','success');setProfileVersion(v=>v+1);}
      else{addToast('Could not resolve the proposal — please retry','error');}
    }catch(e){addToast('Could not resolve the proposal — please retry','error');}
    setResolvingProp(prev=>{const s=new Set(prev);s.delete(propId);return s;});
  };
  const approveDimension=async(dim)=>{
    try{
      const r=await authFetch(`/api/profile/${dim}/approve`,{method:'POST',body:{}});
      if(r&&r.ok){addToast('✓ Dimension approved','success');setProfileVersion(v=>v+1);}
      else{addToast('Could not approve — please retry','error');}
    }catch(e){addToast('Could not approve — please retry','error');}
  };

  React.useEffect(()=>{
    Promise.all([
      authFetch('/api/portfolio').catch(()=>[]),
      authFetch('/api/dealroom').catch(()=>[]),
    ]).then(([locs,deals])=>{
      const pending=[];
      // Portfolio documents
      (Array.isArray(locs)?locs:[]).forEach(loc=>{
        (loc.documents||[]).forEach(doc=>{
          if(doc.scribeAbstract&&!approvedIds.has(doc.id))
            pending.push({...doc,contextName:loc.name,contextType:'Portfolio',approveUrl:`/api/scribe/approve/${doc.id}`});
        });
      });
      // Deal documents
      (Array.isArray(deals)?deals:[]).forEach(deal=>{
        (deal.documents||[]).forEach(doc=>{
          if(doc.scribeAbstract&&!approvedIds.has(doc.id))
            pending.push({...doc,contextName:deal.name,contextType:'Setting',approveUrl:`/api/dealroom/scribe/approve/${doc.id}`});
        });
      });
      setPendingDocs(pending);
    });
  },[approvedIds]);

  const approveDoc=async(doc)=>{
    setApproving(prev=>new Set(prev).add(doc.id));
    try{
      await authFetch(doc.approveUrl,{method:'POST',body:{approver:'Advisor'}});
      setApprovedIds(prev=>new Set(prev).add(doc.id));
    }catch(e){alert(`Approval failed: ${e.message}`);}
    finally{setApproving(prev=>{const s=new Set(prev);s.delete(doc.id);return s;});}
  };

  // ── Pending Sentry gates ──────────────────────────────────────
  React.useEffect(()=>{
    authFetch('/api/sentry/gates').then(r=>{if(r&&r.gates)setPendingGates(r.gates);}).catch(()=>{});
  },[gatesVersion]);

  // ── Sage oversight mode ───────────────────────────────────────
  React.useEffect(()=>{
    authFetch('/api/sage/mode').then(r=>{if(r&&r.mode)setOrchMode(r.mode);}).catch(()=>{});
  },[]);

  const saveOrchMode=async(newMode)=>{
    setOrchModeLoading(true);
    try{
      await authFetch('/api/sage/mode',{method:'PUT',body:{mode:newMode}});
      setOrchMode(newMode);
    }catch(e){alert(`Mode change failed: ${e.message}`);}
    finally{setOrchModeLoading(false);}
  };

  // ── Sage trace log ────────────────────────────────────────────
  React.useEffect(()=>{
    const load=()=>authFetch('/api/sage/traces?limit=50').then(r=>{if(Array.isArray(r))setLiveTraces(r);}).catch(()=>{});
    load();
    const id=setInterval(load,15000);
    return()=>clearInterval(id);
  },[tracesVersion]);

  const clearGate=async(id)=>{
    setClearingGate(prev=>new Set(prev).add(id));
    try{
      await authFetch(`/api/sentry/${id}/clear`,{method:'POST',body:{cleared_by:'Advisor'}});
      setPendingGates(prev=>prev.filter(g=>g.id!==id));
      setGatesVersion(v=>v+1);
    }catch(e){alert(`Failed to clear gate: ${e.message}`);}
    finally{setClearingGate(prev=>{const s=new Set(prev);s.delete(id);return s;});}
  };

  // ── Update Assumption ─────────────────────────────────────────
  const openUpdateModal=(entry)=>{
    setUpdateDraft({title:entry.title||'',provenance:entry.provenance||'',conf:String(entry.conf||'')});
    setShowUpdateModal({slug:entry.slug,title:entry.title});
  };
  const submitUpdateAssumption=async()=>{
    if(!showUpdateModal) return;
    try{
      const body={approver:'Advisor'};
      if(updateDraft.title&&updateDraft.title!==showUpdateModal.title) body.title=updateDraft.title;
      if(updateDraft.provenance) body.provenance=updateDraft.provenance;
      if(updateDraft.conf&&!isNaN(parseInt(updateDraft.conf))) body.conf=parseInt(updateDraft.conf);
      await authFetch(`/api/safe/${showUpdateModal.slug}`,{method:'PATCH',body});
      setShowUpdateModal(null);
    }catch(e){alert(`Update failed: ${e.message}`);}
  };

  const agentColor={sense:'#E8813A',strat:'#7C3AED',scope:'#059669',scribe:'#D97706',scout:'#2563EB',shop:'#9333ea',sage:'#059669',scribe2:'#D97706',human:'#6b7280'};
  const getAC=id=>(id?agentColor[id.toLowerCase()]||'#9ca3af':'#9ca3af');
  const permLabel=v=>v==='RW'?'Read / Write':v==='R'?'Read Only':v==='none'?'No Access':'Derive';
  const permColor=v=>v==='RW'?'#16a34a':v==='R'?'#2563EB':v==='none'?'rgba(61,31,13,.25)':'#d97706';
  const catColor={HR:'#7C3AED',Finance:'#059669',Legal:'#D97706',Leasing:'#2563EB',Capital:'#8b0020'};
  const classColors={Fact:{bg:'rgba(22,163,74,.07)',border:'#bbf7d0',text:'#16a34a'},Assumption:{bg:'rgba(217,119,6,.07)',border:'#fcd34d',text:'#d97706'},Decision:{bg:'rgba(37,99,235,.07)',border:'rgba(37,99,235,.3)',text:'#1D4ED8'}};
  const tierColors={'Pre-action brief':{bg:'rgba(37,99,235,.06)',border:'rgba(37,99,235,.18)',text:'#1D4ED8',label:'PRE'},'Human gate':{bg:'rgba(139,0,33,.06)',border:'rgba(139,0,33,.2)',text:'#8b0020',label:'GATE'},'Post-action confirmation':{bg:'rgba(22,163,74,.06)',border:'rgba(22,163,74,.18)',text:'#16a34a',label:'POST'},'Escalation record':{bg:'rgba(217,119,6,.07)',border:'rgba(217,119,6,.2)',text:'#d97706',label:'ESC'}};
  const orchModes=[{id:'autonomy',label:'Autonomy',desc:'Agents act on pre-approved policy rules without per-action gates. Sentry records all artifacts. Best for routine data enrichment.',icon:'⚡'},{id:'copilot',label:'Co-pilot',desc:'Agents prepare recommendations and pre-action briefs. Human reviews and approves before each consequential action. Platform default.',icon:'◈'},{id:'manual',label:'Manual',desc:'Agents analyse and surface insights only. All actions initiated by humans. Maximum control for sensitive periods.',icon:'⛨'}];
  const modeColor={autonomy:{bg:'rgba(239,68,68,.06)',border:'rgba(239,68,68,.2)',text:'#dc2626'},copilot:{bg:'rgba(232,129,58,.06)',border:'rgba(232,129,58,.2)',text:'#E8813A'},manual:{bg:'rgba(37,99,235,.06)',border:'rgba(37,99,235,.2)',text:'#1D4ED8'}};
  const trustCommitments=[{icon:'⛓',title:'Provenance',body:'Every fact, assumption, and decision carries a full chain of custody — source system, agent ID, confidence score, and human approver.'},
    {icon:'⬡',title:'Isolation',body:'Tenant data never mingles. AES-256 encryption at rest and in transit. BYOK supported. Per-tenant key rotation on demand.'},
    {icon:'✦',title:'Verification',body:'All consequential actions trigger a 4-tier Sentry artifact: Pre-action brief → Human gate → Post-action confirmation → Escalation record.'},
    {icon:'◈',title:'Human Authority',body:'Agents propose, humans decide. Capital, legal, and communications actions require explicit human authorisation before execution.'},
    {icon:'⟷',title:'Epistemic Honesty',body:'Agents distinguish facts from assumptions and always flag confidence scores. Uncertainty is surfaced, never hidden.'}];

  const tabs=['overview','safe','sentry','profile','sage','workflows','integrations','history'];
  const tabLabels={overview:'Overview',safe:'Safe · Data Memory',sentry:'Sentry · Audit',profile:'Profile · Learning',sage:'Sage · Orchestration',workflows:'Workflows',integrations:'Integrations',history:'Version History'};

  return(
    <React.Fragment>
    <div style={{maxWidth:1240,margin:'0 auto'}}>
      <div style={{marginBottom:20,display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
        <div><div className="page-header-label">AA-1 Trust Architecture</div><div className="page-title">Governance & Agent Oversight</div></div>
        <div style={{display:'flex',gap:8,alignItems:'center',padding:'6px 14px',background:pendingGates.length>0?'rgba(217,119,6,.08)':'rgba(22,163,74,.08)',border:pendingGates.length>0?'1px solid #fcd34d':'1px solid #bbf7d0',borderRadius:20,fontSize:11,fontWeight:600,color:pendingGates.length>0?'#92400e':'#16a34a'}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:pendingGates.length>0?'#f59e0b':'#22c55e'}}/>
          {pendingGates.length>0?`⚠ ${pendingGates.length} gate${pendingGates.length!==1?'s':''} pending clearance`:'All systems nominal · 0 escalations unresolved'}
        </div>
      </div>

      <div style={{display:'flex',gap:2,marginBottom:20,background:'rgba(61,31,13,.05)',borderRadius:12,padding:4}}>
        {tabs.map(t=>(
          <button key={t} onClick={()=>setGovTab(t)} style={{flex:1,padding:'8px 4px',border:'none',borderRadius:9,cursor:'pointer',fontSize:12,fontWeight:govTab===t?700:500,background:govTab===t?'#3D1F0D':'transparent',color:govTab===t?'#fff':'rgba(61,31,13,.55)',fontFamily:'inherit',transition:'all .15s',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
            {tabLabels[t]}
          </button>
        ))}
      </div>

      {govTab==='overview'&&(()=>{
        return(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:18}}>
              {trustCommitments.map((tc,i)=>(
                <div key={i} className="card" style={{padding:'14px 12px',textAlign:'center'}}>
                  <div style={{fontSize:20,marginBottom:8}}>{tc.icon}</div>
                  <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',marginBottom:6}}>{tc.title}</div>
                  <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.5}}>{tc.body}</div>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:16}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Specialist Agents — Current Status</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  {AA1_AGENTS.map(a=>(
                    <div key={a.id} className="card" style={{padding:14,cursor:'pointer',border:selAgent===a.id?'1px solid #E8813A':'1px solid rgba(61,31,13,.08)',transition:'all .15s'}} onClick={()=>setSelAgent(selAgent===a.id?null:a.id)}>
                      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                        <div style={{width:32,height:32,borderRadius:9,background:getAC(a.id),display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:13}}>{a.name[0]}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>{a.name}</div>
                          <div style={{fontSize:10,color:'rgba(61,31,13,.45)'}}>{a.role}</div>
                        </div>
                        <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999,background:a.status==='Active'?'#d1fae5':'#ede9fe',color:a.status==='Active'?'#065f46':'#6d28d9',whiteSpace:'nowrap'}}>{a.status}</span>
                      </div>
                      <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.45,marginBottom:8}}>{a.desc}</div>
                      {a.handoffs&&(
                        <div style={{marginBottom:8}}>
                          <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:5}}>Setting Handoffs</div>
                          {a.handoffs.map(h=>(
                            <div key={h.phase} style={{marginBottom:8,paddingBottom:8,borderBottom:'1px solid rgba(61,31,13,.06)'}}>
                              <div style={{display:'flex',alignItems:'flex-start',gap:7,marginBottom:h.desc?4:0}}>
                                <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:3,background:h.status==='active'?'rgba(5,150,105,.1)':'rgba(61,31,13,.06)',color:h.status==='active'?'#059669':'rgba(61,31,13,.35)',flexShrink:0,whiteSpace:'nowrap'}}>Phase {h.phase}{h.status==='active'?' ✓':''}</span>
                                <span style={{fontSize:10,color:'rgba(61,31,13,.55)',lineHeight:1.4,fontWeight:h.status==='active'?600:400}}>{h.label}</span>
                              </div>
                              {h.desc&&<div style={{fontSize:10,color:'rgba(61,31,13,.45)',lineHeight:1.5,paddingLeft:4}}>{h.desc}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                        {a.domains.map(d=><span key={d} style={{fontSize:9,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.5)',padding:'2px 7px',borderRadius:5,fontWeight:500}}>{d}</span>)}
                        {a.sandbox&&<span style={{fontSize:9,background:'rgba(239,68,68,.08)',color:'#dc2626',padding:'2px 7px',borderRadius:5,fontWeight:700}}>SANDBOXED</span>}
                        {a.phase!=='P1'&&<span style={{fontSize:9,background:'rgba(124,58,237,.08)',color:'#7c3aed',padding:'2px 7px',borderRadius:5,fontWeight:700}}>{a.phase}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em'}}>Platform Components</div>
                {[{name:'Safe',subtitle:'Data Memory Layer',icon:'⬡',status:'Operational',detail:`AES-256 · ${_sa.length} entries · immutable hash chain`,color:'#16a34a'},{name:'Sage',subtitle:'Orchestration Engine',icon:'◈',status:'Co-pilot Mode',detail:'6 agents registered · 0 queued',color:'#E8813A'},{name:'Sentry',subtitle:'Verification Layer',icon:'⛨',status:'Operational',detail:`${_sn.length} artifacts · ${pendingGates.length} pending gate${pendingGates.length!==1?'s':''}`,color:'#2563EB'}].map(c=>(
                  <div key={c.name} className="card" style={{padding:'14px 16px',display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:40,height:40,borderRadius:10,background:c.color+'15',border:`1px solid ${c.color}33`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{c.icon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13,color:'#3D1F0D'}}>{c.name}</div>
                      <div style={{fontSize:11,color:'rgba(61,31,13,.45)'}}>{c.subtitle}</div>
                      <div style={{fontSize:10,color:'rgba(61,31,13,.35)',marginTop:2}}>{c.detail}</div>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:999,background:c.color+'15',color:c.color,whiteSpace:'nowrap'}}>{c.status}</span>
                  </div>
                ))}
                <div className="card" style={{padding:'14px 16px'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Non-Negotiable Gates</div>
                  {[['Lease abstraction publish','Human review before Portfolio Spine update'],['Client communications','Advisor approval before delivery'],['Capital recommendations','Explicit CFO authorisation'],['Conversational capture','Explicit opt-in per session']].map(([g,a])=>(
                    <div key={g} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <span style={{fontSize:14,flexShrink:0,marginTop:1}}>⛨</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{g}</div>
                        <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginTop:1}}>{a}</div>
                      </div>
                      <span style={{fontSize:9,fontWeight:700,background:'#d1fae5',color:'#065f46',padding:'2px 7px',borderRadius:999,whiteSpace:'nowrap',flexShrink:0}}>Enforced</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {govTab==='safe'&&(()=>{
        const filtered=_sa.filter(a=>a.class===safeClass);
        const cc=classColors[safeClass];
        return(
          <div style={{display:'grid',gridTemplateColumns:'1fr 310px',gap:16}}>
            <div>
              {pendingDocs.length>0&&(
                <div className="card" style={{marginBottom:16,overflow:'hidden',border:'1px solid #fcd34d'}}>
                  <div style={{padding:'10px 16px',background:'#fffbeb',borderBottom:'1px solid #fcd34d',display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:10,fontWeight:700,color:'#92400e',background:'#fef3c7',border:'1px solid #fcd34d',padding:'2px 8px',borderRadius:4}}>GATE</span>
                    <span style={{fontSize:12,fontWeight:700,color:'#92400e'}}>Scribe Pending Approvals — {pendingDocs.length} abstract{pendingDocs.length>1?'s':''} awaiting human gate</span>
                  </div>
                  {pendingDocs.map(doc=>(
                    <div key={doc.id} style={{padding:'12px 16px',borderBottom:'1px solid rgba(61,31,13,.05)',display:'flex',gap:12,alignItems:'flex-start'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',marginBottom:2}}>{doc.name}</div>
                        <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginBottom:8,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                          <span style={{fontWeight:600,color:'#D97706'}}>Scribe</span>
                          <span>·</span>
                          <span style={{fontWeight:700,padding:'1px 6px',borderRadius:4,fontSize:9,background:doc.contextType==='Setting'?'rgba(37,99,235,.08)':'rgba(22,163,74,.08)',color:doc.contextType==='Setting'?'#1D4ED8':'#16a34a'}}>{doc.contextType}</span>
                          <span>·</span>
                          <span>{doc.contextName}</span>
                          <span>·</span>
                          <span>{doc.date}</span>
                        </div>
                        {/* Collapsed preview — 3 lines */}
                        {expandedAbstract!==doc.id&&(
                          <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.5,background:'rgba(61,31,13,.03)',padding:'8px 10px',borderRadius:6,border:'1px solid rgba(61,31,13,.07)',maxHeight:72,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical'}}>
                            {doc.scribeAbstract}
                          </div>
                        )}
                        {/* Expand / collapse toggle */}
                        <button onClick={()=>setExpandedAbstract(expandedAbstract===doc.id?null:doc.id)} style={{marginTop:4,border:'none',background:'none',fontSize:10,color:'#D97706',cursor:'pointer',padding:0,fontFamily:'inherit',fontWeight:600,textAlign:'left',textDecoration:'underline'}}>
                          {expandedAbstract===doc.id?'▲ Collapse abstract':'▼ View full abstract before approving'}
                        </button>
                        {/* Full formatted abstract */}
                        {expandedAbstract===doc.id&&(()=>{
                          const sections=(doc.scribeAbstract||'').split(/\n(?===)/);
                          return(
                            <div style={{marginTop:8,border:'1px solid rgba(217,119,6,.25)',borderRadius:8,overflow:'hidden',background:'rgba(255,251,235,.5)'}}>
                              {sections.map((sec,si)=>{
                                const headerMatch=sec.match(/^===\s*(.+?)\s*===\s*\n?([\s\S]*)/);
                                if(headerMatch){
                                  const [,title,body]=headerMatch;
                                  const isAssessment=title.toLowerCase().includes('assessment')||title.toLowerCase().includes('risk');
                                  return(
                                    <div key={si} style={{borderBottom:si<sections.length-1?'1px solid rgba(217,119,6,.1)':'none'}}>
                                      <div style={{padding:'6px 12px',background:'rgba(217,119,6,.08)',fontSize:9,fontWeight:700,color:'#92400e',letterSpacing:'.08em',textTransform:'uppercase'}}>{title}</div>
                                      <div style={{padding:'8px 12px',fontSize:11,lineHeight:1.65,color:'rgba(61,31,13,.75)',whiteSpace:'pre-wrap',fontStyle:isAssessment?'italic':'normal'}}>
                                        {body.trim().split('\n').map((line,li)=>{
                                          const fieldMatch=line.match(/^-\s*([^:]+):\s*(.*)/);
                                          if(fieldMatch){
                                            const [,label,val]=fieldMatch;
                                            const notFound=val.trim().toLowerCase()==='not found'||val.trim()===''||val.trim()==='-';
                                            return(
                                              <div key={li} style={{display:'flex',gap:6,marginBottom:3,alignItems:'flex-start'}}>
                                                <span style={{flexShrink:0,width:200,fontSize:10,color:'rgba(61,31,13,.4)',fontWeight:600}}>{label.trim()}</span>
                                                <span style={{flex:1,color:notFound?'rgba(61,31,13,.28)':'rgba(61,31,13,.8)',fontStyle:notFound?'italic':'normal'}}>{val.trim()||'Not found'}</span>
                                              </div>
                                            );
                                          }
                                          return line.trim()?<div key={li} style={{marginBottom:3,color:'rgba(61,31,13,.7)'}}>{line}</div>:null;
                                        })}
                                      </div>
                                    </div>
                                  );
                                }
                                // No section header — plain text (pre-expanded format or Scribe Assessment at end)
                                return sec.trim()?(
                                  <div key={si} style={{padding:'8px 12px',fontSize:11,lineHeight:1.65,color:'rgba(61,31,13,.75)',whiteSpace:'pre-wrap',borderBottom:si<sections.length-1?'1px solid rgba(217,119,6,.1)':'none'}}>
                                    {sec.trim()}
                                  </div>
                                ):null;
                              })}
                            </div>
                          );
                        })()}
                      </div>
                      {/* Multi-stage review workflow */}
                      <div style={{flexShrink:0,minWidth:220}}>
                        {(()=>{
                          const docStageIdx=getDocStage(doc.id);
                          const stages=reviewStages[doc.id]||{business:null,legal:null,rePro:null};
                          return(
                            <div>
                              {/* Stage progress bar */}
                              <div style={{marginBottom:10}}>
                                <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>3-Stage Review</div>
                                <div style={{display:'flex',gap:3,marginBottom:8}}>
                                  {REVIEW_STAGES.map((s,i)=>{
                                    const done=stages[s.key]===('confirmed')||stages[s.key]==='flagged';
                                    const active=i===docStageIdx;
                                    return(
                                      <div key={s.key} style={{flex:1,height:4,borderRadius:2,background:done?'#16a34a':active?'#E8813A':'rgba(61,31,13,.1)'}}/>
                                    );
                                  })}
                                </div>
                                <div style={{fontSize:9,color:'rgba(61,31,13,.5)'}}>
                                  {docStageIdx===0?'Awaiting business team review':docStageIdx===1?'Awaiting legal review':docStageIdx===2?'Awaiting RE professional final review':'All stages complete — ready to publish'}
                                </div>
                              </div>
                              {/* Current stage action */}
                              {docStageIdx<3&&(()=>{
                                const s=REVIEW_STAGES[docStageIdx];
                                return(
                                  <div style={{background:'rgba(245,237,224,.8)',borderRadius:8,padding:'10px 12px',marginBottom:8,border:'1px solid rgba(232,129,58,.2)'}}>
                                    <div style={{fontSize:10,fontWeight:700,color:'#E8813A',marginBottom:2}}>{s.label}</div>
                                    <div style={{fontSize:10,color:'rgba(61,31,13,.5)',marginBottom:8,lineHeight:1.4}}>{s.desc}</div>
                                    <div style={{display:'flex',gap:6}}>
                                      <button onClick={()=>setDocStageAction(doc.id,s.key,'confirmed')} style={{flex:1,padding:'5px 0',background:'#16a34a',color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>✓ Confirm</button>
                                      <button onClick={()=>setDocStageAction(doc.id,s.key,'flagged')} style={{flex:1,padding:'5px 0',background:'transparent',color:'#d97706',border:'1px solid rgba(217,119,6,.4)',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>⚑ Flag</button>
                                    </div>
                                  </div>
                                );
                              })()}
                              {/* Prior stage confirmations */}
                              {REVIEW_STAGES.filter((_,i)=>i<docStageIdx).map(s=>(
                                <div key={s.key} style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                                  <span style={{width:16,height:16,borderRadius:4,background:stages[s.key]==='confirmed'?'rgba(22,163,74,.1)':'rgba(217,119,6,.1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,flexShrink:0}}>{stages[s.key]==='confirmed'?'✓':'⚑'}</span>
                                  <span style={{fontSize:10,color:stages[s.key]==='confirmed'?'#16a34a':'#d97706',fontWeight:600}}>{s.label}</span>
                                  <span style={{fontSize:9,color:'rgba(61,31,13,.35)'}}>{stages[s.key]==='confirmed'?'confirmed':'flagged for review'}</span>
                                </div>
                              ))}
                              {/* Final publish button — only after all 3 stages */}
                              {docStageIdx>=3&&(
                                <button
                                  onClick={()=>approveDoc(doc)}
                                  disabled={approving.has(doc.id)}
                                  style={{width:'100%',padding:'9px 0',background:approving.has(doc.id)?'rgba(139,0,33,.4)':'#8b0020',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:approving.has(doc.id)?'not-allowed':'pointer',fontFamily:'inherit',marginTop:6}}
                                >
                                  {approving.has(doc.id)?'Publishing…':'✓ Publish → Safe'}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{display:'flex',gap:8,marginBottom:14}}>
                {['Fact','Assumption','Decision'].map(cls=>{
                  const cnt=_sa.filter(a=>a.class===cls).length;
                  const active=safeClass===cls;
                  const c=classColors[cls];
                  return(
                    <button key={cls} onClick={()=>setSafeClass(cls)} style={{padding:'7px 16px',border:`1px solid ${active?c.border:'rgba(61,31,13,.12)'}`,borderRadius:20,cursor:'pointer',fontSize:12,fontWeight:active?700:500,background:active?c.bg:'transparent',color:active?c.text:'rgba(61,31,13,.5)',fontFamily:'inherit',transition:'all .15s'}}>
                      {cls}s <span style={{fontSize:10,opacity:.7}}>({cnt})</span>
                    </button>
                  );
                })}
              </div>
              <div className="card" style={{overflow:'hidden',marginBottom:16}}>
                <div style={{padding:'10px 14px',background:'#F5EDE0',borderBottom:'1px solid rgba(61,31,13,.07)',display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:10,fontWeight:700,color:cc.text,background:cc.bg,border:`1px solid ${cc.border}`,padding:'2px 8px',borderRadius:4}}>{safeClass.toUpperCase()}S</span>
                  <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{filtered.length} entries · AES-256 encrypted · immutable hash chain</span>
                </div>
                <table>
                  <thead>
                    <tr style={{borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                      {['Entry','Source','Agent','Conf','Approved','Provenance',''].map(h=><th key={h} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em',padding:'8px 14px'}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(entry=>(
                      <tr key={entry.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                        <td style={{padding:'10px 14px'}}>
                          <div style={{display:'flex',alignItems:'flex-start',gap:6,marginBottom:2}}>
                            <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',flex:1}}>{entry.title}</div>
                            {safeClass==='Assumption'&&<span style={{fontSize:9,fontWeight:700,color:'#d97706',background:'rgba(217,119,6,.1)',border:'1px solid rgba(217,119,6,.25)',padding:'1px 5px',borderRadius:4,whiteSpace:'nowrap',flexShrink:0}}>v{entry.version||1}</span>}
                            {safeClass==='Decision'&&<span style={{fontSize:9,fontWeight:700,color:'#1D4ED8',background:'rgba(37,99,235,.08)',border:'1px solid rgba(37,99,235,.2)',padding:'1px 5px',borderRadius:4,whiteSpace:'nowrap',flexShrink:0}}>🔒</span>}
                            {!entry.approved&&<span style={{fontSize:9,fontWeight:700,color:'#d97706',background:'#fffbeb',border:'1px solid #fcd34d',padding:'1px 5px',borderRadius:4,whiteSpace:'nowrap',flexShrink:0}}>Draft</span>}
                          </div>
                          <div style={{fontSize:10,color:'rgba(61,31,13,.4)',fontFamily:'monospace'}}>{entry.date}</div>
                          {entry.slug&&<div style={{fontSize:9,color:'rgba(61,31,13,.25)',fontFamily:'monospace',marginTop:1}}>{entry.slug}</div>}
                        </td>
                        <td style={{padding:'10px 14px',fontSize:11,color:'rgba(61,31,13,.6)'}}>{entry.source}</td>
                        <td style={{padding:'10px 14px'}}>
                          <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:4,background:`${getAC(entry.agent)}18`,color:getAC(entry.agent)}}>{entry.agent}</span>
                        </td>
                        <td style={{padding:'10px 14px',fontSize:12,fontWeight:700,color:entry.conf>=90?'#16a34a':entry.conf>=70?'#d97706':'#dc2626',fontFamily:'monospace'}}>{entry.conf}%</td>
                        <td style={{padding:'10px 14px'}}>
                          {entry.approved
                            ?<span style={{fontSize:10,display:'flex',alignItems:'center',gap:4}}><span style={{color:'#16a34a',fontSize:13}}>✓</span><span style={{color:'rgba(61,31,13,.5)'}}>{entry.approver}</span></span>
                            :<span style={{fontSize:10,fontWeight:600,color:'#d97706',background:'rgba(217,119,6,.08)',padding:'2px 7px',borderRadius:4}}>Pending</span>}
                        </td>
                        <td style={{padding:'10px 14px',maxWidth:220}}>
                          <div style={{fontSize:10,color:'rgba(61,31,13,.45)',lineHeight:1.4,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{entry.provenance}</div>
                        </td>
                        <td style={{padding:'10px 14px'}}>
                          {safeClass==='Assumption'&&entry.slug&&(
                            <button onClick={()=>openUpdateModal(entry)} style={{fontSize:10,fontWeight:600,padding:'4px 10px',borderRadius:6,border:'1px solid rgba(217,119,6,.3)',background:'rgba(217,119,6,.06)',color:'#d97706',cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>Update</button>
                          )}
                          {safeClass==='Decision'&&<span style={{fontSize:10,color:'rgba(61,31,13,.25)',fontStyle:'italic'}}>Immutable</span>}
                          {safeClass==='Fact'&&<span style={{fontSize:10,color:'rgba(61,31,13,.25)',fontStyle:'italic'}}>Append-only</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Agent Access Matrix — Safe Permissions</div>
              <div className="card" style={{overflow:'hidden'}}>
                <table>
                  <thead>
                    <tr style={{borderBottom:'1px solid rgba(61,31,13,.08)',background:'#F5EDE0'}}>
                      <th style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase'}}>Agent</th>
                      {['Facts','Assumptions','Decisions'].map(h=><th key={h} style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',textAlign:'center'}}>{h}</th>)}
                      <th style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase'}}>Sandbox</th>
                    </tr>
                  </thead>
                  <tbody>
                    {AA1_AGENTS.map(a=>(
                      <tr key={a.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                        <td style={{padding:'10px 14px'}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <div style={{width:24,height:24,borderRadius:6,background:getAC(a.id),display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,fontWeight:700}}>{a.name[0]}</div>
                            <div>
                              <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{a.name}</div>
                              <div style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>{a.role}</div>
                            </div>
                          </div>
                        </td>
                        {['facts','assumptions','decisions'].map(k=>{
                          const v=a.safePerms[k];
                          return(
                            <td key={k} style={{padding:'10px 14px',textAlign:'center'}}>
                              <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:4,background:`${permColor(v)}18`,color:permColor(v)}}>{permLabel(v)}</span>
                            </td>
                          );
                        })}
                        <td style={{padding:'10px 14px'}}>
                          {a.sandbox?<span style={{fontSize:10,fontWeight:700,color:'#dc2626',background:'rgba(239,68,68,.08)',padding:'2px 8px',borderRadius:4}}>Yes — no write</span>:<span style={{fontSize:11,color:'rgba(61,31,13,.3)'}}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Encryption & Storage</div>
                {[['Encryption at rest','AES-256-GCM','✓'],['Encryption in transit','TLS 1.3','✓'],['Key management','Tenant-managed (BYOK ready)','✓'],['Data residency','US-East (AWS)','⚙'],['Deletion rights','Full purge on request','✓'],['Backup retention','7 years · immutable','✓']].map(([label,value,icon])=>(
                  <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.6)'}}>{label}</span>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{value}</span>
                      <span style={{fontSize:12,color:icon==='✓'?'#16a34a':'#d97706'}}>{icon}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Data Residency</div>
                {['US-East (Active)','EU-West','APAC'].map((r,i)=>(
                  <div key={r} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:8,marginBottom:6,background:i===0?'rgba(22,163,74,.06)':'transparent',border:i===0?'1px solid #bbf7d0':'1px solid rgba(61,31,13,.08)',cursor:'pointer'}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:i===0?'#22c55e':'rgba(61,31,13,.2)',flexShrink:0}}/>
                    <span style={{fontSize:12,fontWeight:i===0?600:400,color:i===0?'#16a34a':'rgba(61,31,13,.45)',flex:1}}>{r}</span>
                    {i===0&&<span style={{fontSize:9,fontWeight:700,color:'#16a34a'}}>Active</span>}
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Safe Health</div>
                {[['Total entries',_sa.length],['Facts',_sa.filter(a=>a.class==='Fact').length],['Assumptions',_sa.filter(a=>a.class==='Assumption').length],['Decisions',_sa.filter(a=>a.class==='Decision').length],['Pending approval',_sa.filter(a=>!a.approved).length]].map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'6px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>{k}</span>
                    <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D'}}>{v}</span>
                  </div>
                ))}
                {delReq==='sent'
                  ?<div style={{width:'100%',marginTop:12,fontSize:11,padding:'7px 12px',textAlign:'center',color:'#16a34a',fontWeight:700}}>✓ Deletion request filed — on the Action Rail for compliance review</div>
                  :delReq==='confirm'||delReq==='sending'
                    ?<div style={{marginTop:12,padding:'9px 11px',background:'rgba(200,75,47,.06)',border:'1px solid rgba(200,75,47,.25)',borderRadius:8}}>
                       <div style={{fontSize:10.5,color:'rgba(61,31,13,.65)',lineHeight:1.5,marginBottom:8}}>Files a formal, audited data-deletion request for compliance review. This does not erase data immediately.</div>
                       <div style={{display:'flex',gap:6}}>
                         <button onClick={()=>setDelReq('idle')} disabled={delReq==='sending'} style={{flex:1,padding:'5px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:6,background:'transparent',fontSize:10.5,fontWeight:600,color:'rgba(61,31,13,.5)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
                         <button onClick={requestDataDeletion} disabled={delReq==='sending'} style={{flex:1,padding:'5px 10px',border:'none',borderRadius:6,background:'#C84B2F',color:'#fff',fontSize:10.5,fontWeight:700,cursor:delReq==='sending'?'wait':'pointer',fontFamily:'inherit'}}>{delReq==='sending'?'Filing…':'Confirm request'}</button>
                       </div>
                     </div>
                    :<button onClick={()=>setDelReq('confirm')} className="btn-ghost" style={{width:'100%',marginTop:12,fontSize:11,padding:'7px 12px'}}>Request Data Deletion</button>}
              </div>
            </div>
          </div>
        );
      })()}

      {govTab==='profile'&&(()=>{
        const dims=(profileData&&profileData.dimensions)||[];
        const history=(profileData&&profileData.history)||[];
        const proposals=(profileData&&profileData.pendingProposals)||[];
        const dimName=slug=>slug.replace(/^profile-/,'').replace(/-v\d+$/,'');
        const historyFor=slug=>{const d=dimName(slug);return history.filter(h=>dimName(h.slug)===d).sort((a,b)=>b.version-a.version);};
        return(
          <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:16}}>
            <div>
              <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Corporate Profile — What the Platform Believes</div>
              {profileData===null&&<div className="card" style={{padding:24,textAlign:'center',color:'rgba(61,31,13,.4)',fontSize:12}}>Loading profile…</div>}
              {profileData!==null&&dims.length===0&&(
                <div className="card" style={{padding:24,textAlign:'center'}}>
                  <div style={{fontSize:20,marginBottom:8}}>◌</div>
                  <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginBottom:6}}>No profile yet</div>
                  <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.5}}>Profile dimensions are written by the Sense audit and iterated by the nightly distillation over confirmed meetings and verified outcomes.</div>
                </div>
              )}
              {dims.map(d=>{
                const versions=historyFor(d.slug);
                const open=expandedDim===d.slug;
                return(
                  <div key={d.slug} className="card" style={{padding:16,marginBottom:12}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>{dimName(d.slug)}</div>
                        <div style={{fontSize:13,fontWeight:600,color:'#3D1F0D',lineHeight:1.45}}>{d.title}</div>
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
                        <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999,background:'rgba(217,119,6,.07)',border:'1px solid #fcd34d',color:'#d97706'}}>{d.conf}% · v{d.version}</span>
                        {d.approved
                          ?<span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999,background:'#d1fae5',color:'#065f46'}} title={d.approver?`Approved by ${d.approver}`:''}>✓ Approved</span>
                          :isAdmin&&<button onClick={()=>approveDimension(dimName(d.slug))} style={{fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:999,border:'1px solid rgba(22,163,74,.3)',background:'rgba(22,163,74,.07)',color:'#16a34a',cursor:'pointer',fontFamily:'inherit'}}>Approve</button>}
                      </div>
                    </div>
                    <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.5,marginBottom:8}}>{(d.provenance||'').split('\n')[0]}</div>
                    <div style={{display:'flex',gap:8,alignItems:'center'}}>
                      <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>by {d.agent} · {d.assetDate}</span>
                      {versions.length>1&&<button onClick={()=>setExpandedDim(open?null:d.slug)} style={{fontSize:10,fontWeight:600,border:'none',background:'transparent',color:'#2563EB',cursor:'pointer',fontFamily:'inherit',padding:0}}>{open?'Hide history':`${versions.length} versions`}</button>}
                    </div>
                    {open&&(
                      <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid rgba(61,31,13,.06)'}}>
                        {versions.map(v=>(
                          <div key={v.slug} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'5px 0',opacity:v.isActive?1:.55}}>
                            <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:3,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.5)',flexShrink:0}}>v{v.version}</span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:11,color:'#3D1F0D',lineHeight:1.4}}>{v.title}</div>
                              <div style={{fontSize:9,color:'rgba(61,31,13,.4)',marginTop:1}}>{v.conf}% · {v.agent} · {v.assetDate}{v.approved?` · approved by ${v.approver||'—'}`:''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em'}}>Contradictions Awaiting Review</div>
              {proposals.length===0&&<div className="card" style={{padding:'14px 16px',fontSize:11,color:'rgba(61,31,13,.45)'}}>None pending — the distiller escalates here when new evidence contradicts a strong belief.</div>}
              {proposals.map(p=>{
                const prior=history.find(h=>h.slug===p.priorSlug);
                const busy=resolvingProp.has(p.id);
                return(
                  <div key={p.id} className="card" style={{padding:14,border:'1px solid #fcd34d'}}>
                    <div style={{fontSize:10,fontWeight:700,color:'#92400e',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:8}}>⚠ {p.dimension} · {p.agreement}/{p.samples} samples</div>
                    {prior&&(
                      <div style={{marginBottom:8}}>
                        <div style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',marginBottom:2}}>Current belief ({prior.conf}%)</div>
                        <div style={{fontSize:11,color:'#3D1F0D',lineHeight:1.4}}>{prior.title}</div>
                      </div>
                    )}
                    <div style={{marginBottom:8}}>
                      <div style={{fontSize:9,fontWeight:700,color:'#d97706',textTransform:'uppercase',marginBottom:2}}>Proposed ({p.conf}%)</div>
                      <div style={{fontSize:11,color:'#3D1F0D',lineHeight:1.4,fontWeight:600}}>{p.statement}</div>
                    </div>
                    {p.evidence&&p.evidence.length>0&&(
                      <div style={{marginBottom:10}}>
                        {p.evidence.slice(0,3).map((e,i)=><div key={i} style={{fontSize:10,color:'rgba(61,31,13,.5)',lineHeight:1.45,paddingLeft:8,borderLeft:'2px solid rgba(217,119,6,.25)',marginBottom:3}}>{e}</div>)}
                      </div>
                    )}
                    <div style={{display:'flex',gap:8}}>
                      <button disabled={busy||!isAdmin} onClick={()=>resolveProposal(p.id,'accept')} style={{flex:1,fontSize:11,fontWeight:700,padding:'7px 0',borderRadius:8,border:'none',background:busy?'rgba(22,163,74,.4)':'#16a34a',color:'#fff',cursor:busy?'default':'pointer',fontFamily:'inherit'}}>{busy?'…':'Accept'}</button>
                      <button disabled={busy||!isAdmin} onClick={()=>resolveProposal(p.id,'keep_current')} style={{flex:1,fontSize:11,fontWeight:700,padding:'7px 0',borderRadius:8,border:'1px solid rgba(61,31,13,.15)',background:'transparent',color:'#3D1F0D',cursor:busy?'default':'pointer',fontFamily:'inherit'}}>Keep current</button>
                    </div>
                  </div>
                );
              })}
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>How the Loop Works</div>
                {[['Sense audit','writes archetype + workplace profile (dual-model consensus)'],['Nightly distillation','iterates beliefs from confirmed meetings, verified outcomes, and action follow-through'],['Contradiction gate','new evidence vs a strong belief stops here for your decision'],['Every brief','Sage, the daily brief, and pre-meeting briefs read the profile back']].map(([t,d])=>(
                  <div key={t} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'6px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <span style={{fontSize:12,flexShrink:0,marginTop:1}}>↻</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{t}</div>
                      <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginTop:1,lineHeight:1.4}}>{d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {govTab==='sentry'&&(()=>{
        const tierLabel=[null,'Dollar Threshold','Irrevocability','External Exposure','Counterparty Risk'];
        return(
          <div style={{display:'grid',gridTemplateColumns:'1fr 310px',gap:16}}>
            <div>
              {pendingGates.length>0&&(
                <div className="card" style={{marginBottom:16,overflow:'hidden',border:'1px solid rgba(139,0,33,.25)'}}>
                  <div style={{padding:'10px 16px',background:'rgba(139,0,33,.05)',borderBottom:'1px solid rgba(139,0,33,.15)',display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontSize:10,fontWeight:700,color:'#8b0020',background:'rgba(139,0,33,.1)',border:'1px solid rgba(139,0,33,.2)',padding:'2px 8px',borderRadius:4}}>GATE</span>
                    <span style={{fontSize:12,fontWeight:700,color:'#8b0020'}}>Pending Human Gates — {pendingGates.length} action{pendingGates.length!==1?'s':''} awaiting advisor clearance</span>
                  </div>
                  {pendingGates.map(gate=>(
                    <div key={gate.id} style={{padding:'12px 16px',borderBottom:'1px solid rgba(61,31,13,.05)',display:'flex',gap:12,alignItems:'flex-start'}}>
                      <div style={{display:'flex',flexDirection:'column',gap:4,alignItems:'center',flexShrink:0}}>
                        <span style={{fontSize:9,fontWeight:700,padding:'3px 7px',borderRadius:4,background:'rgba(139,0,33,.08)',color:'#8b0020',border:'1px solid rgba(139,0,33,.2)',whiteSpace:'nowrap'}}>T{gate.gateTier||'?'}</span>
                        <span style={{fontSize:8,fontWeight:600,color:'rgba(61,31,13,.35)',textAlign:'center',maxWidth:40,lineHeight:1.2}}>{gate.gateTier?tierLabel[gate.gateTier]:'Unknown'}</span>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',marginBottom:3}}>{gate.action}</div>
                        {gate.triggeredRule&&<div style={{fontSize:10,color:'#8b0020',fontWeight:500,marginBottom:3}}>{gate.triggeredRule}</div>}
                        <div style={{display:'flex',gap:8,fontSize:10,color:'rgba(61,31,13,.4)',flexWrap:'wrap'}}>
                          <span style={{fontWeight:600,color:getAC(gate.agent)}}>{gate.agent}</span>
                          {gate.ts&&<><span>·</span><span style={{fontFamily:'monospace'}}>{gate.ts.replace('T',' ').slice(0,16)}</span></>}
                          {gate.gateOwner&&<><span>·</span><span>Gate owner: {gate.gateOwner}</span></>}
                        </div>
                      </div>
                      <button
                        onClick={()=>clearGate(gate.id)}
                        disabled={clearingGate.has(gate.id)}
                        style={{flexShrink:0,padding:'7px 14px',background:clearingGate.has(gate.id)?'rgba(22,163,74,.4)':'#16a34a',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:clearingGate.has(gate.id)?'not-allowed':'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}
                      >
                        {clearingGate.has(gate.id)?'Clearing…':'Clear Gate'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Doc 2 D-08 — two severity tiers: Escalated requires acknowledgment;
                  Monitored accumulates without forced resolution (embrace the messiness). */}
              {(()=>{
                const isEsc=a=>{const s=(a.artifact_status||a.status||'');return s==='Pending Gate'||s==='Escalated';};
                const esc=_sn.filter(isEsc).length, mon=_sn.length-esc;
                return(
                  <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,background:esc?'rgba(239,68,68,.08)':'rgba(61,31,13,.04)',border:`1px solid ${esc?'rgba(239,68,68,.3)':'rgba(61,31,13,.12)'}`}}>
                      <span style={{width:8,height:8,borderRadius:'50%',background:esc?'#dc2626':'rgba(61,31,13,.25)'}}/>
                      <span style={{fontSize:12,fontWeight:700,color:esc?'#dc2626':'rgba(61,31,13,.55)'}}>{esc} Escalated</span>
                      <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>requires acknowledgment</span>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:8,background:'rgba(61,31,13,.04)',border:'1px solid rgba(61,31,13,.12)'}}>
                      <span style={{width:8,height:8,borderRadius:'50%',background:'rgba(61,31,13,.3)'}}/>
                      <span style={{fontSize:12,fontWeight:700,color:'rgba(61,31,13,.55)'}}>{mon} Monitored</span>
                      <span style={{fontSize:11,color:'rgba(61,31,13,.5)'}}>accumulate · no forced action</span>
                    </div>
                  </div>
                );
              })()}
              <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Audit Artifact Log — 4-Tier Sentry Protocol</div>
              <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
                {Object.entries(tierColors).map(([tier,c])=>(
                  <div key={tier} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:6,background:c.bg,border:`1px solid ${c.border}`}}>
                    <span style={{fontSize:9,fontWeight:700,color:c.text}}>{c.label}</span>
                    <span style={{fontSize:10,color:c.text}}>{tier}</span>
                  </div>
                ))}
              </div>
              <div className="card" style={{overflow:'hidden'}}>
                {_sn.map(art=>{
                  const tc=tierColors[art.tier];
                  return(
                    <div key={art.id} style={{padding:'12px 16px',borderBottom:'1px solid rgba(61,31,13,.05)',display:'flex',gap:12,alignItems:'flex-start'}}>
                      <span style={{fontSize:9,fontWeight:700,padding:'3px 7px',borderRadius:4,background:tc.bg,color:tc.text,border:`1px solid ${tc.border}`,flexShrink:0,whiteSpace:'nowrap',marginTop:2}}>{tc.label}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:600,color:'#3D1F0D',marginBottom:3}}>{art.action}</div>
                        <div style={{display:'flex',gap:8,flexWrap:'wrap',fontSize:10,color:'rgba(61,31,13,.45)',alignItems:'center'}}>
                          <span style={{fontWeight:600,color:getAC(art.agent)}}>{art.agent}</span>
                          <span>·</span>
                          <span style={{fontFamily:'monospace'}}>{art.ts.replace('T',' ')}</span>
                          <span>·</span>
                          <span>{art.conf}% confidence</span>
                          {art.gateOwner&&<><span>·</span><span>Gate owner: {art.gateOwner}</span></>}
                        </div>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:999,flexShrink:0,
                        background:art.status==='Approved'?'#d1fae5':art.status==='Confirmed'?'rgba(37,99,235,.08)':art.status==='Pending Gate'?'#fffbeb':art.status==='Escalated'?'rgba(239,68,68,.08)':'#f3f4f6',
                        color:art.status==='Approved'?'#065f46':art.status==='Confirmed'?'#1D4ED8':art.status==='Pending Gate'?'#92400e':art.status==='Escalated'?'#dc2626':'#6b7280'
                      }}>{art.status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Gate Policy — Active Agents</div>
                {AA1_AGENTS.filter(a=>a.status==='Active').map(a=>(
                  <div key={a.id} style={{padding:'10px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                      <div style={{width:22,height:22,borderRadius:6,background:getAC(a.id),display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:9,fontWeight:700}}>{a.name[0]}</div>
                      <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{a.name}</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:5}}>
                      {[['Conf threshold','≥ 80%'],['Gate mode','Human approval'],['Escalation to','VP Real Estate']].map(([k,v])=>(
                        <div key={k} style={{padding:'5px 8px',background:'rgba(61,31,13,.03)',borderRadius:6,border:'1px solid rgba(61,31,13,.07)'}}>
                          <div style={{fontSize:9,color:'rgba(61,31,13,.4)',marginBottom:2}}>{k}</div>
                          <div style={{fontSize:10,fontWeight:600,color:'#3D1F0D'}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Escalation Authorities</div>
                {[['Lease decisions','VP Real Estate'],['Capital allocations','CFO'],['Legal commitments','General Counsel'],['Data deletion','Data Protection Officer'],['System override','CISO']].map(([scope,auth])=>(
                  <div key={scope} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                    <span style={{fontSize:11,color:'rgba(61,31,13,.55)'}}>{scope}</span>
                    <span style={{fontSize:11,fontWeight:600,color:'#8b0020'}}>{auth}</span>
                  </div>
                ))}
              </div>
              <button className="btn-ghost" style={{fontSize:11,padding:'8px 14px'}} onClick={()=>{
                const rows=[['Timestamp','Tier','Agent','Action','Confidence','Status','Gate Owner']];
                _sn.forEach(s=>rows.push([s.created_at||s.createdAt||'',s.tier,s.agent,s.action,s.conf,s.artifact_status||s.status||'',s.gate_owner||s.gateOwner||'']));
                const csv=rows.map(r=>r.map(v=>`"${String(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
                const a=document.createElement('a');
                a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
                a.download=`sentry-log-${new Date().toISOString().slice(0,10)}.csv`;
                a.click();
              }}>Export Full Sentry Log (7yr)</button>
            </div>
          </div>
        );
      })()}

      {govTab==='sage'&&(()=>{
        return(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:18}}>
              {!isAdmin&&(
                <div style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:8,padding:'8px 14px',background:'rgba(139,0,33,.04)',border:'1px solid rgba(139,0,33,.14)',borderRadius:10,marginBottom:4,fontSize:11,color:'#8b0020',fontWeight:600}}>
                  🔒 Oversight mode changes require Admin access. Contact your platform administrator.
                </div>
              )}
              {orchModes.map(m=>{
                const active=orchMode===m.id;
                const mc=modeColor[m.id];
                const locked=!isAdmin;
                return(
                  <div key={m.id} onClick={()=>!locked&&!orchModeLoading&&saveOrchMode(m.id)} className="card" style={{padding:'16px 18px',cursor:locked?'default':orchModeLoading?'wait':'pointer',border:active?`1px solid ${mc.border}`:'1px solid rgba(61,31,13,.08)',background:active?mc.bg:'#fff',transition:'all .15s',opacity:locked?0.55:orchModeLoading&&!active?.6:1,position:'relative'}}>
                    {locked&&<div style={{position:'absolute',top:10,right:10,fontSize:12,color:'rgba(61,31,13,.3)'}}>🔒</div>}
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10}}>
                      <span style={{fontSize:22}}>{m.icon}</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:active?mc.text:'#3D1F0D'}}>{m.label}</div>
                        {active&&<span style={{fontSize:9,fontWeight:700,color:mc.text,background:mc.bg,padding:'1px 6px',borderRadius:4,border:`1px solid ${mc.border}`}}>CURRENT MODE</span>}
                      </div>
                    </div>
                    <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.5}}>{m.desc}</div>
                  </div>
                );
              })}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:16}}>
              <div className="card" style={{padding:18}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
                  <span style={{fontWeight:700,fontSize:13}}>◈ Orchestration Trace Log</span>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{fontSize:10,fontWeight:700,background:'#d1fae5',color:'#065f46',padding:'2px 8px',borderRadius:999}}>Live · {orchMode} mode</span>
                    <button onClick={()=>setTracesVersion(v=>v+1)} style={{border:'none',background:'rgba(61,31,13,.06)',borderRadius:6,fontSize:10,color:'rgba(61,31,13,.5)',padding:'2px 8px',cursor:'pointer',fontFamily:'inherit'}}>↻ Refresh</button>
                  </div>
                </div>
                {liveTraces.length===0&&(
                  <div style={{padding:'24px 0',textAlign:'center',fontSize:11,color:'rgba(61,31,13,.35)'}}>
                    No traces yet — traces appear here as Sage executes tools during chat sessions.
                  </div>
                )}
                {liveTraces.map((e,i)=>{
                  const agentName=e.agent||'Sage';
                  const toolLabel={portfolio_lookup:'portfolio lookup',deal_lookup:'deal lookup',action_list:'action list',action_create:'action create',strat_model:'strat model',insights_query:'insights query',safe_lookup:'safe lookup',scout_search:'scout search',sense_query:'sense query'}[e.toolName]||e.toolName;
                  const intentColors={lease_legal:'#D97706',market_comp:'#2563EB',financial:'#7C3AED',operations:'#059669',portfolio:'#E8813A',governance:'#8b0020',general:'#6b7280'};
                  const ic=intentColors[e.intentCategory]||'#9ca3af';
                  const ts=e.ts?e.ts.replace('T',' ').slice(0,16):'';
                  return(
                    <div key={e.id||i} style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <span style={{fontSize:9,color:'rgba(61,31,13,.35)',fontFamily:'monospace',flexShrink:0,paddingTop:3,minWidth:44,lineHeight:1.4}}>{ts.slice(11,16)}</span>
                      <div style={{width:24,height:24,borderRadius:7,background:getAC(agentName),display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:9,fontWeight:700,flexShrink:0,marginTop:1}}>{agentName[0]}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3,flexWrap:'wrap'}}>
                          <span style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{agentName}</span>
                          <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>→</span>
                          <span style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.65)',fontFamily:'monospace'}}>{toolLabel}</span>
                          {e.intentCategory&&e.intentCategory!=='general'&&(
                            <span style={{fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:`${ic}14`,color:ic,border:`1px solid ${ic}30`}}>{e.intentCategory.replace('_',' ')}</span>
                          )}
                        </div>
                        <div style={{display:'flex',gap:10,fontSize:10,color:'rgba(61,31,13,.4)'}}>
                          <span>{e.conf}% conf</span>
                          {e.latencyMs!=null&&<span style={{fontFamily:'monospace'}}>{e.latencyMs}ms</span>}
                          <span style={{fontFamily:'monospace',fontSize:9,color:'rgba(61,31,13,.25)'}}>{e.sessionId?.slice(0,12)}…</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                <div className="card" style={{padding:'14px 16px'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:12}}>Intent Routing Rules</div>
                  {[['Lease / legal queries','Scribe → human gate'],['Market / comp research','Scout (sandboxed)'],['Financial scenarios','Strat → advisor review'],['Experience / RBW-I data','Sense → Sage summary'],['Deal velocity','Sage → Action Rail'],['Delivery tracking','Scope (Phase 2)']].map(([intent,route])=>(
                    <div key={intent} style={{padding:'7px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{intent}</div>
                      <div style={{fontSize:10,color:'rgba(61,31,13,.45)',marginTop:2}}>→ {route}</div>
                    </div>
                  ))}
                </div>
                <div className="card" style={{padding:'14px 16px'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Privacy Controls</div>
                  {[['Conversation logging','Opt-in only'],['Cross-session memory','Tenant-scoped'],['PII redaction','Auto (pre-Safe write)'],['Data sharing across tenants','None — isolated'],['Model training opt-out','Active']].map(([k,v])=>(
                    <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <span style={{fontSize:11,color:'rgba(61,31,13,.55)'}}>{k}</span>
                      <span style={{fontSize:11,fontWeight:600,color:'#3D1F0D'}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {govTab==='workflows'&&<WorkflowTemplatesPanel isAdmin={isAdmin}/>}

      {govTab==='integrations'&&(()=>{
        return(
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:18}}>
              {INTEGRATIONS.map(intg=>{
                const active=selIntegration===intg.id;
                const cc=catColor[intg.category];
                return(
                  <div key={intg.id} className="card" style={{padding:16,cursor:'pointer',border:active?`1px solid ${cc}55`:'1px solid rgba(61,31,13,.08)',transition:'all .15s'}} onClick={()=>setSelIntegration(active?null:intg.id)}>
                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:10}}>
                      <div>
                        <span style={{fontSize:9,fontWeight:700,color:cc,background:`${cc}12`,padding:'2px 8px',borderRadius:999}}>{intg.category}</span>
                        <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D',marginTop:6}}>{intg.name}</div>
                      </div>
                      <span style={{fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:999,background:intg.status==='Connected'?'#d1fae5':'#fffbeb',color:intg.status==='Connected'?'#065f46':'#92400e',whiteSpace:'nowrap'}}>{intg.status}</span>
                    </div>
                    <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginBottom:10}}>
                      {intg.lastSync?`Last sync: ${intg.lastSync.replace('T',' ').replace('Z','')} UTC`:'Not yet connected'}
                    </div>
                    <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:8}}>
                      <span style={{fontSize:9,fontWeight:700,color:'rgba(61,31,13,.4)'}}>Agent:</span>
                      <span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:4,background:`${getAC(intg.agent)}18`,color:getAC(intg.agent)}}>{intg.agent}</span>
                    </div>
                    <div style={{fontSize:9,color:'rgba(61,31,13,.35)',fontWeight:500}}>{intg.compliance}</div>
                    {active&&(
                      <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid rgba(61,31,13,.07)'}}>
                        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',marginBottom:6}}>DATA IN</div>
                        {intg.dataIn.map(d=><div key={d} style={{fontSize:10,color:'rgba(61,31,13,.55)',padding:'3px 0'}}>← {d}</div>)}
                        <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',margin:'8px 0 6px'}}>DATA OUT</div>
                        {intg.dataOut.map(d=><div key={d} style={{fontSize:10,color:'rgba(61,31,13,.55)',padding:'3px 0'}}>→ {d}</div>)}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="card" style={{padding:16,border:'1px dashed rgba(61,31,13,.15)',background:'transparent',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8,minHeight:130,cursor:'pointer',opacity:.6}}>
                <div style={{fontSize:24,color:'rgba(61,31,13,.3)'}}>+</div>
                <div style={{fontSize:11,fontWeight:600,color:'rgba(61,31,13,.4)',textAlign:'center'}}>Future Integration</div>
                <div style={{fontSize:10,color:'rgba(61,31,13,.3)',textAlign:'center'}}>ERP · Facilities · IoT · Custom API</div>
              </div>
            </div>
            <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Agent Registry — All Phases</div>
            <div className="card" style={{overflow:'hidden'}}>
              <table>
                <thead>
                  <tr style={{borderBottom:'1px solid rgba(61,31,13,.08)',background:'#F5EDE0'}}>
                    {['Agent','Role','Phase','Status','Data Domains','Sandbox'].map(h=><th key={h} style={{padding:'8px 14px',fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em'}}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {AA1_AGENTS.map(a=>(
                    <tr key={a.id} style={{borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                      <td style={{padding:'10px 14px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{width:26,height:26,borderRadius:7,background:getAC(a.id),display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:10,fontWeight:700}}>{a.name[0]}</div>
                          <span style={{fontSize:12,fontWeight:600,color:'#3D1F0D'}}>{a.name}</span>
                        </div>
                      </td>
                      <td style={{padding:'10px 14px',fontSize:11,color:'rgba(61,31,13,.6)'}}>{a.role}</td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:4,background:a.phase==='P1'?'rgba(22,163,74,.08)':a.phase==='P2'?'rgba(37,99,235,.08)':'rgba(124,58,237,.08)',color:a.phase==='P1'?'#16a34a':a.phase==='P2'?'#1D4ED8':'#6d28d9'}}>{a.phase}</span>
                      </td>
                      <td style={{padding:'10px 14px'}}>
                        <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999,background:a.status==='Active'?'#d1fae5':'#ede9fe',color:a.status==='Active'?'#065f46':'#6d28d9'}}>{a.status}</span>
                      </td>
                      <td style={{padding:'10px 14px'}}>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {a.domains.map(d=><span key={d} style={{fontSize:9,background:'rgba(61,31,13,.06)',color:'rgba(61,31,13,.5)',padding:'2px 6px',borderRadius:4}}>{d}</span>)}
                        </div>
                      </td>
                      <td style={{padding:'10px 14px',fontSize:11,color:a.sandbox?'#dc2626':'rgba(61,31,13,.3)',fontWeight:a.sandbox?700:400}}>{a.sandbox?'Yes':'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {govTab==='history'&&(
        <VersionHistoryPanel authFetch={authFetch}/>
      )}

    </div>

    {/* ── Update Assumption Modal ─────────────────────────────── */}
    {showUpdateModal&&(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={e=>{if(e.target===e.currentTarget)setShowUpdateModal(null);}}>
        <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:480,boxShadow:'0 24px 60px rgba(0,0,0,.18)',overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid rgba(61,31,13,.08)',display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:28,height:28,borderRadius:8,background:'rgba(217,119,6,.12)',border:'1px solid rgba(217,119,6,.25)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>⟳</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Update Assumption</div>
              <div style={{fontSize:10,color:'rgba(61,31,13,.4)',fontFamily:'monospace',marginTop:1}}>{showUpdateModal.slug}</div>
            </div>
            <button onClick={()=>setShowUpdateModal(null)} style={{border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.35)',cursor:'pointer',padding:4,lineHeight:1}}>×</button>
          </div>
          <div style={{padding:'20px'}}>
            <div style={{fontSize:11,color:'rgba(61,31,13,.45)',background:'rgba(217,119,6,.06)',border:'1px solid rgba(217,119,6,.2)',borderRadius:8,padding:'8px 12px',marginBottom:16,lineHeight:1.5}}>
              A new versioned record will be created (preserving current version in audit history).
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',display:'block',marginBottom:5}}>Title</label>
              <input value={updateDraft.title} onChange={e=>setUpdateDraft(d=>({...d,title:e.target.value}))} style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9',boxSizing:'border-box'}} placeholder={showUpdateModal.title}/>
            </div>
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',display:'block',marginBottom:5}}>Provenance / Rationale</label>
              <textarea value={updateDraft.provenance} onChange={e=>setUpdateDraft(d=>({...d,provenance:e.target.value}))} rows={3} style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9',resize:'vertical',boxSizing:'border-box'}} placeholder="Reason for update…"/>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.5)',display:'block',marginBottom:5}}>Confidence % <span style={{fontWeight:400}}>(1–100)</span></label>
              <input type="number" min="1" max="100" value={updateDraft.conf} onChange={e=>setUpdateDraft(d=>({...d,conf:e.target.value}))} style={{width:'100%',padding:'8px 10px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,fontSize:12,fontFamily:'inherit',color:'#3D1F0D',background:'#fafaf9',boxSizing:'border-box'}}/>
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setShowUpdateModal(null)} style={{padding:'9px 18px',border:'1px solid rgba(61,31,13,.15)',borderRadius:8,background:'transparent',fontSize:12,fontWeight:600,color:'rgba(61,31,13,.55)',cursor:'pointer',fontFamily:'inherit'}}>Cancel</button>
              <button onClick={submitUpdateAssumption} style={{padding:'9px 18px',border:'none',borderRadius:8,background:'#D97706',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Create New Version</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </React.Fragment>
  );
}

/* ── APP ────────────────────────────────────────────────────── */
/* ── SAGE CHAT OVERLAY ──────────────────────────────────────────── */
/* Gap Patch §Bypass — chips personalized to the onboarding intent/persona, so
   a bypassed setup still lands on a configured dashboard, never a generic one. */
const SAGE_PROMPTS_BY_INTENT={
  baseline:['How is my portfolio performing overall?','Where are my biggest occupancy gaps?','What would sharpen my baseline?'],
  expiration:['Which leases expire soonest?','What renewal risks need attention this quarter?','Walk me through my next notice deadline'],
  friction:['What is costing my team the most time?','Where are the data gaps slowing us down?','What should we automate first?'],
  mis_sizing:['Where are we most over-housed?','Which markets are mis-sized for headcount?','What data would ground the mis-sizing read?'],
  strategist:['Where are we most over-housed?','Which spaces are failing their people?','What is dragging Culture this week?'],
  deal_brief:['What do I need before the next broker call?','What governs leverage on my current deal?','Draft my deal one-pager'],
  transaction_manager:['What is the renewal calculus on my top deal?','Which option windows close soonest?','Prepare a decision brief for my most urgent Setting'],
  portfolio_manager:['Build my CFO baseline summary','Which critical dates can\'t be missed?','What changed in the portfolio this week?'],
};
function intentPrompts(){
  try{return SAGE_PROMPTS_BY_INTENT[localStorage.getItem('stage_onboarding_intent')||'']||[];}catch(e){return[];}
}
const SAGE_PROMPTS_DEFAULT={
  brief:['What are my most urgent decisions right now?','Summarise portfolio risk across all locations','What should I tell the CFO today?','Which agents flagged something overnight?'],
  portfolio:['Which leases expire in the next 12 months?','Where is utilisation lowest vs. rent paid?','Tell me about the 1 Penn Plaza renewal risk','Are there any invoice variances I should know about?'],
  financial:['What\'s my total annual lease obligation?','Which location has the worst $/SF vs. market?','What does the Houston exit save annually?','Model a 5% rent reduction at Willis Tower'],
  actions:['Which P1 actions are overdue?','What decisions are blocking deal progress?','Summarise open Scribe review items','Draft a weekly action summary'],
  setting:['What\'s due in this month\'s lease review?','Where does the Chicago drawdown stand?','Which TI windows are at risk of lapsing?','What did we decide on Austin last quarter?'],
  analytics:['Where is experience drift happening?','Which CE setting is underperforming?','Compare 1 Penn Plaza vs 30 Hudson Yards settings','What\'s causing the utilisation drop at 1 Penn?'],
  governance:['Show me pending Sentry gate items','What data has Scribe written to Safe today?','Who approved the last lease abstract?','What assumptions need human validation?'],
  sage:['What\'s the highest-confidence insight today?','What patterns are emerging across the portfolio?','Which assumptions need validation?','Summarise agent activity in the last 24 hours'],
};
const agentAvatarColor={Sage:'#059669',Scribe:'#D97706',Strat:'#7C3AED',Scout:'#2563EB',Sense:'#E8813A',System:'#6b7280'};

const INTENT_LABELS={lease_legal:'Lease / Legal',market_comp:'Market · Scout',financial:'Financial · Strat',operations:'Operations',portfolio:'Portfolio',governance:'Governance',general:'General'};
const INTENT_COLORS={lease_legal:'#D97706',market_comp:'#2563EB',financial:'#7C3AED',operations:'#059669',portfolio:'#E8813A',governance:'#8b0020',general:'#6b7280'};
// Verified-citation chip colours, reusing the trust palette: Facts green,
// Assumptions amber, Decisions maroon; entity sources take their domain colour.
const CITE_COLORS={Fact:'#059669',Assumption:'#D97706',Decision:'#8b0020',Location:'#E8813A',Deal:'#7C3AED',Action:'#2563EB'};

/* Lightweight, XSS-safe markdown for streamed Sage replies. Everything is rendered as
   React children (escaped by React) — no raw-HTML injection sink is used, so the app's
   clean posture is preserved. Handles **bold**, `inline code`, and - / • bullet lists;
   an unterminated token mid-stream shows literally until its closing marker arrives. */
function _inlineMd(text){
  const parts=[]; let last=0;
  for(const m of (text||'').matchAll(/(\*\*[^*]+\*\*|`[^`]+`)/g)){
    if(m.index>last)parts.push(text.slice(last,m.index));
    const tok=m[0];
    if(tok.startsWith('**'))parts.push(<strong key={parts.length}>{tok.slice(2,-2)}</strong>);
    else parts.push(<code key={parts.length} style={{background:'rgba(61,31,13,.06)',borderRadius:3,padding:'1px 4px',fontSize:'.92em'}}>{tok.slice(1,-1)}</code>);
    last=m.index+tok.length;
  }
  if(last<text.length)parts.push(text.slice(last));
  return parts;
}
function renderRich(text){
  const lines=(text||'').split('\n'); const out=[]; let bullets=null;
  const flush=()=>{ if(bullets){out.push(<ul key={'u'+out.length} style={{margin:'4px 0',paddingLeft:18}}>{bullets}</ul>);bullets=null;} };
  lines.forEach((ln,i)=>{
    const bm=ln.match(/^\s*[-•*]\s+(.*)/);
    if(bm){ if(!bullets)bullets=[]; bullets.push(<li key={i} style={{marginBottom:2}}>{_inlineMd(bm[1])}</li>); }
    else{ flush(); if(ln.trim()==='')out.push(<div key={'b'+i} style={{height:6}}/>); else out.push(<div key={'l'+i}>{_inlineMd(ln)}</div>); }
  });
  flush();
  return out;
}

/* ── Reconcile attach panel — state machine ─────────────────────────────
   idle              paperclip button, no panel
   selecting         file picker shown, waiting for file
   detecting         file chosen, Gemini reading document (spinner)
   confirming        detection done, AI suggestions shown for review/edit
   analysing         user confirmed, Haiku comparison running (spinner)
   done              result injected into Sage chat, panel closes
   error             something failed, error message shown
─────────────────────────────────────────────────────────────────────── */
const DOC_TYPE_OPTS=[
  {value:'rent_invoice', label:'Rent Invoice'},
  {value:'cam_rec',      label:'CAM Reconciliation'},
  {value:'capital_draw', label:'Capital Draw'},
];
const CONF_COLOUR=c=>c>=80?'#16a34a':c>=50?'#D97706':'#DC2626';

function ReconcilePanel({onClose,onDone,authFetch,portfolio,deals}){
  const fileRef=useRef(null);
  const [phase,setPhase]=useState('selecting');           // selecting|detecting|confirming|analysing|error
  const [file,setFile]=useState(null);
  const [errMsg,setErrMsg]=useState('');

  // Detection result
  const [docId,setDocId]=useState('');
  const [detected,setDetected]=useState(null);  // full API response

  // Confirmed (user-editable) values — initialised from detected
  const [confDocType,setConfDocType]=useState('rent_invoice');
  const [confScope,setConfScope]=useState('portfolio');
  const [confSlug,setConfSlug]=useState('');
  const [confPeriod,setConfPeriod]=useState('');

  // Scope-appropriate asset list
  const scopeItems=confScope==='portfolio'
    ? (portfolio||[]).map(l=>({slug:l.id,name:l.name}))
    : (deals||[]).map(d=>({slug:d.id,name:d.name}));

  // ── Phase 1: detect ────────────────────────────────────────────
  const handleDetect=useCallback(async()=>{
    if(!file){setErrMsg('Choose a file first.');return;}
    setErrMsg('');
    setPhase('detecting');
    try{
      const fd=new FormData();
      fd.append('file',file);
      const r=await authFetch('/api/reconcile/detect',{method:'POST',body:fd});
      if(!r?.docId)throw new Error(r?.detail||'Detection failed — please try again.');
      setDocId(r.docId);
      setDetected(r);
      // Pre-fill confirmed fields from detection
      setConfDocType(r.detectedDocType||'rent_invoice');
      setConfPeriod(r.billingPeriod||'');
      // Best property match: prefer portfolio if confidence ≥ 50, else dealroom
      const bestPortfolio=r.portfolioMatches?.[0];
      const bestDeal=r.dealMatches?.[0];
      if(bestPortfolio&&(!bestDeal||bestPortfolio.confidence>=bestDeal.confidence)){
        setConfScope('portfolio');
        setConfSlug(bestPortfolio.slug);
      } else if(bestDeal){
        setConfScope('setting');
        setConfSlug(bestDeal.slug);
      } else {
        setConfScope('portfolio');
        setConfSlug('');
      }
      setPhase('confirming');
    }catch(err){
      setErrMsg(err.message||'Detection failed.');
      setPhase('error');
    }
  },[file,authFetch]);

  // ── Phase 2: confirm ───────────────────────────────────────────
  const handleConfirm=useCallback(async()=>{
    if(!confSlug){setErrMsg('Select a location or deal to compare against.');return;}
    setErrMsg('');
    setPhase('analysing');
    try{
      const r=await authFetch(`/api/reconcile/${docId}/confirm`,{
        method:'POST',
        body:{doc_type:confDocType,scope:confScope,slug:confSlug,billing_period:confPeriod},
      });
      if(!r?.ok&&!r?.docId)throw new Error(r?.detail||'Comparison failed — please try again.');
      onDone(r);
    }catch(err){
      setErrMsg(err.message||'Comparison failed.');
      setPhase('error');
    }
  },[docId,confDocType,confScope,confSlug,confPeriod,authFetch,onDone]);

  const retry=()=>{setPhase('selecting');setFile(null);setDetected(null);setErrMsg('');if(fileRef.current)fileRef.current.value='';};

  return(
    <div style={{padding:'14px 20px',borderTop:'1px solid rgba(61,31,13,.07)',background:'#FFF8F0',flexShrink:0}}>
      {/* Panel header */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
        <span style={{fontSize:11,fontWeight:700,color:'#D97706',textTransform:'uppercase',letterSpacing:'.06em'}}>
          📎 Reconcile Document
        </span>
        {phase==='detecting'&&<span style={{fontSize:10,color:'#D97706',fontWeight:600}}>Gemini reading document…</span>}
        {phase==='analysing'&&<span style={{fontSize:10,color:'#059669',fontWeight:600}}>Comparing against lease…</span>}
        <button onClick={onClose} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'rgba(61,31,13,.4)',fontSize:18,lineHeight:1}}>×</button>
      </div>

      {/* ── SELECTING: file picker ── */}
      {(phase==='selecting'||phase==='error')&&(
        <div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <input ref={fileRef} type="file" accept=".pdf,.xlsx,.csv,.png,.jpg,.jpeg"
              onChange={e=>setFile(e.target.files?.[0]||null)}
              style={{flex:1,fontSize:12,color:'#3D1F0D',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'5px 8px',background:'#fff',cursor:'pointer'}}
            />
            <button onClick={handleDetect} disabled={!file}
              style={{background:file?'#D97706':'#9CA3AF',color:'#fff',border:'none',borderRadius:6,padding:'7px 14px',fontSize:12,fontWeight:700,cursor:file?'pointer':'default',fontFamily:'inherit',whiteSpace:'nowrap',transition:'background .15s'}}>
              Analyse →
            </button>
          </div>
          {errMsg&&<div style={{fontSize:11,color:'#DC2626',marginTop:6}}>{errMsg} <button onClick={retry} style={{background:'none',border:'none',cursor:'pointer',color:'#D97706',fontSize:11,fontWeight:600,textDecoration:'underline'}}>Try again</button></div>}
          <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:6}}>
            Gemini reads the document · identifies type and property · you confirm before comparison runs
          </div>
        </div>
      )}

      {/* ── DETECTING / ANALYSING: spinner ── */}
      {(phase==='detecting'||phase==='analysing')&&(
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0'}}>
          <div style={{width:20,height:20,borderRadius:'50%',border:'2px solid #D97706',borderTopColor:'transparent',animation:'spin 0.8s linear infinite'}}/>
          <span style={{fontSize:12,color:'rgba(61,31,13,.6)'}}>
            {phase==='detecting'?'Reading document…':'Running comparison against your lease…'}
          </span>
        </div>
      )}

      {/* ── CONFIRMING: AI detection card ── */}
      {phase==='confirming'&&detected&&(
        <div>
          {/* Confidence badge */}
          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
            <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>Gemini detected with</span>
            <span style={{fontSize:11,fontWeight:700,color:CONF_COLOUR(detected.docTypeConfidence||0)}}>
              {detected.docTypeConfidence||0}% confidence
            </span>
            <span style={{fontSize:10,color:'rgba(61,31,13,.4)'}}>— review and confirm:</span>
          </div>

          {/* Detected metadata row */}
          {(detected.propertyName||detected.propertyAddress||detected.landlordName)&&(
            <div style={{background:'rgba(217,119,6,.06)',border:'1px solid rgba(217,119,6,.15)',borderRadius:8,padding:'8px 12px',marginBottom:10,fontSize:11,color:'rgba(61,31,13,.7)',lineHeight:1.6}}>
              {detected.propertyName&&<div><strong>Property:</strong> {detected.propertyName}</div>}
              {detected.propertyAddress&&<div><strong>Address:</strong> {detected.propertyAddress}</div>}
              {detected.landlordName&&<div><strong>Landlord:</strong> {detected.landlordName}</div>}
            </div>
          )}

          {/* Editable confirmation fields */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            {/* Doc type */}
            <div>
              <div style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.5)',marginBottom:3}}>Document Type</div>
              <select value={confDocType} onChange={e=>setConfDocType(e.target.value)}
                style={{width:'100%',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'6px 8px',fontSize:12,fontFamily:'inherit',background:'#fff',color:'#3D1F0D',outline:'none'}}>
                {DOC_TYPE_OPTS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {/* Billing period */}
            <div>
              <div style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.5)',marginBottom:3}}>Billing Period</div>
              <input value={confPeriod} onChange={e=>setConfPeriod(e.target.value)} placeholder="e.g. June 2026"
                style={{width:'100%',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'6px 8px',fontSize:12,fontFamily:'inherit',background:'#fff',color:'#3D1F0D',outline:'none'}}
              />
            </div>
            {/* Scope */}
            <div>
              <div style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.5)',marginBottom:3}}>Scope</div>
              <select value={confScope} onChange={e=>{setConfScope(e.target.value);setConfSlug('');}}
                style={{width:'100%',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'6px 8px',fontSize:12,fontFamily:'inherit',background:'#fff',color:'#3D1F0D',outline:'none'}}>
                <option value="portfolio">Portfolio</option>
                <option value="setting">Setting</option>
              </select>
            </div>
            {/* Location / deal — dropdown with matched confidence hints */}
            <div>
              <div style={{fontSize:10,fontWeight:600,color:'rgba(61,31,13,.5)',marginBottom:3}}>
                {confScope==='portfolio'?'Location':'Deal'}
              </div>
              <select value={confSlug} onChange={e=>setConfSlug(e.target.value)}
                style={{width:'100%',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'6px 8px',fontSize:12,fontFamily:'inherit',background:'#fff',color:'#3D1F0D',outline:'none'}}>
                <option value="">Select…</option>
                {/* Matched candidates with confidence score first */}
                {(confScope==='portfolio'?detected.portfolioMatches:detected.dealMatches||[]).map(m=>(
                  <option key={m.slug} value={m.slug}>{m.name} ({m.confidence}% match)</option>
                ))}
                {/* Remaining assets without a match score */}
                {scopeItems
                  .filter(s=>!(confScope==='portfolio'?detected.portfolioMatches:detected.dealMatches||[]).find(m=>m.slug===s.slug))
                  .map(s=><option key={s.slug} value={s.slug}>{s.name}</option>)
                }
              </select>
            </div>
          </div>

          {errMsg&&<div style={{fontSize:11,color:'#DC2626',marginBottom:6}}>{errMsg}</div>}

          {/* Action buttons */}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button onClick={retry}
              style={{background:'none',border:'1px solid rgba(61,31,13,.14)',borderRadius:6,padding:'7px 14px',fontSize:12,color:'rgba(61,31,13,.6)',cursor:'pointer',fontFamily:'inherit'}}>
              ← Re-upload
            </button>
            <button onClick={handleConfirm} disabled={!confSlug}
              style={{background:confSlug?'#059669':'#9CA3AF',color:'#fff',border:'none',borderRadius:6,padding:'7px 18px',fontSize:12,fontWeight:700,cursor:confSlug?'pointer':'default',fontFamily:'inherit',transition:'background .15s'}}>
              Confirm & Analyse →
            </button>
          </div>
          <div style={{fontSize:10,color:'rgba(61,31,13,.4)',marginTop:6}}>
            Scribe compares against your lease abstraction · variances logged to Sentry
          </div>
        </div>
      )}
    </div>
  );
}

function SageChatOverlay({page,open,onClose,wsRef,messages,setMessages,streaming,trustStage,wsStatus,onReconnect,onNavigate}){
  const inputRef=useRef(null);
  const scrollRef=useRef(null);
  const {portfolio,deals}=useData();
  const authFetch=useAuthFetch();
  // Personalized chips lead (onboarding intent/persona), page defaults follow — max 4.
  const prompts=[...new Set([...intentPrompts(),...(SAGE_PROMPTS_DEFAULT[page]||SAGE_PROMPTS_DEFAULT.brief)])].slice(0,4);

  const [showAttach,setShowAttach]=useState(false);

  useEffect(()=>{
    if(open&&inputRef.current)inputRef.current.focus();
  },[open]);

  useEffect(()=>{
    if(scrollRef.current)scrollRef.current.scrollTop=scrollRef.current.scrollHeight;
  },[messages]);

  const send=useCallback((text)=>{
    const t=text.trim();
    if(!t)return;
    // Clear the input immediately so the user knows the message was received
    if(inputRef.current)inputRef.current.value='';
    const ws=wsRef.current;
    if(ws&&ws.readyState===1){
      setMessages(prev=>[...prev,{id:_mid(),role:'user',content:t}]);
      ws.send(JSON.stringify({type:'message',content:t,page,trust_stage:trustStage||3}));
    }else if(!ws||ws.readyState===0){
      // Socket is still CONNECTING (e.g. first load) — poll up to 2 s
      setMessages(prev=>[...prev,{id:_mid(),role:'user',content:t}]);
      const _tryWait=(n)=>{
        const w=wsRef.current;
        if(w&&w.readyState===1){
          w.send(JSON.stringify({type:'message',content:t,page,trust_stage:trustStage||3}));
        }else if(n>0){
          setTimeout(()=>_tryWait(n-1),100);
        }else{
          setMessages(prev=>[...prev,{id:_mid(),role:'sage',
            content:'Sage is reconnecting — please try again in a moment.',
            agent:'Sage',streaming:false,isError:true}]);
        }
      };
      setTimeout(()=>_tryWait(19),100);
    }else{
      // Closed / closing — reconnect is in progress, surface friendly message
      setMessages(prev=>[...prev,
        {id:_mid(),role:'user',content:t},
        {id:_mid(),role:'sage',content:'Sage is reconnecting — please try again in a moment.',
         agent:'Sage',streaming:false,isError:true},
      ]);
    }
  },[page,wsRef,setMessages]);

  // Called by ReconcilePanel when comparison is complete
  const handleReconDone=useCallback((result)=>{
    setShowAttach(false);
    const dt={rent_invoice:'Rent Invoice',cam_rec:'CAM Reconciliation',capital_draw:'Capital Draw Statement'};
    const statusTag=result.reconcileStatus==='variance'
      ?`⚠ ${result.variances?.length||0} variance(s) found — total exposure $${(result.varianceTotal||0).toLocaleString('en-US',{maximumFractionDigits:0})}`
      :result.reconcileStatus==='insufficient_data'
        ?'⚠ No lease baseline available — please upload and approve a lease first'
        :'✓ No material variances detected';
    const msg=`I've uploaded a ${dt[result.docType]||result.docType} for ${result.asset||result.scope} (doc ID: ${result.docId}). ${statusTag}. Please analyse the reconciliation and advise on any next steps.`;
    send(msg);
  },[send]);

  if(!open)return null;
  return(
    <div style={{position:'fixed',top:56,left:240,right:0,zIndex:300,display:'flex',flexDirection:'column',background:'#fff',borderBottom:'2px solid rgba(61,31,13,.1)',boxShadow:'0 12px 40px rgba(61,31,13,.1)',maxHeight:'60vh'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:10,padding:'9px 20px',borderBottom:'1px solid rgba(61,31,13,.07)',background:'#F5EDE0',flexShrink:0}}>
        <div style={{position:'relative',width:26,height:26,flexShrink:0}}>
          <div style={{width:26,height:26,borderRadius:8,background:'#059669',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:800}}>S</div>
          {/* Connection status dot — bottom-right corner of the avatar */}
          <div title={wsStatus==='open'?'Sage is connected':wsStatus==='reconnecting'?'Sage is reconnecting…':'Sage is offline'} style={{position:'absolute',bottom:-2,right:-2,width:8,height:8,borderRadius:'50%',border:'2px solid #F5EDE0',background:wsStatus==='open'?'#16a34a':wsStatus==='reconnecting'?'#d97706':'#dc2626',transition:'background .4s'}}/>
        </div>
        <span style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Sage</span>
        <span style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>· Master intelligence · {page} context</span>
        {trustStage&&<span style={{fontSize:9,fontWeight:700,color:'#8b0020',background:'rgba(139,0,33,.08)',padding:'2px 8px',borderRadius:999,marginLeft:4}}>Stage {trustStage}</span>}
        {streaming&&<span style={{fontSize:10,fontWeight:700,color:'#059669',background:'rgba(5,150,105,.08)',padding:'2px 8px',borderRadius:999,marginLeft:4}}>thinking…</span>}
        <button onClick={onClose} aria-label="Close Sage panel" style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer',color:'rgba(61,31,13,.4)',fontSize:22,lineHeight:1,padding:'0 4px'}}>×</button>
      </div>
      {/* Dead-connection banner — only shown after all retries are exhausted */}
      {wsStatus==='dead'&&(
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 20px',background:'#fef2f2',borderBottom:'1px solid #fecaca',flexShrink:0}}>
          <span style={{fontSize:11,color:'#dc2626',flex:1}}>⚠ Connection lost — Sage cannot respond until you reconnect.</span>
          <button onClick={onReconnect} style={{fontSize:11,fontWeight:700,color:'#fff',background:'#dc2626',border:'none',borderRadius:6,padding:'4px 12px',cursor:'pointer'}}>Reconnect</button>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} role="log" aria-live="polite" aria-label="Sage conversation" style={{flex:1,overflowY:'auto',padding:'14px 20px',display:'flex',flexDirection:'column',gap:12}}>
        {messages.length===0&&(
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.35)',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:10}}>Suggested</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
              {prompts.map(p=>(
                <button key={p} onClick={()=>send(p)} style={{padding:'10px 14px',background:'rgba(61,31,13,.03)',border:'1px solid rgba(61,31,13,.08)',borderRadius:8,cursor:'pointer',fontSize:12,color:'rgba(61,31,13,.65)',fontFamily:'inherit',textAlign:'left',transition:'all .15s'}}
                  onMouseOver={e=>e.currentTarget.style.background='rgba(61,31,13,.07)'}
                  onMouseOut={e=>e.currentTarget.style.background='rgba(61,31,13,.03)'}
                >{p}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m,i)=>(
          <div key={m.id||i} style={{display:'flex',gap:10,flexDirection:m.role==='user'?'row-reverse':'row',alignItems:'flex-start'}}>
            {m.role==='sage'&&(
              <div style={{width:28,height:28,borderRadius:8,background:agentAvatarColor[m.agent]||'#059669',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:800,flexShrink:0,marginTop:2}}>{(m.agent||'S')[0]}</div>
            )}
            <div style={{maxWidth:'75%',display:'flex',flexDirection:'column',gap:4}}>
              {m.role==='sage'&&m.intentCategory&&m.intentCategory!=='general'&&(
                <span style={{fontSize:9,fontWeight:700,padding:'1px 7px',borderRadius:4,background:`${INTENT_COLORS[m.intentCategory]||'#6b7280'}14`,color:INTENT_COLORS[m.intentCategory]||'#6b7280',border:`1px solid ${INTENT_COLORS[m.intentCategory]||'#6b7280'}28`,alignSelf:'flex-start'}}>
                  {INTENT_LABELS[m.intentCategory]||m.intentCategory}
                </span>
              )}
              <div style={{padding:'10px 14px',borderRadius:10,
                background:m.isError?'rgba(220,38,38,.06)':m.role==='user'?'#3D1F0D':'#F5EDE0',
                color:m.isError?'#DC2626':m.role==='user'?'#fff':'#3D1F0D',
                border:m.isError?'1px solid rgba(220,38,38,.2)':'none',
                fontSize:13,lineHeight:1.65,whiteSpace:m.role==='sage'?'normal':'pre-wrap',wordBreak:'break-word'}}>
                {m.role==='sage'&&!m.isError?renderRich(m.content):m.content}
                {m.streaming&&<span style={{opacity:.4,marginLeft:2}}>▌</span>}
              </div>
              {m.role==='sage'&&m.citations&&m.citations.length>0&&(
                <div style={{display:'flex',flexWrap:'wrap',gap:4,marginTop:2,alignItems:'center'}}>
                  <span style={{fontSize:9,fontWeight:700,letterSpacing:.4,color:'rgba(61,31,13,.4)'}}>SOURCES</span>
                  {m.citations.map((c,ci)=>{
                    const col=CITE_COLORS[c.kind]||'#6b7280';
                    const isSafe=c.kind==='Fact'||c.kind==='Assumption'||c.kind==='Decision';
                    const canNav=!!(c.target&&c.target.page&&onNavigate);
                    return(
                      <button key={ci}
                        onClick={canNav?()=>onNavigate(c.target):undefined}
                        disabled={!canNav}
                        aria-label={canNav?`Open source: ${c.label}`:c.label}
                        title={`${c.kind}${c.conf!=null?` · ${c.conf}% confidence`:''} — ${c.label}${canNav?' · click to open':''}`}
                        style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:999,
                          background:`${col}14`,color:col,border:`1px solid ${col}30`,fontFamily:'inherit',
                          cursor:canNav?'pointer':'default',
                          maxWidth:220,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {isSafe?'◆ ':''}{c.label}{canNav?' ↗':''}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Reconcile panel — slides in above input bar */}
      {showAttach&&(
        <ReconcilePanel
          onClose={()=>setShowAttach(false)}
          onDone={handleReconDone}
          authFetch={authFetch}
          portfolio={portfolio}
          deals={deals}
        />
      )}

      {/* Input bar */}
      <div style={{padding:'10px 20px',borderTop:'1px solid rgba(61,31,13,.07)',display:'flex',gap:8,flexShrink:0,background:'#fff',alignItems:'center'}}>
        <button
          title="Reconcile a landlord document"
          aria-label="Attach a document to reconcile"
          onClick={()=>setShowAttach(v=>!v)}
          style={{background:showAttach?'rgba(217,119,6,.12)':'none',border:`1px solid ${showAttach?'#D97706':'rgba(61,31,13,.14)'}`,borderRadius:7,padding:'7px 10px',cursor:'pointer',color:showAttach?'#D97706':'rgba(61,31,13,.45)',fontSize:15,lineHeight:1,flexShrink:0,transition:'all .15s'}}>
          📎
        </button>
        <input ref={inputRef} placeholder="Ask Sage anything…" aria-label="Ask Sage a question"
          style={{flex:1,border:'1px solid rgba(61,31,13,.14)',borderRadius:8,padding:'9px 14px',fontSize:13,fontFamily:'inherit',outline:'none',background:'#F5EDE0',color:'#3D1F0D'}}
          onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send(e.currentTarget.value);}}}
        />
        <button className="btn-primary" aria-label="Send message" style={{fontSize:13,padding:'9px 18px'}}
          onClick={()=>send(inputRef.current?.value||'')}>↑</button>
      </div>
    </div>
  );
}

/* ── PLANS & CREDITS PAGE ───────────────────────────────────── */
function PlansPage(){
  const {currentUser,billingEnabled,pricingUrl}=useData();
  const isAdmin=currentUser?.role==='admin'||!currentUser;
  const authFetch=useAuthFetch();
  const {addToast}=useToast();
  // Enabling an add-on is a provisioning request, not a self-serve toggle —
  // file an audited P1 action so the team can turn it on, and confirm to the user.
  const [enabling,setEnabling]=useState('');   // addon name currently being requested
  const [requested,setRequested]=useState(()=>new Set());
  const requestAddon=async(name)=>{
    setEnabling(name);
    try{
      const r=await authFetch('/api/actions',{method:'POST',body:{
        title:`Enable add-on — ${name}`,
        owner:(currentUser&&currentUser.name)||'Account team',
        asset:'Subscription',
        desc:`${(currentUser&&currentUser.name)||'An admin'} requested the "${name}" add-on be enabled for this workspace.`,
        priority:'P1',
        action_type:'Review',
      }});
      if(r&&r.ok){setRequested(s=>new Set(s).add(name));addToast(`✓ Requested "${name}" — the account team will enable it`,'success');}
      else addToast('Could not send the request — please retry','error');
    }catch(e){addToast('Could not send the request — please retry','error');}
    finally{setEnabling('');}
  };

  // ── Coming-soon gate ──────────────────────────────────────────────────────
  // Shown when BILLING_ENABLED=false (the default). The nav item is also hidden
  // by Sidebar, so this only renders if someone navigates directly to #plans.
  if(!billingEnabled){
    return(
      <div style={{maxWidth:600,margin:'80px auto',padding:'0 32px',textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:20}}>◑</div>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:'.12em',textTransform:'uppercase',color:'#E8813A',marginBottom:8}}>Plans &amp; Credits</div>
        <h2 style={{fontFamily:'Georgia,serif',fontSize:28,color:'#3D1F0D',marginBottom:12}}>Billing is coming soon</h2>
        <p style={{fontSize:14,color:'rgba(61,31,13,.55)',lineHeight:1.7,marginBottom:28}}>
          The Plans &amp; Credits dashboard — including credit pool tracking, consumption reporting, and add-on management — will be available once our billing integration is live. Your account and usage are tracked internally in the meantime.
        </p>
        <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap',marginBottom:16}}>
          <a
            href={pricingUrl}
            target="_blank" rel="noopener noreferrer"
            style={{display:'inline-block',padding:'12px 28px',background:'#8b0020',color:'#fff',borderRadius:8,fontSize:14,fontWeight:700,textDecoration:'none'}}
          >
            View pricing →
          </a>
          <a
            href="mailto:team@wxstage.com?subject=Plans%20%26%20Credits%20enquiry"
            style={{display:'inline-block',padding:'12px 28px',background:'transparent',color:'#8b0020',borderRadius:8,fontSize:14,fontWeight:700,textDecoration:'none',border:'1.5px solid #8b0020'}}
          >
            Contact us
          </a>
        </div>
        <p style={{marginTop:8,fontSize:12,color:'rgba(61,31,13,.35)'}}>
          Admins: set <code style={{background:'rgba(61,31,13,.06)',padding:'1px 5px',borderRadius:3}}>BILLING_ENABLED=true</code> in Railway Variables to enable this page.
        </p>
      </div>
    );
  }

  // Demo credit data (Pro tier)
  const currentTier={name:'Pro',price:'$1,999',locations:'10–50',users:15,credits:20};
  const creditUsed=12;
  const creditIncluded=20;
  const creditPurchased=0;
  const creditRemaining=creditIncluded+creditPurchased-creditUsed;
  const creditPct=Math.round((creditUsed/(creditIncluded+creditPurchased))*100);
  const projected=Math.round(creditUsed/20*30); // simple trailing rate

  const creditEvents=[
    {event:'Lease abstracted → Portfolio Spine',credits:1,agent:'Scribe',agentColor:'#D97706',count:5},
    {event:'Setting delta summary generated',credits:0.5,agent:'Scribe',agentColor:'#D97706',count:6},
    {event:'Option exercise monitored & alerted',credits:1,agent:'Scribe + Sage',agentColor:'#059669',count:1},
    {event:'Pre-meeting brief generated',credits:0,agent:'Sage',agentColor:'#059669',count:12},
    {event:'Renewal scenario modeled (Strat)',credits:2,agent:'Strat',agentColor:'#7C3AED',count:0},
  ];

  const addons=[
    {name:'CKE Module',status:'included',statusColor:'#16a34a',desc:'100 meetings/month · Pre-Meeting Brief · Cross-deal signals'},
    {name:'Workplace Analytics',status:'included',statusColor:'#16a34a',desc:'HRIS integration · Predictive Occupancy · Benchmark comparison'},
    {name:'RBW-I / Sense Module',status:'available',statusColor:'#D97706',desc:'Floor plan ingestion · Zone performance · Friction detection · $299/month'},
    {name:'SSO (SAML/OIDC)',status:'available',statusColor:'#D97706',desc:'Enterprise identity integration · $199/month'},
  ];

  const creditPoolOptions=[
    {size:'50 credits',price:'$140',rate:'$2.80',discount:'7%'},
    {size:'100 credits',price:'$260',rate:'$2.60',discount:'13%'},
    {size:'250 credits',price:'$600',rate:'$2.40',discount:'20%'},
  ];

  const [showPoolModal,setShowPoolModal]=useState(false);
  const [selectedPool,setSelectedPool]=useState(null);
  const [poolPurchased,setPoolPurchased]=useState(false);

  return(
    <div style={{padding:'32px 32px 64px',maxWidth:1100,margin:'0 auto'}}>
      {/* Header */}
      <div style={{marginBottom:28}}>
        <div style={{fontSize:10,fontWeight:700,letterSpacing:'.1em',textTransform:'uppercase',color:'#E8813A',marginBottom:4}}>Plans & Credits</div>
        <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
          <div>
            <h2 style={{fontFamily:'Georgia,serif',fontSize:26,color:'#3D1F0D',marginBottom:2}}>Account &amp; Credit Dashboard</h2>
            <p style={{fontSize:13,color:'rgba(61,31,13,.5)'}}>Current plan: <strong style={{color:'#E8813A'}}>Pro</strong> · Billing period: May 2026 · Next renewal: Jun 1, 2026</p>
          </div>
          <a href={pricingUrl} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:'#8b0020',fontWeight:700,border:'1px solid rgba(139,0,33,.2)',borderRadius:6,padding:'7px 14px',display:'inline-flex',alignItems:'center',gap:5}}>
            ↗ View full pricing page
          </a>
        </div>
      </div>

      {/* Top KPI row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:24}}>
        {[
          {label:'Current Tier',value:'Pro',sub:'$1,999 / month',color:'#E8813A'},
          {label:'Credits Included',value:creditIncluded,sub:'per billing period',color:'#059669'},
          {label:'Credits Consumed',value:creditUsed,sub:`${creditPct}% of pool used`,color:creditPct>=80?'#dc2626':'#3D1F0D'},
          {label:'Credits Remaining',value:creditRemaining,sub:`Projected ${projected} by period end`,color:creditRemaining<=4?'#dc2626':'#059669'},
        ].map(k=>(
          <div key={k.label} className="card" style={{padding:'16px 18px'}}>
            <div style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:6}}>{k.label}</div>
            <div style={{fontFamily:'Georgia,serif',fontSize:28,color:k.color,lineHeight:1,marginBottom:3}}>{k.value}</div>
            <div style={{fontSize:11,color:'rgba(61,31,13,.4)'}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Two-column layout */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 320px',gap:20}}>
        {/* Left — credit consumption */}
        <div>
          {/* Progress bar */}
          <div className="card" style={{marginBottom:16,padding:'18px 20px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
              <span style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Credit Pool — May 2026</span>
              {creditPct>=80&&<span style={{fontSize:10,fontWeight:700,color:'#dc2626',background:'#fef2f2',border:'1px solid #fecaca',padding:'2px 8px',borderRadius:10}}>⚠ Above 80% threshold</span>}
            </div>
            <div style={{background:'rgba(61,31,13,.08)',borderRadius:999,height:10,overflow:'hidden',marginBottom:8}}>
              <div style={{height:'100%',borderRadius:999,width:`${Math.min(creditPct,100)}%`,background:creditPct>=80?'#dc2626':'#E8813A',transition:'width .4s ease'}}/>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'rgba(61,31,13,.45)'}}>
              <span>{creditUsed} consumed · {creditPurchased} purchased</span>
              <span>{creditRemaining} remaining of {creditIncluded+creditPurchased}</span>
            </div>
          </div>

          {/* Consumption by event */}
          <div className="card" style={{marginBottom:16,overflow:'hidden'}}>
            <div style={{padding:'10px 16px',background:'#F5EDE0',borderBottom:'1px solid rgba(61,31,13,.07)',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.7)'}}>Consumption by Event Type — May 2026</span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid rgba(61,31,13,.07)'}}>
                  {['Credit Event','Agent','Cost','Count','Total'].map(h=><th key={h} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em',padding:'8px 14px',textAlign:h==='Total'||h==='Count'||h==='Cost'?'center':'left'}}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {creditEvents.map((ev,i)=>{
                  const total=ev.credits*ev.count;
                  return(
                    <tr key={i} style={{borderBottom:'1px solid rgba(61,31,13,.04)'}}>
                      <td style={{padding:'10px 14px',fontSize:12,color:'#3D1F0D'}}>{ev.event}</td>
                      <td style={{padding:'10px 14px'}}><span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:10,background:`${ev.agentColor}18`,color:ev.agentColor}}>{ev.agent}</span></td>
                      <td style={{padding:'10px 14px',textAlign:'center',fontSize:12,fontWeight:700,color:'#8b0020'}}>{ev.credits===0?'Free':ev.credits}</td>
                      <td style={{padding:'10px 14px',textAlign:'center',fontSize:12,color:'rgba(61,31,13,.6)'}}>{ev.count}</td>
                      <td style={{padding:'10px 14px',textAlign:'center',fontSize:12,fontWeight:700,color:total>0?'#3D1F0D':'rgba(61,31,13,.25)'}}>{total>0?total:'—'}</td>
                    </tr>
                  );
                })}
                <tr style={{background:'rgba(61,31,13,.03)',borderTop:'1px solid rgba(61,31,13,.1)'}}>
                  <td colSpan={4} style={{padding:'10px 14px',fontSize:12,fontWeight:700,color:'rgba(61,31,13,.5)'}}>Total consumed this period</td>
                  <td style={{padding:'10px 14px',textAlign:'center',fontSize:14,fontWeight:700,color:'#E8813A'}}>{creditUsed}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Trailing 3-month trend */}
          <div className="card" style={{marginBottom:16,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'rgba(61,31,13,.7)',marginBottom:14}}>Trailing 3-Month Credit Consumption</div>
            <div style={{display:'flex',gap:16,alignItems:'flex-end',height:80}}>
              {[{mo:'Mar',val:8},{mo:'Apr',val:14},{mo:'May',val:12,current:true}].map(m=>{
                const maxVal=20;
                const pct=Math.round((m.val/maxVal)*100);
                return(
                  <div key={m.mo} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
                    <span style={{fontSize:11,fontWeight:700,color:m.current?'#E8813A':'rgba(61,31,13,.5)'}}>{m.val}</span>
                    <div style={{width:'100%',background:'rgba(61,31,13,.07)',borderRadius:4,height:56,display:'flex',alignItems:'flex-end'}}>
                      <div style={{width:'100%',height:`${pct}%`,background:m.current?'#E8813A':'rgba(61,31,13,.2)',borderRadius:4,transition:'height .4s ease'}}/>
                    </div>
                    <span style={{fontSize:10,color:'rgba(61,31,13,.4)',fontWeight:m.current?700:400}}>{m.mo}{m.current?' (now)':''}</span>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop:12,padding:'8px 10px',background:'rgba(61,31,13,.03)',borderRadius:6,fontSize:11,color:'rgba(61,31,13,.5)'}}>
              Avg: 11.3 credits/month · Projected May close-out: ~{projected} credits · {projected>20?'⚠ Overage likely — consider purchasing additional credits.':'Within pool — no overage expected.'}
            </div>
          </div>
        </div>

        {/* Right column — plan info + add-ons + purchase */}
        <div>
          {/* Current plan */}
          <div className="card" style={{marginBottom:14,padding:'18px 20px',borderLeft:'3px solid #E8813A'}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#E8813A',marginBottom:8}}>Current Plan — Pro</div>
            {[
              ['Locations','Up to 50'],
              ['Named users','15 (2 Admin, 5 Mgr, 13 Member)'],
              ['Scribe','Unlimited abstractions'],
              ['Scout comps','50 / month'],
              ['Strat scenarios','5 / month'],
              ['CKE','Included — 100 meetings / month'],
              ['Workplace Analytics','Included with HRIS'],
              ['Credits included','20 / month'],
            ].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'4px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                <span style={{color:'rgba(61,31,13,.5)'}}>{k}</span>
                <span style={{fontWeight:600,color:'#3D1F0D',textAlign:'right',maxWidth:140}}>{v}</span>
              </div>
            ))}
            <div style={{marginTop:12,display:'flex',gap:8}}>
              <a href={`${pricingUrl}#tiers`} target="_blank" rel="noopener noreferrer" style={{flex:1,textAlign:'center',padding:'8px',background:'rgba(139,0,33,.05)',border:'1px solid rgba(139,0,33,.15)',borderRadius:6,fontSize:12,fontWeight:700,color:'#8b0020',cursor:'pointer'}}>Upgrade to Enterprise →</a>
            </div>
          </div>

          {/* Purchase credits */}
          {isAdmin&&(
            <div className="card" style={{marginBottom:14,padding:'18px 20px'}}>
              <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',marginBottom:10}}>Purchase Additional Credits</div>
              {poolPurchased?(
                <div style={{background:'#d1fae5',borderRadius:8,padding:'10px 12px',fontSize:12,color:'#065f46',fontWeight:600}}>✓ Credit pool added to this billing period.</div>
              ):(
                <>
                  {creditPoolOptions.map(p=>(
                    <div key={p.size} onClick={()=>setSelectedPool(selectedPool===p.size?null:p.size)} style={{padding:'9px 12px',borderRadius:7,border:`1px solid ${selectedPool===p.size?'#E8813A':'rgba(61,31,13,.1)'}`,marginBottom:6,cursor:'pointer',background:selectedPool===p.size?'rgba(232,129,58,.06)':'transparent',transition:'all .15s'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                        <div>
                          <span style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>{p.size}</span>
                          <span style={{fontSize:11,color:'rgba(61,31,13,.4)',marginLeft:6}}>{p.rate}/credit</span>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <span style={{fontSize:13,fontWeight:700,color:'#8b0020'}}>{p.price}</span>
                          <span style={{fontSize:10,color:'#16a34a',marginLeft:4,background:'#d1fae5',padding:'1px 5px',borderRadius:8}}>{p.discount} off</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    disabled={!selectedPool}
                    onClick={()=>{if(selectedPool){setShowPoolModal(true);}}}
                    style={{width:'100%',marginTop:8,padding:'9px',background:selectedPool?'#8b0020':'rgba(61,31,13,.1)',color:selectedPool?'#fff':'rgba(61,31,13,.3)',border:'none',borderRadius:7,fontSize:13,fontWeight:700,cursor:selectedPool?'pointer':'not-allowed',fontFamily:'inherit',transition:'all .15s'}}
                  >
                    {selectedPool?`Purchase ${selectedPool}`:'Select a pool size'}
                  </button>
                  <p style={{fontSize:10,color:'rgba(61,31,13,.35)',marginTop:6,textAlign:'center'}}>Charged to card on file. Credits expire end of billing period.</p>
                </>
              )}
            </div>
          )}

          {/* Add-ons */}
          <div className="card" style={{padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#3D1F0D',marginBottom:12}}>Add-Ons &amp; Modules</div>
            {addons.map(a=>(
              <div key={a.name} style={{padding:'10px 0',borderBottom:'1px solid rgba(61,31,13,.05)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:3}}>
                  <span style={{fontSize:12,fontWeight:700,color:'#3D1F0D'}}>{a.name}</span>
                  <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:10,background:`${a.statusColor}18`,color:a.statusColor,textTransform:'capitalize'}}>{a.status}</span>
                </div>
                <div style={{fontSize:11,color:'rgba(61,31,13,.45)',lineHeight:1.4}}>{a.desc}</div>
                {a.status==='available'&&isAdmin&&(requested.has(a.name)
                  ?<span style={{marginTop:6,display:'inline-block',fontSize:10,fontWeight:700,color:'#16a34a'}}>✓ Requested — enabling shortly</span>
                  :<button onClick={()=>requestAddon(a.name)} disabled={enabling===a.name} style={{marginTop:6,fontSize:10,fontWeight:700,color:'#E8813A',background:'none',border:'1px solid rgba(232,129,58,.3)',borderRadius:5,padding:'3px 8px',cursor:enabling===a.name?'wait':'pointer',fontFamily:'inherit'}}>{enabling===a.name?'Requesting…':'Enable add-on →'}</button>)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full credit event reference */}
      <div className="card" style={{marginTop:20,overflow:'hidden'}}>
        <div style={{padding:'10px 16px',background:'#F5EDE0',borderBottom:'1px solid rgba(61,31,13,.07)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontSize:11,fontWeight:700,color:'rgba(61,31,13,.7)'}}>Complete Outcome Credit Table</span>
          <a href={`${pricingUrl}#credits`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,color:'#8b0020',fontWeight:600}}>See pricing page →</a>
        </div>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',minWidth:560}}>
            <thead>
              <tr style={{borderBottom:'1px solid rgba(61,31,13,.08)'}}>
                {['Credit Event','Credits','Completion Criterion','Agent'].map(h=><th key={h} style={{fontSize:10,fontWeight:700,color:'rgba(61,31,13,.4)',textTransform:'uppercase',letterSpacing:'.05em',padding:'8px 14px',textAlign:h==='Credits'?'center':'left'}}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                ['Lease abstracted → Portfolio Spine','1','Scribe extraction confirmed by human; written to Safe','Scribe','#D97706'],
                ['Option exercise monitored & alerted','1','Sentry artifact confirming monitoring event + Action Rail routed','Scribe + Sage','#059669'],
                ['Market comp retrieved → Portfolio Spine','1','Scout comp confirmed via Sentry; written to Safe Facts','Scout','#2563EB'],
                ['Client status update drafted & delivered','1','Draft approved by advisor and delivered via Setting or email','Scribe + Sage','#059669'],
                ['Floor plan settings mapping published','1','Sense settings map confirmed by human; written to Safe','Sense','#E8813A'],
                ['Settings performance report generated','1','Sense report written to Safe and surfaced in Status','Sense','#E8813A'],
                ['Scope milestone report (Enterprise)','1','Scope actuals written to Safe; variance vs. projection','Scope','#6b7280'],
                ['Stakeholder link created & first accessed','0.5','White-label view activated and first access confirmed','Sage','#059669'],
                ['Setting delta summary generated','0.5','Scribe redline analysis written to Setting','Scribe','#D97706'],
                ['Renewal scenario modeled (full Strat)','2','Strat output written to Safe; available in Setting + Financial','Strat','#7C3AED'],
                ['Due diligence report generated','2','Scribe report written to Safe; source documents cited','Scribe + Sage','#059669'],
                ['Capital Case modeled','2','Strat Capital Case written to Safe with budget, timeline, NPV','Strat','#7C3AED'],
                ['Right-sizing scenario modeled','2','Strat headcount-to-footprint reconciliation written to Safe','Strat','#7C3AED'],
                ['Friction audit report generated','2','Sense friction report written to Safe with typology + recs','Sense','#E8813A'],
                ['Pre-meeting brief generated','0 (included)','Compute cost absorbed in subscription','Sage','#059669'],
              ].map(([ev,cr,crit,ag,ac],i)=>(
                <tr key={i} style={{borderBottom:'1px solid rgba(61,31,13,.04)',background:cr==='0 (included)'?'rgba(61,31,13,.02)':'transparent'}}>
                  <td style={{padding:'9px 14px',fontSize:12,color:'#3D1F0D'}}>{ev}</td>
                  <td style={{padding:'9px 14px',textAlign:'center',fontWeight:700,color:'#8b0020',fontSize:13}}>{cr}</td>
                  <td style={{padding:'9px 14px',fontSize:11,color:'rgba(61,31,13,.55)',lineHeight:1.4}}>{crit}</td>
                  <td style={{padding:'9px 14px'}}><span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:10,background:`${ac}18`,color:ac}}>{ag}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Purchase confirmation modal */}
      {showPoolModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowPoolModal(false)}>
          <div style={{background:'#fff',borderRadius:16,padding:32,maxWidth:420,width:'90%',boxShadow:'0 20px 60px rgba(0,0,0,.2)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:14,fontWeight:700,color:'#3D1F0D',marginBottom:8}}>Purchase {selectedPool}</div>
            <p style={{fontSize:13,color:'rgba(61,31,13,.6)',marginBottom:8,lineHeight:1.6}}>
              Credit purchases are processed by our team. Click below to send a request and we'll add the credits and update your invoice within one business day.
            </p>
            <p style={{fontSize:12,color:'rgba(61,31,13,.4)',marginBottom:20}}>Selected: <strong>{selectedPool}</strong> · {creditPoolOptions.find(p=>p.size===selectedPool)?.price}</p>
            <div style={{display:'flex',gap:10}}>
              <button onClick={()=>setShowPoolModal(false)} style={{flex:1,padding:'10px',background:'rgba(61,31,13,.06)',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit',color:'rgba(61,31,13,.7)'}}>Cancel</button>
              <a
                href={`mailto:team@wxstage.com?subject=Credit%20pool%20purchase%3A%20${encodeURIComponent(selectedPool)}&body=Please%20add%20${encodeURIComponent(selectedPool)}%20to%20my%20account.`}
                onClick={()=>setShowPoolModal(false)}
                style={{flex:1,padding:'10px',background:'#8b0020',color:'#fff',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',textDecoration:'none',textAlign:'center',display:'flex',alignItems:'center',justifyContent:'center'}}
              >
                Send purchase request →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── APP ────────────────────────────────────────────────────── */
/* ── TEAM-1 Phase C — First-login profiling survey ──────────────────────────
   Three questions (department / size / focus) shown once per org on first login.
   Posts to /api/org/profile (idempotent upsert). Dismissible — a generic
   workspace still works. */
/* ════════════════════════════════════════════════════════════════════════════
   PRD v1.2 — The Focused Path. The stacked welcome modal (department / size /
   focus over a dimmed dashboard) is superseded by the /start Focus Shell:
   one decision per screen, persona-routed, fidelity-honest, revealed at the end.
   ════════════════════════════════════════════════════════════════════════════ */

/* Persona Router — one question that bends the flow (§Router). Each persona
   gets its own earliest win, critical inputs, intent ordering, and invite. */
const SHELL_PERSONAS=[
  {id:'strategist',glyph:'🌍',label:'Workplace Strategist',archetype:'e.g. Sr. Director, Global Workplace',
   intentOrder:['baseline','friction','expiration'],
   // §Router — intent cards are re-ordered AND re-labeled per persona
   intentLabels:{
     baseline:{intent:'Portfolio Health — over/under-housed?',hint:'A mis-sizing read across your largest markets'},
     friction:{intent:'Operational Efficiency',hint:'Find what’s costing your workplaces time'},
     expiration:{intent:'Lease Risk',hint:'Expirations that could force a space decision'},
   },
   tour:{workflow:'mis_sizing',title:'Where is the portfolio mis-sized?',
     desc:'No data needed. Sage compares your seats-to-headcount pattern against typical occupier patterns and flags where you are likely over- or under-housed.',
     fields:[['markets','Your three largest markets and rough headcount in each','e.g. NYC ~1,200 · Austin ~400 · London ~300'],
             ['utilizationSense','Your rough sense of utilisation','e.g. NYC feels half-empty midweek; Austin is over capacity']],
     inviteWho:'regional lead',
     inviteWhy:'The over/under-housed read becomes a planning decision only when the region and Finance see the same picture.',
     climbNav:'analytics'}},
  {id:'portfolio_manager',glyph:'📊',label:'Portfolio Manager',archetype:'e.g. PM, high-growth occupier',
   intentOrder:['baseline','expiration','friction'],
   intentLabels:{
     baseline:{intent:'Portfolio Health — the CFO baseline',hint:'A defensible picture of every obligation'},
     expiration:{intent:'Lease Risk — dates that can’t be missed',hint:'Find the rollovers before they find you'},
     friction:{intent:'Operational Efficiency',hint:'Where the assembly tax is hiding'},
   },
   tour:{workflow:'baseline',title:'Build a baseline the CFO will trust',
     desc:'Locations, approximate total SF, primary asset type, and your single biggest concern. Sage benchmarks it against comparable portfolios — no upload required.',
     fields:GUIDED_WORKFLOWS.baseline.fields,
     inviteWho:'CFO or lease admin',
     inviteWhy:'The baseline is the team’s shared artifact; the CFO viewer link is the credibility moment the role is built around.',
     climbNav:'portfolio'}},
  {id:'transaction_manager',glyph:'🤝',label:'Transaction Manager',archetype:'e.g. TM, multi-market occupier',
   intentOrder:['expiration','baseline','friction'],
   intentLabels:{
     expiration:{intent:'Lease Risk — the deal on your desk',hint:'Start from the expiration driving the deal'},
     baseline:{intent:'Portfolio Health',hint:'Context for the negotiation — the whole picture'},
     friction:{intent:'Operational Efficiency',hint:'Deal-process friction worth removing'},
   },
   tour:{workflow:'deal_brief',title:'What do I need before I engage the broker?',
     desc:'Name the deal: one submarket, deal type, and what’s driving the timeline. Sage builds a pre-engagement brief — no upload required.',
     fields:[['submarket','Target site or submarket','e.g. Austin — The Domain'],
             ['dealType','Deal type','e.g. Renewal, Expansion, Rightsizing, Exit'],
             ['timelineDriver','What’s driving the timeline','e.g. lease expires Dec 2026; option notice in 90 days']],
     inviteWho:'broker',
     inviteWhy:'This brief is sharper with your broker in the room — they can validate the comps today.',
     climbNav:'setting'}},
  {id:'other',glyph:'✦',label:'Something else',archetype:'browse the generic starting points'},
];

/* The Fidelity Ladder (§Fidelity) — every Sage output states how good it is
   and names the exact next input that climbs it. Honesty is the feature. */
const FIDELITY_TIERS=[
  ['Directional','#C84B2F','Built from typed answers — useful for orientation, not for the board.'],
  ['Grounded','#B8860B','Built from your real documents — figures you can put in front of a colleague.'],
  ['Portfolio-true','#059669','Whole portfolio, monitored live — an answer you can defend to the CFO.'],
];
function FidelityBadge({tier='Directional',climb}){
  const idx=Math.max(0,FIDELITY_TIERS.findIndex(t=>t[0].toLowerCase()===String(tier).toLowerCase()));
  const t=FIDELITY_TIERS[idx];
  return(
    <span style={{display:'inline-flex',alignItems:'center',gap:7}} title={t[2]+(climb?(' To climb: '+climb):'')}>
      <span style={{display:'inline-flex',gap:2,alignItems:'flex-end'}}>
        {FIDELITY_TIERS.map((x,i)=>(<span key={i} style={{width:7,height:13,borderRadius:2,background:i<=idx?t[1]:'rgba(61,31,13,.12)'}}/>))}
      </span>
      <span style={{fontSize:11,fontWeight:700,color:t[1],letterSpacing:'.02em'}}>{t[0]}</span>
    </span>
  );
}

/* §Reveal — hero-slot continuity: the first finding carries into the dashboard
   with its fidelity badge. Rendered by StatusBriefPage from sessionStorage. */
function FirstFindingHero({finding,onDismiss,onNav}){
  const o=finding.output||{};
  return(
    <div className="card joy-glow" style={{padding:26,marginBottom:18,borderLeft:'4px solid #059669'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:8,flexWrap:'wrap'}}>
        <span style={{fontSize:11,fontWeight:700,color:'#059669',textTransform:'uppercase',letterSpacing:'.07em'}}>Your first finding · Sage · {o.workflowLabel||'First output'}</span>
        <FidelityBadge tier={o.fidelity||'Directional'} climb={o.climb}/>
      </div>
      <div style={{fontFamily:'Georgia,serif',fontSize:20,color:'#3D1F0D',lineHeight:1.25,marginBottom:8}}>{o.headline}</div>
      {o.summary&&<div style={{fontSize:13.5,color:'#2D2A24',lineHeight:1.7,marginBottom:10}}>{o.summary}</div>}
      {(o.points||[]).length>0&&(
        <ul style={{margin:'0 0 12px',paddingLeft:18}}>{o.points.map((p,i)=>(<li key={i} style={{fontSize:13,color:'#3A3530',lineHeight:1.6,marginBottom:4}}>{p}</li>))}</ul>
      )}
      {o.recommendedAction&&(
        <div style={{padding:'10px 14px',background:'rgba(232,129,58,.08)',borderLeft:'3px solid #E8813A',borderRadius:'0 6px 6px 0',fontSize:13,color:'#3D1F0D',marginBottom:10}}>
          <b>▶ Recommended:</b> {o.recommendedAction}
        </div>
      )}
      {o.climb&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.55)',marginBottom:12}}><b style={{color:'#B8860B'}}>To climb →</b> {o.climb}</div>}
      <div style={{display:'flex',gap:12,alignItems:'center'}}>
        <button onClick={()=>onNav&&onNav('portfolio')} style={{padding:'9px 16px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:5,fontSize:12.5,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Connect data to ground it →</button>
        <span onClick={onDismiss} style={{fontSize:12,color:'rgba(61,31,13,.5)',cursor:'pointer'}}>Dismiss</span>
      </div>
    </div>
  );
}

/* The Single-Focus Shell (§Shell) — a dedicated /start surface whose only job
   is the first win. Wordmark, one decision per screen, one quiet exit ramp.
   No sidebar, no tiles, no Ask-Sage bar: the chrome waits (Rule 02). */
function FocusShell({onReveal,tenantId}){
  const authFetch=useAuthFetch();
  const [checking,setChecking]=useState(true);
  const [step,setStep]=useState('role');        // role | intent | inputs | output | climb
  const [persona,setPersona]=useState(null);    // SHELL_PERSONAS entry
  const [wfKey,setWfKey]=useState(null);        // active workflow key
  const [inputs,setInputs]=useState({});
  const [busy,setBusy]=useState(false);
  const [err,setErr]=useState('');
  const [output,setOutput]=useState(null);
  const [findingId,setFindingId]=useState(null);
  const [inviteEmail,setInviteEmail]=useState('');
  const [inviteState,setInviteState]=useState('');   // '' | sending | sent | skipped
  // P0-3 — Step-3 upload in place: both paths converge at the First Output
  const [docBusy,setDocBusy]=useState(false);
  const [docErr,setDocErr]=useState('');
  const docInputRef=useRef(null);
  // Returning users with completed setup never see the shell (P0).
  useEffect(()=>{
    let live=true;
    authFetch('/api/org/profile')
      .then(p=>{if(!live)return; if(p&&p.completed===true)onReveal({skipAnim:true}); else setChecking(false);})
      .catch(()=>{if(live)setChecking(false);});  // dev/unauthenticated: show the shell
    return()=>{live=false;};
  },[]);
  // Tour spec for the current path: persona tour, or a generic intent (re-ordered per persona).
  const tour=persona&&persona.tour&&wfKey===persona.tour.workflow?persona.tour:null;
  const generic=wfKey&&GUIDED_WORKFLOWS[wfKey]?GUIDED_WORKFLOWS[wfKey]:null;
  const fields=tour?tour.fields:(generic?generic.fields:[]);
  const tourTitle=tour?tour.title:(generic?generic.label:'');
  const inviteWho=(tour&&tour.inviteWho)||(output&&output.collaboratorRole)||'teammate';
  const ready=fields.length>0&&fields.every(([k])=>String(inputs[k]||'').trim());
  // P1 instrumentation — fire-and-forget funnel events (never block the UX).
  const track=(event,meta)=>{try{authFetch('/api/onboarding/event',{method:'POST',body:{event,persona:(persona&&persona.id)||undefined,meta:meta||undefined}});}catch(e){}};
  const complete=async(opts={})=>{
    try{await authFetch('/api/org/profile',{method:'POST',body:{role:(persona&&persona.id)||'other'}});}catch(e){}
    // Resolve the authoritative tenant so the finding/chips are STAMPED to this
    // account and can never render under a different org after a switch.
    let tid=tenantId;
    if(!tid){try{const me=await authFetch('/api/me');tid=me&&(me.tenantId||me.orgId);}catch(e){}}
    // Gap Patch §Bypass — the dashboard's Sage chips personalize to the chosen
    // role/intent even when the tour is skipped (never a blank, unconfigured state).
    try{localStorage.setItem('stage_onboarding_intent',(wfKey||(persona&&persona.id))||'other');}catch(e){}
    try{if(tid)localStorage.setItem('stage_active_tenant',tid);}catch(e){}
    // Funnel: finishing after a real output = completed; leaving before = bypassed.
    track(output?'tour_completed':'tour_bypassed');
    if(output){
      try{sessionStorage.setItem('stage_first_finding',JSON.stringify({output,findingId,persona:(persona&&persona.id)||'other',tenantId:tid||null}));}catch(e){}
    }
    onReveal({navTo:opts.navTo||'brief'});
  };
  const generate=async()=>{
    setBusy(true);setErr('');
    try{
      const r=await authFetch('/api/onboarding/guided',{method:'POST',body:{workflow:wfKey,inputs}});
      if(r&&r.ok){setOutput(r.output);setFindingId(r.findingId||null);setStep('output');track('first_output');track('fidelity_reached',(r.output&&r.output.fidelity)||'Directional');}
      else setErr((r&&r.error)||'Could not generate your first output.');
    }catch(e){
      const code=(String(e.message||'').match(/^(\d{3})/)||[])[1];
      setErr(code==='422'?'Please complete the fields and try again.':'Could not reach Sage — please retry.');
    }finally{setBusy(false);}
  };
  const sendInvite=async()=>{
    if(!findingId||!inviteEmail.trim())return;
    setInviteState('sending');
    try{
      const r=await authFetch(`/api/findings/${findingId}/invite`,{method:'POST',body:{email:inviteEmail.trim(),role:inviteWho}});
      setInviteState(r&&r.ok?'sent':'error');
    }catch(e){setInviteState('error');}
  };
  // P0-3 — the upload path activates in place of manual input; on success we
  // return to the SAME First Output screen, now wearing a Grounded badge.
  const uploadDocument=async(e)=>{
    const f=e.target.files&&e.target.files[0];
    if(!f||!wfKey)return;
    setDocBusy(true);setDocErr('');
    try{
      const fd=new FormData();
      fd.append('workflow',wfKey);
      fd.append('inputs',JSON.stringify(inputs));
      fd.append('file',f);
      const r=await authFetch('/api/onboarding/guided-document',{method:'POST',body:fd});
      if(r&&r.ok){
        setOutput(r.output);
        if(r.findingId)setFindingId(r.findingId);
        setInviteState('');          // the grounded finding gets its own invite moment
        setStep('output');           // converge at the same First Output moment
        track('first_output');track('fidelity_reached',(r.output&&r.output.fidelity)||'Grounded');
      }else setDocErr((r&&r.error)||'Could not read that document.');
    }catch(err){
      const code=(String(err.message||'').match(/^(\d{3})/)||[])[1];
      setDocErr(code==='422'
        ?'That file couldn’t be read — try a text-based PDF, an XLSX, or a CSV. Scanned images need the full Scribe flow in your workspace.'
        :'Could not reach Scribe — please retry, or add the document from your workspace after setup.');
    }finally{setDocBusy(false);if(docInputRef.current)docInputRef.current.value='';}
  };
  const stepNum=step==='inputs'?1:step==='output'?2:step==='climb'?3:0;
  if(checking)return(
    <div className="focus-shell">
      <div style={{fontFamily:'Georgia,serif',fontSize:15,color:'#3D1F0D'}}>stage <span style={{color:'#8b0020'}}>Status</span></div>
      <div className="shell-step"><div className="skeleton" style={{width:280,height:16,borderRadius:8}}/></div>
    </div>
  );
  return(
    <div className="focus-shell">
      {/* The only persistent chrome: the wordmark (top-left) */}
      <div style={{fontFamily:'Georgia,serif',fontSize:15,color:'#3D1F0D',flexShrink:0}}>stage <span style={{color:'#8b0020'}}>Status</span></div>

      {/* ── Step: role — "Who are you here as?" (one decision) ── */}
      {step==='role'&&(
        <div className="shell-step" key="role">
          <div style={{fontFamily:'Georgia,serif',fontSize:30,color:'#3D1F0D',marginBottom:8}}>Who are you here as?</div>
          <div style={{fontSize:13.5,color:'rgba(61,31,13,.55)',marginBottom:30,maxWidth:440,lineHeight:1.6}}>One question — it shapes your first win, what Sage asks for, and who you’ll want in the room.</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap',justifyContent:'center',marginBottom:34}}>
            {SHELL_PERSONAS.map(p=>(
              <div key={p.id} className="persona-card" onClick={()=>{
                setPersona(p);setInputs({});setErr('');
                try{authFetch('/api/onboarding/event',{method:'POST',body:{event:'role_selected',persona:p.id}});}catch(e){}
                if(p.tour){setWfKey(p.tour.workflow);setStep('inputs');}
                else{setWfKey(null);setStep('intent');}
              }}>
                <div style={{fontSize:26,marginBottom:10}}>{p.glyph}</div>
                <div style={{fontFamily:'Georgia,serif',fontSize:15,color:'#3D1F0D',marginBottom:4,lineHeight:1.25}}>{p.label}</div>
                <div style={{fontSize:10.5,color:'rgba(61,31,13,.45)',fontFamily:'monospace'}}>{p.archetype}</div>
              </div>
            ))}
          </div>
          <button className="shell-exit" onClick={()=>complete({navTo:'brief'})}>I know my way around — skip setup</button>
        </div>
      )}

      {/* ── Step: intent — generic picker; persona re-orders, never discards ── */}
      {step==='intent'&&(
        <div className="shell-step" key="intent">
          <div style={{fontFamily:'Georgia,serif',fontSize:26,color:'#3D1F0D',marginBottom:8}}>What do you want to solve first?</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.55)',marginBottom:26,maxWidth:440,lineHeight:1.6}}>Sage produces a real result from your answers — before any data is connected.</div>
          <div style={{display:'flex',gap:14,flexWrap:'wrap',justifyContent:'center',marginBottom:30}}>
            {((persona&&persona.intentOrder)||Object.keys(GUIDED_WORKFLOWS)).map(k=>{
              const w=GUIDED_WORKFLOWS[k];if(!w)return null;
              // §Router — re-labeled per persona; "Something else" keeps the
              // unreordered, unrelabeled generic picker.
              const ov=(persona&&persona.intentLabels&&persona.intentLabels[k])||{};
              return(
                <div key={k} className="persona-card" style={{width:190}} onClick={()=>{setWfKey(k);setInputs({});setErr('');setStep('inputs');}}>
                  <div style={{fontSize:23,marginBottom:8}}>{w.icon}</div>
                  <div style={{fontFamily:'Georgia,serif',fontSize:14,color:'#3D1F0D',marginBottom:4}}>{ov.intent||w.intent}</div>
                  <div style={{fontSize:11,color:'rgba(61,31,13,.5)',lineHeight:1.4}}>{ov.hint||w.hint}</div>
                </div>
              );
            })}
          </div>
          <button className="shell-exit" onClick={()=>complete({navTo:'brief'})}>Skip guided setup — go to dashboard</button>
        </div>
      )}

      {/* ── Step 1: inputs — context as a trade, not a toll (Rule 06) ── */}
      {step==='inputs'&&(
        <div className="shell-step" key="inputs">
          <div style={{fontFamily:'monospace',fontSize:10,letterSpacing:'.14em',textTransform:'uppercase',color:'#E8813A',marginBottom:10}}>Step 1 of 3 · Directional</div>
          <div style={{fontFamily:'Georgia,serif',fontSize:26,color:'#3D1F0D',marginBottom:8,maxWidth:520,lineHeight:1.2}}>{tourTitle}</div>
          {(tour&&tour.desc)&&<div style={{fontSize:13,color:'rgba(61,31,13,.55)',marginBottom:24,maxWidth:480,lineHeight:1.6}}>{tour.desc}</div>}
          <div style={{display:'grid',gap:13,width:'100%',maxWidth:460,textAlign:'left',marginBottom:18}}>
            {fields.map(([k,label,ph])=>(
              <div key={k}>
                <label style={{display:'block',fontSize:11,fontWeight:600,color:'rgba(61,31,13,.6)',marginBottom:4}}>{label}</label>
                {(k==='description'||k==='markets')
                  ?<textarea value={inputs[k]||''} onChange={e=>setInputs({...inputs,[k]:e.target.value})} placeholder={ph} rows={3} style={{width:'100%',padding:'10px 13px',border:'1px solid rgba(61,31,13,.2)',borderRadius:8,fontSize:13,fontFamily:'inherit',resize:'vertical',background:'#fff',boxSizing:'border-box'}}/>
                  :<input value={inputs[k]||''} onChange={e=>setInputs({...inputs,[k]:e.target.value})} placeholder={ph} style={{width:'100%',padding:'10px 13px',border:'1px solid rgba(61,31,13,.2)',borderRadius:8,fontSize:13,fontFamily:'inherit',background:'#fff',boxSizing:'border-box'}}/>}
              </div>
            ))}
          </div>
          {err&&<div style={{fontSize:12.5,color:'#b91c1c',marginBottom:12}}>{err}</div>}
          <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:26}}>
            <button disabled={!ready||busy} onClick={generate} style={{padding:'11px 24px',background:ready&&!busy?'#3D1F0D':'rgba(61,31,13,.25)',color:'#F5EDE0',border:'none',borderRadius:6,fontSize:13.5,fontWeight:600,cursor:ready&&!busy?'pointer':'not-allowed',fontFamily:'inherit'}}>{busy?'Sage is thinking…':'Generate with Sage'}</button>
            <span onClick={()=>{setStep(persona&&persona.tour?'role':'intent');setErr('');}} style={{fontSize:12,color:'rgba(61,31,13,.5)',cursor:'pointer'}}>← Back</span>
            {persona&&persona.tour&&wfKey===persona.tour.workflow&&(
              <span onClick={()=>{setStep('intent');setErr('');}} style={{fontSize:12,color:'rgba(61,31,13,.5)',cursor:'pointer',textDecoration:'underline',textUnderlineOffset:3}}>Choose a different focus</span>
            )}
          </div>
          <button className="shell-exit" onClick={()=>complete({navTo:'brief'})}>Skip guided setup — go to dashboard</button>
        </div>
      )}

      {/* ── Step 2: the first output + invite at the peak (Rule 05) ── */}
      {step==='output'&&output&&(
        <div className="shell-step" key="output" style={{justifyContent:'flex-start',paddingTop:36}}>
          <div style={{fontFamily:'monospace',fontSize:10,letterSpacing:'.14em',textTransform:'uppercase',color:'#059669',marginBottom:14}}>Step 2 of 3 · Your first output</div>
          <div style={{width:'100%',maxWidth:620,textAlign:'left',background:'#fff',border:'1px solid rgba(61,31,13,.1)',borderLeft:'4px solid #059669',borderRadius:12,padding:'24px 26px',marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,marginBottom:10,flexWrap:'wrap'}}>
              <span style={{fontSize:10.5,fontWeight:700,color:'rgba(61,31,13,.5)',textTransform:'uppercase',letterSpacing:'.07em'}}>Sage · {output.workflowLabel}</span>
              <FidelityBadge tier={output.fidelity||'Directional'} climb={output.climb}/>
            </div>
            <div style={{fontFamily:'Georgia,serif',fontSize:21,color:'#3D1F0D',lineHeight:1.3,marginBottom:8}}>{output.headline}</div>
            <div style={{fontSize:13.5,color:'#2D2A24',lineHeight:1.7,marginBottom:12}}>{output.summary}</div>
            {(output.points||[]).length>0&&(
              <ul style={{margin:'0 0 12px',paddingLeft:18}}>{output.points.map((p,i)=>(<li key={i} style={{fontSize:13,color:'#3A3530',lineHeight:1.6,marginBottom:4}}>{p}</li>))}</ul>
            )}
            {output.recommendedAction&&(
              <div style={{padding:'10px 14px',background:'rgba(232,129,58,.08)',borderLeft:'3px solid #E8813A',borderRadius:'0 6px 6px 0',fontSize:13,color:'#3D1F0D',marginBottom:10}}>
                <b>▶ Recommended:</b> {output.recommendedAction}
              </div>
            )}
            {output.caveat&&<div style={{fontSize:11.5,color:'rgba(61,31,13,.5)',fontStyle:'italic'}}>{output.caveat}</div>}
          </div>
          {/* Persona-aware invite, inline at the emotional peak — never generic */}
          {findingId&&inviteState!=='skipped'&&(
            inviteState==='sent'
            ?<div style={{width:'100%',maxWidth:620,textAlign:'left',padding:'12px 16px',background:'rgba(5,150,105,.08)',borderLeft:'3px solid #059669',borderRadius:'0 8px 8px 0',fontSize:13,color:'#065f46',marginBottom:18}}>✓ Invite sent — your {inviteWho} will land directly on this finding, not a blank dashboard.</div>
            :<div style={{width:'100%',maxWidth:620,textAlign:'left',background:'#3D1F0D',borderRadius:12,padding:'18px 22px',marginBottom:18}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,marginBottom:6}}>
                <div style={{fontFamily:'Georgia,serif',fontSize:16,color:'#F5EDE0'}}>Invite your {inviteWho}</div>
                <span onClick={()=>{if(findingId)authFetch(`/api/findings/${findingId}/skip-invite`,{method:'POST'}).catch(()=>{});setInviteState('skipped');}} style={{fontFamily:'monospace',fontSize:10,letterSpacing:'.06em',textTransform:'uppercase',color:'rgba(245,237,224,.45)',textDecoration:'underline',cursor:'pointer'}}>Skip for now</span>
              </div>
              <div style={{fontSize:12,color:'rgba(245,237,224,.6)',lineHeight:1.55,marginBottom:12}}>{(tour&&tour.inviteWhy)||'This finding is more actionable with the right colleague in the room — they land directly on it.'}</div>
              <div style={{display:'flex',gap:10}}>
                <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={{flex:1,padding:'9px 12px',border:'1px solid rgba(245,237,224,.25)',borderRadius:6,fontSize:13,fontFamily:'inherit',background:'rgba(245,237,224,.08)',color:'#F5EDE0',outline:'none'}}/>
                <button disabled={!inviteEmail.trim()||inviteState==='sending'} onClick={sendInvite} style={{padding:'9px 18px',background:inviteEmail.trim()?'#C84B2F':'rgba(245,237,224,.2)',color:'#fff',border:'none',borderRadius:6,fontSize:13,fontWeight:600,cursor:inviteEmail.trim()?'pointer':'not-allowed',fontFamily:'inherit',whiteSpace:'nowrap'}}>{inviteState==='sending'?'Sending…':'Send invite'}</button>
              </div>
              {inviteState==='error'&&<div style={{fontSize:12,color:'#fca5a5',marginTop:8}}>Could not send — try again.</div>}
            </div>
          )}
          <button onClick={()=>setStep('climb')} style={{padding:'11px 26px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:6,fontSize:13.5,fontWeight:600,cursor:'pointer',fontFamily:'inherit',marginBottom:22}}>Continue →</button>
          <button className="shell-exit" onClick={()=>complete({navTo:'brief'})}>Skip ahead — open my workspace</button>
        </div>
      )}

      {/* ── Step 3: enhancement, never a gate — climb the ladder (§Tours) ── */}
      {step==='climb'&&(
        <div className="shell-step" key="climb">
          <div style={{fontFamily:'monospace',fontSize:10,letterSpacing:'.14em',textTransform:'uppercase',color:'#B8860B',marginBottom:10}}>Step 3 of 3 · Optional — your answer, sharpened</div>
          <div style={{fontFamily:'Georgia,serif',fontSize:26,color:'#3D1F0D',marginBottom:10}}>Make it Grounded.</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.55)',marginBottom:24,maxWidth:460,lineHeight:1.65}}>{(output&&output.climb)||'Add one real document — a single lease, rent roll, or term sheet — and Scribe replaces estimates with actuals.'}</div>
          <div style={{display:'flex',flexDirection:'column',gap:8,width:'100%',maxWidth:480,marginBottom:28,textAlign:'left'}}>
            {FIDELITY_TIERS.map(([name,color,desc],i)=>(
              <div key={name} style={{display:'flex',gap:12,alignItems:'center',padding:'10px 14px',background:i===0?'#fff':'rgba(255,255,255,.5)',border:`1px solid ${i===0?color+'55':'rgba(61,31,13,.08)'}`,borderRadius:10}}>
                <span style={{display:'inline-flex',gap:2}}>{FIDELITY_TIERS.map((x,j)=>(<span key={j} style={{width:7,height:14,borderRadius:2,background:j<=i?color:'rgba(61,31,13,.1)'}}/>))}</span>
                <div><div style={{fontSize:12.5,fontWeight:700,color:'#3D1F0D'}}>{name}{i===0?' — you are here':''}</div>
                <div style={{fontSize:11,color:'rgba(61,31,13,.55)',lineHeight:1.45}}>{desc}</div></div>
              </div>
            ))}
          </div>
          {/* P0-3 — the upload activates IN PLACE; success converges at the
              same First Output moment, upgraded to Grounded */}
          <div style={{display:'flex',flexDirection:'column',gap:10,alignItems:'center',marginBottom:26}}>
            <input ref={docInputRef} type="file" accept=".pdf,.xlsx,.csv,.txt,.md,.tsv" style={{display:'none'}} onChange={uploadDocument}/>
            <button onClick={()=>docInputRef.current&&docInputRef.current.click()} disabled={docBusy} style={{padding:'11px 24px',background:docBusy?'rgba(200,75,47,.55)':'#C84B2F',color:'#fff',border:'none',borderRadius:6,fontSize:13.5,fontWeight:600,cursor:docBusy?'wait':'pointer',fontFamily:'inherit'}}>{docBusy?'Scribe is reading your document…':'Upload a document now — right here'}</button>
            {docErr&&<div style={{fontSize:12,color:'#b91c1c',maxWidth:440,lineHeight:1.5,textAlign:'center'}}>{docErr}</div>}
            <span onClick={()=>complete({navTo:(tour&&tour.climbNav)||'portfolio'})} style={{fontSize:12,color:'rgba(61,31,13,.5)',cursor:'pointer',textDecoration:'underline',textUnderlineOffset:3}}>or add it later from your workspace →</span>
          </div>
          <button className="shell-exit" onClick={()=>complete({navTo:'brief'})}>Finish — open my workspace</button>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   HELP SURFACE — the always-available "I'm not sure what this does" panel.
   A right-side drawer (not a modal): replay onboarding (the 30-day tour
   re-entry promised by v1.1 §Bypass and v1.2 exit-ramps), the Friendly Guide,
   the keyboard map, the agent roster, contextual page help, and a clear
   support + security path for enterprise decision-makers.
   ════════════════════════════════════════════════════════════════════════════ */
const HELP_PAGES={
  brief:{title:'Status Brief',blurb:'Your morning hello — the one signal that matters most right now, with a button to dig in.',asks:['What are my most urgent decisions right now?','What should I tell the CFO today?']},
  portfolio:{title:'Portfolio Spine',blurb:'Every building, lease, and critical date in one place. It opens with a verdict — the next decision that cannot wait.',asks:['Which leases expire in the next 12 months?','What is the next irreversible decision?']},
  setting:{title:'WX Delivery',blurb:'Your projects as tabs. One fills the screen at a time and always shows its One Driver — the single thing that matters on it.',asks:['Which Setting needs action right now?','What is driving the 1 Penn Plaza renewal?']},
  analytics:{title:'WX Performance',blurb:'Fly high for the headline, swoop low for the evidence. Each layer is one click deeper; test your own theory in the Workbench.',asks:['Where is experience drift happening?','What is dragging Culture this week?']},
  actions:{title:'Action Rail',blurb:'Every task the team and the agents have raised, with owners and due dates. Calendar or board view — your pick.',asks:['Which P1 actions are overdue?','What is blocking deal progress?']},
  financial:{title:'Financial Surface',blurb:'Rent, budgets, and what each visit to the office really costs — numbers with explanations, not just numbers.',asks:['What is my total annual lease obligation?','Which location has the worst $/SF vs market?']},
  cke:{title:'Knowledge Engine',blurb:'Meetings and documents turned into searchable, decision-ready intelligence.',asks:['Summarise open Scribe review items','What did we decide last quarter?']},
  sage:{title:'Sage Intelligence',blurb:'The analytical partner. Ask anything — every answer shows its sources and confidence.',asks:["What's the highest-confidence insight today?",'What patterns are emerging across the portfolio?']},
  governance:{title:'Governance',blurb:'Who can do what, and a permanent diary of everything the agents have done. Trust, written down.',asks:['Show me pending Sentry gate items','Who approved the last lease abstract?']},
  plans:{title:'Plans & Credits',blurb:'Your subscription, usage, and billing.',asks:[]},
};
const HELP_KEYS=[
  ['⌘K','Jump to anything — switch Setting, run a command, or ask Sage'],
  ['⌘O','See all your projects at once, sorted by who needs help first'],
  ['⌘⇧T','Triage — show only what needs action right now'],
  ['⌘] / ⌘[','Next / previous project tab'],
  ['⌘P','Pin the project you are on'],
  ['Esc','Zoom out to the big picture'],
  ['⌘/','Open this Help panel'],
];
const HELP_AGENTS=[
  ['Sg','Sage','#059669','Answers your questions — always shows its sources'],
  ['Sc','Scribe','#D97706','Reads the paperwork; pulls every date and dollar'],
  ['Sn','Sentry','#8b0020','Watches every deadline so nothing lapses'],
  ['St','Scout','#2563EB','Checks the market so you negotiate with eyes open'],
  ['Sr','Strat','#7C3AED','Does the financial math and scenario models'],
  ['Se','Sense','#E8813A','Feels how spaces actually work for people'],
];
function HelpDrawer({open,onClose,page,onNav,onReplay,onAsk}){
  const closeRef=useRef(null);
  // a11y — focus the close control on open; Esc closes; restore focus on close.
  useEffect(()=>{
    if(!open)return;
    const prev=document.activeElement;
    const t=setTimeout(()=>{if(closeRef.current)closeRef.current.focus();},40);
    const onKey=e=>{if(e.key==='Escape'){e.stopPropagation();onClose();}};
    window.addEventListener('keydown',onKey,true);
    return()=>{clearTimeout(t);window.removeEventListener('keydown',onKey,true);try{prev&&prev.focus&&prev.focus();}catch(_){}};
  },[open,onClose]);
  if(!open)return null;
  const pg=HELP_PAGES[page]||HELP_PAGES.brief;
  const Item=({ico,bg,t,d,onClick,href})=>{
    const inner=<><span className="ico" style={{background:bg}}>{ico}</span><span style={{flex:1,minWidth:0}}><span className="t" style={{display:'block'}}>{t}</span><span className="d">{d}</span></span><span style={{color:'rgba(61,31,13,.3)',fontSize:14,flexShrink:0}}>→</span></>;
    return href
      ? <a className="help-item" href={href} target="_blank" rel="noreferrer" style={{textDecoration:'none'}}>{inner}</a>
      : <button className="help-item" onClick={onClick}>{inner}</button>;
  };
  return(
    <div className="help-scrim" role="dialog" aria-modal="true" aria-label="Help and resources" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="help-drawer">
        <div style={{display:'flex',alignItems:'center',gap:10,padding:'18px 22px 14px',borderBottom:'1px solid rgba(61,31,13,.08)',position:'sticky',top:0,background:'#F5EDE0',zIndex:1}}>
          <span style={{fontFamily:'Georgia,serif',fontSize:17,fontWeight:700,color:'#8b0020'}}>Help</span>
          <span style={{fontSize:11.5,color:'rgba(61,31,13,.45)'}}>everything you need to get unstuck</span>
          <button ref={closeRef} onClick={onClose} aria-label="Close help" style={{marginLeft:'auto',border:'none',background:'none',fontSize:22,color:'rgba(61,31,13,.4)',cursor:'pointer',lineHeight:1,padding:'0 2px'}}>×</button>
        </div>

        {/* Replay onboarding — the 30-day tour re-entry (v1.1/v1.2) */}
        <div className="help-sec">
          <div className="help-sec-label">Get set up</div>
          <Item ico="✦" bg="rgba(232,129,58,.14)" t="Replay the guided setup"
            d="Walk through your first win again — pick a focus, get a real Sage output." onClick={()=>{onReplay();onClose();}}/>
          <Item ico="📖" bg="rgba(139,0,33,.1)" t="The Friendly Guide"
            d="A plain-language tour of every page, the team, and the magic keys." href="https://status.wxstage.com/guide"/>
        </div>

        {/* Contextual — what's on this page */}
        <div className="help-sec">
          <div className="help-sec-label">You're on · {pg.title}</div>
          <div style={{fontSize:13,color:'rgba(61,31,13,.7)',lineHeight:1.6,marginBottom:pg.asks.length?12:0}}>{pg.blurb}</div>
          {pg.asks.length>0&&(
            <div style={{display:'flex',flexDirection:'column',gap:7}}>
              {pg.asks.map(q=>(
                <button key={q} onClick={()=>{onAsk(q);onClose();}} style={{textAlign:'left',background:'rgba(232,129,58,.07)',border:'1px solid rgba(232,129,58,.2)',borderRadius:9,padding:'9px 12px',fontSize:12.5,color:'#3D1F0D',cursor:'pointer',fontFamily:'inherit'}}>✦ {q}</button>
              ))}
            </div>
          )}
        </div>

        {/* Keyboard map */}
        <div className="help-sec">
          <div className="help-sec-label">Keyboard shortcuts</div>
          {HELP_KEYS.map(([k,d])=>(
            <div key={k} className="help-kbd-row">
              <span style={{fontSize:12.5,color:'rgba(61,31,13,.7)',lineHeight:1.4}}>{d}</span>
              <span className="help-kbd">{k}</span>
            </div>
          ))}
          <div style={{fontSize:10.5,color:'rgba(61,31,13,.4)',marginTop:8}}>On Windows, use Ctrl wherever you see ⌘.</div>
        </div>

        {/* Agent roster */}
        <div className="help-sec">
          <div className="help-sec-label">Meet the team</div>
          <div style={{display:'flex',flexDirection:'column',gap:9}}>
            {HELP_AGENTS.map(([ab,name,color,d])=>(
              <div key={name} style={{display:'flex',alignItems:'center',gap:11}}>
                <span style={{width:30,height:30,borderRadius:8,background:color,color:'#fff',fontFamily:'Georgia,serif',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{ab}</span>
                <span style={{minWidth:0}}><b style={{fontSize:13,color:'#3D1F0D'}}>{name}</b> <span style={{fontSize:12,color:'rgba(61,31,13,.55)'}}>— {d}</span></span>
              </div>
            ))}
          </div>
        </div>

        {/* Support + security — the enterprise trust path */}
        <div className="help-sec">
          <div className="help-sec-label">Support &amp; security</div>
          <Item ico="✉" bg="rgba(37,99,235,.1)" t="Contact support"
            d="Reach the Stage team — we usually reply the same business day." href="mailto:support@wxstage.com"/>
          <Item ico="⛨" bg="rgba(5,150,105,.1)" t="Trust &amp; security center"
            d="How your data is isolated, encrypted, and audited." href="https://trust.wxstage.com"/>
        </div>
      </div>
    </div>
  );
}

function App(){
  const clerk=useClerk(); // null in dev mode (auth disabled)
  const [page,setPage]=useState('brief');
  // Live mirror of `page` for non-React listeners (the back-nav guard below).
  const pageRef=useRef(page); pageRef.current=page;

  // Clickable grounded citations: a Sage SOURCES chip carries a `target` navigation
  // hint ({page, slug/match/id}); clicking it jumps to the surface where the cited
  // record lives. Maps the citation's logical page to the app's route key (the
  // Dealroom historically renders under 'setting'), stashes the item ref for the
  // destination page to focus, then closes the overlay so the page is visible.
  const navigateToCitation=useCallback((target)=>{
    if(!target||!target.page)return;
    const CITE_PAGE={governance:'governance',portfolio:'portfolio',dealroom:'setting',actions:'actions',cke:'cke'};
    const dest=CITE_PAGE[target.page]||target.page;
    try{
      const focus=target.slug||target.match||target.id||target.meeting||'';
      if(focus)sessionStorage.setItem('citationFocus',JSON.stringify({page:dest,tab:target.tab||'',ref:focus}));
    }catch(e){}
    setPage(dest);
    setSageOpen(false);
  },[]);
  // v1.2 — Focus Shell routing: 'shell' renders /start with NO dashboard chrome
  // mounted; 'reveal' mounts the dashboard with the assemble-in animation.
  const [shellMode,setShellMode]=useState(IS_START_ROUTE?'shell':'dashboard');
  const handleReveal=useCallback((opts={})=>{
    try{window.history.replaceState({},'','/status');}catch(e){}
    if(opts.navTo)setPage(opts.navTo);
    if(opts.skipAnim){setShellMode('dashboard');return;}
    setShellMode('reveal');
    setTimeout(()=>setShellMode('dashboard'),1200);
  },[]);
  // v1.1 §3.6 — global ⌘K command palette (Setting switcher + actions + Ask Sage)
  const [paletteOpen,setPaletteOpen]=useState(false);
  // Help surface — always-available drawer (⌘/ or the topbar ? button)
  const [helpOpen,setHelpOpen]=useState(false);
  // Replay onboarding — re-enter the Focus Shell on demand (the 30-day
  // tour re-entry promised by v1.1 §Bypass / v1.2 exit-ramps).
  const replayOnboarding=useCallback(()=>{
    try{window.history.replaceState({},'','/start');}catch(e){}
    setShellMode('shell');
  },[]);
  // ── Back-button guard ────────────────────────────────────────────────────
  // A freshly-onboarded session reaches the workspace straight off the Clerk /
  // Google OAuth redirect, and the shell collapses /start into /status via
  // replaceState — so the workspace's only predecessor in history is the
  // sign-in page. An unguarded Back therefore ejects the user back to Clerk
  // instead of moving within the app. In-app page changes are React state (not
  // history entries), so Back has nothing in-app to land on. We arm ONE history
  // entry on entering the dashboard; the first Back routes to the Status Brief
  // (the home surface) and re-arms while the user is on a deeper page. Once they
  // are on the Brief, a further Back is treated as a deliberate exit.
  React.useEffect(()=>{
    if(shellMode!=='dashboard')return;
    const arm=()=>{try{window.history.pushState({stageHome:1},'',window.location.href);}catch(e){}};
    arm();
    const onPop=()=>{
      if(pageRef.current!=='brief'){
        // Deep surface → keep the user inside the workspace, on the home surface.
        setPage('brief');
        setSageOpen(false);
        arm();
      }
      // else: already on the Brief — let this Back through (natural exit).
    };
    window.addEventListener('popstate',onPop);
    return ()=>window.removeEventListener('popstate',onPop);
  },[shellMode]);
  // P1 re-engagement — the 7-day resume nudge (bypassed + no data + >7 days).
  // Server decides eligibility + one-shot; we just show/dismiss.
  const [resumeNudge,setResumeNudge]=useState(false);
  const dismissNudge=useCallback(()=>{setResumeNudge(false);try{authFetch('/api/onboarding/nudge/dismiss',{method:'POST'});}catch(e){}},[]);
  // Gap 02 — deep-linked finding (invited collaborator lands here, ?finding=<id>)
  const deepFindingId=useMemo(()=>{try{return new URLSearchParams(window.location.search).get('finding');}catch(e){return null;}},[]);
  const [deepDismissed,setDeepDismissed]=useState(false);
  const dismissDeep=()=>{setDeepDismissed(true);try{const u=new URL(window.location.href);u.searchParams.delete('finding');window.history.replaceState({},'',u);}catch(e){}};
  const [sageOpen,setSageOpen]=useState(false);
  const [sageMessages,setSageMessages]=useState([]);
  const [sageStreaming,setSageStreaming]=useState(false);
  // Trust stage is DATA-GATED (see computeTrustCeiling): trustPref is what the
  // user has pinned (null = follow the data ceiling), and the effective stage —
  // derived after the live data is in scope below — is clamped to that ceiling so
  // Sage can never present analysis the org's data can't support.
  const [trustPref,setTrustPref]=useState(null); // hoisted: gates Sage content + WS tool access
  // WS connection status: 'connecting' | 'open' | 'reconnecting' | 'dead'
  // 'dead' means all retry attempts are exhausted — user must click Reconnect.
  const [wsStatus,setWsStatus]=useState('connecting');
  // Incrementing wsKey forces the WS useEffect to tear down and reconnect cleanly.
  // Used by the "Reconnect" banner button after exhaustion.
  const [wsKey,setWsKey]=useState(0);
  const wsRef=useRef(null);
  // Sage WS session id MUST be a UUID — the backend rejects any non-UUID session
  // (main.py _UUID_RE, "Issue 11"). The old `s_<ts>_<rand>` format was rejected
  // with a 1008/403 close, breaking Sage chat entirely ("reconnecting" loop).
  const sessionId=useMemo(()=>(
    (window.crypto&&window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g,c=>{const r=Math.random()*16|0;return (c==='x'?r:(r&0x3|0x8)).toString(16);})
  ),[]);

  // ── Live data state (demo constants as immediate fallback) ────
  const [livePortfolio,setLivePortfolio]=useState(null);
  const [liveDeals,setLiveDeals]=useState(null);
  const [currentUser,setCurrentUser]=useState(null); // {sub,email,name,role,authEnabled}
  const [liveActions,setLiveActions]=useState(null);
  const [liveInsights,setLiveInsights]=useState(null);
  const [liveSafe,setLiveSafe]=useState(null);
  const [liveSentry,setLiveSentry]=useState(null);
  const [liveTws,setLiveTws]=useState(null); // EW3 — per-org Time Well Spent (computed)
  // Feature flags from /api/client-config (fetched once; default safe=false)
  const [billingEnabled,setBillingEnabled]=useState(false);
  const [pricingUrl,setPricingUrl]=useState('https://wxstage.com/pricing');

  // Data-gated trust stage. Ceiling = what the org's grounded data can support
  // (demo uses the seeded constant, same as DataCtx). The effective stage follows
  // the ceiling unless the user has pinned a lower one; it can never exceed it, so
  // a zero-data org is hard-locked to Observer and unlocks automatically as data
  // lands. setTrustStage clamps every request into [1, ceiling].
  const _trustPortfolio=livePortfolio!==null?livePortfolio:(currentUser?.isDemo===true?PORTFOLIO:[]);
  const trustCeiling=computeTrustCeiling(_trustPortfolio,liveTws);
  const trustStage=trustPref==null?trustCeiling:Math.max(1,Math.min(trustPref,trustCeiling));
  const setTrustStage=(n)=>{setTrustPref(Math.max(1,Math.min(Number(n)||1,trustCeiling)));};

  // ── Authenticated fetch helper (shared via useAuthFetch hook) ──
  const authFetch=useAuthFetch();

  // ── ACCOUNT PRIVACY FENCE ───────────────────────────────────────────────
  // Last line of defence against cross-account bleed in a shared browser.
  // Identity = tenant + user, so a different ORG *or* a different EMAIL on the
  // same machine is treated as a new account. On a mismatch with the last-seen
  // identity we purge every account-scoped key so no later mount re-reads the
  // previous account's artifacts. Sign-out already clears these (see AuthGate),
  // so this normally fires only on the rare path where a session changes hands
  // without a clean sign-out.
  React.useEffect(()=>{
    if(!currentUser)return;
    // Tenant boundary = org context; tenantId in prod, orgId always present and
    // equal to it (org-scoped). Fall back so the fence is never silently a no-op.
    const tenant=currentUser.tenantId||currentUser.orgId;
    if(!tenant)return;
    const id=tenant+'|'+(currentUser.sub||'');
    let prev=null;try{prev=localStorage.getItem('stage_active_account');}catch(e){}
    if(prev&&prev!==id){
      // Account changed in this browser — purge the prior account's client state
      // so nothing mounts (or re-reads) the previous account's artifacts. No
      // reload: switching accounts requires logout→login, which already remounts
      // the whole app via AuthGate, and the visible finding has its own tenant
      // render gate (see StatusBriefPage). A reload here only risks loops.
      purgeAccountState();
    }
    try{localStorage.setItem('stage_active_account',id);localStorage.setItem('stage_active_tenant',tenant);}catch(e){}
  },[currentUser&&currentUser.tenantId,currentUser&&currentUser.orgId,currentUser&&currentUser.sub]);

  // P1 re-engagement — once we're in the dashboard, ask the server whether the
  // 7-day resume nudge is due (server enforces eligibility + one-shot).
  React.useEffect(()=>{
    if(shellMode!=='dashboard')return;
    let live=true;
    authFetch('/api/onboarding/nudge').then(r=>{if(live&&r&&r.show)setResumeNudge(true);}).catch(()=>{});
    return()=>{live=false;};
  },[shellMode,authFetch]);

  // Fetch feature flags once on mount (client-config is always public)
  React.useEffect(()=>{
    fetch(API_BASE+'/api/client-config').then(r=>r.json()).then(cfg=>{
      setBillingEnabled(!!cfg.billingEnabled);
      if(cfg.pricingUrl) setPricingUrl(cfg.pricingUrl);
    }).catch(()=>{});
  },[]);

  const fetchAllData=useCallback(async()=>{
    try{
      // Fetch /api/me once (role doesn't change mid-session)
      if(!currentUser){
        authFetch('/api/me').then(u=>{
          // Enrich from the Clerk client object — the JWT often omits name/email,
          // but the browser Clerk user has them (it powers the account menu), so
          // we can greet by real first name instead of "Servant leader".
          if(u)enrichIdentityFromClerk(u,clerk);
          if(u?.role)setCurrentUser(u);
          // v1.2 — new users are ROUTED to the /start Focus Shell on first
          // sign-in (supersedes the v1.0 welcome modal). Real orgs only
          // (authEnabled, not the demo tenant); checked once per session.
          // Returning users with a completed profile never see it.
          // INVITED users (?finding= deep link) skip the shell entirely —
          // P0-5: their first interaction is a contribution, not orientation.
          if(u&&u.authEnabled&&u.isDemo!==true&&!deepFindingId&&!shellCheckedRef.current){
            shellCheckedRef.current=true;
            authFetch('/api/org/profile').then(p=>{
              if(p&&p.completed===false){
                try{window.history.replaceState({},'','/start');}catch(e){}
                setShellMode('shell');
              }
            }).catch(()=>{});
          }
        }).catch(()=>{});
      }

      const [p,a,i,s,sn,d]=await Promise.all([
        authFetch('/api/portfolio'),
        authFetch('/api/actions'),
        authFetch('/api/insights'),
        authFetch('/api/safe'),
        authFetch('/api/sentry'),
        authFetch('/api/dealroom').catch(()=>null),
      ]);
      // Adopt the org's OWN data — even when empty. An authenticated org should
      // see its real state (a fresh org legitimately has 0 of everything), so we
      // guard on Array.isArray, not length. Demo constants only apply as a
      // fallback when the fetch fails entirely (e.g. not signed in → Promise.all
      // rejects → catch below → live state stays null → DataCtx '|| DEMO').
      // (Previously: empty results were ignored and the Action Rail blended the
      // demo ACTIONS on top of live data, so real empty orgs saw demo numbers —
      // e.g. "0 open" in Pulse but "15 Critical Alerts". The seed↔constant parity
      // means the demo `default` tenant still renders identically from its DB.)
      if(Array.isArray(p))setLivePortfolio(p);
      if(Array.isArray(a))setLiveActions(a);
      if(Array.isArray(i))setLiveInsights(i);
      if(Array.isArray(s))setLiveSafe(s);
      if(Array.isArray(sn))setLiveSentry(sn);
      if(Array.isArray(d))setLiveDeals(d);
      // EW3 — live Time Well Spent (separate fetch; tolerant of absence)
      authFetch('/api/tws').then(t=>{ if(t&&typeof t.available==='boolean') setLiveTws(t); }).catch(()=>{});
    }catch(e){
      console.warn('Live data fetch — using demo data',e);
    }
  },[authFetch]);

  useEffect(()=>{
    fetchAllData();
    const id=setInterval(fetchAllData,30000);
    return()=>clearInterval(id);
  },[fetchAllData]);

  // ── TEAM-1 Phase B — real-time collaboration (SSE) ───────────
  // One EventSource per tab subscribes to this tenant's change stream.
  // On an `invalidate` event we refetch (fetchAllData already pulls every
  // resource), turning the 30s poll above into instant team sync. A toast
  // surfaces teammate activity. EventSource can't set Authorization headers,
  // so the JWT rides as a ?token= query param (same pattern as the WS).
  const [liveToast,setLiveToast]=useState(null);
  const shellCheckedRef=React.useRef(false); // v1.2 — check the onboarding profile at most once per session
  const [presence,setPresence]=useState([]); // TEAM-1 Phase E — live teammate roster
  // TEAM-1 Gap T4 — the user's notification prefs, fetched once; the live toast
  // (collaborator_activity / in_app channel) is suppressed per these. Held in a
  // ref so the long-lived SSE effect reads the latest without re-subscribing.
  const notifPrefsRef=useRef({muted:false,prefs:null});
  useEffect(()=>{
    authFetch('/api/notifications/preferences')
      .then(d=>{ if(d&&d.prefs) notifPrefsRef.current={muted:!!d.muted,prefs:d.prefs}; })
      .catch(()=>{});
  },[authFetch]);
  useEffect(()=>{
    let es=null, stopped=false, backoff=1000, toastTimer=null, refetchTimer=null;
    const RESOURCE_LABEL={
      actions:'Action Rail', insights:'Sage Intelligence', portfolio:'Portfolio Spine',
      dealroom:'Dealroom', safe:'Safe layer', sentry:'Sentry log',
    };
    // M2 — debounce refetches: a single mutation can fan out into several
    // invalidate events (e.g. a deal approval emits dealroom+safe+sentry), and
    // fetchAllData() already pulls every surface. Coalesce a burst into one
    // refetch ~250ms after the last event instead of one per event.
    const scheduleRefetch=()=>{
      if(refetchTimer) clearTimeout(refetchTimer);
      refetchTimer=setTimeout(()=>{ refetchTimer=null; fetchAllData(); },250);
    };
    const connect=async()=>{
      if(stopped) return;
      let url=API_BASE+'/api/stream';
      try{
        // Attach Clerk token when auth is enabled (no-op in dev mode).
        if(clerk&&clerk.session){
          const tok=await clerk.session.getToken();
          if(tok) url+='?token='+encodeURIComponent(tok);
        }
      }catch(e){/* dev mode / no session — connect unauthenticated */}
      try{ es=new EventSource(url); }catch(e){ return; }

      es.onopen=()=>{ backoff=1000; };  // reset backoff on a good connection
      es.onmessage=(ev)=>{
        let msg; try{ msg=JSON.parse(ev.data); }catch(e){ return; }
        if(!msg||!msg.type) return;
        if(msg.type==='invalidate'){
          scheduleRefetch();  // M2 — debounced; pulls every surface once per burst
          // M1 — suppress the toast for the user's own edits; only surface a
          // teammate's change. currentUser.sub is this user's id (server `actor`).
          const isOwnEdit=msg.actor&&currentUser&&msg.actor===currentUser.sub;
          // TEAM-1 Gap T4 — honor the user's notification prefs: skip the toast
          // when muted, or when the collaborator_activity in-app channel is off.
          const _np=notifPrefsRef.current;
          const _collabOn=!_np.muted && (!_np.prefs || _np.prefs.collaborator_activity?.in_app!==false);
          if(!isOwnEdit && _collabOn){
            const label=RESOURCE_LABEL[msg.resource]||msg.resource;
            setLiveToast('🔄 '+label+' updated by a teammate');
            if(toastTimer) clearTimeout(toastTimer);
            toastTimer=setTimeout(()=>setLiveToast(null),4000);
          }
        }else if(msg.type==='presence'){
          // TEAM-1 Phase E — a teammate's presence changed; refresh the roster.
          // (Cheap GET; debounced via the same scheduleRefetch cadence is overkill,
          //  so just pull the small roster directly.)
          if(msg.user_id&&currentUser&&msg.user_id===currentUser.sub) return; // ignore self
          authFetch('/api/presence').then(d=>{ if(d&&Array.isArray(d.roster)) setPresence(d.roster); }).catch(()=>{});
        }
      };
      es.onerror=()=>{
        // Browser auto-reconnects EventSource, but if it closed hard we back off.
        if(es&&es.readyState===2&&!stopped){
          es.close();
          backoff=Math.min(backoff*2,30000);
          setTimeout(connect,backoff);
        }
      };
    };
    connect();
    return()=>{ stopped=true; if(es)es.close(); if(toastTimer)clearTimeout(toastTimer); if(refetchTimer)clearTimeout(refetchTimer); };
  },[fetchAllData,clerk,currentUser]);  // currentUser: re-bind once /api/me resolves so own-edit suppression works

  // ── TEAM-1 Phase E — presence heartbeat ──────────────────────────────────
  // POST /api/presence every 30s with the current page so teammates see where
  // we are; the server TTLs us out ~60s after the last beat (closed-tab fade).
  // The response carries the live roster, so a single beat both announces and
  // refreshes. On unmount we send a best-effort "gone".
  useEffect(()=>{
    let stopped=false;
    const beat=()=>{
      if(stopped) return;
      authFetch('/api/presence',{method:'POST',body:{location:page,status:'active'}})
        .then(d=>{ if(d&&Array.isArray(d.roster)) setPresence(d.roster); })
        .catch(()=>{});
    };
    beat();
    const id=setInterval(beat,30000);
    return()=>{
      stopped=true; clearInterval(id);
      // Best-effort departure so we fade from teammates' views immediately.
      try{ authFetch('/api/presence',{method:'POST',body:{status:'gone'}}); }catch(e){}
    };
  },[authFetch,page]);  // re-beat on page change so `location` stays current

  // ── WebSocket lifecycle (token passed as query param) ────────
  useEffect(()=>{
    let ws;
    // Reconnection state — reset to 0 on every successful open
    let retryCount=0;
    let retryTimer=null;
    // Client-side keepalive — mirrors the server's 30 s pong task
    let clientPingTimer=null;
    // Prevents reconnect attempts after the effect is torn down (page nav / unmount)
    let destroyed=false;

    setWsStatus('connecting');

    const connect=async()=>{
      if(destroyed)return;
      const token=await getToken(clerk);
      if(destroyed)return;
      // Use Railway backend for WebSocket when hosted on Cloudflare Pages
      const wsBase=API_BASE
        ?API_BASE.replace(/^https/,'wss').replace(/^http/,'ws')
        :`${location.protocol==='https:'?'wss':'ws'}://${location.host}`;
      const url=`${wsBase}/ws/status/${sessionId}`+(token?`?token=${encodeURIComponent(token)}`:'');
      ws=new WebSocket(url);

      ws.onopen=()=>{
        if(destroyed){ws.close();return;}
        wsRef.current=ws;
        retryCount=0; // successful connect — reset backoff counter
        setWsStatus('open');
        ws.send(JSON.stringify({type:'ping'}));
        // Client-side keepalive: Cloudflare's proxy silently drops WebSocket
        // connections idle for >100 s. The server already pings every 30 s
        // (server→client). We mirror that from the client side (client→server)
        // so the socket is bidirectionally active and Cloudflare never sees
        // silence from either direction.
        clearInterval(clientPingTimer);
        clientPingTimer=setInterval(()=>{
          if(ws&&ws.readyState===1)ws.send(JSON.stringify({type:'ping'}));
        },25000);
      };

      // Intent accumulator — set by 'intent' event, consumed by next 'text' event
      let pendingIntent=null;

      ws.onmessage=(e)=>{
        const msg=JSON.parse(e.data);
        if(msg.type==='intent'){
          // Store the intent category; it will be attached to the next sage message
          pendingIntent=msg.category||null;
        } else if(msg.type==='thinking'){
          setSageStreaming(true);
        } else if(msg.type==='text'){
          setSageStreaming(true);
          setSageMessages(prev=>{
            const last=prev[prev.length-1];
            if(last?.role==='sage'&&last.streaming){
              return[...prev.slice(0,-1),{...last,content:last.content+msg.content}];
            }
            // Attach the pending intent to this new message, then clear it
            const newMsg={id:_mid(),role:'sage',content:msg.content,agent:msg.agent||'Sage',streaming:true,intentCategory:pendingIntent};
            pendingIntent=null;
            return[...prev,newMsg];
          });
        } else if(msg.type==='citations'){
          // Verified sources for the current Sage reply (validated server-side).
          setSageMessages(prev=>{
            const last=prev[prev.length-1];
            if(last?.role==='sage')return[...prev.slice(0,-1),{...last,citations:msg.items||[]}];
            return prev;
          });
        } else if(msg.type==='done'){
          setSageStreaming(false);
          setSageMessages(prev=>{
            const last=prev[prev.length-1];
            if(last?.role==='sage')return[...prev.slice(0,-1),{...last,streaming:false}];
            return prev;
          });
        } else if(msg.type==='error'){
          // Server-side error (auth failure, API unavailable, circuit open, etc.)
          // Always clear streaming — this is the most common cause of stuck "thinking…"
          setSageStreaming(false);
          setSageMessages(prev=>{
            // If there's an in-progress streaming message, finalise it first
            const last=prev[prev.length-1];
            const base=last?.role==='sage'&&last.streaming
              ?[...prev.slice(0,-1),{...last,streaming:false}]
              :prev;
            return[...base,{
              id:_mid(),
              role:'sage',
              content:msg.content||'Sage encountered an error. Please try again.',
              agent:'Sage',
              streaming:false,
              isError:true,
            }];
          });
        } else if(msg.type==='action'){
          const p=msg.payload||{};
          setSageMessages(prev=>[...prev,{
            id:_mid(),
            role:'sage',
            content:`▶ Action created: **${p.title||'New action'}** (${p.priority||'P2'}) — added to Action Rail.`,
            agent:'Sage',streaming:false
          }]);
          fetchAllData();
        }
      };

      // Connection-level failures also clear the streaming indicator so the
      // "thinking…" badge can never get stuck on a dropped socket.
      ws.onerror=(evt)=>{
        console.warn('Sage WS error',evt);
        setSageStreaming(false);
        setSageMessages(prev=>{
          const last=prev[prev.length-1];
          if(last?.role==='sage'&&last.streaming){
            return[...prev.slice(0,-1),{...last,streaming:false,isError:true,
              content:last.content||'Connection error — please try again.'}];
          }
          return prev;
        });
      };
      ws.onclose=(evt)=>{
        // Stop the keepalive ping immediately — socket is gone
        clearInterval(clientPingTimer);
        wsRef.current=null;
        // Only react if this wasn't a clean client-initiated close (codes 1000/1001)
        if(evt.code!==1000&&evt.code!==1001){
          setSageStreaming(false);
          setSageMessages(prev=>{
            const last=prev[prev.length-1];
            if(last?.role==='sage'&&last.streaming){
              return[...prev.slice(0,-1),{...last,streaming:false,isError:true,
                content:last.content||'Connection dropped — reconnecting…'}];
            }
            return prev;
          });
          // Auto-reconnect with exponential backoff.
          // Delays: 1s·2s·4s·8s·16s·30s×5 — 10 attempts, last 5 at 30s intervals.
          // That covers a ~3.5 minute Railway cold start window before giving up.
          // After 10 failures the status transitions to 'dead' and the UI shows
          // a persistent "Reconnect" banner the user must click.
          if(!destroyed&&retryCount<10){
            const delay=Math.min(1000*Math.pow(2,retryCount),30000);
            retryCount++;
            setWsStatus('reconnecting');
            console.info(
              'Sage WS: closed (code=%d) — reconnecting in %dms (attempt %d/10)',
              evt.code,delay,retryCount
            );
            retryTimer=setTimeout(connect,delay);
          }else if(!destroyed){
            setWsStatus('dead');
          }
        }else{
          setSageStreaming(false);
        }
      };
    };

    connect();
    return()=>{
      // Tear down cleanly: cancel any pending reconnect + keepalive, close socket
      destroyed=true;
      clearTimeout(retryTimer);
      clearInterval(clientPingTimer);
      if(ws)ws.close();
    };
  },[sessionId,fetchAllData,clerk,wsKey]);

  // ── Open Sage and focus input ────────────────────────────────
  const openSage=useCallback(()=>setSageOpen(true),[]);

  // ── Open Sage overlay and immediately send a pre-composed message ────────────
  // Used by per-card buttons (View Evidence, Ask Sage, Drill Through, Draft with Scribe)
  // so every action routes through the real WS rather than a mock.
  const openSageWithMessage=useCallback((msg,fromPage)=>{
    setSageOpen(true);
    // Wait up to 2 s for the WS to be OPEN before sending.
    // 80 ms was too short: the WS handshake through Cloudflare + Railway can
    // take 200–500 ms, so quick-prompt clicks on first load always fired into
    // a CONNECTING socket and showed the "reconnecting" error.
    const _sendWhenReady=(attempts)=>{
      if(!msg||!msg.trim())return;
      const ws=wsRef.current;
      if(ws&&ws.readyState===1){
        setSageMessages(prev=>[...prev,{id:_mid(),role:'user',content:msg}]);
        ws.send(JSON.stringify({
          type:'message',
          content:msg,
          // Use the caller's page context (e.g. 'portfolio', 'dealroom') so
          // Sage's system prompt reflects where the user actually is.
          page:fromPage||page,
          trust_stage:trustStage,
        }));
      }else if(attempts>0&&(!ws||ws.readyState===0)){
        // Socket is still CONNECTING — poll every 100 ms, up to 2 s total
        setTimeout(()=>_sendWhenReady(attempts-1),100);
      }else{
        // Either permanently closed (readyState 2/3) or timed out waiting
        setSageMessages(prev=>[...prev,
          {id:_mid(),role:'user',content:msg},
          {id:_mid(),role:'sage',content:'Sage is reconnecting — please try again in a moment.',
           agent:'Sage',streaming:false,isError:true},
        ]);
      }
    };
    // Initial delay so the overlay is mounted; then start polling
    setTimeout(()=>_sendWhenReady(20),80);
  },[trustStage,page,setSageMessages]);

  // ── v1.1 §3.6/§5 — palette dispatch + global shortcuts ───────────────────
  // Focus intents are handed to the Experience Setting page via a window event
  // (already mounted) or a sessionStorage note consumed on its next mount.
  const sendFocusIntent=useCallback((detail)=>{
    if(page==='setting'){window.dispatchEvent(new CustomEvent('stage-focus',{detail}));}
    else{
      try{sessionStorage.setItem('stage_focus_pending',JSON.stringify(detail));}catch(e){}
      setPage('setting');
    }
  },[page]);
  const paletteAction=useCallback((act)=>{
    if(act==='triage')sendFocusIntent({triage:true});
    else if(act==='overview')sendFocusIntent({overview:true});
    else if(act==='pin')sendFocusIntent({pin:true});
    else if(act==='new')sendFocusIntent({newSetting:true});
    else if(act==='lp')openSageWithMessage('Generate the LP Update for this quarter — portfolio status, Setting velocity, and the key risks an LP should know about.','sage');
  },[sendFocusIntent,openSageWithMessage]);
  useEffect(()=>{
    const onKey=e=>{
      const meta=e.metaKey||e.ctrlKey;
      if(meta&&!e.shiftKey&&e.key.toLowerCase()==='k'){e.preventDefault();setPaletteOpen(o=>!o);}
      // ⌘/ opens Help from anywhere (distinct from the '?' Why-this key on the
      // Experience Setting header, so the two never collide).
      else if(meta&&e.key==='/'){e.preventDefault();setHelpOpen(o=>!o);}
      // ⌘⇧T from any page reaches Triage in one action (§3.5); the Experience
      // Setting page has its own handler while mounted.
      else if(meta&&e.shiftKey&&e.key.toLowerCase()==='t'&&page!=='setting'){e.preventDefault();sendFocusIntent({triage:true});}
    };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[page,sendFocusIntent]);

  // Demo-account isolation: ONLY the dedicated demo tenant (backend isDemo, true
  // also in dev mode) renders the bundled demo constants. Every real account gets
  // empty arrays → real data or proper empty states, never illustrative figures.
  const isDemo=currentUser?.isDemo===true;
  const demoOr=(live,demo)=>live!==null?live:(isDemo?demo:[]);
  const dataCtxValue={
    portfolio:demoOr(livePortfolio,PORTFOLIO),
    deals:demoOr(liveDeals,DEALS),
    actions:demoOr(liveActions,ACTIONS),
    insights:demoOr(liveInsights,SAGE_INSIGHTS),
    safeAssets:demoOr(liveSafe,SAFE_ASSETS),
    sentry:demoOr(liveSentry,SENTRY_ARTIFACTS),
    tws:liveTws, // EW3 — null until /api/tws resolves; {available,composite,pillars,...} after
    currentUser,
    isDemo,              // surfaces the demo state to any consumer (banners, etc.)
    presence,            // TEAM-1 Phase E — live teammate roster
    refresh:fetchAllData,
    // True once the API has returned real portfolio data (not the demo constant).
    // Real accounts are "live" the moment we know they aren't the demo tenant.
    portfolioIsLive:livePortfolio!==null||(currentUser!==null&&!isDemo),
    // True once live insights have arrived from the API
    insightsIsLive:liveInsights!==null&&liveInsights.length>0,
    // Feature flags
    billingEnabled,
    pricingUrl,
  };

  // v1.2 — the Single-Focus Shell owns the viewport. Its own layout, not a
  // modal: no sidebar, tiles, Ask-Sage bar, or banners are mounted behind it.
  if(shellMode==='shell'){
    return <FocusShell onReveal={handleReveal} tenantId={currentUser&&(currentUser.tenantId||currentUser.orgId)}/>;
  }

  return(
    <DataCtx.Provider value={dataCtxValue}>
    <div className={'app'+(shellMode==='reveal'?' revealing':'')}>
      {liveToast&&(
        <div style={{position:'fixed',bottom:'20px',right:'20px',zIndex:9999,
          background:'#3D1F0D',color:'#F5EDE0',padding:'10px 16px',borderRadius:'8px',
          fontSize:'13px',boxShadow:'0 4px 16px rgba(0,0,0,0.25)',
          fontFamily:'system-ui,sans-serif',maxWidth:'320px'}}>
          {liveToast}
        </div>
      )}
      <Sidebar page={page} onNav={p=>{setPage(p);}}/>
      {/* v1.1 §3.6 — ⌘K: Setting switcher + `>` actions + Ask-Sage passthrough */}
      <CommandPalette open={paletteOpen} onClose={()=>setPaletteOpen(false)}
        deals={dataCtxValue.deals||[]} recentIds={loadFocusState().tabs}
        onFocusSetting={id=>sendFocusIntent({settingId:id})}
        onAction={paletteAction}
        onAskSage={q=>openSageWithMessage(q,page)}/>
      {/* Help surface — always one click (?) or ⌘/ away */}
      <HelpDrawer open={helpOpen} onClose={()=>setHelpOpen(false)} page={page}
        onNav={setPage} onReplay={replayOnboarding} onAsk={q=>openSageWithMessage(q,page)}/>
      <div className="main">
        <Topbar onSageOpen={openSage} onHelpOpen={()=>setHelpOpen(true)}/>
        <BillingBanner/>
        <SageChatOverlay
          page={page}
          open={sageOpen}
          onClose={()=>setSageOpen(false)}
          wsRef={wsRef}
          messages={sageMessages}
          setMessages={setSageMessages}
          streaming={sageStreaming}
          trustStage={trustStage}
          wsStatus={wsStatus}
          onReconnect={()=>{setWsStatus('connecting');setWsKey(k=>k+1);}}
          onNavigate={navigateToCitation}
        />
        <div className="content">
          {/* P1 re-engagement — one dismissable nudge to resume the bypassed tour from Help */}
          {resumeNudge&&(
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'11px 16px',marginBottom:16,background:'rgba(232,129,58,.08)',border:'1px solid rgba(232,129,58,.3)',borderRadius:11}}>
              <span style={{fontSize:16}}>✦</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:'#3D1F0D'}}>Pick up where you left off</div>
                <div style={{fontSize:12,color:'rgba(61,31,13,.6)',marginTop:1}}>Your guided setup is still here — get a real Sage finding in a couple of minutes.</div>
              </div>
              <button onClick={()=>{setHelpOpen(true);dismissNudge();}} style={{padding:'7px 15px',background:'#3D1F0D',color:'#F5EDE0',border:'none',borderRadius:7,fontSize:12.5,fontWeight:600,cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>Resume setup</button>
              <button onClick={dismissNudge} aria-label="Dismiss" style={{border:'none',background:'none',fontSize:18,color:'rgba(61,31,13,.4)',cursor:'pointer',lineHeight:1,flexShrink:0}}>×</button>
            </div>
          )}
          {deepFindingId&&!deepDismissed&&<FindingLanding findingId={deepFindingId} onDismiss={dismissDeep} onNav={setPage}/>}
          <ErrorBoundary key={page} onHome={()=>setPage('brief')}>
          <PageTransition>
            {page==='brief'      &&<StatusBriefPage onNav={setPage}/>}
            {page==='portfolio'  &&<PortfolioSpinePage onSageAsk={openSageWithMessage} onNav={setPage}/>}
            {page==='setting'    &&<DealroomPage onSageAsk={openSageWithMessage}/>}
            {page==='dealbrief'  &&<DealBriefPage/>}
            {page==='analytics'  &&<AnalyticsPage onSageAsk={openSageWithMessage} surface="analytics" onNav={setPage}/>}
            {page==='scorecard'  &&<AnalyticsPage onSageAsk={openSageWithMessage} surface="scorecard" onNav={setPage}/>}
            {page==='demand'     &&<SpaceDemandPage/>}
            {page==='actions'    &&<ActionRailPage/>}
            {page==='financial'  &&<FinancialPage onNav={setPage}/>}
            {page==='cke'        &&<CKEPage/>}
            {page==='sage'       &&<SagePage trustStage={trustStage} setTrustStage={setTrustStage} maxStage={trustCeiling} onSageAsk={openSageWithMessage}/>}
            {page==='governance' &&<GovernancePage/>}
            {page==='plans'      &&<PlansPage/>}
          </PageTransition>
          </ErrorBoundary>
        </div>
      </div>
    </div>
    </DataCtx.Provider>
  );
}

/* Sense too → Status bridge (PRD §14 v2). A converted lead lands here from the
   Brief handoff with ?src=sensetoo&sid=<session>. Once authenticated (this only
   mounts inside AuthGate), fire the one-time import that seeds the corporate
   profile + TWS standards from that session's Ideal Spec, then strip the
   attribution params so a refresh can't re-fire or leak them. Best-effort and
   idempotent server-side (409 on repeat) — never blocks the app. */
function SensetooLandingImport(){
  const authFetch=useAuthFetch();
  const {addToast}=useToast();
  useEffect(()=>{
    let params; try{params=new URLSearchParams(window.location.search);}catch(e){return;}
    if(params.get('src')!=='sensetoo')return;
    const sid=params.get('sid')||'';
    if(!/^[0-9a-f-]{36}$/i.test(sid))return;
    const key='sensetoo_import_'+sid;
    try{if(localStorage.getItem(key))return;}catch(e){}
    (async()=>{
      try{
        const r=await authFetch('/api/onboarding/import-sensetoo',{method:'POST',body:{session_id:sid}});
        if(r&&r.ok){
          addToast('Your Sense too evaluation is now in your workspace — profile & standards seeded.','success',5200);
        }
      }catch(e){/* 409 already-imported / 404 / offline — best-effort, stay silent */}
      try{localStorage.setItem(key,'1');}catch(e){}
      try{
        params.delete('src');params.delete('sid');params.delete('variant');params.delete('op');
        const qs=params.toString();
        window.history.replaceState({}, '', window.location.pathname+(qs?('?'+qs):''));
      }catch(e){}
    })();
  },[]);  // once, on first authenticated mount
  return null;
}

ReactDOM.createRoot(document.getElementById('root')).render(<AuthGate><ToastProvider><SensetooLandingImport/><App/></ToastProvider></AuthGate>);
